import { useState, useEffect, useRef, useContext, createContext, Fragment } from "react";
import botanicalLeftImg from "./assets/botanical-left.png";
import botanicalRightImg from "./assets/botanical-right.png";
import logoImg from "./assets/logo.png";

// ── PALETTE & TOKENS ─────────────────────────────────────────────────────────
// ── THEME PALETTES ────────────────────────────────────────────────────────
const C_DARK = {
  cream:"#34362D", cream2:"#3D4136", cream3:"#4F5445", parchment:"#5D5E4F",
  ink:"#F1ECE3", inkMid:"#C6BFB3", inkLight:"#C6BFB3", inkFaint:"#969184",
  amber:"#B8743E", amberDim:"#C9874E", green:"#69846A", red:"#C4483A",
  border:"#565849", borderSoft:"#525446",
};
const C_LIGHT = {
  cream:"#F7F2EA", cream2:"#F0EAE1", cream3:"#E7E0D5", parchment:"#D9D0C4",
  ink:"#292722", inkMid:"#706A61", inkLight:"#706A61", inkFaint:"#9B948A",
  amber:"#A96E3E", amberDim:"#B97845", green:"#55765B", red:"#B94040",
  border:"#D9D0C4", borderSoft:"#D9D0C4",
};
const C_FIXED_DARK = "#1C1814";

const THEME_KEY = "fdc-theme";
const initialTheme = (() => {
  try { return localStorage.getItem(THEME_KEY) || "dark"; } catch { return "dark"; }
})();
let C = { ...(initialTheme === "dark" ? C_DARK : C_LIGHT) };
function applyTheme(theme) { Object.assign(C, theme === "dark" ? C_DARK : C_LIGHT); }

const fontSans = "'Instrument Sans', 'DM Sans', system-ui, -apple-system, sans-serif";
const fontSerif = "'Cormorant Garamond', Georgia, serif";

if (typeof document !== "undefined" && !document.getElementById("fdc-font-loader")) {
  const link = document.createElement("link");
  link.id = "fdc-font-loader";
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Instrument+Sans:wght@400;500;600&family=DM+Sans:wght@400;500;600&display=swap";
  document.head.appendChild(link);
}

// ══════════════════════════════════════════════════════════════════════════════
// SYNC ENGINE — cloud backup via a Cloudflare Worker + D1 database
// ─────────────────────────────────────────────────────────────────────────────
// The Hub still reads and writes localStorage exactly as it always has —
// every existing loadXXX()/saveXXX() function elsewhere in this file is
// completely unchanged. This module works underneath by shadowing
// localStorage.setItem/getItem for the specific keys the Hub uses, so any
// save anywhere in the app automatically queues a push to the cloud without
// that component needing to know sync exists at all.
//
// Left unconfigured (SYNC_WORKER_URL blank), every function here is a no-op
// and the Hub behaves exactly as it did before this file existed.
//
// See /fdc-sync-worker/README.md for deployment steps.
// ══════════════════════════════════════════════════════════════════════════════
const SYNC_WORKER_URL = "https://fdc-sync-worker.firstdraftcoffee.workers.dev";
// The account system this powers is the fdc-customer-portal-worker's
// /roaster/* routes — the same Worker that already handles the wholesale
// portal, with roaster signup/login added alongside it.
const AUTH_WORKER_URL = "https://fdc-customer-portal.firstdraftcoffee.workers.dev";

// ── COMMUNITY / POOLED LEARNING ──────────────────────────────────────────
// Optional, opt-in sharing of anonymised diagnostic + Agtron entries to a
// separate pooled database (fdc-pooled-learning-worker) — cultivar GROUP +
// process + the numeric adjustment only, never identity, exact cultivar,
// origin, or account. Off by default; see PooledLearningToggle below.
// Reading pooled stats back INTO the recommendation engine is a separate
// follow-up step, not built here — this only handles submitting.
const POOL_WORKER_URL = "https://fdc-pooled-learning-worker.firstdraftcoffee.workers.dev";
const POOL_KEY = "test123"; // must match `wrangler secret put POOL_KEY` on fdc-pooled-learning-worker

function isPooledLearningOptedIn() {
  try { return localStorage.getItem("fdc_pooled_learning_opt_in") === "true"; } catch { return false; }
}

// Fire-and-forget — never blocks the UI, never surfaces an error to the
// roaster. If it fails, that one data point just doesn't get shared;
// nothing about the Hub's own local behaviour depends on this succeeding.
function submitPoolEntry(path, payload) {
  if (!isPooledLearningOptedIn() || !POOL_WORKER_URL) return;
  fetch(`${POOL_WORKER_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Pool-Key": POOL_KEY },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
}

// The old shared SYNC_KEY constant is gone — every roaster now sends their
// own session token instead, so the sync worker knows whose data it's
// reading/writing rather than everyone sharing one bucket. AuthGate sets
// this via setSyncToken() once someone's actually logged in; nothing syncs
// before that. A plain module-level variable (not React state) because the
// plumbing functions below (scheduleSyncFlush, pullAndMergeCloudData) are
// ordinary functions outside the component tree, not hooks.
let currentSyncToken = null;
function setSyncToken(token) { currentSyncToken = token; }

// Every localStorage key the Hub uses, mirrored to the cloud. Bookkeeping
// keys (pending queue, sync metadata) are deliberately NOT in this list —
// syncing sync's-own-bookkeeping would be circular and pointless.
const SYNCED_KEYS = [
  "fdc_inventory", "fdc_history", "fdc_blends", "fdc_profiles",
  "fdc_cleaning_v2", "fdc_yearend", "fdc_diagnostic_log",
  "fdc_qc_approval", "fdc_packaging_batches", "fdc_packaging_stock", "fdc_customers", "fdc_allocations",
  "fdc_shelf_life_policy", "fdc_sku_counters", "fdc_pricing_origins", "fdc_pricing_settings",
  "fdc_alerts_enabled", "fdc_orders", "fdc_inventory_cost_settings", "fdc_batch_counter",
  "fdc_pooled_learning_opt_in", "fdc_machine_profile",
  "fdc_premises_tasks", "fdc_premises_actions",
  "fdc_custom_barcodes", "fdc_label_settings",
  "fdc_invoice_settings", "fdc_contract_prices",
  "fdc_stocktake_counts", "fdc_pricing_wholesale",
  "fdc_products",
];

// Not synced on purpose: "fdc_roast_draft_active" (the crash-recovery copy of
// the roast in progress — it's rewritten every second and only matters on
// this computer; syncing it meant an upload a second during every roast) and
// "fdc_audio_device" (microphones differ from computer to computer).

const SYNC_PENDING_KEY = "fdc_sync_pending"; // array of keys awaiting push
const SYNC_META_KEY = "fdc_sync_meta"; // { key: lastAppliedTimestamp } — sync's own bookkeeping, not synced itself

// Keep real references to the original methods before shadowing them, so
// the sync engine's own bookkeeping writes (queue, meta) never re-trigger
// themselves, and so a push can read the current value without recursion.
const _origSetItem = localStorage.setItem.bind(localStorage);
const _origGetItem = localStorage.getItem.bind(localStorage);

function loadSyncPending() {
  try { return JSON.parse(_origGetItem(SYNC_PENDING_KEY) || "[]"); } catch { return []; }
}
function saveSyncPending(arr) { _origSetItem(SYNC_PENDING_KEY, JSON.stringify(arr)); }
function loadSyncMeta() {
  try { return JSON.parse(_origGetItem(SYNC_META_KEY) || "{}"); } catch { return {}; }
}
function saveSyncMeta(obj) { _origSetItem(SYNC_META_KEY, JSON.stringify(obj)); }

// Tiny pub-sub so the header dot can react to sync state without prop
// drilling — this module has no React import dependency of its own.
let _syncStatus = "idle"; // "idle" | "syncing" | "synced" | "offline"
const _syncListeners = new Set();
function setSyncStatus(next) {
  _syncStatus = next;
  _syncListeners.forEach(fn => fn(next));
}
function useSyncStatus() {
  const [status, setStatus] = useState(_syncStatus);
  useEffect(() => {
    _syncListeners.add(setStatus);
    return () => _syncListeners.delete(setStatus);
  }, []);
  return status;
}

// Shadow localStorage.setItem — every existing saveXXX() in this file
// already calls localStorage.setItem under the hood, so this one hook
// point covers all of them without touching a single one individually.
localStorage.setItem = function (key, value) {
  _origSetItem(key, value);
  if (SYNC_WORKER_URL && SYNCED_KEYS.includes(key)) {
    const pending = loadSyncPending();
    if (!pending.includes(key)) { pending.push(key); saveSyncPending(pending); }
    setSyncStatus("syncing");
    scheduleSyncFlush();
  }
};

let _flushTimer = null;
function scheduleSyncFlush() {
  if (_flushTimer) return;
  _flushTimer = setTimeout(() => { _flushTimer = null; flushPendingSync(); }, 800);
}

async function pushKeyToCloud(key) {
  const value = _origGetItem(key);
  if (value == null) return true; // nothing to push, treat as done
  const updatedAt = Date.now();
  const res = await fetch(`${SYNC_WORKER_URL}/kv/${encodeURIComponent(key)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", "X-Sync-Key": currentSyncToken },
    body: JSON.stringify({ value, updatedAt }),
    // Browsers refuse keepalive uploads over 64 KB (it's in the fetch spec),
    // which silently stopped Roast History ever reaching the cloud. So only
    // small items use keepalive; big ones go up as a normal upload.
    // keepalive lets this request keep running even as the page is being
    // torn down (closed, navigated away, put to sleep) — a normal fetch
    // gets cancelled mid-flight in that situation, which is exactly how a
    // save that had already landed on screen could still lose the race to
    // reach the cloud before the tab actually closed.
    keepalive: value.length < 60000,
  });
  if (!res.ok) return false;
  const meta = loadSyncMeta();
  meta[key] = updatedAt;
  saveSyncMeta(meta);
  return true;
}

async function flushPendingSync() {
  if (!SYNC_WORKER_URL) return;
  const pending = [...new Set(loadSyncPending())];
  if (pending.length === 0) { setSyncStatus("synced"); return; }
  setSyncStatus("syncing");
  const stillPending = [];
  for (const key of pending) {
    try {
      const ok = await pushKeyToCloud(key);
      if (!ok) stillPending.push(key);
    } catch { stillPending.push(key); }
  }
  saveSyncPending(stillPending);
  if (stillPending.length > 0) {
    setSyncStatus("offline");
    setTimeout(flushPendingSync, 5000); // keep retrying — wifi may come back
  } else {
    setSyncStatus("synced");
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => flushPendingSync());

  // Force an immediate flush — bypassing the normal 800ms debounce below
  // — the moment the tab is hidden, backgrounded, or about to close.
  // Previously a save could sit inside that debounce window right up
  // until the tab closed, taking the correction with it: it looked saved
  // on screen, but the cloud never actually received it, so the NEXT
  // device to open the Hub pulled the old, uncorrected value back down
  // and overwrote the fix. "pagehide" catches close/navigate-away more
  // reliably than "beforeunload" across browsers, especially with
  // back/forward cache; "visibilitychange" also catches simply switching
  // tabs or the laptop going to sleep, not just closing outright.
  const forceImmediateFlush = () => {
    if (loadSyncPending().length === 0) return;
    clearTimeout(_flushTimer);
    _flushTimer = null;
    flushPendingSync();
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") forceImmediateFlush();
  });
  window.addEventListener("pagehide", forceImmediateFlush);
}

// Pull-and-merge, run once at app startup before anything else renders.
// Last-write-wins by timestamp: only overwrite a local key if the cloud's
// copy is newer than the last version this device applied or pushed.
// Anything local that the cloud has never seen at all gets pushed up —
// this is what covers the one-time import of existing browser data.
async function pullAndMergeCloudData() {
  if (!SYNC_WORKER_URL) return;
  let cloudData;
  try {
    const res = await fetch(`${SYNC_WORKER_URL}/kv`, { headers: { "X-Sync-Key": currentSyncToken } });
    if (!res.ok) return; // offline or worker unreachable — proceed with local data as-is
    cloudData = await res.json();
  } catch { return; }

  const meta = loadSyncMeta();
  // Anything changed here that hasn't reached the cloud yet always wins —
  // the cloud's copy of it is older by definition, and must never replace it.
  const unsent = new Set(loadSyncPending());
  for (const key of SYNCED_KEYS) {
    const cloud = cloudData[key];
    if (!cloud) continue; // cloud has never seen this key — will be pushed below instead
    if (unsent.has(key)) continue;
    const localAppliedAt = meta[key] || 0;
    if (cloud.updatedAt > localAppliedAt) {
      _origSetItem(key, cloud.value);
      meta[key] = cloud.updatedAt;
    }
  }
  saveSyncMeta(meta);

  // First-time / catch-up push: any locally-present key the cloud has
  // nothing for yet gets uploaded once, unconditionally.
  for (const key of SYNCED_KEYS) {
    if (!cloudData[key] && _origGetItem(key) != null) {
      const pending = loadSyncPending();
      if (!pending.includes(key)) { pending.push(key); saveSyncPending(pending); }
    }
  }
  saveSyncPending(loadSyncPending());
  flushPendingSync();
}

// Bundles everything the Hub tracks into one downloadable JSON file — the
// "download all my data" safety valve, independent of whether cloud sync
// is even configured.
function exportAllDataAsFile() {
  const bundle = { exportedAt: new Date().toISOString(), data: {} };
  for (const key of SYNCED_KEYS) {
    const raw = _origGetItem(key);
    if (raw == null) continue;
    try { bundle.data[key] = JSON.parse(raw); } catch { bundle.data[key] = raw; }
  }
  const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `fdc-roasting-hub-backup-${localIsoDate()}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function SyncStatusDot() {
  const status = useSyncStatus();
  if (!SYNC_WORKER_URL) return null; // nothing configured yet — stay invisible rather than show a misleading "offline"
  const cfg = {
    idle: { color: C.inkFaint, label: "—" },
    synced: { color: C.green, label: "Synced" },
    syncing: { color: C.amber, label: "Saving..." },
    offline: { color: C.inkFaint, label: "Offline" },
  }[status] || { color: C.inkFaint, label: "—" };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }} title={cfg.label}>
      <div style={{ width: 8, height: 8, borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
      <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: cfg.color, textTransform: "uppercase" }}>{cfg.label}</div>
    </div>
  );
}

// Opt-in toggle for pooled/anonymised learning — off by default. Clicking
// the ⓘ shows exactly what gets shared, so the choice is genuinely
// informed rather than a blind switch.
function PooledLearningToggle() {
  const [optIn, setOptIn] = useState(isPooledLearningOptedIn);
  const [showInfo, setShowInfo] = useState(false);
  const toggle = () => {
    const next = !optIn;
    setOptIn(next);
    localStorage.setItem("fdc_pooled_learning_opt_in", String(next));
  };
  return (
    <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 6 }}>
      <div onClick={() => setShowInfo(v => !v)} style={{ fontFamily: fontSans, fontSize: 13, color: C.inkFaint, cursor: "pointer" }} title="What's this?">ⓘ</div>
      <div onClick={toggle} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }} title="Share anonymised diagnostic and colour data to help every roaster's recipes learn faster">
        <div style={{ width: 26, height: 15, borderRadius: 8, background: optIn ? C.amber : C.border, position: "relative", transition: "background 0.15s", flexShrink: 0 }}>
          <div style={{ width: 11, height: 11, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: optIn ? 13 : 2, transition: "left 0.15s" }} />
        </div>
        <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 600, color: optIn ? C.ink : C.inkFaint }}>Community learning</div>
      </div>
      {showInfo && (
        <div style={{ position: "absolute", top: 24, right: 0, width: 260, background: "#fff", border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 14px", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 50 }}>
          <div style={{ fontFamily: fontSans, fontSize: 11.5, color: C.inkMid, lineHeight: 1.5 }}>
            When on, your diagnostic feedback and colour readings are shared anonymously — cultivar group, process, and the adjustment only, never your identity, exact cultivar, origin, or account — so every roaster's recipes learn faster. Off by default.
          </div>
        </div>
      )}
    </div>
  );
}

// Small roastery name + logout control for the header — reads from
// AuthContext, so it only renders once AuthGate has confirmed someone's
// actually logged in.
function RoasterAccountMenu() {
  const { roaster, logout } = useContext(AuthContext);
  if (!roaster) return null;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
      <div style={{ fontFamily:fontSans, fontSize:11, color:C.inkFaint }} title={roaster.email}>
        {roaster.roastery_name}
      </div>
      <button onClick={logout} title="Log out" style={{
        fontFamily:fontSans, fontSize:11, color:C.inkFaint, background:"none", border:"none", cursor:"pointer", padding:0,
      }}>Log out</button>
    </div>
  );
}


// component's loadXXX() initializer sees merged data on its very first
// read rather than racing an in-flight fetch. Invisible when sync isn't
// configured — resolves on the same tick, no flash of a loading screen.
function SyncGate({ children }) {
  const [ready, setReady] = useState(!SYNC_WORKER_URL);
  useEffect(() => {
    if (!SYNC_WORKER_URL) return;
    let cancelled = false;
    pullAndMergeCloudData().finally(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
  }, []);
  if (!ready) {
    return (
      <div style={{ minHeight: "100vh", background: C.cream, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <BeanMark size={48} />
          <div style={{ fontFamily: fontSans, fontSize: 12, color: C.inkFaint, marginTop: 12 }}>Syncing your data…</div>
        </div>
      </div>
    );
  }
  return children;
}

// ── Roaster account gate ──────────────────────────────────────────────────
// Sits outside SyncGate — checks whether anyone's actually logged in before
// showing the app at all, rather than showing the dashboard to anyone who
// visits the URL. AuthContext carries the logged-in roaster's details plus
// a logout() function down to wherever the header needs them.
const AuthContext = createContext({ roaster: null, logout: () => {} });

// The roaster's own session token, persisted in localStorage — cross-site
// cookies get blocked (Incognito by default, and an increasing number of
// regular browser configs too) since app.theroastinghub.com and the auth
// Worker are technically different sites. Storing the token ourselves and
// sending it explicitly in an Authorization header sidesteps that entirely
// — same approach the sync worker already used successfully.
const ROASTER_TOKEN_KEY = "fdc_roaster_token";
function saveRoasterToken(token) {
  if (token) localStorage.setItem(ROASTER_TOKEN_KEY, token);
  else localStorage.removeItem(ROASTER_TOKEN_KEY);
}
function loadRoasterToken() {
  try { return localStorage.getItem(ROASTER_TOKEN_KEY); } catch { return null; }
}

function AuthGate({ children }) {
  const [status, setStatus] = useState("loading"); // "loading" | "authed" | "anon"
  const [roaster, setRoaster] = useState(null);

  useEffect(() => {
    const token = loadRoasterToken();
    if (!token || resetTokenFromUrl()) { setStatus("anon"); return; }
    fetch(`${AUTH_WORKER_URL}/roaster/me`, { headers: { "Authorization": `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => { setSyncToken(data.sync_token); setRoaster(data.roaster); setStatus("authed"); })
      .catch(() => { saveRoasterToken(null); setStatus("anon"); });
  }, []);

  const logout = async () => {
    const token = loadRoasterToken();
    try {
      await fetch(`${AUTH_WORKER_URL}/roaster/logout`, { method: "POST", headers: token ? { "Authorization": `Bearer ${token}` } : {} });
    } catch {} // logging out client-side regardless — worst case the old session just expires on its own
    saveRoasterToken(null);
    setSyncToken(null);
    setRoaster(null);
    setStatus("anon");
  };

  if (status === "loading") {
    return (
      <div style={{ minHeight: "100vh", background: C.cream, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <BeanMark size={48} />
          <div style={{ fontFamily: fontSans, fontSize: 12, color: C.inkFaint, marginTop: 12 }}>Checking your account…</div>
        </div>
      </div>
    );
  }
  if (status === "anon") {
    return <AuthScreen onAuthed={r => { setRoaster(r); setStatus("authed"); }} />;
  }
  return <AuthContext.Provider value={{ roaster, logout }}>{children}</AuthContext.Provider>;
}

// Password reset links from the email look like https://app.theroastinghub.com/?reset=<token>
function resetTokenFromUrl() {
  try { return new URLSearchParams(window.location.search).get("reset"); } catch { return null; }
}

function AuthScreen({ onAuthed }) {
  const [resetToken] = useState(resetTokenFromUrl);
  const [mode, setMode] = useState(resetToken ? "reset" : "login"); // "login" | "signup" | "forgot" | "reset"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [sent, setSent] = useState(false);
  const [linkBad, setLinkBad] = useState(false);
  const [roasteryName, setRoasteryName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const goTo = m => { setMode(m); setError(""); setSent(false); };

  const submit = async e => {
    e.preventDefault();
    setError("");
    if (mode === "reset" && password !== confirm) { setError("The two passwords don't match."); return; }
    setBusy(true);
    try {
      const path = { login: "/roaster/login", signup: "/roaster/signup", forgot: "/roaster/forgot-password", reset: "/roaster/reset-password" }[mode];
      const body = mode === "login" ? { email, password }
        : mode === "signup" ? { email, password, roastery_name: roasteryName }
        : mode === "forgot" ? { email }
        : { token: resetToken, password };
      const res = await fetch(`${AUTH_WORKER_URL}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Something went wrong — try again."); setLinkBad(mode === "reset"); setBusy(false); return; }
      if (mode === "forgot") { setSent(true); setBusy(false); return; }
      // A successful reset logs straight in, same as login — drop the used token from the address bar
      if (mode === "reset") window.history.replaceState(null, "", window.location.pathname);
      // Signup/login now hand the token straight back in the response body
      // — no follow-up /me call needed, and nothing here depends on the
      // cookie actually surviving the cross-site round trip.
      saveRoasterToken(data.token);
      setSyncToken(data.token);
      onAuthed({ id: data.id, email: data.email, roastery_name: data.roastery_name });
    } catch {
      setError("Couldn't reach the server — check your connection and try again.");
      setBusy(false);
    }
  };

  const tabBtn = active => ({
    flex: 1, fontFamily: fontSans, fontSize: 12, fontWeight: 600, letterSpacing: "0.04em",
    padding: "9px 0", borderRadius: 6, cursor: "pointer",
    background: active ? "#fff" : "none",
    border: active ? `1px solid ${C.border}` : "1px solid transparent",
    color: active ? C.ink : C.inkFaint,
    boxShadow: active ? "0 1px 2px rgba(28,24,20,0.06)" : "none",
  });
  const linkBtn = { background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: fontSans, fontSize: 12, color: C.amber, textDecoration: "underline" };

  return (
    <div style={{ minHeight: "100vh", background: C.cream, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 380 }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <BeanMark size={40} />
          <div style={{ fontFamily: fontSerif, fontSize: 20, color: C.ink, marginTop: 10, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            FDC Roasting Hub
          </div>
        </div>
        <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 10, padding: 28 }}>
          {(mode === "login" || mode === "signup") && (
            <div style={{ display: "flex", gap: 4, background: C.cream3, borderRadius: 8, padding: 3, marginBottom: 22 }}>
              <button type="button" onClick={() => goTo("login")} style={tabBtn(mode === "login")}>Log in</button>
              <button type="button" onClick={() => goTo("signup")} style={tabBtn(mode === "signup")}>Create account</button>
            </div>
          )}
          {mode === "forgot" && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontFamily: fontSerif, fontSize: 20, color: C.ink, marginBottom: 6 }}>Reset your password</div>
              <div style={s.hint}>Enter your email and we'll send you a link to set a new password.</div>
            </div>
          )}
          {mode === "reset" && (
            <div style={{ fontFamily: fontSerif, fontSize: 20, color: C.ink, marginBottom: 18 }}>Choose a new password</div>
          )}
          {mode === "forgot" && sent ? (
            <div>
              <div style={{ fontFamily: fontSans, fontSize: 13, color: C.ink, lineHeight: 1.6, marginBottom: 18 }}>
                If that email has an account, we've sent a link. It works once, for 1 hour.
              </div>
              <button type="button" onClick={() => goTo("login")} style={linkBtn}>← Back to log in</button>
            </div>
          ) : (
          <form onSubmit={submit}>
            {mode === "signup" && (
              <div style={{ marginBottom: 14 }}>
                <div style={s.fieldLabel}>Roastery name</div>
                <input required style={s.input} value={roasteryName} onChange={e => setRoasteryName(e.target.value)} placeholder="e.g. Skelligs Coffee" />
              </div>
            )}
            {mode !== "reset" && (
              <div style={{ marginBottom: mode === "forgot" ? 20 : 14 }}>
                <div style={s.fieldLabel}>Email</div>
                <input required type="email" style={s.input} value={email} onChange={e => setEmail(e.target.value)} />
              </div>
            )}
            {mode !== "forgot" && (
              <div style={{ marginBottom: mode === "reset" ? 14 : 20 }}>
                <div style={s.fieldLabel}>{mode === "reset" ? "New password" : "Password"}</div>
                <input required type="password" minLength={8} style={s.input} value={password} onChange={e => setPassword(e.target.value)} />
                {mode === "signup" && <div style={s.hint}>At least 8 characters</div>}
              </div>
            )}
            {mode === "reset" && (
              <div style={{ marginBottom: 20 }}>
                <div style={s.fieldLabel}>Confirm new password</div>
                <input required type="password" minLength={8} style={s.input} value={confirm} onChange={e => setConfirm(e.target.value)} />
                <div style={s.hint}>At least 8 characters</div>
              </div>
            )}
            {error && <div style={{ color: C.red, fontFamily: fontSans, fontSize: 12, marginBottom: 14, lineHeight: 1.5 }}>{error}</div>}
            <button type="submit" disabled={busy} style={{
              width: "100%", background: C.amber, color: "#fff", border: "none", borderRadius: 6,
              padding: "12px", fontFamily: fontSans, fontWeight: 700, fontSize: 14,
              cursor: busy ? "default" : "pointer", opacity: busy ? 0.7 : 1,
            }}>
              {busy ? "Please wait…" : { login: "Log in", signup: "Create account", forgot: "Send reset link", reset: "Save and log in" }[mode]}
            </button>
            {mode === "login" && (
              <div style={{ textAlign: "center", marginTop: 14 }}>
                <button type="button" onClick={() => goTo("forgot")} style={linkBtn}>Forgot password?</button>
              </div>
            )}
            {mode === "forgot" && (
              <div style={{ textAlign: "center", marginTop: 14 }}>
                <button type="button" onClick={() => goTo("login")} style={linkBtn}>← Back to log in</button>
              </div>
            )}
            {mode === "reset" && linkBad && (
              <div style={{ textAlign: "center", marginTop: 14 }}>
                <button type="button" onClick={() => { window.history.replaceState(null, "", window.location.pathname); goTo("forgot"); }} style={linkBtn}>Request a new link</button>
              </div>
            )}
          </form>
          )}
        </div>
      </div>
    </div>
  );
}


function BeanMark({ size = 42 }) {
  return (
    <svg
      width={size}
      height={size * 1.14}
      viewBox="0 0 48 54"
      fill="none"
      role="img"
      aria-label="FDC Roasting Hub"
      style={{ flexShrink: 0, display: "block" }}
    >
      <ellipse
        cx="24" cy="27" rx="15" ry="20"
        transform="rotate(14 24 27)"
        stroke={C.amber} strokeWidth="2.2"
      />
      <path
        d="M25 9 C 15 20, 32 33, 21 46"
        stroke={C.amber} strokeWidth="2.2" strokeLinecap="round"
      />
      <path
        d="M30.5 10.5 C 20.5 21.5, 37.5 34.5, 26.5 47"
        stroke={C.amber} strokeWidth="2.2" strokeLinecap="round"
      />
    </svg>
  );
}

// ── REFERENCE DATA ────────────────────────────────────────────────────────────

// Inventory helpers — inventory is the single source of truth for green coffees
const INVENTORY_DEFAULTS = [
 { id:1, name:"Carmo — Brazil", origin:"Brazil", process:"Natural", masl:"1100", moisture:"11.0", cultivar:"Yellow Bourbon", bagWeight:"60", bags:"1", costPerKg:"", arrived:"" },
 { id:2, name:"Pinhal — Brazil", origin:"Brazil", process:"Natural", masl:"1000", moisture:"11.0", cultivar:"Mundo Novo", bagWeight:"60", bags:"1", costPerKg:"", arrived:"" },
 { id:3, name:"Pocos de Caldas Decaf", origin:"Brazil — Pocos de Caldas", process:"Decaf — Sugarcane (EA)", masl:"1000", moisture:"11.5", cultivar:"Catuaí", bagWeight:"60", bags:"1", costPerKg:"", arrived:"" },
 { id:4, name:"El Faro — Colombia", origin:"Colombia", process:"Washed", masl:"1800", moisture:"10.5", cultivar:"Pink Bourbon", bagWeight:"60", bags:"1", costPerKg:"", arrived:"" },
 { id:5, name:"Halo Beriti — Ethiopia", origin:"Ethiopia", process:"Washed", masl:"2000", moisture:"10.8", cultivar:"Heirloom (mixed)", bagWeight:"60", bags:"1", costPerKg:"", arrived:"" },
];

function loadInventory() {
 try {
 const saved = localStorage.getItem("fdc_inventory");
 return saved ? JSON.parse(saved) : INVENTORY_DEFAULTS;
 } catch { return INVENTORY_DEFAULTS; }
}

function saveInventory(items) {
 localStorage.setItem("fdc_inventory", JSON.stringify(items));
}

// ── MACHINE PROFILE ──────────────────────────────────────────────────────────
// Everything that used to be hardcoded as "the Loring's numbers" now lives in
// one per-account profile, so a second machine brand only needs a new entry
// here rather than a hunt through the recipe engine, roast mode and the fault
// reference page. Bean Temperature thresholds are from the Loring Owners
// Manual p. 64 (1007056 Rev B): Warning at 515°F (268°C), Fault at 525°F
// (273°C) — factory-set on the Loring, editable here for any other brand. A
// separate, independent High Temperature Limit thermocouple on the Loring
// kills the roaster at 1700°F stack temp — manual reset button inside the
// electrical panel; that's machine-specific behaviour, not part of this
// profile.
const MACHINE_PROFILE_KEY = "fdc_machine_profile";
const DEFAULT_MACHINE_PROFILE = {
  brand: "Loring", model: "S70 Peregrine",
  connectionType: "native", // "native" | "probe-tap" | "manual"
  burnerFloorPct: 20, maillardCapPct: 78,
  beanWarnC: 268, beanFaultC: 273,
};
function loadMachineProfile() {
  try {
    const raw = localStorage.getItem(MACHINE_PROFILE_KEY);
    if (raw) return { ...DEFAULT_MACHINE_PROFILE, ...JSON.parse(raw) };
  } catch {}
  return { ...DEFAULT_MACHINE_PROFILE };
}
// M is a live, mutable reference read throughout the recipe engine and roast
// charts — same pattern as the C theme palette above. Mutating it in place
// means every place that already wrote `M.beanFaultC` etc. sees the update
// the moment applyMachineProfile runs, without threading a prop everywhere.
let M = loadMachineProfile();
function applyMachineProfile(profile) { Object.assign(M, profile); }
function saveMachineProfile(profile) {
  localStorage.setItem(MACHINE_PROFILE_KEY, JSON.stringify(profile));
  applyMachineProfile(profile);
}

// ── CLEANING SCHEDULE ─────────────────────────────────────────────────────────
// Sourced from the Loring Roaster Owners Manual (doc 1007056 Rev B),
// "Recommended Maintenance" pp. 97–105 + "Suggested Maintenance Schedule" p. 106.
// Intervals below are Loring's own. Drum Paddle Drive Bearing interval is the
// S70-specific one (annual; S35 is 2-yearly, S15 5-yearly).
const CLEANING_DEFAULTS = [
 { id:"c1", freq:"Daily", task:"Empty the Chaff Barrel", days:1,
 note:"Every ~10 full batches during the day (less often on smaller batches), and always at end of day. Never let chaff exceed the Full mark — about 2/3 of the way up the barrel." },
 { id:"c2", freq:"Daily", task:"End-of-day Cool-Down and full power-off", days:1,
 note:"Tap COOL DOWN on the Main Roast Screen, let the sequence complete, then power the roaster fully off including utilities." },

 { id:"c3", freq:"Weekly", task:"Clean the Cooling Tray", days:7,
 note:"Power down and cool. Pull the Mixer Paddle pin and assembly (mind the washer stack), remove the two knobs and screen stay, fold the screen along its hinge and lift out, vacuum, food-grade cleaners only." },
 { id:"c4", freq:"Weekly", task:"Clean under the Chaff Barrel Lid", days:7,
 note:"Wipe or scrape combustible material off the underside of the lid every time the barrel is emptied for this task. Roaster stays powered on, burner off via Cool Down. Two-person job with the cyclone clean." },
 { id:"c5", freq:"Weekly", task:"Clean the cyclone outlet / Burner Hole into the Chaff Barrel", days:7,
 note:"Stiff bottle brush around the cyclone outlet. Loring flags this one hard: debris buildup here can cause a serious malfunction if not cleaned regularly." },
 { id:"c6", freq:"Weekly", task:"Test the Chaff Quench Spray Nozzle", days:7,
 note:"Second person taps CHAFF QUENCH while you hold a container at the nozzle. Look for a 120° spray pattern; rated 3.8 L/min, so a 1 L container fills in ~15 s." },
 { id:"c7", freq:"Weekly", task:"Clean the Cooler Ducts", days:7,
 note:"Power down and cool. Right Side Access Panel off, un-clamp the end cap on the 90° elbow, vacuum out fine debris and bean chips." },
 { id:"c8", freq:"Weekly", task:"Clean the Auto Hopper Blower Vacuum Filter", days:7,
 note:"Release tabs on the filter housing, pull filter, tap / roll / shake out green-bean dust. Compressed air from the inside out if needed. Replace the filter once cleaning stops working." },
 { id:"c9", freq:"Weekly", task:"Clean the Mini Cyclone cartridge filter and container", days:7,
 note:"Tap out the dust (compressed air inside-out, outdoors, with a respirator). Manual says the container under the Mini Cyclone needs it about twice a month depending on use." },

 { id:"c10", freq:"Monthly", task:"Clean the Cyclone Spray Nozzle (Process Quench)", days:30,
 note:"Two-person job, roaster on but cooled via COOL DOWN. Remove the plate (4 thumb knobs), hold QUENCH PROCESS 15 s — container should fill halfway, conic spray pattern. Don't disturb the small vane inside the nozzle." },
 { id:"c11", freq:"Monthly", task:"Clean the CAB (Combustion Air Blower) filters", days:30,
 note:"Vacuum or low-flow compressed air on settled dust. Replace the CAB filter itself every 6 months — separate task below." },
 { id:"c12", freq:"Monthly", task:"Clean behind the Drum Screen", days:30,
 note:"Remove the spring-loaded hex screws on the rectangular Inspection Plate at the back of the drum (mind the gasket), reach in and vacuum out particulate." },
 { id:"c13", freq:"Monthly", task:"Inspect the Purge Gate", days:30,
 note:"Power and utilities off, cooled. Check it swings freely; if sticky, one drop of high-temp silicone oil on upper and lower bushings and work the gate by hand 10–12 times." },

 { id:"c14", freq:"2–6 Months", task:"Clean the Cooler Fan", days:90,
 note:"Disconnect power wires at the fan motor, remove the mounting nuts (top nuts last), lift motor + fan wheel out as one unit — never separate fan from motor or the balancing goes. Scrape residue off wheel and housing. Dark roasts = more residue = shorter interval." },
 { id:"c15", freq:"2–6 Months", task:"Deep clean the Cooler Ducts", days:90,
 note:"As per weekly duct clean, but remove all sections of cooler tubing and immerse in hot water (~60°C / 140°F) — buildup lifts far more easily hot. Light scrub." },

 { id:"c16", freq:"6 Months", task:"Inspect the Cooler Vent Stacks", days:182,
 note:"Full vent and stack system from the Cooler Fan housing to the exit point outside the building. Method depends on the install — chimney brush from the roof, or pull quick-fit sections and clean separately. Walls of the stack should be free of buildup after." },
 { id:"c17", freq:"6 Months", task:"Replace the CAB filters", days:182,
 note:"Replacement (not just cleaning) is on a 6-month cycle per the manual. Keep spares on hand — they're on Loring's recommended replacement-parts list." },

 { id:"c18", freq:"Annual", task:"Clean the Green Bean Hopper", days:365,
 note:"Two people + stepladder. Lid off, air hose quick coupler and vacuum hose off, 4 mounting bolts loose, hopper off. Extreme care with the funnel opening at the bottom — the high-temp fibreglass gasket must stay in place for the vacuum seal (1-inch self-adhesive spare in the Spare Parts Kit if it comes away)." },
 { id:"c19", freq:"Annual", task:"Inspect the Drum Paddle Drive Bearings", days:365,
 note:"S70 interval is annual (S35 every 2 years, S15 every 5). Bearings are on the outside of the roaster — contact Loring for model-specific inspection steps and replacement parts." },
 { id:"c20", freq:"Annual", task:"Year End Maintenance Checklist / full service", days:365,
 note:"The manual's full checklist (pp. 108–111): bean cart grounding, burner + electrode inspection by mirror, chaff barrel fire signs, Eclipse regulator, cooler tray paddle wear, drum bearing clearances front and rear, thermocouple placement, E-Stop test, gas pressure stable at 20% burner, flame stability 20→100% in 20% steps." },

 { id:"c21", freq:"As needed", task:"Clean roaster surfaces", days:0,
 note:"Non-abrasive cleaner only on the polished stainless — anything abrasive scratches it." },
 { id:"c22", freq:"As needed", task:"Oil the Drum Paddle Bearings", days:0,
 note:"Only if the front or rear bearing starts squeaking: 3–4 drops of high-temperature silicone oil between the black insert and the stainless sleeve. Oil available direct from Loring." },
];

function loadCleaning() {
 try {
 // v2 key — schedule rebuilt from the Loring manual, so prior saved
 // placeholder tasks (fdc_cleaning) are deliberately not migrated.
 const saved = localStorage.getItem("fdc_cleaning_v2");
 return saved ? JSON.parse(saved) : CLEANING_DEFAULTS.map(t => ({ ...t, lastDone: null }));
 } catch { return CLEANING_DEFAULTS.map(t => ({ ...t, lastDone: null })); }
}

function saveCleaning(items) {
 localStorage.setItem("fdc_cleaning_v2", JSON.stringify(items));
}

function CleaningSchedule() {
 const [tasks, setTasks] = useState(loadCleaning);
 const [showAdd, setShowAdd] = useState(false);
 const [expanded, setExpanded] = useState(null);
 const [newTask, setNewTask] = useState({ freq:"Daily", task:"", days:1 });

 const daysSince = (dateStr) => {
 if (!dateStr) return Infinity;
 return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
 };

 const markDone = (id) => {
 const updated = tasks.map(t => t.id === id ? { ...t, lastDone: new Date().toISOString() } : t);
 setTasks(updated);
 saveCleaning(updated);
 };

 const removeTask = (id) => {
 if (!window.confirm("Remove this task from the schedule?")) return;
 const updated = tasks.filter(t => t.id !== id);
 setTasks(updated);
 saveCleaning(updated);
 };

 const addTask = () => {
 if (!newTask.task.trim()) return;
 const updated = [...tasks, { id:"c"+Date.now(), freq:newTask.freq, task:newTask.task, days:parseInt(newTask.days)||1, lastDone:null }];
 setTasks(updated);
 saveCleaning(updated);
 setNewTask({ freq:"Daily", task:"", days:1 });
 setShowAdd(false);
 };

 const FREQ_ORDER = ["Daily","Weekly","Monthly","2–6 Months","6 Months","Annual","As needed"];
 const grouped = FREQ_ORDER.map(freq => ({ freq, items: tasks.filter(t => t.freq === freq) })).filter(g => g.items.length);

 const overdueCount = tasks.filter(t => t.days > 0 && daysSince(t.lastDone) > t.days).length;

 return (
 <div>
 <div style={{fontFamily:fontSans,fontSize:11,color:C.inkFaint,marginBottom:4,lineHeight:1.5}}>
 Intervals from the Loring Roaster Owners Manual (1007056 Rev B, pp. 97–106) — Recommended Maintenance and Loring's own Suggested Maintenance Schedule, with the S70-specific bearing interval. Tap a task for the manual's procedure notes.
 </div>
 <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:20,marginTop:14}}>
 {[
 ["Total tasks", tasks.length],
 ["Overdue", overdueCount],
 ["On track", tasks.length - overdueCount],
 ].map(([label,val]) => (
 <div key={label} style={{background:C.cream2,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 16px"}}>
 <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>{label}</div>
 <div style={{fontFamily:fontSerif,fontSize:24,color: label==="Overdue"&&val>0 ? C.red : C.ink}}>{val}</div>
 </div>
 ))}
 </div>

 {grouped.map(({freq, items}) => (
 <div key={freq} style={{marginBottom:20}}>
 <div style={{fontFamily:fontSans,fontSize:10,fontWeight:700,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:8}}>{freq}</div>
 <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden"}}>
 {items.map((t,i) => {
 const since = daysSince(t.lastDone);
 const asNeeded = !t.days || t.days === 0;
 const overdue = !asNeeded && since > t.days;
 const dueSoon = !asNeeded && !overdue && since > t.days * 0.75;
 const isOpen = expanded === t.id;
 return (
 <div key={t.id} style={{borderBottom: i<items.length-1?`1px solid ${C.borderSoft}`:"none",background: overdue ? "#FDEAEA" : "transparent"}}>
 <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",cursor:t.note?"pointer":"default"}}
 onClick={()=> t.note && setExpanded(isOpen ? null : t.id)}>
 <button onClick={(e)=>{e.stopPropagation();markDone(t.id);}} style={{width:22,height:22,borderRadius:5,border:`1.5px solid ${overdue?C.red:C.border}`,background:"#fff",cursor:"pointer",flexShrink:0}} title="Mark done today"/>
 <div style={{flex:1}}>
 <div style={{fontFamily:fontSans,fontSize:13,color:C.ink}}>{t.task}{t.note && <span style={{color:C.inkFaint,fontSize:11,marginLeft:6}}>{isOpen?"▾":"▸"}</span>}</div>
 <div style={{fontFamily:fontSans,fontSize:11,color: overdue?C.red:dueSoon?C.amber:C.inkFaint}}>
 {asNeeded
 ? (t.lastDone ? `Last done ${since}d ago — as needed` : "As needed — log when done")
 : <>{t.lastDone ? `Last done ${since}d ago` : "Never logged"} — due every {t.days}d {overdue?"· OVERDUE":dueSoon?"· due soon":""}</>}
 </div>
 </div>
 <button onClick={(e)=>{e.stopPropagation();removeTask(t.id);}} style={{background:"none",border:"none",cursor:"pointer",color:C.inkFaint,fontSize:16}}>×</button>
 </div>
 {isOpen && t.note && (
 <div style={{padding:"0 14px 12px 48px",fontFamily:fontSans,fontSize:12,color:C.inkMid,lineHeight:1.55}}>{t.note}</div>
 )}
 </div>
 );
 })}
 </div>
 </div>
 ))}

 {showAdd ? (
 <div style={{border:`1px solid ${C.border}`,borderRadius:8,padding:16,marginBottom:16}}>
 <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
 <div>
 <div style={s.fieldLabel}>Frequency</div>
 <select style={s.input} value={newTask.freq} onChange={e=>{
 const f = e.target.value;
 const days = {"Daily":1,"Weekly":7,"Monthly":30,"2–6 Months":90,"6 Months":182,"Annual":365,"As needed":0}[f];
 setNewTask(n=>({...n,freq:f,days}));
 }}>
 {FREQ_ORDER.map(f=><option key={f}>{f}</option>)}
 </select>
 </div>
 <div style={{gridColumn:"span 2"}}>
 <div style={s.fieldLabel}>Task</div>
 <input style={s.input} value={newTask.task} onChange={e=>setNewTask(n=>({...n,task:e.target.value}))} placeholder="e.g. Check drive belt tension"/>
 </div>
 </div>
 <div style={{display:"flex",gap:8}}>
 <button style={s.btnPrimary} onClick={addTask}>Add task</button>
 <button style={s.btn} onClick={()=>setShowAdd(false)}>Cancel</button>
 </div>
 </div>
 ) : (
 <button style={s.btnPrimary} onClick={()=>setShowAdd(true)}>+ Add task</button>
 )}

 <YearEndChecklist/>
 <ReplacementParts/>
 </div>
 );
}


// ── REPLACEMENT PARTS ─────────────────────────────────────────────────────────
// Loring's keep-on-hand list, Owners Manual p. 107 (1007056 Rev B) — in
// addition to the Spare Parts Kit that ships with the roaster. Order from
// Loring as needed and allow lead time.
const LORING_PARTS = [
 { part:"Burner Tip and Electrode", note:"Inspect annually with mirror — cracks and wear" },
 { part:"Teflon Wipers", note:"Cooler tray paddle assembly — wear item" },
 { part:"Cooling Tray Screen", note:"" },
 { part:"Mini Cyclone Filter", note:"Replace when tap-out / compressed-air cleaning stops working" },
 { part:"Drum Paddle Bearings", note:"S70: inspect annually" },
 { part:"Combustion Air Filters", note:"Replace every 6 months" },
 { part:"1/2\u2033 Fibreglass Adhesive-Backed Gasket Material", note:"" },
 { part:"1\u2033 Fibreglass Adhesive-Backed Gasket Material", note:"Green bean hopper funnel seal" },
 { part:"Cyclone Burner Sight Glass", note:"" },
 { part:"Bean Thermocouple", note:"Check placement at annual service" },
];

function ReplacementParts() {
 const [open, setOpen] = useState(false);
 return (
 <div style={{marginTop:16}}>
 <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",padding:"12px 16px",background:C.cream2,border:`1px solid ${C.border}`,borderRadius:8}} onClick={()=>setOpen(o=>!o)}>
 <div>
 <div style={{fontFamily:fontSans,fontSize:10,fontWeight:700,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.1em"}}>Replacement parts to keep on hand {open?"▾":"▸"}</div>
 <div style={{fontFamily:fontSans,fontSize:11,color:C.inkFaint,marginTop:2}}>Loring's recommended spares list (manual p. 107) — order ahead, allow lead time.</div>
 </div>
 </div>
 {open && (
 <div style={{border:`1px solid ${C.border}`,borderTop:"none",borderRadius:"0 0 8px 8px",overflow:"hidden"}}>
 {LORING_PARTS.map((p,i) => (
 <div key={p.part} style={{display:"flex",justifyContent:"space-between",gap:16,padding:"9px 16px",borderBottom: i<LORING_PARTS.length-1?`1px solid ${C.borderSoft}`:"none",background: i%2 ? C.cream : "transparent"}}>
 <div style={{fontFamily:fontSans,fontSize:12.5,color:C.ink}}>{p.part}</div>
 {p.note && <div style={{fontFamily:fontSans,fontSize:11,color:C.inkFaint,textAlign:"right"}}>{p.note}</div>}
 </div>
 ))}
 </div>
 )}
 </div>
 );
}

// ── PREMISES CLEANING (the building, not the roaster itself) ────────────────
// HACCP-style cleaning record for the building — floors, drains, waste,
// pest control, storage, staff facilities. Separate from the Loring's own
// Cleaning tab above. Sensible starter list, fully editable (add/remove/
// reorder) since Ger manages this list himself going forward.
const PREMISES_FREQ_DAYS = { "Daily": 1, "Weekly": 7, "Monthly": 30, "Quarterly": 91, "Annual": 365 };
const PREMISES_FREQ_ORDER = ["Daily", "Weekly", "Monthly", "Quarterly", "Annual"];

const PREMISES_DEFAULTS = [
  { id: "p1", freq: "Daily", task: "Sweep & mop production floor", days: 1 },
  { id: "p2", freq: "Daily", task: "Empty & sanitise waste/chaff bins", days: 1 },
  { id: "p3", freq: "Daily", task: "Clean & restock hand-wash station", days: 1 },
  { id: "p4", freq: "Daily", task: "Wipe down packaging/work surfaces", days: 1 },

  { id: "p5", freq: "Weekly", task: "Deep clean floor drains", days: 7 },
  { id: "p6", freq: "Weekly", task: "Check pest control bait stations/traps", days: 7 },
  { id: "p7", freq: "Weekly", task: "Clean staff facilities (toilet, kitchenette)", days: 7 },
  { id: "p8", freq: "Weekly", task: "Wipe down walls/surfaces in production area", days: 7 },

  { id: "p9", freq: "Monthly", task: "Full pest control inspection, all areas", days: 30 },
  { id: "p10", freq: "Monthly", task: "Check storage areas for damp, pests, spillage", days: 30 },
  { id: "p11", freq: "Monthly", task: "Clean light fittings and high surfaces", days: 30 },
  { id: "p12", freq: "Monthly", task: "Check first aid kit and fire extinguisher access", days: 30 },

  { id: "p13", freq: "Quarterly", task: "Deep clean roof space / high-level dust", days: 91 },
  { id: "p14", freq: "Quarterly", task: "Review pest control contractor visit records", days: 91 },
  { id: "p15", freq: "Quarterly", task: "Inspect building exterior for pest/damp entry points", days: 91 },

  { id: "p16", freq: "Annual", task: "Full building deep clean", days: 365 },
  { id: "p17", freq: "Annual", task: "Review & update HACCP building risk assessment", days: 365 },
  { id: "p18", freq: "Annual", task: "Pest control annual contract review", days: 365 },
];

function loadPremisesTasks() {
  try {
    const saved = localStorage.getItem("fdc_premises_tasks");
    return saved ? JSON.parse(saved) : PREMISES_DEFAULTS.map(t => ({ ...t, lastDone: null, completedBy: "" }));
  } catch { return PREMISES_DEFAULTS.map(t => ({ ...t, lastDone: null, completedBy: "" })); }
}
function savePremisesTasks(items) { localStorage.setItem("fdc_premises_tasks", JSON.stringify(items)); }

function loadPremisesActions() {
  try { return JSON.parse(localStorage.getItem("fdc_premises_actions") || "[]"); } catch { return []; }
}
function savePremisesActions(items) { localStorage.setItem("fdc_premises_actions", JSON.stringify(items)); }

// Shared with the Today tab, so overdue premises tasks show up there too.
function overduePremisesTasks() {
  const tasks = loadPremisesTasks();
  const daysSince = (dateStr) => !dateStr ? Infinity : Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  return tasks.filter(t => t.days > 0 && daysSince(t.lastDone) > t.days);
}

function PremisesCleaningTab() {
  const [tasks, setTasks] = useState(loadPremisesTasks);
  const [actions, setActions] = useState(loadPremisesActions);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState({ freq: "Daily", task: "", days: 1 });
  const [showAddAction, setShowAddAction] = useState(false);
  const [newAction, setNewAction] = useState({ dateRaised: localIsoDate(), area: "", issue: "", actionTaken: "" });

  const daysSince = (dateStr) => !dateStr ? Infinity : Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);

  const markDone = (id) => {
    const by = window.prompt("Completed by (name or initials):", "");
    if (by === null) return;
    const updated = tasks.map(t => t.id === id ? { ...t, lastDone: new Date().toISOString(), completedBy: by.trim() } : t);
    setTasks(updated);
    savePremisesTasks(updated);
  };

  const removeTask = (id) => {
    if (!window.confirm("Remove this task from the premises cleaning list?")) return;
    const updated = tasks.filter(t => t.id !== id);
    setTasks(updated);
    savePremisesTasks(updated);
  };

  const moveTask = (id, dir) => {
    const freq = tasks.find(t => t.id === id).freq;
    const sameFreq = tasks.filter(t => t.freq === freq);
    const posInGroup = sameFreq.findIndex(t => t.id === id);
    const swapWith = sameFreq[posInGroup + dir];
    if (!swapWith) return;
    const a = tasks.findIndex(t => t.id === id);
    const b = tasks.findIndex(t => t.id === swapWith.id);
    const updated = [...tasks];
    [updated[a], updated[b]] = [updated[b], updated[a]];
    setTasks(updated);
    savePremisesTasks(updated);
  };

  const addTask = () => {
    if (!newTask.task.trim()) return;
    const updated = [...tasks, { id: "pc" + Date.now(), freq: newTask.freq, task: newTask.task, days: parseInt(newTask.days) || 1, lastDone: null, completedBy: "" }];
    setTasks(updated);
    savePremisesTasks(updated);
    setNewTask({ freq: "Daily", task: "", days: 1 });
    setShowAddTask(false);
  };

  const addAction = () => {
    if (!newAction.issue.trim()) return;
    const updated = [...actions, { id: "pa" + Date.now(), ...newAction, closedBy: "", dateClosed: null, status: "open" }];
    setActions(updated);
    savePremisesActions(updated);
    setNewAction({ dateRaised: localIsoDate(), area: "", issue: "", actionTaken: "" });
    setShowAddAction(false);
  };

  const closeAction = (id) => {
    const current = actions.find(a => a.id === id);
    const action = current.actionTaken && current.actionTaken.trim() ? current.actionTaken : window.prompt("Action taken:", "");
    if (action === null) return;
    const by = window.prompt("Closed by (name or initials):", "");
    if (by === null) return;
    const updated = actions.map(a => a.id === id ? { ...a, actionTaken: action, closedBy: by.trim(), dateClosed: localIsoDate(), status: "closed" } : a);
    setActions(updated);
    savePremisesActions(updated);
  };

  const removeAction = (id) => {
    if (!window.confirm("Remove this corrective action record?")) return;
    const updated = actions.filter(a => a.id !== id);
    setActions(updated);
    savePremisesActions(updated);
  };

  const grouped = PREMISES_FREQ_ORDER.map(freq => ({ freq, items: tasks.filter(t => t.freq === freq) })).filter(g => g.items.length);
  const overdueCount = tasks.filter(t => t.days > 0 && daysSince(t.lastDone) > t.days).length;
  const dueTodayCount = tasks.filter(t => t.days > 0 && daysSince(t.lastDone) === t.days).length;
  const openActionsCount = actions.filter(a => a.status === "open").length;

  return (
    <div>
      <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 4, lineHeight: 1.5 }}>
        HACCP-style cleaning record for the building — floors, drains, waste, pest control, and staff facilities. Separate from the Loring's own Cleaning tab. Fully editable — add, remove, or reorder tasks as your process changes.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 20, marginTop: 14 }}>
        {[
          ["Due today", dueTodayCount],
          ["Overdue", overdueCount],
          ["Open corrective actions", openActionsCount],
        ].map(([label, val]) => (
          <div key={label} style={{ background: C.cream2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 16px" }}>
            <div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{label}</div>
            <div style={{ fontFamily: fontSerif, fontSize: 24, color: (label !== "Due today" && val > 0) ? C.red : C.ink }}>{val}</div>
          </div>
        ))}
      </div>

      {grouped.map(({ freq, items }) => (
        <div key={freq} style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>{freq}</div>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
            {items.map((t, i) => {
              const since = daysSince(t.lastDone);
              const overdue = since > t.days;
              const dueSoon = !overdue && since > t.days * 0.75;
              return (
                <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: i < items.length - 1 ? `1px solid ${C.borderSoft}` : "none", background: overdue ? "#FDEAEA" : "transparent" }}>
                  <button onClick={() => markDone(t.id)} style={{ width: 22, height: 22, borderRadius: 5, border: `1.5px solid ${overdue ? C.red : C.border}`, background: "#fff", cursor: "pointer", flexShrink: 0 }} title="Mark done today" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: fontSans, fontSize: 13, color: C.ink }}>{t.task}</div>
                    <div style={{ fontFamily: fontSans, fontSize: 11, color: overdue ? C.red : dueSoon ? C.amber : C.inkFaint }}>
                      {t.lastDone ? `Last done ${since}d ago${t.completedBy ? ` by ${t.completedBy}` : ""}` : "Never logged"} — due every {t.days}d {overdue ? "· OVERDUE" : dueSoon ? "· due soon" : ""}
                    </div>
                  </div>
                  <button onClick={() => moveTask(t.id, -1)} disabled={i === 0} style={{ background: "none", border: "none", cursor: i === 0 ? "default" : "pointer", color: i === 0 ? C.borderSoft : C.inkFaint, fontSize: 13 }}>▲</button>
                  <button onClick={() => moveTask(t.id, 1)} disabled={i === items.length - 1} style={{ background: "none", border: "none", cursor: i === items.length - 1 ? "default" : "pointer", color: i === items.length - 1 ? C.borderSoft : C.inkFaint, fontSize: 13 }}>▼</button>
                  <button onClick={() => removeTask(t.id)} style={{ background: "none", border: "none", cursor: "pointer", color: C.inkFaint, fontSize: 16 }}>×</button>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {showAddTask ? (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 16, marginBottom: 24 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 10, marginBottom: 10 }}>
            <div>
              <div style={s.fieldLabel}>Frequency</div>
              <select style={s.input} value={newTask.freq} onChange={e => {
                const f = e.target.value;
                setNewTask(n => ({ ...n, freq: f, days: PREMISES_FREQ_DAYS[f] }));
              }}>
                {PREMISES_FREQ_ORDER.map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <div style={s.fieldLabel}>Task</div>
              <input style={s.input} value={newTask.task} onChange={e => setNewTask(n => ({ ...n, task: e.target.value }))} placeholder="e.g. Check drain covers for blockage" />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={s.btnPrimary} onClick={addTask}>Add task</button>
            <button style={s.btn} onClick={() => setShowAddTask(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button style={{ ...s.btnPrimary, marginBottom: 24 }} onClick={() => setShowAddTask(true)}>+ Add task</button>
      )}

      <div style={{ marginTop: 8, paddingTop: 20, borderTop: `1px solid ${C.border}` }}>
        <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>Corrective actions</div>

        {actions.length === 0 && (
          <div style={{ fontFamily: fontSans, fontSize: 12, color: C.inkFaint, marginBottom: 12 }}>No corrective actions logged.</div>
        )}

        {actions.length > 0 && (
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden", marginBottom: 16 }}>
            {actions.slice().reverse().map((a, i) => (
              <div key={a.id} style={{ padding: "10px 14px", borderBottom: i < actions.length - 1 ? `1px solid ${C.borderSoft}` : "none", background: a.status === "open" ? "#FDEAEA" : "transparent" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: fontSans, fontSize: 13, color: C.ink }}>{a.area ? `${a.area} — ` : ""}{a.issue}</div>
                    <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginTop: 2 }}>
                      Raised {dmy(a.dateRaised)}{a.actionTaken ? ` — ${a.actionTaken}` : ""}{a.status === "closed" ? ` — closed by ${a.closedBy} on ${dmy(a.dateClosed)}` : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexShrink: 0 }}>
                    {a.status === "open" && <button style={{ ...s.btn, fontSize: 11, padding: "4px 10px" }} onClick={() => closeAction(a.id)}>Close</button>}
                    <button onClick={() => removeAction(a.id)} style={{ background: "none", border: "none", cursor: "pointer", color: C.inkFaint, fontSize: 16 }}>×</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showAddAction ? (
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 10, marginBottom: 10 }}>
              <div>
                <div style={s.fieldLabel}>Date raised</div>
                <input type="date" style={s.input} value={newAction.dateRaised} onChange={e => setNewAction(n => ({ ...n, dateRaised: e.target.value }))} />
              </div>
              <div>
                <div style={s.fieldLabel}>Area</div>
                <input style={s.input} value={newAction.area} onChange={e => setNewAction(n => ({ ...n, area: e.target.value }))} placeholder="e.g. Storage" />
              </div>
              <div>
                <div style={s.fieldLabel}>Issue</div>
                <input style={s.input} value={newAction.issue} onChange={e => setNewAction(n => ({ ...n, issue: e.target.value }))} placeholder="e.g. Signs of pest activity near pallets" />
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={s.fieldLabel}>Action taken (optional — can add when you close it)</div>
              <input style={s.input} value={newAction.actionTaken} onChange={e => setNewAction(n => ({ ...n, actionTaken: e.target.value }))} placeholder="e.g. Pest control contractor called out" />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button style={s.btnPrimary} onClick={addAction}>Log corrective action</button>
              <button style={s.btn} onClick={() => setShowAddAction(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <button style={s.btnPrimary} onClick={() => setShowAddAction(true)}>+ Log corrective action</button>
        )}
      </div>
    </div>
  );
}

// ── YEAR END MAINTENANCE CHECKLIST ────────────────────────────────────────────
// Transcribed from the Loring Owners Manual pp. 108–111 (1007056 Rev B).
// Items marked rec:true have a value to record during the inspection.
// Safety per the manual: inspect while the roaster is cool, proper
// lockout/tagout, leave water, air and gas lines ON during inspection.
const YEAREND_SECTIONS = [
 { title:"Vacuum Elevator and Bean Cart", items:[
 { id:"y1", t:"Bean cart properly grounded to roaster; check grounding between bean cart hopper and bean cart frame" },
 { id:"y2", t:"Black grounding strap installed" },
 { id:"y3", t:"Inspect green bean hopper clamps and gaskets; clean out mesh basket under green bean hopper lid" },
 { id:"y4", t:"Remove green bean hopper — inspect funnel door, air ram, air hoses, and gasket between hopper and plenum" },
 { id:"y5", t:"Clean out or replace green bean vacuum filter" },
 ]},
 { title:"Burner / Electrode / Sight Glass", items:[
 { id:"y6", t:"Remove and clean cyclone sight glass" },
 { id:"y7", t:"Inspect sight glass gasket" },
 { id:"y8", t:"Inspect burner and electrode with inspection mirror — look for cracks and wear" },
 { id:"y9", t:"Burner and/or electrode replacement if necessary" },
 { id:"y10", t:"Replace Combustion Air Blower filters" },
 ]},
 { title:"Chaff Barrel and Cyclone", items:[
 { id:"y11", t:"Remove chaff barrel — inspect lid gasket, wheels, and clamps" },
 { id:"y12", t:"Look for signs of chaff barrel fires" },
 { id:"y13", t:"Inspect chaff quench hose, ignition wire, and high-temp limit wires for signs of damage and fires" },
 { id:"y14", t:"Looking up into the cyclone: inspect cyclone, incineration tube, and stack for damage or crumpling" },
 ]},
 { title:"Purge Gate Door and Air Ram", items:[
 { id:"y15", t:"Inspect air ram for leaks" },
 { id:"y16", t:"Open and close door with air ram connected, looking for sticking (air off on the S70)" },
 { id:"y17", t:"Disconnect purge gate door arm — ensure the door swings freely" },
 { id:"y18", t:"Reconnect; ensure both purge-gate-open and purge-gate-closed magnetic sensors engage in the respective positions" },
 { id:"y19", t:"Look for signs the purge gate door gasket is leaking" },
 { id:"y20", t:"Look for broken purge gate door studs" },
 { id:"y21", t:"Replace centre nuts with non-stainless nut and acorn as a jam nut" },
 ]},
 { title:"Utility Tray / Utilities", items:[
 { id:"y22", t:"In main electrical panel: check incoming power lines for loose connections; record incoming voltage", rec:true, unit:"V" },
 { id:"y23", t:"Inspect rear utility tray for leaks (water, air or gas) or damage" },
 { id:"y24", t:"If solid stainless load line from Eclipse regulator to cyclone: remove at regulator, blow compressed air back towards cyclone" },
 { id:"y25", t:"Record gas mixer and regulator settings", rec:true, unit:"" },
 { id:"y26", t:"Inspect Eclipse regulator for corrosion (remove knurled screw and spring)" },
 { id:"y27", t:"Remove gas mixer, inspect insert neck seal with flashlight — don't remove insert unless resealing is required" },
 ]},
 { title:"Cooler Tray and Fan", items:[
 { id:"y28", t:"Cooler tray discharge door stays open on its own" },
 { id:"y29", t:"Check discharge door guide rails for broken studs" },
 { id:"y30", t:"Test cooler tray paddle clutch — not too strong or weak" },
 { id:"y31", t:"Remove and inspect cooler tray paddle assembly; check Teflon and metal wipers for wear or damage" },
 { id:"y32", t:"Remove metal and Teflon washers; inspect for wear or damage" },
 { id:"y33", t:"Remove cooler tray screen and deep clean cooler tray" },
 { id:"y34", t:"Inspect cooler tray retaining ring for stripped threads — helicoil repair if needed" },
 { id:"y35", t:"Remove and deep clean all Jacobs tubing under the roaster" },
 { id:"y36", t:"Inspect gaskets for damage and wear" },
 { id:"y37", t:"Inspect cooler tray paddle motor" },
 ]},
 { title:"Roaster Drum / Paddles / Mechanicals", items:[
 { id:"y38", t:"Inspect rear bearing, black polymer sleeve, and stainless sleeve split collar" },
 { id:"y39", t:"Inspect taper lock hubs, sprockets, and chain condition" },
 { id:"y40", t:"At back of drum: check and record rear bearing axial clearance", rec:true, unit:"mm" },
 { id:"y41", t:"Remove rear access panel, clean out behind drum screen — look for excessive bean collection" },
 { id:"y42", t:"Rear screen bean blocker positioned correctly so beans don't slip past" },
 { id:"y43", t:"Check front bearing, black polymer and split collar for wear / damage" },
 { id:"y44", t:"With paddle pushed fully back: record front bearing axial clearance", rec:true, unit:"mm" },
 { id:"y45", t:"Remove drum discharge door" },
 { id:"y46", t:"Inspect bean thermocouple for damage and proper placement" },
 { id:"y47", t:"Inspect drum discharge door gasket for wear" },
 { id:"y48", t:"Inspect tryer and tryer spring for proper seal" },
 { id:"y49", t:"Record minimum paddle clearances for each flight (before and after replacing polymer bearing inserts)", rec:true, unit:"mm" },
 ]},
 { title:"Electrical Panel and Miscellaneous", items:[
 { id:"y50", t:"Inspect and clean cold exhaust duct; check all connections for smoke leaks" },
 { id:"y51", t:"Inspect electrical cabinet for damage or modifications" },
 { id:"y52", t:"User manual on site and current" },
 ]},
 { title:"Roaster Startup and Operation", items:[
 { id:"y53", t:"Power on the roaster" },
 { id:"y54", t:"LCSv2: SYSTEM menu — verify PLC REV and SCREEN REV on latest v2.x; update if out of date" },
 { id:"y55", t:"Turn on Paddle Drive" },
 { id:"y56", t:"Turn on Circ Fan" },
 { id:"y57", t:"Press E-Stop — red light alert works and motors turn off" },
 { id:"y58", t:"Reset all faults on touchscreen" },
 { id:"y59", t:"Request burner" },
 { id:"y60", t:"Watch purge gate — verify normal operation" },
 { id:"y61", t:"Watch burner through sight glass for strong spark and ignition" },
 { id:"y62", t:"Record gas pressure — stable at 20% burner", rec:true, unit:"" },
 { id:"y63", t:"Record magnehelic pressure at 20% burner", rec:true, unit:"" },
 { id:"y64", t:"Start a test roast cycle; ensure bean drop works properly" },
 { id:"y65", t:"Take burner 20% → 100% in 20% increments — flame stable throughout" },
 { id:"y66", t:"Record magnehelic reading at 100% burner (if it drops more than ~5%, retune the combustion air blower)", rec:true, unit:"" },
 { id:"y67", t:"End of Roast flasher and buzzer work" },
 { id:"y68", t:"Auto discharge works; mixer paddles turn; cooler fan motor turns on" },
 { id:"y69", t:"Idle and warm up — engage Warm Up cycle on v2" },
 { id:"y70", t:"Inspect the work light and arm" },
 { id:"y71", t:"Open discharge door by both the on-screen button and the Drop Door Override push button" },
 { id:"y72", t:"Once warm: lift green and watch Vacuum Elevator performance" },
 { id:"y73", t:"Complete a roast" },
 { id:"y74", t:"Beans cool: open cooler tray discharge chute, ensure all beans evacuate — adjust cooler tray paddles if needed" },
 { id:"y75", t:"Turn burner off and relight — ensure it lights while hot" },
 { id:"y76", t:"Put the roaster into Cool Down mode" },
 ]},
];

function loadYearEnd() {
 try {
 const saved = localStorage.getItem("fdc_yearend");
 return saved ? JSON.parse(saved) : { year: new Date().getFullYear(), by:"", checks:{}, vals:{} };
 } catch { return { year: new Date().getFullYear(), by:"", checks:{}, vals:{} }; }
}

function YearEndChecklist() {
 const [open, setOpen] = useState(false);
 const [st, setSt] = useState(loadYearEnd);
 const save = (next) => { setSt(next); localStorage.setItem("fdc_yearend", JSON.stringify(next)); };
 const toggle = id => save({ ...st, checks: { ...st.checks, [id]: !st.checks[id] } });
 const setVal = (id, v) => save({ ...st, vals: { ...st.vals, [id]: v } });

 const total = YEAREND_SECTIONS.reduce((n,s)=>n+s.items.length,0);
 const done = Object.values(st.checks).filter(Boolean).length;

 const resetAll = () => {
 if (!window.confirm("Clear all ticks and recorded values for a fresh year?")) return;
 save({ year: new Date().getFullYear(), by: st.by, checks:{}, vals:{} });
 };

 const printChecklist = () => {
 const dateStr = todayDmy();
 const rows = YEAREND_SECTIONS.map(sec => `
 <tr><td class="sec" colspan="2">${sec.title}</td></tr>
 ${sec.items.map(it => `
 <tr>
 <td class="chk">${st.checks[it.id] ? "☑" : "☐"}</td>
 <td class="lbl">${it.t}${it.rec ? ` &nbsp;<span class="rec">— recorded: <strong>${st.vals[it.id] || "____________"}</strong> ${it.unit||""}</span>` : ""}</td>
 </tr>`).join("")}
 `).join("");
 const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Year End Maintenance Checklist — Loring S70 — ${st.year}</title>
<style>
 @page { margin: 16mm 18mm; size: A4 portrait; }
 * { box-sizing:border-box; margin:0; padding:0; }
 body { font-family:-apple-system,"Helvetica Neue",Arial,sans-serif; font-size:12px; color:#1C1814; background:#fff; max-width:680px; padding:28px 0; }
 h1 { font-size:22px; margin-bottom:4px; }
 .sub { font-size:11px; color:#7A6E63; padding-bottom:12px; margin-bottom:18px; border-bottom:2px solid #1C1814; }
 .safety { font-size:11px; color:#8B3A2A; background:#FDF3EF; border:1px solid #E5C8BC; border-radius:6px; padding:8px 12px; margin-bottom:16px; }
 table { width:100%; border-collapse:collapse; }
 td { padding:5px 0; vertical-align:top; border-bottom:1px solid #EDE8DE; line-height:1.35; }
 td.chk { width:26px; font-size:14px; }
 td.sec { font-size:10px; font-weight:700; color:#8B3A2A; text-transform:uppercase; letter-spacing:0.1em; padding:18px 0 6px; border-bottom:1.5px solid #C17F24; }
 .rec { color:#7A6E63; font-size:11px; }
 .footer { margin-top:28px; font-size:10px; color:#A89E93; border-top:1px solid #D5CCBE; padding-top:8px; }
 @media print { body { -webkit-print-color-adjust:exact; print-color-adjust:exact; padding:0; } }
</style></head><body>
<h1>Year End Maintenance Checklist</h1>
<div class="sub">Loring S70 Peregrine &nbsp;&middot;&nbsp; Skelligs Coffee &nbsp;&middot;&nbsp; Year ${st.year} &nbsp;&middot;&nbsp; Conducted by: ${st.by || "____________________"} &nbsp;&middot;&nbsp; ${dateStr} &nbsp;&middot;&nbsp; ${done}/${total} complete</div>
<div class="safety">Per the Loring manual: inspect while the roaster is cool, follow lockout/tagout protocols, and leave water, air and gas lines ON during inspection.</div>
<table>${rows}</table>
<div class="footer">From Loring Roaster Owners Manual pp. 108–111 (1007056 Rev B) &nbsp;&middot;&nbsp; Generated ${dateStr} &nbsp;&middot;&nbsp; fdc-roasting-hub.pages.dev</div>
</body></html>`;
 const win = window.open("", "_blank");
 if (!win) { window.alert("Please allow pop-ups for this site to print the checklist."); return; }
 win.document.write(html); win.document.close();
 setTimeout(() => { win.focus(); win.print(); }, 400);
 };

 return (
 <div style={{marginTop:28}}>
 <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",padding:"12px 16px",background:C.cream2,border:`1px solid ${C.border}`,borderRadius:8}} onClick={()=>setOpen(o=>!o)}>
 <div>
 <div style={{fontFamily:fontSans,fontSize:10,fontWeight:700,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.1em"}}>Year End Maintenance Checklist {open?"▾":"▸"}</div>
 <div style={{fontFamily:fontSans,fontSize:11,color:C.inkFaint,marginTop:2}}>Loring manual pp. 108–111 — the full annual service inspection. {done}/{total} ticked.</div>
 </div>
 <div style={{fontFamily:fontSerif,fontSize:20,color: done===total ? C.green : C.ink}}>{Math.round(done/total*100)}%</div>
 </div>

 {open && (
 <div style={{border:`1px solid ${C.border}`,borderTop:"none",borderRadius:"0 0 8px 8px",padding:16}}>
 <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:12,flexWrap:"wrap"}}>
 <input style={{...inputSm,width:90}} type="number" value={st.year} onChange={e=>save({...st,year:e.target.value})} title="Year"/>
 <input style={{...inputSm,width:220}} placeholder="Conducted by" value={st.by} onChange={e=>save({...st,by:e.target.value})}/>
 <button style={btnSm} onClick={printChecklist}>⎙ Print / PDF</button>
 <button style={{...btnSm,color:C.red}} onClick={resetAll}>Reset for new year</button>
 </div>
 <div style={{fontFamily:fontSans,fontSize:11,color:"#8B3A2A",background:"#FDF3EF",border:"1px solid #E5C8BC",borderRadius:6,padding:"8px 12px",marginBottom:14,lineHeight:1.5}}>
 Manual safety note: inspect while the roaster is cool, use proper lockout/tagout, and leave water, air and gas lines ON during inspection.
 </div>
 {YEAREND_SECTIONS.map(sec => (
 <div key={sec.title} style={{marginBottom:16}}>
 <div style={{fontFamily:fontSans,fontSize:10,fontWeight:700,color:"#8B3A2A",textTransform:"uppercase",letterSpacing:"0.1em",borderBottom:`1.5px solid ${C.amber}`,paddingBottom:4,marginBottom:6}}>{sec.title}</div>
 {sec.items.map(it => (
 <div key={it.id} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"5px 0",borderBottom:`1px solid ${C.borderSoft}`}}>
 <input type="checkbox" checked={!!st.checks[it.id]} onChange={()=>toggle(it.id)} style={{marginTop:2,cursor:"pointer"}}/>
 <div style={{flex:1,fontFamily:fontSans,fontSize:12.5,color: st.checks[it.id] ? C.inkFaint : C.ink,textDecoration: st.checks[it.id] ? "line-through" : "none",lineHeight:1.45}}>{it.t}</div>
 {it.rec && <input style={{...inputSm,width:110}} placeholder={`record ${it.unit||"value"}`} value={st.vals[it.id]||""} onChange={e=>setVal(it.id,e.target.value)}/>}
 </div>
 ))}
 </div>
 ))}
 </div>
 )}
 </div>
 );
}

const inputSm = {
 fontFamily:fontSans, fontSize:12, padding:"5px 7px",
 border:`1px solid ${C.parchment}`, borderRadius:4,
 background:C.cream, color:C.ink, width:"100%", outline:"none",
};
const btnSm = {
 padding:"4px 10px", fontSize:11, borderRadius:4, cursor:"pointer",
 border:`1px solid ${C.parchment}`, background:C.cream, color:C.inkMid, fontFamily:fontSans,
};

const ORIGINS = [
 "Ethiopia",
 "Colombia",
 "Kenya",
 "Brazil",
 "Guatemala",
 "Costa Rica",
 "Honduras",
 "Peru",
 "Mexico",
 "El Salvador",
 "Nicaragua",
 "Panama",
 "Rwanda",
 "Burundi",
 "Uganda",
 "Tanzania",
 "Yemen",
 "Indonesia",
 "Papua New Guinea",
 "Jamaica",
];

const CULTIVARS = [
 "Bourbon","Castillo","Caturra","Catuaí","Gesha / Geisha","Heirloom (mixed)",
 "Kent","Marsellesa","Mundo Novo","Pacamara","Pacas","Red Bourbon","Ruiru 11",
 "SL28","SL34","Typica","Villa Sarchi","Wush Wush","Yellow Bourbon","Yellow Catuaí",
 // Robusta (Coffea canephora) — a different species; choosing it switches the recipe to robusta behaviour.
 "Robusta","Conilon",
];

const BATCH_WEIGHTS = Array.from({length:12},(_,i)=>15+(i*5)); // 15–70kg in 5kg steps

// ── COMBOBOX (free text + dropdown suggestions) ───────────────────────────────
function ComboBox({ value, onChange, options, placeholder, style }) {
 const [open, setOpen] = useState(false);
 const [filter, setFilter] = useState("");
 const ref = useRef(null);

 useEffect(() => {
 const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
 document.addEventListener("mousedown", handler);
 return () => document.removeEventListener("mousedown", handler);
 }, []);

 const filtered = options.filter(o => o.toLowerCase().includes((filter||value||"").toLowerCase())).slice(0,8);

 return (
 <div ref={ref} style={{position:"relative"}}>
 <div style={{display:"flex",gap:0}}>
 <input
 style={{...style, borderRadius:"5px 0 0 5px", flex:1}}
 value={value}
 placeholder={placeholder}
 onChange={e=>{ onChange(e.target.value); setFilter(e.target.value); setOpen(true); }}
 onFocus={()=>setOpen(true)}
 />
 <button
 style={{padding:"0 10px",background:C.cream2,border:`1px solid ${C.parchment}`,borderLeft:"none",borderRadius:"0 5px 5px 0",cursor:"pointer",color:C.inkLight,fontSize:11}}
 onClick={()=>setOpen(o=>!o)}
 type="button"
 >▾</button>
 </div>
 {open && filtered.length > 0 && (
 <div style={{position:"absolute",top:"100%",left:0,right:0,zIndex:100,background:C.cream,border:`1px solid ${C.parchment}`,borderRadius:5,boxShadow:"0 4px 12px rgba(0,0,0,0.1)",marginTop:2,maxHeight:220,overflowY:"auto"}}>
 {filtered.map(o=>(
 <div key={o}
 style={{padding:"8px 12px",fontFamily:fontSans,fontSize:13,color:C.inkMid,cursor:"pointer",borderBottom:`1px solid ${C.borderSoft}`}}
 onMouseEnter={e=>e.currentTarget.style.background=C.cream2}
 onMouseLeave={e=>e.currentTarget.style.background=""}
 onMouseDown={()=>{ onChange(o); setFilter(""); setOpen(false); }}
 >{o}</div>
 ))}
 </div>
 )}
 </div>
 );
}

// ══════════════════════════════════════════════════════════════════════════════
// ROAST INTELLIGENCE ENGINE
// ══════════════════════════════════════════════════════════════════════════════

// ── CULTIVAR DATABASE ( — Roasting by Cultivar) ──────────────────────────
// Format: { group, fcTime, yellowTime, eorTime, devTime, speed, notes, flavour }
// speed: "fast" | "medium" | "slow" | "flexible"
// fcTime: target first crack time in minutes (used for 75-80% rule)
const CULTIVAR_DATA = {
 // ── BOURBON GROUP ──
 // slightly longer, gentler roasts. FC 8:30–9:00. Avoid papery/starchy.
 "Bourbon": { group:"Bourbon", fcTime:8.75, yellowTime:4.75, devTime:1.4, speed:"medium", eorAdj:0,
 notes:"Gentle, slightly longer roast. FC 8:30–9:00. Too fast = papery/starchy. Sweet spot broad.",
 flavour:"Cherry | Caramel | Nutty | Full bodied" },
 "Caturra": { group:"Bourbon", fcTime:8.75, yellowTime:4.75, devTime:1.35, speed:"medium", eorAdj:0,
 notes:"Follows Bourbon closely. FC 8:30–9:00. Slightly shorter dev time than Bourbon acceptable.",
 flavour:"Caramel | Nutty | Full bodied | Red fruit" },
 "Pacas": { group:"Bourbon", fcTime:8.75, yellowTime:4.75, devTime:1.35, speed:"medium", eorAdj:0,
 notes:"Similar to Caturra. Narrow sweet spot — don't roast outside 8:30–9:00 FC range. Minerality emerges if off.",
 flavour:"Caramel | Chocolate | Nutty | Red fruit" },
 "SL28": { group:"Bourbon", fcTime:8.75, yellowTime:4.75, devTime:1.4, speed:"medium", eorAdj:-1,
 notes:"Bourbon lineage. Darkens quickly — SL28 will be darker in colour than expected for a given EoR. Taper heat aggressively post-FC. Watch for RoR crash.",
 flavour:"Tropical fruit | Red fruit | Sweet | Moderate florality" },
 "SL34": { group:"Bourbon", fcTime:7.5, yellowTime:4.25, devTime:1.5, speed:"fast", eorAdj:-1,
 notes:"Fast to FC (~7:30). Darkens very easily — same profile yields 5–10 Agtron points darker than most cultivars. Taper heat aggressively. RoR crash risk post-FC.",
 flavour:"Citrus | Floral | Tropical fruits and berries" },
 "Yellow Bourbon": { group:"Bourbon", fcTime:8.75, yellowTime:4.75, devTime:1.35, speed:"medium", eorAdj:0,
 notes:"Standard Bourbon approach. Yellow colour makes visual cues less reliable — rely on bean probe and RoR.",
 flavour:"Caramel | Cherry | Nutty | Mild sweetness" },
 "Red Bourbon": { group:"Bourbon", fcTime:8.75, yellowTime:4.75, devTime:1.4, speed:"medium", eorAdj:0,
 notes:"Classic Red Bourbon — sweet spot broad. FC 8:30–9:00. Watch colour carefully; red fruit notes need full Maillard.",
 flavour:"Red cherry | Brown sugar | Winey sweetness" },
 "Catuaí": { group:"Bourbon", fcTime:7.75, yellowTime:4.5, devTime:1.5, speed:"flexible", eorAdj:0,
 notes:"Transitional cultivar — enormous sweet spot. FC 6:30–9:00 range all work. Fast = floral/tea-like; slow = caramel/chocolate. Very forgiving.",
 flavour:"Melon | Citrus | Syrupy sweetness | Lighter body" },
 "Yellow Catuaí": { group:"Bourbon", fcTime:7.75, yellowTime:4.5, devTime:1.5, speed:"flexible", eorAdj:0,
 notes:"Follow Red Catuaí approach. Slightly higher sugar load — visual cues less reliable, lean on probe.",
 flavour:"Honey | Toffee | Stone fruit" },
 "Villa Sarchi": { group:"Bourbon", fcTime:8.5, yellowTime:4.6, devTime:1.4, speed:"medium", eorAdj:-1,
 notes:"Compact Costa Rican Bourbon. Slightly delicate — protect florals with restrained EoR.",
 flavour:"Floral | Citrus | Delicate sweetness" },

 // ── TYPICA GROUP ──
 // fast to FC preferred. Florality and fruit express best with quick approach.
 "Typica": { group:"Typica", fcTime:7.5, yellowTime:4.25, devTime:1.5, speed:"fast", eorAdj:0,
 notes:"Fast to FC. Florality and tea-like character. Slower roast = savory, less expressive. Light touch post-FC — darkens quickly.",
 flavour:"Floral | Tea-like | Delicate fruit | Clean" },
 "Maragogipe": { group:"Typica", fcTime:7.75, yellowTime:4.25, devTime:1.5, speed:"fast", eorAdj:0,
 notes:"Large bean — higher defect risk (scorching, tipping, facing). Smaller batches recommended. Fast to FC preserves florality. If elongated to FC, becomes savory.",
 flavour:"Tropical fruits | Floral | Baking spices" },
 "Pache": { group:"Typica", fcTime:8.25, yellowTime:4.5, devTime:1.5, speed:"flexible", eorAdj:0,
 notes:"Massive sweet spot — very forgiving. FC 7:30–9:00 all viable. Avoid going faster than 7:30. Longer roast adds savory but not aggressively so.",
 flavour:"Grape/pear | Floral | Baking spices | Milk chocolate" },
 "Pacamara": { group:"Typica", fcTime:7.75, yellowTime:4.25, devTime:1.6, speed:"fast", eorAdj:0,
 notes:"Large bean = high defect risk. Keep batches small, airflow high, drum/inlet temp lower. Longer dev time (1:45) recommended to tame acidity. FC >8:00 = vegetative/starchy.",
 flavour:"Strong acidity | Citrus | Baking spices | Red fruits" },
 "Kent": { group:"Typica", fcTime:7.5, yellowTime:4.25, devTime:1.5, speed:"fast", eorAdj:0,
 notes:"Old Typica selection. Treat as Typica: gentle approach, fast to FC, light touch post-FC.",
 flavour:"Mellow sweetness | Low acidity | Soft body" },
 "Mundo Novo": { group:"Typica", fcTime:8.5, yellowTime:4.6, devTime:1.4, speed:"medium", eorAdj:0,
 notes:"Bourbon × Typica. Inherits density of Bourbon, sweetness of Typica. Reliable and consistent.",
 flavour:"Chocolate | Nuts | Mild fruit" },

 // ── ETHIOPIAN GROUP ──
 // fast to FC (~7:30) accentuates florality. FC 7:30 target across group.
 "Heirloom (mixed)": { group:"Ethiopian", fcTime:7.5, yellowTime:4.25, devTime:1.5, speed:"fast", eorAdj:-1,
 notes:"Fast to FC (~7:30) preserves intense florality and fruit. Too slow = papery or savory. If cup is hollow/papery, push FC toward 8:00–9:00. If savory/muddled, go faster.",
 flavour:"Crisp floral | Citrus fruit | Caramel" },
 "Gesha / Geisha": { group:"Ethiopian", fcTime:7.5, yellowTime:4.25, devTime:1.5, speed:"fast", eorAdj:-1,
 notes:"Roast quickly. Slower = savory dominates and buries the florality. Tipping risk — lower inlet temp, more airflow. Delicate; light touch throughout.",
 flavour:"Crisp floral (jasmine) | Candied citrus | Sweet" },
 "Pink Bourbon": { group:"Ethiopian", fcTime:7.5, yellowTime:4.25, devTime:1.33, speed:"fast", eorAdj:-1,
 notes:"Fast to FC. Pushing FC to 8:00+ causes significant savory increase. Darkens quickly — light touch post-FC essential. Shorter dev preferred.",
 flavour:"Tartness | Taffy/cotton candy | Citrus | Florality" },
 "Sidra": { group:"Ethiopian", fcTime:7.5, yellowTime:4.25, devTime:1.33, speed:"fast", eorAdj:-1,
 notes:"Fast approach for floral and tropical fruit expression. Forgiving cultivar — works across range but best fast. Darkens quickly — keep colour light.",
 flavour:"Tropical fruits | Citrus | Floral | Sweet" },
 "Papayo": { group:"Ethiopian", fcTime:7.33, yellowTime:4.25, devTime:1.33, speed:"fast", eorAdj:-1,
 notes:"Very fast to FC (~7:20). FC is often quiet — rely on smell and colour, not sound. Darkens quickly. Narrow sweet spot.",
 flavour:"Tropical fruits | Citrus | Baking spices | Syrupy" },
 "Wush Wush": { group:"Ethiopian", fcTime:7.5, yellowTime:4.25, devTime:1.5, speed:"fast", eorAdj:-1,
 notes:"Treat as Ethiopian Heirloom. Fast to FC. Protect florality.",
 flavour:"Floral | Fruity | Sweet" },
 "Ruiru 11": { group:"Ethiopian", fcTime:8.75, yellowTime:4.75, devTime:1.5, speed:"slow", eorAdj:0,
 notes:"Kenyan hybrid — SL28/SL34 parentage. Denser than typical Ethiopian. Roasts more like a Bourbon — can handle more heat.",
 flavour:"Blackcurrant | Tomato | Bold body" },

 // ── HYBRID GROUP ──
 // longer roasts reduce herbaceousness. FC timing varies by cultivar.
 "Castillo": { group:"Hybrid", fcTime:8.75, yellowTime:4.75, devTime:1.5, speed:"slow", eorAdj:0,
 notes:"Longer roast substantially reduces herbaceousness. FC before 8:45 = more herbal/savory. Allow FC at 8:30–9:00. Longer dev for espresso to mute acidity.",
 flavour:"High acidity | Citrus fruit | Caramel | Slight herbaceousness" },
 "Catimor": { group:"Hybrid", fcTime:9.0, yellowTime:4.875, devTime:1.75, speed:"slow", eorAdj:0,
 notes:"Longer roast reduces herbaceous/vegetable notes. FC before 8:30 = papery/dry. Best at 9:00+ FC. Natural/Honey process helps significantly.",
 flavour:"Sharp acidity | Herbaceous/vegetable | Caramel | Red fruits" },
 "Marsellesa": { group:"Hybrid", fcTime:8.75, yellowTime:4.75, devTime:1.5, speed:"slow", eorAdj:0,
 notes:"Sarchimor group. Similar to Catimor but slightly smoother. FC 8:30–9:00. Honey process recommended.",
 flavour:"Pleasant acidity | Caramel | Mild herbaceous" },
 "Tabi": { group:"Hybrid", fcTime:8.0, yellowTime:4.25, devTime:1.5, speed:"medium", eorAdj:0,
 notes:"Narrow sweet spot — FC must land near 8:00. Too fast = papery/overly light. Too slow = savory/herbaceous. Between-8-mark is the target.",
 flavour:"Floral | Citrus | Deep purple fruits | Botanical" },

 // ── DEFAULT (unknown cultivar) ──
 // ── ROBUSTA (Coffea canephora) — a different species, not a cultivar, but it
 // plugs in here so every recipe rule can see it. Grown low and less dense on
 // paper, yet its bean structure makes it roast like a very dense high-altitude
 // arabica: hotter charge, finish ~5–7°C hotter, slightly longer roast, and easy
 // to under-develop (hot finish, pale grainy inside). First crack is much
 // quieter than arabica's. Sources: Perfect Daily Grind / Lucas Venturim;
 // FreshCup (2023). Starting points only — the Hub refines them from logged roasts.
 "Robusta": { group:"Robusta", fcTime:9.0, yellowTime:5.0, devTime:1.65, speed:"slow", eorAdj:6,
 notes:"Robusta (Coffea canephora) — roasts like a very dense high-altitude arabica despite growing low: hotter charge, finish ~5–7°C hotter than arabica, slightly longer roast. Easy to under-develop — a hot finish can still leave a pale, grainy inside, so judge by ground colour, not bean surface. First crack is much quieter than arabica's: rely on the RoR first-crack signal, smell and the trier. Agtron readings don't line up with arabica's. For blends, roast robusta separately and blend after.",
 flavour:"Dark chocolate | Nutty | Spice | Heavy body | Low acidity" },
 "Conilon": { group:"Robusta", fcTime:9.0, yellowTime:5.0, devTime:1.65, speed:"slow", eorAdj:6,
 notes:"Conilon (Brazilian robusta, Coffea canephora) — roast as robusta: hotter charge, finish ~5–7°C hotter than arabica, slightly longer roast, watch for under-development inside. Quiet first crack — use the RoR signal, smell and trier. Roast separately for blends.",
 flavour:"Cocoa | Nutty | Grain | Heavy body | Low acidity" },
 "Canephora": { group:"Robusta", fcTime:9.0, yellowTime:5.0, devTime:1.65, speed:"slow", eorAdj:6,
 notes:"Coffea canephora (robusta) — roast as robusta: hotter charge, finish ~5–7°C hotter than arabica, slightly longer roast, watch for under-development inside. Quiet first crack. Roast separately for blends.",
 flavour:"Dark chocolate | Nutty | Spice | Heavy body | Low acidity" },
 "default": { group:"Unknown", fcTime:8.5, yellowTime:4.5, devTime:1.5, speed:"medium", eorAdj:0,
 notes:"Unknown cultivar — using standard profile. Enter cultivar for more precise recommendations.",
 flavour:"—" },
};

// Cultivar lookup with fuzzy matching
function getCultivarData(cultivar) {
 if (!cultivar) return CULTIVAR_DATA["default"];
 const key = Object.keys(CULTIVAR_DATA).find(k =>
 k.toLowerCase() === cultivar.toLowerCase() ||
 cultivar.toLowerCase().includes(k.toLowerCase()) ||
 k.toLowerCase().includes(cultivar.toLowerCase())
 );
 return key ? CULTIVAR_DATA[key] : CULTIVAR_DATA["default"];
}

// ── CULTIVAR COMPARE ──────────────────────────────────────────────────────────
function CultivarCompare() {
  const [open, setOpen] = useState(false);
  const [picks, setPicks] = useState(["", "", ""]);

  const speedColor = { fast: "#c0392b", medium: C.amber, slow: "#4A90D9", flexible: C.green };

  return (
    <div style={{border:`1px solid ${C.border}`,borderRadius:8,marginBottom:20,overflow:"hidden"}}>
      <div onClick={()=>setOpen(o=>!o)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",background:C.cream2,cursor:"pointer",userSelect:"none"}}>
        <div style={{fontFamily:fontSans,fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:C.inkLight}}>Compare cultivars</div>
        <div style={{color:C.inkFaint,fontSize:13}}>{open?"▲":"▼"}</div>
      </div>
      {open && (
        <div style={{padding:16}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:16}}>
            {picks.map((p, i) => (
              <div key={i}>
                <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,marginBottom:3}}>Cultivar {i+1}</div>
                <ComboBox value={p} onChange={v=>setPicks(ps=>ps.map((x,j)=>j===i?v:x))} options={CULTIVARS} placeholder="e.g. Gesha" style={s.input}/>
              </div>
            ))}
          </div>

          {picks.some(p=>p) && (
            <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden"}}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead>
                  <tr style={{background:C.cream3}}>
                    <th style={s.thStyle}>Attribute</th>
                    {picks.map((p,i)=>p && <th key={i} style={s.thStyle}>{p}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Group", d=>d.group],
                    ["Speed", d=>d.speed],
                    ["FC target", d=>d.fcTime.toFixed(2)+" min"],
                    ["Yellow point", d=>d.yellowTime.toFixed(2)+" min"],
                    ["Dev time", d=>d.devTime.toFixed(2)+" min"],
                    ["EoR adjust", d=>(d.eorAdj>0?"+":"")+d.eorAdj+"°C"],
                    ["Flavour", d=>d.flavour],
                  ].map(([label, get]) => (
                    <tr key={label} style={{borderBottom:`1px solid ${C.borderSoft}`}}>
                      <td style={{...s.td,fontWeight:600,color:C.inkLight,whiteSpace:"nowrap"}}>{label}</td>
                      {picks.map((p,i) => {
                        if (!p) return null;
                        const d = getCultivarData(p);
                        const val = get(d);
                        return (
                          <td key={i} style={{...s.td, color: label==="Speed" ? speedColor[val] : C.ink, fontWeight: label==="Speed" ? 700 : 400, textTransform: label==="Speed" ? "capitalize" : "none"}}>
                            {val}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  <tr>
                    <td style={{...s.td,fontWeight:600,color:C.inkLight,verticalAlign:"top"}}>Roasting notes</td>
                    {picks.map((p,i) => {
                      if (!p) return null;
                      const d = getCultivarData(p);
                      return <td key={i} style={{...s.td, fontSize:11, color:C.inkMid, lineHeight:1.5}}>{d.notes}</td>;
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          {!picks.some(p=>p) && (
            <div style={{fontFamily:fontSans,fontSize:12,color:C.inkFaint}}>Pick two or three cultivars to compare timing, speed, and flavour outcomes side by side.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── ROAST DIAGNOSTIC ──────────────────────────────────────────────────────────
const DIAGNOSTIC_SYMPTOMS = [
  { id:"papery", label:"Papery / grassy / vegetal",
    cause:"Underdeveloped — most often FC arrived too early relative to the cultivar's norm, or the drying phase was rushed leaving residual moisture unresolved into Maillard.",
    fixes:["Push FC later — check cultivar's target FC time and compare to what actually happened.","Confirm drying phase wasn't cut short — moisture needs to be fully driven off before Maillard can develop properly.","If this is a fast cultivar (Ethiopian/Typica group), it's especially sensitive — even 15–20s too fast to FC shows as papery."],
    delta:{ eor:+1, note:"raise EoR slightly / extend development" } },
  { id:"sour", label:"Sour / sharp acidity dominates / underdeveloped",
    cause:"Insufficient development time post-FC — DTR too low, or dropped too soon after FC before sugars had time to caramelise.",
    fixes:["Extend development time — check DTR against the cultivar's target range.","Drop temp (EoR) may be too low for this process/cultivar — consider raising 1–2°C.","Confirm RoR wasn't crashing hard right after FC — a sharp RoR drop cuts development short even if total DTR% looks fine."],
    delta:{ eor:+2, note:"raise EoR / extend development time" } },
  { id:"flat", label:"Flat / muted / low sweetness",
    cause:"RoR likely flatlined or stalled for an extended period — often during Maillard — robbing the roast of the steady declining curve that builds sweetness.",
    fixes:["Check the roast curve for any flat stretches in RoR, especially 4–7 minutes in.","Increase burner earlier in the drying phase to build momentum ahead of Maillard.","If batch size was unusually large or small for this charge temp, thermal mass may be throwing off the curve — cross-check IBS%."],
    delta:{ charge:+2, note:"build more energy earlier — raise charge/initial burner" } },
  { id:"bitter", label:"Bitter / harsh / ashy",
    cause:"Over-developed — DTR too high, or EoR temp too high/dark for this process and cultivar.",
    fixes:["Reduce development time — check DTR against target range.","Consider dropping 2–4°C earlier next time.","Naturals and honeys are especially prone to this — they need less development than washed, not more."],
    delta:{ eor:-3, note:"drop earlier — lower EoR / shorten development" } },
  { id:"baked", label:"Baked / tobacco / cardboard",
    cause:"Classic sign of RoR stalling for a long stretch — usually a long total roast time with the curve going flat rather than declining, more common on longer/slower cultivars or ambient/seasonal charge issues.",
    fixes:["Check total roast time against target — baked flavours often come from a roast that ran too long overall, not just development.","Look for any point where RoR sat flat for 60+ seconds — that's the likely stall point.","Increase burner earlier rather than trying to push through with a late burner increase — by the time you notice, the bake has already happened."],
    delta:{ charge:+3, note:"raise charge to prevent early-roast stalling" } },
  { id:"tipping", label:"Tipping / scorch marks / uneven roast",
    cause:"Usually inlet air temperature, not fan speed. The endosperm cap (where the embryo would germinate) takes localised heat damage when hit with high-temperature air. Airflow volume and inlet temperature are two separate levers — it's easy to conflate them, but a small volume of hot air and a large volume of cooler air can produce the same bean temperature curve while carrying very different tipping risk.",
    fixes:["Try more air volume at a lower inlet temperature rather than less air at a higher temperature — many roasters can hold the same time/temp curve this way while tipping disappears.","Naturals/honeys with surface mucilage are especially prone to this — they need a cooler entry regardless of airflow setting.","Confirm batch size isn't below the Loring's practical minimum for even tumbling.","If tipping persists, it's worth checking whether the endosperm cap is drying and depleting faster than the rest of the bean — that dryness plus concentrated heat is what chars it."],
    delta:{ charge:-3, note:"lower charge/inlet temperature" } },
];

// ── DIAGNOSTIC → PROFILE FEEDBACK LOOP ───────────────────────────────────────
// Logging a diagnostic result against a cultivar/process turns a one-off cup
// complaint into data. Once a few entries exist for a cultivar group + process
// combination, recommendTemps nudges its charge/EoR output in the corrected
// direction — so recipes actually learn from what's gone wrong in the cup,
// not just from static cultivar tables.
const DIAGNOSTIC_LOG_KEY = "fdc_diagnostic_log";
const DIAGNOSTIC_ADJUST_MIN_SAMPLES = 2;
const DIAGNOSTIC_ADJUST_MAX = 5; // cap total nudge magnitude — data should refine, not override

function logDiagnosticEntry(symptomId, cultivar, process) {
  const symptom = DIAGNOSTIC_SYMPTOMS.find(d => d.id === symptomId);
  if (!symptom) return;
  let log = [];
  try { log = JSON.parse(localStorage.getItem(DIAGNOSTIC_LOG_KEY) || "[]"); } catch { log = []; }
  log.unshift({
    id: Date.now(), symptomId, label: symptom.label, delta: symptom.delta,
    cultivar: cultivar || "", cultivarGroup: getCultivarData(cultivar).group,
    process: process || "", date: localIsoDate(),
  });
  localStorage.setItem(DIAGNOSTIC_LOG_KEY, JSON.stringify(log.slice(0, 200))); // cap log size
}

function getDiagnosticAdjustment(cultivar, process) {
  let log = [];
  try { log = JSON.parse(localStorage.getItem(DIAGNOSTIC_LOG_KEY) || "[]"); } catch { log = []; }
  const group = getCultivarData(cultivar).group;
  // Prefer exact cultivar+process matches; fall back to group-level matches if too few
  const exact = log.filter(e => e.cultivar === cultivar && e.process === process);
  const groupMatch = log.filter(e => e.cultivarGroup === group && e.process === process);
  const pool = exact.length >= DIAGNOSTIC_ADJUST_MIN_SAMPLES ? exact : groupMatch;
  if (pool.length < DIAGNOSTIC_ADJUST_MIN_SAMPLES) return { charge: 0, eor: 0, n: 0, notes: [] };

  let chargeSum = 0, eorSum = 0, chargeN = 0, eorN = 0;
  const notes = [];
  for (const e of pool) {
    if (e.delta?.charge) { chargeSum += e.delta.charge; chargeN++; }
    if (e.delta?.eor) { eorSum += e.delta.eor; eorN++; }
    if (e.delta?.note && !notes.includes(e.delta.note)) notes.push(e.delta.note);
  }
  const chargeAdj = chargeN ? Math.max(-DIAGNOSTIC_ADJUST_MAX, Math.min(DIAGNOSTIC_ADJUST_MAX, Math.round(chargeSum / chargeN))) : 0;
  const eorAdj = eorN ? Math.max(-DIAGNOSTIC_ADJUST_MAX, Math.min(DIAGNOSTIC_ADJUST_MAX, Math.round(eorSum / eorN))) : 0;
  return { charge: chargeAdj, eor: eorAdj, n: pool.length, notes };
}

// ── LORING MACHINE FAULT & WARNING REFERENCE ──────────────────────────────────
// Transcribed from the Owners Manual Tables 2 & 3, pp. 85–91 (1007056 Rev B).
// Warnings: roaster still operable. Faults: burner shuts off, Machine State
// goes FAULTED — clear the cause, CLEAR ALL FAULTS, ROAST, then BURNER to
// re-ignite. Corrective actions condensed from the manual's wording.
const LORING_WARNINGS = [
 { t:"Purge gate go to open exceeded timer set point", a:"Gate opening too slowly. Check Purge Gate Open switch LED, listen for air leaks; with air pressure and roaster off, check the gate swings without binding. Verify air pressure against the Pre-Installation & Site Prep Guide." },
 { t:"Purge gate go to closed exceeded timer set point", a:"Gate closing too slowly — same checks as above, against the Closed switch LED." },
 { t:"Bean Temperature High Limit Range warning", a:"Bean temp over the Warning level in Burner Settings (default 268°C). Use Manual Air Cool." },
 { t:"Inlet Temperature High Limit Range warning", a:"Inlet air over its Warning level in Burner Settings. Use Manual Air Cool." },
 { t:"Stack Temperature High Limit Range warning", a:"Stack temp over its Warning level in Burner Settings. Use Manual Air Cool." },
 { t:"Return Temperature High Limit Range warning", a:"Return air over its Warning level in Burner Settings. Use Manual Air Cool." },
 { t:"Low gas pressure during operation", a:"Verify incoming gas pressure is within the range in the Pre-Installation & Site Prep Guide." },
 { t:"Combustion Air Blower on during Ready", a:"CAB motor shouldn't run in Ready. Check the CAB pressure switch LED — should not be lit in Ready state." },
 { t:"Discharge gate showing open during roast", a:"Roaster Door reads open mid-roast. Check the door; if it's actually closed, suspect the door indicator switch." },
 { t:"Flame on when not commanded", a:"Check the cyclone Sight Glass. If flame is genuinely lit, possible DSI/BCU fault — close the gas ball valve at the back of the roaster to extinguish." },
 { t:"Flame off when commanded on", a:"Check the Sight Glass. If flame is actually on, it's an indicator problem; if off, try to relight the Burner." },
 { t:"Purge gate closed during cool down", a:"Gate should be open in Cooldown. Check proximity switch LEDs, air leaks, binding, air pressure." },
 { t:"Purge gate open during green drop / start of roast / profile", a:"Gate may be stuck open. Check proximity switch LEDs, air leaks, binding, air pressure." },
 { t:"Drop Door Override button stuck", a:"Check the Drop Door Override push button on the left side of the roaster — plunger shouldn't be stuck down. Press to verify it operates." },
 { t:"BCU error (generic)", a:"Check the numeric display on the BCU at the back. Flashing = error — note the number, clear, relight, re-check. Normal: steady 00 flame off, 08 flame on and stable." },
 { t:"BCU ignite position on when it should be off", a:"Note the BCU flashing code, clear the Warning, see if it returns. If it does, call Loring with the code." },
 { t:"BCU closed position off when it should be on", a:"As above — note code, clear, call Loring if it persists." },
 { t:"BCU open position on when it should be off", a:"As above — note code, clear, call Loring if it persists." },
 { t:"Email attempted to send but failed", a:"Verify email setup under Screen Settings; use the TEST EMAIL button." },
 { t:"Burner failed to ignite", a:"Check gas pressure, try to relight. Persisting — contact Loring." },
 { t:"Circulation Fan drive off when requested on", a:"Open the electrical cabinet — Circ Fan VFD powered on and no ALM alarm showing." },
 { t:"Auto Air Cool bean temperature exceeded", a:"Bean temp passed the Roaster Settings threshold. Set Auto Air Cool to start at a lower temperature." },
 { t:"Auto gate confirm open switch not operating", a:"Cooling Tray Auto Gate not operating — tap AUTO GATE on the Touchscreen. (Auto Gate option.)" },
 { t:"Auto gate confirm closed switch not operating", a:"Cooling Tray Auto Gate not closing. (Auto Gate option.)" },
 { t:"Auto gate auxiliary switch overridden", a:"Manual switch stuck on — press to check it depresses and releases. NEVER open the Auto Gate by hand: it can sever fingers. (Auto Gate option.)" },
 { t:"Close drop roasted gate before igniting", a:"Discharge Door (Drop Roasted door) is open while trying to light the Burner." },
 { t:"External CSI system warning", a:"Cold Stack Incinerator is third-party kit — contact the CSI manufacturer; stop roasting until fixed. (CSI option.)" },
 { t:"External CSI communications drop", a:"Check the CSI Ethernet cable to the roaster, otherwise contact the CSI manufacturer. (CSI option.)" },
 { t:"External CSI not ready at Roast EoR", a:"Check the CSI's own panel for faults, power-cycle roaster and CSI, retry. (CSI option.)" },
];
const LORING_FAULTS = [
 { t:"Chaff high temperature switch is on", a:"CHAFF BARREL POSSIBLY ON FIRE. Tap QUENCH CHAFF as many times as needed. Make sure the Circulation Fan is OFF. Slowly remove the Chaff Barrel from under the cyclone — it may be hot.", hot:true },
 { t:"Purge gate go to open exceeded timer set point", a:"Opening too slowly or not at all. Open switch LED, air leaks, binding with air + roaster off, air pressure per Site Prep Guide." },
 { t:"Purge gate go to close exceeded timer set point", a:"Closing too slowly or not at all — same checks against the Closed switch LED." },
 { t:"Utilities air supply — air is off", a:"Check the air supply ball valve at the back of the roaster and the air pressure gauge; verify pressure is within the Site Prep Guide range." },
 { t:"Utilities water supply — water is off", a:"Check the water ball valve at the back and the water pressure gauge against spec; look for leaks; verify supply with the utility provider." },
 { t:"Combustion Air Pressure switch off during operation", a:"CAB motor not running. Inspect the CAB switch, listen for the motor, tap BURNER to relight — Burner should light and the CAB LED come on." },
 { t:"High gas pressure switch is on", a:"Check the High Gas Pressure LED — ON means pressure is fine, OFF means pressure is high. Check the gauge against spec. Then: power off, shut gas at the SOURCE (not the roaster), disconnect hose at source, open the gas ball valve at the back and bleed out, reconnect, power on, re-check the LED. VENTILATE before bleeding gas indoors and keep ignition sources away.", hot:true },
 { t:"Circulation fan not running during operation", a:"Tap CIRC FAN and check the motor runs; electrical cabinet — Circ Fan VFD powered, no ALM." },
 { t:"Discharge gate switch not showing closed", a:"Roaster Door reads open while lighting the Burner. Check the door; if closed, suspect the indicator switch." },
 { t:"Flame on during purge sequence", a:"Inspect through the Sight Glass — if no actual flame, likely a faulty DSI or BCU input." },
 { t:"ESTOP", a:"Emergency Stop is pressed. Confirm whatever prompted the stop is corrected before releasing the button." },
 { t:"Purge gate showing closed when it should be open", a:"Gate closed during purge cycle. Check for air leaks; confirm proximity switch Open/Closed LEDs match the gate's real position; check for binding." },
 { t:"Purge gate showing open when it should be closed", a:"Same checks — LEDs vs actual position, leaks, binding." },
 { t:"Drum drive is faulted", a:"Drum Paddle motor not running. Verify with the DRUM button; Drum Paddle VFD powered and no ALM; power-cycle the roaster." },
 { t:"BCU faulted during ignite sequence", a:"Burner not lighting correctly. Check the BCU at the back for a flashing number, note it, contact Loring — leave the roaster powered on, they may want the current Machine State." },
 { t:"Bean thermocouple is faulted", a:"Bean temp sensor not reporting — failed or broken wire. Inspect the thermocouple for damage." },
 { t:"Inlet Air thermocouple is faulted", a:"Inlet air sensor not reporting — inspect for damage or broken wire." },
 { t:"Stack thermocouple is faulted", a:"Stack sensor not reporting — inspect for damage or broken wire." },
 { t:"Return air thermocouple is faulted", a:"Return air sensor not reporting — inspect for damage or broken wire." },
 { t:"BCU combustion air blower on when it should be off", a:"CAB switch reads on with flame off, flame on when it shouldn't be, or switch stuck on. Check the CAB switch LED." },
 { t:"BCU combustion air blower not on during operation", a:"BCU needs to see the CAB switch transition off→on when lighting. Check the switch and LED." },
 { t:"BCU ignite position off during ignition", a:"Can occur in Ignite/Stabilize. Note the BCU flashing code, clear, retry; persisting — call Loring with the code. Check CAB switch LED." },
 { t:"BCU close position on when it should be off", a:"Ignite/Stabilize states — note code, clear, call Loring if it returns." },
 { t:"BCU open position on when it should be off", a:"Ignite/Stabilize states — note code, clear, call Loring if it returns." },
 { t:"Bean temperature too high fault", a:"Bean temp over the factory-set Fault threshold (273°C — can't be changed). Use Manual Air Cool.", hot:true },
 { t:"Inlet air temperature too high fault", a:"Over the factory-set threshold. Use Manual Air Cool." },
 { t:"Stack temperature too high fault", a:"Over the factory-set threshold. Use Manual Air Cool." },
 { t:"Return air temperature too high fault", a:"Over the factory-set threshold. Use Manual Air Cool." },
];

function MachineFaultReference({ defaultOpen = false }) {
 const [open, setOpen] = useState(defaultOpen);
 const [q, setQ] = useState("");
 const [openItem, setOpenItem] = useState(null);
 const match = it => !q || it.t.toLowerCase().includes(q.toLowerCase()) || it.a.toLowerCase().includes(q.toLowerCase());
 const warns = LORING_WARNINGS.filter(match);
 const faults = LORING_FAULTS.filter(match);

 const Row = ({ it, kind }) => {
 const id = kind + it.t;
 const isOpen = openItem === id;
 return (
 <div style={{borderBottom:`1px solid ${C.borderSoft}`}}>
 <div onClick={()=>setOpenItem(isOpen?null:id)} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 14px",cursor:"pointer",background: it.hot ? "#FDF3EF" : "transparent"}}>
 <div style={{width:8,height:8,borderRadius:"50%",flexShrink:0,background: kind==="F" ? C.red : C.amber}}/>
 <div style={{flex:1,fontFamily:fontSans,fontSize:12.5,color:C.ink,fontWeight: it.hot?700:400}}>{it.t}</div>
 <div style={{fontFamily:fontSans,fontSize:11,color:C.inkFaint}}>{isOpen?"▾":"▸"}</div>
 </div>
 {isOpen && <div style={{padding:"0 14px 10px 32px",fontFamily:fontSans,fontSize:12,color:C.inkMid,lineHeight:1.55}}>{it.a}</div>}
 </div>
 );
 };

 return (
 <div style={{marginBottom:24}}>
 <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",padding:"12px 16px",background:C.cream2,border:`1px solid ${C.border}`,borderRadius:8}} onClick={()=>setOpen(o=>!o)}>
 <div>
 <div style={{fontFamily:fontSans,fontSize:10,fontWeight:700,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.1em"}}>Machine faults &amp; warnings — Loring reference {open?"▾":"▸"}</div>
 <div style={{fontFamily:fontSans,fontSize:11,color:C.inkFaint,marginTop:2}}>Owners Manual Tables 2 &amp; 3 (pp. 85–91) — what the touchscreen message means and what to do.</div>
 </div>
 </div>
 {open && (
 <div style={{border:`1px solid ${C.border}`,borderTop:"none",borderRadius:"0 0 8px 8px",overflow:"hidden"}}>
 <div style={{padding:"12px 14px",borderBottom:`1px solid ${C.borderSoft}`,background:C.cream}}>
 <input style={{...s.input,maxWidth:340}} placeholder="Search fault or warning text…" value={q} onChange={e=>setQ(e.target.value)}/>
 <div style={{fontFamily:fontSans,fontSize:11,color:C.inkFaint,marginTop:8,lineHeight:1.5}}>
 <span style={{color:C.amber,fontWeight:700}}>Warning</span> — roaster still operable. Fix the cause, then CLEAR ALL WARNINGS → ROAST.&nbsp;
 <span style={{color:C.red,fontWeight:700}}>Fault</span> — Burner shuts off, Machine State goes FAULTED. Fix the cause, CLEAR ALL FAULTS → ROAST → BURNER to re-ignite.
 </div>
 </div>
 {faults.length > 0 && <div style={{fontFamily:fontSans,fontSize:9,fontWeight:700,color:C.red,textTransform:"uppercase",letterSpacing:"0.1em",padding:"10px 14px 4px"}}>Faults ({faults.length})</div>}
 {faults.map(it => <Row key={"F"+it.t} it={it} kind="F"/>)}
 {warns.length > 0 && <div style={{fontFamily:fontSans,fontSize:9,fontWeight:700,color:C.amber,textTransform:"uppercase",letterSpacing:"0.1em",padding:"12px 14px 4px"}}>Warnings ({warns.length})</div>}
 {warns.map(it => <Row key={"W"+it.t} it={it} kind="W"/>)}
 {faults.length===0 && warns.length===0 && <div style={{padding:"14px",fontFamily:fontSans,fontSize:12,color:C.inkFaint}}>No matches — try fewer words.</div>}
 </div>
 )}
 </div>
 );
}

function RoastDiagnostic() {
  const [open, setOpen] = useState(false);
  const [symptom, setSymptom] = useState("");
  const [cultivar, setCultivar] = useState("");
  const [process, setProcess] = useState("");
  const [logged, setLogged] = useState(false);

  const diag = DIAGNOSTIC_SYMPTOMS.find(d => d.id === symptom);
  const cvData = cultivar ? getCultivarData(cultivar) : null;
  const existingAdjust = (cultivar && process) ? getDiagnosticAdjustment(cultivar, process) : { n: 0 };

  const handleLog = () => {
    logDiagnosticEntry(symptom, cultivar, process);
    submitPoolEntry("/diagnostic", {
      symptomId: symptom,
      cultivarGroup: getCultivarData(cultivar).group,
      process,
      deltaCharge: diag?.delta?.charge || 0,
      deltaEor: diag?.delta?.eor || 0,
    });
    setLogged(true);
    setTimeout(() => setLogged(false), 2500);
  };

  return (
    <div style={{border:`1px solid ${C.border}`,borderRadius:8,marginBottom:20,overflow:"hidden"}}>
      <div onClick={()=>setOpen(o=>!o)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",background:C.cream2,cursor:"pointer",userSelect:"none"}}>
        <div style={{fontFamily:fontSans,fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:C.inkLight}}>Roast diagnostic</div>
        <div style={{color:C.inkFaint,fontSize:13}}>{open?"▲":"▼"}</div>
      </div>
      {open && (
        <div style={{padding:16}}>
          <div style={{fontFamily:fontSans,fontSize:11,color:C.inkLight,marginBottom:14}}>
            Something off in the cup? Pick the closest symptom for a likely cause and fix — cross-referenced against cultivar behaviour where relevant.
          </div>

          <div style={{marginBottom:14}}>
            <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,marginBottom:6}}>Symptom</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {DIAGNOSTIC_SYMPTOMS.map(d => (
                <button key={d.id} onClick={()=>setSymptom(d.id)} style={{
                  fontFamily:fontSans, fontSize:12, padding:"6px 14px", borderRadius:20, cursor:"pointer",
                  background: symptom===d.id ? C_FIXED_DARK : C.cream3,
                  color: symptom===d.id ? "#fff" : C.inkMid,
                  border: `1px solid ${symptom===d.id ? C_FIXED_DARK : C.border}`,
                }}>{d.label}</button>
              ))}
            </div>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
            <div>
              <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,marginBottom:3}}>Cultivar (optional)</div>
              <ComboBox value={cultivar} onChange={setCultivar} options={CULTIVARS} placeholder="e.g. Gesha" style={s.input}/>
            </div>
            <div>
              <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,marginBottom:3}}>Process (optional)</div>
              <select style={s.input} value={process} onChange={e=>setProcess(e.target.value)}>
                <option value="">—</option>
                {["Washed","Natural","Honey","Anaerobic"].map(o=><option key={o}>{o}</option>)}
              </select>
            </div>
          </div>

          {diag && (
            <div style={{background:"#FDF6E8",border:`1px solid ${C.amber}`,borderRadius:8,padding:"14px 16px"}}>
              <div style={{fontFamily:fontSans,fontSize:10,color:C.amber,textTransform:"uppercase",letterSpacing:"0.06em",fontWeight:700,marginBottom:6}}>Likely cause</div>
              <div style={{fontFamily:fontSans,fontSize:13,color:C.ink,marginBottom:12,lineHeight:1.5}}>{diag.cause}</div>
              <div style={{fontFamily:fontSans,fontSize:10,color:C.amber,textTransform:"uppercase",letterSpacing:"0.06em",fontWeight:700,marginBottom:6}}>Try this</div>
              <ul style={{margin:0,paddingLeft:18}}>
                {diag.fixes.map((f,i)=>(
                  <li key={i} style={{fontFamily:fontSans,fontSize:12,color:C.inkMid,marginBottom:5,lineHeight:1.5}}>{f}</li>
                ))}
              </ul>
              {cvData && cvData.group !== "Unknown" && (
                <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${C.border}`}}>
                  <div style={{fontFamily:fontSans,fontSize:10,color:C.amber,textTransform:"uppercase",letterSpacing:"0.06em",fontWeight:700,marginBottom:6}}>Cultivar-specific note — {cultivar}</div>
                  <div style={{fontFamily:fontSans,fontSize:12,color:C.inkMid,lineHeight:1.5}}>{cvData.notes}</div>
                </div>
              )}
              {process && (process==="Natural"||process==="Honey"||process==="Anaerobic") && (symptom==="bitter"||symptom==="tipping") && (
                <div style={{marginTop:10,fontFamily:fontSans,fontSize:11,color:C.inkLight,fontStyle:"italic"}}>
                  {process} process carries surface sugars/mucilage — this raises risk for exactly this symptom. Charge and development should both be more conservative than washed.
                </div>
              )}

              <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${C.border}`}}>
                {cultivar && process ? (
                  <>
                    <button onClick={handleLog} style={{...s.btnSmall, background: logged ? C.green : C.ink, color:"#fff"}}>
                      {logged ? "✓ Logged" : `Log this for ${cultivar} / ${process}`}
                    </button>
                    <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,marginTop:6,lineHeight:1.5}}>
                      {existingAdjust.n > 0
                        ? `${existingAdjust.n} entr${existingAdjust.n===1?"y":"ies"} logged for this combination so far${existingAdjust.n >= DIAGNOSTIC_ADJUST_MIN_SAMPLES ? " — Profile builder recipes for this cultivar/process are now nudged from this data." : ` — ${DIAGNOSTIC_ADJUST_MIN_SAMPLES - existingAdjust.n} more before it starts adjusting recipes.`}`
                        : "Logging feeds the Profile Builder's recipe engine — once a couple of entries exist for this cultivar and process, future recipes nudge automatically toward the fix."}
                    </div>
                  </>
                ) : (
                  <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,lineHeight:1.5}}>Set cultivar and process above to log this result and feed it into future recipe recommendations.</div>
                )}
              </div>
            </div>
          )}

          <div style={{marginTop:14,paddingTop:12,borderTop:`1px solid ${C.border}`,fontFamily:fontSans,fontSize:10,color:C.inkFaint,lineHeight:1.6}}>
            <strong style={{color:C.inkLight}}>Colour reference (2025 UC Davis Coffee Center research):</strong> across wildly different roast profiles and origins, bean colour in L*a*b* space consistently maps to the same "universal roast colour curve" — including at first and second crack. If your FC colour looks visibly off despite hitting the right bean temp, suspect probe calibration or bean density before doubting the read.
          </div>
        </div>
      )}
    </div>
  );
}

// ── THREE CORE ROASTING PRINCIPLES (applied to profile generation) ───────────
// I. Apply adequate energy at the beginning — establish high ΔT early
// II. Bean temperature shall always decelerate — declining RoR throughout
// III. First crack shall begin at 75–80% of total roast time

function calcDevMetrics(fcTime, totalTime) {
 const fcPct = (fcTime / totalTime) * 100;
 const dtrPct = ((totalTime - fcTime) / totalTime) * 100;
 return { fcPct: Math.round(fcPct), dtrPct: Math.round(dtrPct) };
}

// ── MAIN RECOMMENDATION ENGINE ───────────────────────────────────────────────

// ── AGTRON CALIBRATION FROM MEASURED READINGS ────────────────────────────────
// Every time a roast is logged with a real light-meter reading (history.agtron)
// alongside what the model predicted at drop (history.agtronPredicted), that's
// a data point on how wrong the model was for that cultivar group. Once there
// are enough points for a group, apply the average error as a correction —
// so predictions get more precise the more roasts get logged, without ever
// needing anyone to touch the underlying formula by hand.
const AGTRON_CALIBRATION_MIN_SAMPLES = 3;

function getAgtronCalibration() {
  let hist = [];
  try { hist = JSON.parse(localStorage.getItem("fdc_history") || "[]"); } catch { hist = []; }

  // Agtron whole-bean realistically runs roughly 25 (very dark) to 95 (very
  // light) — nothing on real roasted coffee falls outside that. A stray typo
  // (620 instead of 62, or a misplaced decimal) would otherwise get averaged
  // in at full weight with only 3 samples required, which is exactly enough
  // for one bad entry to badly skew or even invert the calibration for an
  // entire cultivar group. Implausible entries are skipped, not corrected —
  // no attempt to guess what was meant, just excluded from the average.
  const AGTRON_PLAUSIBLE_MIN = 25;
  const AGTRON_PLAUSIBLE_MAX = 95;

  const samples = hist.filter(r => {
    if (!r.agtron || isNaN(parseFloat(r.agtron))) return false;
    if (r.agtronPredicted === undefined || r.agtronPredicted === null || isNaN(parseFloat(r.agtronPredicted))) return false;
    const measured = parseFloat(r.agtron);
    return measured >= AGTRON_PLAUSIBLE_MIN && measured <= AGTRON_PLAUSIBLE_MAX;
  });

  // Group by cultivar group (Bourbon/Typica/Ethiopian/Hybrid/Unknown) rather than
  // by exact cultivar — keeps sample sizes usable while still being meaningful,
  // since darkening behaviour is mostly a group-level trait in the model.
  const byGroup = {};
  for (const r of samples) {
    const grp = getCultivarData(r.cultivar).group;
    const measured = parseFloat(r.agtron);
    const predicted = parseFloat(r.agtronPredicted);
    if (!byGroup[grp]) byGroup[grp] = [];
    byGroup[grp].push(measured - predicted); // positive = actual is lighter than predicted
  }

  const calibration = {};
  for (const [grp, diffs] of Object.entries(byGroup)) {
    if (diffs.length >= AGTRON_CALIBRATION_MIN_SAMPLES) {
      const avg = diffs.reduce((a, b) => a + b, 0) / diffs.length;
      calibration[grp] = { offset: Math.round(avg * 10) / 10, n: diffs.length };
    }
  }
  return calibration; // e.g. { Bourbon: { offset: -3.2, n: 5 } }
}

// ── AGTRON / ROAST COLOUR PREDICTION ─────────────────────────────────────────
// Correlates EoR temp with predicted Agtron whole-bean range
// Adjusted for cultivar group (Ethiopian, SL28/SL34, Pink Bourbon darken faster)
// ── LIVE PHASE ALERTS ──────────────────────────────────────────────────────────
// Reacts to actual live conditions (real RoR trend from the live-logged curve,
// real elapsed time against this profile's targets) rather than showing a
// fixed message at a fixed time — the crash/flick alerts only fire when the
// RoR data genuinely shows one happening, not on a timer. Returns at most one
// alert (highest priority first) so the roaster isn't shown competing advice.
// ── WHOLE-ROAST CURVE TRACKING ────────────────────────────────────────────────
// The alert engine below reacts to sudden RoR events. This is different: at
// any point in the roast, is the actual bean temp tracking near where THIS
// profile's own recipe curve says it should be — not just in the last 30
// seconds, across the whole roast so far. Interpolates between this recipe's
// own generated steps (same tilde-prefixed values the live chart already
// plots as its reference line, parsed the same safe way).
function getExpectedTempAtTime(steps, minElapsed) {
  const pts = (steps || [])
    .map(s => ({ t: s.time, temp: parseFloat(String(s.temp).replace("~","")) }))
    .filter(p => !isNaN(p.t) && !isNaN(p.temp))
    .sort((a,b) => a.t - b.t);
  if (!pts.length) return null;
  if (minElapsed <= pts[0].t) return pts[0].temp;
  if (minElapsed >= pts[pts.length-1].t) return pts[pts.length-1].temp;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i], b = pts[i+1];
    if (minElapsed >= a.t && minElapsed <= b.t) {
      const frac = (b.t - a.t) > 0 ? (minElapsed - a.t) / (b.t - a.t) : 0;
      return Math.round((a.temp + (b.temp - a.temp) * frac) * 10) / 10;
    }
  }
  return null;
}

// Returns the live delta (actual − expected) plus whether it's a genuine,
// sustained deviation (checked over the last ~45s of the live curve) rather
// than a single noisy reading — same "trend, not snapshot" principle as the
// crash/flick detection below.
function getCurveTracking({ steps, dataPoints, sec, beanTemp }) {
  const minElapsed = sec / 60;
  const expected = getExpectedTempAtTime(steps, minElapsed);
  const actual = parseFloat(beanTemp);
  if (expected === null || isNaN(actual)) return null;
  const delta = Math.round((actual - expected) * 10) / 10;

  const window = dataPoints.filter(p => p.t >= minElapsed - 0.75 && p.temp != null);
  let sustained = false;
  if (window.length >= 3) {
    const deltas = window.map(p => p.temp - (getExpectedTempAtTime(steps, p.t) ?? p.temp));
    sustained = deltas.every(d => Math.abs(d) >= 8 && Math.sign(d) === Math.sign(delta));
  }
  return { delta, sustained, expected };
}

// ── CURVE ADHERENCE SUMMARY ──────────────────────────────────────────────────
// Computed once, at drop — walks the FULL live-logged curve (not just a
// recent window like the live alert engine below) and summarises how well
// the actual roast tracked this profile's own recipe curve, broken down by
// phase. Saved onto the history record so it accumulates over time, the
// same way the Agtron calibration and diagnostic feedback loop already do.
function computeCurveAdherence(dataPoints, steps, dryEndMin, fcTarget, totalMin) {
  const pts = (dataPoints || []).filter(p => p.temp != null && p.t != null).sort((a,b) => a.t - b.t);
  if (pts.length < 3 || !steps) return null;

  const phaseOf = t => {
    if (dryEndMin && t <= dryEndMin) return "drying";
    if (fcTarget && t <= fcTarget) return "maillard";
    return "development";
  };

  const byPhase = { drying: [], maillard: [], development: [] };
  const deviations = []; // { t, delta } — kept in time order for the area calc below
  for (const p of pts) {
    const expected = getExpectedTempAtTime(steps, p.t);
    if (expected === null) continue;
    const delta = p.temp - expected;
    byPhase[phaseOf(p.t)].push(delta);
    deviations.push({ t: p.t, delta });
  }

  const avg = arr => arr.length ? Math.round((arr.reduce((a,b)=>a+b,0) / arr.length) * 10) / 10 : null;
  const phases = { drying: avg(byPhase.drying), maillard: avg(byPhase.maillard), development: avg(byPhase.development) };
  const allDeltas = deviations.map(d => d.delta);
  const overall = avg(allDeltas);
  if (overall === null) return null;

  // Mean absolute deviation — how closely the roast tracked the curve
  // regardless of direction. The signed "overall" average above can hide
  // a roast that ran hot then cool in equal measure; this can't.
  const meanAbsDev = Math.round((allDeltas.reduce((a,b)=>a+Math.abs(b),0) / allDeltas.length) * 10) / 10;

  // Total accumulated deviation — trapezoidal area between the actual and
  // reference curves, in °C·min, across the whole roast. Captures both
  // how big a deviation was AND how long it lasted, not just a snapshot.
  let totalAccumDev = 0;
  for (let i = 1; i < deviations.length; i++) {
    const dt = deviations[i].t - deviations[i-1].t;
    const avgAbs = (Math.abs(deviations[i].delta) + Math.abs(deviations[i-1].delta)) / 2;
    totalAccumDev += avgAbs * dt;
  }
  totalAccumDev = Math.round(totalAccumDev * 10) / 10;

  // A short, honest verdict — describes what happened, doesn't grade it
  const describe = (v) => v === null ? null : Math.abs(v) < 3 ? "on target" : v > 0 ? `+${v}°C hot` : `${v}°C cool`;
  const parts = [["Drying", phases.drying], ["Maillard", phases.maillard], ["Development", phases.development]]
    .filter(([,v]) => v !== null)
    .map(([label, v]) => `${label} ${describe(v)}`);

  return { overall, meanAbsDev, totalAccumDev, phases, summary: parts.join(" · ") };
}

// Reads accumulated curve-adherence data across past roasts for a matching
// cultivar group + process, the same grouping and minimum-sample pattern
// already used by getDiagnosticAdjustment — a display note only for now
// (not yet feeding a numeric nudge into recommendTemps, unlike the
// diagnostic loop, since a whole-curve bias is more roaster-technique than
// green-coffee behaviour and deserves a person's read before it adjusts a
// recipe automatically).
const CURVE_ADHERENCE_MIN_SAMPLES = 3;
function getCurveAdherenceHistory(cultivar, process) {
  let hist = [];
  try { hist = JSON.parse(localStorage.getItem("fdc_history") || "[]"); } catch { hist = []; }
  const group = getCultivarData(cultivar).group;
  const matches = hist.filter(r => r.curveAdherence && r.process === process && getCultivarData(r.cultivar).group === group);
  if (matches.length < CURVE_ADHERENCE_MIN_SAMPLES) return null;
  const avg = key => {
    const vals = matches.map(r => r.curveAdherence.phases[key]).filter(v => v !== null && v !== undefined);
    return vals.length ? Math.round((vals.reduce((a,b)=>a+b,0)/vals.length)*10)/10 : null;
  };
  return { n: matches.length, drying: avg("drying"), maillard: avg("maillard"), development: avg("development") };
}

function getPhaseAlert({ sec, events, fcTarget, totalMin, dataPoints, steps, beanTemp, predictedFcMin, envRor }) {
  const minElapsed = sec / 60;
  const hasEvent = name => events.some(e => e.name === name);
  const fcLogged = hasEvent("First crack");
  const dropLogged = hasEvent("Drop");
  if (dropLogged) return null;

  // Last ~30 seconds of live-logged RoR readings — the actual trend, not a snapshot.
  const recent = dataPoints.filter(p => p.ror != null && p.t >= minElapsed - 0.5);
  const trend = recent.length >= 2 ? recent[recent.length - 1].ror - recent[0].ror : null;
  const latestRor = recent.length ? recent[recent.length - 1].ror : null;

  // 1 — RoR flicking up after FC (most urgent: gas added at exactly the wrong moment)
  if (fcLogged && trend !== null && trend > 2.5 && latestRor > 6) {
    return { level: "danger", title: "RoR FLICKING UP", body: "Don't add more gas — the exothermic reaction is doing this on its own. Let it settle, or drop early if aroma's already shifting." };
  }
  // 1b — Heat in the drum rising after FC: environment temperature climbing
  // usually shows up as a flick in bean RoR about half a minute later, so
  // this warns while there's still time to ease off.
  if (fcLogged && envRor != null && envRor > 3 && (latestRor == null || latestRor < 9)) {
    return { level: "danger", title: "FLICK RISK — DRUM HEAT RISING", body: "The environment temperature is climbing after first crack. That usually turns into a flick in about 30 seconds — ease the burner back now." };
  }
  // 2 — RoR crashing before FC (stall risk — will bake if not corrected)
  // Only once the turning point is past — straight after charge RoR is
  // supposed to be deeply negative while the beans soak up heat.
  const pastTurn = hasEvent("Turning point") || minElapsed > 2.5;
  if (pastTurn && !fcLogged && trend !== null && trend < -3 && latestRor !== null && latestRor < 4) {
    return { level: "danger", title: "RoR CRASHING", body: "Add a small gas increment now — a stall here risks baking the batch before it reaches development." };
  }
  // 3 — Sustained deviation from this profile's own curve, checked across the whole
  // roast so far, not just a recent blip. A genuine, held gap here also explains why
  // the approach-FC/approach-drop timings below might land early or late.
  const tpEvA = events.find(e => e.name === "Turning point");
  const tpMinA = tpEvA ? (() => { const [a, b] = String(tpEvA.time).split(":").map(x => parseInt(x) || 0); return a + b / 60; })() : null;
  if (steps && beanTemp && tpMinA != null && minElapsed >= tpMinA + 1) {
    const tracking = getCurveTracking({ steps, dataPoints, sec, beanTemp });
    if (tracking && tracking.sustained) {
      if (tracking.delta > 0) {
        return { level: "danger", title: `RUNNING HOT — ${tracking.delta > 0 ? "+" : ""}${tracking.delta}°C vs curve`, body: "Bean temp has been sustained well above this profile's own target for a while — check for tipping/scorch risk and consider easing the burner back." };
      } else {
        return { level: "danger", title: `RUNNING COOL — ${tracking.delta}°C vs curve`, body: "Bean temp has been sustained well below this profile's own target for a while — likely to under-develop or arrive at FC late unless corrected." };
      }
    }
  }
  // 4 — Approaching First Crack (the exact moment the mockup screenshot showed)
  const fcDue = predictedFcMin != null ? predictedFcMin : fcTarget;
  if (!fcLogged && fcDue && (fcDue - minElapsed) <= 1.0 && (fcDue - minElapsed) > -0.5) {
    return { level: "approach", title: "APPROACHING FIRST CRACK — NOW", body: "Stay alert. RoR often flatlines just before FC — counter it before it crashes." };
  }
  // 5 — Approaching target drop
  if (fcLogged && totalMin && (totalMin - minElapsed) <= 0.75 && (totalMin - minElapsed) > -0.5) {
    return { level: "approach", title: "APPROACHING TARGET DROP", body: "Check DTR%, RoR, and aroma together before dropping — no single signal decides alone." };
  }
  return null;
}

function predictAgtron(eorTemp, cultivar) {
  const cv = getCultivarData(cultivar);
  // Robusta stays paler inside than arabica at the same drop temperature, so
  // map it ~5° cooler. Its own learned calibration (group "Robusta") refines it.
  const eor = (parseFloat(eorTemp) || 210) - (cv.group === "Robusta" ? 5 : 0);
  let agtron;
  if (eor <= 200) agtron = 85;
  else if (eor <= 203) agtron = 80;
  else if (eor <= 206) agtron = 75;
  else if (eor <= 209) agtron = 70;
  else if (eor <= 212) agtron = 65;
  else if (eor <= 215) agtron = 58;
  else if (eor <= 218) agtron = 50;
  else agtron = 44;
  const darkening = (
    cv.group === "Ethiopian" ||
    (cultivar||"").toLowerCase().includes("sl28") ||
    (cultivar||"").toLowerCase().includes("sl34") ||
    (cultivar||"").toLowerCase().includes("pink bourbon")
  ) ? -7 : 0;
  let predicted = agtron + darkening;

  // Apply learned calibration for this cultivar's group, if enough measured
  // roasts exist to trust it. Falls back to the base model with zero data.
  const calibration = getAgtronCalibration();
  const cal = calibration[cv.group];
  if (cal) predicted += cal.offset;

  predicted = Math.max(35, Math.min(95, predicted));
  const range = `${Math.max(35, predicted - 4)}–${Math.min(95, predicted + 4)}`;
  let level, colour;
  if (predicted >= 78) { level = "Light"; colour = "#C8A96E"; }
  else if (predicted >= 68) { level = "Light-medium"; colour = "#B8834A"; }
  else if (predicted >= 58) { level = "Medium"; colour = "#9B6432"; }
  else if (predicted >= 48) { level = "Medium-dark"; colour = "#7A4520"; }
  else { level = "Dark"; colour = "#4A2810"; }
  return { predicted, range, level, colour, calibrated: !!cal, calibrationN: cal ? cal.n : 0 };
}

// ── AGTRON COLOUR SWATCH & STRIP ──────────────────────────────────────────────
// Maps a reading on the real Agtron scale (5 = near-black, ~140 = raw/green)
// to a representative hex colour by interpolating between anchor points —
// continuous, not banded, so two readings 2 points apart look almost
// identical rather than jumping between fixed swatch colours. Separate from
// predictAgtron's banded Light/Medium/Dark colours above, which are for
// labelling a predicted outcome, not rendering an exact instrument reading.
const AGTRON_SCALE_ANCHORS = [
  { v: 5,   c: [42, 20, 12] },   // near-black
  { v: 25,  c: [58, 32, 18] },
  { v: 45,  c: [90, 55, 28] },
  { v: 65,  c: [138, 92, 48] },
  { v: 85,  c: [178, 132, 78] },
  { v: 110, c: [214, 178, 128] },
  { v: 140, c: [232, 210, 172] }, // pale, raw/green territory
];
function agtronToColor(value) {
  const v = Math.max(5, Math.min(140, parseFloat(value) || 60));
  for (let i = 0; i < AGTRON_SCALE_ANCHORS.length - 1; i++) {
    const a = AGTRON_SCALE_ANCHORS[i], b = AGTRON_SCALE_ANCHORS[i + 1];
    if (v >= a.v && v <= b.v) {
      const t = (v - a.v) / (b.v - a.v);
      const rgb = a.c.map((av, idx) => Math.round(av + (b.c[idx] - av) * t));
      return `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
    }
  }
  return `rgb(${AGTRON_SCALE_ANCHORS[AGTRON_SCALE_ANCHORS.length-1].c.join(",")})`;
}

function AgtronStrip({ value }) {
  const v = parseFloat(value);
  const pct = isNaN(v) ? null : Math.max(0, Math.min(100, ((Math.max(5,Math.min(140,v)) - 5) / (140 - 5)) * 100));
  return (
    <div style={{ position:"relative", height:10, borderRadius:5, marginTop:6,
      background:"linear-gradient(90deg, rgb(42,20,12) 0%, rgb(58,32,18) 14%, rgb(90,55,28) 30%, rgb(138,92,48) 44%, rgb(178,132,78) 59%, rgb(214,178,128) 78%, rgb(232,210,172) 100%)" }}>
      {pct !== null && (
        <div style={{ position:"absolute", top:-4, left:`${pct}%`, width:2, height:18, background:C.ink, transform:"translateX(-1px)" }} />
      )}
    </div>
  );
}

// ── FIXED RECIPE STANDARD ─────────────────────────────────────────────────────
// Ger's roasting standard (05/10/26). The first half of every roast is the same:
// charge (return-air temperature, the way Roast Architect sets it), initial burner
// and turning point are fixed for each batch-size band. Colour change, first crack
// and the end temperature are set targets, and development time is the one thing
// that changes between roasts.
// The previous adjustments (altitude, process, moisture, density, residual heat,
// cultivar, logged diagnostics) are all still in recommendTempsAdjusted() below,
// untouched. recommendTemps() now sits in front of it: with the standard switched
// on it overrides the numbers; switched off it returns exactly what it always did.
// Charge and burner numbers marked "board" came from the Loring masterclass
// whiteboard (05/10/26) and are not proven on Ger's own roasts yet, so they are
// editable on the Profile builder screen.
const RECIPE_STD_KEY = "fdc_recipe_standard"; // local to this computer, not synced
const DRUM_KG = 70; // Loring S70
const RECIPE_STD_DEFAULTS = {
  on: true,
  bands: [
    { upToKg: 40, charge: 210, ibs: 90 },
    { upToKg: 50, charge: 220, ibs: 90 },
    { upToKg: 70, charge: 237, ibs: 95 },
  ],
  minKg: 35, maxKg: 60,
  tapSec: 44, tapTemp: 70,
  ccMin: 5, ccTemp: 167,
  fcMin: 8, fcTemp: 200,
  filter: { devSec: 80, endLo: 207, endHi: 210 },
  espresso: { devSec: 135, endLo: 214, endHi: 216 },
  robusta: { charge: 3, ibs: 2, end: 6 },
};
function loadRecipeStandard() {
  let saved = {};
  try { saved = JSON.parse(localStorage.getItem(RECIPE_STD_KEY) || "{}") || {}; } catch { saved = {}; }
  const D = RECIPE_STD_DEFAULTS;
  const num = (v, d) => { const x = parseFloat(v); return isNaN(x) ? d : x; };
  const sect = (k) => { const sv = saved[k] || {}; const o = {}; Object.keys(D[k]).forEach(f => { o[f] = num(sv[f], D[k][f]); }); return o; };
  return {
    on: saved.on === false ? false : true,
    bands: D.bands.map((b, i) => { const sb = (saved.bands && saved.bands[i]) || {}; return { upToKg: num(sb.upToKg, b.upToKg), charge: num(sb.charge, b.charge), ibs: num(sb.ibs, b.ibs) }; }),
    minKg: num(saved.minKg, D.minKg), maxKg: num(saved.maxKg, D.maxKg),
    tapSec: num(saved.tapSec, D.tapSec), tapTemp: num(saved.tapTemp, D.tapTemp),
    ccMin: num(saved.ccMin, D.ccMin), ccTemp: num(saved.ccTemp, D.ccTemp),
    fcMin: num(saved.fcMin, D.fcMin), fcTemp: num(saved.fcTemp, D.fcTemp),
    filter: sect("filter"), espresso: sect("espresso"), robusta: sect("robusta"),
  };
}
function saveRecipeStandard(next) { try { localStorage.setItem(RECIPE_STD_KEY, JSON.stringify(next)); } catch { /* storage full or blocked: the standard just keeps its defaults */ } }

const stdMmss = (sec) => { const s = Math.max(0, Math.round(sec)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };

// Smooth curve through a few points that never overshoots (monotone cubic).
function monotoneCubic(xs, ys) {
  const n = xs.length;
  const d = [], m = [];
  for (let i = 0; i < n - 1; i++) d.push((ys[i + 1] - ys[i]) / (xs[i + 1] - xs[i]));
  m[0] = d[0]; m[n - 1] = d[n - 2];
  for (let i = 1; i < n - 1; i++) m[i] = d[i - 1] * d[i] <= 0 ? 0 : (d[i - 1] + d[i]) / 2;
  for (let i = 0; i < n - 1; i++) {
    if (d[i] === 0) { m[i] = 0; m[i + 1] = 0; continue; }
    const a = m[i] / d[i], b = m[i + 1] / d[i], q = a * a + b * b;
    if (q > 9) { const t = 3 / Math.sqrt(q); m[i] = t * a * d[i]; m[i + 1] = t * b * d[i]; }
  }
  return (x) => {
    if (x <= xs[0]) return ys[0];
    if (x >= xs[n - 1]) return ys[n - 1];
    let i = 0; while (x > xs[i + 1]) i++;
    const h = xs[i + 1] - xs[i], t = (x - xs[i]) / h, t2 = t * t, t3 = t2 * t;
    return (2 * t3 - 3 * t2 + 1) * ys[i] + (t3 - 2 * t2 + t) * h * m[i] + (-2 * t3 + 3 * t2) * ys[i + 1] + (t3 - t2) * h * m[i + 1];
  };
}
// After first crack the climb eases from about 8-9 °C/min to about 6-7, so the
// end temperature follows from how long development lasts.
function standardEndTemp(fcTemp, devSec) {
  const m = Math.max(0, devSec) / 60;
  return m <= 0.5 ? fcTemp + m * 7.6 : fcTemp + 3.8 + (m - 0.5) * 6.9;
}
// The target bean-temperature curve, one point a second. The first 44 seconds
// follow the plunge shape of Roast Architect's own example roast.
function standardCurve(r) {
  const tapMin = r.tapSec / 60;
  const pts = [[tapMin, r.tapTemp], [r.ccMin, r.ccTemp], [r.fcMin, r.fcTemp], [r.endMin, r.endTemp]];
  [[65 / 60, 82], [1.5, 97], [2.0, 111], [2.67, 127], [4.0, 153], [6.5, 184], [r.fcMin + 0.5, r.fcTemp + (r.endTemp - r.fcTemp) * Math.min(0.5 / Math.max(r.devSec / 60, 0.01), 1) * 1.06]].forEach(p => {
    if (p[0] > tapMin + 0.2 && p[0] < r.endMin - 0.05 && Math.abs(p[0] - r.ccMin) > 0.4 && Math.abs(p[0] - r.fcMin) > 0.2) pts.push(p);
  });
  pts.sort((a, b) => a[0] - b[0]);
  const clean = [];
  pts.forEach(p => { const last = clean[clean.length - 1]; if (!last || (p[0] > last[0] + 0.01 && p[1] > last[1])) clean.push(p); });
  const f = monotoneCubic(clean.map(p => p[0]), clean.map(p => p[1]));
  const plunge = [[0, 1], [6, 0.741], [12, 0.522], [18, 0.339], [24, 0.196], [30, 0.092], [36, 0.026], [42, 0]];
  const scale = r.tapSec / 42;
  const endSec = Math.round(r.endMin * 60);
  const secs = [], temps = [];
  for (let s = 0; s <= endSec; s++) {
    let t;
    if (s <= r.tapSec) {
      const x = s / scale; let fr = 0;
      for (let i = 0; i < plunge.length - 1; i++) {
        if (x >= plunge[i][0] && x <= plunge[i + 1][0]) { fr = plunge[i][1] + (plunge[i + 1][1] - plunge[i][1]) * ((x - plunge[i][0]) / (plunge[i + 1][0] - plunge[i][0])); break; }
      }
      t = r.tapTemp + (r.chargeTemp - r.tapTemp) * fr;
    } else t = f(s / 60);
    secs.push(s); temps.push(Math.round(t * 10) / 10);
  }
  return { secs, temps, endSec };
}
// Plain-words checks on the curve, for someone who does not watch RoR.
function standardChecks(r, curve, std) {
  const out = [];
  const T = curve.temps;
  const ror = (s) => { const lo = Math.max(0, s - 30), hi = Math.min(T.length - 1, s + 30); return (T[hi] - T[lo]) / ((hi - lo) / 60); };
  // A real upturn is a rise of 0.4 °C a minute or more above the lowest pace so far; smaller wobbles are just the readings being rounded.
  let smooth = true, lowest = Infinity;
  for (let s = 150; s <= T.length - 1; s += 2) { const v = ror(s); if (v > lowest + 0.4) { smooth = false; break; } if (v < lowest) lowest = v; }
  const rFc = ror(Math.round(r.fcMin * 60)), rEnd = ror(T.length - 1);
  out.push(smooth
    ? { tag: "OK", text: `The pace only ever slows after the first couple of minutes: no flick and no crash. It eases from about ${Math.round(rFc)}° a minute at first crack to about ${Math.round(rEnd)}° a minute at the drop.` }
    : { tag: "Check", text: "The pace picks up again partway through, which can taste harsh or scorched. Change the targets before using this curve." });
  const inRange = r.endTemp >= r.endLo - 0.05 && r.endTemp <= r.endHi + 0.05;
  out.push({ tag: inRange ? "OK" : "Check", text: `End temperature ${r.endTemp.toFixed(1)}° is ${inRange ? "inside" : "outside"} the ${r.style} range of ${Math.round(r.endLo)} to ${Math.round(r.endHi)}°${r.rob ? " (robusta runs hotter)" : ""}.` });
  if (r.kg && r.kg < std.minKg - 0.01) out.push({ tag: "Check", text: `${r.kg} kg is below your 50% minimum of ${std.minKg} kg. The lightest band is used, so treat this one as a check-by-hand roast.` });
  else if (r.kg && r.kg > std.maxKg + 0.01) out.push({ tag: "Check", text: `${r.kg} kg is above the 85% top end of ${std.maxKg} kg. The heaviest band is used.` });
  out.push({ tag: "Note", text: `Colour change to first crack is about ${stdMmss((r.fcMin - r.ccMin) * 60)} and ${Math.round(r.fcTemp - r.ccTemp)}°. The colour-change time is a guide, not a target.` });
  out.push({ tag: "Note", text: `Development is ${stdMmss(r.devSec)}, about ${Math.round(r.devSec / (r.endMin * 60) * 100)}% of the roast. It is the one thing to change between roasts.` });
  return out;
}
function standardRecipe({ cupProfile, batchKg, batchNumToday, std, old }) {
  const kg = parseFloat(batchKg) || 0;
  const rob = !!(old.cv && old.cv.group === "Robusta");
  const style = cupProfile === "Espresso" ? "espresso" : "filter";
  const band = std.bands.find(b => (kg || std.minKg) <= b.upToKg) || std.bands[std.bands.length - 1];
  const sty = std[style];
  const charge = Math.round(band.charge + (rob ? std.robusta.charge : 0));
  const ibs = Math.min(100, Math.round(band.ibs + (rob ? std.robusta.ibs : 0)));
  const endTemp = standardEndTemp(std.fcTemp, sty.devSec) + (rob ? std.robusta.end : 0);
  const endMin = std.fcMin + sty.devSec / 60;
  const r = {
    kg, style, rob, charge, chargeTemp: charge, ibs,
    tapSec: std.tapSec, tapTemp: std.tapTemp, ccMin: std.ccMin, ccTemp: std.ccTemp,
    fcMin: std.fcMin, fcTemp: std.fcTemp, endMin, endTemp, devSec: sty.devSec,
    endLo: sty.endLo + (rob ? std.robusta.end : 0), endHi: sty.endHi + (rob ? std.robusta.end : 0),
  };
  const curve = standardCurve(r);
  const firstAbove = (deg, fallback) => { for (let i = 0; i < curve.temps.length; i++) if (curve.secs[i] > r.tapSec && curve.temps[i] >= deg) return curve.secs[i] / 60; return fallback; };
  const dryEndMin = Math.round(firstAbove(150, 4) * 100) / 100;
  const maillardEndMin = Math.round(firstAbove(191, std.fcMin - 0.5) * 100) / 100;
  const totalMin = Math.round(endMin * 100) / 100;
  const eor = Math.round(endTemp);
  const { fcPct, dtrPct } = calcDevMetrics(std.fcMin, totalMin);
  const batchNum = parseInt(batchNumToday) || 1;
  const styleName = style === "espresso" ? "Espresso" : "Filter";
  return {
    ...old,
    charge, eor, ibs, tapTarget: std.tapTemp,
    fcTarget: std.fcMin, totalMin, dryEndMin, maillardEndMin,
    fcPct, dtrPct, fcRuleCompliant: true,
    dtr: `${dtrPct}%`, totalTime: `~${totalMin} min`,
    chargeNotes: `Fixed standard: charge ${charge}° (return-air temperature, the way Roast Architect sets it) for ${kg ? kg + " kg" : "this batch size"}. Altitude, process, moisture and density do not change it.${rob ? ` Robusta: +${std.robusta.charge}° charge, +${std.robusta.ibs}% burner, +${std.robusta.end}° end.` : ""}`,
    eorNotes: `${styleName}: drop at about ${eor}°, ${stdMmss(sty.devSec)} after first crack. Development time is the lever; change it a little at a time and taste the result.`,
    timingNotes: `First crack about ${stdMmss(std.fcMin * 60)} at ${std.fcTemp}°, colour change about ${stdMmss(std.ccMin * 60)} at ${std.ccTemp}°. Drying time is not fixed; it falls out of a correct start.`,
    tapNotes: `Fixed turning point: about ${std.tapTemp}° at ${stdMmss(std.tapSec)}, initial burner ${ibs}%. These do not change with the coffee, so every roast starts the same. Burner moves after the start are set in Roast Architect; the Hub does not suggest them.`,
    residualNote: batchNum === 1 ? old.residualNote : "Fixed standard: the charge is not trimmed for heat left from earlier batches. Let the roaster come back to the same reset point before every charge so each batch starts alike.",
    chargeProbe: old.charge, // what the previous recipe expected the Hub's own probe to read at charge; Roast Mode's charge detection still uses it
    old: { charge: old.charge, ibs: old.ibs, eor: old.eor, fcTarget: old.fcTarget, totalMin: old.totalMin, tapTarget: old.tapTarget },
    standard: { ...r, pct: kg ? Math.round(kg / DRUM_KG * 100) : null, lbs: kg ? Math.round(kg * 2.20462 * 10) / 10 : null, dtrPct, checks: standardChecks(r, curve, std) },
  };
}
// Files for Roast Architect (formats read from its own example exports, 05/10/26).
function standardLoringCsv(curve) {
  const rows = ["RoastTimeSeconds,DegreesCelciusOnOff,BeanTemperature"];
  for (let s = 0; s <= curve.endSec; s += 6) rows.push(`${s},1,${curve.temps[s].toFixed(1)}`);
  if (curve.endSec % 6 !== 0) rows.push(`${curve.endSec},1,${curve.temps[curve.endSec].toFixed(1)}`);
  return "\uFEFF" + rows.join("\r\n") + "\r\n";
}
function standardArtisanJson(curve, r, title) {
  const idx = (min) => Math.min(curve.endSec, Math.round(min * 60));
  return JSON.stringify({
    mode: "C", title, roastertype: "Loring", roasterheating: 2, samplinginterval: 1.0,
    timex: curve.secs, temp1: curve.secs.map(() => -1), temp2: curve.temps,
    timeindex: [0, idx(r.ccMin), idx(r.fcMin), 0, 0, 0, curve.endSec, 0],
  });
}
function downloadTextFile(name, text, mime) {
  const blob = new Blob([text], { type: mime || "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// The recipe engine the rest of the Hub calls. With the standard on it returns the
// fixed-standard numbers (same fields, plus .standard and .old); off, it returns
// exactly what the previous adjustments work out.
function recommendTemps(args) {
  const old = recommendTempsAdjusted(args);
  const std = loadRecipeStandard();
  if (!std.on) return old;
  return standardRecipe({ ...args, std, old });
}

// The previous adjustment-based recipe, unchanged. Called by recommendTemps() above.
function recommendTempsAdjusted({ masl, process, cupProfile, batchKg, moisture, density, cultivar, batchNumToday, prevRoastLevel }) {
 const m = parseFloat(masl) || 1750;
 const batch = parseFloat(batchKg) || 35;
 const moist = parseFloat(moisture) || 10.5;
 // Density has no reliable default the way moisture/MASL do — it only
 // adjusts anything when an actual measured value has been entered.
 const dens = parseFloat(density) || null;
 const isEspresso = cupProfile === "Espresso";
 const isFilter = cupProfile === "Filter";
 const isNatural = process === "Natural";
 const isHoney = process === "Honey";
 const isAnaerobic = process === "Anaerobic";
 const isWashed = process === "Washed";
 const isDecaf = (process || "").toLowerCase().includes("decaf");
 const isDecafGentle = isDecaf && (process || "").toLowerCase().includes("sugarcane");
 const isDecafHarsh = isDecaf && !isDecafGentle; // chemical / Swiss Water — more structural damage

 // Get cultivar intelligence
 const cv = getCultivarData(cultivar);

 // ── CHARGE TEMP ──────────────────────────────────────────────────────────────
 // six variables — machine design, batch size, bean density, bean size, process, roast time
 // Higher altitude = denser bean = needs more careful entry
 // BUT Loring is convection-dominant (recirculating) — less conductive risk than classic drum
 // Loring can run slightly lower charges than a Probat would for same bean
 let charge = 175;
 if (m >= 2000) charge = 160;
 else if (m >= 1800) charge = 163;
 else if (m >= 1600) charge = 167;
 else if (m >= 1400) charge = 170;
 else charge = 174;

 // Process: Natural/honey have mucilage = higher surface scorching risk = lower charge
 // Washed can tolerate hotter charge than natural
 if (isNatural) charge -= 2; // mucilage = scorching risk, lower entry
 else if (isHoney) charge -= 1; // partial mucilage, modest reduction
 else if (isAnaerobic) charge -= 1; // fermentation softens cell structure, careful entry
 else if (isDecafGentle) charge -= 6; // sugarcane (EA) — cell structure already compromised by the process itself, independent of moisture reading. This is the dominant risk factor for decaf, not a modest tweak.
 else if (isDecafHarsh) charge -= 9; // chemical/Swiss Water — most porous, most fragile, highest scorch risk of any process type
 else if (isWashed) charge += 2; // washed tolerates hotter charge — no surface sugars/mucilage

 // larger batch = greater temp drop at charge = hotter charge needed
 // But Loring recirculation is more stable than classic drum
 if (batch >= 60) charge -= 4;
 else if (batch >= 45) charge -= 2;
 else if (batch >= 35) charge -= 1;
 else if (batch <= 20) charge += 3;

 // moister beans need more aggressive heat; drier beans need judicious heat
 // NOTE: skip for decaf — the moisture reading on a decaf bean reflects the
 // processing method (e.g. sugarcane EA re-hydration), not raw bean density,
 // so it isn't a meaningful signal here and would otherwise cancel out the
 // decaf charge protection above. Decaf gets its own moisture note instead.
 if (!isDecaf) {
   if (moist > 12) charge += 2;
   else if (moist > 11) charge += 1;
   else if (moist < 9.5) charge -= 2;
 }

 // Measured density is a more direct read on bean hardness than the MASL
 // proxy above — a genuinely dense/hard bean resists heat and can take a
 // hotter charge without scorching (needs it, even, to develop evenly all
 // the way through); a low-density/porous bean heats through fast and
 // scorches easily, so it wants a cooler entry. Skipped for decaf for the
 // same reason moisture is — the processing method dominates there, not
 // raw bean hardness.
 // Robusta measures low-density but roasts dense — a low density reading would
 // wrongly pull its charge down, so density only nudges arabica.
 if (!isDecaf && dens != null && cv.group !== "Robusta") {
   if (dens >= 780) charge += 2;
   else if (dens >= 750) charge += 1;
   else if (dens < 650) charge -= 3;
   else if (dens < 690) charge -= 1;
 }

 // Residual heat: drum/system retains heat from prior batches — later batches in the
 // day, and especially a prior dark roast, need a lower charge to compensate.
 // Follows Between Batch Protocol (BBP) principle: reset thermal energy to the same
 // starting conditions every time, rather than just nudging charge temp by feel.
 const batchNum = parseInt(batchNumToday) || 1;
 let residualNote = "";
 if (batchNum === 1) {
   residualNote = "First roast of the day — warm up properly before charging: 30–60 min, and don't charge until bean-probe and exhaust temps are within ~10°C of each other. Charging too early is the single most common cause of an inconsistent first batch.";
 } else if (batchNum >= 2 && batchNum <= 3) {
   charge -= 2;
   residualNote = `Batch ${batchNum} of the day — modest residual heat, charge trimmed 2°C to compensate. Let the roaster return to its normal idle/reset point between batches rather than charging on a fixed clock — consistency comes from matching starting conditions, not elapsed time.`;
 } else {
   charge -= 4;
   residualNote = `Batch ${batchNum} of the day — significant residual heat built up, charge trimmed 4°C to compensate. If batches keep creeping faster as the day goes on, your between-batch reset isn't fully clearing accumulated heat — worth lengthening the gap or adding an idle/cool step.`;
 }
 if (prevRoastLevel === "Dark") {
   charge -= 3;
   residualNote += " Previous batch was a dark roast — system is holding extra heat, charge trimmed a further 3°C.";
 } else if (prevRoastLevel === "Medium") {
   charge -= 1;
   residualNote += " Previous batch was medium — slight extra heat retained, charge trimmed 1°C further.";
 }
 if (batchNum >= 2) {
   residualNote += " Sequencing tip: where the day's lineup allows, roast smaller batches before larger, lighter roasts before darker, and your least critical coffee before your most — each keeps the thermal step to the next batch smaller.";
 }

  // Robusta behaves like a very dense bean in the roaster — hotter entry.
  if (cv.group === "Robusta") charge += 3;

  // Cultivar adjustment: Ethiopian group + large beans = tipping risk = softer entry
  if (cv.group === "Ethiopian") charge -= 2;
 if (cv.group === "Typica" && (cultivar||"").toLowerCase().includes("maragogipe")) charge -= 2;
 if (cv.group === "Typica" && (cultivar||"").toLowerCase().includes("pacamara")) charge -= 2;

 // ── DIAGNOSTIC FEEDBACK LOOP ─────────────────────────────────────────────────
 // Logged cupping diagnostics for this cultivar+process nudge the recipe toward
 // what's actually been going wrong in the cup — data refining the static rules
 // above, not replacing them. Requires DIAGNOSTIC_ADJUST_MIN_SAMPLES logged
 // entries before it does anything.
 const diagAdjust = getDiagnosticAdjustment(cultivar, process);
 charge += diagAdjust.charge;

 // Sanity clamp — several adjustments stacking (e.g. high altitude + harsh decaf + large
 // batch + late in day + prior dark roast + Ethiopian) can compound to an unrealistically
 // low charge. Keep within a range a Loring can actually kick a roast off from.
 const preClampCharge = charge;
 charge = Math.max(150, Math.min(190, charge));
 const wasClamped = Math.round(preClampCharge) !== Math.round(charge);

 charge = Math.round(charge);

 // ── ROAST TIMING ( + cultivar timing) ────────────────
 // FC should begin at 75–80% of total roast time
 // cultivar determines the timing shape/speed
 // Combined: use cultivar FC target, calculate total time from 's 75–80% rule

 const fcTarget = cv.fcTime; // minutes, from cultivar data
 // NOTE: cupProfile (espresso/filter) and process (natural/washed/etc) are
 // independent dimensions — a natural coffee roasted for espresso is a very
 // common real combination, not an edge case. Each adjustment is applied on
 // its own line so both can take effect together; a single ternary chain
 // here previously meant isEspresso being true silently skipped checking
 // isNatural at all, so any natural-process espresso coffee got the same
 // development-time bump as a washed espresso, completely losing its
 // shorter-development treatment.
 const devTargetMin = cv.devTime
   + (isEspresso ? 0.25 : 0)
   + (isNatural ? -0.2 : 0)
   + (isDecafGentle ? -0.05 : isDecafHarsh ? -0.15 : 0);
 const totalMin = Math.round((fcTarget + devTargetMin) * 10) / 10;

 // Verify compliance and flag if off
 const { fcPct, dtrPct } = calcDevMetrics(fcTarget, totalMin);
 const fcRuleCompliant = fcPct >= 74 && fcPct <= 81;

 // ── EoR TEMP ─────────────────────────────────────────────────────────────────
 // EoR = degree of sugar browning
 // Lighter = nutty/floral/fruity/sweeter. Darker = caramel/chocolate/smoky/more bitter.
 // confirms: caramelization begins at 171°C, decreases sweetness, increases bitterness
 let eor = isEspresso ? 213 : 206;

 // Process adjustments
 if (isNatural) eor -= 2;
 else if (isHoney) eor -= 1;
 else if (isAnaerobic) eor -= 1;
 else if (isWashed) eor += 1;
 else if (isDecafGentle) eor -= 3; // sugarcane (EA) — browns faster and more unpredictably than raw beans; drop noticeably lighter to protect what flavour survives the process
 else if (isDecafHarsh) eor -= 6; // chemical/Swiss Water — most heat-damaged structure, darkens fastest, needs the lightest drop of any process type

 // Altitude/density
 // NOTE: washed coffees are deliberately restrained relative to non-washed at
 // the same altitude (protecting florality/acidity rather than chasing sugar
 // browning) — but that restraint should still scale with altitude, not go
 // completely flat. Previously both 1800m and 2500m+ washed landed on the
 // exact same +0, so e.g. a 1800m Colombia and a 2200m Gesha got identical
 // altitude treatment despite being very different beans.
 if (m >= 2000 && !isWashed) eor += 2;
 else if (m >= 2000 && isWashed) eor += 1;
 else if (m >= 1800 && !isWashed) eor += 1;
 else if (m >= 1800 && isWashed) eor += 0;
 else if (m < 1400) eor -= 1;

 // Cultivar EoR adjustment from data
 eor += (cv.eorAdj || 0);

 // Batch momentum
 if (batch >= 60) eor -= 1;
 else if (batch <= 20) eor += 1;

 // Diagnostic feedback loop — same source as the charge adjustment above
 eor += diagAdjust.eor;

 eor = Math.round(eor);

 // DTR target ranges (development time as % of total roast)
 // Published guidance: 20–25% DTR. Tighter sweet spot in practice: 20–22% espresso, 18–21% filter
 // Naturals: process sweetness = shorter dev needed, lower end of range
 const dtrBase = isEspresso
   ? (isNatural ? "20–21%" : "20–22%")
   : isFilter
   ? (isNatural ? "18–19%" : "18–21%")
   : (isNatural ? "19–20%" : "19–21%");
 const totalTimeStr = `~${totalMin} min`;

 // Drying timing from moisture AND batch size
 // larger batch = more thermal mass = longer drying phase
 const batchDryAdj = batch >= 60 ? 0.75 : batch >= 45 ? 0.5 : batch >= 35 ? 0.25 : 0;
 // A dense bean takes longer for heat to reach its core, so it wants a
 // slightly longer drying phase to come through evenly; a porous one dries
 // out faster and can move on sooner.
 const densDryAdj = (dens == null || cv.group === "Robusta") ? 0 : dens >= 780 ? 0.4 : dens < 650 ? -0.3 : 0;
  const robustaDryAdj = cv.group === "Robusta" ? 0.4 : 0;  // dense-behaving — slightly longer drying for even heat
 const dryEndMin = Math.round(((moist > 11.5 ? 6.0 : moist > 10.5 ? 5.5 : 5.0) + batchDryAdj + densDryAdj + robustaDryAdj) * 100) / 100;
 const maillardBurner = isEspresso ? 62 : 60;
 const maillardEndMin = fcTarget - 0.5; // enter development ~30s before FC

 // ── RATIONALE NOTES ──────────────────────────────────────────────────────────
 const cultivarNote = cv.group !== "Unknown"
 ? `${cultivar} (${cv.group} group): ${cv.notes}`
 : "Cultivar unknown — enter for precise timing.";

 const chargeNotes = [
 m >= 1800 ? "High altitude — dense bean. Lower charge prevents tipping."
 : m < 1400 ? "Lower altitude — less dense. Higher charge builds drying momentum."
 : "Mid altitude — standard charge.",
 isNatural ? "Natural: mucilage on the surface scorches easily — charge is reduced. Monitor carefully in first 2 minutes."
 : isHoney ? "Honey: partial mucilage present — modest charge reduction. Less risk than full natural."
 : isAnaerobic ? "Anaerobic: fermentation softens cell structure — careful entry, reduced charge."
 : isDecafGentle ? "Decaf (sugarcane/EA): structure partially compromised — charge reduced, heat penetrates faster than regular washed."
 : isDecafHarsh ? "Decaf (chemical/Swiss Water): fragile, porous bean structure. Lower charge essential — heat penetrates faster than a regular bean of the same density, high scorching risk."
    : "Washed: no surface mucilage — tolerates hotter charge. Standard entry.",
 cv.group === "Robusta" ? "Robusta: roasts like a very dense high-altitude arabica despite growing low — charge raised 3°C and initial burner +2%. A low density reading is ignored for robusta (it measures light but roasts dense)." : "",
 batch >= 45 ? "Large batch — charge reduced to account for thermal mass."
 : batch <= 20 ? "Small batch — charge raised to compensate for rapid heat loss."
 : "",
 moist > 11 ? "Higher moisture: more heat needed to drive off water in drying phase."
 : moist < 9.5 ? "Low moisture: bean is drier and more porous — judicious entry to avoid scorching."
 : "",
 dens != null ? (
   dens >= 780 ? "High density: hard, heat-resistant bean — charge raised and drying extended slightly for even penetration."
   : dens >= 750 ? "Above-average density: slightly hotter charge to develop it evenly."
   : dens < 650 ? "Low density: soft, porous bean — charge reduced and drying shortened, high scorch risk otherwise."
   : dens < 690 ? "Below-average density: modest charge reduction."
   : "Typical density — no adjustment needed."
 ) : "",
 wasClamped ? `Note: several factors stacked toward an extreme charge (${Math.round(preClampCharge)}°C) — held at ${charge}°C, the realistic working range for this machine. Treat this one as a check-by-hand roast rather than pure auto-trust.` : "",
 ].filter(Boolean).join(" ");

 const eorNotes = [
 isEspresso
 ? "Espresso: higher EoR for sugar browning → caramel, body, sweetness. Balances extraction bitterness."
 : "Filter: restrained EoR preserves brightness and fruit. lighter = sweeter even below taste threshold.",
 isNatural
 ? "Natural: earlier drop — fruitiness bell curve. Catch it ascending. Over-development loses the fruit ."
 : isHoney ? "Honey: process-derived sweetness — don't overwrite with roast caramelisation."
 : isAnaerobic ? "Anaerobic: fermentation did the flavour work — roast is the frame."
 : isDecafGentle ? "Decaf (sugarcane/EA): structure close to normal washed — browning behaviour fairly predictable, slight pull back on EoR."
 : isDecafHarsh ? "Decaf (chemical/Swiss Water): bean already structurally altered before roasting. Browns faster and darker for a given temp — visual cues unreliable. Shorter, gentler approach avoids baked/flat flavours."
 : "Washed: full sugar browning needed. Sweetness must come from the bean — EoR is the primary lever.",
 m >= 1800 && isWashed ? "High-altitude washed: restrain EoR to protect florality and acidity — don't push sugar browning."
 : m >= 1800 ? "Dense bean: can handle fuller development, more heat needed to unlock inner cell sugars."
 : m < 1400 ? "Low density: restrained EoR — over-development goes flat/woody quickly."
 : "",
    cv.eorAdj < 0 ? `${cultivar}: darkens more quickly than typical — EoR adjusted down .` : "",
    cv.group === "Robusta" ? "Robusta: drop ~5–7°C hotter than an arabica would need — the surface can look done while the inside is still pale and grainy. Check ground colour against bean colour; Agtron readings don't line up with your arabicas, so learn robusta's own numbers." : "",
 ].filter(Boolean).join(" ");

 const timingNotes = [
 `${cultivar||"This cultivar"} target FC: ~${fcTarget} min . 75–80% rule: FC at ${fcPct}% of total roast = ${fcRuleCompliant ? "✓ compliant" : "⚠ review timing"}.`,
 cv.speed === "fast" ? "Fast-roasting cultivar: push quickly to FC to preserve florality and fruit. Slower approach risks savory/herbaceous notes."
 : cv.speed === "slow" ? "Slow-roasting cultivar: longer approach to FC reduces herbaceousness and papery notes."
 : cv.speed === "flexible" ? "Flexible cultivar: wide sweet spot — fast approach for floral/fruit, slow for caramel/body."
 : "Standard timing cultivar.",
 isNatural ? "Natural: 15–30s shorter development recommended . Process-derived sweetness doesn't need as much roast development."
 : isWashed ? "Washed: full development needed — sweetness comes from the roast, not the process."
 : isDecafGentle ? "Decaf (sugarcane/EA): slightly shorter development is sensible but bean structure is close to normal washed — less urgency than chemically processed decaf."
 : isDecafHarsh ? "Decaf (chemical/Swiss Water): shorter, gentler development. Bean is already structurally compromised — extending development risks baked/flat flavours fast."
 : "",
 ].filter(Boolean).join(" ");

 // ── TAP (Turn Around Point) & IBS% (Initial Burner Setting) ──────────────────
 // Loring-specific: pair charge temp with initial burner % to hit a consistent
 // TAP regardless of batch size or ambient roastery conditions.
 // Larger/colder batches need higher IBS% to reach the same TAP as smaller/warmer ones.
 // The 20% floor is Loring's own: manual p. 54 defines the Roast Burner range as
 // 20–100%, with 20% the minimum burner output while roasting. (Idle is a separate
 // reduced-output range on the machine — its "100%" is a capped idle maximum,
 // not full thermal output, so idle burner numbers aren't comparable to roast ones.)
 let ibs = 49; // baseline initial burner setting
 if (batch >= 60) ibs += 6;
 else if (batch >= 45) ibs += 3;
 else if (batch <= 20) ibs -= 4;
 if (moist > 11.5) ibs += 2;
 else if (moist < 9.5) ibs -= 2;
 // Decaf and Ethiopian-group beans are treated as materially higher scorch/
 // tipping risk everywhere else in this function (charge, EoR) — IBS was the
 // one lever left untouched for them, which is inconsistent: initial burner
 // % is exactly the setting that determines how hard that fragile structure
 // gets hit in the first place.
 if (isDecafHarsh) ibs -= 6;
 else if (isDecafGentle) ibs -= 4;
  if (cv.group === "Ethiopian") ibs -= 3;
  if (cv.group === "Robusta") ibs += 2; // dense-behaving bean — a little more heat from the start
 ibs = Math.max(M.burnerFloorPct, Math.min(70, Math.round(ibs)));

 // TAP target — Loring turning point typically 65–80°C bean probe
 // Higher charge = slightly higher TAP, but relationship is subtle
 // Base 70°C + small charge influence
 const tapTarget = Math.round(70 + (charge - 165) * 0.15);

 const tapNotes = `Target TAP ~${tapTarget}°C at IBS ${ibs}%. Pair charge temp and initial burner setting to hit this TAP consistently — adjust IBS% (not just charge temp) for batch size and ambient conditions so TAP stays steady across seasons and batch sizes. Lower TAP = longer drying phase ahead. Roast burner range is ${M.burnerFloorPct}–100% on the ${M.brand} — never plan below ${M.burnerFloorPct}%.`;

 return {
 charge, eor, chargeNotes, eorNotes, timingNotes, cultivarNote,
 dtr: dtrBase, totalTime: totalTimeStr,
 fcTarget, totalMin, dryEndMin, maillardEndMin, maillardBurner,
 fcPct, dtrPct, fcRuleCompliant, cv,
 ibs, tapTarget, tapNotes, residualNote,
 diagAdjust,
 };
}

// ── DT / RoR / END TEMP CALCULATOR ───────────────────────────────────────────
// Loring-specific relationship: any two of Development Time, average RoR during
// development, and End Temp let you calculate the third.
// devTempRise = avgRoR * devTimeMin
// endTemp = fcTemp + devTempRise
// devTimeMin = devTempRise / avgRoR
function calcDevTriangle({ devTimeMin, avgRoR, fcTemp, endTemp }) {
 const fc = parseFloat(fcTemp) || 197;
 // If we have devTime and RoR, calculate endTemp
 if (devTimeMin && avgRoR && !endTemp) {
 const rise = parseFloat(devTimeMin) * parseFloat(avgRoR);
 return { endTemp: Math.round((fc + rise) * 10) / 10, devTempRise: Math.round(rise*10)/10, solvedFor:"endTemp" };
 }
 // If we have devTime and endTemp, calculate avgRoR
 if (devTimeMin && endTemp && !avgRoR) {
 const rise = parseFloat(endTemp) - fc;
 const ror = rise / parseFloat(devTimeMin);
 return { avgRoR: Math.round(ror*10)/10, devTempRise: Math.round(rise*10)/10, solvedFor:"avgRoR" };
 }
 // If we have RoR and endTemp, calculate devTime
 if (avgRoR && endTemp && !devTimeMin) {
 const rise = parseFloat(endTemp) - fc;
 const time = rise / parseFloat(avgRoR);
 return { devTimeMin: Math.round(time*100)/100, devTempRise: Math.round(rise*10)/10, solvedFor:"devTimeMin" };
 }
 return null;
}

// Precision note: missing FC temp by 2°F (~1.1°C) ≈ 10 sec off planned development time.
// Missing by 6°F (~3.3°C) ≈ 30 sec off. Roughly 5 sec of DT drift per 1°F of FC miss.
function fcMissImpact(missDegF) {
 const sec = Math.round(Math.abs(missDegF) * 5);
 return sec;
}

// ── GENERATE PROFILE ─────────────────────────────────────────────────────────
// ── GENERATE ROAST STEPS TABLE ───────────────────────────────────────────────
// Granular checkpoint-by-checkpoint roast guide: time, bean temp, burner %, RoR target, notes
// Built from profile targets — interpolated curve following the declining-RoR principle
function generateRoastSteps(profile) {
  const { charge, eor, fcTarget, totalMin, dryEndMin, maillardEndMin, ibs, tapTarget, cv, process, standard } = profile;
  const isNatural = process === "Natural";
  const isWashed = process === "Washed";
  const isDecaf = (process||"").toLowerCase().includes("decaf");

  const tapTime = standard ? standard.tapSec / 60 : 1.15 + (charge > 172 ? 0.1 : 0);
  // Robusta's first crack runs a few degrees hotter — a starting estimate the
  // Hub replaces with your own logged first cracks for that coffee.
  const FC_TEMP = standard ? standard.fcTemp : cv && cv.group === "Robusta" ? 200 : isDecaf ? 193 : 196;

  const maillardStartTemp = 150;
  const dryEndTemp = 150;

  const lerp = (t, t0, t1, v0, v1) => v0 + (v1 - v0) * Math.max(0, Math.min(1, (t - t0) / (t1 - t0)));

  const maillardMidTime = (dryEndMin + maillardEndMin) / 2;
  const maillardMidTemp = Math.round(lerp(maillardMidTime, dryEndMin, maillardEndMin, dryEndTemp, 188));
  const approachFcTemp = 191;
  const devMidTime = (fcTarget + totalMin) / 2;
  const devMidTemp = Math.round(lerp(devMidTime, fcTarget, totalMin, FC_TEMP, eor));

  const toTimeStr = (min) => {
    const m = Math.floor(min), sec = Math.round((min - m) * 60);
    return `${m}:${String(sec).padStart(2,"0")}`;
  };

  // Fixed standard: only the starting burner is set by the Hub. After that the burner follows the
  // Roast Architect profile, so the other steps show a dash and Roast Mode suggests no burner moves.
  const burnerAt = (phase) => {
    if (standard) return (phase === "charge" || phase === "tap") ? ibs : phase === "drop" ? 0 : "—";
    switch(phase) {
      case "charge": return ibs;
      case "tap": return ibs;
      case "drying": return Math.min(70, ibs + 8);
      // Cap raised from 65 to 78: at 65, any batch ≥45kg (ibs ≥52) saturated
      // this value flat regardless of moisture/batch differences, and at the
      // top of the real IBS range it became numerically identical to the
      // Drying step above — erasing the ramp-up shape across the one
      // transition (entering Maillard) where it matters most.
      case "maillardStart": return Math.min(M.maillardCapPct, ibs + 13);
      case "maillardMid": return Math.max(M.burnerFloorPct, ibs + 10);
      case "approachFc": return Math.max(M.burnerFloorPct, ibs + 5);
      case "fc": return Math.max(M.burnerFloorPct, ibs - 2);
      case "dev": return Math.max(M.burnerFloorPct, ibs - 8);
      case "drop": return 0;
      default: return ibs;
    }
  };

  const steps = [
    { phase: "Charge", time: 0, timeStr: "0:00", temp: charge, burner: burnerAt("charge"), ror: "—",
      notes: `Charge at ${charge}°C. Adequate energy early establishes high ΔT. Clean entry.` },
    { phase: "Turning Point", time: tapTime, timeStr: toTimeStr(tapTime), temp: `~${tapTarget}`, burner: burnerAt("tap"), ror: "declining",
      notes: `Hold burner steady. Don't chase the dip — bean is absorbing heat, not losing it. Thermometric lag means probe reads low here.` },
    { phase: "Drying", time: dryEndMin, timeStr: toTimeStr(dryEndMin), temp: `~${dryEndTemp}`, burner: burnerAt("drying"), ror: "11–13",
      notes: `Moisture escaping steadily. Target drying end ~${toTimeStr(dryEndMin)}. Momentum established here — Maillard self-sustaining above 160°C.` },
    { phase: "Maillard Start", time: dryEndMin + 0.1, timeStr: toTimeStr(dryEndMin+0.1), temp: `~${maillardStartTemp}`, burner: burnerAt("maillardStart"), ror: "10–12",
      notes: `Maillard zone begins — primary flavour development. ${isWashed ? "Washed: allow full Maillard — sweetness must come from the bean." : isNatural ? "Natural: shorter dev needed later — process sweetness already present." : "Standard Maillard progression."} RoR must be declining.` },
    { phase: "Maillard Mid", time: maillardMidTime, timeStr: toTimeStr(maillardMidTime), temp: `~${maillardMidTemp}`, burner: burnerAt("maillardMid"), ror: "8–10",
      notes: `Begin pulling burner back steadily. Caramelisation starts ~171°C. Aroma development accelerating.` },
    { phase: "Approaching FC", time: maillardEndMin, timeStr: toTimeStr(maillardEndMin), temp: `~${approachFcTemp}`, burner: burnerAt("approachFc"), ror: "6–8",
      notes: `RoR often flatlines just before FC — anticipate and counter before it crashes. FC due in ~${Math.round((fcTarget-maillardEndMin)*60)}s.` },
    { phase: "First Crack", time: fcTarget, timeStr: toTimeStr(fcTarget), temp: FC_TEMP, burner: burnerAt("fc"), ror: "4–6",
      notes: `FC should begin here — ${cv?.speed === "fast" ? "fast-roasting cultivar, may arrive slightly early" : cv?.speed === "slow" ? "slower cultivar, may arrive slightly late" : "on target"}. Listen for the crack, don't just watch temp.` },
    { phase: "Development", time: devMidTime, timeStr: toTimeStr(devMidTime), temp: `~${devMidTemp}`, burner: burnerAt("dev"), ror: "2–4",
      notes: `Gentle declining RoR. Don't crash it. ${isNatural ? "Natural: catch the fruit ascending — don't over-develop." : "Full development for sweetness — don't rush the drop."}` },
    { phase: "Drop", time: totalMin, timeStr: toTimeStr(totalMin), temp: eor, burner: 0, ror: "—",
      notes: `Drop at ${eor}°C. Total roast ~${toTimeStr(totalMin)}. Engage air cool immediately.` },
  ];

  if (standard) {
    steps[0].notes = `Charge at ${charge}°C return air, initial burner ${ibs}%. Fixed standard: the same start every roast.`;
  }
  return steps;
}

// Where today's roasting stands, worked out from Roast History: which batch
// of the day this is, and how dark the previous one was — the two things the
// charge temperature adjusts for residual heat. Only counts real roasts
// (weighed, or with a proper logged curve), not empty test rows.
function roastLevelBucket(eor, cultivar) {
  if (!eor) return "";
  const l = predictAgtron(eor, cultivar).level;
  return (l === "Light" || l === "Light-medium") ? "Light" : l === "Medium" ? "Medium" : "Dark";
}
function todaysBatchInfo() {
  let hist = [];
  try { hist = JSON.parse(localStorage.getItem("fdc_history") || "[]"); } catch { hist = []; }
  const today = localIsoDate();
  const todays = hist.filter(r => r.date === today && parseFloat(r.roasted) > 0)
    .sort((a, b) => (b.id || 0) - (a.id || 0));
  const last = todays[0];
  return { batchNumToday: String(todays.length + 1), prevRoastLevel: last ? roastLevelBucket(last.eor, last.cultivar) : "", lastOrigin: last?.origin || "" };
}
// Builds everything Roast Mode needs from a Profile Builder-style form — the
// same targets Profile Builder's own "Start Roast Mode" produces. Used to
// start a roast straight from the queue, and for "next batch".
function buildRoastProfileFromForm(form) {
  const p = generateProfile(form);
  return {
    ...form,
    batch: form.batchId,
    charge: p.charge,
    eor: p.eor,
    fcTarget: p.rec.fcTarget,
    totalMin: p.totalMin || (p.rec.fcTarget + (p.rec.cv?.devTime || 1.5)),
    dryEndMin: p.rec.dryEndMin,
    maillardEndMin: p.rec.maillardEndMin,
    dtr: p.dtr,
    ibs: p.rec.ibs,
    tapTarget: p.rec.tapTarget,
    chargeProbe: p.rec.chargeProbe,
    cv: p.rec.cv,
    cultivarNote: p.rec.cultivarNote,
    residualNote: p.rec.residualNote,
    batchKg: form.batchKg,
    origin: form.origin,
    steps: p.steps,
  };
}

function generateProfile({ origin, cultivar, masl, process, cupProfile, batchKg, moisture, density, chargeOverride, eorOverride, batchNumToday, prevRoastLevel }) {
 const rec = recommendTemps({ masl, process, cupProfile, batchKg, moisture, density, cultivar, batchNumToday, prevRoastLevel });
 const charge = (rec.standard ? null : chargeOverride) || rec.charge;
 const eor = (rec.standard ? null : eorOverride) || rec.eor;

 // Single source of truth for the step-by-step roast guide —
 // same generator used by Roast Mode, so edits made here carry straight through.
 const steps = generateRoastSteps({
 charge, eor, fcTarget: rec.fcTarget, totalMin: rec.totalMin,
 dryEndMin: rec.dryEndMin, maillardEndMin: rec.maillardEndMin,
 ibs: rec.ibs, tapTarget: rec.tapTarget, cv: rec.cv, process, standard: rec.standard,
 });

 return { steps, charge, eor, totalTime: rec.totalTime, totalMin: rec.totalMin, dtr: rec.dtr, rec };
}

// ── ROAST CURVE ───────────────────────────────────────────────────────────────
function RoastCurve({ steps, totalMin = 13, charge = 165, eor = 210, fcTarget }) {
 const canvasRef = useRef(null);

 useEffect(() => {
 const canvas = canvasRef.current;
 if (!canvas) return;
 const dpr = window.devicePixelRatio || 1;
 const W = canvas.offsetWidth, H = canvas.offsetHeight;
 canvas.width = W * dpr; canvas.height = H * dpr;
 const ctx = canvas.getContext("2d");
 ctx.scale(dpr, dpr);

 const pad = { l: 42, r: 48, t: 20, b: 38 };
 const cw = W - pad.l - pad.r, ch = H - pad.t - pad.b;
 const xMax = Math.max(totalMin, 1) + 0.5;
 const minT = 60, maxT = Math.max(charge, eor) + 15;
 const minR = 0, maxR = 20;

 const xOf = m => pad.l + (m / xMax) * cw;
 const yOfT = t => pad.t + ch - ((t - minT) / (maxT - minT)) * ch;
 const yOfR = r => pad.t + ch - ((r - minR) / (maxR - minR)) * ch;

 // Background
 ctx.fillStyle = C.cream2;
 ctx.fillRect(0, 0, W, H);

 // Grid lines
 ctx.strokeStyle = C.parchment;
 ctx.lineWidth = 0.75;
 const tGridStep = maxT > 220 ? 40 : 20;
 for (let t = Math.ceil(minT/tGridStep)*tGridStep; t <= maxT; t += tGridStep) {
 ctx.beginPath(); ctx.moveTo(pad.l, yOfT(t)); ctx.lineTo(pad.l+cw, yOfT(t)); ctx.stroke();
 }
 for (let m = 0; m <= xMax; m += 2) {
 ctx.beginPath(); ctx.moveTo(xOf(m), pad.t); ctx.lineTo(xOf(m), pad.t+ch); ctx.stroke();
 }

 // EoR target dashed line
 ctx.setLineDash([5, 4]);
 ctx.strokeStyle = C.amber;
 ctx.lineWidth = 1.2;
 ctx.beginPath(); ctx.moveTo(pad.l, yOfT(eor)); ctx.lineTo(pad.l+cw, yOfT(eor)); ctx.stroke();
 ctx.setLineDash([]);

 // ── Bean temp curve — built directly from profile.steps, not hardcoded ──
 const beanPts = (steps && steps.length ? steps : [])
 .map(s => [s.time, parseFloat(String(s.temp).replace("~",""))])
 .filter(([m,t]) => !isNaN(m) && !isNaN(t))
 .sort((a,b) => a[0]-b[0]);

 // ── RoR curve — derived from the actual bean temp points (real slope, not invented) ──
 const rorPts = beanPts.map(([m,t],i) => {
 if (i === 0) return [m, 0];
 const [pm, pt] = beanPts[i-1];
 const dt = m - pm;
 const ror = dt > 0 ? (t - pt) / dt : 0;
 return [m, Math.max(0, Math.min(maxR, ror))];
 });

 if (rorPts.length > 1) {
 ctx.beginPath();
 rorPts.forEach(([m,r],i) => i===0 ? ctx.moveTo(xOf(m),yOfR(r)) : ctx.lineTo(xOf(m),yOfR(r)));
 ctx.strokeStyle = C.green;
 ctx.lineWidth = 1.5;
 ctx.stroke();
 }

 if (beanPts.length > 1) {
 ctx.beginPath();
 beanPts.forEach(([m,t],i) => {
 if (i === 0) { ctx.moveTo(xOf(m), yOfT(t)); return; }
 const prev = beanPts[i-1];
 const cpx1 = xOf(prev[0] + (m-prev[0])*0.5);
 const cpx2 = xOf(m - (m-prev[0])*0.5);
 ctx.bezierCurveTo(cpx1,yOfT(prev[1]),cpx2,yOfT(t),xOf(m),yOfT(t));
 });
 ctx.strokeStyle = C.ink;
 ctx.lineWidth = 2.5;
 ctx.stroke();
 }

 // FC vertical marker — from the actual fcTarget, not a hardcoded time
 const fcM = fcTarget || (steps?.find(s => s.phase === "First Crack")?.time);
 if (fcM) {
 ctx.setLineDash([4,3]);
 ctx.strokeStyle = C.amber;
 ctx.lineWidth = 1;
 ctx.beginPath(); ctx.moveTo(xOf(fcM),pad.t); ctx.lineTo(xOf(fcM),pad.t+ch); ctx.stroke();
 ctx.setLineDash([]);
 }

 // Drop marker — from actual totalMin
 ctx.strokeStyle = C.red;
 ctx.lineWidth = 1.5;
 ctx.beginPath(); ctx.moveTo(xOf(totalMin),pad.t); ctx.lineTo(xOf(totalMin),pad.t+ch); ctx.stroke();

 // Left axis labels (bean temp)
 ctx.fillStyle = C.inkLight; ctx.font = `10px ${fontSans}`; ctx.textAlign = "right";
 for (let t = Math.ceil(minT/tGridStep)*tGridStep; t <= maxT; t += tGridStep) ctx.fillText(t+"°", pad.l-5, yOfT(t)+3);
 ctx.fillStyle = C.inkMid; ctx.font = `bold 10px ${fontSans}`; ctx.textAlign = "left";
 ctx.fillText("Bean °C", 0, pad.t+10);

 // Right axis labels (RoR)
 ctx.fillStyle = C.green; ctx.textAlign = "left";
 [5,10,15].forEach(r => ctx.fillText(r, W-pad.r+5, yOfR(r)+3));
 ctx.fillStyle = C.green; ctx.font = `bold 10px ${fontSans}`;
 ctx.fillText("RoR", W-pad.r+5, pad.t+10);

 // Time axis
 ctx.fillStyle = C.inkLight; ctx.font = `10px ${fontSans}`; ctx.textAlign = "center";
 for (let m = 0; m <= xMax; m += 2) ctx.fillText(m, xOf(m), pad.t+ch+16);
 ctx.fillStyle = C.inkFaint; ctx.font = `10px ${fontSans}`;
 ctx.fillText("Time (minutes)", pad.l+cw/2, pad.t+ch+30);

 // Inline labels
 ctx.font = `9px ${fontSans}`; ctx.textAlign = "left";
 ctx.fillStyle = C.amber;
 ctx.fillText("EoR "+eor+"°C", pad.l+4, yOfT(eor)-5);
 if (fcM) ctx.fillText("FC", xOf(fcM)+3, pad.t+14);
 ctx.fillStyle = C.red;
 ctx.fillText("DROP", xOf(totalMin)+3, pad.t+14);

 }, [steps, totalMin, charge, eor, fcTarget]);

 return (
 <canvas ref={canvasRef} style={{ width:"100%", height:240, display:"block", borderRadius:4 }} />
 );
}

// ── SHARED STYLES ─────────────────────────────────────────────────────────────
const s = {
 fieldLabel: {
 fontFamily: fontSans, fontSize:10, fontWeight:600,
 color: C.inkLight, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:5,
 },
 hint: { fontFamily:fontSans, fontSize:11, color:C.inkFaint, marginTop:4, lineHeight:1.4 },
 input: {
 fontFamily: fontSans, fontSize:13, padding:"9px 11px",
 border:`1px solid ${C.parchment}`, borderRadius:5,
 background: C.cream, color: C.ink, width:"100%", outline:"none",
 transition:"border-color 0.12s",
 },
 sectionLabel: {
 fontFamily: fontSans, fontSize:10, fontWeight:700,
 color: C.inkFaint, textTransform:"uppercase", letterSpacing:"0.1em",
 marginTop:24, marginBottom:12, paddingTop:18,
 borderTop:`1px solid ${C.border}`,
 },
 td: { padding:"10px 14px", color:C.inkMid, verticalAlign:"middle", fontFamily:fontSans, fontSize:13 },
 thStyle: {
 padding:"9px 14px", textAlign:"left", fontSize:10, color:C.inkFaint,
 textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:600,
 fontFamily:fontSans, borderBottom:`1px solid ${C.border}`,
 },
 btn: {
 padding:"9px 18px", fontSize:13, fontWeight:500, borderRadius:5,
 cursor:"pointer", border:`1px solid ${C.parchment}`,
 background:C.cream, color:C.inkMid, fontFamily:fontSans,
 transition:"background 0.1s",
 },
 btnSm: { padding:"5px 12px", fontSize:12, borderRadius:4, cursor:"pointer", border:`1px solid ${C.parchment}`, background:C.cream, color:C.inkMid, fontFamily:fontSans },
 btnPrimary: {
 padding:"9px 20px", fontSize:13, fontWeight:600, borderRadius:5,
 cursor:"pointer", border:"none",
 background:C.amber, color:"#fff", fontFamily:fontSans,
 letterSpacing:"0.02em",
 },
 btnSmall: {
 padding:"4px 10px", fontSize:12, borderRadius:4, cursor:"pointer",
 border:`1px solid ${C.parchment}`, background:C.cream, color:C.inkMid, fontFamily:fontSans,
 },
 card: {
 background:C.cream2, border:`1px solid ${C.border}`, borderRadius:8, padding:"14px 18px", marginBottom:10,
 },
 notesBox: {
 background:C.cream2, border:`1px solid ${C.border}`,
 borderLeft:`3px solid ${C.amber}`, borderRadius:6, padding:"12px 16px", marginBottom:16,
 },
};

// ── TECHNICAL INTELLIGENCE RAIL ───────────────────────────────────────────────
function FlameIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={C.amber} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2c1 3-2 4-2 7a4 4 0 0 0 8 0c0-2-1-3-1-3s1 4-2 4c-1.5 0-2-1-2-2.5C13 6 12 4 12 2z" />
      <path d="M8.5 12.5c-1 2-1.5 3.5-1.5 5A5 5 0 0 0 12 22a5 5 0 0 0 5-4.5c0-1.8-1-3-1-3s.5 2.5-1.5 2.5-2-1.5-2-3c0-1.2.5-2 .5-2s-3 .5-4.5 3z" />
    </svg>
  );
}

function RecommendationRail({ rec, chargeDisplay, eorDisplay }) {
  if (!rec) return null;
  const railCard = { background: C.cream2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "18px 20px", marginBottom: 18 };
  const bigNumber = { fontFamily: fontSerif, fontSize: 40, color: C.amber, fontWeight: 600, lineHeight: 1 };
  const label = { fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 };
  const caption = { fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginTop: 6 };

  return (
    <div className="pbRail" style={{ flex: "0 0 28%", minWidth: 220, position: "relative" }}>
      <img src={botanicalRightImg} alt="" aria-hidden="true" style={{
        position:"absolute", right:0, top:0, width:240, height:300, objectFit:"cover", objectPosition:"top right",
        opacity:0.35, pointerEvents:"none", userSelect:"none",
        maskImage:"linear-gradient(to left, black 45%, transparent 100%)",
        WebkitMaskImage:"linear-gradient(to left, black 45%, transparent 100%)",
      }} />
      <div style={railCard}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={label}>Charge temp</div>
          <FlameIcon />
        </div>
        <div style={bigNumber}>{chargeDisplay}°C</div>
        <div style={caption}>Auto recommendation</div>
      </div>

      <div style={railCard}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={label}>End of roast</div>
          <FlameIcon />
        </div>
        <div style={bigNumber}>{eorDisplay}°C</div>
        <div style={caption}>Auto recommendation</div>
      </div>

      {rec.residualNote && (
        <div style={{ ...railCard, background: C.cream3 }}>
          <div style={label}>Residual heat guideline</div>
          <div style={{ fontFamily: fontSans, fontSize: 12, color: C.inkMid, lineHeight: 1.55 }}>{rec.residualNote}</div>
        </div>
      )}

      <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, lineHeight: 1.5, padding: "4px 4px 0" }}>
        All recommendations are starting points. Trust the cup. Log everything.
      </div>
    </div>
  );
}

// ── PROFILE BUILDER ───────────────────────────────────────────────────────────
// ── Recipe standard: the switch + editable settings (sits above "Generate profile") ──
function RecipeStandardCard({ cfg, onChange, rec }) {
  const [open, setOpen] = useState(false);
  const [rev, setRev] = useState(0); // remounts the number boxes after a reset
  const st = rec.standard;
  const setPath = (path, raw) => {
    const x = parseFloat(raw);
    if (isNaN(x)) return;
    const next = JSON.parse(JSON.stringify(cfg));
    let o = next;
    for (let i = 0; i < path.length - 1; i++) o = o[path[i]];
    o[path[path.length - 1]] = x;
    onChange(next);
  };
  const field = (label, path, value) => (
    <label key={path.join(".")} style={{ display: "block" }}>
      <div style={s.fieldLabel}>{label}</div>
      <input style={s.input} type="number" step="any" defaultValue={value} key={`${path.join(".")}-${value}-${rev}`} onBlur={e => setPath(path, e.target.value)} />
    </label>
  );
  const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10, marginBottom: 14 };
  const sub = { fontFamily: fontSans, fontSize: 10, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.08em", margin: "4px 0 8px" };
  const seg = (on) => ({ padding: "9px 14px", border: "none", cursor: "pointer", fontFamily: fontSans, fontSize: 12.5, fontWeight: 600, background: on ? C.ink : "transparent", color: on ? C.cream : C.inkMid });
  return (
    <div style={{ background: C.cream2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "14px 16px", margin: "4px 0 18px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.08em" }}>Recipe starting point</div>
        <div style={{ display: "flex", border: `1px solid ${C.border}`, borderRadius: 6, overflow: "hidden" }}>
          <button type="button" style={seg(cfg.on)} onClick={() => onChange({ ...cfg, on: true })}>Fixed standard</button>
          <button type="button" style={seg(!cfg.on)} onClick={() => onChange({ ...cfg, on: false })}>Previous adjustments</button>
        </div>
      </div>
      {cfg.on && st ? (
        <>
          <div style={{ fontFamily: fontSans, fontSize: 13, color: C.ink, lineHeight: 1.5 }}>
            {st.kg ? `${st.kg} kg (${st.pct}% full)` : "No batch weight yet"}: charge <strong>{rec.charge}°</strong>, initial burner <strong>{rec.ibs}%</strong>, turning point <strong>{rec.tapTarget}°</strong>, first crack <strong>{stdMmss(rec.fcTarget * 60)} at {cfg.fcTemp}°</strong>, drop about <strong>{rec.eor}°</strong>.
          </div>
          <div style={{ fontFamily: fontSans, fontSize: 11.5, color: C.inkMid, marginTop: 4 }}>
            The previous adjustments would have said: charge {rec.old.charge}°, burner {rec.old.ibs}%, first crack {rec.old.fcTarget} min, drop {rec.old.eor}°. Altitude, process, moisture, density and earlier batches no longer move the start.
          </div>
        </>
      ) : (
        <div style={{ fontFamily: fontSans, fontSize: 12.5, color: C.inkMid }}>The previous recipe adjustments (altitude, process, moisture, density, residual heat) are in use. Switch back to the fixed standard any time; nothing is lost either way.</div>
      )}
      {cfg.on && (
        <div style={{ marginTop: 8 }}>
          <button type="button" onClick={() => setOpen(o => !o)} style={{ background: "none", border: "none", padding: 0, color: C.amber, cursor: "pointer", fontFamily: fontSans, fontSize: 12, fontWeight: 600 }}>{open ? "Hide standard settings" : "Standard settings"}</button>
          {open && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontFamily: fontSans, fontSize: 11.5, color: C.inkMid, marginBottom: 12 }}>The charge and burner numbers came from the Loring masterclass whiteboard (05/10/26) and are not proven on your roasts yet. Change any number here; each box saves when you click out of it. Charge is the return-air temperature, as Roast Architect uses it.</div>
              <div style={sub}>By batch size</div>
              {cfg.bands.map((b, i) => (
                <div key={i} style={grid}>
                  {field(`Band ${i + 1}: up to (kg)`, ["bands", i, "upToKg"], b.upToKg)}
                  {field("Charge, return air (°C)", ["bands", i, "charge"], b.charge)}
                  {field("Initial burner (%)", ["bands", i, "ibs"], b.ibs)}
                </div>
              ))}
              <div style={grid}>
                {field("Lowest batch (kg)", ["minKg"], cfg.minKg)}
                {field("Highest batch (kg)", ["maxKg"], cfg.maxKg)}
              </div>
              <div style={sub}>Same every roast</div>
              <div style={grid}>
                {field("Turning point (sec)", ["tapSec"], cfg.tapSec)}
                {field("Turning point (°C)", ["tapTemp"], cfg.tapTemp)}
                {field("Colour change (min)", ["ccMin"], cfg.ccMin)}
                {field("Colour change (°C)", ["ccTemp"], cfg.ccTemp)}
                {field("First crack (min)", ["fcMin"], cfg.fcMin)}
                {field("First crack (°C)", ["fcTemp"], cfg.fcTemp)}
              </div>
              <div style={sub}>Filter</div>
              <div style={grid}>
                {field("Development (sec)", ["filter", "devSec"], cfg.filter.devSec)}
                {field("End range, low (°C)", ["filter", "endLo"], cfg.filter.endLo)}
                {field("End range, high (°C)", ["filter", "endHi"], cfg.filter.endHi)}
              </div>
              <div style={sub}>Espresso</div>
              <div style={grid}>
                {field("Development (sec)", ["espresso", "devSec"], cfg.espresso.devSec)}
                {field("End range, low (°C)", ["espresso", "endLo"], cfg.espresso.endLo)}
                {field("End range, high (°C)", ["espresso", "endHi"], cfg.espresso.endHi)}
              </div>
              <div style={sub}>Robusta adds</div>
              <div style={grid}>
                {field("Charge (°C)", ["robusta", "charge"], cfg.robusta.charge)}
                {field("Initial burner (%)", ["robusta", "ibs"], cfg.robusta.ibs)}
                {field("End temperature (°C)", ["robusta", "end"], cfg.robusta.end)}
              </div>
              <button type="button" style={s.btnSm} onClick={() => { onChange({ ...JSON.parse(JSON.stringify(RECIPE_STD_DEFAULTS)), on: cfg.on }); setRev(r => r + 1); }}>Reset to the masterclass numbers</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Recipe standard: plain-words checks, what to type into Roast Architect, and the files ──
function StandardRecipePanel({ rec, form }) {
  const st = rec.standard;
  const curve = standardCurve(st);
  const styleName = st.style === "espresso" ? "Espresso" : "Filter";
  const title = `${String(form.origin || "Roast").trim()} ${styleName}${st.kg ? " " + st.kg + "kg" : ""}`;
  const fileBase = title.replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "Roast";
  const rows = [
    ["Batch", st.lbs != null ? `${st.lbs} lbs` : "enter a batch weight", "type it in"],
    ["Initial burner", `${st.ibs}%`, "type it in"],
    ["Charge (return air)", `${st.charge}°`, ""],
    ["Turning point", `${stdMmss(st.tapSec)} at ${st.tapTemp}°`, ""],
    ["Colour change", `${stdMmss(st.ccMin * 60)} at ${st.ccTemp}°`, ""],
    ["First crack", `${stdMmss(st.fcMin * 60)} at ${st.fcTemp}°`, ""],
    ["End of roast", `${stdMmss(st.endMin * 60)} at ${st.endTemp.toFixed(1)}°`, ""],
  ];
  const tagColour = (t) => t === "Check" ? C.red : t === "OK" ? C.green : C.inkFaint;
  const box = { background: C.cream2, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.amber}`, borderRadius: 6, padding: "12px 16px", marginBottom: 16 };
  const lab = { fontFamily: fontSans, fontSize: 10, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 };
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 4 }}>
      <div style={{ ...box, flex: "1 1 340px", minWidth: 0 }}>
        <div style={lab}>Check, in plain words</div>
        {st.checks.map((c, i) => (
          <div key={i} style={{ display: "flex", gap: 12, padding: "7px 0", borderTop: i ? `1px solid ${C.borderSoft}` : "none", fontFamily: fontSans, fontSize: 12.5, color: C.ink, lineHeight: 1.45 }}>
            <span style={{ flex: "0 0 44px", fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: tagColour(c.tag), paddingTop: 2 }}>{c.tag}</span>
            <span>{c.text}</span>
          </div>
        ))}
      </div>
      <div style={{ ...box, flex: "1 1 340px", minWidth: 0 }}>
        <div style={lab}>Into Roast Architect</div>
        {rows.map(([k, v, note], i) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 0", borderTop: i ? `1px solid ${C.borderSoft}` : "none", fontFamily: fontSans, fontSize: 12.5, color: C.ink }}>
            <span>{k}{note && <span style={{ color: C.inkFaint, fontSize: 11, marginLeft: 6 }}>{note}</span>}</span>
            <strong>{v}</strong>
          </div>
        ))}
        <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkLight, margin: "8px 0 10px" }}>The two lines marked "type it in" are not saved in Roast Architect's exported files, so enter them by hand.</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <button type="button" style={s.btnSm} onClick={() => downloadTextFile(`${fileBase}.csv`, standardLoringCsv(curve), "text/csv;charset=utf-8")}>Download Loring CSV</button>
          <button type="button" style={s.btnSm} onClick={() => downloadTextFile(`${fileBase}.json`, standardArtisanJson(curve, st, title), "application/json")}>Download Artisan JSON</button>
        </div>
        <div style={{ fontFamily: fontSans, fontSize: 10.5, color: C.inkFaint, marginTop: 8 }}>Whether Roast Architect will load a file made here is still being tested. Do not send a downloaded file to the roaster until it has been checked on screen.</div>
      </div>
    </div>
  );
}

function ProfileBuilder({ onStartRoast, inventory }) {
 const [form, setForm] = useState({
 origin:"", cultivar:"", masl:"", process:"Washed", cupProfile:"Espresso",
 batchKg:"", moisture:"", density:"", chargeOverride:"", eorOverride:"",
 batchNumToday:"1", prevRoastLevel:"", batchId:"",
 });
 const [profile, setProfile] = useState(null);
 const [editIdx, setEditIdx] = useState(null);
 const [editVal, setEditVal] = useState({});
 const [cupNotes, setCupNotes] = useState("");
  const [stdCfg, setStdCfg] = useState(loadRecipeStandard);
  const updateStd = (next) => { saveRecipeStandard(next); setStdCfg(next); };

 const set = (k,v) => setForm(f=>({...f,[k]:v}));

 // Batch of the day and previous roast level come from today's Roast History
 // automatically — still editable below if the day's gone differently.
 useEffect(() => {
   const info = todaysBatchInfo();
   setForm(f => ({ ...f, batchNumToday: info.batchNumToday, prevRoastLevel: info.prevRoastLevel }));
 }, []);

 // Live recommendations — recalculate whenever form changes
 const rec = recommendTemps({...form, cultivar: form.cultivar});
 const chargeDisplay = (!rec.standard && form.chargeOverride) ? parseInt(form.chargeOverride) : rec.charge;
 const eorDisplay = (!rec.standard && form.eorOverride) ? parseInt(form.eorOverride) : rec.eor;
 const agtron = predictAgtron(eorDisplay, form.cultivar);
 const curveHistory = getCurveAdherenceHistory(form.cultivar, form.process);

 const generate = () => {
 const result = generateProfile({
 ...form,
 cultivar: form.cultivar,
 chargeOverride: form.chargeOverride ? parseInt(form.chargeOverride) : null,
 eorOverride: form.eorOverride ? parseInt(form.eorOverride) : null,
 });
 result.agtron = predictAgtron(result.eor, form.cultivar);
 setProfile(result);
 setEditIdx(null);
 };

 const startEdit = i => { setEditIdx(i); setEditVal({...profile.steps[i]}); };
 const saveEdit = () => {
 const steps = profile.steps.map((s,i)=>i===editIdx?{...s,...editVal}:s);
 setProfile(p=>({...p,steps})); setEditIdx(null);
 };

 const saveProfile = () => {
 const saved = JSON.parse(localStorage.getItem("fdc_profiles")||"[]");
 const p = { id:Date.now(), ...form, ...profile, cupNotes, date:localIsoDate() };
 localStorage.setItem("fdc_profiles", JSON.stringify([p,...saved]));
 window.alert("Profile saved: "+(form.origin||"Untitled"));
 };

 const loadFromInventory = (coffee) => {
 setForm(f => ({
 ...f,
 // Use the coffee's actual name (e.g. "Pinhal — Brazil"), not its separate
 // bare origin/country field — the button itself shows the specific name,
 // so what gets loaded should match, not fall back to just "Brazil".
 origin: coffee.name || coffee.origin,
 cultivar: coffee.cultivar || "",
 process: coffee.process,
 masl: coffee.masl || "",
 moisture: coffee.moisture || "",
 density: coffee.density || "",
 chargeOverride: "",
 eorOverride: "",
 }));
 setProfile(null);
 };

 const isActive = (coffee) => form.origin===(coffee.name||coffee.origin) && form.process===coffee.process && form.masl===coffee.masl;

 // Most recent logged roast per distinct coffee — lets "Repeat this roast"
 // restore the full set of inputs actually used last time (batch weight,
 // cup profile, overrides), not just the coffee's static Inventory
 // attributes the way Quick Load above does.
 const [pastRoasts] = useState(() => {
 try {
 const hist = JSON.parse(localStorage.getItem("fdc_history") || "[]");
 const byOrigin = {};
 hist.filter(r => r.origin).forEach(r => {
 if (!byOrigin[r.origin] || (r.date||"") > (byOrigin[r.origin].date||"")) byOrigin[r.origin] = r;
 });
 return Object.values(byOrigin).sort((a,b) => (b.date||"").localeCompare(a.date||"")).slice(0, 8);
 } catch { return []; }
 });

 const loadFromPastRoast = (rec) => {
 const matchedInv = inventory.find(i => (i.name||i.origin) === rec.origin);
 setForm(f => ({
 ...f,
 origin: rec.origin || f.origin,
 cultivar: rec.cultivar || matchedInv?.cultivar || "",
 process: rec.process || matchedInv?.process || "Washed",
 masl: rec.masl || matchedInv?.masl || "",
 moisture: rec.moisture || matchedInv?.moisture || "",
 density: rec.density || matchedInv?.density || "",
 cupProfile: rec.cupProfile || "Espresso",
 batchKg: rec.green || "",
 chargeOverride: rec.chargeOverride || "",
 eorOverride: rec.eorOverride || "",
 // Today's position in the day, not the old roast's — residual heat is about now.
 batchNumToday: todaysBatchInfo().batchNumToday,
 prevRoastLevel: todaysBatchInfo().prevRoastLevel,
 }));
 setProfile(null);
 };

 return (
 <div className="pbCols" style={{ display:"flex", gap:32, alignItems:"flex-start", position:"relative" }}>
 <style>{`
   /* Below this width there isn't room for the 220px-minimum recommendation
      column beside the main form, so stack them into one full-width column
      instead of squeezing both sideways. Threshold is phone-width only —
      tablets (768px+) keep the desktop two-column layout. */
   @media (max-width: 680px) {
     .pbCols { flex-direction: column !important; }
     .pbCols > .pbMain, .pbCols > .pbRail { flex: 1 1 100% !important; min-width: 0 !important; }
     .pbCols .pbBotanical { display: none !important; }
   }
 `}</style>
 <img className="pbBotanical" src={botanicalLeftImg} alt="" aria-hidden="true" style={{
   position:"absolute", left:0, top:0, width:340, height:"100%", objectFit:"cover", objectPosition:"top",
   opacity:0.42, pointerEvents:"none", userSelect:"none",
   maskImage:"linear-gradient(to right, black 45%, transparent 100%)",
   WebkitMaskImage:"linear-gradient(to right, black 45%, transparent 100%)",
 }} />
 <div className="pbMain" style={{ flex:"1 1 70%", minWidth:0, position:"relative" }}>
 {/* Green coffee quick-load from inventory */}
 {inventory.length > 0 && (
 <div style={{marginBottom:4}}>
 <div style={{fontFamily:fontSans,fontSize:10,fontWeight:700,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:8,paddingTop:18,borderTop:`1px solid ${C.border}`}}>
 Green inventory — quick load
 </div>
 <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
 {inventory.map(coffee=>(
 <button key={coffee.id} style={{
 ...s.btn, fontSize:12, padding:"6px 14px",
 background: C.cream2,
 borderColor: isActive(coffee) ? C.amber : C.parchment,
 color: isActive(coffee) ? C.amber : C.inkMid,
 fontWeight: isActive(coffee) ? 600 : 400,
 }} onClick={()=>loadFromInventory(coffee)}>{coffee.name}</button>
 ))}
 </div>
 </div>
 )}

 {pastRoasts.length > 0 && (
 <div style={{marginBottom:4}}>
 <div style={{fontFamily:fontSans,fontSize:10,fontWeight:700,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:8,paddingTop:18,borderTop:`1px solid ${C.border}`}}>
 Repeat a past roast
 </div>
 <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
 {pastRoasts.map(rec=>(
 <button key={rec.id} style={{...s.btn, fontSize:12, padding:"6px 14px", background:C.cream2}} onClick={()=>loadFromPastRoast(rec)} title={rec.date?`Last roasted ${dmy(rec.date)}`:""}>
 {rec.origin}{rec.date?` · ${dmy(rec.date)}`:""}
 </button>
 ))}
 </div>
 </div>
 )}

 {/* Coffee details */}
 <div style={s.sectionLabel}>Coffee details</div>
 <div style={{display:"grid", gridTemplateColumns:"2fr 1.5fr 1fr 1.2fr 1.2fr 1fr", gap:12, marginBottom:4}}>
 <div>
 <div style={s.fieldLabel}>Origin</div>
 <ComboBox value={form.origin} onChange={v=>set("origin",v)} options={ORIGINS} placeholder="e.g. Colombian Huila" style={s.input} />
 </div>
 <div>
 <div style={s.fieldLabel}>Batch #</div>
 <div style={{display:"flex",gap:6}}>
 <input style={s.input} value={form.batchId} onChange={e=>set("batchId",e.target.value)} placeholder="e.g. R26-100" />
 <button type="button" style={{...s.btn,padding:"9px 12px",fontSize:12,flexShrink:0}} onClick={()=>set("batchId",generateBatchNumber())}>Next</button>
 </div>
 </div>
 <div>
 <div style={s.fieldLabel}>Cultivar</div>
 <ComboBox value={form.cultivar} onChange={v=>set("cultivar",v)} options={CULTIVARS} placeholder="e.g. Castillo" style={s.input} />
 </div>
 <div>
 <div style={s.fieldLabel}>MASL (m)</div>
 <input style={s.input} value={form.masl} onChange={e=>set("masl",e.target.value)} placeholder="e.g. 1750" />
 <div style={s.hint}>Metres above sea level — affects bean density.</div>
 </div>
 <div>
 <div style={s.fieldLabel}>Processing</div>
 <select style={s.input} value={form.process} onChange={e=>set("process",e.target.value)}>
 {["Washed","Natural","Honey","Anaerobic","Wet-hulled","Decaf — Washed","Decaf — Natural","Decaf — Sugarcane (EA)","Decaf — Swiss Water"].map(o=><option key={o}>{o}</option>)}
 </select>
 </div>
 <div>
 <div style={s.fieldLabel}>Cup profile</div>
 <select style={s.input} value={form.cupProfile} onChange={e=>set("cupProfile",e.target.value)}>
 {["Espresso","Filter","Both"].map(o=><option key={o}>{o}</option>)}
 </select>
 </div>
 </div>

 {/* Roast parameters */}
 <div style={s.sectionLabel}>Roast parameters</div>
 <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:12, marginBottom:20}}>
 <div>
 <div style={s.fieldLabel}>Batch weight (kg)</div>
 <ComboBox value={form.batchKg} onChange={v=>set("batchKg",v)} options={BATCH_WEIGHTS.map(String)} placeholder="e.g. 35" style={s.input} />
 </div>
 <div>
 <div style={s.fieldLabel}>Moisture (%)</div>
 <input style={s.input} type="number" value={form.moisture} onChange={e=>set("moisture",e.target.value)} placeholder="e.g. 11.2" />
 <div style={s.hint}>Ideal: 9.5–11.5%</div>
 </div>
 <div>
 <div style={s.fieldLabel}>
   Density (g/L)
   <span title="How to measure: weigh out 100g of green beans, pour into a measuring jug, and note the volume in mL. Density (g/L) = 100,000 ÷ that volume in mL." style={{marginLeft:5,color:C.inkFaint,cursor:"help",fontWeight:400}}>ⓘ</span>
 </div>
 <input style={s.input} type="number" value={form.density} onChange={e=>set("density",e.target.value)} placeholder="e.g. 740" />
 <div style={s.hint}>Typical: 650–800 g/L</div>
 </div>
 <div>
 <div style={s.fieldLabel}>
 Charge temp (°C)
 {!form.chargeOverride && <span style={{fontWeight:400,color:C.amber,marginLeft:6}}>● {rec.charge}°C AUTO</span>}
 {form.chargeOverride && <span style={{fontWeight:400,color:C.inkFaint,marginLeft:6}}>{rec.standard ? "override ignored: fixed standard is on" : "override"}</span>}
 </div>
 <input style={s.input} type="number" value={form.chargeOverride} onChange={e=>set("chargeOverride",e.target.value)} placeholder={`${rec.charge}`} />
 <div style={s.hint}>{rec.chargeNotes || "Enter MASL, process and batch weight for a recommendation."}</div>
 {rec.diagAdjust && rec.diagAdjust.n >= DIAGNOSTIC_ADJUST_MIN_SAMPLES && rec.diagAdjust.charge !== 0 && (
 <div style={{fontFamily:fontSans,fontSize:10,color:C.green,marginTop:3}}>↳ {rec.diagAdjust.charge > 0 ? "+" : ""}{rec.diagAdjust.charge}°C applied from {rec.diagAdjust.n} logged diagnostic{rec.diagAdjust.n===1?"":"s"} for this cultivar/process</div>
 )}
 {curveHistory && curveHistory.drying !== null && Math.abs(curveHistory.drying) >= 3 && (
 <div style={{fontFamily:fontSans,fontSize:10,color:C.amber,marginTop:3}}>ℹ Your last {curveHistory.n} logged roasts for this cultivar/process ran drying phase {curveHistory.drying > 0 ? "+" : ""}{curveHistory.drying}°C vs their own curve — worth a look, not auto-applied.</div>
 )}
 </div>
 <div>
 <div style={s.fieldLabel}>
 EoR temp (°C)
 {!form.eorOverride && <span style={{fontWeight:400,color:C.amber,marginLeft:6}}>● {rec.eor}°C AUTO</span>}
 {form.eorOverride && <span style={{fontWeight:400,color:C.inkFaint,marginLeft:6}}>{rec.standard ? "override ignored: fixed standard is on" : "override"}</span>}
 </div>
 <input style={s.input} type="number" value={form.eorOverride} onChange={e=>set("eorOverride",e.target.value)} placeholder={`${rec.eor}`} />
 <div style={s.hint}>{rec.eorNotes || "Enter process and cup profile for a recommendation."}</div>
 {rec.diagAdjust && rec.diagAdjust.n >= DIAGNOSTIC_ADJUST_MIN_SAMPLES && rec.diagAdjust.eor !== 0 && (
 <div style={{fontFamily:fontSans,fontSize:10,color:C.green,marginTop:3}}>↳ {rec.diagAdjust.eor > 0 ? "+" : ""}{rec.diagAdjust.eor}°C applied from {rec.diagAdjust.n} logged diagnostic{rec.diagAdjust.n===1?"":"s"} for this cultivar/process</div>
 )}
 <div style={{display:"flex",alignItems:"center",gap:8,marginTop:6}}>
 <div style={{width:24,height:24,borderRadius:4,background:agtron.colour,border:`1px solid ${C.border}`,flexShrink:0}}/>
 <div style={{fontFamily:fontSans,fontSize:11,color:C.inkMid}}>
 <span style={{fontWeight:600}}>{agtron.level}</span>
 <span style={{color:C.inkFaint,marginLeft:4}}>· Agtron WB ~{agtron.range}</span>
 {agtron.calibrated && <span style={{color:C.green,marginLeft:6,fontWeight:600}}>✓ calibrated from {agtron.calibrationN} logged reading{agtron.calibrationN===1?"":"s"}</span>}
 </div>
 </div>
 {!agtron.calibrated && <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,marginTop:3}}>This is a base estimate — log your light-meter reading against {AGTRON_CALIBRATION_MIN_SAMPLES}+ roasts of this cultivar group in Roast History to start calibrating it.</div>}
 </div>
 </div>

 {/* Residual heat */}
 <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 2fr", gap:12, marginBottom:20}}>
 <div>
 <div style={s.fieldLabel}>Batch # today</div>
 <input style={s.input} type="number" min="1" value={form.batchNumToday} onChange={e=>set("batchNumToday",e.target.value)} placeholder="1" />
 </div>
 <div>
 <div style={s.fieldLabel}>Previous batch level</div>
 <select style={s.input} value={form.prevRoastLevel} onChange={e=>set("prevRoastLevel",e.target.value)}>
 <option value="">— None / N/A —</option>
 <option value="Light">Light</option>
 <option value="Medium">Medium</option>
 <option value="Dark">Dark</option>
 </select>
 </div>
 <div>
 <div style={s.fieldLabel}>Residual heat</div>
 <div style={{...s.hint, marginTop:8, lineHeight:1.5}}>{rec.residualNote}</div>
 </div>
 </div>

 <RecipeStandardCard cfg={stdCfg} onChange={updateStd} rec={rec} />

  <button style={s.btnPrimary} onClick={generate}>Generate profile</button>

 {profile && (<>
 {/* Summary bar */}
 <div style={{display:"flex",gap:0,flexWrap:"wrap",borderTop:`1px solid ${C.border}`,borderBottom:`1px solid ${C.border}`,margin:"22px 0 18px",padding:"10px 0"}}>
 {[
 ["Coffee",form.origin||"—"],["Cultivar",form.cultivar||"—"],["MASL",form.masl||"—"],
 ["Batch",form.batchKg?form.batchKg+"kg":"—"],["Process",form.process],
 ["Target",form.cupProfile],["Moisture",form.moisture?form.moisture+"%":"—"],["Density",form.density?form.density+" g/L":"—"],
 ["Charge",chargeDisplay+"°C"],["EoR",eorDisplay+"°C"],
 ["Agtron WB",profile.agtron?profile.agtron.range:agtron.range],
 ["Colour",profile.agtron?profile.agtron.level:agtron.level],
 ["Time",profile.totalTime],["DTR",profile.dtr],
 ].map(([k,v])=>(
 <div key={k} style={{paddingRight:24,paddingBottom:4}}>
 <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:2}}>{k}</div>
 <div style={{fontFamily:fontSans,fontSize:13,fontWeight:600,color:C.ink,display:"flex",alignItems:"center",gap:4}}>
 {k==="Colour" && profile.agtron && <div style={{width:12,height:12,borderRadius:2,background:profile.agtron.colour,display:"inline-block"}}/>}
 {v}
 </div>
 </div>
 ))}
 </div>

 {/* Profile notes */}
 <div style={s.notesBox}>
 <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:5}}>Profile notes</div>
 <div style={{fontFamily:fontSans,fontSize:13,color:C.inkMid}}>
 → {form.cupProfile==="Espresso"?"Espresso: prioritise body and caramel — tighter DTR (~21–22%).":"Filter: prioritise clarity and acidity — lighter DTR (~18–20%)."}
 </div>
 </div>

 {/* Cultivar intelligence panel */}
 {profile.rec && profile.rec.cv && profile.rec.cv.group !== "Unknown" && (
 <div style={{background:"#F0F7F1",border:`1px solid ${C.border}`,borderLeft:`3px solid ${C.green}`,borderRadius:6,padding:"12px 16px",marginBottom:16}}>
 <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>
 Cultivar intelligence — {profile.rec.cv.group} group
 </div>
 <div style={{fontFamily:fontSans,fontSize:12,color:C.inkMid,marginBottom:6,fontStyle:"italic"}}>
 {profile.rec.cv.flavour}
 </div>
 <div style={{fontFamily:fontSans,fontSize:12,color:C.inkMid,marginBottom:8}}>
 {profile.rec.cv.notes}
 </div>
 <div style={{display:"flex",gap:16,flexWrap:"wrap",marginBottom:6}}>
 <div style={{fontFamily:fontSans,fontSize:11,color:C.inkLight}}>
 <span style={{fontWeight:600}}>Target FC:</span> ~{profile.rec.fcTarget} min
 </div>
 <div style={{fontFamily:fontSans,fontSize:11,color:profile.rec.fcRuleCompliant?C.green:C.amber}}>
 <span style={{fontWeight:600}}> 75–80% rule:</span> FC at {profile.rec.fcPct}% {profile.rec.fcRuleCompliant ? "✓" : "⚠"}
 </div>
 <div style={{fontFamily:fontSans,fontSize:11,color:C.inkLight}}>
 <span style={{fontWeight:600}}>DTR:</span> {profile.rec.dtrPct}%
 </div>
 <div style={{fontFamily:fontSans,fontSize:11,color:C.inkLight}}>
 <span style={{fontWeight:600}}>Roast speed:</span> {profile.rec.cv.speed}
 </div>
 </div>
 {profile.rec.timingNotes && (
 <div style={{fontFamily:fontSans,fontSize:11,color:C.inkLight,paddingTop:8,borderTop:`1px solid ${C.borderSoft}`}}>
 {profile.rec.timingNotes}
 </div>
 )}
 </div>
 )}

 <CultivarCompare />

 {/* TAP / IBS% panel — Loring specific */}
 {profile.rec && profile.rec.tapTarget && (
 <div style={{background:C.cream2,border:`1px solid ${C.border}`,borderLeft:`3px solid ${C.amber}`,borderRadius:6,padding:"12px 16px",marginBottom:16}}>
 <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>
 TAP & initial burner setting
 </div>
 <div style={{display:"flex",gap:20,flexWrap:"wrap",marginBottom:6}}>
 <div style={{fontFamily:fontSans,fontSize:13,color:C.ink}}>
 <span style={{fontWeight:600}}>Target TAP:</span> ~{profile.rec.tapTarget}°C
 </div>
 <div style={{fontFamily:fontSans,fontSize:13,color:C.ink}}>
 <span style={{fontWeight:600}}>IBS:</span> {profile.rec.ibs}%
 </div>
 </div>
 <div style={{fontFamily:fontSans,fontSize:11,color:C.inkLight}}>
 {profile.rec.tapNotes}
 </div>
 </div>
 )}

 {/* Fixed standard: checks, Roast Architect numbers, files */}
  {profile.rec && profile.rec.standard && <StandardRecipePanel rec={profile.rec} form={form} />}

  {/* Steps table */}
 <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>
 Roast steps — click any cell to edit
 </div>
 <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden",marginBottom:20}}>
 <table style={{width:"100%",borderCollapse:"collapse"}}>
 <thead>
 <tr style={{background:C.cream3}}>
 {["Phase","Time","Bean temp","Burner %","RoR target","Notes"].map(h=>(
 <th key={h} style={s.thStyle}>{h}</th>
 ))}
 </tr>
 </thead>
 <tbody>
 {profile.steps.map((step,i)=>(
 editIdx===i ? (
 <tr key={i} style={{background:"#FDF6E8"}}>
 <td style={s.td}><span style={{color:C.amber,fontWeight:600}}>{step.phase}</span></td>
 {["timeStr","temp","burner","ror","notes"].map(k=>(
 <td key={k} style={s.td}>
 <input style={{...s.input,padding:"5px 7px",fontSize:12}} value={editVal[k]??step[k]} onChange={e=>setEditVal(v=>({...v,[k]:e.target.value}))} />
 </td>
 ))}
 <td style={s.td}>
 <button style={{...s.btnSmall,marginRight:4}} onClick={saveEdit}>Save</button>
 <button style={s.btnSmall} onClick={()=>setEditIdx(null)}>Cancel</button>
 </td>
 </tr>
 ) : (
 <tr key={i}
 style={{borderBottom:`1px solid ${C.borderSoft}`,cursor:"pointer",transition:"background 0.1s"}}
 onClick={()=>startEdit(i)}
 onMouseEnter={e=>e.currentTarget.style.background=C.cream2}
 onMouseLeave={e=>e.currentTarget.style.background=""}>
 <td style={{...s.td,color:C.amber,fontWeight:600}}>{step.phase}</td>
 <td style={s.td}>{step.timeStr}</td>
 <td style={s.td}>{step.temp}°C</td>
 <td style={{...s.td,fontWeight:600}}>{step.burner}%</td>
 <td style={s.td}>{step.ror}</td>
 <td style={{...s.td,color:C.inkLight,maxWidth:260}}>{step.notes}</td>
 </tr>
 )
 ))}
 </tbody>
 </table>
 </div>

 {/* Cupping notes */}
 <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Roast notes / cupping</div>
 <textarea
 style={{...s.input,height:80,resize:"vertical",marginBottom:20}}
 placeholder="Add cupping notes, observations, adjustments for next roast..."
 value={cupNotes} onChange={e=>setCupNotes(e.target.value)}
 />

 {/* Reference curve */}
 <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Reference roast curve</div>
 <div style={{background:C.cream2,border:`1px solid ${C.border}`,borderRadius:8,padding:14,marginBottom:16}}>
 <RoastCurve steps={profile.steps} totalMin={profile.totalMin} charge={profile.charge} eor={profile.eor} fcTarget={profile.rec.fcTarget} />
 <div style={{display:"flex",gap:20,marginTop:10,flexWrap:"wrap"}}>
 {[
 {color:C.ink,label:"Bean temperature",dash:false},
 {color:C.green,label:"Rate of rise (RoR)",dash:false},
 {color:C.amber,label:"EoR target",dash:true},
 {color:C.amber,label:"First crack / development",dash:true},
 ].map(({color,label,dash})=>(
 <div key={label} style={{display:"flex",alignItems:"center",gap:6,fontFamily:fontSans,fontSize:11,color:C.inkLight}}>
 <svg width={22} height={8}><line x1={0} y1={4} x2={22} y2={4} stroke={color} strokeWidth={2} strokeDasharray={dash?"5,3":""}/></svg>
 {label}
 </div>
 ))}
 </div>
 </div>

 <div style={{display:"flex",gap:8}}>
 <button style={s.btnPrimary} onClick={saveProfile}>Save profile</button>
 <button style={{...s.btnPrimary, background:C.green, marginLeft:"auto"}} onClick={()=>onStartRoast&&onStartRoast({
   ...form,
   batch: form.batchId,
   charge: profile.charge,
   eor: profile.eor,
   fcTarget: profile.rec.fcTarget,
   totalMin: profile.totalMin || (profile.rec.fcTarget + (profile.rec.cv?.devTime||1.5)),
   dryEndMin: profile.rec.dryEndMin,
   maillardEndMin: profile.rec.maillardEndMin,
   dtr: profile.dtr,
   ibs: profile.rec.ibs,
   tapTarget: profile.rec.tapTarget,
    chargeProbe: profile.rec.chargeProbe,
   cv: profile.rec.cv,
   cultivarNote: profile.rec.cultivarNote,
   residualNote: profile.rec.residualNote,
   batchKg: form.batchKg,
   origin: form.origin,
   steps: profile.steps,
 })}>
   🔥 Start Roast Mode
 </button>
 </div>
 </>)}
 </div>
 <RecommendationRail rec={rec} chargeDisplay={chargeDisplay} eorDisplay={eorDisplay} />
 </div>
 );
}

// ── BLEND BUILDER ─────────────────────────────────────────────────────────────
function BlendBuilder({ inventory }) {
 const [name,setName]=useState(""); const [level,setLevel]=useState("Medium");
 const [use,setUse]=useState("Espresso"); const [batch,setBatch]=useState(""); const [notes,setNotes]=useState("");
 const [rows,setRows]=useState([{id:1,origin:"",pct:""}]);
 const [saved,setSaved]=useState(()=>JSON.parse(localStorage.getItem("fdc_blends")||"[]"));

 const total=rows.reduce((a,r)=>a+(parseFloat(r.pct)||0),0);
 const addRow=()=>setRows(r=>[...r,{id:Date.now(),origin:"",pct:""}]);
 const removeRow=id=>setRows(r=>r.filter(x=>x.id!==id));
 const updateRow=(id,k,v)=>setRows(r=>r.map(x=>x.id===id?{...x,[k]:v}:x));

 const save=()=>{
 const b={id:Date.now(),name:name||"Untitled blend",level,use,batch,notes,components:rows.filter(r=>r.origin),date:localIsoDate()};
 const next=[b,...saved]; setSaved(next); localStorage.setItem("fdc_blends",JSON.stringify(next));
 window.alert("Blend saved: "+b.name);
 };

 const ok = Math.abs(total-100)<0.1;
 const over = total>100;

 return (
 <div>
 <div style={s.sectionLabel}>Blend details</div>
 <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:12,marginBottom:4}}>
 <div><div style={s.fieldLabel}>Blend name</div><input style={s.input} value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Dublin House Blend" /></div>
 <div><div style={s.fieldLabel}>Roast level</div>
 <select style={s.input} value={level} onChange={e=>setLevel(e.target.value)}>
 {["Light","Light-medium","Medium","Medium-dark","Dark"].map(o=><option key={o}>{o}</option>)}
 </select>
 </div>
 </div>

 <div style={s.sectionLabel}>Components</div>
 {rows.map(row=>(
 <div key={row.id} style={{display:"flex",gap:8,alignItems:"center",marginBottom:6}}>
 <input style={{...s.input,flex:1}} placeholder="Origin" value={row.origin} onChange={e=>updateRow(row.id,"origin",e.target.value)} list="blend-origins" />
 <input style={{...s.input,width:72}} type="number" placeholder="%" min={0} max={100} value={row.pct} onChange={e=>updateRow(row.id,"pct",e.target.value)} />
 {(() => {
   const matched = inventory?.find(i => i.name === row.origin);
   if (!matched) return null;
   const stockKg = Math.round((parseFloat(matched.bagWeight)||0) * (parseFloat(matched.bags)||0) * 10) / 10;
   const needKg = batch && row.pct ? Math.round(parseFloat(batch) * (parseFloat(row.pct)/100) * 10) / 10 : null;
   const short = needKg !== null && needKg > stockKg;
   return (
     <span style={{ fontFamily: fontSans, fontSize: 10, color: short ? C.red : C.inkFaint, whiteSpace: "nowrap" }}>
       {stockKg}kg in stock{needKg !== null ? ` · need ${needKg}kg${short ? " ⚠" : ""}` : ""}
     </span>
   );
 })()}
 <button style={{background:"none",border:"none",cursor:"pointer",color:C.inkFaint,fontSize:18,lineHeight:1}} onClick={()=>removeRow(row.id)}>×</button>
 </div>
 ))}
 <datalist id="blend-origins">
   {inventory?.map(item => <option key={item.id} value={item.name} />)}
 </datalist>
 <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
 <button style={s.btn} onClick={addRow}>+ Add origin</button>
 <span style={{fontFamily:fontSans,fontSize:13,fontWeight:600,color:ok?C.green:over?C.red:C.inkLight}}>
 Total: {Math.round(total*10)/10}%{ok?" ✓":""}
 </span>
 </div>

 <div style={s.sectionLabel}>Parameters</div>
 <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
 <div><div style={s.fieldLabel}>Batch size (kg)</div><input style={s.input} value={batch} onChange={e=>setBatch(e.target.value)} placeholder="35" type="number" /></div>
 <div><div style={s.fieldLabel}>Intended use</div>
 <select style={s.input} value={use} onChange={e=>setUse(e.target.value)}>
 {["Espresso","Filter","Both"].map(o=><option key={o}>{o}</option>)}
 </select>
 </div>
 </div>
 <div style={{marginBottom:20}}>
 <div style={s.fieldLabel}>Blend notes</div>
 <textarea style={{...s.input,height:70,resize:"vertical"}} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Flavour targets, blend rationale..." />
 </div>
 <div style={{display:"flex",gap:8,marginBottom:28}}>
 <button style={s.btnPrimary} onClick={save}>Save blend</button>
 <button style={s.btn} onClick={()=>{setName("");setBatch("");setNotes("");setRows([{id:1,origin:"",pct:""}]);}}>Clear</button>
 </div>

 {saved.length>0&&(<>
 <div style={s.sectionLabel}>Saved blends</div>
 {saved.map(b=>(
 <div key={b.id} style={{...s.card,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
 <div>
 <div style={{fontFamily:fontSans,fontWeight:600,fontSize:14,color:C.ink,marginBottom:3}}>{b.name}</div>
 <div style={{fontFamily:fontSans,fontSize:12,color:C.inkLight}}>
 {b.level} · {b.use} · {b.components.map(c=>c.origin+" "+c.pct+"%").join(", ")}
 </div>
 </div>
 <span style={{fontFamily:fontSans,fontSize:11,padding:"3px 10px",background:C.cream3,borderRadius:100,color:C.inkLight}}>
 {b.batch?b.batch+"kg":dmy(b.date)}
 </span>
 </div>
 ))}
 </>)}
 </div>
 );
}

// ── TDS / EXTRACTION YIELD CALCULATOR ────────────────────────────────────────
function TDSCalculator({ recipe }) {
  const [open, setOpen] = useState(false);
  const [showWater, setShowWater] = useState(false);
  const [mode, setMode] = useState("espresso"); // espresso | filter
  const [tds, setTds] = useState("");
  const [dose, setDose] = useState("");
  const [bev, setBev] = useState("");

  const ey = () => {
    const t = parseFloat(tds), d = parseFloat(dose), b = parseFloat(bev);
    if (!t || !d || !b) return null;
    return Math.round((t * b / d) * 100) / 100;
  };

  const eyVal = ey();

  // ── Target zones ──
  // Espresso: TDS 8–12% lungo, 8–12% normale, 12–18% ristretto; EY 18–22%
  // Filter: TDS 1.15–1.45%, EY 18–22%, brew ratio commonly 1:15–1:17
  const zones = mode === "espresso" ? [
    { label: "Lungo",    eyMin: 18, eyMax: 22, tdsMin: 4,  tdsMax: 8,  color: "#E8F4E8" },
    { label: "Normale",  eyMin: 18, eyMax: 22, tdsMin: 8,  tdsMax: 12, color: "#EDF4FB" },
    { label: "Ristretto",eyMin: 18, eyMax: 22, tdsMin: 12, tdsMax: 18, color: "#FDF3E8" },
  ] : [
    { label: "Target", eyMin: 18, eyMax: 22, tdsMin: 1.15, tdsMax: 1.45, color: "#EDF4FB" },
    { label: "Strong",    eyMin: 18, eyMax: 22, tdsMin: 1.45, tdsMax: 1.8,  color: "#FDF3E8" },
    { label: "Weak",      eyMin: 18, eyMax: 22, tdsMin: 0.8,  tdsMax: 1.15, color: "#F8F4EE" },
  ];

  // SVG plot dimensions
  const W = 420, H = 260, PAD = { t: 16, r: 16, b: 36, l: 44 };
  const plotW = W - PAD.l - PAD.r;
  const plotH = H - PAD.t - PAD.b;

  const esp = mode === "espresso";
  const xMin = 14, xMax = 26; // EY %
  const yMin = esp ? 3 : 0.6;
  const yMax = esp ? 19 : 2.2;

  const toX = v => PAD.l + (v - xMin) / (xMax - xMin) * plotW;
  const toY = v => PAD.t + (1 - (v - yMin) / (yMax - yMin)) * plotH;

  // Dot position
  const dotX = eyVal ? toX(eyVal) : null;
  const dotY = (tds && eyVal) ? toY(parseFloat(tds)) : null;
  const dotInBounds = dotX !== null && dotX >= PAD.l && dotX <= PAD.l + plotW && dotY >= PAD.t && dotY <= PAD.t + plotH;

  // Extraction status
  const getStatus = () => {
    if (!eyVal || !tds) return null;
    const t = parseFloat(tds);
    if (eyVal < 18) return { label: "Under-extracted", color: C.green };
    if (eyVal > 22) return { label: "Over-extracted", color: "#c0392b" };
    if (esp && t < 8) return { label: "Weak / lungo", color: C.amber };
    if (esp && t > 12) return { label: "Strong / ristretto", color: C.amber };
    return { label: "In the zone ✓", color: C.green };
  };
  const status = getStatus();

  // X axis ticks
  const xTicks = [14,16,18,20,22,24,26];
  const yTicks = esp ? [4,6,8,10,12,14,16,18] : [0.6,0.8,1.0,1.2,1.4,1.6,1.8,2.0,2.2];

  return (
    <div style={{border:`1px solid ${C.border}`,borderRadius:8,marginBottom:20,overflow:"hidden"}}>
      <div onClick={()=>setOpen(o=>!o)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",background:C.cream2,cursor:"pointer",userSelect:"none"}}>
        <div style={{fontFamily:fontSans,fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:C.inkLight}}>TDS & Extraction Yield</div>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          {eyVal && <div style={{fontFamily:"Georgia,serif",fontSize:16,color:C.amber,fontWeight:700}}>{eyVal}% EY</div>}
          {status && <div style={{fontFamily:fontSans,fontSize:11,color:status.color}}>{status.label}</div>}
          <div style={{color:C.inkFaint,fontSize:13}}>{open?"▲":"▼"}</div>
        </div>
      </div>

      {open && (
        <div style={{padding:16}}>
          {/* Mode toggle */}
          <div style={{display:"flex",gap:0,marginBottom:16,border:`1px solid ${C.border}`,borderRadius:6,overflow:"hidden",width:"fit-content"}}>
            {["espresso","filter"].map(m=>(
              <button key={m} onClick={()=>setMode(m)} style={{padding:"6px 18px",fontFamily:fontSans,fontSize:12,fontWeight:mode===m?700:400,background:mode===m?C.ink:"transparent",color:mode===m?C.cream:C.inkMid,border:"none",cursor:"pointer",textTransform:"capitalize"}}>
                {m === "espresso" ? "Espresso" : "Filter / batch"}
              </button>
            ))}
          </div>

          {/* Recipe used */}
          {recipe && <div style={{fontFamily:fontSans,fontSize:11,color:C.inkFaint,marginBottom:12}}>Recipe: <span style={{color:C.amber,fontWeight:600}}>{recipe}</span></div>}

          {/* Inputs */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:16}}>
            <div>
              <div style={s.fieldLabel}>TDS % (refractometer)</div>
              <input style={s.input} type="number" step="0.01" min="0" value={tds} onChange={e=>setTds(e.target.value)} placeholder={esp?"9.5":"1.35"}/>
            </div>
            <div>
              <div style={s.fieldLabel}>Dose (g)</div>
              <input style={s.input} type="number" step="0.1" min="0" value={dose} onChange={e=>setDose(e.target.value)} placeholder={esp?"18":"15"}/>
            </div>
            <div>
              <div style={s.fieldLabel}>Beverage weight (g)</div>
              <input style={s.input} type="number" step="0.1" min="0" value={bev} onChange={e=>setBev(e.target.value)} placeholder={esp?"36":"250"}/>
            </div>
          </div>

          {/* Results row */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:20}}>
            {[
              ["Extraction yield", eyVal !== null ? eyVal+"%" : "—", eyVal && (eyVal < 18 ? C.green : eyVal > 22 ? "#c0392b" : C.amber)],
              ["Brew ratio", dose && bev ? "1 : "+(Math.round(parseFloat(bev)/parseFloat(dose)*10)/10) : "—", C.ink],
              ["Status", status ? status.label : "—", status ? status.color : C.inkFaint],
            ].map(([label, val, col])=>(
              <div key={label} style={{background:C.cream2,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 14px"}}>
                <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>{label}</div>
                <div style={{fontFamily:"Georgia,serif",fontSize:20,color:col||C.ink,fontWeight:700}}>{val}</div>
              </div>
            ))}
          </div>

          {/* SVG Plot */}
          <div style={{overflowX:"auto"}}>
            <svg width={W} height={H} style={{fontFamily:fontSans,fontSize:10}}>
              {/* Grid */}
              {xTicks.map(x=>(
                <line key={x} x1={toX(x)} y1={PAD.t} x2={toX(x)} y2={PAD.t+plotH} stroke={C.borderSoft} strokeWidth="0.5"/>
              ))}
              {yTicks.map(y=>(
                <line key={y} x1={PAD.l} y1={toY(y)} x2={PAD.l+plotW} y2={toY(y)} stroke={C.borderSoft} strokeWidth="0.5"/>
              ))}

              {/* Target zones */}
              {zones.map(z=>{
                const x1=toX(Math.max(z.eyMin,xMin)), x2=toX(Math.min(z.eyMax,xMax));
                const y1=toY(Math.min(z.tdsMax,yMax)), y2=toY(Math.max(z.tdsMin,yMin));
                return (
                  <g key={z.label}>
                    <rect x={x1} y={y1} width={x2-x1} height={y2-y1} fill={z.color} stroke={C.border} strokeWidth="0.5"/>
                    <text x={(x1+x2)/2} y={(y1+y2)/2+3} textAnchor="middle" fill={C.inkLight} fontSize="10">{z.label}</text>
                  </g>
                );
              })}

              {/* Axes */}
              <line x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={PAD.t+plotH} stroke={C.ink} strokeWidth="1"/>
              <line x1={PAD.l} y1={PAD.t+plotH} x2={PAD.l+plotW} y2={PAD.t+plotH} stroke={C.ink} strokeWidth="1"/>

              {/* X axis labels */}
              {xTicks.map(x=>(
                <text key={x} x={toX(x)} y={PAD.t+plotH+14} textAnchor="middle" fill={C.inkLight}>{x}</text>
              ))}
              <text x={PAD.l+plotW/2} y={H-2} textAnchor="middle" fill={C.inkLight} fontSize="10">Extraction yield (%)</text>

              {/* Y axis labels */}
              {yTicks.map(y=>(
                <text key={y} x={PAD.l-6} y={toY(y)+3} textAnchor="end" fill={C.inkLight}>{y}</text>
              ))}
              <text x={10} y={PAD.t+plotH/2} textAnchor="middle" fill={C.inkLight} fontSize="10" transform={`rotate(-90,10,${PAD.t+plotH/2})`}>TDS (%)</text>

              {/* Live dot */}
              {dotInBounds && (
                <g>
                  <circle cx={dotX} cy={dotY} r={7} fill="white" stroke={C.amber} strokeWidth="2"/>
                  <circle cx={dotX} cy={dotY} r={3} fill={C.amber}/>
                  <text x={dotX+10} y={dotY-6} fill={C.ink} fontSize="10" fontWeight="700">{eyVal}% / {tds}%</text>
                </g>
              )}
              {/* Out of bounds indicator */}
              {eyVal && !dotInBounds && (
                <text x={PAD.l+plotW/2} y={PAD.t+plotH/2} textAnchor="middle" fill={C.inkFaint} fontSize="11">
                  {eyVal < xMin ? "← Under-extracted (off scale)" : eyVal > xMax ? "Over-extracted →" : "Adjust values"}
                </text>
              )}
            </svg>
          </div>
          <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,marginTop:4}}>
            {esp ? "Espresso targets: Normale 18–22% EY · 8–12% TDS" : "Filter targets: 18–22% EY · 1.15–1.45% TDS · brew ratio commonly 1:15–1:17"}
          </div>

          {/* ── WATER CHEMISTRY REFERENCE ── */}
          <div style={{marginTop:14,borderTop:`1px solid ${C.border}`,paddingTop:12}}>
            <div onClick={()=>setShowWater(w=>!w)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",userSelect:"none"}}>
              <div style={{fontFamily:fontSans,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",color:C.inkFaint}}>Water chemistry reference</div>
              <div style={{color:C.inkFaint,fontSize:11}}>{showWater?"▲":"▼"}</div>
            </div>
            {showWater && (
              <div style={{marginTop:10}}>
                <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,marginBottom:8,lineHeight:1.5}}>
                  If ratio, dose, and grind all check out but the cup still tastes off, water is the next thing to rule out. SCA-inspired targets:
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6}}>
                  {[
                    ["TDS", "75–150 ppm"], ["Calcium (Ca²⁺)", "10–40 ppm"], ["Magnesium (Mg²⁺)", "5–30 ppm"],
                    ["Alkalinity (HCO₃⁻)", "40–80 ppm"], ["Chloride (Cl⁻)", "< 25 ppm"], ["pH", "6.5–7.5"],
                  ].map(([label,val])=>(
                    <div key={label} style={{background:C.cream2,borderRadius:5,padding:"7px 9px"}}>
                      <div style={{fontFamily:fontSans,fontSize:9,color:C.inkFaint,marginBottom:2}}>{label}</div>
                      <div style={{fontFamily:fontSans,fontSize:12,color:C.ink,fontWeight:600}}>{val}</div>
                    </div>
                  ))}
                </div>
                <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,marginTop:8,lineHeight:1.5}}>
                  Low calcium → thin, sour. Too much → dull, harsh. Low alkalinity → sharp, sour. Too much → flat, muted, muddies clarity.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── FLAVOUR WHEEL QUICK REFERENCE ────────────────────────────────────────────
const FLAVOUR_WHEEL = [
  { cat:"Fruity", color:"#C0392B", notes:["Citrus","Berry","Stone fruit","Tropical","Dried fruit"] },
  { cat:"Floral", color:"#9B59B6", notes:["Jasmine","Rose","Chamomile","Black tea"] },
  { cat:"Sweet", color:"#D4A537", notes:["Honey","Caramel","Vanilla","Brown sugar","Molasses"] },
  { cat:"Chocolate/Nutty", color:"#7B4A2E", notes:["Cocoa","Dark chocolate","Hazelnut","Almond","Peanut"] },
  { cat:"Roasted", color:"#4A3F35", notes:["Toast","Grain","Malt","Tobacco","Pipe smoke"] },
  { cat:"Spice", color:"#B5541E", notes:["Cinnamon","Clove","Pepper","Nutmeg"] },
  { cat:"Sour/Fermented", color:"#4A90D9", notes:["Winey","Whiskey","Overripe","Fermented"] },
  { cat:"Vegetal/Green", color:"#5C8A3A", notes:["Grassy","Herbal","Under-ripe","Papery","Peapod"] },
];

function FlavourWheelRef() {
  const [open, setOpen] = useState(false);
  return (
    <div style={{border:`1px solid ${C.border}`,borderRadius:8,marginBottom:14,overflow:"hidden"}}>
      <div onClick={()=>setOpen(o=>!o)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",background:C.cream3,cursor:"pointer",userSelect:"none"}}>
        <div style={{fontFamily:fontSans,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:C.inkLight}}>Flavour wheel — quick reference</div>
        <div style={{color:C.inkFaint,fontSize:12}}>{open?"▲":"▼"}</div>
      </div>
      {open && (
        <div style={{padding:"12px 14px",display:"flex",flexWrap:"wrap",gap:8}}>
          {FLAVOUR_WHEEL.map(g => (
            <div key={g.cat} style={{border:`1px solid ${C.border}`,borderRadius:6,overflow:"hidden"}}>
              <div style={{background:g.color,color:"#fff",fontFamily:fontSans,fontSize:10,fontWeight:700,padding:"3px 8px"}}>{g.cat}</div>
              <div style={{padding:"5px 8px",fontFamily:fontSans,fontSize:10,color:C.inkMid}}>{g.notes.join(" · ")}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
// ── ROAST QC — CONTROL CHARTS ─────────────────────────────────────────────────
// Per-recipe (origin + process) control chart tracking a chosen roast
// variable across every logged roast. The target line and in-spec band
// come from the recipe's OWN history (its average and spread), not a
// fixed engineering number: standard SPC practice is to let the process
// define its own baseline, then flag drift away from it. Needs a minimum
// number of logged roasts before flags mean anything statistically.
const QC_MIN_SAMPLES = 8;

// Which variables can be tracked. EoR temp is the original; the two
// deviation-from-curve metrics pull from history.curveAdherence, saved
// whenever a roast is logged with a live curve against its own recipe.
const QC_VARIABLES = [
  { key: "eor", label: "EoR temp", unit: "°C", get: r => (r.eor && !isNaN(parseFloat(r.eor))) ? parseFloat(r.eor) : null },
  { key: "madev", label: "Mean absolute deviation", unit: "°C", get: r => r.curveAdherence?.meanAbsDev ?? null },
  { key: "tadev", label: "Total accumulated deviation", unit: "°C·min", get: r => r.curveAdherence?.totalAccumDev ?? null },
];

function qcMean(arr) { return arr.reduce((a, b) => a + b, 0) / arr.length; }
function qcStdev(arr) {
  const m = qcMean(arr);
  const variance = arr.reduce((a, v) => a + (v - m) ** 2, 0) / (arr.length - 1);
  return Math.sqrt(variance);
}

// Four flag rules, applied in sequence over the ordered list of values:
//   outOfLimit   — a single point beyond 3 standard deviations
//   twoOfThree   — 2 of the last 3 points beyond 2 standard deviations, same side
//   sevenInARow  — 7 consecutive points all on the same side of the average
//   sixTrending  — 6 consecutive points steadily rising, or steadily falling
function computeControlFlags(values) {
  const n = values.length;
  const m = qcMean(values);
  const sd = qcStdev(values) || 0.0001; // guard against a zero-spread group
  const upper2 = m + 2 * sd, lower2 = m - 2 * sd;
  const upper3 = m + 3 * sd, lower3 = m - 3 * sd;
  const flags = values.map(() => ({ outOfLimit: false, twoOfThree: false, sevenInARow: false, sixTrending: false }));

  for (let i = 0; i < n; i++) {
    if (values[i] > upper3 || values[i] < lower3) flags[i].outOfLimit = true;
  }
  for (let i = 2; i < n; i++) {
    const win = [values[i - 2], values[i - 1], values[i]];
    if (win.filter(v => v > upper2).length >= 2 || win.filter(v => v < lower2).length >= 2) flags[i].twoOfThree = true;
  }
  for (let i = 6; i < n; i++) {
    const win = values.slice(i - 6, i + 1);
    if (win.every(v => v > m) || win.every(v => v < m)) for (let j = i - 6; j <= i; j++) flags[j].sevenInARow = true;
  }
  for (let i = 5; i < n; i++) {
    const win = values.slice(i - 5, i + 1);
    let inc = true, dec = true;
    for (let j = 1; j < win.length; j++) { if (win[j] <= win[j - 1]) inc = false; if (win[j] >= win[j - 1]) dec = false; }
    if (inc || dec) for (let j = i - 5; j <= i; j++) flags[j].sixTrending = true;
  }
  return { m, sd, upper2, lower2, upper3, lower3, flags };
}

function RoastQC() {
  const [history] = useState(() => { try { return JSON.parse(localStorage.getItem("fdc_history") || "[]"); } catch { return []; } });

  const [selectedVarKey, setSelectedVarKey] = useState("eor");
  const varDef = QC_VARIABLES.find(v => v.key === selectedVarKey);

  const eligible = history.filter(r => r.origin && varDef.get(r) != null);
  const groups = {};
  eligible.forEach(r => {
    const key = `${r.origin}||${r.process || ""}`;
    (groups[key] = groups[key] || []).push(r);
  });
  Object.values(groups).forEach(g => g.sort((a, b) => (a.date || "").localeCompare(b.date || "") || a.id - b.id));
  const groupKeys = Object.keys(groups).sort((a, b) => groups[b].length - groups[a].length);

  const [selected, setSelected] = useState("");
  useEffect(() => { if (!groupKeys.includes(selected)) setSelected(groupKeys[0] || ""); }, [groupKeys.join("|"), selectedVarKey]);

  const rows = groups[selected] || [];
  const values = rows.map(r => varDef.get(r));
  const n = values.length;
  const enoughData = n >= QC_MIN_SAMPLES;
  const stats = n >= 2 ? computeControlFlags(values) : null;

  const messages = [];
  if (stats && enoughData) {
    stats.flags.forEach((f, i) => {
      if (f.outOfLimit) messages.push({ level: "critical", text: `Roast ${i + 1} (${rows[i].batch || dmy(rows[i].date) || "—"}) — out of limit at ${values[i]}${varDef.unit}.` });
    });
    const reportRun = (key, label) => {
      let start = null;
      for (let i = 0; i <= n; i++) {
        const active = i < n && stats.flags[i][key];
        if (active && start === null) start = i;
        if (!active && start !== null) { messages.push({ level: "warning", text: `Roasts ${start + 1}–${i} — ${label}.` }); start = null; }
      }
    };
    reportRun("sevenInARow", "7 in a row on the same side of the average — check for a shift in ingredients, machine, or technique");
    reportRun("sixTrending", "6 in a row trending steadily in one direction — worth a look before it drifts further");
    reportRun("twoOfThree", "2 of the last 3 landed beyond 2 standard deviations — an early warning, not yet a hard miss");
  }

  // ── Chart ──
  const W = 640, H = 260, PAD = { t: 16, r: 16, b: 30, l: 44 };
  const plotW = W - PAD.l - PAD.r, plotH = H - PAD.t - PAD.b;
  const yMin = stats ? Math.min(...values, stats.lower3) - 2 : (values.length ? Math.min(...values) - 4 : 190);
  const yMax = stats ? Math.max(...values, stats.upper3) + 2 : (values.length ? Math.max(...values) + 4 : 220);
  const toX = i => PAD.l + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const toY = v => PAD.t + plotH - ((v - yMin) / (yMax - yMin)) * plotH;
  const yTicks = [];
  if (stats) { const step = Math.max(1, Math.round((yMax - yMin) / 5)); for (let t = Math.ceil(yMin / step) * step; t <= yMax; t += step) yTicks.push(t); }

  const dotColor = (i) => {
    if (!stats || !enoughData) return C.amber;
    const f = stats.flags[i];
    if (f.outOfLimit || f.twoOfThree) return C.red;
    if (f.sevenInARow || f.sixTrending) return "#D4A537";
    return C.green;
  };

  return (
    <div style={{ paddingTop: 24 }}>
      <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 16, lineHeight: 1.5 }}>
        Tracks a chosen variable across every logged roast of a recipe. The target line and in-spec band come from this recipe's own history — its average and spread — not a fixed number, so flags only start once there's enough data to trust ({QC_MIN_SAMPLES}+ roasts).
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <select style={{ ...s.input, maxWidth: 240 }} value={selectedVarKey} onChange={e => setSelectedVarKey(e.target.value)}>
          {QC_VARIABLES.map(v => <option key={v.key} value={v.key}>{v.label}</option>)}
        </select>
        {groupKeys.length > 0 && (
          <select style={{ ...s.input, maxWidth: 340 }} value={selected} onChange={e => setSelected(e.target.value)}>
            {groupKeys.map(k => { const [o, p] = k.split("||"); return <option key={k} value={k}>{o}{p ? ` — ${p}` : ""} ({groups[k].length})</option>; })}
          </select>
        )}
      </div>

      {groupKeys.length === 0 ? (
        <div style={{ fontFamily: fontSans, fontSize: 13, color: C.inkFaint }}>No roasts with a logged {varDef.label.toLowerCase()} yet — nothing to chart.</div>
      ) : (
        <>
          {!enoughData && (
            <div style={{ fontFamily: fontSans, fontSize: 12, color: C.inkFaint, background: C.cream2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", marginBottom: 16 }}>
              {n} roast{n === 1 ? "" : "s"} logged for this recipe — flags need {QC_MIN_SAMPLES}+ before they mean anything statistically. Showing the raw points below in the meantime.
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 18 }}>
            <div style={{ background: C.cream2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 16px" }}>
              <div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Roasts plotted</div>
              <div style={{ fontFamily: fontSerif, fontSize: 24, color: C.ink }}>{n}</div>
            </div>
            <div style={{ background: enoughData && messages.some(m => m.level === "warning") ? "#FDF3E3" : C.cream2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 16px" }}>
              <div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Trending / early-warning flags</div>
              <div style={{ fontFamily: fontSerif, fontSize: 24, color: C.ink }}>{messages.filter(m => m.level === "warning").length}</div>
            </div>
            <div style={{ background: enoughData && messages.some(m => m.level === "critical") ? "#FDEAEA" : C.cream2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 16px" }}>
              <div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Out-of-limit</div>
              <div style={{ fontFamily: fontSerif, fontSize: 24, color: C.ink }}>{messages.filter(m => m.level === "critical").length}</div>
            </div>
          </div>

          <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 8, padding: "14px 16px 8px", marginBottom: 16, overflowX: "auto" }}>
            <svg width={W} height={H} style={{ display: "block" }}>
              {stats && (
                <rect x={PAD.l} y={toY(stats.upper2)} width={plotW} height={Math.max(0, toY(stats.lower2) - toY(stats.upper2))} fill={C.amber} opacity="0.08" />
              )}
              {yTicks.map(t => <line key={t} x1={PAD.l} y1={toY(t)} x2={PAD.l + plotW} y2={toY(t)} stroke={C.borderSoft} strokeWidth="0.5" />)}
              {stats && <line x1={PAD.l} y1={toY(stats.m)} x2={PAD.l + plotW} y2={toY(stats.m)} stroke={C.inkFaint} strokeWidth="1.2" strokeDasharray="5 4" />}
              <line x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={PAD.t + plotH} stroke={C.ink} strokeWidth="1" />
              <line x1={PAD.l} y1={PAD.t + plotH} x2={PAD.l + plotW} y2={PAD.t + plotH} stroke={C.ink} strokeWidth="1" />
              {yTicks.map(t => <text key={t} x={PAD.l - 6} y={toY(t) + 3} textAnchor="end" fill={C.inkLight} fontSize="10" fontFamily={fontSans}>{t}</text>)}
              {values.map((v, i) => i > 0 && <line key={i} x1={toX(i - 1)} y1={toY(values[i - 1])} x2={toX(i)} y2={toY(v)} stroke={C.ink} strokeWidth="1.5" />)}
              {values.map((v, i) => <circle key={i} cx={toX(i)} cy={toY(v)} r={dotColor(i) === C.green ? 3 : 5} fill={dotColor(i)} stroke="#fff" strokeWidth="1.5" />)}
            </svg>
            <div style={{ display: "flex", gap: 16, fontFamily: fontSans, fontSize: 10, color: C.inkFaint, flexWrap: "wrap", paddingTop: 4 }}>
              <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: C.green, marginRight: 4, verticalAlign: "middle" }} />In spec</span>
              <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#D4A537", marginRight: 4, verticalAlign: "middle" }} />Trending / early warning</span>
              <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: C.red, marginRight: 4, verticalAlign: "middle" }} />Out of limit</span>
              {stats && <span>Target {Math.round(stats.m * 10) / 10}{varDef.unit} · band ±{Math.round(stats.sd * 2 * 10) / 10}{varDef.unit}</span>}
            </div>
          </div>

          {enoughData && messages.length > 0 && (
            <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
              <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em", padding: "10px 14px", background: C.cream3 }}>Flags</div>
              {messages.map((m, i) => (
                <div key={i} style={{ padding: "8px 14px", borderBottom: i < messages.length - 1 ? `1px solid ${C.borderSoft}` : "none", display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: m.level === "critical" ? C.red : "#D4A537", flexShrink: 0, marginTop: 4 }} />
                  <div style={{ fontFamily: fontSans, fontSize: 12.5, color: C.inkMid, lineHeight: 1.5 }}>{m.text}</div>
                </div>
              ))}
            </div>
          )}
          {enoughData && messages.length === 0 && (
            <div style={{ fontFamily: fontSans, fontSize: 12, color: C.green }}>✓ No flags — this recipe's {varDef.label.toLowerCase()} has stayed in control across all {n} logged roasts.</div>
          )}
        </>
      )}
    </div>
  );
}


// ── PRODUCTION PLANNING ───────────────────────────────────────────────────────
// Orders drive the roast queue: each order is a customer + coffee + quantity +
// due date, and the queue is just those orders sorted by urgency. "Today's
// suggested sequence" applies the same smaller-before-larger, lighter-before-
// darker logic already used in Profile Builder's residual-heat guidance —
// here across a whole day's lineup instead of one roast at a time.
const ORDERS_KEY = "fdc_orders";
function loadOrders() { try { return JSON.parse(localStorage.getItem(ORDERS_KEY) || "[]"); } catch { return []; } }
function saveOrders(o) { localStorage.setItem(ORDERS_KEY, JSON.stringify(o)); }

const ROAST_LEVEL_ORDER = { "Light": 0, "Light-medium": 1, "Medium": 2, "Medium-dark": 3, "Dark": 4 };

function ProductionPlanningTab({ inventory = [], onStartRoast }) {
  // Coffees come from Green stock (plus saved blends), so an order always
  // names a coffee the rest of the Hub can match against real stock.
  const [savedBlends] = useState(() => { try { return JSON.parse(localStorage.getItem("fdc_blends") || "[]"); } catch { return []; } });
  const greenNames = [...new Set(inventory.map(i => (i.name || i.origin || "").trim()).filter(Boolean))];
  const blendNames = [...new Set(savedBlends.map(b => (b.name || "").trim()).filter(Boolean))].filter(nm => !greenNames.includes(nm));
  const [orders, setOrders] = useState(loadOrders);
  const [customers] = useState(loadCustomers);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ customerName: "", origin: "", roastLevel: "Medium", qtyKg: "", dueDate: "", notes: "" });
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addOrder = () => {
    if (!form.origin.trim() || !form.qtyKg) return;
    const o = { id: Date.now(), ...form, status: "pending" };
    const updated = [...orders, o];
    setOrders(updated); saveOrders(updated);
    setForm({ customerName: "", origin: "", roastLevel: "Medium", qtyKg: "", dueDate: "", notes: "" });
    setShowForm(false);
  };
  const setStatus = (id, status) => {
    const updated = orders.map(o => o.id === id ? { ...o, status } : o);
    setOrders(updated); saveOrders(updated);
  };
  // Opens Roast Mode for this order: the coffee's last roast settings (or its
  // Green stock details if it's never been roasted), a batch size from the
  // order, today's batch-of-day and residual heat, and the next batch number.
  // The order is marked roasted when the roast is saved.
  const startFromOrder = (o) => {
    if (!onStartRoast) return;
    let hist = [];
    try { hist = JSON.parse(localStorage.getItem("fdc_history") || "[]"); } catch { hist = []; }
    const past = hist.filter(r => sameCoffee(r.origin, o.origin) && parseFloat(r.green) > 0)
      .sort((a, b) => (b.date || "").localeCompare(a.date || "") || (b.id || 0) - (a.id || 0))[0];
    const inv = inventory.find(i => sameCoffee(i.name || i.origin, o.origin));
    const info = todaysBatchInfo();
    const fromOrder = parseFloat(o.qtyKg) > 0 ? Math.min(70, Math.round(parseFloat(o.qtyKg) / 0.86 * 2) / 2) : null;
    const greenKg = past?.green || (fromOrder != null ? String(fromOrder) : "");
    onStartRoast(buildRoastProfileFromForm({
      origin: o.origin,
      cultivar: past?.cultivar || inv?.cultivar || "",
      masl: past?.masl || inv?.masl || "",
      process: past?.process || inv?.process || "Washed",
      cupProfile: past?.cupProfile || "Espresso",
      batchKg: greenKg,
      moisture: past?.moisture || inv?.moisture || "",
      density: past?.density || inv?.density || "",
      chargeOverride: past?.chargeOverride || "",
      eorOverride: past?.eorOverride || "",
      batchNumToday: info.batchNumToday,
      prevRoastLevel: info.prevRoastLevel,
      batchId: peekNextBatchNumber(),
      orderId: o.id,
      orderLabel: `For ${o.customerName || "—"} · ${o.qtyKg}kg · ${o.roastLevel}${o.dueDate ? ` · due ${dmy(o.dueDate)}` : ""}`,
    }));
  };
  const removeOrder = (id) => {
    if (!window.confirm("Remove this order?")) return;
    const updated = orders.filter(o => o.id !== id);
    setOrders(updated); saveOrders(updated);
  };

  const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  };

  const queue = orders.filter(o => o.status === "pending" || o.status === "queued")
    .sort((a, b) => (a.dueDate || "9999").localeCompare(b.dueDate || "9999"));

  const todaysSequence = orders.filter(o => o.status === "queued")
    .sort((a, b) => {
      const q = (parseFloat(a.qtyKg) || 0) - (parseFloat(b.qtyKg) || 0);
      if (q !== 0) return q;
      return (ROAST_LEVEL_ORDER[a.roastLevel] ?? 2) - (ROAST_LEVEL_ORDER[b.roastLevel] ?? 2);
    });

  const sequenceNote = (idx, prevLevel) => {
    if (idx === 0) return "First of the day — warm up 30–60 min before charging.";
    let note = idx <= 2 ? "Modest residual heat — trim charge ~2°C from a fresh start." : "Significant residual heat by now — trim charge ~4°C from a fresh start.";
    if (prevLevel === "Dark") note += " Previous roast was dark — trim a further 3°C.";
    else if (prevLevel === "Medium" || prevLevel === "Medium-dark") note += " Previous roast was medium — trim a further 1°C.";
    return note;
  };

  return (
    <div style={{ paddingTop: 24 }}>
      <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 16, lineHeight: 1.5 }}>
        Orders drive the queue — add what's owed, mark items "Queued" once you've committed to roasting them, and the sequence below sorts that day's lineup the same way Profile Builder already reasons about residual heat: smaller and lighter first.
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={s.sectionLabel}>Orders</div>
        <button style={s.btnPrimary} onClick={() => setShowForm(v => !v)}>{showForm ? "Cancel" : "+ Add order"}</button>
      </div>

      {showForm && (
        <div style={{ background: C.cream2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "16px 18px", marginBottom: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1.5fr 1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div>
              <div style={s.fieldLabel}>Customer</div>
              <input style={s.input} list="planning-customers" value={form.customerName} onChange={e => setF("customerName", e.target.value)} placeholder="e.g. Ómós" />
              <datalist id="planning-customers">{customers.map(c => <option key={c.id} value={c.name} />)}</datalist>
            </div>
            <div>
              <div style={s.fieldLabel}>Coffee</div>
              <select style={s.input} value={form.origin} onChange={e => setF("origin", e.target.value)}>
                <option value="">— Select —</option>
                {greenNames.length > 0 && <optgroup label="Green stock">{greenNames.map(nm => <option key={nm} value={nm}>{nm}</option>)}</optgroup>}
                {blendNames.length > 0 && <optgroup label="Blends">{blendNames.map(nm => <option key={nm} value={nm}>{nm}</option>)}</optgroup>}
                {form.origin && !greenNames.includes(form.origin) && !blendNames.includes(form.origin) && <option value={form.origin}>{form.origin}</option>}
              </select>
            </div>
            <div>
              <div style={s.fieldLabel}>Target roast</div>
              <select style={s.input} value={form.roastLevel} onChange={e => setF("roastLevel", e.target.value)}>
                {["Light", "Light-medium", "Medium", "Medium-dark", "Dark"].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <div style={s.fieldLabel}>Qty (kg)</div>
              <input style={s.input} type="number" value={form.qtyKg} onChange={e => setF("qtyKg", e.target.value)} placeholder="e.g. 20" />
            </div>
            <div>
              <div style={s.fieldLabel}>Due date</div>
              <input style={s.input} type="date" value={form.dueDate} onChange={e => setF("dueDate", e.target.value)} />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={s.fieldLabel}>Notes</div>
            <input style={s.input} value={form.notes} onChange={e => setF("notes", e.target.value)} placeholder="Optional" />
          </div>
          <button style={s.btnPrimary} onClick={addOrder}>Add to queue</button>
        </div>
      )}

      {queue.length === 0 ? (
        <div style={{ fontFamily: fontSans, fontSize: 13, color: C.inkFaint, marginBottom: 24 }}>No pending or queued orders.</div>
      ) : (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden", marginBottom: 28 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: C.cream3 }}>
                {["Customer", "Coffee", "Roast", "Qty", "Due", "Status", ""].map(h => <th key={h} style={s.thStyle}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {queue.map(o => {
                const d = daysUntil(o.dueDate);
                const overdue = d !== null && d < 0;
                const soon = d !== null && d >= 0 && d <= 3;
                return (
                  <tr key={o.id} style={{ borderBottom: `1px solid ${C.borderSoft}`, background: overdue ? "#FDEAEA" : soon ? "#FDF3E3" : "transparent" }}>
                    <td style={s.td}>{o.customerName || "—"}</td>
                    <td style={{ ...s.td, fontWeight: 600, color: C.amber }}>{o.origin}</td>
                    <td style={s.td}>{o.roastLevel}</td>
                    <td style={s.td}>{o.qtyKg}kg</td>
                    <td style={{ ...s.td, color: overdue ? C.red : soon ? "#8B5A1E" : C.inkLight }}>{dmy(o.dueDate) || "—"}{overdue ? " · overdue" : soon ? ` · ${d}d` : ""}</td>
                    <td style={s.td}>
                      <select style={{ ...s.input, fontSize: 12, padding: "4px 6px" }} value={o.status} onChange={e => setStatus(o.id, e.target.value)}>
                        {["pending", "queued", "roasted", "fulfilled"].map(st => <option key={st}>{st}</option>)}
                      </select>
                    </td>
                    <td style={{ ...s.td, whiteSpace: "nowrap" }}>
                      {onStartRoast && <button style={{ ...s.btnSm, background: C.amber, color: "#fff", borderColor: C.amber, marginRight: 6 }} onClick={() => startFromOrder(o)}>Roast this</button>}
                      <button style={{ ...s.btnSm, color: C.red, borderColor: C.red }} onClick={() => removeOrder(o.id)}>Remove</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={s.sectionLabel}>Today's suggested roast sequence</div>
      <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 14 }}>
        Only orders marked "Queued" appear here. Sorted smallest batch and lightest roast first — the same thermal-step logic Profile Builder uses for residual heat.
      </div>
      {todaysSequence.length === 0 ? (
        <div style={{ fontFamily: fontSans, fontSize: 13, color: C.inkFaint }}>Mark an order "Queued" above to build today's sequence.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {todaysSequence.map((o, i) => (
            <div key={o.id} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 16px", background: C.cream2 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                <div style={{ fontFamily: fontSerif, fontSize: 15, color: C.ink }}>{i + 1}. {o.origin} — {o.qtyKg}kg ({o.roastLevel})</div>
                <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint }}>{o.customerName || "—"}</div>
              </div>
              <div style={{ fontFamily: fontSans, fontSize: 12, color: C.inkMid, lineHeight: 1.5 }}>{sequenceNote(i, i > 0 ? todaysSequence[i - 1].roastLevel : null)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── INVENTORY — COST, IMPACT & TRACEABILITY ───────────────────────────────────
// Sits alongside the existing Inventory table rather than inside it, so the
// original add/edit/remove logic stays completely untouched. Adds four things:
// a lot number per item (auto-backfilled if missing), a true landed
// cost-per-kilo (green cost adjusted for roast loss, plus a flat non-green
// overhead figure), a rough energy/CO2-per-kg estimate (openly a starting
// point to calibrate, same spirit as the Agtron calibration elsewhere), and a
// printable QR label per lot.
const INV_COST_SETTINGS_KEY = "fdc_inventory_cost_settings";
const INV_COST_DEFAULTS = { overheadPerKg: "4.50", roastLossPct: "13.5", energyPerKgRoasted: "1.5", co2PerKwh: "0.35" };
function loadInvCostSettings() {
  try {
    const saved = JSON.parse(localStorage.getItem(INV_COST_SETTINGS_KEY));
    return saved ? { ...INV_COST_DEFAULTS, ...saved } : { ...INV_COST_DEFAULTS };
  } catch { return { ...INV_COST_DEFAULTS }; }
}
function saveInvCostSettings(s) { localStorage.setItem(INV_COST_SETTINGS_KEY, JSON.stringify(s)); }

function printQrLabel(item) {
  const data = encodeURIComponent(`${item.lot} | ${item.name || "—"} | ${item.origin || "—"} | arrived ${dmy(item.arrived) || "—"}`);
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${data}`;
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>QR Label — ${item.lot}</title>
<style>
  @page { margin:6mm; size: 90mm 90mm; }
  * { box-sizing:border-box; margin:0; padding:0; font-family:-apple-system,"Helvetica Neue",Arial,sans-serif; }
  .label { border:1.5px solid #1C1814; border-radius:4px; padding:12px; text-align:center; }
  .brand { font-size:12px; font-weight:700; letter-spacing:0.1em; text-transform:uppercase; color:#1C1814; }
  .name { font-size:15px; font-weight:700; margin-top:6px; color:#1C1814; }
  .meta { font-size:11px; color:#7A6E63; margin-top:2px; }
  .lot { font-family:monospace; font-size:11px; letter-spacing:1.5px; margin-top:8px; color:#1C1814; }
  img { margin-top:8px; }
</style></head><body>
<div class="label">
  <div class="brand">Skelligs Coffee — Green Stock</div>
  <div class="name">${item.name || item.origin || "—"}</div>
  <div class="meta">${item.origin || ""}${item.process ? " · " + item.process : ""}</div>
  <img src="${qrUrl}" width="180" height="180"/>
  <div class="lot">${item.lot}</div>
</div>
<script>window.onload = () => { window.focus(); window.print(); };</script>
</body></html>`;
  const win = window.open("", "_blank");
  if (!win) { window.alert("Please allow pop-ups for this site to print the label."); return; }
  win.document.write(html);
  win.document.close();
}

function InventoryCostAndTraceability({ inventory, setInventory }) {
  const [settings, setSettings] = useState(loadInvCostSettings);
  const [showSettings, setShowSettings] = useState(false);
  const updateSetting = (k, v) => { const next = { ...settings, [k]: v }; setSettings(next); saveInvCostSettings(next); };

  // Backfill a lot number onto any item that doesn't have one yet — runs
  // once per missing item, doesn't touch anything else about the item.
  useEffect(() => {
    const missing = inventory.filter(i => !i.lot);
    if (missing.length === 0) return;
    const arrivalDate = (i) => i.arrived ? new Date(i.arrived) : new Date();
    const updated = inventory.map(i => i.lot ? i : { ...i, lot: generateLotNumber({ roastBatch: i.name || i.origin, date: arrivalDate(i) }) });
    setInventory(updated);
    saveInventory(updated);
  }, [inventory.length]);

  const totalKg = (item) => Math.round(parseFloat(item.bagWeight || 0) * parseFloat(item.bags || 0) * 10) / 10;
  const loss = (parseFloat(settings.roastLossPct) || 13.5) / 100;
  const trueCostPerKg = (item) => {
    const green = parseFloat(item.costPerKg);
    if (!green) return null;
    const roastedEquivCost = green / (1 - loss);
    return Math.round((roastedEquivCost + (parseFloat(settings.overheadPerKg) || 0)) * 100) / 100;
  };
  const co2PerKg = Math.round((parseFloat(settings.energyPerKgRoasted) || 0) * (parseFloat(settings.co2PerKwh) || 0) * 100) / 100;

  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", padding: "12px 16px", background: C.cream2, border: `1px solid ${C.border}`, borderRadius: 8 }} onClick={() => setShowSettings(o => !o)}>
        <div>
          <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em" }}>Cost & impact settings {showSettings ? "▾" : "▸"}</div>
          <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginTop: 2 }}>Non-green cost and energy figures used below — starting estimates, adjust to match your actual numbers.</div>
        </div>
      </div>
      {showSettings && (
        <div style={{ border: `1px solid ${C.border}`, borderTop: "none", borderRadius: "0 0 8px 8px", padding: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
            <div>
              <div style={s.fieldLabel}>Non-green cost/kg (€)</div>
              <input style={s.input} type="number" step="0.1" value={settings.overheadPerKg} onChange={e => updateSetting("overheadPerKg", e.target.value)} />
              <div style={s.hint}>Labour, rent, energy, overheads combined — see Pricing tab for the full breakdown.</div>
            </div>
            <div>
              <div style={s.fieldLabel}>Roast loss %</div>
              <input style={s.input} type="number" step="0.5" value={settings.roastLossPct} onChange={e => updateSetting("roastLossPct", e.target.value)} />
            </div>
            <div>
              <div style={s.fieldLabel}>Energy per roasted kg</div>
              <input style={s.input} type="number" step="0.1" value={settings.energyPerKgRoasted} onChange={e => updateSetting("energyPerKgRoasted", e.target.value)} />
              <div style={s.hint}>kWh-equivalent — rough estimate, calibrate against a real gas/electric bill when you have one.</div>
            </div>
            <div>
              <div style={s.fieldLabel}>CO₂ per kWh (kg)</div>
              <input style={s.input} type="number" step="0.01" value={settings.co2PerKwh} onChange={e => updateSetting("co2PerKwh", e.target.value)} />
            </div>
          </div>
          <div style={{ marginTop: 10, fontFamily: fontSans, fontSize: 11, color: C.inkFaint }}>
            Current estimate: ~{co2PerKg}kg CO₂ per roasted kg, at {settings.energyPerKgRoasted} kWh/kg.
          </div>
        </div>
      )}

      <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em", marginTop: 20, marginBottom: 10 }}>
        Lot traceability & true cost
      </div>
      {inventory.length === 0 ? (
        <div style={{ fontFamily: fontSans, fontSize: 13, color: C.inkFaint }}>No coffees in inventory yet.</div>
      ) : (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: C.cream3 }}>
                {["Coffee", "Lot", "Stock", "True cost/kg", "Est. CO₂/kg", ""].map(h => <th key={h} style={s.thStyle}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {inventory.map(item => {
                const tc = trueCostPerKg(item);
                return (
                  <tr key={item.id} style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                    <td style={{ ...s.td, fontWeight: 600, color: C.amber }}>{item.name || item.origin || "—"}</td>
                    <td style={{ ...s.td, fontFamily: "monospace", fontSize: 12 }}>{item.lot || "—"}</td>
                    <td style={s.td}>{totalKg(item)}kg</td>
                    <td style={s.td}>{tc != null ? `€${tc}` : "—"}</td>
                    <td style={s.td}>{co2PerKg}kg</td>
                    <td style={s.td}><button style={s.btnSm} onClick={() => printQrLabel(item)} disabled={!item.lot}>Print QR label</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, marginTop: 8, lineHeight: 1.5 }}>
        True cost/kg = green cost adjusted for roast loss, plus the non-green figure above. QR labels need an internet connection to render the code image when printing.
      </div>
    </div>
  );
}


// ── CUPPING CORRELATION VIEW ──────────────────────────────────────────────────
// Links cupping scores (from QC Tools) to roast variables — DTR%, drop temp,
// charge temp — pulled from each roast's own logged events, not re-entered.
// Bucket into three ranges (low/mid/high) and call out which one scored best,
// same spirit as the diagnostic feedback loop elsewhere: data refining
// judgement, not replacing it. Needs a handful of cupped roasts of the same
// coffee before a bucket comparison means anything.
const CORR_MIN_SAMPLES = 5;

function parseEventTime(timeStr) {
  if (!timeStr || typeof timeStr !== "string") return null;
  const parts = timeStr.split(":");
  if (parts.length !== 2) return null;
  const m = parseInt(parts[0], 10), s = parseInt(parts[1], 10);
  if (isNaN(m) || isNaN(s)) return null;
  return m * 60 + s;
}

function extractRoastVariables(record) {
  const events = record.events || [];
  const findEvent = (names) => events.find(e => names.includes(e.name) || names.includes(e.label));
  const chargeEv = findEvent(["Charge", "CHARGE"]);
  const fcEv = findEvent(["First crack", "FCs"]);
  const dropEv = findEvent(["Drop", "DROP"]);

  const chargeTemp = chargeEv?.temp ?? (record.charge != null ? parseFloat(record.charge) : null);
  const dropTemp = dropEv?.temp ?? (record.eor ? parseFloat(record.eor) : null);

  let dtrPct = null;
  const fcSec = fcEv ? parseEventTime(fcEv.time) : null;
  const dropSec = dropEv ? parseEventTime(dropEv.time) : null;
  if (fcSec != null && dropSec != null && dropSec > 0) {
    dtrPct = Math.round(((dropSec - fcSec) / dropSec) * 1000) / 10;
  }

  return { dtrPct, dropTemp, chargeTemp: chargeTemp != null && !isNaN(chargeTemp) ? chargeTemp : null };
}

function getCuppingScore(record) {
  if (record.cupping?.mode === "cva") {
    const vals = CVA_ATTRS.map(k => parseFloat(record.cupping.attrs[k])).filter(v => !isNaN(v));
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  }
  const s = parseFloat(record.score);
  return isNaN(s) ? null : s;
}

function CorrelationView() {
  const [history] = useState(() => { try { return JSON.parse(localStorage.getItem("fdc_history") || "[]"); } catch { return []; } });

  const withData = history
    .filter(r => r.origin && getCuppingScore(r) != null)
    .map(r => ({ ...r, score: getCuppingScore(r), vars: extractRoastVariables(r) }));

  const byOrigin = {};
  withData.forEach(r => { (byOrigin[r.origin] = byOrigin[r.origin] || []).push(r); });
  const originKeys = Object.keys(byOrigin).sort((a, b) => byOrigin[b].length - byOrigin[a].length);

  const [selectedOrigin, setSelectedOrigin] = useState("");
  useEffect(() => { if (!selectedOrigin && originKeys.length) setSelectedOrigin(originKeys[0]); }, [originKeys.length]);

  const VARIABLES = [
    { key: "dtrPct", label: "DTR %", unit: "%" },
    { key: "dropTemp", label: "Drop temp", unit: "°C" },
    { key: "chargeTemp", label: "Charge temp", unit: "°C" },
  ];
  const [selectedVar, setSelectedVar] = useState("dtrPct");
  const varDef = VARIABLES.find(v => v.key === selectedVar);

  const rows = (byOrigin[selectedOrigin] || []).filter(r => r.vars[selectedVar] != null);
  const n = rows.length;
  const enoughData = n >= CORR_MIN_SAMPLES;

  let insight = null;
  if (enoughData) {
    const sorted = [...rows].sort((a, b) => a.vars[selectedVar] - b.vars[selectedVar]);
    const third = Math.ceil(n / 3);
    const buckets = [
      { label: "Low", items: sorted.slice(0, third) },
      { label: "Mid", items: sorted.slice(third, n - third) },
      { label: "High", items: sorted.slice(n - third) },
    ].filter(b => b.items.length > 0);
    const withAvg = buckets.map(b => ({
      ...b,
      avgScore: Math.round((b.items.reduce((a, r) => a + r.score, 0) / b.items.length) * 100) / 100,
      minVar: Math.min(...b.items.map(r => r.vars[selectedVar])),
      maxVar: Math.max(...b.items.map(r => r.vars[selectedVar])),
    }));
    const best = withAvg.reduce((a, b) => (b.avgScore > a.avgScore ? b : a), withAvg[0]);
    insight = { best, buckets: withAvg };
  }

  // ── Scatter plot ──
  const W = 560, H = 260, PAD = { t: 16, r: 16, b: 34, l: 44 };
  const plotW = W - PAD.l - PAD.r, plotH = H - PAD.t - PAD.b;
  const xVals = rows.map(r => r.vars[selectedVar]);
  const yVals = rows.map(r => r.score);
  const xMin = xVals.length ? Math.min(...xVals) - 1 : 0, xMax = xVals.length ? Math.max(...xVals) + 1 : 1;
  const yMin = yVals.length ? Math.min(...yVals) - 1 : 0, yMax = yVals.length ? Math.max(...yVals) + 1 : 10;
  const toX = v => PAD.l + ((v - xMin) / (xMax - xMin || 1)) * plotW;
  const toY = v => PAD.t + plotH - ((v - yMin) / (yMax - yMin || 1)) * plotH;

  return (
    <div style={{ paddingTop: 24 }}>
      <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 16, lineHeight: 1.5 }}>
        Plots your cupping scores against a roast variable, pulled from each roast's own logged charge/FC/drop events — nothing re-entered by hand. Needs {CORR_MIN_SAMPLES}+ cupped roasts of the same coffee before the range comparison below means anything.
      </div>

      {originKeys.length === 0 ? (
        <div style={{ fontFamily: fontSans, fontSize: 13, color: C.inkFaint }}>No cupped roasts yet — log a score in QC Tools first.</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
            <select style={{ ...s.input, maxWidth: 320 }} value={selectedOrigin} onChange={e => setSelectedOrigin(e.target.value)}>
              {originKeys.map(k => <option key={k} value={k}>{k} ({byOrigin[k].length})</option>)}
            </select>
            <select style={{ ...s.input, maxWidth: 200 }} value={selectedVar} onChange={e => setSelectedVar(e.target.value)}>
              {VARIABLES.map(v => <option key={v.key} value={v.key}>{v.label}</option>)}
            </select>
          </div>

          {!enoughData && (
            <div style={{ fontFamily: fontSans, fontSize: 12, color: C.inkFaint, background: C.cream2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", marginBottom: 16 }}>
              {n} cupped roast{n === 1 ? "" : "s"} with a logged {varDef.label.toLowerCase()} for this coffee — need {CORR_MIN_SAMPLES}+ before a range comparison is meaningful.
            </div>
          )}

          <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 8, padding: "14px 16px 8px", marginBottom: 20, overflowX: "auto" }}>
            {n === 0 ? (
              <div style={{ fontFamily: fontSans, fontSize: 12, color: C.inkFaint, padding: "20px 0" }}>No roasts of this coffee have a logged {varDef.label.toLowerCase()} yet.</div>
            ) : (
              <svg width={W} height={H}>
                <line x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={PAD.t + plotH} stroke={C.ink} strokeWidth="1" />
                <line x1={PAD.l} y1={PAD.t + plotH} x2={PAD.l + plotW} y2={PAD.t + plotH} stroke={C.ink} strokeWidth="1" />
                <text x={PAD.l + plotW / 2} y={H - 4} textAnchor="middle" fill={C.inkFaint} fontSize="10" fontFamily={fontSans}>{varDef.label} ({varDef.unit})</text>
                <text x={10} y={PAD.t + plotH / 2} textAnchor="middle" fill={C.inkFaint} fontSize="10" fontFamily={fontSans} transform={`rotate(-90,10,${PAD.t + plotH / 2})`}>Score</text>
                {rows.map((r, i) => <circle key={i} cx={toX(r.vars[selectedVar])} cy={toY(r.score)} r="4.5" fill={C.amber} stroke="#fff" strokeWidth="1" opacity="0.85" />)}
              </svg>
            )}
          </div>

          {insight && (
            <div style={{ background: "#F0F7F1", border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.green}`, borderRadius: 6, padding: "14px 16px" }}>
              <div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>What the data shows</div>
              <div style={{ fontFamily: fontSans, fontSize: 13, color: C.ink, marginBottom: 10 }}>
                <strong>{selectedOrigin}</strong> scores highest when {varDef.label.toLowerCase()} is <strong>{insight.best.minVar}–{insight.best.maxVar}{varDef.unit}</strong> (avg score {insight.best.avgScore}, {insight.best.items.length} roast{insight.best.items.length === 1 ? "" : "s"}).
              </div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                {insight.buckets.map(b => (
                  <div key={b.label} style={{ fontFamily: fontSans, fontSize: 11, color: b.label === insight.best.label ? C.green : C.inkLight }}>
                    {b.label} ({b.minVar}–{b.maxVar}{varDef.unit}): avg {b.avgScore}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ── CURVE CONSISTENCY ─────────────────────────────────────────────────────────
// Overlays every logged curve for a given coffee on one chart — the point
// isn't any single roast's shape, it's whether the shape repeats roast to
// roast. Recent roasts drawn darker/thicker than older ones so a drift shows
// up as the newest lines visibly peeling away from the older bundle.
function CurveConsistencyView() {
  const [history] = useState(() => { try { return JSON.parse(localStorage.getItem("fdc_history") || "[]"); } catch { return []; } });

  const withCurves = history.filter(r => r.origin && Array.isArray(r.curve) && r.curve.length > 2);
  const byOrigin = {};
  withCurves.forEach(r => { (byOrigin[r.origin] = byOrigin[r.origin] || []).push(r); });
  const originKeys = Object.keys(byOrigin).sort((a, b) => byOrigin[b].length - byOrigin[a].length);

  const [selectedOrigin, setSelectedOrigin] = useState("");
  useEffect(() => { if (!selectedOrigin && originKeys.length) setSelectedOrigin(originKeys[0]); }, [originKeys.length]);

  // Most recent first, capped so the chart doesn't turn into visual noise —
  // consistency is best judged against a recent run of batches anyway, not
  // every roast of this coffee ever done.
  const MAX_OVERLAY = 10;
  const roasts = (byOrigin[selectedOrigin] || [])
    .slice()
    .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
    .slice(0, MAX_OVERLAY);

  const W = 860, H = 340, PAD = { t: 16, r: 20, b: 34, l: 46 };
  const plotW = W - PAD.l - PAD.r, plotH = H - PAD.t - PAD.b;
  const allPts = roasts.flatMap(r => r.curve.filter(p => p.temp != null));
  const xMax = allPts.length ? Math.max(...allPts.map(p => p.t)) + 0.5 : 12;
  const yMin = 60;
  const yMax = allPts.length ? Math.max(...allPts.map(p => p.temp)) + 12 : 230;
  const toX = v => PAD.l + (v / xMax) * plotW;
  const toY = v => PAD.t + (1 - (v - yMin) / (yMax - yMin)) * plotH;

  const xTicks = []; for (let m = 0; m <= Math.ceil(xMax); m += 2) xTicks.push(m);
  const yTicks = []; for (let t = Math.ceil(yMin/20)*20; t <= yMax; t += 20) yTicks.push(t);

  return (
    <div style={{ paddingTop: 24 }}>
      <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 16, lineHeight: 1.5 }}>
        Overlays every logged curve for a coffee, most recent {MAX_OVERLAY} roasts — the newest is drawn darkest, so a batch that's drifted from how this coffee usually roasts shows up as a line peeling away from the rest.
      </div>

      {originKeys.length === 0 ? (
        <div style={{ fontFamily: fontSans, fontSize: 13, color: C.inkFaint }}>No roasts with a logged curve yet — curves are captured automatically once Roast Mode is used.</div>
      ) : (
        <>
          <select style={{ ...s.input, maxWidth: 320, marginBottom: 18 }} value={selectedOrigin} onChange={e => setSelectedOrigin(e.target.value)}>
            {originKeys.map(k => <option key={k} value={k}>{k} ({byOrigin[k].length})</option>)}
          </select>

          <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 8, padding: "14px 16px 8px", marginBottom: 16, overflowX: "auto" }}>
            <svg width={W} height={H}>
              {xTicks.map(x => <line key={x} x1={toX(x)} y1={PAD.t} x2={toX(x)} y2={PAD.t+plotH} stroke={C.borderSoft} strokeWidth="0.5"/>)}
              {yTicks.map(y => <line key={y} x1={PAD.l} y1={toY(y)} x2={PAD.l+plotW} y2={toY(y)} stroke={C.borderSoft} strokeWidth="0.5"/>)}
              <line x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={PAD.t+plotH} stroke={C.ink} strokeWidth="1"/>
              <line x1={PAD.l} y1={PAD.t+plotH} x2={PAD.l+plotW} y2={PAD.t+plotH} stroke={C.ink} strokeWidth="1"/>
              {xTicks.map(x => <text key={x} x={toX(x)} y={PAD.t+plotH+16} textAnchor="middle" fill={C.inkLight} fontSize="10" fontFamily={fontSans}>{x}</text>)}
              {yTicks.map(y => <text key={y} x={PAD.l-6} y={toY(y)+3} textAnchor="end" fill={C.inkLight} fontSize="10" fontFamily={fontSans}>{y}</text>)}
              <text x={PAD.l+plotW/2} y={H-2} textAnchor="middle" fill={C.inkLight} fontSize="10" fontFamily={fontSans}>Time (minutes)</text>

              {roasts.map((r, i) => {
                const pts = r.curve.filter(p => p.temp != null).sort((a,b) => a.t - b.t).map(p => [toX(p.t), toY(p.temp)]);
                if (pts.length < 2) return null;
                // Newest (i=0) darkest/thickest, fading out toward the oldest —
                // this is what actually makes a drifted batch pop visually.
                const fade = 1 - (i / Math.max(1, roasts.length - 1)) * 0.65;
                return <path key={r.id} d={smoothSvgPath(pts)} fill="none" stroke={C.amber} strokeWidth={i === 0 ? 2.5 : 1.5} opacity={Math.max(0.25, fade)}/>;
              })}
            </svg>
          </div>

          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            {roasts.map((r, i) => (
              <div key={r.id} style={{ display:"flex", alignItems:"center", gap:6, fontFamily:fontSans, fontSize:11, color:C.inkLight }}>
                <span style={{ display:"inline-block", width:14, height:2, background:C.amber, opacity:Math.max(0.25, 1 - (i/Math.max(1,roasts.length-1))*0.65) }}/>
                {r.batch || "—"} · {dmy(r.date) || "no date"}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


// ── HISTORY ───────────────────────────────────────────────────────────────────
// ── ARTISAN CSV IMPORT ────────────────────────────────────────────────────────
// Confirmed against a real Halo Beriti roast export, July 2026:
//   Row 1: metadata — Date, Unit, event marker times (MM:SS from recording start)
//   Row 2: column headers — Time1 (from recording start), Time2 (from Charge),
//          ET, BT, Event, {3} (this is Burner %, confirmed by tracking it
//          through a real roast: sits at the 20% floor through drying, ramps
//          through Maillard, pulls back near FC, returns to 20% after drop),
//          Inlet, Stack, RoR
// IMPORTANT — charge temp ambiguity, confirmed from real data: the BT value
// AT the CHARGE event marker is not a reliable "charge temp" on its own. On
// this machine (Loring auto-detects Charge off the hopper door opening) the
// probe is still reading close to idle/return-air temp at that instant — the
// real thermal drop from beans only shows up over the following seconds, and
// on a 30kg batch the dip doesn't bottom out until the Turning Point, which
// can be 2-3 minutes later. There is no single clean "true charge" instant to
// extract automatically — this returns both the marker value and a value a
// few seconds later, and surfaces the full curve, so the person doing the
// import can judge it against what they actually saw happen.
function parseArtisanCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.length > 0);
  if (lines.length < 3) throw new Error("File too short to be a valid Artisan export");

  const meta = {};
  lines[0].split("\t").forEach(pair => {
    const idx = pair.indexOf(":");
    if (idx === -1) return;
    meta[pair.slice(0, idx).trim()] = pair.slice(idx + 1).trim();
  });

  const cols = lines[1].split("\t").map(c => c.trim());
  const idx = name => cols.indexOf(name);
  const iTime2 = idx("Time2"), iET = idx("ET"), iBT = idx("BT"),
        iEvent = idx("Event"), iBurner = idx("{3}"), iInlet = idx("Inlet"), iStack = idx("Stack"), iRoR = idx("RoR");
  if (iBT === -1 || iEvent === -1) throw new Error("Doesn't look like an Artisan CSV export — expected columns not found (check it's the CSV export, not RoastLogger or another format)");

  const rows = lines.slice(2).map(line => {
    const f = line.split("\t");
    return {
      time2: f[iTime2] || null,
      et: parseFloat(f[iET]), bt: parseFloat(f[iBT]),
      event: (f[iEvent] || "").trim(),
      burner: parseFloat(f[iBurner]), inlet: parseFloat(f[iInlet]), stack: parseFloat(f[iStack]),
      ror: parseFloat(f[iRoR]),
    };
  }).filter(r => !isNaN(r.bt));

  const chargeIdx = rows.findIndex(r => r.event === "CHARGE");
  if (chargeIdx === -1) throw new Error("No CHARGE event found in this file — make sure Charge was marked during the roast");

  const chargeMarkerBT = Math.round(rows[chargeIdx].bt * 10) / 10;
  const settledIdx = Math.min(chargeIdx + 5, rows.length - 1);
  const chargeSettledBT = Math.round(rows[settledIdx].bt * 10) / 10;

  const findEvent = label => rows.find(r => r.event === label);
  const tp = findEvent("TP"), dryEnd = findEvent("DRY End"), fcs = findEvent("FCs"), drop = findEvent("DROP");

  return {
    meta,
    chargeMarkerBT, chargeSettledBT,
    events: {
      tp: tp?.time2 || null, tpBT: tp ? Math.round(tp.bt*10)/10 : null,
      dryEnd: dryEnd?.time2 || null, dryEndBT: dryEnd ? Math.round(dryEnd.bt*10)/10 : null,
      fcs: fcs?.time2 || null, fcsBT: fcs ? Math.round(fcs.bt*10)/10 : null,
      drop: drop?.time2 || null, dropBT: drop ? Math.round(drop.bt*10)/10 : null,
    },
    curve: rows.slice(chargeIdx).map(r => ({ t: r.time2, bt: r.bt, et: r.et, burner: r.burner, ror: r.ror })),
  };
}

function ArtisanImportPanel({ onImported }) {
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState("");
  const [parsed, setParsed] = useState(null);
  const [error, setError] = useState("");
  const [chargeChoice, setChargeChoice] = useState("settled"); // "marker" | "settled" | "custom"
  const [customCharge, setCustomCharge] = useState("");
  const [origin, setOrigin] = useState("");
  const [batch, setBatch] = useState("");
  const [green, setGreen] = useState("");
  const [roasted, setRoasted] = useState("");
  const [saved, setSaved] = useState(false);

  const handleParse = (text) => {
    setRaw(text);
    setError("");
    setParsed(null);
    if (!text.trim()) return;
    try {
      setParsed(parseArtisanCSV(text));
    } catch (e) {
      setError(e.message);
    }
  };

  const handleFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => handleParse(String(e.target.result));
    reader.readAsText(file);
  };

  const finalCharge = chargeChoice === "marker" ? parsed?.chargeMarkerBT
    : chargeChoice === "settled" ? parsed?.chargeSettledBT
    : parseFloat(customCharge) || "";

  const handleSave = () => {
    if (!parsed) return;
    const hist = JSON.parse(localStorage.getItem("fdc_history") || "[]");
    const r = {
      id: Date.now(), batch: batch || "", origin: origin || "",
      green: green || "", roasted: roasted || "",
      loss: (green && roasted) ? (((parseFloat(green) - parseFloat(roasted)) / parseFloat(green)) * 100).toFixed(1) + "%" : "",
      score: "", agtron: "", notes: `Imported from Artisan (${parsed.meta.Date || "date unknown"}). Charge: marker ${parsed.chargeMarkerBT}°C / settled ${parsed.chargeSettledBT}°C — used ${finalCharge}°C.`,
      eor: parsed.events.dropBT || "", cultivar: "", process: "",
      events: [
        parsed.events.tp && { label: "TP", time: parsed.events.tp, temp: parsed.events.tpBT },
        parsed.events.dryEnd && { label: "Dry End", time: parsed.events.dryEnd, temp: parsed.events.dryEndBT },
        parsed.events.fcs && { label: "FCs", time: parsed.events.fcs, temp: parsed.events.fcsBT },
        parsed.events.drop && { label: "Drop", time: parsed.events.drop, temp: parsed.events.dropBT },
      ].filter(Boolean),
      curve: parsed.curve,
      date: parsed.meta.Date ? parsed.meta.Date.split(".").reverse().join("-") : localIsoDate(),
    };
    localStorage.setItem("fdc_history", JSON.stringify([r, ...hist]));
    setSaved(true);
    if (onImported) onImported();
    setTimeout(() => { setOpen(false); setSaved(false); setParsed(null); setRaw(""); setOrigin(""); setBatch(""); setGreen(""); setRoasted(""); }, 1800);
  };

  return (
    <div style={{ marginTop: 16, border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: C.cream2, cursor: "pointer" }}>
        <div>
          <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em" }}>Import from Artisan {open ? "▾" : "▸"}</div>
          <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginTop: 2 }}>Paste or upload an Artisan CSV export (File → Export → CSV) to pull in the real logged curve and event timings.</div>
        </div>
      </div>
      {open && (
        <div style={{ padding: 16 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
            <label style={{ ...s.btn, cursor: "pointer" }}>
              Choose file…
              <input type="file" accept=".csv,.txt" style={{ display: "none" }} onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
            </label>
            <span style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint }}>or paste the raw file content below</span>
          </div>
          <textarea
            value={raw}
            onChange={e => handleParse(e.target.value)}
            placeholder="Paste Artisan CSV export content here…"
            style={{ ...s.input, width: "100%", minHeight: 90, fontFamily: "monospace", fontSize: 11 }}
          />
          {error && <div style={{ fontFamily: fontSans, fontSize: 12, color: C.red, marginTop: 8 }}>{error}</div>}

          {parsed && (
            <div style={{ marginTop: 14 }}>
              <div style={{ background: "#FDF3EF", border: "1px solid #E5C8BC", borderRadius: 6, padding: "10px 12px", marginBottom: 14 }}>
                <div style={{ fontFamily: fontSans, fontSize: 11, fontWeight: 700, color: "#8B3A2A", marginBottom: 6 }}>Charge temp — two real candidates, your call which to use</div>
                <div style={{ fontFamily: fontSans, fontSize: 11.5, color: "#8B3A2A", lineHeight: 1.6 }}>
                  On this batch size, the CHARGE marker fires before the bean probe has caught up — the reading keeps falling for a while after. Neither number below is automatically "correct"; pick whichever matches what you actually saw on the touchscreen at charge, or enter your own.
                </div>
                <div style={{ display: "flex", gap: 16, marginTop: 10, flexWrap: "wrap" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: fontSans, fontSize: 12.5, cursor: "pointer" }}>
                    <input type="radio" checked={chargeChoice === "marker"} onChange={() => setChargeChoice("marker")} />
                    At CHARGE marker: <strong>{parsed.chargeMarkerBT}°C</strong>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: fontSans, fontSize: 12.5, cursor: "pointer" }}>
                    <input type="radio" checked={chargeChoice === "settled"} onChange={() => setChargeChoice("settled")} />
                    +5 seconds: <strong>{parsed.chargeSettledBT}°C</strong>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: fontSans, fontSize: 12.5, cursor: "pointer" }}>
                    <input type="radio" checked={chargeChoice === "custom"} onChange={() => setChargeChoice("custom")} />
                    Custom:
                    <input type="number" value={customCharge} onChange={e => { setCustomCharge(e.target.value); setChargeChoice("custom"); }} style={{ ...s.input, width: 70, fontSize: 12 }} placeholder="°C" />
                  </label>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 14 }}>
                {[["Turning Point", parsed.events.tp, parsed.events.tpBT], ["Dry End", parsed.events.dryEnd, parsed.events.dryEndBT], ["First Crack", parsed.events.fcs, parsed.events.fcsBT], ["Drop", parsed.events.drop, parsed.events.dropBT]].map(([label, time, bt]) => (
                  <div key={label} style={{ border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 10px", background: C.cream }}>
                    <div style={{ fontFamily: fontSans, fontSize: 9, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
                    <div style={{ fontFamily: fontSerif, fontSize: 15, color: C.ink }}>{time || "—"}</div>
                    <div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint }}>{bt != null ? `${bt}°C` : ""}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 14 }}>
                <div><div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, marginBottom: 3 }}>Origin</div><input style={s.input} value={origin} onChange={e => setOrigin(e.target.value)} placeholder="e.g. Halo Beriti — Ethiopia" /></div>
                <div><div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, marginBottom: 3 }}>Batch #</div><input style={s.input} value={batch} onChange={e => setBatch(e.target.value)} placeholder="R26-098" /></div>
                <div><div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, marginBottom: 3 }}>Green in (kg)</div><input style={s.input} type="number" value={green} onChange={e => setGreen(e.target.value)} /></div>
                <div><div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, marginBottom: 3 }}>Roasted out (kg)</div><input style={s.input} type="number" value={roasted} onChange={e => setRoasted(e.target.value)} /></div>
              </div>

              <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 10 }}>
                Will save with charge = <strong style={{ color: C.ink }}>{finalCharge || "—"}°C</strong>, drop (EoR) = <strong style={{ color: C.ink }}>{parsed.events.dropBT || "—"}°C</strong>, total time = <strong style={{ color: C.ink }}>{parsed.events.drop || "—"}</strong>. Agtron and cultivar can be filled in after, same as any other logged roast.
              </div>

              <button style={{ ...s.btnPrimary, background: saved ? C.green : C.ink }} onClick={handleSave} disabled={saved}>
                {saved ? "✓ Added to Roast History" : "Add to Roast History"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── SENSORY ATTRIBUTE SETS — module-level since both History and QC Tools reference them ──
const SCA_ATTRS = ["Fragrance/Aroma","Flavour","Aftertaste","Acidity","Body","Balance","Uniformity","Clean Cup","Sweetness","Overall"];
const CVA_ATTRS = ["Fragrance/Aroma","Flavor","Aftertaste","Acidity","Sweetness","Mouthfeel","Overall"];

// ── QC TOOLS ───────────────────────────────────────────────────────────────────
// Its own place in the Hub, not nested inside a roast log entry — pick which
// batch you're recording against, then colour and cupping are both right
// there. Reads and writes the same fdc_history records History does, so
// nothing here duplicates data — it's a different door into the same room.
function QCTools() {
  const loadHistory = () => { try { return JSON.parse(localStorage.getItem("fdc_history") || "[]"); } catch { return []; } };
  const [history, setHistory] = useState(loadHistory);
  const [selectedId, setSelectedId] = useState(null);
  const selected = history.find(r => r.id === selectedId) || null;

  // ── Colour ──
  const [agtron, setAgtron] = useState("");
  const [agtronGround, setAgtronGround] = useState("");
  const [agtronUniformity, setAgtronUniformity] = useState("");

  // ── Cupping ──
  const initCup = () => Object.fromEntries(SCA_ATTRS.map(a=>[a,"8.00"]));
  const initCva = () => Object.fromEntries(CVA_ATTRS.map(a=>[a,"7"]));
  const [showCupping, setShowCupping] = useState(false);
  const [cuppingMode, setCuppingMode] = useState("classic");
  const [cup, setCup] = useState(initCup());
  const [defects, setDefects] = useState({ taints: 0, faults: 0 });
  const [cva, setCva] = useState(initCva());
  const [cvaUniformCups, setCvaUniformCups] = useState(5);
  const [cvaCleanCups, setCvaCleanCups] = useState(5);
  const cupTotal = () => {
    const sum = SCA_ATTRS.reduce((a,k)=>a+(parseFloat(cup[k])||0),0);
    return Math.max(0, sum - ((defects.taints*2)+(defects.faults*4))).toFixed(2);
  };
  const cvaAvg = () => {
    const vals = CVA_ATTRS.map(k => parseFloat(cva[k])||0);
    return (vals.reduce((a,b)=>a+b,0) / vals.length).toFixed(1);
  };

  const selectRoast = (r) => {
    setSelectedId(r.id);
    setAgtron(r.agtron || ""); setAgtronGround(r.agtronGround || ""); setAgtronUniformity(r.agtronUniformity || "");
    if (r.cupping) {
      setShowCupping(true);
      setCuppingMode(r.cupping.mode || "classic");
      if (r.cupping.mode === "cva") { setCva(r.cupping.attrs); setCvaUniformCups(r.cupping.uniformCups ?? 5); setCvaCleanCups(r.cupping.cleanCups ?? 5); setCup(initCup()); setDefects({taints:0,faults:0}); }
      else { setCup(r.cupping.attrs); setDefects(r.cupping.defects); setCva(initCva()); setCvaUniformCups(5); setCvaCleanCups(5); }
    } else {
      setShowCupping(false); setCuppingMode("classic");
      setCup(initCup()); setDefects({taints:0,faults:0}); setCva(initCva()); setCvaUniformCups(5); setCvaCleanCups(5);
    }
  };

  const [saved, setSaved] = useState(false);
  const saveQC = () => {
    if (!selectedId) return;
    const finalScore = showCupping ? (cuppingMode === "cva" ? cvaAvg() : cupTotal()) : selected?.score;
    const cuppingData = !showCupping ? null
      : cuppingMode === "cva" ? { mode:"cva", attrs: cva, uniformCups: cvaUniformCups, cleanCups: cvaCleanCups }
      : { mode:"classic", attrs: cup, defects };
    const updated = history.map(r => r.id === selectedId ? { ...r, agtron, agtronGround, agtronUniformity, score: finalScore, cupping: cuppingData } : r);
    setHistory(updated);
    localStorage.setItem("fdc_history", JSON.stringify(updated));
    if (!isNaN(wb) && pred) {
      submitPoolEntry("/agtron", {
        cultivarGroup: getCultivarData(selected?.cultivar).group,
        measured: wb,
        predicted: pred.predicted,
      });
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 1800);
  };

  const wb = parseFloat(agtron), grd = parseFloat(agtronGround), uni = parseFloat(agtronUniformity);
  const delta = (!isNaN(wb) && !isNaN(grd)) ? Math.abs(Math.round((wb - grd) * 10) / 10) : null;
  const deltaGood = delta !== null && delta <= 5;
  const uniGood = !isNaN(uni) && uni >= 88;
  const pred = selected && (selected.eor || selected.cultivar) ? predictAgtron(selected.eor, selected.cultivar) : null;

  return (
    <div style={{ paddingTop: 24 }}>
      <div style={{ fontFamily:fontSans, fontSize:11, color:C.inkFaint, marginBottom:16, lineHeight:1.5 }}>
        Pick a roast, then log its colour reading and cupping score — both live here, not buried inside the roast log itself. Everything saves straight back to that roast's record in Roast History.
      </div>

      {/* ── ROAST PICKER ── */}
      <div style={{ border:`1px solid ${C.border}`, borderRadius:8, padding:"14px 16px", marginBottom:20, background:C.cream2 }}>
        <div style={{ fontFamily:fontSans, fontSize:10, fontWeight:700, color:C.inkFaint, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>Select a roast</div>
        {history.length === 0 ? (
          <div style={{ fontFamily:fontSans, fontSize:12, color:C.inkFaint }}>No roasts logged yet — nothing to record QC against.</div>
        ) : (
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {history.slice(0, 24).map(r => (
              <button key={r.id} onClick={() => selectRoast(r)} style={{
                fontFamily:fontSans, fontSize:11.5, padding:"8px 12px", borderRadius:6, cursor:"pointer", textAlign:"left",
                background: selectedId===r.id ? C_FIXED_DARK : C.cream, color: selectedId===r.id ? C.cream : C.inkMid,
                border:`1px solid ${selectedId===r.id ? C_FIXED_DARK : C.border}`,
              }}>
                <div style={{ fontWeight:600 }}>{r.batch || "No batch #"} {r.agtron ? "●" : ""}{r.cupping ? "☕" : ""}</div>
                <div style={{ fontSize:10, opacity:0.75 }}>{r.origin || "—"} · {dmy(r.date) || ""}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", padding:"10px 16px", background:C.cream3, borderRadius:6, marginBottom:16, fontFamily:fontSans }}>
            <div style={{ fontFamily:fontSerif, fontSize:16, color:C.ink }}>{selected.origin || "Untitled"} — {selected.batch || "no batch #"}</div>
            <div style={{ fontSize:10, color:C.inkFaint, textTransform:"uppercase", letterSpacing:"0.06em" }}>{dmy(selected.date) || ""}</div>
          </div>

          {/* ── CM-200 COLOUR READING ── */}
          <div style={{ border:`1px solid ${C.border}`, borderRadius:8, padding:"14px 16px", marginBottom:16, background:C.cream }}>
            <div style={{ fontFamily:fontSans, fontSize:10, fontWeight:700, color:C.amber, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:12 }}>● CM-200 Reading</div>
            <div style={{ display:"flex", gap:20, marginBottom:14 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint, marginBottom:4 }}>Whole Bean Agtron</div>
                <input style={{ ...s.input, fontSize:18, fontFamily:"monospace" }} type="number" placeholder="e.g. 61" value={agtron} onChange={e => setAgtron(e.target.value)} />
                <AgtronStrip value={agtron} />
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint, marginBottom:4 }}>Ground Agtron</div>
                <input style={{ ...s.input, fontSize:18, fontFamily:"monospace" }} type="number" placeholder="e.g. 56" value={agtronGround} onChange={e => setAgtronGround(e.target.value)} />
                <AgtronStrip value={agtronGround} />
              </div>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", fontFamily:fontSans, fontSize:9, color:C.inkFaint, marginBottom:14 }}>
              <span>5 · Very Dark</span><span>140 · Very Light</span>
            </div>
            <div style={{ display:"flex", gap:20, paddingTop:12, borderTop:`1px solid ${C.borderSoft}` }}>
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint, marginBottom:4 }}>Δ Whole/Ground</div>
                <div style={{ display:"flex", alignItems:"baseline", gap:8 }}>
                  <span style={{ fontFamily:fontSerif, fontSize:22, color:C.ink }}>{delta ?? "—"}</span>
                  {delta !== null && <span style={{ fontFamily:fontSans, fontSize:9, fontWeight:700, padding:"2px 8px", borderRadius:10, background: deltaGood?"#E8F0E4":"#FCEFE7", color: deltaGood?C.green:C.amber }}>{deltaGood?"Tight":"Wide"}</span>}
                </div>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint, marginBottom:4 }}>Uniformity %</div>
                <div style={{ display:"flex", alignItems:"baseline", gap:8 }}>
                  <input style={{ ...s.input, fontSize:18, fontFamily:"monospace", width:70 }} type="number" placeholder="—" value={agtronUniformity} onChange={e => setAgtronUniformity(e.target.value)} />
                  {!isNaN(uni) && <span style={{ fontFamily:fontSans, fontSize:9, fontWeight:700, padding:"2px 8px", borderRadius:10, background: uniGood?"#E8F0E4":"#FCEFE7", color: uniGood?C.green:C.amber }}>{uniGood?"Even":"Check"}</span>}
                </div>
                <div style={{ fontFamily:fontSans, fontSize:9, color:C.inkFaint, marginTop:3 }}>Read straight off the CM-200 — not derived from Δ.</div>
              </div>
            </div>
            {pred && !isNaN(wb) && (
              <div style={{ marginTop:14, padding:"10px 12px", background:C.cream2, borderRadius:6, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8 }}>
                <div style={{ fontFamily:fontSans, fontSize:11.5, color:C.inkMid }}>
                  Cultivar model predicted <strong>Agtron {pred.predicted}</strong> for {selected.cultivar || "this cultivar"} at {selected.eor || "—"}°C EoR{pred.calibrated ? ` (calibrated from ${pred.calibrationN} logged readings)` : ""}
                </div>
                <div style={{ fontFamily:"monospace", fontSize:10.5, fontWeight:700, color: Math.abs(wb - pred.predicted) <= 4 ? C.green : C.amber }}>
                  ACTUAL {wb > pred.predicted ? "+" : ""}{Math.round((wb - pred.predicted)*10)/10} · {Math.abs(wb - pred.predicted) <= 4 ? "WITHIN RANGE" : "OUTSIDE RANGE"}
                </div>
              </div>
            )}
          </div>

          <FlavourWheelRef />

          {/* ── CUPPING FORM ── */}
          <div style={{border:`1px solid ${C.border}`,borderRadius:8,marginBottom:16,overflow:"hidden"}}>
            <div onClick={()=>setShowCupping(v=>!v)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",background:C.cream3,cursor:"pointer",userSelect:"none"}}>
              <div style={{fontFamily:fontSans,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:C.inkLight}}>
                {cuppingMode==="cva" ? "CVA Affective Assessment" : "SCA Cupping Score (classic)"}
              </div>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                {showCupping && (cuppingMode==="cva"
                  ? <div style={{fontFamily:"Georgia,serif",fontSize:16,color:C.amber,fontWeight:700}}>{cvaAvg()}/9</div>
                  : <div style={{fontFamily:"Georgia,serif",fontSize:16,color:C.amber,fontWeight:700}}>{cupTotal()}</div>
                )}
                <div style={{color:C.inkFaint,fontSize:12}}>{showCupping?"▲":"▼"}</div>
              </div>
            </div>
            {showCupping && (
              <div style={{padding:"14px"}}>
                <div style={{display:"flex",gap:0,marginBottom:14,border:`1px solid ${C.border}`,borderRadius:6,overflow:"hidden",width:"fit-content"}}>
                  {[["classic","Classic (2004)"],["cva","CVA Affective (2024)"]].map(([m,label])=>(
                    <button key={m} onClick={()=>setCuppingMode(m)} style={{padding:"6px 14px",fontFamily:fontSans,fontSize:11,fontWeight:cuppingMode===m?700:400,background:cuppingMode===m?C.ink:"transparent",color:cuppingMode===m?C.cream:C.inkMid,border:"none",cursor:"pointer"}}>
                      {label}
                    </button>
                  ))}
                </div>
                {cuppingMode === "classic" ? (
                  <>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
                      {SCA_ATTRS.map(attr=>(
                        <div key={attr}>
                          <div style={{fontFamily:fontSans,fontSize:9,color:C.inkFaint,marginBottom:2}}>{attr}</div>
                          <input style={{...s.input,fontSize:12}} type="number" min="6" max="10" step="0.25" value={cup[attr]} onChange={e=>setCup(c=>({...c,[attr]:e.target.value}))} />
                        </div>
                      ))}
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12,paddingTop:10,borderTop:`1px solid ${C.border}`}}>
                      <div>
                        <div style={{fontFamily:fontSans,fontSize:9,color:C.inkFaint,marginBottom:2}}>Taints (×2)</div>
                        <input style={{...s.input,fontSize:12}} type="number" min="0" step="1" value={defects.taints} onChange={e=>setDefects(d=>({...d,taints:parseInt(e.target.value)||0}))} />
                      </div>
                      <div>
                        <div style={{fontFamily:fontSans,fontSize:9,color:C.inkFaint,marginBottom:2}}>Faults (×4)</div>
                        <input style={{...s.input,fontSize:12}} type="number" min="0" step="1" value={defects.faults} onChange={e=>setDefects(d=>({...d,faults:parseInt(e.target.value)||0}))} />
                      </div>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingTop:10,borderTop:`1px solid ${C.border}`}}>
                      <div style={{fontFamily:fontSans,fontSize:10,color:C.inkLight}}>Sum {SCA_ATTRS.reduce((a,k)=>a+(parseFloat(cup[k])||0),0).toFixed(2)} − Defects {(defects.taints*2)+(defects.faults*4)}</div>
                      <div style={{fontFamily:"Georgia,serif",fontSize:20,color:C.amber,fontWeight:700}}>{cupTotal()} <span style={{fontSize:11,color:C.inkLight,fontFamily:fontSans}}>/100</span></div>
                    </div>
                    <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,marginTop:10,lineHeight:1.5}}>
                      SCA retired this 2004 form in Nov 2024 in favour of the Coffee Value Assessment (CVA). Kept here since many roasters still run it alongside CVA during the transition.
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,marginBottom:12,lineHeight:1.5}}>
                      SCA-104 (2024) — a 9-point hedonic "liking" scale per attribute. By design there's no single total score — the point is a fuller quality picture, not one number.
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
                      {CVA_ATTRS.map(attr=>(
                        <div key={attr}>
                          <div style={{fontFamily:fontSans,fontSize:9,color:C.inkFaint,marginBottom:2}}>{attr}</div>
                          <input style={{...s.input,fontSize:12}} type="number" min="1" max="9" step="1" value={cva[attr]} onChange={e=>setCva(c=>({...c,[attr]:e.target.value}))} />
                        </div>
                      ))}
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12,paddingTop:10,borderTop:`1px solid ${C.border}`}}>
                      <div>
                        <div style={{fontFamily:fontSans,fontSize:9,color:C.inkFaint,marginBottom:2}}>Uniform cups (of 5)</div>
                        <input style={{...s.input,fontSize:12}} type="number" min="0" max="5" step="1" value={cvaUniformCups} onChange={e=>setCvaUniformCups(parseInt(e.target.value)||0)} />
                      </div>
                      <div>
                        <div style={{fontFamily:fontSans,fontSize:9,color:C.inkFaint,marginBottom:2}}>Clean cups (of 5)</div>
                        <input style={{...s.input,fontSize:12}} type="number" min="0" max="5" step="1" value={cvaCleanCups} onChange={e=>setCvaCleanCups(parseInt(e.target.value)||0)} />
                      </div>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingTop:10,borderTop:`1px solid ${C.border}`}}>
                      <div style={{fontFamily:fontSans,fontSize:10,color:C.inkLight}}>Average liking across {CVA_ATTRS.length} attributes</div>
                      <div style={{fontFamily:"Georgia,serif",fontSize:20,color:C.amber,fontWeight:700}}>{cvaAvg()} <span style={{fontSize:11,color:C.inkLight,fontFamily:fontSans}}>/9</span></div>
                    </div>
                    <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,marginTop:10,lineHeight:1.5}}>
                      Informal summary only — not an official SCA total. For certified scoring use the SCA's own CVA calculator.
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <TDSCalculator recipe={selected.origin} />

          <button style={{ ...s.btnPrimary, background: saved ? C.green : C.ink }} onClick={saveQC}>{saved ? "✓ Saved" : "Save QC data"}</button>
        </>
      )}
    </div>
  );
}

// ── Shared inventory sync for a logged/edited roast ──────────────────────────
// Used by both the Batch Log's manual edit (History) and Roast Mode's save,
// so a roast's green-kg impact on Inventory is applied exactly once no matter
// which screen touched it, and re-saving/re-editing the same record adjusts
// the delta rather than double-counting. invAppliedKg on the record tracks
// how much has been applied so far.
function syncInventoryForRoast({ record, newGreenKg, origin, inventory, setInventory }) {
  if (!inventory || !setInventory) return { ...record, invAppliedKg: parseFloat(newGreenKg) || 0 };
  const prevApplied = parseFloat(record.invAppliedKg) || 0;
  const nextGreen = parseFloat(newGreenKg) || 0;
  const delta = Math.round((nextGreen - prevApplied) * 100) / 100;
  if (!origin || Math.abs(delta) < 0.01) return { ...record, invAppliedKg: nextGreen };
  // A saved blend (matched by its exact name) is split across its component
  // coffees by their percentages — each one comes off Green stock by its share.
  let savedBlends = [];
  try { savedBlends = JSON.parse(localStorage.getItem("fdc_blends") || "[]"); } catch { savedBlends = []; }
  const blend = savedBlends.find(b => (b.name || "").trim().toLowerCase() === String(origin).trim().toLowerCase());
  const comps = blend && Array.isArray(blend.components) ? blend.components.filter(c => c.origin && parseFloat(c.pct) > 0) : [];
  const totalPct = comps.reduce((a, c) => a + parseFloat(c.pct), 0);
  if (blend && comps.length && totalPct > 0) {
    let updatedInv = inventory;
    comps.forEach(c => {
      const item = updatedInv.find(i => coffeeNamesMatch(i.name || i.origin, c.origin));
      if (!item) return;
      const share = Math.round(delta * (parseFloat(c.pct) / totalPct) * 100) / 100;
      const bw = parseFloat(item.bagWeight) || 0, bg = parseFloat(item.bags) || 0;
      const left = Math.max(0, bw * bg - share);
      updatedInv = updatedInv.map(i => i.id === item.id
        ? { ...i, bags: bw > 0 ? Math.round(left / bw * 100) / 100 : 0, totalRoastedKg: Math.round(((parseFloat(i.totalRoastedKg) || 0) + share) * 10) / 10 }
        : i);
    });
    setInventory(updatedInv); saveInventory(updatedInv);
    return { ...record, invAppliedKg: nextGreen };
  }
  const matched = inventory.find(i => coffeeNamesMatch(i.name || i.origin, origin));
  if (!matched) return { ...record, invAppliedKg: nextGreen };
  const bagWt = parseFloat(matched.bagWeight) || 0, bags = parseFloat(matched.bags) || 0;
  const remaining = Math.max(0, bagWt * bags - delta);
  const updatedInv = inventory.map(i => i.id === matched.id
    ? { ...i, bags: bagWt > 0 ? Math.round(remaining / bagWt * 100) / 100 : 0, totalRoastedKg: Math.round(((parseFloat(i.totalRoastedKg) || 0) + delta) * 10) / 10 }
    : i);
  setInventory(updatedInv); saveInventory(updatedInv);
  return { ...record, invAppliedKg: nextGreen };
}

function History({ inventory, setInventory }) {
  const [history, setHistory] = useState(() => JSON.parse(localStorage.getItem("fdc_history") || "[]"));
  const [expanded, setExpanded] = useState({});
  const [expandedOrigin, setExpandedOrigin] = useState(null);
  const thisMonth = localIsoDate().slice(0, 7);

  const losses = history.map(r => parseFloat(r.loss)).filter(v => !isNaN(v));
  const scores = history.map(r => parseFloat(r.score)).filter(v => !isNaN(v));
  const avg = arr => arr.length ? (arr.reduce((a, b) => a + b, 0) / arr.length).toFixed(1) : "—";

  // Totals only count roasts that were weighed — a false start or unfinished
  // roast has green in but nothing out, which would wreck every loss figure.
  const weighed = history.filter(r => parseFloat(r.roasted) > 0);
  const totalGreen = weighed.reduce((a, r) => a + (parseFloat(r.green) || 0), 0);
  const totalRoasted = weighed.reduce((a, r) => a + (parseFloat(r.roasted) || 0), 0);

  // Stock summary: roasted output grouped by origin — keeps each contributing
  // batch (with its own date) so the rollup row can be expanded into the
  // actual roasts behind it, not just a flat total.
  const stockByOrigin = weighed.reduce((acc, r) => {
    const key = String(r.origin || "").trim() || "Unknown";
    if (!acc[key]) acc[key] = { green: 0, roasted: 0, batches: 0, records: [] };
    acc[key].green += parseFloat(r.green) || 0;
    acc[key].roasted += parseFloat(r.roasted) || 0;
    acc[key].batches += 1;
    acc[key].records.push(r);
    return acc;
  }, {});

  const currentInventory = (() => { try { return JSON.parse(localStorage.getItem("fdc_inventory") || "[]"); } catch { return []; } })();
  const totalKgFn = item => Math.round(parseFloat(item.bagWeight||0) * parseFloat(item.bags||0) * 10) / 10;

  const clear = () => {
    if (window.confirm("Clear all roast history?")) {
      setHistory([]);
      localStorage.setItem("fdc_history", "[]");
    }
  };

  const toggleExpand = id => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const [editing, setEditing] = useState(null); // id of row being edited
  const [editVals, setEditVals] = useState({});

  // ── SCA CUPPING (used in edit mode) ──
  // Classic 2004 form — officially retired by SCA Nov 2024, superseded by CVA (SCA-102/103/104),
  // now in active global use as of 2026. Both kept here: many roasters still run classic
  // alongside CVA during the transition, per SCA's own guidance.

  // The roast events that can be edited — older Artisan imports used short
  // labels, so those are read as the same events.
  const EDIT_EVENTS = [["Charge", "Charge"], ["Turning point", "Turning point"], ["Yellow point", "Yellowing"], ["First crack", "First crack"], ["Second crack", "Second crack"], ["Drop", "Drop"]];
  const EVENT_ALIASES = { "TP": "Turning point", "Dry End": "Yellow point", "FCs": "First crack", "SCs": "Second crack", "DROP": "Drop", "CHARGE": "Charge" };
  const eventNameOf = ev => { const nm = ev.name || ev.label || ""; return EVENT_ALIASES[nm] || nm; };
  const parseMmSs = str => { const m2 = String(str || "").trim().match(/^(\d{1,2}):(\d{2})$/); return m2 ? parseInt(m2[1]) * 60 + parseInt(m2[2]) : null; };
  const startEdit = (r) => {
    setEditing(r.id);
    const evs = {};
    (r.events || []).forEach(ev => { const nm = eventNameOf(ev); if (EDIT_EVENTS.some(([k]) => k === nm) && !evs[nm]) evs[nm] = { time: ev.time || "", temp: ev.temp != null ? String(ev.temp) : "" }; });
    setEditVals({
      green: r.green||"", roasted: r.roasted||"", batch: r.batch||"", origin: r.origin||"", score: r.score||"", notes: r.notes||"",
      date: r.date || localIsoDate(),
      process: r.process || "", cultivar: r.cultivar || "",
      agtron: r.agtron != null ? String(r.agtron) : "", agtronGround: r.agtronGround != null ? String(r.agtronGround) : "",
      events: evs,
    });
  };

  const addManualRoast = () => {
    const r = { id: Date.now(), batch:"", origin:"", green:"", roasted:"", loss:"—", score:"", agtron:"", notes:"", events:[], date: localIsoDate() };
    const updated = [r, ...history];
    setHistory(updated);
    localStorage.setItem("fdc_history", JSON.stringify(updated));
    setExpanded(e => ({ ...e, [r.id]: true }));
    startEdit(r);
  };

  const [importError, setImportError] = useState(null);
  const importArtisanFile = (file) => {
    setImportError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = parseArtisanCSV(e.target.result);
        const totalMin = parsed.events.drop ? (() => {
          const [m, sec] = parsed.events.drop.split(":").map(Number);
          return Math.round((m + sec / 60) * 10) / 10;
        })() : null;
        const charge = parsed.chargeSettledBT;
        const r = {
          id: Date.now(), batch:"", origin:"", cultivar:"",
          green:"", roasted:"", loss:"—", score:"", agtron:"", agtronPredicted:null,
          eor: parsed.events.dropBT || "",
          notes: `Imported from Artisan (${parsed.meta.Date || "date unknown"}). Charge: marker ${parsed.chargeMarkerBT}°C / settled ${parsed.chargeSettledBT}°C — used ${charge}°C.`,
          events: [
            parsed.events.tp && { label: "TP", time: parsed.events.tp, temp: parsed.events.tpBT },
            parsed.events.dryEnd && { label: "Dry End", time: parsed.events.dryEnd, temp: parsed.events.dryEndBT },
            parsed.events.fcs && { label: "FCs", time: parsed.events.fcs, temp: parsed.events.fcsBT },
            parsed.events.drop && { label: "Drop", time: parsed.events.drop, temp: parsed.events.dropBT },
          ].filter(Boolean),
          curve: parsed.curve,
          date: parsed.meta.Date ? parsed.meta.Date.split(".").reverse().join("-") : localIsoDate(),
        };
        const updated = [r, ...history];
        setHistory(updated);
        localStorage.setItem("fdc_history", JSON.stringify(updated));
        setExpanded(ex => ({ ...ex, [r.id]: true }));
        startEdit(r);
      } catch (err) {
        setImportError(err.message || "Couldn't read that file — make sure it's Artisan's CSV export (File → Export → CSV).");
      }
    };
    reader.onerror = () => setImportError("Couldn't read the file. Try again.");
    reader.readAsText(file);
  };

  const saveEdit = (id) => {
    const orig = history.find(r => r.id === id);
    const { events: evEdits, ...plainVals } = editVals;
    // Rebuild the event list: edited standard events replace their originals
    // (keeping anything else stored on them), cleared ones are removed, and
    // any other events on the roast are kept exactly as they were.
    const origEvents = orig.events || [];
    const std = new Set(EDIT_EVENTS.map(([k]) => k));
    const kept = origEvents.filter(ev => !std.has(eventNameOf(ev)));
    const rebuilt = EDIT_EVENTS.map(([nm]) => {
      const ed = (evEdits || {})[nm];
      const secs = ed ? parseMmSs(ed.time) : null;
      if (secs == null) return null;
      const before = origEvents.find(ev => eventNameOf(ev) === nm) || {};
      const temp = ed.temp !== "" && !isNaN(parseFloat(ed.temp)) ? parseFloat(ed.temp) : null;
      const time = `${String(Math.floor(secs / 60)).padStart(2, "0")}:${String(secs % 60).padStart(2, "0")}`;
      const changed = before.time !== time || (before.temp ?? null) !== temp;
      const { auto, label, ...rest } = before;
      return { ...rest, ...(changed ? { edited: true } : { ...(auto ? { auto } : {}) }), name: nm, time, temp };
    }).filter(Boolean);
    let events = [...rebuilt, ...kept].sort((a, b) => (parseMmSs(a.time) ?? 0) - (parseMmSs(b.time) ?? 0));
    // Charge moved later than 0:00 (the clock started early — e.g. a missed or
    // mis-detected charge)? Offer to make it the new 0:00: every event and the
    // logged curve shift to match, and readings from before the charge go.
    let curve = orig.curve;
    const chargeEv = rebuilt.find(ev => ev.name === "Charge");
    const shiftS = chargeEv ? parseMmSs(chargeEv.time) : 0;
    if (shiftS > 0 && window.confirm(`Charge is set at ${chargeEv.time}.\n\nMake that 0:00 and move everything else to match?\n\nOK — re-time the roast from the charge (readings from before it are removed)\nCancel — keep the times exactly as typed`)) {
      const mmss = x => `${String(Math.floor(x / 60)).padStart(2, "0")}:${String(x % 60).padStart(2, "0")}`;
      events = events
        .map(ev => { const t0 = parseMmSs(ev.time); return t0 == null ? ev : { ...ev, time: mmss(t0 - shiftS) }; })
        .filter(ev => (parseMmSs(ev.time) ?? 0) >= 0 && !String(ev.time).startsWith("-"));
      if (Array.isArray(curve)) curve = curve.filter(p => p.t * 60 >= shiftS - 0.5).map(p => ({ ...p, t: Math.max(0, Math.round((p.t - shiftS / 60) * 10000) / 10000) }));
    }
    const dropEv = events.find(ev => eventNameOf(ev) === "Drop");
    let merged = {
      ...orig, ...plainVals, events, ...(curve ? { curve } : {}),
      ...(dropEv && dropEv.temp != null ? { eor: dropEv.temp } : {}),
      loss: plainVals.roasted && plainVals.green ? (((parseFloat(plainVals.green) - parseFloat(plainVals.roasted)) / parseFloat(plainVals.green)) * 100).toFixed(1) + "%" : orig.loss,
    };
    // Reflect this batch's green-kg usage in Inventory — whether it's a fresh
    // manual entry or an edit to an existing figure, only the change since
    // last save is applied (see syncInventoryForRoast).
    merged = syncInventoryForRoast({ record: merged, newGreenKg: plainVals.green, origin: plainVals.origin || orig.origin, inventory, setInventory });
    const updated = history.map(r => r.id === id ? merged : r);
    setHistory(updated);
    localStorage.setItem("fdc_history", JSON.stringify(updated));
    setEditing(null);
  };

  // Puts a deleted roast's green coffee back into Green stock — only for
  // roasts that actually took coffee off stock when they were saved.
  const giveBackGreen = (r) => {
    const kg = parseFloat(r.invAppliedKg) || 0;
    if (!(kg > 0) || !setInventory || !inventory) return false;
    const item = inventory.find(i => coffeeNamesMatch(i.name || i.origin || "", r.origin || ""));
    if (!item) return false;
    const bw = parseFloat(item.bagWeight) || 0;
    const nowKg = bw * (parseFloat(item.bags) || 0);
    const upInv = inventory.map(i => i.id === item.id
      ? { ...i, bags: bw > 0 ? Math.round((nowKg + kg) / bw * 1000) / 1000 : i.bags, totalRoastedKg: Math.max(0, Math.round(((parseFloat(i.totalRoastedKg) || 0) - kg) * 10) / 10) }
      : i);
    setInventory(upInv); saveInventory(upInv);
    return true;
  };
  const deleteRow = (id) => {
    const r = history.find(x => x.id === id);
    if (!r) return;
    const label = `${r.batch || "This roast"}${r.origin ? " — " + r.origin : ""}${r.date ? " (" + dmy(r.date) + ")" : ""}`;
    if (!window.confirm(`Delete ${label} from Roast history?`)) return;
    const took = parseFloat(r.invAppliedKg) || 0;
    if (took > 0 && window.confirm(`When it was saved, ${took} kg of green came off ${r.origin || "Green stock"}.\n\nPut ${took} kg back into Green stock?\n\nOK — put it back (the roast didn't really happen)\nCancel — leave stock as it is`)) {
      giveBackGreen(r);
    }
    const updated = history.filter(x => x.id !== id);
    setHistory(updated);
    localStorage.setItem("fdc_history", JSON.stringify(updated));
    setExpanded(e => ({ ...e, [id]: false }));
    setEditing(null);
  };
  // ── Storage: browsers give the Hub roughly 5 MB on this computer, and roast
  // curves are by far the biggest thing in it. Show how full it is, and offer
  // to slim OLD curves (one reading every 5 s instead of every second) —
  // only when asked, and never touching events, weights or results.
  const STORAGE_LIMIT_CHARS = 5000000;
  const storageUsed = (() => {
    let total = 0;
    try { for (let i = 0; i < localStorage.length; i++) { const k = localStorage.key(i); total += k.length + (localStorage.getItem(k) || "").length; } } catch { /* unreadable — show nothing */ }
    return total;
  })();
  const storagePct = Math.min(100, Math.round(storageUsed / STORAGE_LIMIT_CHARS * 100));
  const slimCutoff = localIsoDate(new Date(Date.now() - 60 * 86400000));
  const slimmable = history.filter(r => Array.isArray(r.curve) && r.curve.length > 200 && !r.curveSlimmed && (r.date || "9999") < slimCutoff);
  const slimCurve = (curve) => {
    const out = []; let lastBucket = -1;
    curve.forEach((p, i) => {
      const bucket = Math.floor((p.t || 0) * 12); // 5-second buckets
      if (i === 0 || i === curve.length - 1 || bucket !== lastBucket) { out.push(p); lastBucket = bucket; }
    });
    return out;
  };
  const slimOldCurves = () => {
    if (!slimmable.length) return;
    if (!window.confirm(`Slim ${slimmable.length} roast curve${slimmable.length === 1 ? "" : "s"} older than 60 days?\n\nEach keeps one reading every 5 seconds instead of every second — the charts look the same. Events, weights, scores, notes and results are not touched.\n\nTip: click ⤓ Backup first if you want a copy with every reading.`)) return;
    const ids = new Set(slimmable.map(r => r.id));
    const updated = history.map(r => ids.has(r.id) ? { ...r, curve: slimCurve(r.curve), curveSlimmed: true } : r);
    try {
      localStorage.setItem("fdc_history", JSON.stringify(updated));
      setHistory(updated);
    } catch { window.alert("Couldn't save the slimmed history — storage may already be completely full. Remove a few unneeded roasts first."); }
  };

  // ── Tidy roast records: clears two kinds of leftover mess that make a roast
  // look wrong — readings from another recording mixed into the start of a
  // curve (the clock jumps back to 0:00 partway through), and the same event
  // logged twice within seconds (e.g. three Drop taps). Nothing else is
  // touched, and nothing changes until you confirm.
  const planTidy = (r) => {
    const notes = [];
    let curve = r.curve;
    let events = r.events;
    if (Array.isArray(curve) && curve.length > 1) {
      const starts = [0];
      for (let i = 1; i < curve.length; i++) {
        if ((curve[i].t ?? 0) < (curve[i - 1].t ?? 0) - 0.25) starts.push(i);
      }
      const lastStart = starts[starts.length - 1];
      if (starts.length > 1 && curve.length - lastStart >= 100) {
        notes.push(`${lastStart} stray reading${lastStart === 1 ? "" : "s"} from another recording removed from the start of the curve`);
        curve = curve.slice(lastStart);
      }
    }
    if (Array.isArray(events) && events.length > 1) {
      const secOf = (t) => { const m = /^(\d+):(\d{2})$/.exec(String(t || "")); return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null; };
      const nameOf = (ev) => ev.name || ev.label;
      const kept = [];
      let repeats = 0;
      events.forEach(ev => {
        const s = secOf(ev.time);
        const repeat = s != null && kept.some(k => nameOf(k) === nameOf(ev) && secOf(k.time) != null && Math.abs(secOf(k.time) - s) <= 30);
        if (repeat) repeats++; else kept.push(ev);
      });
      if (repeats > 0) {
        notes.push(`${repeats} repeated event tap${repeats === 1 ? "" : "s"} removed (the first is kept)`);
        events = kept;
      }
    }
    if (!notes.length) return null;
    return { id: r.id, batch: r.batch, notes, record: { ...r, ...(curve !== r.curve ? { curve } : {}), ...(events !== r.events ? { events } : {}) } };
  };
  const tidyPlans = history.map(planTidy).filter(Boolean);
  const tidyRecords = () => {
    if (!tidyPlans.length) return;
    const list = tidyPlans.map(p => `• ${p.batch || "Roast"}: ${p.notes.join("; ")}`).join("\n");
    if (!window.confirm(`Tidy ${tidyPlans.length} roast record${tidyPlans.length === 1 ? "" : "s"}?\n\n${list}\n\nWeights, results, scores and notes are not touched.\n\nTip: click ⤓ Backup first if you want a copy of how they are now.`)) return;
    const byId = new Map(tidyPlans.map(p => [p.id, p.record]));
    const updated = history.map(r => byId.get(r.id) || r);
    try {
      localStorage.setItem("fdc_history", JSON.stringify(updated));
      setHistory(updated);
    } catch { window.alert("Couldn't save the tidied history — storage may be full. Nothing was changed."); }
  };

  // ── Tidy coffee names: the same coffee logged under different names splits
  // its totals, and stops the Hub learning from its past roasts (first-crack
  // temperature, reference roasts, last settings). Suggests the matching
  // Green stock coffee for each name; nothing changes until Merge.
  const [showNameTidy, setShowNameTidy] = useState(false);
  const stockNames = [...new Set((inventory || []).map(i => String(i.name || i.origin || "").trim()).filter(Boolean))];
  const historyNames = (() => {
    const counts = {};
    history.forEach(r => { const nm = String(r.origin || "").trim(); if (nm) counts[nm] = (counts[nm] || 0) + 1; });
    return Object.entries(counts).map(([name, count]) => ({ name, count })).sort((a, b) => a.name.localeCompare(b.name));
  })();
  const suggestFor = (name) => {
    if (stockNames.some(sn => sameCoffee(sn, name))) return "";      // already the Green stock name
    const tok = new Set(coffeeNameTokens(name));
    const scored = stockNames
      .filter(sn => coffeeNamesMatch(sn, name))
      .map(sn => { const t2 = coffeeNameTokens(sn); let o = 0; t2.forEach(w => { if (tok.has(w)) o++; }); return { sn, o }; })
      .sort((a, b) => b.o - a.o);
    if (!scored.length) return "";
    if (scored.length > 1 && scored[0].o === scored[1].o) return "";  // too close to call — you choose
    return scored[0].sn;
  };
  const [nameTargets, setNameTargets] = useState({});
  const openNameTidy = () => {
    const t = {};
    historyNames.forEach(({ name }) => { t[name] = suggestFor(name); });
    setNameTargets(t);
    setShowNameTidy(true);
  };
  const plannedMerges = historyNames.filter(({ name }) => nameTargets[name] && !sameCoffee(nameTargets[name], name));
  const mergeNames = () => {
    if (!plannedMerges.length) return;
    const lines = plannedMerges.map(({ name, count }) => `• ${name} (${count} roast${count === 1 ? "" : "s"}) → ${nameTargets[name]}`).join("\n");
    if (!window.confirm(`Rename these in Roast history?\n\n${lines}\n\nOnly the coffee name changes — stock, weights, events and results stay exactly as they are. Packing batches keep the name they were packed under.`)) return;
    const map = {};
    plannedMerges.forEach(({ name }) => { map[name.toLowerCase()] = nameTargets[name]; });
    const renamed = (nm) => { const k = String(nm || "").trim().toLowerCase(); return map[k] !== undefined ? map[k] : nm; };
    const updated = history.map(r => { const n2 = renamed(r.origin); return n2 !== r.origin ? { ...r, origin: n2 } : r; });
    try {
      localStorage.setItem("fdc_history", JSON.stringify(updated));
      setHistory(updated);
      // Orders waiting to be roasted follow too, so "Roast this" finds the right past roasts.
      try {
        const orders = loadOrders();
        if (orders.some(o => map[String(o.origin || "").trim().toLowerCase()] !== undefined)) saveOrders(orders.map(o => { const n2 = renamed(o.origin); return n2 !== o.origin ? { ...o, origin: n2 } : o; }));
      } catch { /* no orders — nothing to do */ }
      setShowNameTidy(false);
    } catch { window.alert("Couldn't save the renamed history — storage may be full."); }
  };

  // Roasts that were never weighed — false starts, test runs, or roasts left
  // unfinished. They never took coffee off stock, so removing them is safe.
  const unweighed = history.filter(r => !(parseFloat(r.roasted) > 0) && !(parseFloat(r.invAppliedKg) > 0));
  const removeUnweighed = () => {
    if (!unweighed.length) return;
    const list = unweighed.slice(0, 12).map(r => `• ${r.batch || "—"} ${r.origin || ""} ${dmy(r.date) || ""} (${(r.curve || []).length} readings)`).join("\n");
    if (!window.confirm(`Remove ${unweighed.length} roast${unweighed.length === 1 ? "" : "s"} that ${unweighed.length === 1 ? "was" : "were"} never weighed?\n\n${list}${unweighed.length > 12 ? "\n…" : ""}\n\nThese never took coffee off Green stock.`)) return;
    const gone = new Set(unweighed.map(r => r.id));
    const updated = history.filter(r => !gone.has(r.id));
    setHistory(updated);
    localStorage.setItem("fdc_history", JSON.stringify(updated));
    setEditing(null);
  };

  // ── PDF EXPORT — clean print page ──
  const exportPDF = () => {
    const dateStr = todayDmy();

    const r2 = (label, value, bold) =>
      `<tr><td class="lbl">${label}</td><td class="val${bold?" bld":""}">${value}</td></tr>`;

    const sec = (title) =>
      `<tr><td colspan="2" class="sec">${title}</td></tr>`;

    // Green stock remaining from inventory
    const totalGreenStock = currentInventory.reduce((a, i) => a + totalKgFn(i), 0);
    const totalStockValue = currentInventory.reduce((a, i) => a + (totalKgFn(i) * (parseFloat(i.costPerKg)||0)), 0);
    const greenStockRows = currentInventory.map(item => {
      const kg = totalKgFn(item);
      const val = item.costPerKg ? ` · €${Math.round(kg * parseFloat(item.costPerKg))}` : "";
      return r2(item.name || "—", kg + " kg" + val, false);
    }).join("");

    const stockRows = Object.entries(stockByOrigin).map(([origin, data]) => {
      const lossAvg = data.green > 0 ? (((data.green - data.roasted) / data.green) * 100).toFixed(1) + "%" : "—";
      return `
        <tr class="origin-row">
          <td class="lbl" style="padding-top:10px;font-weight:600;color:#1C1814">${origin}</td>
          <td class="val" style="padding-top:10px"></td>
        </tr>
        ${r2("Batches", data.batches)}
        ${r2("Green in", data.green.toFixed(1) + " kg")}
        ${r2("Roasted out", data.roasted.toFixed(1) + " kg", true)}
        ${r2("Average loss", lossAvg)}
      `;
    }).join("");

    const batchRows = history.map(r => `
      <tr>
        <td class="lbl" style="padding-top:10px;font-weight:600;color:#1C1814">${r.batch || "—"} &nbsp;<span style="font-weight:400;color:#7A6E63">${dmy(r.date) || ""}</span></td>
        <td class="val" style="padding-top:10px;color:#7A6E63;font-size:11px">${r.origin || "—"}</td>
      </tr>
      ${r2("Green in", r.green ? r.green + " kg" : "—")}
      ${r2("Roasted out", r.roasted ? r.roasted + " kg" : "—", true)}
      ${r2("Weight loss", r.loss || "—")}
      ${r.score ? r2("Cupping score", r.score) : ""}
      ${r.agtron ? r2("Agtron (Whole Bean)", r.agtron) : ""}
      ${r.agtronGround ? r2("Agtron (Ground)", r.agtronGround) : ""}
      ${r.agtronUniformity ? r2("Uniformity %", r.agtronUniformity) : ""}
    `).join("");

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>FDC Roast Report — ${dateStr}</title>
<style>
  @page { margin: 18mm 20mm; size: A4 portrait; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, "Helvetica Neue", Arial, sans-serif; font-size: 13px; color: #1C1814; background: #fff; max-width: 680px; padding: 32px 0; }
  h1 { font-size: 24px; font-weight: 700; color: #1C1814; margin-bottom: 5px; }
  .sub { font-size: 12px; color: #7A6E63; padding-bottom: 16px; margin-bottom: 24px; border-bottom: 2px solid #1C1814; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 7px 0; vertical-align: middle; border-bottom: 1px solid #EDE8DE; line-height: 1.3; }
  td.lbl { color: #4A3F35; width: 58%; padding-left: 14px; font-size: 13px; }
  td.val { color: #1C1814; text-align: right; font-size: 13px; }
  td.val.bld { font-weight: 700; }
  td.sec { font-size: 10px; font-weight: 700; color: #8B3A2A; text-transform: uppercase; letter-spacing: 0.1em; padding: 22px 0 7px; border-bottom: 1.5px solid #C17F24; }
  .footer { margin-top: 36px; font-size: 10px; color: #A89E93; border-top: 1px solid #D5CCBE; padding-top: 10px; }
  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; padding: 0; }
  }
</style>
</head>
<body>
<h1>Roast History Report</h1>
<div class="sub">Skelligs Coffee &nbsp;&middot;&nbsp; Loring S70 Peregrine &nbsp;&middot;&nbsp; ${dateStr}</div>

<table>
  ${sec("Summary")}
  ${r2("Total roasts", weighed.length)}
  ${r2("This month", weighed.filter(r => r.date?.startsWith(thisMonth)).length)}
  ${r2("Total green in", totalGreen.toFixed(1) + " kg")}
  ${r2("Total roasted out", totalRoasted.toFixed(1) + " kg", true)}
  ${r2("Average weight loss", avg(losses) + "%")}
  ${avg(scores) !== "—" ? r2("Average cupping score", avg(scores)) : ""}

  ${Object.keys(stockByOrigin).length > 0 ? sec("Roasted output by origin") + stockRows : ""}
  ${currentInventory.length > 0 ? sec("Green stock remaining") + greenStockRows + r2("Total green stock", totalGreenStock.toFixed(1) + " kg", true) + (totalStockValue > 0 ? r2("Estimated stock value", "€" + Math.round(totalStockValue)) : "") : ""}
  ${history.length > 0 ? sec("Batch log") + batchRows : ""}
</table>

<div class="footer">Skelligs Coffee &nbsp;&middot;&nbsp; Generated ${dateStr} &nbsp;&middot;&nbsp; fdc-roasting-hub.pages.dev</div>
</body>
</html>`;

    const win = window.open("", "_blank");
    if (!win) { window.alert("Please allow pop-ups for this site to export the report."); return; }
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 400);
  };

  // ── EXCEL EXPORT ──
  const exportExcel = () => {
    try {
      const esc = v => {
        const s = String(v ?? "");
        return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g,'""')}"` : s;
      };
      const row = cols => cols.map(esc).join(",");
      const blank = () => "";

      const lines = [
        // ── Summary ──
        row(["SKELLIGS COFFEE — ROAST HISTORY REPORT"]),
        row(["Generated", todayDmy()]),
        blank(),
        row(["SUMMARY",""]),
        row(["Total roasts", weighed.length]),
        row(["This month", weighed.filter(r => r.date?.startsWith(thisMonth)).length]),
        row(["Total green in (kg)", totalGreen.toFixed(1)]),
        row(["Total roasted out (kg)", totalRoasted.toFixed(1)]),
        row(["Average weight loss (%)", avg(losses)]),
        row(["Average cupping score", avg(scores)]),
        blank(),
        // ── Green stock ──
        row(["GREEN STOCK REMAINING",""]),
        row(["Coffee","Stock (kg)","Cost/kg (€)","Value (€)"]),
        ...currentInventory.map(item => {
          const kg = totalKgFn(item);
          const val = Math.round(kg * (parseFloat(item.costPerKg)||0));
          return row([item.name, kg, parseFloat(item.costPerKg)||"", val||""]);
        }),
        blank(),
        // ── Roasted by origin ──
        row(["ROASTED OUTPUT BY ORIGIN",""]),
        row(["Origin","Batches","Green in (kg)","Roasted out (kg)","Avg loss (%)"]),
        ...Object.entries(stockByOrigin).map(([origin, data]) => {
          const lossAvg = data.green > 0 ? (((data.green - data.roasted) / data.green) * 100).toFixed(1) : "";
          return row([origin, data.batches, data.green.toFixed(1), data.roasted.toFixed(1), lossAvg]);
        }),
        blank(),
        // ── Batch log ──
        row(["BATCH LOG",""]),
        row(["Date","Batch","Origin","Green in (kg)","Roasted out (kg)","Loss (%)","Score","Agtron WB","Agtron Ground","Uniformity %","Notes"]),
        ...history.map(r => row([r.date||"",r.batch||"",r.origin||"",r.green||"",r.roasted||"",r.loss||"",r.score||"",r.agtron||"",r.agtronGround||"",r.agtronUniformity||"",r.notes||""]))
      ];

      const csv = lines.join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Skelligs-Roast-Report-${localIsoDate()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch(e) {
      window.alert("Export failed: " + e.message);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint }}>Roasts logged via Roast Mode auto-deduct inventory. Cup here once beans have rested.</div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <label style={{...s.btnPrimary, cursor:"pointer", display:"inline-block"}}>
            ⤒ Import from Artisan
            <input type="file" accept=".csv,.txt" style={{display:"none"}} onChange={e => { if (e.target.files[0]) importArtisanFile(e.target.files[0]); e.target.value=""; }} />
          </label>
          <button style={s.btnPrimary} onClick={addManualRoast}>+ Log roast manually</button>
        </div>
      </div>
      {importError && (
        <div style={{fontFamily:fontSans,fontSize:12,color:C.red,background:"#FDEAEA",border:`1px solid ${C.red}`,borderRadius:6,padding:"8px 12px",marginBottom:12}}>
          {importError}
        </div>
      )}

      <RoastDiagnostic />

      {unweighed.length > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", border: `1px solid ${C.amber}`, borderRadius: 8, padding: "12px 16px", marginBottom: 16, background: C.cream2 }}>
          <div style={{ fontFamily: fontSans, fontSize: 13, color: C.ink, lineHeight: 1.5 }}>
            <strong>{unweighed.length} roast{unweighed.length === 1 ? "" : "s"} never weighed</strong> — usually false starts or roasts left unfinished. They're left out of the totals below and didn't take coffee off stock.
          </div>
          <button style={{ ...s.btn, borderColor: C.amber, color: C.amber, fontWeight: 600 }} onClick={removeUnweighed}>Remove {unweighed.length === 1 ? "it" : `all ${unweighed.length}`}</button>
        </div>
      )}

      {storagePct >= 60 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", border: `1px solid ${storagePct >= 85 ? C.red : C.amber}`, borderRadius: 8, padding: "12px 16px", marginBottom: 16, background: C.cream2 }}>
          <div style={{ fontFamily: fontSans, fontSize: 13, color: C.ink, lineHeight: 1.5 }}>
            <strong>Storage on this computer is {storagePct}% full.</strong> Roast curves take the most room. {slimmable.length ? `Slimming the ${slimmable.length} curve${slimmable.length === 1 ? "" : "s"} older than 60 days frees space without losing any results.` : "Download a ⤓ Backup, then remove roasts you no longer need."}
          </div>
          {slimmable.length > 0 && <button style={{ ...s.btn, borderColor: C.amber, color: C.amber, fontWeight: 600 }} onClick={slimOldCurves}>Slim older roast curves</button>}
        </div>
      )}

      {/* ── STAT CARDS ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 10, marginBottom: 20 }}>
        {[
          ["Total roasts", weighed.length],
          ["This month", weighed.filter(r => r.date?.startsWith(thisMonth)).length],
          ["Green in (kg)", totalGreen.toFixed(1)],
          ["Roasted out (kg)", totalRoasted.toFixed(1)],
          ["Avg loss", avg(losses) + "%"],
        ].map(([label, val]) => (
          <div key={label} style={{ background: C.cream2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 16px" }}>
            <div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{label}</div>
            <div style={{ fontFamily: fontSerif, fontSize: 22, color: C.ink }}>{val}</div>
          </div>
        ))}
      </div>

      {/* ── STOCK SUMMARY BY ORIGIN ── */}
      {Object.keys(stockByOrigin).length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 10, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
            <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em" }}>Roasted output by origin</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>{tidyPlans.length > 0 && <button style={{ ...s.btnSm }} onClick={tidyRecords}>Tidy roast records ({tidyPlans.length})</button>}{!showNameTidy && historyNames.length > 1 && <button style={{ ...s.btnSm }} onClick={openNameTidy}>Tidy coffee names</button>}</div>
          </div>
          {showNameTidy && (
            <div style={{ border: `1px solid ${C.amber}`, borderRadius: 8, padding: "14px 16px", marginBottom: 14, background: C.cream2 }}>
              <div style={{ fontFamily: fontSans, fontSize: 13, color: C.ink, lineHeight: 1.5, marginBottom: 10 }}>
                <strong>Tidy coffee names.</strong> The same coffee under different names splits its totals and stops the Hub learning from its past roasts. Suggestions come from your Green stock — check them, change any, then Merge.
              </div>
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 6, overflow: "hidden", marginBottom: 12 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr style={{ background: C.cream3 }}>{["Name in Roast history", "Roasts", "Becomes"].map(h => <th key={h} style={s.thStyle}>{h}</th>)}</tr></thead>
                  <tbody>
                    {historyNames.map(({ name, count }) => {
                      const target = nameTargets[name] || "";
                      const options = [...new Set([...stockNames, ...historyNames.map(h => h.name)])].filter(o => !sameCoffee(o, name)).sort((a, b) => a.localeCompare(b));
                      return (
                        <tr key={name} style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                          <td style={{ ...s.td, fontWeight: 600 }}>{name}</td>
                          <td style={s.td}>{count}</td>
                          <td style={s.td}>
                            <select style={{ ...s.input, fontSize: 12, borderColor: target ? C.amber : C.border }} value={target} onChange={e => setNameTargets(t => ({ ...t, [name]: e.target.value }))}>
                              <option value="">— keep as it is —</option>
                              {options.map(o => <option key={o} value={o}>{o}{stockNames.includes(o) ? " (Green stock)" : ""}</option>)}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <button style={{ ...s.btnPrimary, opacity: plannedMerges.length ? 1 : 0.5 }} disabled={!plannedMerges.length} onClick={mergeNames}>Merge {plannedMerges.length ? `${plannedMerges.length} name${plannedMerges.length === 1 ? "" : "s"}` : ""}</button>
                <button style={s.btn} onClick={() => setShowNameTidy(false)}>Cancel</button>
                <span style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint }}>Only names change — stock, weights, events and results stay as they are.</span>
              </div>
            </div>
          )}
          <div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, marginBottom: 8 }}>Click a row to see the individual batches behind the total. ⚠ marks an origin logged too generically to tell coffees apart — edit that batch's Origin in the log below to fix it.</div>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: C.cream3 }}>
                  {["Origin", "Batches", "Dates", "Green in", "Roasted out", "Avg loss", ""].map(h => (
                    <th key={h} style={s.thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(stockByOrigin).map(([origin, data]) => {
                  // Flags an origin string that's just a bare country name with
                  // nothing distinguishing it (e.g. "Brazil" instead of "Pinhal
                  // — Brazil") — there's no way to recover which actual coffee
                  // that was from the string alone, so this just makes the gap
                  // visible rather than silently guessing.
                  const isGeneric = ORIGINS.some(o => o.toLowerCase() === origin.trim().toLowerCase());
                  const dates = [...new Set(data.records.map(r => r.date).filter(Boolean))].sort((a,b) => new Date(b) - new Date(a));
                  return (
                  <Fragment key={origin}>
                    <tr style={{ borderBottom: `1px solid ${C.borderSoft}`, cursor: "pointer" }} onClick={() => setExpandedOrigin(o => o === origin ? null : origin)}>
                      <td style={{ ...s.td, fontWeight: 600, color: C.amber }}>
                        {origin}{isGeneric && <span title="Logged too generically to tell which coffee this was — edit the batch below to specify." style={{ marginLeft: 6, color: C.red }}>⚠</span>}
                      </td>
                      <td style={s.td}>{data.batches}</td>
                      <td style={{ ...s.td, fontSize: 11, color: C.inkFaint }}>{dates.length ? dates.map(dmy).join(", ") : "—"}</td>
                      <td style={s.td}>{data.green.toFixed(1)} kg</td>
                      <td style={{ ...s.td, fontWeight: 600 }}>{data.roasted.toFixed(1)} kg</td>
                      <td style={{ ...s.td, color: C.inkLight }}>
                        {data.green > 0 ? (((data.green - data.roasted) / data.green) * 100).toFixed(1) + "%" : "—"}
                      </td>
                      <td style={{ ...s.td, color: C.inkFaint, fontSize: 11 }}>{expandedOrigin === origin ? "▲" : "▼"}</td>
                    </tr>
                    {expandedOrigin === origin && (
                      <tr style={{ background: C.cream2 }}>
                        <td colSpan={7} style={{ padding: "10px 16px" }}>
                          <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 6 }}>Weighed roasts only — Edit or Delete any of them here.</div>
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr>
                                {["Batch", "Date", "Green in", "Roasted out", "Loss", ""].map(h => (
                                  <th key={h} style={{ ...s.thStyle, fontSize: 10, background: "transparent" }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {[...data.records].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)).map(r => (
                                <tr key={r.id} style={{ borderTop: `1px solid ${C.borderSoft}` }}>
                                  <td style={{ ...s.td, fontWeight: 600 }}>{r.batch || "—"}</td>
                                  <td style={{ ...s.td, color: C.inkFaint }}>{dmy(r.date) || "—"}</td>
                                  <td style={s.td}>{r.green ? r.green + " kg" : "—"}</td>
                                  <td style={s.td}>{r.roasted ? r.roasted + " kg" : "—"}</td>
                                  <td style={{ ...s.td, color: C.inkLight }}>{r.loss || "—"}</td>
                                  <td style={{ ...s.td, whiteSpace: "nowrap", textAlign: "right" }}>
                                    <button style={{ ...s.btnSm, marginRight: 6 }} onClick={() => {
                                      setExpanded(e => ({ ...e, [r.id]: true })); startEdit(r);
                                      setTimeout(() => { const el = document.getElementById(`roast-row-${r.id}`); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }, 50);
                                    }}>Edit</button>
                                    <button style={{ ...s.btnSm, color: C.red, borderColor: C.red }} onClick={() => deleteRow(r.id)}>Delete</button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── BATCH LOG TABLE ── */}
      <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
        Batch log
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", fontFamily: fontSans, fontSize: 11.5, color: C.inkFaint, marginBottom: 10 }}>
        <span>Click a row to see its curve and details, or use Edit / Delete on the right.</span>
        <span title="How much of the browser's space for the Hub on this computer is in use">
          Storage {(storageUsed / 1e6).toFixed(1)} MB of about 5 MB
          <span style={{ display: "inline-block", width: 60, height: 5, borderRadius: 3, background: C.cream3, marginLeft: 8, verticalAlign: "middle", overflow: "hidden" }}>
            <span style={{ display: "block", height: 5, width: `${storagePct}%`, background: storagePct >= 85 ? C.red : storagePct >= 60 ? C.amber : C.green }}/>
          </span>
          {slimmable.length > 0 && storagePct < 60 && <button style={{ background: "none", border: "none", padding: 0, marginLeft: 8, color: C.inkFaint, textDecoration: "underline", cursor: "pointer", fontSize: 11.5 }} onClick={slimOldCurves}>slim old curves</button>}
        </span>
      </div>
      {history.length === 0 ? (
        <div style={{ fontFamily: fontSans, fontSize: 13, color: C.inkFaint, padding: "1rem 0" }}>No roasts logged yet.</div>
      ) : (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: C.cream3 }}>
                {["Batch", "Origin", "Green in", "Roasted out", "Loss", "Score", "Agtron WB", "Agtron Grd", "Δ", "Date", ""].map(h => (
                  <th key={h} style={s.thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {history.map(r => (
                <Fragment key={r.id}>
                  <tr id={`roast-row-${r.id}`} style={{ borderBottom: `1px solid ${C.borderSoft}`, cursor: "pointer" }}
                    onClick={() => {
                      const willOpen = !expanded[r.id];
                      toggleExpand(r.id);
                      if (willOpen) { startEdit(r); }
                      else { setEditing(null); }
                    }}>
                    <td style={{ ...s.td, fontWeight: 600, color: C.amber }}>{r.batch || "—"}</td>
                    <td style={s.td}>{r.origin || "—"}</td>
                    <td style={{ ...s.td, color: C.inkMid }}>{r.green ? r.green + " kg" : "—"}</td>
                    <td style={{ ...s.td, fontWeight: 600 }}>{parseFloat(r.roasted) > 0 ? r.roasted + " kg" : <span style={{ fontSize: 11, fontWeight: 600, color: C.amber }}>not weighed</span>}</td>
                    <td style={{ ...s.td, color: C.inkLight }}>{r.loss || "—"}</td>
                    <td style={s.td}>{r.score || "—"}</td>
                    <td style={s.td}>
                      {r.agtron ? <span style={{display:"flex",alignItems:"center",gap:6,fontFamily:"monospace"}}><span style={{width:16,height:16,borderRadius:4,background:agtronToColor(r.agtron),border:`1px solid ${C.border}`,display:"inline-block",flexShrink:0}}/>{r.agtron}</span> : "—"}
                    </td>
                    <td style={s.td}>
                      {r.agtronGround ? <span style={{display:"flex",alignItems:"center",gap:6,fontFamily:"monospace"}}><span style={{width:16,height:16,borderRadius:4,background:agtronToColor(r.agtronGround),border:`1px solid ${C.border}`,display:"inline-block",flexShrink:0}}/>{r.agtronGround}</span> : "—"}
                    </td>
                    <td style={{ ...s.td, fontFamily:"monospace", color: (r.agtron && r.agtronGround && Math.abs(parseFloat(r.agtron)-parseFloat(r.agtronGround)) > 5) ? C.amber : C.inkMid }}>
                      {(r.agtron && r.agtronGround) ? Math.abs(Math.round((parseFloat(r.agtron)-parseFloat(r.agtronGround))*10)/10) : "—"}
                    </td>
                    <td style={{ ...s.td, color: C.inkFaint }}>{dmy(r.date) || ""}</td>
                    <td style={{ ...s.td, whiteSpace: "nowrap" }} onClick={e => e.stopPropagation()}>
                      <button style={{ ...s.btnSm, marginRight: 6 }} onClick={() => { if (!expanded[r.id]) toggleExpand(r.id); startEdit(r); }}>Edit</button>
                      <button style={{ ...s.btnSm, color: C.red, borderColor: C.red }} onClick={() => deleteRow(r.id)}>Delete</button>
                    </td>
                  </tr>
                  {expanded[r.id] && (
                    <tr style={{ background: C.cream2 }}>
                      <td colSpan={11} style={{ padding: "12px 16px" }}>
                        {editing === r.id ? (
                          <div>
                            <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Batch</div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 14 }}>
                              {[["Batch", "batch"], ["Coffee", "origin"], ["Green in (kg)", "green"], ["Roasted out (kg)", "roasted"], ["Date", "date"], ["Process", "process"], ["Cultivar", "cultivar"]].map(([label, key]) => (
                                <div key={key}>
                                  <div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, marginBottom: 3 }}>{label}</div>
                                  <input
                                    type={key === "date" ? "date" : "text"}
                                    style={{ ...s.input, fontSize: 12 }}
                                    value={editVals[key]}
                                    onChange={e => setEditVals(v => ({ ...v, [key]: e.target.value }))}
                                  />
                                </div>
                              ))}
                            </div>
                            <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Quality</div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 6 }}>
                              {[["Agtron whole bean", "agtron"], ["Agtron ground", "agtronGround"], ["Cup score", "score"]].map(([label, key]) => (
                                <div key={key}>
                                  <div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, marginBottom: 3 }}>{label}</div>
                                  <input type="number" step="0.1" style={{ ...s.input, fontSize: 12 }} value={editVals[key] ?? ""} onChange={e => setEditVals(v => ({ ...v, [key]: e.target.value }))} />
                                </div>
                              ))}
                              <div>
                                <div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, marginBottom: 3 }}>Whole bean vs ground</div>
                                {(() => {
                                  const wb = parseFloat(editVals.agtron), gr = parseFloat(editVals.agtronGround);
                                  if (isNaN(wb) || isNaN(gr)) return <div style={{ fontFamily: fontSans, fontSize: 13, color: C.inkFaint, paddingTop: 6 }}>—</div>;
                                  const gap = Math.round(Math.abs(gr - wb) * 10) / 10;
                                  return <div style={{ fontFamily: fontSans, fontSize: 13, fontWeight: 600, paddingTop: 6, color: gap > 5 ? C.amber : C.ink }}>{gap} apart{gap > 5 ? " — outside dark / inside light" : ""}</div>;
                                })()}
                              </div>
                            </div>
                            {r.cupping && <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 14 }}>{r.cupping.mode === "cva" ? "CVA cupping" : "SCA cupping"} on file — full form in QC Suite → QC tools.</div>}

                            <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em", margin: "14px 0 8px" }}>Roast events</div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, marginBottom: 8 }}>
                              {EDIT_EVENTS.map(([nm, label]) => {
                                const ev = (editVals.events || {})[nm] || { time: "", temp: "" };
                                const setEv = (k, val) => setEditVals(v => ({ ...v, events: { ...(v.events || {}), [nm]: { ...((v.events || {})[nm] || { time: "", temp: "" }), [k]: val } } }));
                                const badTime = ev.time !== "" && parseMmSs(ev.time) == null;
                                return (
                                  <div key={nm} style={{ background: C.cream, borderRadius: 6, padding: "8px 10px" }}>
                                    <div style={{ fontFamily: fontSans, fontSize: 11, fontWeight: 600, color: C.ink, marginBottom: 5 }}>{label}</div>
                                    <div style={{ display: "flex", gap: 6 }}>
                                      <input style={{ ...s.input, fontSize: 12, width: 70, borderColor: badTime ? C.red : undefined }} value={ev.time} onChange={e => setEv("time", e.target.value)} placeholder="m:ss" title="Time since charge, e.g. 7:25" />
                                      <input style={{ ...s.input, fontSize: 12, width: 80 }} type="number" step="0.1" value={ev.temp} onChange={e => setEv("temp", e.target.value)} placeholder="°C" />
                                    </div>
                                    {badTime && <div style={{ fontFamily: fontSans, fontSize: 10, color: C.red, marginTop: 3 }}>Use minutes:seconds, e.g. 7:25</div>}
                                  </div>
                                );
                              })}
                            </div>
                            {(() => {
                              const evs = editVals.events || {};
                              const fcS = parseMmSs(evs["First crack"]?.time), dropS = parseMmSs(evs["Drop"]?.time);
                              const dtrNow = fcS != null && dropS != null && dropS > fcS ? Math.round((dropS - fcS) / dropS * 1000) / 10 : null;
                              return (
                                <div style={{ fontFamily: fontSans, fontSize: 11.5, color: C.inkLight, marginBottom: 14 }}>
                                  {dropS != null ? <>Total time <strong style={{ color: C.ink }}>{Math.floor(dropS / 60)}:{String(dropS % 60).padStart(2, "0")}</strong></> : "Add a drop time to see the total"}
                                  {dtrNow != null ? <> · development <strong style={{ color: C.ink }}>{Math.floor((dropS - fcS) / 60)}:{String((dropS - fcS) % 60).padStart(2, "0")}</strong> (DTR <strong style={{ color: C.ink }}>{dtrNow}%</strong>)</> : fcS == null && dropS != null ? " · add first crack to see development time" : ""}
                                  <span style={{ color: C.inkFaint }}> — times are from charge. Clear a time to remove that event.</span>
                                </div>
                              );
                            })()}
                            {r.curveAdherence && (
                              <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 14 }}>
                                <strong style={{color:C.ink}}>vs curve:</strong> {r.curveAdherence.summary}
                                {r.curveAdherence.meanAbsDev != null && <span> · Mean abs deviation {r.curveAdherence.meanAbsDev}°C</span>}
                                {r.curveAdherence.totalAccumDev != null && <span> · Total accumulated deviation {r.curveAdherence.totalAccumDev}°C·min</span>}
                              </div>
                            )}
                            {/* AI Roast Assistant temporarily removed — the Anthropic
                                organization behind fdc-roast-assistant's API key was
                                disabled and Anthropic confirmed that's final, so this
                                has no working backend right now. Re-add <AIReviewButton
                                roast={r} onReview={...}/> here once a working key/Worker
                                is back in place. */}
                            <div style={{ marginBottom: 14, marginTop: 14 }}>
                              <div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, marginBottom: 3 }}>Notes</div>
                              <input style={{ ...s.input, fontSize: 12, width: "100%" }} value={editVals.notes} onChange={e => setEditVals(v => ({ ...v, notes: e.target.value }))} />
                            </div>

                            <div style={{ display: "flex", gap: 8, marginTop: 6, paddingTop: 14, borderTop: `1px solid ${C.borderSoft}` }}>
                              <button style={s.btnPrimary} onClick={() => saveEdit(r.id)}>Save changes</button>
                              <button style={s.btn} onClick={() => { setEditing(null); setExpanded(e => ({ ...e, [r.id]: false })); }}>Cancel</button>
                              <button style={{ ...s.btn, color: "#c0392b", marginLeft: "auto" }} onClick={() => deleteRow(r.id)}>Delete</button>
                            </div>
                          </div>
                        ) : null}

                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
        <button style={s.btnPrimary} onClick={exportPDF}>Export PDF</button>
        <button style={s.btnPrimary} onClick={exportExcel}>Export CSV</button>
        <button style={s.btn} onClick={clear}>Clear all history</button>
      </div>

      <ArtisanImportPanel onImported={() => setHistory(JSON.parse(localStorage.getItem("fdc_history") || "[]"))} />
    </div>
  );
}

// ── INVENTORY ─────────────────────────────────────────────────────────────────
// ── GREEN DEFECT REFERENCE ───────────────────────────────────────────────────
const GREEN_DEFECTS = [
  { name:"Full black bean", note:"Over-fermented or dried in contact with ground/water. Roasts uneven, sour/phenolic in cup." },
  { name:"Partial black bean", note:"Localised over-fermentation. Same risk as full black but less severe." },
  { name:"Sour bean", note:"Fermentation defect — over-ripe or delayed processing. Vinegary, unclean in cup." },
  { name:"Moldy bean", note:"Poor drying/storage moisture control. Musty, earthy off-notes — reject on sight." },
  { name:"Insect-damaged bean", note:"Borer damage. Small holes create uneven roasting and can carry off-flavours." },
  { name:"Broken/chipped bean", note:"Mechanical damage during processing. Roasts faster than intact beans — tipping risk." },
  { name:"Immature (quaker) bean", note:"Underripe at harvest. Roasts pale, tastes papery/peanutty — the classic quaker defect." },
  { name:"Shell bean (elephant ear)", note:"Genetic malformation. Thin shell roasts much faster than the rest of the batch." },
  { name:"Floater / lightweight bean", note:"Low density — under-ripe, insect-hollowed, or poorly dried. Roasts unevenly, often scorches." },
  { name:"Foreign matter (stone, stick)", note:"Contamination from processing. Damages equipment and burns in the roaster." },
];

// Export helpers for the editable Stocktake panel — separate from
// exportInventoryPDF/CSV since this one includes the actual counted
// amounts and differences, which those reports don't track.
function exportStocktakePDF(inventory, greenInFor, totalRoastedFor, totalKg, stocktakeCounts, bagRows = []) {
  const dateStr = todayDmy();
  const r2 = (label, value, bold, color) => `<tr><td class="lbl">${label}</td><td class="val${bold?" bld":""}"${color?` style="color:${color}"`:""}>${value}</td></tr>`;
  const rows = inventory.map(item => {
    const count = stocktakeCounts[item.id];
    const expected = totalKg(item);
    const actual = count && count.actual !== "" ? parseFloat(count.actual) : null;
    const diff = actual != null ? Math.round((actual - expected) * 10) / 10 : null;
    return `
    <tr><td class="lbl" style="padding-top:12px;font-weight:600;color:#1C1814">${item.name||item.origin||"—"}</td><td class="val" style="padding-top:12px"></td></tr>
    ${r2("Green in", greenInFor(item)+" kg")}
    ${r2("Roasted", totalRoastedFor(item)+" kg")}
    ${r2("Expected remaining", expected+" kg", true)}
    ${r2("Actual counted", actual != null ? actual+" kg" : "— not counted")}
    ${diff != null ? r2("Difference", (diff>0?"+":"")+diff+" kg", true, diff===0?"#2E7D32":"#B94040") : ""}
    `;
  }).join("");
  const bagHtml = bagRows.map(b => {
    const diff = b.actual != null ? b.actual - b.expected : null;
    return `
    <tr><td class="lbl" style="padding-top:12px;font-weight:600;color:#1C1814">${b.label}</td><td class="val" style="padding-top:12px"></td></tr>
    ${r2("Expected on shelf", b.expected+" bags", true)}
    ${r2("Actual counted", b.actual != null ? b.actual+" bags" : "— not counted")}
    ${diff != null ? r2("Difference", (diff>0?"+":"")+diff+" bags", true, diff===0?"#2E7D32":"#B94040") : ""}
    `;
  }).join("");
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Stocktake — ${dateStr}</title>
<style>
  @page { margin: 18mm 20mm; size: A4 portrait; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, "Helvetica Neue", Arial, sans-serif; font-size: 13px; color: #1C1814; background: #fff; max-width: 680px; padding: 32px 0; }
  h1 { font-size: 24px; font-weight: 700; color: #1C1814; margin-bottom: 5px; }
  .sub { font-size: 12px; color: #7A6E63; padding-bottom: 16px; margin-bottom: 24px; border-bottom: 2px solid #1C1814; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 7px 0; vertical-align: middle; border-bottom: 1px solid #EDE8DE; line-height: 1.3; }
  td.lbl { color: #4A3F35; width: 58%; padding-left: 14px; font-size: 13px; }
  td.val { color: #1C1814; text-align: right; font-size: 13px; }
  td.val.bld { font-weight: 700; }
  .footer { margin-top: 36px; font-size: 10px; color: #A89E93; border-top: 1px solid #D5CCBE; padding-top: 10px; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; padding: 0; } }
</style></head><body>
<h1>Stocktake — Actual vs Expected</h1>
<div class="sub">Skelligs Coffee &nbsp;&middot;&nbsp; ${dateStr}</div>
<h2 style="font-size:15px;margin:0 0 6px">Green coffee</h2>
<table>${rows}</table>
${bagHtml ? `<h2 style="font-size:15px;margin:26px 0 6px">Bagged coffee</h2><table>${bagHtml}</table>` : ""}
<div class="footer">Skelligs Coffee &nbsp;&middot;&nbsp; Generated ${dateStr} &nbsp;&middot;&nbsp; fdc-roasting-hub.pages.dev</div>
</body></html>`;
  const win = window.open("", "_blank");
  if (!win) { window.alert("Please allow pop-ups for this site to export the report."); return; }
  win.document.write(html);
  win.document.close();
  setTimeout(() => { win.focus(); win.print(); }, 400);
}

function exportStocktakeCSV(inventory, greenInFor, totalRoastedFor, totalKg, stocktakeCounts, bagRows = []) {
  try {
    const esc = v => {
      const str = String(v ?? "");
      return str.includes(",") || str.includes('"') || str.includes("\n") ? `"${str.replace(/"/g,'""')}"` : str;
    };
    const row = cols => cols.map(esc).join(",");
    const lines = [
      row(["SKELLIGS COFFEE — STOCKTAKE"]),
      row(["Generated", todayDmy()]),
      "",
      row(["Coffee","Green In (kg)","Roasted (kg)","Expected Remaining (kg)","Actual Counted (kg)","Difference (kg)","Counted on"]),
      ...inventory.map(item => {
        const count = stocktakeCounts[item.id];
        const expected = totalKg(item);
        const actual = count && count.actual !== "" ? parseFloat(count.actual) : "";
        const diff = actual !== "" ? Math.round((actual - expected) * 10) / 10 : "";
        return row([item.name||item.origin||"", greenInFor(item), totalRoastedFor(item), expected, actual, diff, count?.date||""]);
      }),
      ...(bagRows.length ? [
        "",
        row(["Bagged coffee","Code","Packaged (bags)","Expected on shelf (bags)","Actual Counted (bags)","Difference (bags)","Counted on"]),
        ...bagRows.map(b => row([b.label, b.code, b.packaged, b.expected, b.actual ?? "", b.actual != null ? b.actual - b.expected : "", b.date || ""])),
      ] : []),
    ];
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Skelligs-Stocktake-${localIsoDate()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch(e) {
    window.alert("Export failed: " + e.message);
  }
}

function GreenDefectReference() {
  const [open, setOpen] = useState(false);
  return (
    <div style={{border:`1px solid ${C.border}`,borderRadius:8,marginBottom:20,overflow:"hidden"}}>
      <div onClick={()=>setOpen(o=>!o)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",background:C.cream2,cursor:"pointer",userSelect:"none"}}>
        <div style={{fontFamily:fontSans,fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:C.inkLight}}>Green defect reference</div>
        <div style={{color:C.inkFaint,fontSize:13}}>{open?"▲":"▼"}</div>
      </div>
      {open && (
        <div style={{padding:16}}>
          <div style={{fontFamily:fontSans,fontSize:11,color:C.inkLight,marginBottom:12}}>
            Quick reference for grading incoming green — spotting these before roasting saves a batch, not after.
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            {GREEN_DEFECTS.map(d => (
              <div key={d.name} style={{background:C.cream2,borderRadius:6,padding:"9px 12px"}}>
                <div style={{fontFamily:fontSans,fontSize:12,fontWeight:600,color:C.ink,marginBottom:2}}>{d.name}</div>
                <div style={{fontFamily:fontSans,fontSize:11,color:C.inkMid,lineHeight:1.4}}>{d.note}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Roast History logs the same coffee under inconsistent spellings over
// time ("Halo - Ethiopia" one day, "Halo Beriti — Ethiopia" the next) —
// exact-string matching against Inventory names silently misses most of
// them. This does a forgiving word-overlap match instead: strip
// punctuation/dashes, split into words, and call it a match if at least
// half of the shorter name's words appear in the other. Not bulletproof,
// but far better than exact match, and unmatched origins get surfaced
// below rather than silently dropped.
function normalizeCoffeeName(s) {
  return (s || "").toLowerCase().replace(/[—–-]/g, " ").replace(/\s+/g, " ").trim();
}
// Country/region words (Brazil, Ethiopia, Colombia...) get reused across
// many different coffees, so they're excluded from matching entirely —
// otherwise a short name like "Carmo — Brazil" matches on "Brazil" alone
// and silently pulls in every other Brazil-origin roast too. Matching now
// hinges only on the actually distinctive words (Carmo, Pinhal, Halo).
const COFFEE_NAME_STOPWORDS = new Set(ORIGINS.flatMap(o => o.toLowerCase().split(" ")));
function coffeeNameTokens(s) {
  return new Set(normalizeCoffeeName(s).split(" ").filter(w => w.length > 2 && !COFFEE_NAME_STOPWORDS.has(w)));
}
// Same coffee name, ignoring stray spaces and capital letters —
// "Halo Beriti " and "halo beriti" are the same coffee.
function sameCoffee(a, b) {
  return String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
}

function coffeeNamesMatch(a, b) {
  const ta = coffeeNameTokens(a), tb = coffeeNameTokens(b);
  if (ta.size === 0 || tb.size === 0) return false;
  const [smaller, larger] = ta.size <= tb.size ? [ta, tb] : [tb, ta];
  let overlap = 0;
  smaller.forEach(w => { if (larger.has(w)) overlap++; });
  return overlap / smaller.size >= 0.5;
}

// Actual counted amounts from a physical stock take, keyed by inventory
// item id — separate from the item's own bag count, so entering a count
// here never overwrites the Hub's own running figures. Ger reconciles by
// eye from the Difference column, then corrects the item via Edit if needed.
const STOCKTAKE_KEY = "fdc_stocktake_counts";
function loadStocktakeCounts() { try { return JSON.parse(localStorage.getItem(STOCKTAKE_KEY) || "{}"); } catch { return {}; } }
function saveStocktakeCounts(c) { localStorage.setItem(STOCKTAKE_KEY, JSON.stringify(c)); }

function Inventory({ inventory, setInventory }) {
 const [form,setForm]=useState({name:"",origin:"",process:"Washed",masl:"",moisture:"",density:"",cultivar:"",bagWeight:"",bags:"",costPerKg:"",arrived:"",supplierEmail:""});
 const [showForm,setShowForm]=useState(false);
 const [editId,setEditId]=useState(null);
 const [editVal,setEditVal]=useState({});
 const [logRoastId,setLogRoastId]=useState(null);
 const [logRoastAmount,setLogRoastAmount]=useState("");
 const setF=(k,v)=>setForm(f=>({...f,[k]:v}));
 const totalKg=item=>Math.round(parseFloat(item.bagWeight||0)*parseFloat(item.bags||0)*10)/10;
 const totalStock=inventory.reduce((a,i)=>a+totalKg(i),0);
 const totalValue=inventory.reduce((a,i)=>a+(totalKg(i)*(parseFloat(i.costPerKg)||0)),0);

 // Roasted total per coffee is now a single stored, incrementing figure —
 // written in lockstep with Remaining Green by exactly two actions: a
 // roast saved in Roast Mode (automatic), or "Log roasted" below (manual
 // catch-up). Nothing else changes it, so it can no longer drift out of
 // sync or double-count the way summing fuzzy-matched History entries
 // could — that approach is gone entirely, not just the display of it.
 const totalRoastedFor = item => Math.round((parseFloat(item.totalRoastedKg) || 0) * 10) / 10;
 // Green in = what's currently left (Remaining Green) plus everything
 // already roasted from it — the total ever received, derived rather
 // than separately tracked, so it can't drift out of sync with the
 // other two. A manual stocktake correction to Remaining Green (via
 // Edit) shifts this total accordingly, which is correct — it's saying
 // less was actually received than the system had assumed, not that a
 // roast happened.
 const greenInFor = item => Math.round((totalKg(item) + totalRoastedFor(item)) * 10) / 10;
 // Flags a coffee worth reordering soon — under 20% of what was
 // originally received still remains as green.
 const runningLow = item => { const gi = greenInFor(item); return gi > 0 && (totalKg(item) / gi) < 0.2; };
 // Suggests a reorder quantity from how much of this coffee has actually
 // been roasted through so far (totalRoastedFor), not a flat guess — then
 // opens a pre-filled email via mailto: so Ger can send it straight to
 // whichever supplier is on file for this coffee.
 // Recent burn rate, not lifetime total — looks at how much of THIS coffee
 // has actually been roasted in the last RECENT_BURN_WINDOW_DAYS, derives a
 // kg/week rate from that, and suggests REORDER_COVERAGE_WEEKS worth. A
 // coffee that's been running for a year no longer suggests reordering
 // its entire lifetime volume just because stock is finally running low.
 const RECENT_BURN_WINDOW_DAYS = 60;
 const REORDER_COVERAGE_WEEKS = 4;
 const recentBurnRateKgPerWeek = (item) => {
   let history = [];
   try { history = JSON.parse(localStorage.getItem("fdc_history") || "[]"); } catch { history = []; }
   const cutoffMs = Date.now() - RECENT_BURN_WINDOW_DAYS * 86400000;
   const matches = history.filter(r => r.origin && r.green && coffeeNamesMatch(r.origin, item.name || item.origin) && r.date && new Date(r.date).getTime() >= cutoffMs);
   if (matches.length === 0) return null;
   const totalKgUsed = matches.reduce((a, r) => a + (parseFloat(r.green) || 0), 0);
   const earliestDate = matches.reduce((min, r) => (!min || r.date < min) ? r.date : min, null);
   const daysSpan = Math.max(1, (Date.now() - new Date(earliestDate).getTime()) / 86400000);
   return totalKgUsed / (daysSpan / 7);
 };
 const draftReorderEmail = (item) => {
   const burnRateKgWk = recentBurnRateKgPerWeek(item);
   let suggestQty, basisNote;
   if (burnRateKgWk && burnRateKgWk > 0) {
     suggestQty = Math.max(Math.round(burnRateKgWk * REORDER_COVERAGE_WEEKS), 5);
     basisNote = `based on recent usage of ~${Math.round(burnRateKgWk * 10) / 10}kg/week over the last ${RECENT_BURN_WINDOW_DAYS} days`;
   } else {
     // No recent roast history for this coffee yet — fall back to a small
     // fraction of the lifetime total rather than suggesting zero. Flagged
     // in the email itself so it's clear this is a rougher estimate.
     const usedKg = totalRoastedFor(item);
     suggestQty = Math.max(Math.round(usedKg * 0.25), 5);
     basisNote = "rough estimate — no roasts of this coffee logged in the last " + RECENT_BURN_WINDOW_DAYS + " days to calculate a burn rate from";
   }
   const to = item.supplierEmail || "";
   const subject = encodeURIComponent(`Reorder — ${item.name || item.origin || "coffee"}`);
   const body = encodeURIComponent(`Hi,\n\nWe're running low on ${item.name || item.origin} — currently ${totalKg(item)}kg remaining in stock.\n\nCould we place a reorder for approximately ${suggestQty}kg (${basisNote})?\n\nThanks,\nGer\nSkelligs Coffee`);
   window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
 };

 const add=()=>{
 const next=[...inventory,{...form,id:Date.now()}];
 setInventory(next);saveInventory(next);
 setForm({name:"",origin:"",process:"Washed",masl:"",moisture:"",density:"",cultivar:"",bagWeight:"",bags:"",costPerKg:"",arrived:"",supplierEmail:""});
 setShowForm(false);
 };
 const remove=id=>{
 const item=inventory.find(i=>i.id===id);
 const label=item?.name||item?.origin||"this coffee";
 if(!window.confirm(`Remove ${label} from inventory? This can't be undone.`))return;
 const next=inventory.filter(i=>i.id!==id);setInventory(next);saveInventory(next);
 };
 const startEdit=item=>{setEditId(item.id);setEditVal({...item});};
 const saveEdit=()=>{
 const original = inventory.find(i=>i.id===editId);
 let merged = {...original, ...editVal};
 // If Roasted total was corrected but bags weren't ALSO manually touched in
 // this same edit, hold Green In (total ever received) steady and recompute
 // Remaining Green to compensate — the same atomic-update principle Log
 // Roasted and Roast Mode already use, so the two figures can't drift apart
 // again after a manual Roasted correction. If both are edited together on
 // purpose, both entered values are trusted as-is.
 const roastedChanged = editVal.totalRoastedKg !== undefined && editVal.totalRoastedKg !== "" && (parseFloat(editVal.totalRoastedKg)||0) !== (parseFloat(original.totalRoastedKg)||0);
 const bagsManuallyChanged = editVal.bags !== undefined && editVal.bags !== "" && (parseFloat(editVal.bags)||0) !== (parseFloat(original.bags)||0);
 if (roastedChanged && !bagsManuallyChanged) {
 const preservedGreenIn = Math.round((Math.round(parseFloat(original.bagWeight||0)*parseFloat(original.bags||0)*10)/10 + (parseFloat(original.totalRoastedKg)||0)) * 10) / 10;
 const newRoasted = parseFloat(editVal.totalRoastedKg) || 0;
 const newRemaining = Math.max(0, Math.round((preservedGreenIn - newRoasted) * 100) / 100);
 const bagWt = parseFloat(merged.bagWeight) || 0;
 merged = { ...merged, bags: bagWt > 0 ? Math.round((newRemaining / bagWt) * 100) / 100 : merged.bags };
 }
 const next=inventory.map(i=>i.id===editId?merged:i);
 setInventory(next);saveInventory(next);setEditId(null);
 };
 // Records a roasted amount straight from the Inventory tab itself — for
 // roasts that happened outside Roast Mode, or catching up on ones that
 // weren't logged there. Deducts from green stock (same maths Roast Mode
 // uses: reduce bag count by kg/bagWeight) and keeps a running roasted
 // total on the item so both figures are visible at a glance.
 const logRoasted=(id)=>{
 const kg=parseFloat(logRoastAmount);
 if(!kg||kg<=0)return;
 const next=inventory.map(i=>{
 if(i.id!==id)return i;
 const bagWt=parseFloat(i.bagWeight)||0,bags=parseFloat(i.bags)||0;
 const remaining=Math.max(0,bagWt*bags-kg);
 return {...i,bags: bagWt>0 ? Math.round(remaining/bagWt*100)/100 : 0, totalRoastedKg: Math.round(((parseFloat(i.totalRoastedKg)||0)+kg)*10)/10};
 });
 setInventory(next);saveInventory(next);
 setLogRoastId(null);setLogRoastAmount("");
 };

 const exportInventoryPDF = () => {
 const dateStr = todayDmy();
 const r2 = (label, value, bold) => `<tr><td class="lbl">${label}</td><td class="val${bold?" bld":""}">${value}</td></tr>`;
 const rows = inventory.map(item => `
 <tr><td class="lbl" style="padding-top:12px;font-weight:600;color:#1C1814">${item.name||item.origin||"—"}</td><td class="val" style="padding-top:12px"></td></tr>
 ${r2("Origin", item.origin||"—")}
 ${r2("Process", item.process||"—")}
 ${r2("Stock remaining", totalKg(item)+" kg", true)}
 ${item.totalRoastedKg ? r2("Roasted to date", item.totalRoastedKg+" kg") : ""}
 ${item.costPerKg ? r2("Cost/kg", "€"+item.costPerKg) : ""}
 ${item.costPerKg ? r2("Stock value", "€"+Math.round(totalKg(item)*parseFloat(item.costPerKg))) : ""}
 ${item.arrived ? r2("Arrived", dmy(item.arrived)) : ""}
 `).join("");
 const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Green Inventory Report — ${dateStr}</title>
<style>
 @page { margin: 18mm 20mm; size: A4 portrait; }
 * { box-sizing: border-box; margin: 0; padding: 0; }
 body { font-family: -apple-system, "Helvetica Neue", Arial, sans-serif; font-size: 13px; color: #1C1814; background: #fff; max-width: 680px; padding: 32px 0; }
 h1 { font-size: 24px; font-weight: 700; color: #1C1814; margin-bottom: 5px; }
 .sub { font-size: 12px; color: #7A6E63; padding-bottom: 16px; margin-bottom: 24px; border-bottom: 2px solid #1C1814; }
 table { width: 100%; border-collapse: collapse; }
 td { padding: 7px 0; vertical-align: middle; border-bottom: 1px solid #EDE8DE; line-height: 1.3; }
 td.lbl { color: #4A3F35; width: 58%; padding-left: 14px; font-size: 13px; }
 td.val { color: #1C1814; text-align: right; font-size: 13px; }
 td.val.bld { font-weight: 700; }
 .footer { margin-top: 36px; font-size: 10px; color: #A89E93; border-top: 1px solid #D5CCBE; padding-top: 10px; }
 @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; padding: 0; } }
</style></head><body>
<h1>Green Inventory Report</h1>
<div class="sub">Skelligs Coffee &nbsp;&middot;&nbsp; Loring S70 Peregrine &nbsp;&middot;&nbsp; ${dateStr}</div>
<table>
 ${r2("Coffees in stock", inventory.length)}
 ${r2("Total green stock", totalStock+" kg", true)}
 ${totalValue > 0 ? r2("Estimated stock value", "€"+Math.round(totalValue)) : ""}
 ${rows}
</table>
<div class="footer">Skelligs Coffee &nbsp;&middot;&nbsp; Generated ${dateStr} &nbsp;&middot;&nbsp; fdc-roasting-hub.pages.dev</div>
</body></html>`;
 const win = window.open("", "_blank");
 if (!win) { window.alert("Please allow pop-ups for this site to export the report."); return; }
 win.document.write(html);
 win.document.close();
 setTimeout(() => { win.focus(); win.print(); }, 400);
 };

 const exportInventoryCSV = () => {
 try {
 const esc = v => {
 const str = String(v ?? "");
 return str.includes(",") || str.includes('"') || str.includes("\n") ? `"${str.replace(/"/g,'""')}"` : str;
 };
 const row = cols => cols.map(esc).join(",");
 const lines = [
 row(["SKELLIGS COFFEE — GREEN INVENTORY REPORT"]),
 row(["Generated", todayDmy()]),
 "",
 row(["Coffee","Origin","Process","MASL","Moisture %","Density (g/L)","Cultivar","Stock (kg)","Roasted (kg)","Cost/kg (€)","Value (€)","Arrived"]),
 ...inventory.map(item => row([
 item.name||"", item.origin||"", item.process||"", item.masl||"", item.moisture||"", item.density||"", item.cultivar||"",
 totalKg(item), item.totalRoastedKg||"", item.costPerKg||"",
 item.costPerKg ? Math.round(totalKg(item)*parseFloat(item.costPerKg)) : "", item.arrived||"",
 ])),
 ];
 const csv = lines.join("\n");
 const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
 const url = URL.createObjectURL(blob);
 const a = document.createElement("a");
 a.href = url;
 a.download = `Skelligs-Green-Inventory-${localIsoDate()}.csv`;
 document.body.appendChild(a);
 a.click();
 document.body.removeChild(a);
 URL.revokeObjectURL(url);
 } catch(e) {
 window.alert("Export failed: " + e.message);
 }
 };

 // ── Cost & profit summary, mirroring the Pricing tab's own formulas ──
 // Reads the same persisted settings Pricing writes to (fdc_pricing_settings
 // / fdc_pricing_origins), so this strip and the full calculator on
 // Roastery → Pricing always agree with each other rather than keeping a
 // second, separate set of numbers.
 let pSettings = {};
 try { pSettings = JSON.parse(localStorage.getItem("fdc_pricing_settings") || "{}"); } catch {}
 let pOrigins = [];
 try { pOrigins = JSON.parse(localStorage.getItem("fdc_pricing_origins") || "[]"); } catch {}
 const pHasPricingSetup = pOrigins.some(o => effectiveGreenCost(o, inventory) > 0);
 const pLoss = (parseFloat(pSettings.roastLoss) || 13.5) / 100;
 const pBlendedGreenCost = pOrigins.reduce((a, o) => a + ((parseFloat(o.share) || 0) / 100) * effectiveGreenCost(o, inventory), 0);
 const pGreenCostPerRoastedKg = pLoss < 1 ? pBlendedGreenCost / (1 - pLoss) : pBlendedGreenCost;
 const pRoastedKgHr = (parseFloat(pSettings.roastsPerHour) || 0) * (parseFloat(pSettings.kgPerRoast) || 0) * (1 - pLoss);
 const pFullKgYr = pRoastedKgHr * (parseFloat(pSettings.fullHrDay) || 0) * (parseFloat(pSettings.fullDaysWk) || 0) * (parseFloat(pSettings.fullWeeksYr) || 0);
 const pCurrKgYr = pRoastedKgHr * (parseFloat(pSettings.currHrDay) || 0) * (parseFloat(pSettings.currDaysWk) || 0) * (parseFloat(pSettings.currWeeksYr) || 0);
 const pUtilPct = pSettings.utilisation != null ? pSettings.utilisation : (pFullKgYr > 0 ? Math.round((pCurrKgYr / pFullKgYr) * 100) : 0);
 const pActiveKgYr = pFullKgYr * pUtilPct / 100;
 const pLabourHrYr = (parseFloat(pSettings.currHrDay) || 0) * (parseFloat(pSettings.currDaysWk) || 0) * (parseFloat(pSettings.currWeeksYr) || 0) * (parseFloat(pSettings.employees) || 0);
 const pLabourCostYr = pLabourHrYr * (parseFloat(pSettings.labourRate) || 0);
 const pLabourPerKg = pCurrKgYr > 0 ? pLabourCostYr / pCurrKgYr : 0;
 const pRentYr = parseFloat(pSettings.rent) || 0;
 const pGasYr = (parseFloat(pSettings.gas) || 0) * 12;
 const pElecYr = (parseFloat(pSettings.electricity) || 0) * 12;
 const pOverheadsYr = (pSettings.overheads || []).reduce((a, o) => a + (parseFloat(o.amount) || 0), 0);
 const pRentPerKg = pActiveKgYr > 0 ? pRentYr / pActiveKgYr : 0;
 const pEnergyPerKg = pActiveKgYr > 0 ? (pGasYr + pElecYr) / pActiveKgYr : 0;
 const pOverheadsPerKg = pActiveKgYr > 0 ? pOverheadsYr / pActiveKgYr : 0;
 const pBaseCOGSperKg = pGreenCostPerRoastedKg + pLabourPerKg + pRentPerKg + pEnergyPerKg + pOverheadsPerKg;
 const pBagSizes = [
 { weight: 0.25, pkg: parseFloat(pSettings.pkg250) || 0.55, mixPct: pSettings.mix250 ?? 50 },
 { weight: 0.5, pkg: parseFloat(pSettings.pkg500) || 0.75, mixPct: pSettings.mix500 ?? 30 },
 { weight: 1.0, pkg: parseFloat(pSettings.pkg1000) || 1.10, mixPct: pSettings.mix1000 ?? 20 },
 ];
 const pCalcBag = (bag) => {
 const cogsPerBag = pBaseCOGSperKg * bag.weight + bag.pkg;
 const pSize = Math.round(bag.weight * 1000);
 const pOurR = parseFloat(pSettings.ourPrices?.[pSize]?.retail), pOurW = parseFloat(pSettings.ourPrices?.[pSize]?.wholesale);
 const pVatDiv = 1 + (parseFloat(pSettings.retailVatPct) || 0) / 100;
 const retailPrice = pOurR > 0 ? pOurR / pVatDiv : parseFloat(pSettings.retailMargin) > 0 ? cogsPerBag / (1 - parseFloat(pSettings.retailMargin) / 100) : cogsPerBag;
 const wholesalePrice = pOurW > 0 ? pOurW : parseFloat(pSettings.wholesaleMargin) > 0 ? cogsPerBag / (1 - parseFloat(pSettings.wholesaleMargin) / 100) : cogsPerBag;
 const retailSplit = bag.mixPct / 100;
 const blendedPrice = retailPrice * retailSplit + wholesalePrice * (1 - retailSplit);
 return { cogsPerBag, blendedPrice, contributionPerBag: blendedPrice - cogsPerBag };
 };
 const pWeightedContribPerKg = pBagSizes.reduce((a, bag) => {
 const { contributionPerBag } = pCalcBag(bag);
 const contribPerKg = bag.weight > 0 ? contributionPerBag / bag.weight : 0;
 return a + (bag.mixPct / 100) * contribPerKg;
 }, 0);
 const pAnnualProfit = pActiveKgYr * pWeightedContribPerKg;
 const totalRoastedAll = Math.round(inventory.reduce((a, i) => a + totalRoastedFor(i), 0) * 10) / 10;

 return (
 <div>
 <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:pHasPricingSetup?10:20}}>
 {[["Coffees in stock",inventory.length],["Total green stock",totalStock+"kg"],["Total roasted",totalRoastedAll+"kg"],["Estimated stock value","€"+Math.round(totalValue)],["COGS/kg (roasted)",pHasPricingSetup?"€"+(Math.round(pBaseCOGSperKg*100)/100):"—"],["Est. annual profit",pHasPricingSetup?"€"+Math.round(pAnnualProfit).toLocaleString():"—"]].map(([label,val])=>(
 <div key={label} style={{background:C.cream2,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 16px"}}>
 <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>{label}</div>
 <div style={{fontFamily:fontSerif,fontSize:26,color:C.ink}}>{val}</div>
 </div>
 ))}
 </div>
 {!pHasPricingSetup && (
 <div style={{fontFamily:fontSans,fontSize:11,color:C.inkFaint,marginBottom:20,fontStyle:"italic"}}>
 COGS and profit figures need green cost entered in Green Suite → Pricing first — they'll fill in here automatically once that's set up.
 </div>
 )}
 {pHasPricingSetup && (
 <div style={{fontFamily:fontSans,fontSize:11,color:C.inkFaint,marginBottom:20}}>
 Cost and profit figures reflect your current settings on Green Suite → Pricing, at {pUtilPct}% capacity utilisation.
 </div>
 )}

 <GreenDefectReference />

 <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
 <div style={s.sectionLabel}>Green coffee stock</div>
 <button style={s.btnPrimary} onClick={()=>setShowForm(f=>!f)}>{showForm?"Cancel":"+ Add coffee"}</button>
 </div>

 {showForm&&(
 <div style={{background:C.cream2,border:`1px solid ${C.border}`,borderRadius:8,padding:"16px 18px",marginBottom:16}}>
 <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:10,marginBottom:10}}>
 <div><div style={s.fieldLabel}>Coffee name</div><input style={s.input} value={form.name} onChange={e=>setF("name",e.target.value)} placeholder="e.g. Halo Beriti"/></div>
 <div><div style={s.fieldLabel}>Origin</div><ComboBox value={form.origin} onChange={v=>setF("origin",v)} options={ORIGINS} placeholder="Ethiopia" style={s.input}/></div>
 <div><div style={s.fieldLabel}>Process</div><select style={s.input} value={form.process} onChange={e=>setF("process",e.target.value)}>{["Washed","Natural","Honey","Anaerobic","Wet-hulled","Decaf — Washed","Decaf — Natural","Decaf — Sugarcane (EA)","Decaf — Swiss Water"].map(o=><option key={o}>{o}</option>)}</select></div>
 <div><div style={s.fieldLabel}>Cultivar</div><ComboBox value={form.cultivar} onChange={v=>setF("cultivar",v)} options={CULTIVARS} placeholder="e.g. Castillo" style={s.input}/></div>
 </div>
 <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr 1fr",gap:10,marginBottom:14}}>
 <div><div style={s.fieldLabel}>MASL</div><input style={s.input} value={form.masl} onChange={e=>setF("masl",e.target.value)} placeholder="1900"/></div>
 <div><div style={s.fieldLabel}>Moisture %</div><input style={s.input} value={form.moisture} onChange={e=>setF("moisture",e.target.value)} placeholder="10.5"/></div>
 <div><div style={s.fieldLabel}>Density (g/L) <span title="How to measure: weigh out 100g of green beans, pour into a measuring jug, and note the volume in mL. Density (g/L) = 100,000 ÷ that volume in mL." style={{marginLeft:3,color:C.inkFaint,cursor:"help",fontWeight:400}}>ⓘ</span></div><input style={s.input} value={form.density} onChange={e=>setF("density",e.target.value)} placeholder="740"/></div>
 <div><div style={s.fieldLabel}>Bag weight (kg)</div><input style={s.input} type="number" value={form.bagWeight} onChange={e=>setF("bagWeight",e.target.value)} placeholder="60"/></div>
 <div><div style={s.fieldLabel}>No. of bags</div><input style={s.input} type="number" step="0.5" min="0" value={form.bags} onChange={e=>setF("bags",e.target.value)} placeholder="1"/></div>
 <div><div style={s.fieldLabel}>Cost per kg (€)</div><input style={s.input} type="number" value={form.costPerKg} onChange={e=>setF("costPerKg",e.target.value)} placeholder="8.50" step="0.01"/></div>
 </div>
 <div style={{marginBottom:14}}>
 <div style={s.fieldLabel}>Arrived</div>
 <input style={{...s.input,width:180}} type="date" value={form.arrived} onChange={e=>setF("arrived",e.target.value)}/>
 </div>
  <div style={{marginBottom:14}}>
 <div style={s.fieldLabel}>Supplier email (optional)</div>
 <input style={{...s.input,maxWidth:320}} type="email" value={form.supplierEmail||""} onChange={e=>setF("supplierEmail",e.target.value)} placeholder="e.g. sales@importer.com"/>
 </div>
 <button style={s.btnPrimary} onClick={add}>Add to inventory</button>
 </div>
 )}

 {inventory.length===0?(
 <div style={{fontFamily:fontSans,fontSize:13,color:C.inkFaint,padding:"1rem 0"}}>No coffees in inventory yet. Add one above.</div>
 ):(
 <>
 <div style={{display:"flex",gap:10,marginBottom:12}}>
 <button style={s.btnPrimary} onClick={exportInventoryPDF}>Export PDF</button>
 <button style={s.btnPrimary} onClick={exportInventoryCSV}>Export CSV</button>
 </div>
 <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflowX:"auto"}}>
 <table style={{width:"100%",borderCollapse:"collapse",minWidth:1150}}>
 <thead>
 <tr style={{background:C.cream3}}>
 {["Coffee","","Origin","Process","MASL","Moisture","Density","Cultivar","Green in","Roasted","Remaining green","Cost/kg","Value","Arrived"].map(h=>(
 <th key={h} style={s.thStyle}>{h}</th>
 ))}
 </tr>
 </thead>
 <tbody>
 {inventory.map(item=>editId===item.id?(
 <tr key={item.id} style={{background:"#FDF6E8"}}>
 <td colSpan={11} style={{padding:"12px 16px"}}>
 <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr",gap:8,marginBottom:8}}>
 <div><div style={s.fieldLabel}>Name</div><input style={s.input} value={editVal.name||""} onChange={e=>setEditVal(v=>({...v,name:e.target.value}))}/></div>
 <div><div style={s.fieldLabel}>Origin</div><ComboBox value={editVal.origin||""} onChange={v=>setEditVal(ev=>({...ev,origin:v}))} options={ORIGINS} placeholder="Origin" style={s.input}/></div>
 <div><div style={s.fieldLabel}>Process</div><select style={s.input} value={editVal.process||""} onChange={e=>setEditVal(v=>({...v,process:e.target.value}))}>{["Washed","Natural","Honey","Anaerobic","Wet-hulled","Decaf — Washed","Decaf — Natural","Decaf — Sugarcane (EA)","Decaf — Swiss Water"].map(o=><option key={o}>{o}</option>)}</select></div>
 <div><div style={s.fieldLabel}>Cultivar</div><input style={s.input} value={editVal.cultivar||""} onChange={e=>setEditVal(v=>({...v,cultivar:e.target.value}))}/></div>
 <div><div style={s.fieldLabel}>Arrived</div><input style={s.input} type="date" value={editVal.arrived||""} onChange={e=>setEditVal(v=>({...v,arrived:e.target.value}))}/></div>
 </div>
 <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr 1fr",gap:8,marginBottom:10}}>
 <div><div style={s.fieldLabel}>MASL</div><input style={s.input} value={editVal.masl||""} onChange={e=>setEditVal(v=>({...v,masl:e.target.value}))}/></div>
 <div><div style={s.fieldLabel}>Moisture %</div><input style={s.input} value={editVal.moisture||""} onChange={e=>setEditVal(v=>({...v,moisture:e.target.value}))}/></div>
 <div><div style={s.fieldLabel}>Density (g/L) <span title="How to measure: weigh out 100g of green beans, pour into a measuring jug, and note the volume in mL. Density (g/L) = 100,000 ÷ that volume in mL." style={{marginLeft:3,color:C.inkFaint,cursor:"help",fontWeight:400}}>ⓘ</span></div><input style={s.input} value={editVal.density||""} onChange={e=>setEditVal(v=>({...v,density:e.target.value}))} placeholder="e.g. 740"/></div>
 <div><div style={s.fieldLabel}>Bag weight (kg)</div><input style={s.input} value={editVal.bagWeight||""} onChange={e=>setEditVal(v=>({...v,bagWeight:e.target.value}))}/></div>
 <div><div style={s.fieldLabel}>No. of bags</div><input style={s.input} type="number" step="0.5" min="0" value={editVal.bags||""} onChange={e=>setEditVal(v=>({...v,bags:e.target.value}))}/></div>
 <div><div style={s.fieldLabel}>Cost per kg (€)</div><input style={s.input} value={editVal.costPerKg||""} onChange={e=>setEditVal(v=>({...v,costPerKg:e.target.value}))} placeholder="e.g. 8.50"/></div>
 <div><div style={s.fieldLabel}>Roasted total (kg)</div><input style={s.input} type="number" step="0.1" min="0" value={editVal.totalRoastedKg||""} onChange={e=>setEditVal(v=>({...v,totalRoastedKg:e.target.value}))} placeholder="e.g. 125.4"/></div>
 <div><div style={s.fieldLabel}>Supplier email</div><input style={s.input} type="email" value={editVal.supplierEmail||""} onChange={e=>setEditVal(v=>({...v,supplierEmail:e.target.value}))} placeholder="e.g. sales@importer.com"/></div>
 </div>
 <div style={{display:"flex",gap:8}}>
 <button style={s.btnPrimary} onClick={saveEdit}>Save changes</button>
 <button style={s.btn} onClick={()=>setEditId(null)}>Cancel</button>
 </div>
 </td>
 </tr>
 ):(
 <Fragment key={item.id}>
 <tr style={{borderBottom: logRoastId===item.id ? "none" : `1px solid ${C.borderSoft}`,cursor:"pointer"}}
 onMouseEnter={e=>e.currentTarget.style.background=C.cream2}
 onMouseLeave={e=>e.currentTarget.style.background=""}>
 <td style={{...s.td,color:C.amber,fontWeight:600}}>{item.name||"—"}</td>
 <td style={{...s.td,whiteSpace:"nowrap"}}>
 <button style={{...s.btnSm,marginRight:4}} onClick={()=>{setLogRoastId(logRoastId===item.id?null:item.id);setLogRoastAmount("");}}>Log roasted</button>
 <button style={{...s.btnSm,marginRight:4}} onClick={()=>startEdit(item)}>Edit</button>
 {runningLow(item) && <button style={{...s.btnSm,marginRight:4,color:C.amber,borderColor:C.amber}} onClick={()=>draftReorderEmail(item)}>Draft reorder</button>}
 <button style={{...s.btnSm,color:C.red,borderColor:C.red}} onClick={()=>remove(item.id)}>Remove</button>
 </td>
 <td style={s.td}>{item.origin||"—"}</td>
 <td style={s.td}>{item.process||"—"}</td>
 <td style={{...s.td,color:item.masl?C.ink:C.inkFaint,fontWeight:item.masl?500:400}}>{item.masl||"—"}</td>
 <td style={{...s.td,color:item.moisture?C.ink:C.inkFaint}}>{item.moisture?item.moisture+"%":"—"}</td>
 <td style={{...s.td,color:item.density?C.ink:C.inkFaint}}>{item.density?item.density+" g/L":"—"}</td>
 <td style={s.td}>{item.cultivar||"—"}</td>
 <td style={s.td}>{greenInFor(item)}kg</td>
 <td style={{...s.td,color:totalRoastedFor(item)?C.ink:C.inkFaint}}>{totalRoastedFor(item)?totalRoastedFor(item)+"kg":"—"}</td>
 <td style={{...s.td,fontWeight:700,color: runningLow(item) ? C.red : C.ink}}>{totalKg(item)}kg{runningLow(item) && " ⚠"}</td>
 <td style={s.td}>{item.costPerKg?"€"+item.costPerKg:"—"}</td>
 <td style={s.td}>{item.costPerKg?"€"+Math.round(totalKg(item)*parseFloat(item.costPerKg)):"—"}</td>
 <td style={{...s.td,color:C.inkFaint}}>{item.arrived||"—"}</td>
 </tr>
 {logRoastId===item.id && (
 <tr style={{borderBottom:`1px solid ${C.borderSoft}`,background:"#FDF6E8"}}>
 <td colSpan={12} style={{padding:"10px 16px"}}>
 <div style={{display:"flex",gap:10,alignItems:"center"}}>
 <div style={{fontFamily:fontSans,fontSize:12,color:C.inkMid}}>Roasted amount (kg), deducted from green stock:</div>
 <input style={{...s.input,width:100}} type="number" step="0.1" autoFocus value={logRoastAmount} onChange={e=>setLogRoastAmount(e.target.value)} placeholder="e.g. 30"/>
 <button style={s.btnPrimary} onClick={()=>logRoasted(item.id)}>Log it</button>
 <button style={s.btn} onClick={()=>{setLogRoastId(null);setLogRoastAmount("");}}>Cancel</button>
 </div>
 </td>
 </tr>
 )}
 </Fragment>
 ))}
 </tbody>
 </table>
 </div>
 </>
 )}

 </div>
 );

}


// ── MONTH-END STOCK REPORT (Green Suite) ─────────────────────────────────────
// A read-only snapshot "as at" a chosen date (default today): green coffee on
// hand, finished (bagged, not yet gone out) coffee, and labels/bags used and
// on hand — each with its value at COST. Nothing is stored or changed here.
//   • Green value      = kg on hand × the coffee's cost per kg (Green stock).
//   • Finished value   = bags × (bag kg × green cost per roasted kg + label + bag).
//     Green cost per roasted kg = cost per kg ÷ (1 − roast loss), using the
//     roast loss on the Pricing tab. Labour, gas and rent are not included.
//   • Packaging used   = bags packed during the month (one label + one bag per
//     bag, taken off Packaging materials when a batch is packed). On hand = the
//     quantity left in Packaging materials.
function MonthEndReportTab({ inventory }) {
  const [asAt, setAsAt] = useState(localIsoDate());
  const monthKey = String(asAt || "").slice(0, 7);
  const eur = (n) => (n == null || isNaN(n)) ? "—" : "€" + Number(n).toLocaleString("en-IE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const num1 = (n) => Math.round(n * 10) / 10;

  const batches = loadPackagingBatches();
  const allocations = loadAllocations();
  const pkgItems = loadPackagingStock();
  const counts = loadStocktakeCounts();
  const lossPct = (() => {
    try { const v = parseFloat(JSON.parse(localStorage.getItem("fdc_pricing_settings") || "{}").roastLoss); return isNaN(v) ? 13.5 : v; } catch { return 13.5; }
  })();

  const kgOf = (i) => (parseFloat(i.bagWeight) || 0) * (parseFloat(i.bags) || 0);
  const greenRows = inventory.map(i => {
    const kg = num1(kgOf(i));
    const cost = parseFloat(i.costPerKg);
    const c = counts[i.id];
    const counted = c && c.actual !== "" && c.actual != null ? parseFloat(c.actual) : null;
    return { name: (i.name || i.origin || "—").trim(), kg, cost: isNaN(cost) ? null : cost, value: isNaN(cost) ? null : kg * cost, counted: isNaN(counted) ? null : counted, countDate: c?.date || "" };
  });
  const greenTotal = greenRows.reduce((a, r) => a + (r.value || 0), 0);
  const greenKgTotal = greenRows.reduce((a, r) => a + r.kg, 0);

  const sizeTag = (g) => g >= 1000 ? `${g / 1000}kg` : `${g}g`;
  const findPkg = (type, g) => {
    const t = sizeTag(g);
    return pkgItems.find(it => it.type === type && String(it.name || "").toLowerCase().replace(/\s+/g, "").includes(t)) || null;
  };
  const unit = (it) => { const v = parseFloat(it?.costPerUnit); return isNaN(v) ? null : v; };
  const costFor = (origin) => {
    const it = inventory.find(i => sameCoffee(i.name, origin)) || inventory.find(i => coffeeNamesMatch(i.name, origin)) || inventory.find(i => coffeeNamesMatch(i.origin, origin) && false);
    const v = it ? parseFloat(it.costPerKg) : NaN;
    return isNaN(v) ? null : v;
  };

  const warnings = [];
  const finishedRows = computeFinishedGoodsByProduct(batches, allocations).filter(p => p.available > 0).map(p => {
    const greenCost = costFor(p.origin);
    const lab = unit(findPkg("Labels", p.bagSizeG)), bag = unit(findPkg("Bags", p.bagSizeG));
    let perBag = null;
    if (greenCost != null && lab != null && bag != null) perBag = (p.bagSizeG / 1000) * (greenCost / (1 - lossPct / 100)) + lab + bag;
    if (perBag == null) warnings.push(`${bagSizeText(p.bagSizeG)} ${String(p.origin || "").trim()} — can't price (${greenCost == null ? "no green cost found" : "missing label or bag price"}).`);
    return { label: `${bagSizeText(p.bagSizeG)} ${String(p.origin || "").trim()}`, bags: p.available, perBag, value: perBag == null ? null : perBag * p.available };
  });
  const finishedTotal = finishedRows.reduce((a, r) => a + (r.value || 0), 0);

  // Bags packed during the month, by bag size — one label and one bag each.
  const outBySize = {};
  batches.forEach(b => {
    if (!b.packagedDate || String(b.packagedDate).slice(0, 7) !== monthKey || String(b.packagedDate) > asAt) return;
    const g = parseInt(b.bagSizeG); if (!g) return;
    outBySize[g] = (outBySize[g] || 0) + (parseInt(b.qtyBags) || 0);
  });
  const sizes = Array.from(new Set([250, 1000, ...Object.keys(outBySize).map(Number)])).sort((a, b) => a - b);
  const pkgRows = [];
  sizes.forEach(g => ["Labels", "Bags"].forEach(type => {
    const it = findPkg(type, g); const u = unit(it);
    const used = outBySize[g] || 0;
    const onHand = it && it.quantity !== "" && it.quantity != null ? parseFloat(it.quantity) : null;
    pkgRows.push({ label: `${sizeTag(g)} ${type.toLowerCase()}`, used, usedValue: u == null ? null : used * u, onHand, onHandValue: u == null || onHand == null ? null : onHand * u, tracked: !!it });
    if (!it) warnings.push(`${sizeTag(g)} ${type.toLowerCase()} — not set up in Packaging materials.`);
    else if (u == null) warnings.push(`${it.name} — no cost per unit.`);
  }));
  const pkgUsedTotal = pkgRows.reduce((a, r) => a + (r.usedValue || 0), 0);
  const pkgHandTotal = pkgRows.reduce((a, r) => a + (r.onHandValue || 0), 0);
  const grand = greenTotal + finishedTotal + pkgHandTotal;
  greenRows.forEach(r => { if (r.value == null) warnings.push(`${r.name} — no cost per kg in Green stock.`); });
  const uniqueWarnings = Array.from(new Set(warnings));

  const monthName = (() => { const [y, m] = String(asAt).split("-"); return y && m ? new Date(+y, +m - 1, 1).toLocaleString("en-IE", { month: "long", year: "numeric" }) : ""; })();

  const exportCSV = () => {
    const q = (v) => { const t = String(v ?? ""); return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };
    const rows = [["Month-end stock report", `as at ${dmy(asAt)}`], [],
      ["GREEN COFFEE ON HAND"], ["Coffee", "kg on hand", "€/kg", "Value €", "Last counted kg", "Count date"],
      ...greenRows.map(r => [r.name, r.kg, r.cost ?? "", r.value == null ? "" : r.value.toFixed(2), r.counted ?? "", r.countDate ? dmy(r.countDate) : ""]),
      ["Total", num1(greenKgTotal), "", greenTotal.toFixed(2)], [],
      ["FINISHED PRODUCT (bagged, not yet gone out) — at cost"], ["Product", "Bags", "Cost per bag €", "Value €"],
      ...finishedRows.map(r => [r.label, r.bags, r.perBag == null ? "" : r.perBag.toFixed(2), r.value == null ? "" : r.value.toFixed(2)]),
      ["Total", "", "", finishedTotal.toFixed(2)], [],
      [`PACKAGING — used in ${monthName} = bags packed`], ["Item", "Used", "Used value €", "On hand", "On hand value €"],
      ...pkgRows.map(r => [r.label, r.used, r.usedValue == null ? "" : r.usedValue.toFixed(2), r.onHand ?? "", r.onHandValue == null ? "" : r.onHandValue.toFixed(2)]),
      ["Total", "", pkgUsedTotal.toFixed(2), "", pkgHandTotal.toFixed(2)], [],
      ["TOTAL STOCK VALUE (green + finished + packaging on hand) €", grand.toFixed(2)],
    ];
    downloadTextFile(`stock-report-${asAt}.csv`, "﻿" + rows.map(r => r.map(q).join(",")).join("\n"), "text/csv;charset=utf-8;");
  };

  const exportPDF = () => {
    const td = (v, cls) => `<td class="${cls || ""}">${v}</td>`;
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Month-end stock report — ${dmy(asAt)}</title>
<style>
 @page { margin: 16mm 18mm; size: A4; }
 * { box-sizing:border-box; margin:0; padding:0; }
 body { font-family:-apple-system,"Helvetica Neue",Arial,sans-serif; font-size:12px; color:#1C1814; padding:18px 0; }
 h1 { font-size:22px; margin-bottom:4px; } .sub { font-size:11px; color:#7A6E63; padding-bottom:10px; margin-bottom:6px; border-bottom:2px solid #1C1814; }
 h2 { font-size:11px; text-transform:uppercase; letter-spacing:0.08em; color:#7A6E63; margin:20px 0 6px; }
 table { width:100%; border-collapse:collapse; } th { text-align:left; font-size:10px; text-transform:uppercase; letter-spacing:0.05em; color:#7A6E63; padding:6px 8px; border-bottom:1.5px solid #1C1814; }
 td { padding:7px 8px; border-bottom:1px solid #EDE8DE; } td.n, th.n { text-align:right; font-variant-numeric:tabular-nums; } tr.tot td { font-weight:700; border-top:1.5px solid #1C1814; border-bottom:none; }
 .grand { margin-top:22px; padding:12px 14px; border:1.5px solid #1C1814; display:flex; justify-content:space-between; font-size:15px; font-weight:700; }
 .warn { margin-top:14px; font-size:10.5px; color:#9A5A00; } .foot { margin-top:26px; font-size:10px; color:#A89E93; border-top:1px solid #D5CCBE; padding-top:8px; }
 @media print { body { -webkit-print-color-adjust:exact; print-color-adjust:exact; padding:0; } }
</style></head><body>
<h1>Month-end stock report</h1><div class="sub">Skelligs Coffee &nbsp;·&nbsp; as at ${dmy(asAt)} &nbsp;·&nbsp; valued at cost</div>
<h2>Green coffee on hand</h2>
<table><thead><tr><th>Coffee</th><th class="n">kg</th><th class="n">€ / kg</th><th class="n">Value</th></tr></thead><tbody>
${greenRows.map(r => `<tr>${td(r.name)}${td(r.kg, "n")}${td(r.cost == null ? "—" : eur(r.cost), "n")}${td(eur(r.value), "n")}</tr>`).join("")}
<tr class="tot">${td("Total")}${td(num1(greenKgTotal), "n")}${td("", "n")}${td(eur(greenTotal), "n")}</tr></tbody></table>
<h2>Finished product — bagged, not yet gone out</h2>
<table><thead><tr><th>Product</th><th class="n">Bags</th><th class="n">Cost / bag</th><th class="n">Value</th></tr></thead><tbody>
${finishedRows.map(r => `<tr>${td(r.label)}${td(r.bags, "n")}${td(eur(r.perBag), "n")}${td(eur(r.value), "n")}</tr>`).join("") || `<tr><td colspan="4">None</td></tr>`}
<tr class="tot">${td("Total")}${td("", "n")}${td("", "n")}${td(eur(finishedTotal), "n")}</tr></tbody></table>
<h2>Labels &amp; bags — used in ${monthName} (bags packed) and on hand</h2>
<table><thead><tr><th>Item</th><th class="n">Used</th><th class="n">Used value</th><th class="n">On hand</th><th class="n">On hand value</th></tr></thead><tbody>
${pkgRows.map(r => `<tr>${td(r.label)}${td(r.used, "n")}${td(eur(r.usedValue), "n")}${td(r.onHand ?? "—", "n")}${td(eur(r.onHandValue), "n")}</tr>`).join("")}
<tr class="tot">${td("Total")}${td("", "n")}${td(eur(pkgUsedTotal), "n")}${td("", "n")}${td(eur(pkgHandTotal), "n")}</tr></tbody></table>
<div class="grand"><span>Total stock value (green + finished + packaging on hand)</span><span>${eur(grand)}</span></div>
${uniqueWarnings.length ? `<div class="warn"><strong>Incomplete:</strong> ${uniqueWarnings.join(" · ")}</div>` : ""}
<div class="foot">Finished product at cost: green cost ÷ (1 − ${lossPct}% roast loss) + label + bag. Labour, gas and rent not included. Roasted coffee not yet bagged is not tracked in the Hub. · Generated ${todayDmy()}</div>
</body></html>`;
    const win = window.open("", "_blank");
    if (!win) { window.alert("Please allow pop-ups for this site to print the report."); return; }
    win.document.write(html); win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 400);
  };

  const th = (h, right) => <th key={h} style={{ ...s.thStyle, textAlign: right ? "right" : "left" }}>{h}</th>;
  const tdR = { ...s.td, textAlign: "right", fontVariantNumeric: "tabular-nums" };
  const block = (title, children) => (
    <div style={{ marginBottom: 22 }}>
      <div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, marginBottom: 8 }}>{title}</div>
      <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflowX: "auto" }}>{children}</div>
    </div>
  );
  const totRow = (cells) => <tr style={{ background: C.cream3 }}>{cells.map((c, i) => <td key={i} style={{ ...(i === 0 ? s.td : tdR), fontWeight: 700 }}>{c}</td>)}</tr>;

  return (
    <div style={{ paddingTop: 24, maxWidth: 900 }}>
      <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 16, lineHeight: 1.5 }}>
        A snapshot of stock as at the date below, valued at cost. It reads what's already in the Hub and changes nothing. Run it on the last day of the month.
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 18, flexWrap: "wrap" }}>
        <label style={{ fontFamily: fontSans, fontSize: 12, color: C.inkMid }}>As at</label>
        <input type="date" style={{ ...s.input, width: 160 }} value={asAt} onChange={e => setAsAt(e.target.value || localIsoDate())} />
        <button style={s.btnPrimary} onClick={exportPDF}>Export PDF</button>
        <button style={s.btnPrimary} onClick={exportCSV}>Export CSV</button>
      </div>

      {uniqueWarnings.length > 0 && (
        <div style={{ background: C.cream2, border: `1px solid ${C.amber}`, borderLeft: `3px solid ${C.amber}`, borderRadius: 6, padding: "12px 16px", marginBottom: 20, fontFamily: fontSans, fontSize: 12, color: C.inkMid, lineHeight: 1.6 }}>
          <div style={{ fontSize: 10, color: C.amber, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 6 }}>Incomplete — totals will be short</div>
          {uniqueWarnings.map((w, i) => <div key={i}>{w}</div>)}
        </div>
      )}

      {block("Green coffee on hand", (
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
          <thead><tr style={{ background: C.cream3 }}>{th("Coffee")}{th("kg", true)}{th("€ / kg", true)}{th("Value", true)}{th("Last counted", true)}</tr></thead>
          <tbody>
            {greenRows.map((r, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                <td style={{ ...s.td, fontWeight: 600, color: C.amber }}>{r.name}</td>
                <td style={tdR}>{r.kg}</td><td style={tdR}>{r.cost == null ? "—" : eur(r.cost)}</td><td style={{ ...tdR, fontWeight: 600 }}>{eur(r.value)}</td>
                <td style={{ ...tdR, color: C.inkFaint }}>{r.counted == null ? "—" : `${r.counted} kg · ${dmy(r.countDate)}`}</td>
              </tr>
            ))}
            {totRow(["Total", num1(greenKgTotal), "", eur(greenTotal), ""])}
          </tbody>
        </table>
      ))}

      {block("Finished product — bagged, not yet gone out (at cost)", (
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
          <thead><tr style={{ background: C.cream3 }}>{th("Product")}{th("Bags", true)}{th("Cost / bag", true)}{th("Value", true)}</tr></thead>
          <tbody>
            {finishedRows.length === 0 && <tr><td style={s.td} colSpan={4}>No bagged coffee in stock.</td></tr>}
            {finishedRows.map((r, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                <td style={{ ...s.td, fontWeight: 600, color: C.amber }}>{r.label}</td><td style={tdR}>{r.bags}</td><td style={tdR}>{eur(r.perBag)}</td><td style={{ ...tdR, fontWeight: 600 }}>{eur(r.value)}</td>
              </tr>
            ))}
            {totRow(["Total", "", "", eur(finishedTotal)])}
          </tbody>
        </table>
      ))}

      {block(`Labels & bags — used in ${monthName} (bags packed) and on hand`, (
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
          <thead><tr style={{ background: C.cream3 }}>{th("Item")}{th("Used", true)}{th("Used value", true)}{th("On hand", true)}{th("On hand value", true)}</tr></thead>
          <tbody>
            {pkgRows.map((r, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                <td style={{ ...s.td, fontWeight: 600, color: C.amber }}>{r.label}</td><td style={tdR}>{r.used}</td><td style={tdR}>{eur(r.usedValue)}</td><td style={tdR}>{r.onHand ?? "—"}</td><td style={{ ...tdR, fontWeight: 600 }}>{eur(r.onHandValue)}</td>
              </tr>
            ))}
            {totRow(["Total", "", eur(pkgUsedTotal), "", eur(pkgHandTotal)])}
          </tbody>
        </table>
      ))}

      <div style={{ border: `1.5px solid ${C.ink}`, borderRadius: 8, padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "baseline", fontFamily: fontSans }}>
        <span style={{ fontSize: 12, color: C.inkMid }}>Total stock value — green + finished + packaging on hand</span>
        <span style={{ fontFamily: fontSerif, fontSize: 26, color: C.ink }}>{eur(grand)}</span>
      </div>
      <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginTop: 10, lineHeight: 1.5 }}>
        Finished product at cost: green cost ÷ (1 − {lossPct}% roast loss) + label + bag. Labour, gas and rent are not included. Roasted coffee not yet bagged isn't tracked in the Hub and isn't counted.
      </div>
    </div>
  );
}

// ── STOCKTAKE (Green Suite) ───────────────────────────────────────────────────
// Moved out of the Green stock page into its own tab. Same stored counts
// (fdc_stocktake_counts), same exports, same printable sheet — nothing about
// how counts are saved has changed.
function StocktakeTab({ inventory, setInventory }) {
  const [stocktakeCounts, setStocktakeCounts] = useState(loadStocktakeCounts);
  const setStocktakeCount = (itemId, val) => {
    const updated = { ...stocktakeCounts, [itemId]: { actual: val, date: localIsoDate() } };
    setStocktakeCounts(updated);
    saveStocktakeCounts(updated);
  };
  const totalKg = item => Math.round(parseFloat(item.bagWeight || 0) * parseFloat(item.bags || 0) * 10) / 10;
  const totalRoastedFor = item => Math.round((parseFloat(item.totalRoastedKg) || 0) * 10) / 10;
  const greenInFor = item => Math.round((totalKg(item) + totalRoastedFor(item)) * 10) / 10;
  // Makes the counted amount the Hub's stock figure — only after a clear yes.
  const applyCount = (item, actual) => {
    if (!setInventory) return;
    const was = totalKg(item);
    if (!window.confirm(`Set ${item.name || item.origin} to ${actual} kg in Green stock?\n\nThe Hub currently has ${was} kg. This changes the stock figure — roast history is not touched.`)) return;
    const bw = parseFloat(item.bagWeight) || 0;
    const upInv = inventory.map(i => i.id === item.id
      ? (bw > 0 ? { ...i, bags: Math.round(actual / bw * 1000) / 1000 } : { ...i, bagWeight: "1", bags: actual })
      : i);
    setInventory(upInv); saveInventory(upInv);
    const updated = { ...stocktakeCounts, [item.id]: { ...(stocktakeCounts[item.id] || {}), actual: String(actual), applied: true, appliedFrom: was, date: localIsoDate() } };
    setStocktakeCounts(updated); saveStocktakeCounts(updated);
  };

  // Bagged coffee, grouped by product (same grouping as Finished goods).
  // "Expected on shelf" = packaged minus allocated. Counts are stored in the
  // same place as green counts, under their own "bag:" keys, so they never
  // mix with a green coffee's count.
  const [bagBatches] = useState(loadPackagingBatches);
  const [bagAllocations] = useState(loadAllocations);
  const bagRows = computeFinishedGoodsByProduct(bagBatches, bagAllocations)
    .filter(p => p.packaged > 0)
    .map(p => {
      const count = stocktakeCounts["bag:" + p.code];
      const actual = count && count.actual !== "" && count.actual != null ? parseInt(count.actual) : null;
      return { key: "bag:" + p.code, code: p.code, label: productLabel(p.origin, p.bagSizeG), packaged: p.packaged, expected: p.available, actual: isNaN(actual) ? null : actual, actualStr: count?.actual ?? "", date: count?.date || "" };
    });

  // Compact printable sheet for a quick physical stock take — just the
 // three headline numbers per coffee, plus blank columns to write the
 // actual counted amount and the difference against what the Hub expects.
  const printStocktakeSheet = () => {
 const dateStr = todayDmy();
 const rows = inventory.map(item => `
 <tr>
 <td class="name">${item.name||item.origin||"—"}</td>
 <td class="num">${greenInFor(item)}</td>
 <td class="num">${totalRoastedFor(item)}</td>
 <td class="num bold">${totalKg(item)}</td>
 <td class="blank"></td>
 <td class="blank"></td>
 </tr>`).join("");
 const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Stocktake Sheet — ${dateStr}</title>
<style>
 @page { margin: 16mm 18mm; size: A4 landscape; }
 * { box-sizing: border-box; margin:0; padding:0; }
 body { font-family: -apple-system, "Helvetica Neue", Arial, sans-serif; font-size: 12px; color:#1C1814; background:#fff; padding: 24px 0; }
 h1 { font-size: 22px; margin-bottom: 4px; }
 .sub { font-size: 11px; color:#7A6E63; padding-bottom: 10px; margin-bottom: 14px; border-bottom: 2px solid #1C1814; }
 .meta-row { display:flex; gap:40px; font-size:12px; color:#4A3F35; margin-bottom:18px; }
 .meta-row span { border-bottom:1px solid #999; display:inline-block; min-width:180px; padding-bottom:2px; }
 table { width:100%; border-collapse:collapse; }
 th { text-align:left; font-size:10px; text-transform:uppercase; letter-spacing:0.06em; color:#7A6E63; padding:8px 10px; border-bottom:2px solid #1C1814; }
 td { padding:11px 10px; border-bottom:1px solid #EDE8DE; vertical-align:middle; }
 td.name { font-weight:600; }
 td.num { text-align:right; font-family:monospace; }
 td.num.bold { font-weight:700; }
 td.blank { border-bottom:1px solid #999; }
 .footer { margin-top:30px; font-size:10px; color:#A89E93; border-top:1px solid #D5CCBE; padding-top:8px; }
 .sign-row { display:flex; gap:60px; margin-top:44px; }
 .sign-row div { flex:1; border-top:1px solid #333; padding-top:4px; font-size:11px; color:#7A6E63; }
 @media print { body { -webkit-print-color-adjust:exact; print-color-adjust:exact; padding:0; } }
</style></head><body>
<h1>Stocktake Sheet</h1>
<div class="sub">Skelligs Coffee &nbsp;&middot;&nbsp; ${dateStr}</div>
<div class="meta-row">
 <div>Counted by: <span>&nbsp;</span></div>
 <div>Date completed: <span>&nbsp;</span></div>
</div>
<table>
 <thead><tr>
 <th>Coffee</th><th>Green In (kg)</th><th>Roasted (kg)</th><th>Expected Remaining (kg)</th><th>Actual Counted (kg)</th><th>Difference (kg)</th>
 </tr></thead>
 <tbody>${rows}</tbody>
</table>
${bagRows.length ? `<h1 style="font-size:16px;margin:28px 0 10px">Bagged coffee</h1>
<table>
 <thead><tr>
 <th>Product</th><th>Code</th><th>Packaged (bags)</th><th>Expected on shelf (bags)</th><th>Actual Counted (bags)</th><th>Difference (bags)</th>
 </tr></thead>
 <tbody>${bagRows.map(b => `<tr><td class="name">${b.label}</td><td>${b.code}</td><td class="num">${b.packaged}</td><td class="num bold">${b.expected}</td><td class="blank"></td><td class="blank"></td></tr>`).join("")}</tbody>
</table>` : ""}
<div class="sign-row">
 <div>Signature</div>
 <div>Notes</div>
</div>
<div class="footer">Skelligs Coffee &nbsp;&middot;&nbsp; Generated ${dateStr} &nbsp;&middot;&nbsp; fdc-roasting-hub.pages.dev</div>
</body></html>`;
 const win = window.open("", "_blank");
 if (!win) { window.alert("Please allow pop-ups for this site to print the stocktake sheet."); return; }
 win.document.write(html);
 win.document.close();
 setTimeout(() => { win.focus(); win.print(); }, 400);
 };

  return (
    <div style={{ paddingTop: 24 }}>
      <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 16, lineHeight: 1.5 }}>
        Enter what you actually count — green in kg, bagged coffee in bags — and see the difference against what the Hub expects. Counts save as you type and never change your stock figures on their own.
      </div>
      {inventory.length === 0 ? (
        <div style={{ fontFamily: fontSans, fontSize: 13, color: C.inkFaint }}>No coffees in Green stock yet.</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <button style={s.btnPrimary} onClick={printStocktakeSheet}>Print blank sheet</button>
            <button style={s.btnPrimary} onClick={() => exportStocktakePDF(inventory, greenInFor, totalRoastedFor, totalKg, stocktakeCounts, bagRows)}>Export PDF</button>
            <button style={s.btnPrimary} onClick={() => exportStocktakeCSV(inventory, greenInFor, totalRoastedFor, totalKg, stocktakeCounts, bagRows)}>Export CSV</button>
          </div>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
              <thead>
                <tr style={{ background: C.cream3 }}>
                  {["Coffee", "Green In", "Roasted", "Expected Remaining", "Actual Counted", "Difference"].map(h => (
                    <th key={h} style={s.thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {inventory.map(item => {
                  const count = stocktakeCounts[item.id];
                  const expected = totalKg(item);
                  const actualStr = count?.actual ?? "";
                  const actual = actualStr !== "" ? parseFloat(actualStr) : null;
                  const diff = actual != null ? Math.round((actual - expected) * 10) / 10 : null;
                  return (
                    <tr key={item.id} style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                      <td style={{ ...s.td, fontWeight: 600, color: C.amber }}>{item.name || item.origin || "—"}</td>
                      <td style={s.td}>{greenInFor(item)}kg</td>
                      <td style={s.td}>{totalRoastedFor(item)}kg</td>
                      <td style={{ ...s.td, fontWeight: 600 }}>{expected}kg</td>
                      <td style={s.td}>
                        <input style={{ ...s.input, width: 100 }} type="number" step="0.1" value={actualStr} onChange={e => setStocktakeCount(item.id, e.target.value)} placeholder="—" />
                      </td>
                      <td style={{ ...s.td, fontWeight: 600, color: diff == null ? C.inkFaint : diff === 0 ? C.green : C.red }}>
                        {diff == null ? "—" : (diff > 0 ? "+" : "") + diff + "kg"}
                        {diff != null && diff !== 0 && setInventory && (
                          <button onClick={() => applyCount(item, actual)} style={{ ...s.btnSm, marginLeft: 10, fontWeight: 600 }} title="Make the counted amount the Hub's stock figure">Apply count</button>
                        )}
                        {diff === 0 && count?.applied && <span style={{ fontSize: 11, fontWeight: 400, color: C.inkFaint, marginLeft: 8 }}>applied (was {count.appliedFrom}kg)</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em", margin: "28px 0 10px" }}>Bagged coffee</div>
      {bagRows.length === 0 ? (
        <div style={{ fontFamily: fontSans, fontSize: 13, color: C.inkFaint }}>No bagged coffee yet.</div>
      ) : (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
            <thead>
              <tr style={{ background: C.cream3 }}>
                {["Product", "Code", "Packaged", "Expected on shelf", "Actual counted", "Difference"].map(h => (
                  <th key={h} style={s.thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bagRows.map(b => {
                const diff = b.actual != null ? b.actual - b.expected : null;
                return (
                  <tr key={b.key} style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                    <td style={{ ...s.td, fontWeight: 600, color: C.amber }}>{b.label}</td>
                    <td style={{ ...s.td, fontFamily: "monospace", fontSize: 12 }}>{b.code}</td>
                    <td style={s.td}>{b.packaged} bags</td>
                    <td style={{ ...s.td, fontWeight: 600 }}>{b.expected} bags</td>
                    <td style={s.td}>
                      <input style={{ ...s.input, width: 100 }} type="number" step="1" value={b.actualStr} onChange={e => setStocktakeCount(b.key, e.target.value)} placeholder="—" />
                    </td>
                    <td style={{ ...s.td, fontWeight: 600, color: diff == null ? C.inkFaint : diff === 0 ? C.green : C.red }}>
                      {diff == null ? "—" : (diff > 0 ? "+" : "") + diff + " bags"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginTop: 10, lineHeight: 1.5 }}>
        Expected on shelf is packaged minus allocated — bags promised to a customer but not yet sent still count as allocated.
      </div>
    </div>
  );
}

// ── PACKAGING STOCK ───────────────────────────────────────────────────────────
const PACKAGING_TYPES = ["Bags","Labels","Valves","Boxes","Stickers","Other"];

const PACKAGING_STOCK_DEFAULTS = [
 { id:1, name:"250g Labels", type:"Labels", quantity:"", costPerUnit:"", supplier:"" },
 { id:2, name:"1kg Labels", type:"Labels", quantity:"", costPerUnit:"", supplier:"" },
 { id:3, name:"250g Bags", type:"Bags", quantity:"", costPerUnit:"", supplier:"" },
 { id:4, name:"1kg Bags", type:"Bags", quantity:"", costPerUnit:"", supplier:"" },
];

// Which Packaging materials a bag size uses: any "Bags" or "Labels" item whose
// name carries that size ("250g Bags", "1kg Labels"…). Only items with a
// quantity entered are counted down — untracked items are left alone.
function packagingMaterialsFor(bagSizeG, items) {
  const tags = [bagSizeG >= 1000 ? `${bagSizeG / 1000}kg` : `${bagSizeG}g`, `${bagSizeG}g`];
  return items.filter(it => (it.type === "Bags" || it.type === "Labels")
    && tags.some(t => (it.name || "").toLowerCase().replace(/\s+/g, "").includes(t))
    && it.quantity !== "" && it.quantity != null && !isNaN(parseFloat(it.quantity)));
}
// Takes the materials for a packing run off Packaging materials and returns
// exactly what was taken, so an edit or a correction can put it back.
function takePackagingMaterials(bagSizeG, qtyBags) {
  const items = loadPackagingStock();
  const used = [];
  const updated = items.map(it => {
    if (!packagingMaterialsFor(bagSizeG, [it]).length) return it;
    const have = parseFloat(it.quantity) || 0;
    const take = Math.min(have, qtyBags);
    used.push({ id: it.id, name: it.name, qty: take });
    return { ...it, quantity: String(Math.round((have - take) * 100) / 100) };
  });
  if (used.length) savePackagingStock(updated);
  return used;
}
function returnPackagingMaterials(used) {
  if (!Array.isArray(used) || !used.length) return;
  const items = loadPackagingStock();
  savePackagingStock(items.map(it => {
    const u = used.find(x => x.id === it.id);
    return u ? { ...it, quantity: String(Math.round(((parseFloat(it.quantity) || 0) + u.qty) * 100) / 100) } : it;
  }));
}

function loadPackagingStock() {
 try {
 const saved = localStorage.getItem("fdc_packaging_stock");
 return saved ? JSON.parse(saved) : PACKAGING_STOCK_DEFAULTS;
 } catch { return PACKAGING_STOCK_DEFAULTS; }
}
function savePackagingStock(items) {
 localStorage.setItem("fdc_packaging_stock", JSON.stringify(items));
}

function PackagingStockTab() {
 const [items,setItems]=useState(loadPackagingStock);
 const [form,setForm]=useState({name:"",type:"Bags",quantity:"",costPerUnit:"",supplier:""});
 const [showForm,setShowForm]=useState(false);
 const [editId,setEditId]=useState(null);
 const [editVal,setEditVal]=useState({});
 const setF=(k,v)=>setForm(f=>({...f,[k]:v}));

 const totalQty=items.reduce((a,i)=>a+(parseFloat(i.quantity)||0),0);
 const totalValue=items.reduce((a,i)=>a+((parseFloat(i.quantity)||0)*(parseFloat(i.costPerUnit)||0)),0);

 const add=()=>{
 if(!form.name.trim())return;
 const next=[...items,{...form,id:Date.now()}];
 setItems(next);savePackagingStock(next);
 setForm({name:"",type:"Bags",quantity:"",costPerUnit:"",supplier:""});
 setShowForm(false);
 };
 const remove=id=>{
 const item=items.find(i=>i.id===id);
 const label=item?.name||"this item";
 if(!window.confirm(`Remove ${label} from packaging stock? This can't be undone.`))return;
 const next=items.filter(i=>i.id!==id);setItems(next);savePackagingStock(next);
 };
 const startEdit=item=>{setEditId(item.id);setEditVal({...item});};
 const saveEdit=()=>{const next=items.map(i=>i.id===editId?{...i,...editVal}:i);setItems(next);savePackagingStock(next);setEditId(null);};

 const exportPackagingPDF=()=>{
 const dateStr = todayDmy();
 const r2 = (label, value, bold) => `<tr><td class="lbl">${label}</td><td class="val${bold?" bld":""}">${value}</td></tr>`;
 const rows = items.map(item => `
 <tr><td class="lbl" style="padding-top:12px;font-weight:600;color:#1C1814">${item.name||"—"}</td><td class="val" style="padding-top:12px"></td></tr>
 ${r2("Type", item.type||"—")}
 ${r2("Quantity on hand", (item.quantity||0))}
 ${item.costPerUnit ? r2("Cost/unit", "€"+item.costPerUnit) : ""}
 ${item.costPerUnit ? r2("Stock value", "€"+Math.round((parseFloat(item.quantity)||0)*parseFloat(item.costPerUnit))) : ""}
 ${item.supplier ? r2("Supplier", item.supplier) : ""}
 `).join("");
 const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Packaging Stock Report — ${dateStr}</title>
<style>
 @page { margin: 18mm 20mm; size: A4 portrait; }
 * { box-sizing: border-box; margin: 0; padding: 0; }
 body { font-family: -apple-system, "Helvetica Neue", Arial, sans-serif; font-size: 13px; color: #1C1814; background: #fff; max-width: 680px; padding: 32px 0; }
 h1 { font-size: 24px; font-weight: 700; color: #1C1814; margin-bottom: 5px; }
 .sub { font-size: 12px; color: #7A6E63; padding-bottom: 16px; margin-bottom: 24px; border-bottom: 2px solid #1C1814; }
 table { width: 100%; border-collapse: collapse; }
 td { padding: 7px 0; vertical-align: middle; border-bottom: 1px solid #EDE8DE; line-height: 1.3; }
 td.lbl { color: #4A3F35; width: 58%; padding-left: 14px; font-size: 13px; }
 td.val { color: #1C1814; text-align: right; font-size: 13px; }
 td.val.bld { font-weight: 700; }
 .footer { margin-top: 36px; font-size: 10px; color: #A89E93; border-top: 1px solid #D5CCBE; padding-top: 10px; }
 @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; padding: 0; } }
</style></head><body>
<h1>Packaging Stock Report</h1>
<div class="sub">Skelligs Coffee &nbsp;&middot;&nbsp; ${dateStr}</div>
<table>
 ${r2("Items tracked", items.length)}
 ${r2("Total units on hand", totalQty, true)}
 ${totalValue > 0 ? r2("Estimated stock value", "€"+Math.round(totalValue)) : ""}
 ${rows}
</table>
<div class="footer">Skelligs Coffee &nbsp;&middot;&nbsp; Generated ${dateStr} &nbsp;&middot;&nbsp; fdc-roasting-hub.pages.dev</div>
</body></html>`;
 const win = window.open("", "_blank");
 if (!win) { window.alert("Please allow pop-ups for this site to export the report."); return; }
 win.document.write(html);
 win.document.close();
 setTimeout(() => { win.focus(); win.print(); }, 400);
 };

 const exportPackagingCSV=()=>{
 try {
 const esc = v => `"${String(v??"").replace(/"/g,'""')}"`;
 const header = ["Item","Type","Quantity","Cost per unit","Value","Supplier"].join(",");
 const rows = items.map(item => [
 esc(item.name), esc(item.type), esc(item.quantity||0), esc(item.costPerUnit||""),
 esc(item.costPerUnit ? Math.round((parseFloat(item.quantity)||0)*parseFloat(item.costPerUnit)) : ""),
 esc(item.supplier||""),
 ].join(","));
 const csv = [header, ...rows].join("\n");
 const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
 const url = URL.createObjectURL(blob);
 const a = document.createElement("a");
 a.href = url; a.download = `packaging-stock-${localIsoDate()}.csv`;
 a.click(); URL.revokeObjectURL(url);
 } catch { window.alert("Couldn't export CSV."); }
 };

 return (
 <div>
 <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
 <div style={s.sectionLabel}>Packaging stock</div>
 <button style={s.btnPrimary} onClick={()=>setShowForm(f=>!f)}>{showForm?"Cancel":"+ Add item"}</button>
 </div>

 <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:20}}>
 {[["Items tracked",items.length],["Total units on hand",totalQty],["Estimated stock value","€"+Math.round(totalValue)]].map(([label,val])=>(
 <div key={label} style={{background:C.cream2,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 16px"}}>
 <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>{label}</div>
 <div style={{fontFamily:fontSerif,fontSize:26,color:C.ink}}>{val}</div>
 </div>
 ))}
 </div>

 {showForm&&(
 <div style={{background:C.cream2,border:`1px solid ${C.border}`,borderRadius:8,padding:"16px 18px",marginBottom:16}}>
 <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:10,marginBottom:14}}>
 <div><div style={s.fieldLabel}>Item name</div><input style={s.input} value={form.name} onChange={e=>setF("name",e.target.value)} placeholder="e.g. 250g kraft bags"/></div>
 <div><div style={s.fieldLabel}>Type</div><select style={s.input} value={form.type} onChange={e=>setF("type",e.target.value)}>{PACKAGING_TYPES.map(o=><option key={o}>{o}</option>)}</select></div>
 <div><div style={s.fieldLabel}>Quantity on hand</div><input style={s.input} type="number" value={form.quantity} onChange={e=>setF("quantity",e.target.value)} placeholder="1000"/></div>
 <div><div style={s.fieldLabel}>Cost per unit (€)</div><input style={s.input} type="number" step="0.01" value={form.costPerUnit} onChange={e=>setF("costPerUnit",e.target.value)} placeholder="0.18"/></div>
 </div>
 <div style={{marginBottom:14}}>
 <div style={s.fieldLabel}>Supplier (optional)</div>
 <input style={{...s.input,maxWidth:320}} value={form.supplier} onChange={e=>setF("supplier",e.target.value)} placeholder="e.g. Packaging co."/>
 </div>
 <button style={s.btnPrimary} onClick={add}>Add to packaging stock</button>
 </div>
 )}

 {items.length===0?(
 <div style={{fontFamily:fontSans,fontSize:13,color:C.inkFaint,padding:"1rem 0"}}>No packaging items tracked yet. Add one above.</div>
 ):(
 <>
 <div style={{display:"flex",gap:10,marginBottom:12}}>
 <button style={s.btnPrimary} onClick={exportPackagingPDF}>Export PDF</button>
 <button style={s.btnPrimary} onClick={exportPackagingCSV}>Export CSV</button>
 </div>
 <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflowX:"auto"}}>
 <table style={{width:"100%",borderCollapse:"collapse",minWidth:800}}>
 <thead>
 <tr style={{background:C.cream3}}>
 {["Item","","Type","Quantity","Cost/unit","Value","Supplier"].map(h=>(
 <th key={h} style={s.thStyle}>{h}</th>
 ))}
 </tr>
 </thead>
 <tbody>
 {items.map(item=>editId===item.id?(
 <tr key={item.id} style={{background:"#FDF6E8"}}>
 <td colSpan={7} style={{padding:"12px 16px"}}>
 <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:8,marginBottom:10}}>
 <div><div style={s.fieldLabel}>Item name</div><input style={s.input} value={editVal.name||""} onChange={e=>setEditVal(v=>({...v,name:e.target.value}))}/></div>
 <div><div style={s.fieldLabel}>Type</div><select style={s.input} value={editVal.type||""} onChange={e=>setEditVal(v=>({...v,type:e.target.value}))}>{PACKAGING_TYPES.map(o=><option key={o}>{o}</option>)}</select></div>
 <div><div style={s.fieldLabel}>Quantity</div><input style={s.input} type="number" value={editVal.quantity||""} onChange={e=>setEditVal(v=>({...v,quantity:e.target.value}))}/></div>
 <div><div style={s.fieldLabel}>Cost per unit (€)</div><input style={s.input} type="number" step="0.01" value={editVal.costPerUnit||""} onChange={e=>setEditVal(v=>({...v,costPerUnit:e.target.value}))}/></div>
 </div>
 <div style={{marginBottom:10}}>
 <div style={s.fieldLabel}>Supplier</div>
 <input style={{...s.input,maxWidth:320}} value={editVal.supplier||""} onChange={e=>setEditVal(v=>({...v,supplier:e.target.value}))}/>
 </div>
 <div style={{display:"flex",gap:8}}>
 <button style={s.btnPrimary} onClick={saveEdit}>Save changes</button>
 <button style={s.btn} onClick={()=>setEditId(null)}>Cancel</button>
 </div>
 </td>
 </tr>
 ):(
 <tr key={item.id} style={{borderBottom:`1px solid ${C.borderSoft}`,cursor:"pointer"}}
 onMouseEnter={e=>e.currentTarget.style.background=C.cream2}
 onMouseLeave={e=>e.currentTarget.style.background=""}>
 <td style={{...s.td,color:C.amber,fontWeight:600}}>{item.name||"—"}</td>
 <td style={{...s.td,whiteSpace:"nowrap"}}>
 <button style={{...s.btnSm,marginRight:4}} onClick={()=>startEdit(item)}>Edit</button>
 <button style={{...s.btnSm,color:C.red,borderColor:C.red}} onClick={()=>remove(item.id)}>Remove</button>
 </td>
 <td style={s.td}>{item.type||"—"}</td>
 <td style={s.td}>{item.quantity||0}</td>
 <td style={s.td}>{item.costPerUnit?"€"+item.costPerUnit:"—"}</td>
 <td style={s.td}>{item.costPerUnit?"€"+Math.round((parseFloat(item.quantity)||0)*parseFloat(item.costPerUnit)):"—"}</td>
 <td style={s.td}>{item.supplier||"—"}</td>
 </tr>
 )
 )}
 </tbody>
 </table>
 </div>
 </>
 )}
 </div>
 );
}


// ── PRICING / COGS CALCULATOR ─────────────────────────────────────────────────

// Pricing rows keep their own name and share %, but a row that matches a
// coffee in Green stock reads that coffee's cost per kg live — so changing a
// price in Green stock updates Pricing automatically. Nothing stored is
// rewritten; a row with no match (or a coffee with no cost set) falls back
// to the cost typed on the row itself, exactly as before.
function greenCostLinkFor(row, inventory) {
  if (!row || !Array.isArray(inventory) || inventory.length === 0) return null;
  const nm = (row.name || "").trim().toLowerCase();
  const byId = inventory.find(i => String(i.id) === String(row.id) && (!nm || coffeeNamesMatch(i.name || "", row.name)));
  const byName = nm ? (inventory.find(i => (i.name || "").trim().toLowerCase() === nm) || inventory.find(i => coffeeNamesMatch(i.name || "", row.name))) : null;
  const item = byId || byName;
  return item && parseFloat(item.costPerKg) > 0 ? item : null;
}
function effectiveGreenCost(row, inventory) {
  const linked = greenCostLinkFor(row, inventory);
  return linked ? parseFloat(linked.costPerKg) : (parseFloat(row.costPerKg) || 0);
}

function Pricing({ inventory }) {
  const fs = fontSans;

  // Every input on this page used to live in local React state only —
  // switching tabs and coming back reset everything except the origins
  // list. Now the whole set persists to fdc_pricing_settings so it
  // survives navigation, and so Inventory's cost/profit summary strip can
  // read the same numbers actually entered here rather than duplicating
  // a second set of inputs.
  const _pSaved = (() => { try { return JSON.parse(localStorage.getItem("fdc_pricing_settings") || "{}"); } catch { return {}; } })();

  // ── GREEN COFFEE ──
  const [origins, setOrigins] = useState(() => {
    const saved = localStorage.getItem("fdc_pricing_origins");
    if (saved) return JSON.parse(saved);
    // Pre-populate from inventory if available
    return [{ id:1, name:"", costPerKg:"", share:100 }];
  });
  const [roastLoss, setRoastLoss] = useState(_pSaved.roastLoss ?? "13.5");

  // ── ROASTER THROUGHPUT ──
  const [roastsPerHour, setRoastsPerHour] = useState(_pSaved.roastsPerHour ?? "3");
  const [kgPerRoast, setKgPerRoast] = useState(_pSaved.kgPerRoast ?? "35");

  // ── SCHEDULE ──
  const [fullHrDay, setFullHrDay] = useState(_pSaved.fullHrDay ?? "8");
  const [fullDaysWk, setFullDaysWk] = useState(_pSaved.fullDaysWk ?? "5");
  const [fullWeeksYr, setFullWeeksYr] = useState(_pSaved.fullWeeksYr ?? "50");
  const [currHrDay, setCurrHrDay] = useState(_pSaved.currHrDay ?? "6");
  const [currDaysWk, setCurrDaysWk] = useState(_pSaved.currDaysWk ?? "2");
  const [currWeeksYr, setCurrWeeksYr] = useState(_pSaved.currWeeksYr ?? "48");

  // ── LABOUR ──
  const [labourRate, setLabourRate] = useState(_pSaved.labourRate ?? "13");
  const [employees, setEmployees] = useState(_pSaved.employees ?? "1");

  // ── FIXED COSTS ──
  const [rent, setRent] = useState(_pSaved.rent ?? "");
  const [gas, setGas] = useState(_pSaved.gas ?? "");
  const [electricity, setElectricity] = useState(_pSaved.electricity ?? "");
  const [overheads, setOverheads] = useState(_pSaved.overheads ?? [{ id:1, name:"Insurance", amount:"" }, { id:2, name:"Equipment depreciation", amount:"" }]);

  // ── PACKAGING ──
  const [pkg250, setPkg250] = useState(_pSaved.pkg250 ?? "0.55");
  const [pkg500, setPkg500] = useState(_pSaved.pkg500 ?? "0.75");
  const [pkg1000, setPkg1000] = useState(_pSaved.pkg1000 ?? "1.10");

  // ── PRICING ──
  const [retailMargin, setRetailMargin] = useState(_pSaved.retailMargin ?? "65");
  const [wholesaleMargin, setWholesaleMargin] = useState(_pSaved.wholesaleMargin ?? "35");
  // The prices actually charged, per bag size — when filled in, these are
  // used instead of the suggested prices, and the real margins are shown.
  const [ourPrices, setOurPrices] = useState(_pSaved.ourPrices ?? {});
  const [retailVatPct, setRetailVatPct] = useState(_pSaved.retailVatPct ?? "0");
  // Each coffee's own prices, by Green stock id and bag size. A blank falls
  // back to the bag-size price above, then to the suggested price.
  const [coffeePrices, setCoffeePrices] = useState(_pSaved.coffeePrices ?? {});
  const setCoffeePrice = (coffeeId, sizeG, kind, val) => setCoffeePrices(p => ({ ...p, [coffeeId]: { ...(p[coffeeId] || {}), [sizeG]: { ...((p[coffeeId] || {})[sizeG] || {}), [kind]: val } } }));
  const setOurPrice = (sizeG, kind, val) => setOurPrices(p => ({ ...p, [sizeG]: { ...(p[sizeG] || {}), [kind]: val } }));
  const [mix250, setMix250] = useState(_pSaved.mix250 ?? 50);
  const [mix500, setMix500] = useState(_pSaved.mix500 ?? 30);
  const [mix1000, setMix1000] = useState(_pSaved.mix1000 ?? 20);

  // ── FORECASTING ──
  const [forecastWeeks, setForecastWeeks] = useState(_pSaved.forecastWeeks ?? "12");
  const [utilisation, setUtilisation] = useState(_pSaved.utilisation ?? null); // null = use current schedule

  useEffect(() => {
    const settings = { roastLoss, roastsPerHour, kgPerRoast, fullHrDay, fullDaysWk, fullWeeksYr, currHrDay, currDaysWk, currWeeksYr, labourRate, employees, rent, gas, electricity, overheads, pkg250, pkg500, pkg1000, retailMargin, wholesaleMargin, mix250, mix500, mix1000, forecastWeeks, utilisation, ourPrices, retailVatPct, coffeePrices };
    localStorage.setItem("fdc_pricing_settings", JSON.stringify(settings));
  }, [roastLoss, roastsPerHour, kgPerRoast, fullHrDay, fullDaysWk, fullWeeksYr, currHrDay, currDaysWk, currWeeksYr, labourRate, employees, rent, gas, electricity, overheads, pkg250, pkg500, pkg1000, retailMargin, wholesaleMargin, mix250, mix500, mix1000, forecastWeeks, utilisation, ourPrices, retailVatPct, coffeePrices]);

  // Save origins to localStorage
  const saveOrigins = (next) => { setOrigins(next); localStorage.setItem("fdc_pricing_origins", JSON.stringify(next)); };
  const addOrigin = () => saveOrigins([...origins, { id:Date.now(), name:"", costPerKg:"", share:0 }]);
  const removeOrigin = id => saveOrigins(origins.filter(o => o.id !== id));
  const updateOrigin = (id, k, v) => saveOrigins(origins.map(o => o.id===id ? {...o,[k]:v} : o));
  const addOverhead = () => setOverheads(oh => [...oh, { id:Date.now(), name:"", amount:"" }]);
  const removeOverhead = id => setOverheads(oh => oh.filter(o => o.id !== id));
  const updateOverhead = (id, k, v) => setOverheads(oh => oh.map(o => o.id===id ? {...o,[k]:v} : o));

  // Pre-load from inventory
  const loadFromInventory = () => {
    if (!inventory || inventory.length === 0) return;
    const share = Math.round(100 / inventory.length);
    const rows = inventory.map((item, i) => ({
      id: item.id || i+1,
      name: item.name,
      costPerKg: item.costPerKg || "",
      share: i === inventory.length-1 ? 100 - share*(inventory.length-1) : share,
    }));
    saveOrigins(rows);
  };

  // ── CALCULATIONS ──────────────────────────────────────────────────────────────
  const loss = parseFloat(roastLoss) / 100 || 0.135;
  const lossAdj = 1 / (1 - loss); // green kg needed per roasted kg

  // Blended green cost per kg green
  const totalShare = origins.reduce((a, o) => a + (parseFloat(o.share)||0), 0);
  const blendedGreenCost = origins.reduce((a, o) => {
    const share = (parseFloat(o.share)||0) / 100;
    const cost = effectiveGreenCost(o, inventory);
    return a + share * cost;
  }, 0);

  // Green cost per roasted kg (loss-adjusted)
  const greenCostPerRoastedKg = blendedGreenCost * lossAdj;

  // Throughput
  const roastedKgHr = parseFloat(roastsPerHour) * parseFloat(kgPerRoast) * (1 - loss) || 0;

  // Full capacity
  const fullKgYr = roastedKgHr * parseFloat(fullHrDay) * parseFloat(fullDaysWk) * parseFloat(fullWeeksYr) || 0;

  // Current schedule
  const currKgYr = roastedKgHr * parseFloat(currHrDay) * parseFloat(currDaysWk) * parseFloat(currWeeksYr) || 0;

  // Utilisation (slider overrides current schedule)
  const utilisationPct = utilisation !== null ? utilisation : (fullKgYr > 0 ? Math.round((currKgYr / fullKgYr) * 100) : 24);
  const activeKgYr = fullKgYr * utilisationPct / 100;

  // Labour cost per roasted kg
  const labourHrYr = parseFloat(currHrDay) * parseFloat(currDaysWk) * parseFloat(currWeeksYr) * parseFloat(employees) || 0;
  const labourCostYr = labourHrYr * parseFloat(labourRate) || 0;
  const labourPerKg = currKgYr > 0 ? labourCostYr / currKgYr : 0;

  // Fixed costs per year
  const rentYr = parseFloat(rent) || 0;
  const gasYr = (parseFloat(gas) || 0) * 12;
  const elecYr = (parseFloat(electricity) || 0) * 12;
  const overheadsYr = overheads.reduce((a, o) => a + (parseFloat(o.amount)||0), 0);
  const totalFixedYr = rentYr + gasYr + elecYr + overheadsYr;

  // Fixed cost per roasted kg (based on active volume)
  const rentPerKg = activeKgYr > 0 ? rentYr / activeKgYr : 0;
  const energyPerKg = activeKgYr > 0 ? (gasYr + elecYr) / activeKgYr : 0;
  const overheadsPerKg = activeKgYr > 0 ? overheadsYr / activeKgYr : 0;

  // Base COGS per roasted kg (excludes packaging)
  const baseCOGSperKg = greenCostPerRoastedKg + labourPerKg + rentPerKg + energyPerKg + overheadsPerKg;

  // Full capacity COGS
  const fullCapCOGSperKg = fullKgYr > 0
    ? greenCostPerRoastedKg + labourCostYr/fullKgYr + rentYr/fullKgYr + (gasYr+elecYr)/fullKgYr + overheadsYr/fullKgYr
    : 0;

  // Pricing per bag size
  const bagSizes = [
    { label:"250g", weight:0.25, pkg:parseFloat(pkg250)||0.55, mixPct:mix250 },
    { label:"500g", weight:0.5,  pkg:parseFloat(pkg500)||0.75, mixPct:mix500 },
    { label:"1kg",  weight:1.0,  pkg:parseFloat(pkg1000)||1.10, mixPct:mix1000 },
  ];

  // Saved so Allocation & invoices can start each coffee's price from its own
  // wholesale price (that coffee's green cost + these shared costs).
  const nonGreenPerKg = labourPerKg + rentPerKg + energyPerKg + overheadsPerKg;
  useEffect(() => {
    try {
      localStorage.setItem("fdc_pricing_wholesale", JSON.stringify({
        lossAdj, nonGreenPerKg, blendedGreenCost,
        wholesaleMargin: parseFloat(wholesaleMargin) || 0,
        pkg: { 250: parseFloat(pkg250) || 0.55, 500: parseFloat(pkg500) || 0.75, 1000: parseFloat(pkg1000) || 1.10 },
        ourWholesale: Object.fromEntries([250, 500, 1000].map(g => [g, parseFloat(ourPrices?.[g]?.wholesale) > 0 ? parseFloat(ourPrices[g].wholesale) : null])),
        coffeeWholesale: Object.fromEntries(Object.entries(coffeePrices || {}).map(([id, sizes]) => [id, Object.fromEntries([250, 500, 1000].map(g => [g, parseFloat(sizes?.[g]?.wholesale) > 0 ? parseFloat(sizes[g].wholesale) : null]))])),
        updated: localIsoDate(),
      }));
    } catch { /* not fatal — invoices just won't get a starting price */ }
  }, [lossAdj, nonGreenPerKg, blendedGreenCost, wholesaleMargin, pkg250, pkg500, pkg1000, ourPrices, coffeePrices]);

  // Retail prices are entered as the shelf price; margins are worked out on
  // the price without VAT (wholesale prices are entered without VAT).
  const vatDiv = 1 + (parseFloat(retailVatPct) || 0) / 100;
  const marginPct = (priceExVat, cost) => priceExVat > 0 ? ((priceExVat - cost) / priceExVat) * 100 : null;
  const calcBagPricing = (bag) => {
    const sizeG = Math.round(bag.weight * 1000);
    const cogsPerBag = baseCOGSperKg * bag.weight + bag.pkg;
    const suggestedRetail = parseFloat(retailMargin) > 0 ? cogsPerBag / (1 - parseFloat(retailMargin)/100) : cogsPerBag;
    const suggestedWholesale = parseFloat(wholesaleMargin) > 0 ? cogsPerBag / (1 - parseFloat(wholesaleMargin)/100) : cogsPerBag;
    const ourRetail = parseFloat(ourPrices?.[sizeG]?.retail) > 0 ? parseFloat(ourPrices[sizeG].retail) : null;
    const ourWholesale = parseFloat(ourPrices?.[sizeG]?.wholesale) > 0 ? parseFloat(ourPrices[sizeG].wholesale) : null;
    // The sums below use the ex-VAT retail figure — VAT isn't your money.
    const retailPrice = ourRetail != null ? ourRetail / vatDiv : suggestedRetail;
    const wholesalePrice = ourWholesale != null ? ourWholesale : suggestedWholesale;
    const retailMarginReal = ourRetail != null ? marginPct(ourRetail / vatDiv, cogsPerBag) : null;
    const wholesaleMarginReal = ourWholesale != null ? marginPct(ourWholesale, cogsPerBag) : null;
    const retailSplit = bag.mixPct / 100;
    const blendedPrice = retailPrice * retailSplit + wholesalePrice * (1 - retailSplit);
    const contributionPerBag = blendedPrice - cogsPerBag;
    return { sizeG, cogsPerBag, retailPrice, wholesalePrice, blendedPrice, contributionPerBag, suggestedRetail, suggestedWholesale, ourRetail, ourWholesale, retailMarginReal, wholesaleMarginReal };
  };

  // Blended contribution per kg
  const weightedContribPerKg = bagSizes.reduce((a, bag) => {
    const { contributionPerBag } = calcBagPricing(bag);
    const shareOfVol = bag.mixPct / 100;
    const contribPerKg = bag.weight > 0 ? contributionPerBag / bag.weight : 0;
    return a + shareOfVol * contribPerKg;
  }, 0);

  // Break-even
  const breakEvenKgYr = weightedContribPerKg > 0 ? totalFixedYr / weightedContribPerKg : 0;
  const breakEvenPctOfFull = fullKgYr > 0 ? (breakEvenKgYr / fullKgYr) * 100 : 0;

  // Profit at current volume
  const annualRevenue = bagSizes.reduce((a, bag) => {
    const { blendedPrice } = calcBagPricing(bag);
    const volKg = activeKgYr * (bag.mixPct/100);
    const bags = volKg / bag.weight;
    return a + bags * blendedPrice;
  }, 0);
  // Annual COGS = green + packaging (labour and fixed already in baseCOGSperKg)
  const annualCOGS = activeKgYr * baseCOGSperKg + bagSizes.reduce((a, bag) => {
    const volKg = activeKgYr * (bag.mixPct/100);
    return a + (volKg / bag.weight) * bag.pkg;
  }, 0);
  // Profit = revenue - total COGS (which already includes labour and fixed costs per kg)
  const annualProfit = annualRevenue - annualCOGS;

  // Forecasting — green needed = roasted output / (1 - loss)
  const roastedKgPerWeek = roastedKgHr * parseFloat(currHrDay) * parseFloat(currDaysWk) || 0;
  const greenKgPerWeek = roastedKgPerWeek * lossAdj;
  const greenToPurchase = greenKgPerWeek * parseFloat(forecastWeeks) || 0;
  const roastedOutput = greenToPurchase * (1 - loss);
  const estGreenCost = greenToPurchase * blendedGreenCost;

  const fmt = (n) => isNaN(n) || !isFinite(n) ? "—" : n.toFixed(2);
  const fmtInt = (n) => isNaN(n) || !isFinite(n) ? "—" : Math.round(n).toLocaleString();
  const fmtK = (n) => isNaN(n) || !isFinite(n) ? "—" : (n >= 1000 ? (n/1000).toFixed(1)+"k" : Math.round(n).toString());

  // ── EXPORT — PDF and CSV, covering every figure on this page, for
  // monthly financial reporting. Same report style as Inventory/History's
  // own exports elsewhere in the Hub.
  const exportPricingReport = () => {
    const dateStr = todayDmy();
    const r2 = (label, value, bold) => `<tr><td class="lbl">${label}</td><td class="val${bold?" bld":""}">${value}</td></tr>`;
    const sec = (title) => `<tr><td colspan="2" class="sec">${title}</td></tr>`;

    const originRows = origins.map(o => r2(o.name || "—", `€${fmt(effectiveGreenCost(o, inventory))}/kg · ${o.share}% share`)).join("");
    const overheadRows = overheads.map(o => r2(o.name || "—", "€"+fmtInt(parseFloat(o.amount)||0))).join("");
    const bagRows = bagSizes.map(bag => {
      const { cogsPerBag, blendedPrice, contributionPerBag, suggestedRetail, suggestedWholesale, ourRetail, ourWholesale, retailMarginReal, wholesaleMarginReal } = calcBagPricing(bag);
      const retailShown = ourRetail != null ? ourRetail : suggestedRetail, wholesaleShown = ourWholesale != null ? ourWholesale : suggestedWholesale;
      return `
        ${sec(bag.label + " bag")}
        ${r2("COGS per bag", "€"+fmt(cogsPerBag))}
        ${r2(ourRetail != null ? "Retail price (ours)" : "Retail price (suggested)", "€"+fmt(retailShown) + (retailMarginReal != null ? " · "+fmt(retailMarginReal)+"% margin" : ""))}
        ${r2(ourWholesale != null ? "Wholesale price (ours)" : "Wholesale price (suggested)", "€"+fmt(wholesaleShown) + (wholesaleMarginReal != null ? " · "+fmt(wholesaleMarginReal)+"% margin" : ""))}
        ${r2("Sales mix (retail / wholesale)", bag.mixPct+"% / "+(100-bag.mixPct)+"%")}
        ${r2("Blended price", "€"+fmt(blendedPrice), true)}
        ${r2("Contribution per bag", "€"+fmt(contributionPerBag))}
      `;
    }).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Pricing & COGS Report — ${dateStr}</title>
<style>
  @page { margin: 18mm 20mm; size: A4 portrait; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, "Helvetica Neue", Arial, sans-serif; font-size: 13px; color: #1C1814; background: #fff; max-width: 680px; padding: 32px 0; }
  h1 { font-size: 24px; font-weight: 700; color: #1C1814; margin-bottom: 5px; }
  .sub { font-size: 12px; color: #7A6E63; padding-bottom: 16px; margin-bottom: 24px; border-bottom: 2px solid #1C1814; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 7px 0; vertical-align: middle; border-bottom: 1px solid #EDE8DE; line-height: 1.3; }
  td.lbl { color: #4A3F35; width: 58%; padding-left: 14px; font-size: 13px; }
  td.val { color: #1C1814; text-align: right; font-size: 13px; }
  td.val.bld { font-weight: 700; }
  td.sec { font-size: 10px; font-weight: 700; color: #8B3A2A; text-transform: uppercase; letter-spacing: 0.1em; padding: 22px 0 7px; border-bottom: 1.5px solid #C17F24; }
  .footer { margin-top: 36px; font-size: 10px; color: #A89E93; border-top: 1px solid #D5CCBE; padding-top: 10px; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; padding: 0; } }
</style>
</head>
<body>
<h1>Pricing &amp; COGS Report</h1>
<div class="sub">Skelligs Coffee &nbsp;&middot;&nbsp; Loring S70 Peregrine &nbsp;&middot;&nbsp; ${dateStr}</div>

<table>
  ${sec("Green coffee")}
  ${originRows}
  ${r2("Blended green cost", "€"+fmt(blendedGreenCost)+"/kg green")}
  ${r2("Roast loss", roastLoss+"%")}
  ${r2("Green cost per roasted kg", "€"+fmt(greenCostPerRoastedKg), true)}

  ${sec("Throughput & schedule")}
  ${r2("Roasts per hour", roastsPerHour)}
  ${r2("Green per roast", kgPerRoast+"kg")}
  ${r2("Roasted output", fmt(roastedKgHr)+" kg/hr")}
  ${r2("Full capacity", fmtInt(fullKgYr)+" kg/yr")}
  ${r2("Current schedule", fmtInt(currKgYr)+" kg/yr")}
  ${r2("Capacity utilisation modelled", utilisationPct+"%")}
  ${r2("Active volume at this utilisation", fmtInt(activeKgYr)+" kg/yr", true)}

  ${sec("Labour")}
  ${r2("Labour rate", "€"+labourRate+"/hr")}
  ${r2("Employees", employees)}
  ${r2("Labour cost per roasted kg", "€"+fmt(labourPerKg), true)}

  ${sec("Fixed costs")}
  ${r2("Rent / lease", "€"+fmtInt(rentYr)+"/yr")}
  ${r2("Gas", "€"+fmtInt(gasYr)+"/yr")}
  ${r2("Electricity", "€"+fmtInt(elecYr)+"/yr")}
  ${overheadRows}
  ${r2("Total fixed costs", "€"+fmtInt(totalFixedYr)+"/yr", true)}

  ${sec("COGS per roasted kg — at current volume")}
  ${r2("Green (loss-adjusted)", "€"+fmt(greenCostPerRoastedKg))}
  ${r2("Labour", "€"+fmt(labourPerKg))}
  ${r2("Rent / lease", "€"+fmt(rentPerKg))}
  ${r2("Energy (gas + electric)", "€"+fmt(energyPerKg))}
  ${r2("Overheads", "€"+fmt(overheadsPerKg))}
  ${r2("Base COGS / kg", "€"+fmt(baseCOGSperKg), true)}
  ${r2("Base COGS / kg at full capacity", "€"+fmt(fullCapCOGSperKg))}

  ${sec("Packaging & pricing per bag size")}
  ${bagRows}

  ${sec("Volume split & break-even")}
  ${r2("Break-even volume", fmtInt(breakEvenKgYr)+" kg/yr")}
  ${r2("Break-even as % of full capacity", Math.round(breakEvenPctOfFull)+"%")}
  ${r2("Blended contribution per kg", "€"+fmt(weightedContribPerKg))}
  ${r2("Estimated annual profit", "€"+fmtInt(annualProfit), true)}

  ${sec("Green coffee forecasting")}
  ${r2("Forecast period", forecastWeeks+" weeks")}
  ${r2("Green burn rate", fmt(greenKgPerWeek)+" kg/week")}
  ${r2("Green to purchase", fmtInt(greenToPurchase)+" kg")}
  ${r2("Estimated green cost", "€"+fmtInt(estGreenCost))}
  ${r2("Roasted output", fmtInt(roastedOutput)+" kg", true)}
</table>

<div class="footer">Skelligs Coffee &nbsp;&middot;&nbsp; Generated ${dateStr} &nbsp;&middot;&nbsp; fdc-roasting-hub.pages.dev</div>
</body></html>`;

    const win = window.open("", "_blank");
    if (!win) { window.alert("Please allow pop-ups for this site to export the report."); return; }
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 400);
  };

  const exportPricingCSV = () => {
    try {
      const esc = v => {
        const str = String(v ?? "");
        return str.includes(",") || str.includes('"') || str.includes("\n") ? `"${str.replace(/"/g,'""')}"` : str;
      };
      const row = cols => cols.map(esc).join(",");
      const lines = [
        row(["SKELLIGS COFFEE — PRICING & COGS REPORT"]),
        row(["Generated", todayDmy()]),
        "",
        row(["GREEN COFFEE",""]),
        row(["Origin","€/kg green","Share %"]),
        ...origins.map(o => row([o.name||"", effectiveGreenCost(o, inventory)||"", o.share||""])),
        row(["Blended green cost (€/kg)", fmt(blendedGreenCost)]),
        row(["Roast loss %", roastLoss]),
        row(["Green cost per roasted kg (€)", fmt(greenCostPerRoastedKg)]),
        "",
        row(["THROUGHPUT & SCHEDULE",""]),
        row(["Roasts per hour", roastsPerHour]),
        row(["Green per roast (kg)", kgPerRoast]),
        row(["Roasted output (kg/hr)", fmt(roastedKgHr)]),
        row(["Full capacity (kg/yr)", fmtInt(fullKgYr)]),
        row(["Current schedule (kg/yr)", fmtInt(currKgYr)]),
        row(["Utilisation modelled (%)", utilisationPct]),
        row(["Active volume (kg/yr)", fmtInt(activeKgYr)]),
        "",
        row(["LABOUR",""]),
        row(["Labour rate (€/hr)", labourRate]),
        row(["Employees", employees]),
        row(["Labour cost per roasted kg (€)", fmt(labourPerKg)]),
        "",
        row(["FIXED COSTS",""]),
        row(["Rent / lease (€/yr)", fmtInt(rentYr)]),
        row(["Gas (€/yr)", fmtInt(gasYr)]),
        row(["Electricity (€/yr)", fmtInt(elecYr)]),
        ...overheads.map(o => row([o.name||"", fmtInt(parseFloat(o.amount)||0)])),
        row(["Total fixed costs (€/yr)", fmtInt(totalFixedYr)]),
        "",
        row(["COGS PER ROASTED KG (current volume)",""]),
        row(["Green (loss-adjusted)", fmt(greenCostPerRoastedKg)]),
        row(["Labour", fmt(labourPerKg)]),
        row(["Rent / lease", fmt(rentPerKg)]),
        row(["Energy (gas + electric)", fmt(energyPerKg)]),
        row(["Overheads", fmt(overheadsPerKg)]),
        row(["Base COGS / kg", fmt(baseCOGSperKg)]),
        row(["Base COGS / kg at full capacity", fmt(fullCapCOGSperKg)]),
        "",
        row(["PACKAGING & PRICING PER BAG",""]),
        row(["Bag size","COGS/bag (€)","Retail price (€)","Retail margin %","Wholesale price (€)","Wholesale margin %","Retail mix %","Blended price (€)","Contribution/bag (€)"]),
        ...bagSizes.map(bag => {
          const { cogsPerBag, blendedPrice, contributionPerBag, suggestedRetail, suggestedWholesale, ourRetail, ourWholesale, retailMarginReal, wholesaleMarginReal } = calcBagPricing(bag);
          return row([bag.label, fmt(cogsPerBag), fmt(ourRetail != null ? ourRetail : suggestedRetail), retailMarginReal != null ? fmt(retailMarginReal) : "", fmt(ourWholesale != null ? ourWholesale : suggestedWholesale), wholesaleMarginReal != null ? fmt(wholesaleMarginReal) : "", bag.mixPct, fmt(blendedPrice), fmt(contributionPerBag)]);
        }),
        "",
        row(["VOLUME SPLIT & BREAK-EVEN",""]),
        row(["Break-even volume (kg/yr)", fmtInt(breakEvenKgYr)]),
        row(["Break-even as % of full capacity", Math.round(breakEvenPctOfFull)]),
        row(["Blended contribution per kg (€)", fmt(weightedContribPerKg)]),
        row(["Estimated annual profit (€)", fmtInt(annualProfit)]),
        "",
        row(["GREEN COFFEE FORECASTING",""]),
        row(["Forecast period (weeks)", forecastWeeks]),
        row(["Green burn rate (kg/week)", fmt(greenKgPerWeek)]),
        row(["Green to purchase (kg)", fmtInt(greenToPurchase)]),
        row(["Estimated green cost (€)", fmtInt(estGreenCost)]),
        row(["Roasted output (kg)", fmtInt(roastedOutput)]),
      ];
      const csv = lines.join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Skelligs-Pricing-Report-${localIsoDate()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch(e) {
      window.alert("Export failed: " + e.message);
    }
  };

  const SectionTitle = ({ icon, title }) => (
    <div style={{fontFamily:fs,fontSize:11,fontWeight:700,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.1em",marginTop:28,marginBottom:14,paddingTop:20,borderTop:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:8}}>
      {icon && <span>{icon}</span>}{title}
    </div>
  );

  const StatBox = ({ label, value, highlight }) => (
    <div style={{background:highlight?C.ink:C.cream2,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 16px"}}>
      <div style={{fontFamily:fs,fontSize:10,color:highlight?"rgba(255,255,255,0.6)":C.inkFaint,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>{label}</div>
      <div style={{fontFamily:fontSerif,fontSize:24,color:highlight?"#fff":C.ink,fontWeight:400}}>{value}</div>
    </div>
  );

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"flex-end", gap:8, marginBottom:10 }}>
        <button style={s.btnPrimary} onClick={exportPricingReport}>Export PDF</button>
        <button style={s.btnPrimary} onClick={exportPricingCSV}>Export CSV</button>
      </div>
      {/* ── GREEN COFFEE ── */}
      <SectionTitle icon="☕" title="Green coffee" />
      
      <div style={{fontFamily:fs,fontSize:12,color:C.inkLight,marginBottom:10}}>Each origin and its share of the blend by green weight.</div>

      {inventory && inventory.length > 0 && (
        <button style={{...s.btn,fontSize:12,marginBottom:12}} onClick={loadFromInventory}>
          Load from inventory
        </button>
      )}

      <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden",marginBottom:8}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
          <thead>
            <tr style={{background:C.cream3}}>
              {["Origin","€/kg green","Share %",""].map(h=><th key={h} style={s.thStyle}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {origins.map(o=>(
              <tr key={o.id} style={{borderBottom:`1px solid ${C.borderSoft}`}}>
                <td style={{padding:"7px 10px"}}><input style={s.input} value={o.name} onChange={e=>updateOrigin(o.id,"name",e.target.value)} placeholder="e.g. Halo Beriti"/></td>
                <td style={{padding:"7px 10px"}}>
                  {greenCostLinkFor(o, inventory) ? (
                    <div title="Set in Green Suite → Green stock. Change it there and it updates here.">
                      <div style={{fontFamily:fs,fontSize:14,color:C.ink}}>€{effectiveGreenCost(o, inventory).toFixed(2)}</div>
                      <div style={{fontFamily:fs,fontSize:10,color:C.inkFaint}}>from Green stock</div>
                    </div>
                  ) : (
                    <input style={s.input} type="number" value={o.costPerKg} onChange={e=>updateOrigin(o.id,"costPerKg",e.target.value)} placeholder="9.50" step="0.01"/>
                  )}
                </td>
                <td style={{padding:"7px 10px"}}><input style={{...s.input,width:70}} type="number" value={o.share} onChange={e=>updateOrigin(o.id,"share",e.target.value)} placeholder="100"/></td>
                <td style={{padding:"7px 10px"}}><button style={{background:"none",border:"none",cursor:"pointer",color:C.inkFaint,fontSize:18}} onClick={()=>removeOrigin(o.id)}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <button style={{...s.btn,fontSize:12}} onClick={addOrigin}>+ Add origin</button>
        <span style={{fontFamily:fs,fontSize:12,color:Math.abs(totalShare-100)<0.5?C.green:C.red,fontWeight:600}}>
          Shares total: {Math.round(totalShare*10)/10}% {Math.abs(totalShare-100)<0.5?"✓":""}
        </span>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <div>
          <div style={s.fieldLabel}>Roast loss %</div>
          <input style={s.input} type="number" value={roastLoss} onChange={e=>setRoastLoss(e.target.value)} placeholder="13.5" step="0.5"/>
          <div style={s.hint}>Typically 11–16%. Your history tab average is your best guide.</div>
        </div>
        <div style={{background:C.cream2,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 16px",display:"flex",flexDirection:"column",justifyContent:"center"}}>
          <div style={{fontFamily:fs,fontSize:10,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>Blended green cost</div>
          <div style={{fontFamily:fontSerif,fontSize:22,color:C.ink}}>€{fmt(blendedGreenCost)}/kg green</div>
          <div style={{fontFamily:fs,fontSize:11,color:C.inkLight,marginTop:2}}>€{fmt(greenCostPerRoastedKg)}/kg roasted (loss-adjusted)</div>
        </div>
      </div>

      {/* ── ROASTER THROUGHPUT ── */}
      <SectionTitle icon="📈" title="Roaster throughput" />
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
        <div><div style={s.fieldLabel}>Roasts / hour</div><input style={s.input} type="number" value={roastsPerHour} onChange={e=>setRoastsPerHour(e.target.value)} placeholder="3"/></div>
        <div><div style={s.fieldLabel}>Green / roast (kg)</div><input style={s.input} type="number" value={kgPerRoast} onChange={e=>setKgPerRoast(e.target.value)} placeholder="35"/></div>
        <div style={{background:C.cream2,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 14px"}}>
          <div style={{fontFamily:fs,fontSize:10,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>Output</div>
          <div style={{fontFamily:fontSerif,fontSize:20,color:C.ink}}>{fmt(roastedKgHr)} kg/hr</div>
          <div style={{fontFamily:fs,fontSize:11,color:C.inkLight}}>roasted</div>
        </div>
      </div>

      {/* ── SCHEDULE ── */}
      <SectionTitle icon="🕐" title="Schedule" />
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
        <div>
          <div style={{fontFamily:fs,fontSize:10,fontWeight:700,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Full capacity</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
            <div><div style={s.fieldLabel}>Hrs/day</div><input style={s.input} type="number" value={fullHrDay} onChange={e=>setFullHrDay(e.target.value)}/></div>
            <div><div style={s.fieldLabel}>Days/wk</div><input style={s.input} type="number" value={fullDaysWk} onChange={e=>setFullDaysWk(e.target.value)}/></div>
            <div><div style={s.fieldLabel}>Wks/yr</div><input style={s.input} type="number" value={fullWeeksYr} onChange={e=>setFullWeeksYr(e.target.value)}/></div>
          </div>
          <div style={{fontFamily:fs,fontSize:12,color:C.inkMid,marginTop:8}}>{fmtK(fullKgYr)} kg/yr roasted</div>
        </div>
        <div>
          <div style={{fontFamily:fs,fontSize:10,fontWeight:700,color:C.amber,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Current schedule</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
            <div><div style={s.fieldLabel}>Hrs/day</div><input style={s.input} type="number" value={currHrDay} onChange={e=>setCurrHrDay(e.target.value)}/></div>
            <div><div style={s.fieldLabel}>Days/wk</div><input style={s.input} type="number" value={currDaysWk} onChange={e=>setCurrDaysWk(e.target.value)}/></div>
            <div><div style={s.fieldLabel}>Wks/yr</div><input style={s.input} type="number" value={currWeeksYr} onChange={e=>setCurrWeeksYr(e.target.value)}/></div>
          </div>
          <div style={{fontFamily:fs,fontSize:12,color:C.inkMid,marginTop:8}}>{fmtK(currKgYr)} kg/yr roasted</div>
        </div>
      </div>

      {/* Capacity utilisation slider */}
      <div style={{background:C_FIXED_DARK,borderRadius:10,padding:"16px 20px",marginTop:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div style={{fontFamily:fs,fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.6)",textTransform:"uppercase",letterSpacing:"0.08em"}}>⚙️ Capacity utilisation</div>
          <div style={{fontFamily:fontSerif,fontSize:28,color:"#fff"}}>{utilisationPct}%</div>
        </div>
        <input type="range" min={1} max={100} value={utilisationPct}
          onChange={e=>setUtilisation(parseInt(e.target.value))}
          style={{width:"100%",accentColor:C.amber}}/>
        <div style={{fontFamily:fs,fontSize:11,color:"rgba(255,255,255,0.5)",marginTop:8}}>
          Drag to model running from idle to flat-out. Current schedule sits at {fullKgYr > 0 ? Math.round((currKgYr/fullKgYr)*100) : "—"}%.
        </div>
        <div style={{fontFamily:fs,fontSize:12,color:C.amberDim,marginTop:8}}>
          At {utilisationPct}% capacity: {fmtK(activeKgYr)} kg/yr · COGS €{fmt(baseCOGSperKg)}/kg
        </div>
        {fullKgYr > 0 && baseCOGSperKg > 0 && (
          <div style={{fontFamily:fs,fontSize:11,color:"rgba(255,255,255,0.5)",marginTop:4}}>
            At full capacity: €{fmt(fullCapCOGSperKg)}/kg COGS
            {baseCOGSperKg > fullCapCOGSperKg && ` — COGS €${fmt(baseCOGSperKg - fullCapCOGSperKg)} higher than flat-out`}
          </div>
        )}
      </div>

      {/* ── LABOUR ── */}
      <SectionTitle icon="👥" title="Labour" />
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
        <div><div style={s.fieldLabel}>Labour rate (€/hr)</div><input style={s.input} type="number" value={labourRate} onChange={e=>setLabourRate(e.target.value)} placeholder="13" step="0.5"/></div>
        <div><div style={s.fieldLabel}>Employees</div><input style={s.input} type="number" value={employees} onChange={e=>setEmployees(e.target.value)} placeholder="1"/></div>
        <div style={{background:C.cream2,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 14px"}}>
          <div style={{fontFamily:fs,fontSize:10,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>Per roasted kg</div>
          <div style={{fontFamily:fontSerif,fontSize:22,color:C.amber}}>€{fmt(labourPerKg)}</div>
        </div>
      </div>

      {/* ── FIXED COSTS ── */}
      <SectionTitle icon="🏢" title="Fixed costs" />
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>
        <div><div style={s.fieldLabel}>Rent / lease (€/yr)</div><input style={s.input} type="number" value={rent} onChange={e=>setRent(e.target.value)} placeholder="36000"/></div>
        <div><div style={s.fieldLabel}>Gas (€/mo)</div><input style={s.input} type="number" value={gas} onChange={e=>setGas(e.target.value)} placeholder="450"/></div>
        <div><div style={s.fieldLabel}>Electricity (€/mo)</div><input style={s.input} type="number" value={electricity} onChange={e=>setElectricity(e.target.value)} placeholder="600"/></div>
      </div>
      <div style={{fontFamily:fs,fontSize:11,fontWeight:600,color:C.inkLight,marginBottom:8}}>Other overheads (annual)</div>
      {overheads.map(o=>(
        <div key={o.id} style={{display:"flex",gap:8,alignItems:"center",marginBottom:6}}>
          <input style={{...s.input,flex:1}} value={o.name} onChange={e=>updateOverhead(o.id,"name",e.target.value)} placeholder="e.g. Insurance"/>
          <div style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{fontFamily:fs,fontSize:13,color:C.inkLight}}>€</span>
            <input style={{...s.input,width:100}} type="number" value={o.amount} onChange={e=>updateOverhead(o.id,"amount",e.target.value)} placeholder="2400"/>
          </div>
          <button style={{background:"none",border:"none",cursor:"pointer",color:C.inkFaint,fontSize:16}} onClick={()=>removeOverhead(o.id)}>×</button>
        </div>
      ))}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
        <button style={{...s.btn,fontSize:12}} onClick={addOverhead}>+ Add overhead</button>
        <span style={{fontFamily:fs,fontSize:12,fontWeight:600,color:C.inkMid}}>Total fixed: €{fmtInt(totalFixedYr)}/yr</span>
      </div>

      {/* ── COGS SUMMARY ── */}
      <SectionTitle icon="💰" title="COGS per roasted kg — at this volume" />
      <div style={{background:C_FIXED_DARK,borderRadius:10,padding:"16px 20px",marginBottom:4}}>
        {[
          ["Green (loss-adjusted)", greenCostPerRoastedKg],
          ["Labour", labourPerKg],
          ["Rent / lease", rentPerKg],
          ["Energy (gas + electric)", energyPerKg],
          ["Overheads", overheadsPerKg],
        ].map(([label, val])=>(
          <div key={label} style={{display:"flex",justifyContent:"space-between",fontFamily:fs,fontSize:13,color:"rgba(255,255,255,0.8)",padding:"7px 0",borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
            <span>{label}</span><span>€{fmt(val)}</span>
          </div>
        ))}
        <div style={{display:"flex",justifyContent:"space-between",fontFamily:fs,fontSize:16,fontWeight:700,color:"#fff",padding:"12px 0 0",marginTop:4}}>
          <span>Base COGS / kg</span><span>€{fmt(baseCOGSperKg)}</span>
        </div>
        {fullCapCOGSperKg > 0 && (
          <div style={{fontFamily:fs,fontSize:11,color:C.amberDim,marginTop:6}}>At full capacity: €{fmt(fullCapCOGSperKg)}/kg · Excludes packaging — added per bag size below.</div>
        )}
      </div>

      {/* ── PACKAGING ── */}
      <SectionTitle icon="📦" title="Packaging (per unit)" />
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
        <div><div style={s.fieldLabel}>250g bag (€)</div><input style={s.input} type="number" value={pkg250} onChange={e=>setPkg250(e.target.value)} placeholder="0.55" step="0.01"/></div>
        <div><div style={s.fieldLabel}>500g bag (€)</div><input style={s.input} type="number" value={pkg500} onChange={e=>setPkg500(e.target.value)} placeholder="0.75" step="0.01"/></div>
        <div><div style={s.fieldLabel}>1kg bag (€)</div><input style={s.input} type="number" value={pkg1000} onChange={e=>setPkg1000(e.target.value)} placeholder="1.10" step="0.01"/></div>
      </div>
      <div style={{fontFamily:fs,fontSize:11,color:C.inkFaint,marginTop:6}}>Cost of bag, valve and label per unit.</div>

      {/* ── SALES MIX & PRICING ── */}
      <SectionTitle icon="🏷️" title="Sales mix & pricing" />
      <div style={{fontFamily:fs,fontSize:12,color:C.inkLight,marginBottom:12,lineHeight:1.5}}>
        Enter the prices you actually charge for each bag size to see your real margins. Leave a price blank and the Hub suggests one from your target margin instead.
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:16}}>
        <div>
          <div style={s.fieldLabel}>Target retail margin %</div>
          <input style={s.input} type="number" value={retailMargin} onChange={e=>setRetailMargin(e.target.value)} placeholder="65"/>
        </div>
        <div>
          <div style={s.fieldLabel}>Target wholesale margin %</div>
          <input style={s.input} type="number" value={wholesaleMargin} onChange={e=>setWholesaleMargin(e.target.value)} placeholder="35"/>
        </div>
        <div>
          <div style={s.fieldLabel}>VAT in retail prices %</div>
          <input style={s.input} type="number" value={retailVatPct} onChange={e=>setRetailVatPct(e.target.value)} placeholder="0"/>
          <div style={{fontFamily:fs,fontSize:10,color:C.inkFaint,marginTop:4}}>Coffee beans are generally zero-rated in Ireland — worth confirming with your accountant.</div>
        </div>
      </div>
      {bagSizes.map(bag=>{
        const { sizeG, cogsPerBag, blendedPrice, contributionPerBag, suggestedRetail, suggestedWholesale, ourRetail, ourWholesale, retailMarginReal, wholesaleMarginReal } = calcBagPricing(bag);
        const retailSplit = bag.mixPct;
        const setSplit = bag.label==="250g" ? setMix250 : bag.label==="500g" ? setMix500 : setMix1000;
        const marginLine = (real, target, perBag) => {
          const low = real < (parseFloat(target) || 0);
          return <div style={{fontFamily:fs,fontSize:11,fontWeight:600,color: real < 0 ? C.red : low ? C.amber : C.green}}>{fmt(real)}% margin · {perBag >= 0 ? "+" : "−"}€{fmt(Math.abs(perBag))}/bag{low && real >= 0 ? " · under target" : ""}</div>;
        };
        return (
          <div key={bag.label} style={{border:`1px solid ${C.border}`,borderRadius:8,padding:"14px 16px",marginBottom:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div style={{fontFamily:fs,fontSize:16,fontWeight:600,color:C.ink}}>{bag.label}</div>
              <div style={{fontFamily:fs,fontSize:12,color:C.inkFaint}}>COGS €{fmt(cogsPerBag)}</div>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",fontFamily:fs,fontSize:11,marginBottom:4}}>
              <span style={{color:C.red,fontWeight:600}}>Retail {retailSplit}%</span>
              <span style={{color:C.green,fontWeight:600}}>{100-retailSplit}% Wholesale</span>
            </div>
            <input type="range" min={0} max={100} value={retailSplit} onChange={e=>setSplit(parseInt(e.target.value))}
              style={{width:"100%",accentColor:C.amber,marginBottom:10}}/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
              <div style={{background:C.cream2,borderRadius:6,padding:"10px 12px"}}>
                <div style={{fontFamily:fs,fontSize:10,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:3}}>Retail{ourRetail == null ? " · suggested" : ""}</div>
                <div style={{fontFamily:fontSerif,fontSize:20,color:C.ink}}>€{fmt(ourRetail != null ? ourRetail : suggestedRetail)}</div>
                {ourRetail != null ? marginLine(retailMarginReal, retailMargin, ourRetail / vatDiv - cogsPerBag) : <div style={{fontFamily:fs,fontSize:10,color:C.inkFaint}}>at {retailMargin}% margin</div>}
                <div style={{marginTop:8}}>
                  <div style={{fontFamily:fs,fontSize:10,color:C.inkFaint,marginBottom:2}}>Our retail price (€)</div>
                  <input style={{...s.input,fontSize:13,padding:"5px 8px"}} type="number" step="0.01" value={ourPrices?.[sizeG]?.retail ?? ""} onChange={e=>setOurPrice(sizeG,"retail",e.target.value)} placeholder={fmt(suggestedRetail)}/>
                </div>
              </div>
              <div style={{background:C.cream2,borderRadius:6,padding:"10px 12px"}}>
                <div style={{fontFamily:fs,fontSize:10,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:3}}>Wholesale{ourWholesale == null ? " · suggested" : ""}</div>
                <div style={{fontFamily:fontSerif,fontSize:20,color:C.ink}}>€{fmt(ourWholesale != null ? ourWholesale : suggestedWholesale)}</div>
                {ourWholesale != null ? marginLine(wholesaleMarginReal, wholesaleMargin, ourWholesale - cogsPerBag) : <div style={{fontFamily:fs,fontSize:10,color:C.inkFaint}}>at {wholesaleMargin}% margin</div>}
                <div style={{marginTop:8}}>
                  <div style={{fontFamily:fs,fontSize:10,color:C.inkFaint,marginBottom:2}}>Our wholesale price (€, ex VAT)</div>
                  <input style={{...s.input,fontSize:13,padding:"5px 8px"}} type="number" step="0.01" value={ourPrices?.[sizeG]?.wholesale ?? ""} onChange={e=>setOurPrice(sizeG,"wholesale",e.target.value)} placeholder={fmt(suggestedWholesale)}/>
                </div>
              </div>
              <div style={{background:C.cream2,borderRadius:6,padding:"10px 12px"}}>
                <div style={{fontFamily:fs,fontSize:10,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:3}}>Blended avg</div>
                <div style={{fontFamily:fontSerif,fontSize:20,color:C.amber}}>€{fmt(blendedPrice)}</div>
                <div style={{fontFamily:fs,fontSize:10,color:C.green}}>+€{fmt(contributionPerBag)}</div>
              </div>
            </div>
          </div>
        );
      })}

      {/* ── PRICE LIST BY COFFEE — each coffee's own wholesale and retail price
          per bag size, with the margin it earns on that coffee's real cost. A
          blank uses the bag-size price above (shown faintly as a hint). ── */}
      {(() => {
        const coffees = (inventory || []).filter(i => (i.name || i.origin));
        if (!coffees.length) return null;
        const sizes = bagSizes.map(bag => ({ bag, ...calcBagPricing(bag) }));
        const cell = (c, b, kind) => {
          // A Skelligs product for this coffee and size sets its price (W/S or
          // RRP) — edited in Packaging → Products & SKUs, so there's one source.
          const prod = productFor(c.name || c.origin, b.sizeG);
          const prodPrice = prod ? parseFloat(kind === "wholesale" ? prod.wsPrice : prod.rrp) : NaN;
          if (prodPrice > 0) {
            const greenP = parseFloat(c.costPerKg);
            const costP = greenP > 0 ? (greenP * lossAdj + nonGreenPerKg) * b.bag.weight + b.bag.pkg : null;
            const mP = costP != null ? marginPct(kind === "retail" ? prodPrice / vatDiv : prodPrice, costP) : null;
            const targetP = parseFloat(kind === "wholesale" ? wholesaleMargin : retailMargin) || 0;
            return (
              <td style={{ ...s.td, verticalAlign: "top", minWidth: 96 }} title={`${prod.name} (${prod.sku}) — change it in Packaging → Products & SKUs`}>
                <div style={{ fontFamily: fs, fontSize: 14, color: C.ink, padding: "4px 0" }}>€{fmt(prodPrice)}</div>
                <div style={{ fontFamily: fs, fontSize: 10.5, fontWeight: 600, color: mP == null ? C.inkFaint : mP < 0 ? C.red : mP < targetP ? C.amber : C.green }}>
                  {mP == null ? "no green cost" : `${fmt(mP)}%`}<span style={{ fontWeight: 400, color: C.inkFaint }}> · {prod.sku}</span>
                </div>
              </td>
            );
          }
          const own = coffeePrices?.[c.id]?.[b.sizeG]?.[kind] ?? "";
          const fallback = kind === "wholesale" ? (b.ourWholesale != null ? b.ourWholesale : b.suggestedWholesale) : (b.ourRetail != null ? b.ourRetail : b.suggestedRetail);
          const price = parseFloat(own) > 0 ? parseFloat(own) : fallback;
          const green = parseFloat(c.costPerKg);
          const cost = green > 0 ? (green * lossAdj + nonGreenPerKg) * b.bag.weight + b.bag.pkg : null;
          const exVat = kind === "retail" ? price / vatDiv : price;
          const mPct = cost != null ? marginPct(exVat, cost) : null;
          const target = parseFloat(kind === "wholesale" ? wholesaleMargin : retailMargin) || 0;
          return (
            <td style={{ ...s.td, verticalAlign: "top", minWidth: 96 }}>
              <input style={{ ...s.input, fontSize: 13, padding: "4px 6px", width: 84 }} type="number" step="0.01" value={own} placeholder={fmt(fallback)}
                onChange={e => setCoffeePrice(c.id, b.sizeG, kind, e.target.value)} title={parseFloat(own) > 0 ? "This coffee's own price" : "Blank — uses the bag-size price above"} />
              <div style={{ fontFamily: fs, fontSize: 10.5, fontWeight: 600, marginTop: 3, color: mPct == null ? C.inkFaint : mPct < 0 ? C.red : mPct < target ? C.amber : C.green }}>
                {mPct == null ? "no green cost" : `${fmt(mPct)}%`}{parseFloat(own) > 0 ? "" : <span style={{ fontWeight: 400, color: C.inkFaint }}> · default</span>}
              </div>
            </td>
          );
        };
        return (
          <>
            <SectionTitle icon="☕" title="Price list by coffee" />
            <div style={{ fontFamily: fs, fontSize: 12, color: C.inkLight, marginBottom: 10, lineHeight: 1.5 }}>
              Each coffee's own prices. Prices marked with a Skelligs SKU come from Packaging → Products & SKUs — change them there. Leave any other box blank to use the bag-size price above. The % under each price is that coffee's margin on its own green cost — amber is under your target, red is losing money. Wholesale prices here are what invoices start from.
            </div>
            <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflowX: "auto", marginBottom: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
                <thead>
                  <tr style={{ background: C.cream3 }}>
                    <th style={s.thStyle}>Coffee</th>
                    <th style={s.thStyle}>Green €/kg</th>
                    {sizes.map(b => [
                      <th key={b.sizeG + "w"} style={s.thStyle}>{b.bag.label} wholesale</th>,
                      <th key={b.sizeG + "r"} style={s.thStyle}>{b.bag.label} retail</th>,
                    ])}
                  </tr>
                </thead>
                <tbody>
                  {coffees.map(c => (
                    <tr key={c.id} style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                      <td style={{ ...s.td, fontWeight: 600, verticalAlign: "top" }}>{c.name || c.origin}</td>
                      <td style={{ ...s.td, verticalAlign: "top", color: parseFloat(c.costPerKg) > 0 ? C.ink : C.inkFaint }}>{parseFloat(c.costPerKg) > 0 ? `€${fmt(parseFloat(c.costPerKg))}` : "not set"}</td>
                      {sizes.map(b => [
                        <Fragment key={b.sizeG + "w"}>{cell(c, b, "wholesale")}</Fragment>,
                        <Fragment key={b.sizeG + "r"}>{cell(c, b, "retail")}</Fragment>,
                      ])}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        );
      })()}

      {/* ── VOLUME SPLIT & BREAK-EVEN ── */}
      <SectionTitle icon="🎯" title="Volume split & break-even" />
      <div style={{fontFamily:fs,fontSize:12,color:C.inkLight,marginBottom:12}}>What share of your total roasted volume sells as each bag size.</div>
      {bagSizes.map(bag=>{
        const setSplit = bag.label==="250g" ? setMix250 : bag.label==="500g" ? setMix500 : setMix1000;
        return (
          <div key={bag.label} style={{display:"flex",alignItems:"center",gap:12,marginBottom:8}}>
            <div style={{fontFamily:fs,fontSize:13,fontWeight:600,color:C.inkMid,minWidth:36}}>{bag.label}</div>
            <input type="range" min={0} max={100} value={bag.mixPct} onChange={e=>setSplit(parseInt(e.target.value))} style={{flex:1,accentColor:C.amber}}/>
            <div style={{fontFamily:fs,fontSize:13,fontWeight:600,color:C.ink,minWidth:36,textAlign:"right"}}>{bag.mixPct}%</div>
          </div>
        );
      })}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginTop:14}}>
        <StatBox label="Break-even volume" value={`${fmtInt(breakEvenKgYr)} kg/yr`} />
        <StatBox label="% of full capacity" value={`${Math.round(breakEvenPctOfFull)}%`} />
        <StatBox label="Contribution / kg" value={`€${fmt(weightedContribPerKg)}`} />
      </div>
      <div style={{background:C.cream2,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 16px",marginTop:10}}>
        <div style={{fontFamily:fontSerif,fontSize:26,color:annualProfit>=0?C.green:C.red}}>€{fmtInt(annualProfit)}</div>
        <div style={{fontFamily:fs,fontSize:11,color:C.inkFaint,marginTop:2}}>Estimated annual profit at {utilisationPct}% utilisation & current mix</div>
      </div>
      {breakEvenKgYr > 0 && currKgYr > 0 && (
        <div style={{fontFamily:fs,fontSize:12,color:C.inkMid,marginTop:8,padding:"10px 14px",background:C.cream2,borderRadius:6}}>
          At {Math.round(breakEvenPctOfFull)}% of full capacity you clear break-even. Your current schedule runs at {Math.round((currKgYr/fullKgYr)*100)}% of capacity.
        </div>
      )}

      {/* ── GREEN COFFEE FORECASTING ── */}
      <SectionTitle icon="📦" title="Green coffee forecasting" />
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
        <div>
          <div style={s.fieldLabel}>Run this coffee for (weeks)</div>
          <input style={s.input} type="number" value={forecastWeeks} onChange={e=>setForecastWeeks(e.target.value)} placeholder="12"/>
        </div>
        <div style={{background:C.cream2,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 14px"}}>
          <div style={{fontFamily:fs,fontSize:10,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>Burn rate</div>
          <div style={{fontFamily:fs,fontSize:13,fontWeight:600,color:C.ink}}>{fmt(greenKgPerWeek)} kg green / week</div>
          <div style={{fontFamily:fs,fontSize:11,color:C.inkFaint}}>{fmtInt(greenKgPerWeek*52)} kg/yr</div>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
        <StatBox label="Green to purchase" value={`${fmtInt(greenToPurchase)} kg`} />
        <StatBox label="Est. green cost" value={`€${fmtInt(estGreenCost)}`} />
        <StatBox label="Roasted output" value={`${fmtInt(roastedOutput)} kg`} />
      </div>
      <div style={{fontFamily:fs,fontSize:11,color:C.inkFaint,marginTop:8}}>
        Covers {forecastWeeks} operating weeks at your current schedule. Re-buy before this runs out.
      </div>
    </div>
  );
}

// ── APP ROOT ──────────────────────────────────────────────────────────────────
// ── ROAST MODE ────────────────────────────────────────────────────────────────
// ── LIVE ROAST CHART ──────────────────────────────────────────────────────────
// Real-time bean temp curve, built from logged points, plotted against
// the target guide curve — styled after Loring's Roast Architect software.
// ══════════════════════════════════════════════════════════════════════════════
// AI ROAST ASSISTANT
// Two deliberately separate features, both calling a small Cloudflare Worker
// (see /roast-assistant-worker) that holds the Anthropic API key server-side:
//   - RoastAssistantChat: on-demand Q&A during a live roast. NOT a replacement
//     for the existing RoR crash/flick/approach-FC alerts above — those stay
//     local and instant because a network round-trip during the exact second
//     a stall needs correcting would make things worse, not better. This is
//     for questions the roaster chooses to ask.
//   - AIReviewButton: one-shot post-roast analysis, available both right
//     after saving a roast in Roast Mode and later from any row in History.
// ══════════════════════════════════════════════════════════════════════════════
const ASSISTANT_WORKER_URL = "https://fdc-roast-assistant.firstdraftcoffee.workers.dev";

function updateHistoryRecord(id, patch) {
  try {
    const hist = JSON.parse(localStorage.getItem("fdc_history") || "[]");
    const updated = hist.map(r => r.id === id ? { ...r, ...patch } : r);
    localStorage.setItem("fdc_history", JSON.stringify(updated));
  } catch { /* not fatal — the in-memory state still has the review */ }
}

function buildReviewContext(roast) {
  return {
    origin: roast.origin, cultivar: roast.cultivar, process: roast.process,
    batch: roast.batch, green: roast.green, roasted: roast.roasted, loss: roast.loss,
    eor: roast.eor, score: roast.score, agtron: roast.agtron, agtronGround: roast.agtronGround,
    maiTime: roast.maiTime, events: roast.events, curveAdherence: roast.curveAdherence,
    date: roast.date, notes: roast.notes,
    // A sample, not the full logged curve — plenty for a review, and keeps
    // the request small and fast rather than shipping every single point.
    curveSample: (roast.curve || []).filter((_, i) => i % 3 === 0).map(p => ({ t: Math.round((p.t || 0) * 10) / 10, temp: p.temp })),
  };
}

function AIReviewButton({ roast, onReview }) {
  const [review, setReview] = useState(roast?.aiReview || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const requestReview = () => {
    if (!ASSISTANT_WORKER_URL) { setError("AI assistant isn't connected yet — set ASSISTANT_WORKER_URL near the top of the file."); return; }
    setLoading(true); setError("");
    fetch(ASSISTANT_WORKER_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mode: "review", context: buildReviewContext(roast) }),
    })
      .then(res => res.json())
      .then(json => {
        setLoading(false);
        if (json.error) { setError(json.error); return; }
        setReview(json.text);
        if (onReview) onReview(json.text);
      })
      .catch(() => { setLoading(false); setError("Couldn't reach the assistant — check your connection."); });
  };

  return (
    <div style={{ marginTop: 12 }}>
      {!review && (
        <button style={{ ...s.btnSmall, background: C.ink, color: "#fff" }} onClick={requestReview} disabled={loading}>
          {loading ? "Thinking…" : "🤖 Get AI roast review"}
        </button>
      )}
      {error && <div style={{ fontFamily: fontSans, fontSize: 11, color: C.red, marginTop: 6 }}>{error}</div>}
      {review && (
        <div style={{ marginTop: 10, background: "#F5F0E8", border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.ink}`, borderRadius: 6, padding: "12px 16px" }}>
          <div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>AI roast review</div>
          <div style={{ fontFamily: fontSans, fontSize: 12.5, color: C.inkMid, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{review}</div>
          <button style={{ ...s.btnSm, marginTop: 8 }} onClick={requestReview} disabled={loading}>{loading ? "Thinking…" : "Regenerate"}</button>
        </div>
      )}
    </div>
  );
}

function RoastAssistantChat({ context }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = () => {
    const text = input.trim();
    if (!text) return;
    if (!ASSISTANT_WORKER_URL) { setError("AI assistant isn't connected yet — set ASSISTANT_WORKER_URL near the top of the file."); return; }
    const nextMessages = [...messages, { role: "user", content: text }];
    setMessages(nextMessages);
    setInput("");
    setLoading(true); setError("");
    fetch(ASSISTANT_WORKER_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mode: "chat", messages: nextMessages, context }),
    })
      .then(res => res.json())
      .then(json => {
        setLoading(false);
        if (json.error) { setError(json.error); return; }
        setMessages(m => [...m, { role: "assistant", content: json.text }]);
      })
      .catch(() => { setLoading(false); setError("Couldn't reach the assistant — check your connection."); });
  };

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 24, overflow: "hidden" }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: C.cream2, cursor: "pointer" }}>
        <div style={{ fontFamily: fontSans, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.inkLight }}>🤖 AI roast assistant</div>
        <div style={{ color: C.inkFaint, fontSize: 13 }}>{open ? "▲" : "▼"}</div>
      </div>
      {open && (
        <div style={{ padding: 14 }}>
          {!ASSISTANT_WORKER_URL ? (
            <div style={{ fontFamily: fontSans, fontSize: 12, color: C.inkFaint, lineHeight: 1.6 }}>
              Not connected yet — set <code>ASSISTANT_WORKER_URL</code> near the top of the file (see <code>roast-assistant-worker/README.md</code>).
            </div>
          ) : (
            <>
              <div ref={scrollRef} style={{ maxHeight: 240, overflowY: "auto", marginBottom: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                {messages.length === 0 && (
                  <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, fontStyle: "italic" }}>
                    Ask anything about what's happening right now — e.g. "why is my RoR flat?" or "should I ease off the burner?"
                  </div>
                )}
                {messages.map((m, i) => (
                  <div key={i} style={{
                    alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                    maxWidth: "85%", background: m.role === "user" ? C.ink : C.cream2,
                    color: m.role === "user" ? "#fff" : C.inkMid,
                    borderRadius: 8, padding: "8px 12px", fontFamily: fontSans, fontSize: 12.5, lineHeight: 1.5, whiteSpace: "pre-wrap",
                  }}>{m.content}</div>
                ))}
                {loading && <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, fontStyle: "italic" }}>Thinking…</div>}
              </div>
              {error && <div style={{ fontFamily: fontSans, fontSize: 11, color: C.red, marginBottom: 8 }}>{error}</div>}
              <div style={{ display: "flex", gap: 8 }}>
                <input style={{ ...s.input, flex: 1 }} value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") send(); }} placeholder="Ask about the roast right now…" />
                <button style={s.btnPrimary} onClick={send} disabled={loading || !input.trim()}>Ask</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


// Builds a smooth SVG path through a set of screen-space [x,y] points using a
// Catmull-Rom-to-Bezier spline — passes exactly through every point (so the
// real charge/TAP/FC/drop values are never altered) but curves between them
// instead of the straight-line joins a plain "L L L" path would draw. A real
// roast curve decelerates smoothly; a handful of profile-step points joined
// with straight lines looked like a ramp, which is what this replaces.
function smoothSvgPath(pointsXY) {
  if (pointsXY.length === 0) return "";
  if (pointsXY.length < 3) return pointsXY.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0]} ${p[1]}`).join(" ");
  let d = `M ${pointsXY[0][0]} ${pointsXY[0][1]} `;
  for (let i = 0; i < pointsXY.length - 1; i++) {
    const p0 = pointsXY[i - 1] || pointsXY[i];
    const p1 = pointsXY[i];
    const p2 = pointsXY[i + 1];
    const p3 = pointsXY[i + 2] || p2;
    const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += `C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2[0]} ${p2[1]} `;
  }
  return d;
}

function LiveRoastChart({ dataPoints, steps, charge, eor, totalMin, dryEndMin, tapTime, secNow, running, referenceCurve, events, height, predFcMin, predDropMin }) {
  // Shrunk from 320 to 190 — this one chart was eating roughly half the
  // available screen height, which was the main reason anything below it
  // still needed scrolling to reach.
  const W = 880, H = height || 190, PAD = { t: 14, r: 20, b: 28, l: 46 };
  const plotW = W - PAD.l - PAD.r, plotH = H - PAD.t - PAD.b;

  const xMax = Math.max(totalMin + 1, secNow/60 + 1, 12);
  const xMin = 0;
  // Show the Loring machine-limit band whenever the roast is running hot
  // enough for it to matter (any logged point within 20°C of the warning).
  const hotPts = dataPoints.some(p => p.temp >= M.beanWarnC - 20);
  const yMin = 40;
  const yMax = hotPts ? Math.max(charge, eor, M.beanFaultC) + 12 : Math.max(charge, eor) + 25;

  const toX = v => PAD.l + (v - xMin) / (xMax - xMin) * plotW;
  const toY = v => PAD.t + (1 - (v - yMin) / (yMax - yMin)) * plotH;

  const targetPts = steps
    .map(s => ({ t: s.time, temp: parseFloat(String(s.temp).replace("~","")) }))
    .filter(p => !isNaN(p.temp))
    .sort((a,b) => a.t - b.t);

  const targetPath = smoothSvgPath(targetPts.map(p => [toX(p.t), toY(p.temp)]));

  const referencePath = smoothSvgPath((referenceCurve || [])
    .filter(p => p.t >= 0 && p.t <= xMax && p.temp != null)
    .sort((a,b) => a.t - b.t)
    .map(p => [toX(p.t), toY(p.temp)]));
  const actualPts = [...dataPoints].sort((a,b) => a.t - b.t);
  const actualPath = actualPts.map((p,i) => `${i===0?"M":"L"} ${toX(p.t)} ${toY(p.temp)}`).join(" ");

  // ── Logged phase events marked on the curve — TP, yellow point, FC, drop,
  // etc. — so what's on the timeline lines up visually with what was logged,
  // not just listed as text elsewhere. ──
  const EVENT_LABELS = { "Charge":"CH", "Turning point":"TP", "Yellow point":"Y", "First crack":"FC", "Second crack":"SC", "Drop":"Drop" };
  const eventPts = (events || []).map(e => {
    const parts = String(e.time || "").split(":").map(Number);
    const t = (parts[0] || 0) + (parts[1] || 0) / 60;
    return { t, temp: e.temp, name: e.name, label: EVENT_LABELS[e.name] || e.name };
  }).filter(e => e.temp != null && !isNaN(e.t));

  const xTicks = []; for (let m=0; m<=Math.ceil(xMax); m+=2) xTicks.push(m);
  const yTicks = []; for (let t=Math.ceil(yMin/20)*20; t<=yMax; t+=20) yTicks.push(t);

  const last = actualPts[actualPts.length-1];
  const autoCoolStart = Math.max(0, totalMin - 1);

  return (
    <div style={{ background:"#fff", border:`1px solid ${C.border}`, borderRadius:8, padding:"14px 16px 8px", marginBottom:24, overflowX:"auto" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
        <div style={{ fontFamily:fontSans, fontSize:10, fontWeight:700, color:C.inkFaint, textTransform:"uppercase", letterSpacing:"0.1em" }}>Live roast curve</div>
        <div style={{ display:"flex", gap:14, fontFamily:fontSans, fontSize:10, color:C.inkLight }}>
          <span><span style={{display:"inline-block",width:10,height:2,background:C.green,marginRight:5,verticalAlign:"middle"}}/>Actual</span>
          <span><span style={{display:"inline-block",width:10,height:2,background:C.inkFaint,marginRight:5,verticalAlign:"middle",borderTop:`1px dashed ${C.inkFaint}`}}/>Target</span>
          <span><span style={{display:"inline-block",width:10,height:2,background:C.amber,marginRight:5,verticalAlign:"middle"}}/>EoR</span>
          {referenceCurve && <span><span style={{display:"inline-block",width:10,height:2,background:"#4a3aa7",marginRight:5,verticalAlign:"middle"}}/>Reference roast</span>}
      </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMinYMin meet" style={{ display:"block", width:"100%", height:"auto" }}>
        <rect x={toX(0)} y={PAD.t} width={toX(Math.min(tapTime*1.4, dryEndMin*0.5))-toX(0)} height={plotH} fill="#F0A94E" opacity="0.18"/>
        <rect x={toX(autoCoolStart)} y={PAD.t} width={toX(totalMin)-toX(autoCoolStart)} height={plotH} fill="#4A90D9" opacity="0.16"/>

        {xTicks.map(x => <line key={x} x1={toX(x)} y1={PAD.t} x2={toX(x)} y2={PAD.t+plotH} stroke={C.borderSoft} strokeWidth="0.5"/>)}
        {yTicks.map(y => <line key={y} x1={PAD.l} y1={toY(y)} x2={PAD.l+plotW} y2={toY(y)} stroke={C.borderSoft} strokeWidth="0.5"/>)}

        <line x1={PAD.l} y1={toY(eor)} x2={PAD.l+plotW} y2={toY(eor)} stroke={C.amber} strokeWidth="1.2" strokeDasharray="5 4"/>
        {predFcMin != null && predFcMin > xMin && predFcMin < xMax && (
          <g>
            <rect x={toX(predFcMin) - 10} y={PAD.t} width={20} height={plotH} fill={C.amber} opacity="0.14"/>
            <line x1={toX(predFcMin)} y1={PAD.t} x2={toX(predFcMin)} y2={PAD.t+plotH} stroke={C.amber} strokeWidth="1" strokeDasharray="3 3"/>
            <text x={toX(predFcMin)+6} y={PAD.t+12} fill={C.amber} fontSize="10" fontWeight="700" fontFamily={fontSans}>First crack ~{Math.floor(predFcMin)}:{String(Math.round((predFcMin%1)*60)).padStart(2,"0")}</text>
          </g>
        )}
        {predDropMin != null && predDropMin > xMin && predDropMin < xMax && (
          <g>
            <line x1={toX(predDropMin)} y1={PAD.t} x2={toX(predDropMin)} y2={PAD.t+plotH} stroke={C.inkFaint} strokeWidth="1" strokeDasharray="3 3"/>
            <text x={toX(predDropMin)-6} y={PAD.t+26} textAnchor="end" fill={C.inkLight} fontSize="10" fontFamily={fontSans}>Drop ~{Math.floor(predDropMin)}:{String(Math.round((predDropMin%1)*60)).padStart(2,"0")}</text>
          </g>
        )}
        <text x={PAD.l+plotW-4} y={toY(eor)-5} textAnchor="end" fill={C.amber} fontSize="10" fontFamily={fontSans}>EoR {eor}°C</text>

        {/* Loring machine limits — bean temp Warning 268°C / Fault 273°C (manual p.64) */}
        {yMax >= M.beanWarnC && (
          <>
            <rect x={PAD.l} y={toY(yMax)} width={plotW} height={Math.max(0, toY(M.beanWarnC)-toY(yMax))} fill={C.red} opacity="0.07"/>
            <line x1={PAD.l} y1={toY(M.beanWarnC)} x2={PAD.l+plotW} y2={toY(M.beanWarnC)} stroke={C.red} strokeWidth="1" strokeDasharray="3 3"/>
            <text x={PAD.l+4} y={toY(M.beanWarnC)-4} fill={C.red} fontSize="9" fontFamily={fontSans}>{M.brand} warning {M.beanWarnC}°C</text>
            {yMax >= M.beanFaultC && (
              <>
                <line x1={PAD.l} y1={toY(M.beanFaultC)} x2={PAD.l+plotW} y2={toY(M.beanFaultC)} stroke={C.red} strokeWidth="1.6"/>
                <text x={PAD.l+4} y={toY(M.beanFaultC)-4} fill={C.red} fontSize="9" fontWeight="700" fontFamily={fontSans}>Machine fault {M.beanFaultC}°C</text>
              </>
            )}
          </>
        )}

        <line x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={PAD.t+plotH} stroke={C.ink} strokeWidth="1"/>
        <line x1={PAD.l} y1={PAD.t+plotH} x2={PAD.l+plotW} y2={PAD.t+plotH} stroke={C.ink} strokeWidth="1"/>
        {xTicks.map(x => <text key={x} x={toX(x)} y={PAD.t+plotH+16} textAnchor="middle" fill={C.inkLight} fontSize="10" fontFamily={fontSans}>{x}</text>)}
        <text x={PAD.l+plotW/2} y={H-2} textAnchor="middle" fill={C.inkLight} fontSize="10" fontFamily={fontSans}>Time (minutes)</text>
        {yTicks.map(y => <text key={y} x={PAD.l-6} y={toY(y)+3} textAnchor="end" fill={C.inkLight} fontSize="10" fontFamily={fontSans}>{y}</text>)}
        <text x={12} y={PAD.t+plotH/2} textAnchor="middle" fill={C.inkLight} fontSize="10" fontFamily={fontSans} transform={`rotate(-90,12,${PAD.t+plotH/2})`}>°C</text>

        {targetPath && <path d={targetPath} fill="none" stroke={C.inkFaint} strokeWidth="1.5" strokeDasharray="4 3"/>}
        {referencePath && <path d={referencePath} fill="none" stroke="#4a3aa7" strokeWidth="1.5" strokeDasharray="3 3"/>}
        {actualPath && <path d={actualPath} fill="none" stroke={C.green} strokeWidth="2.5"/>}
        {/* Dots only for hand-typed readings (a few dozen). A live roast logs a
            reading every second — 600 dots made a thick, blobby line and a lot
            of redrawing; the line alone shows it better. */}
        {actualPts.length <= 150 && actualPts.map((p,i) => <circle key={i} cx={toX(p.t)} cy={toY(p.temp)} r="2.5" fill={C.green}/>)}

        {eventPts.map((e,i) => (
          <g key={`ev-${i}`}>
            <line x1={toX(e.t)} y1={toY(e.temp)} x2={toX(e.t)} y2={PAD.t+plotH} stroke={C.inkFaint} strokeWidth="0.75" strokeDasharray="2 2" opacity="0.5"/>
            <circle cx={toX(e.t)} cy={toY(e.temp)} r="4.5" fill="#fff" stroke={C.ink} strokeWidth="1.5"/>
            <text x={toX(e.t)} y={toY(e.temp)-9} textAnchor="middle" fill={C.ink} fontSize="9" fontWeight="700" fontFamily={fontSans}>{e.label}</text>
          </g>
        ))}

        {last && running && (
          <>
            <circle cx={toX(last.t)} cy={toY(last.temp)} r="7" fill="none" stroke={C.green} strokeWidth="2" opacity="0.5">
              <animate attributeName="r" values="5;11;5" dur="1.6s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.6;0;0.6" dur="1.6s" repeatCount="indefinite"/>
            </circle>
            <circle cx={toX(last.t)} cy={toY(last.temp)} r="4" fill={C.green}/>
          </>
        )}
      </svg>
      {dataPoints.length === 0 && (
        <div style={{ fontFamily:fontSans, fontSize:11, color:C.inkFaint, textAlign:"center", padding:"4px 0 10px" }}>
          No points logged yet — turn on <strong style={{color:C.amber}}>Auto-log</strong> or hit <strong style={{color:C.amber}}>● Log point</strong> above as you go.
        </div>
      )}
    </div>
  );
}

// The recipe's burner changes, keyed to what triggers them: an event
// (charge, turning point, first crack) or a bean temperature — the way a
// Loring burner recipe works — rather than the clock. Built straight from
// the editable steps table, so edits there carry through.
function burnerPlanFromSteps(steps) {
  const num = v => { const x = parseFloat(String(v ?? "").replace(/[^0-9.\-]/g, "")); return isNaN(x) ? null : x; };
  const plan = [];
  (steps || []).forEach(st => {
    const ph = (st.phase || "").toLowerCase();
    const burner = num(st.burner);
    const timeStr = st.timeStr;
    if (ph === "charge") plan.push({ key: "charge", label: "At charge", event: "Charge", burner, timeStr });
    else if (ph === "turning point") return; // hold through the turn — the charge setting carries on
    else if (ph === "drying") { if (burner == null) return; plan.push({ key: "drying", label: "After turning point", event: "Turning point", burner, timeStr }); } // fixed standard: same moment as the 150° row, so skip
    else if (ph === "first crack") plan.push({ key: "fc", label: "At first crack", event: "First crack", burner, timeStr });
    else if (ph === "drop") plan.push({ key: "drop", label: "Drop", temp: num(st.temp), burner: null, isDrop: true, timeStr });
    else { const t = num(st.temp); if (t != null) plan.push({ key: ph, label: `At ${Math.round(t)}°`, temp: t, burner, timeStr }); }
  });
  return plan;
}

// First crack, read from the data rather than heard. On a sealed drum like
// the Loring you can't hear it, but with the burner held steady the bean
// RoR tends to LIFT briefly just before first crack and then DIP as the
// beans start cracking and releasing steam. This looks for that shape in the
// last minute and a quarter, near where this coffee is expected to crack.
// Returns the likely moment (half-way down the dip) or null. It only ever
// SUGGESTS — the roaster confirms with the trier.
// Tuned on a real Loring S70 roast (29 Sept): lift 7.9 → 10.9 °/min at
// 194–196°, dip to 6.0 by 199°, burner fixed at 80%.
function detectFirstCrackSignature({ dataPoints, secNow, fcTempExpected, sinceSec }) {
  const win = dataPoints.filter(p => p.ror != null && p.temp != null && p.t * 60 >= secNow - 75 && p.t * 60 > (sinceSec || 0));
  if (win.length < 12) return null;
  const last = win[win.length - 1];
  if (last.temp < fcTempExpected - 8 || last.temp > fcTempExpected + 14) return null;
  // Burner must have been steady — otherwise the RoR is just following the gas.
  const burners = win.map(p => p.burner).filter(b => typeof b === "number");
  if (burners.length && Math.max(...burners) - Math.min(...burners) > 1.5) return null;
  // The lift: the highest RoR in the window, well above the low point before it.
  let peakIdx = 0;
  win.forEach((p, i) => { if (p.ror >= win[peakIdx].ror) peakIdx = i; });
  const peak = win[peakIdx];
  const before = win.slice(0, peakIdx);
  if (!before.length) return null;
  const lowBefore = Math.min(...before.map(p => p.ror));
  const sincePeak = secNow - peak.t * 60;
  // The dip: RoR now clearly below that peak, shortly after it.
  if (peak.ror - lowBefore >= 1.5 && last.ror <= peak.ror - 2.5 && sincePeak >= 5 && sincePeak <= 40) {
    const halfway = peak.ror - (peak.ror - last.ror) / 2;
    const onset = win.slice(peakIdx).find(p => p.ror <= halfway) || last;
    return { sec: Math.round(onset.t * 60), temp: onset.temp, peakSec: Math.round(peak.t * 60), lift: Math.round((peak.ror - lowBefore) * 10) / 10, dip: Math.round((peak.ror - last.ror) * 10) / 10 };
  }
  return null;
}

// When will first crack come? Projects the bean temp forward using the
// current RoR and how fast that RoR is easing off, to the temperature this
// coffee has actually cracked at before (median of its past roasts), or the
// plan's figure when there's no history yet. Returns seconds from now.
function predictFirstCrack({ dataPoints, secNow, btNow, rorNow, fcTemp }) {
  if (btNow == null || isNaN(btNow) || rorNow == null || isNaN(rorNow) || rorNow <= 0.5) return null;
  if (btNow >= fcTemp) return { inSec: 0 };
  const recent = dataPoints.filter(p => p.ror != null && p.t >= secNow / 60 - 1.5);
  let a = 0; // change in RoR, °C/min per minute
  if (recent.length >= 4 && recent[recent.length - 1].t - recent[0].t >= 0.75) {
    const t0 = recent[0].t, pts = recent.map(p => ({ x: p.t - t0, y: p.ror }));
    const nn = pts.length, sx = pts.reduce((q, p) => q + p.x, 0), sy = pts.reduce((q, p) => q + p.y, 0);
    const sxx = pts.reduce((q, p) => q + p.x * p.x, 0), sxy = pts.reduce((q, p) => q + p.x * p.y, 0);
    const d = nn * sxx - sx * sx;
    if (d !== 0) a = (nn * sxy - sx * sy) / d;
  }
  a = Math.max(-8, Math.min(0, a));
  const dist = fcTemp - btNow;
  let tMin = null;
  if (a < -0.01) {
    const disc = rorNow * rorNow + 2 * a * dist;
    if (disc < 0) return { inSec: null, stall: true }; // at this rate RoR runs out before first crack
    tMin = (rorNow - Math.sqrt(disc)) / (-a);
  }
  if (tMin == null || tMin <= 0) tMin = dist / rorNow;
  return { inSec: Math.round(tMin * 60) };
}

// Rate of rise worked out from the bean temperatures themselves — a
// least-squares slope over the last `windowSec` seconds, in °C per minute.
// Smooths out the jitter of reading-to-reading differences. Returns null
// until there's enough of a window to be meaningful.
function calcRoRFromSeries(series, windowSec) {
  if (!series || series.length < 3) return null;
  const lastS = series[series.length - 1].s;
  const win = series.filter(p => p.s >= lastS - windowSec && typeof p.bt === "number" && !isNaN(p.bt));
  if (win.length < 3) return null;
  const span = win[win.length - 1].s - win[0].s;
  if (span < 12) return null;
  const n = win.length;
  // Times are measured from the start of the window — raw clock timestamps
  // are huge numbers and would swamp the maths.
  const s0 = win[0].s;
  const pts = win.map(p => ({ s: p.s - s0, bt: p.bt }));
  const sx = pts.reduce((a, p) => a + p.s, 0), sy = pts.reduce((a, p) => a + p.bt, 0);
  const sxx = pts.reduce((a, p) => a + p.s * p.s, 0), sxy = pts.reduce((a, p) => a + p.s * p.bt, 0);
  const d = n * sxx - sx * sx;
  if (d === 0) return null;
  const perSec = (n * sxy - sx * sy) / d;
  return Math.round(perSec * 60 * 10) / 10;
}
// Adds a curve point, filling in its RoR from the logged curve when none
// was typed in by hand.
// Readings are stored with sensible precision — a time like
// 8.216666666666667 minutes becomes 8.2167 (still to the hundredth of a
// second), temperatures to 0.1°. Same curve, roughly a third less space.
function compactCurve(pts) {
  const r = (v, k) => (typeof v === "number" && isFinite(v)) ? Math.round(v * k) / k : v;
  return (pts || []).map(p => {
    const o = { t: r(p.t, 10000), temp: r(p.temp, 10) };
    if (p.ror != null) o.ror = r(p.ror, 10);
    if (p.burner != null) o.burner = r(p.burner, 10);
    if (p.et != null) o.et = r(p.et, 10);
    if (p.inlet != null) o.inlet = r(p.inlet, 10);
    if (p.stack != null) o.stack = r(p.stack, 10);
    return o;
  });
}

function withCalcRoR(pts, np) {
  if (np.ror == null) {
    const series = [...pts, np].filter(p => p.temp != null).map(p => ({ s: p.t * 60, bt: p.temp }));
    const r = calcRoRFromSeries(series, 45);
    if (r != null) np = { ...np, ror: r };
  }
  return [...pts, np];
}

// ── ROAST AUDIO RECORDER ────────────────────────────────────────────────────
// Records the roast through a microphone plugged into this computer (a
// contact mic on the Loring, via a USB audio adapter), so first crack can be
// looked for in the sound as well as the data. The browser's call-style
// processing (echo cancelling, noise suppression, auto volume) is switched
// OFF — it would treat the cracks as noise and smooth them away. The file is
// saved to this computer, named with the batch and where charge falls in it,
// so it lines up with the roast curve.
function RoastAudioRecorder({ batchId, chargeWallMsRef, roastEnded, onSaved }) {
  const [open, setOpen] = useState(false);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(() => { try { return localStorage.getItem("fdc_audio_device") || ""; } catch { return ""; } });
  const [state, setState] = useState("idle"); // idle | recording | saved | error
  const [error, setError] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0);
  const [savedName, setSavedName] = useState("");
  const recRef = useRef(null), streamRef = useRef(null), chunksRef = useRef([]), startRef = useRef(0);
  const ctxRef = useRef(null), rafRef = useRef(null), tickRef = useRef(null), stopTimerRef = useRef(null);
  const supported = typeof navigator !== "undefined" && navigator.mediaDevices && typeof window !== "undefined" && typeof window.MediaRecorder !== "undefined";

  const listDevices = async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      setDevices(all.filter(d => d.kind === "audioinput"));
    } catch { setDevices([]); }
  };
  const openPanel = async () => {
    setOpen(o => !o);
    if (!supported) return;
    await listDevices();
  };
  const cleanup = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (tickRef.current) clearInterval(tickRef.current);
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (ctxRef.current && ctxRef.current.close) { try { ctxRef.current.close(); } catch { /* already closed */ } }
    streamRef.current = null; ctxRef.current = null;
    setLevel(0);
  };
  useEffect(() => () => { if (recRef.current && recRef.current.state === "recording") recRef.current.stop(); cleanup(); }, []);

  const start = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: {
        ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
        echoCancellation: false, noiseSuppression: false, autoGainControl: false, channelCount: 1,
      } });
      streamRef.current = stream;
      await listDevices(); // names only show once permission is given
      // Live level meter so you can see the mic is hearing the drum.
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx(); ctxRef.current = ctx;
        const an = ctx.createAnalyser(); an.fftSize = 1024;
        ctx.createMediaStreamSource(stream).connect(an);
        const buf = new Float32Array(an.fftSize);
        const loop = () => {
          an.getFloatTimeDomainData(buf);
          let sum = 0; for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
          setLevel(Math.min(1, Math.sqrt(sum / buf.length) * 4));
          rafRef.current = requestAnimationFrame(loop);
        };
        loop();
      } catch { /* meter is a nice-to-have */ }
      const type = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"].find(t => window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported(t)) || "";
      const rec = new window.MediaRecorder(stream, { ...(type ? { mimeType: type } : {}), audioBitsPerSecond: 256000 });
      chunksRef.current = [];
      rec.ondataavailable = e => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => finish(rec.mimeType || type || "audio/webm");
      recRef.current = rec;
      startRef.current = Date.now();
      rec.start(1000);
      setElapsed(0);
      tickRef.current = setInterval(() => setElapsed(Math.round((Date.now() - startRef.current) / 1000)), 1000);
      setState("recording");
      setOpen(false);
    } catch (e) {
      cleanup();
      setState("error");
      setError(e && e.name === "NotAllowedError" ? "Microphone permission was refused — allow it in the browser's address bar and try again." : "Couldn't start the microphone — check it's plugged in and chosen above.");
    }
  };
  const stop = () => { if (recRef.current && recRef.current.state === "recording") recRef.current.stop(); };
  const finish = (mime) => {
    const blob = new Blob(chunksRef.current, { type: mime });
    const startedAt = startRef.current;
    const chargeMs = chargeWallMsRef && chargeWallMsRef.current;
    const offset = chargeMs ? Math.round((chargeMs - startedAt) / 1000) : null;
    const date = new Date(startedAt).toISOString().split("T")[0];
    const ext = /ogg/.test(mime) ? "ogg" : "webm";
    const chargePart = offset != null && offset >= 0 ? `_charge-at-${Math.floor(offset / 60)}m${String(offset % 60).padStart(2, "0")}s` : "";
    const name = `${(batchId || "roast").replace(/[^A-Za-z0-9-]/g, "")}_${date}${chargePart}.${ext}`;
    try {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { /* download blocked — nothing else to do */ }
    cleanup();
    setSavedName(name);
    setState("saved");
    if (onSaved) onSaved({ file: name, startedAt: new Date(startedAt).toISOString(), chargeOffsetSec: offset, seconds: Math.round((Date.now() - startedAt) / 1000), sizeKb: Math.round(blob.size / 1024) });
  };
  // Keep going for 30 seconds after the drop, then save on its own.
  useEffect(() => {
    if (roastEnded && state === "recording" && !stopTimerRef.current) stopTimerRef.current = setTimeout(stop, 30000);
  }, [roastEnded, state]);

  const mmss = x => `${Math.floor(x / 60)}:${String(x % 60).padStart(2, "0")}`;
  const btnBase = { height:36, padding:"0 12px", borderRadius:6, borderWidth:1, borderStyle:"solid", borderColor:"#5A554C", background:"transparent", color:"#F5F0E8", fontFamily:fontSans, fontSize:12, fontWeight:600, cursor:"pointer" };
  return (
    <div style={{ position:"relative" }}>
      {state === "recording" ? (
        <button onClick={stop} style={{ ...btnBase, borderColor:"#B94040", display:"flex", alignItems:"center", gap:8 }} title="Stop and save the recording">
          <span style={{ width:9, height:9, borderRadius:"50%", background:"#E0645A", display:"inline-block" }}/>
          {mmss(elapsed)}
          <span style={{ width:44, height:6, borderRadius:3, background:"#3A362F", overflow:"hidden", display:"inline-block" }}>
            <span style={{ display:"block", height:6, width:`${Math.round(level * 100)}%`, background: level > 0.9 ? "#E0645A" : "#9DBB8C" }}/>
          </span>
          Stop
        </button>
      ) : (
        <button onClick={openPanel} style={{ ...btnBase, color: state === "saved" ? "#9DBB8C" : "#F5F0E8" }} title="Record the roast through a microphone">🎙 {state === "saved" ? "Saved" : "Record"}</button>
      )}
      {open && state !== "recording" && (
        <div style={{ position:"absolute", right:0, top:44, width:320, background:C.cream2, border:`1px solid ${C.border}`, borderRadius:10, padding:"14px 16px", zIndex:5, boxShadow:"0 8px 24px rgba(0,0,0,0.35)" }}>
          <div style={{ fontFamily:fontSans, fontSize:10.5, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:C.inkFaint, marginBottom:8 }}>Record roast audio</div>
          {!supported ? (
            <div style={{ fontFamily:fontSans, fontSize:12.5, color:C.inkLight, lineHeight:1.5 }}>This browser can't record audio. Chrome or Edge on the roaster's computer will.</div>
          ) : (
            <>
              <div style={s.fieldLabel}>Microphone</div>
              <select style={s.input} value={deviceId} onChange={e => { setDeviceId(e.target.value); try { localStorage.setItem("fdc_audio_device", e.target.value); } catch { /* not important */ } }}>
                <option value="">Computer's default microphone</option>
                {devices.map((d, i) => <option key={d.deviceId || i} value={d.deviceId}>{d.label || `Microphone ${i + 1}`}</option>)}
              </select>
              <div style={{ fontFamily:fontSans, fontSize:11, color:C.inkFaint, lineHeight:1.45, margin:"8px 0 12px" }}>
                Choose your contact mic (it may show as the USB adapter's name). Start any time before charge — the file notes where charge falls, and saves itself 30 seconds after the drop.
              </div>
              <button onClick={start} style={{ ...s.btnPrimary, width:"100%" }}>Start recording</button>
            </>
          )}
          {state === "error" && <div style={{ fontFamily:fontSans, fontSize:11.5, color:C.red, marginTop:8, lineHeight:1.45 }}>{error}</div>}
          {state === "saved" && savedName && <div style={{ fontFamily:fontSans, fontSize:11, color:C.inkFaint, marginTop:8 }}>Last saved: {savedName}</div>}
        </div>
      )}
    </div>
  );
}

function RoastMode({ profile, onExit, onNextBatch, inventory, setInventory }) {
  const { charge, eor, fcTarget, totalMin, dryEndMin, maillardEndMin, dtr, ibs, tapTarget, cv, cultivarNote, origin, batchKg, cultivar, residualNote, process } = profile;
  const agtronPredict = predictAgtron(eor, cultivar);

  // (Scroll-lock experiment removed — reverted back to the page's normal
  // scroll behaviour after the sticky-chart layout it was compensating for
  // was itself reverted below.)


  // ── Roast steps table (editable) ──
  const [steps, setSteps] = useState(() => (profile.steps && profile.steps.length ? profile.steps : generateRoastSteps(profile)));
  const [editingCell, setEditingCell] = useState(null); // "rowIdx-field"
  const [stepsTableOpen, setStepsTableOpen] = useState(false);
  const updateStep = (idx, field, val) => {
    setSteps(s => s.map((row,i) => i===idx ? {...row, [field]: val} : row));
  };

  // ── Timer ──
  const [sec, setSec] = useState(0);
  const secRef = useRef(0);
  useEffect(() => { secRef.current = sec; }, [sec]);
  // Seconds since charge right now — exact even in a background tab, where
  // the on-screen clock may not have caught up yet.
  const nowSec = () => {
    const c = clockRef.current;
    return (runningRef.current && c && c.runStart != null) ? c.base + (Date.now() - c.runStart) / 1000 : secRef.current;
  };
  const [running, setRunning] = useState(false);
  // Distinct from Pause — once a roast is actually ended, the timer can't be
  // casually resumed by mis-tapping the same button; Reset is the only way back.
  const [roastEnded, setRoastEnded] = useState(false);
  const runningRef = useRef(false);
  useEffect(() => { runningRef.current = running; }, [running]);
  const timerRef = useRef(null);
  // The clock is measured from the real time it started (plus whatever it
  // already showed), not by counting ticks. Browsers slow timers right down
  // in background tabs — counting ticks would fall behind whenever you
  // switched tab mid-roast.
  const clockRef = useRef({ runStart: null, base: 0 });
  useEffect(() => {
    if (running) {
      clockRef.current = { runStart: Date.now(), base: sec };
      timerRef.current = setInterval(() => {
        const c = clockRef.current;
        if (c.runStart != null) setSec(c.base + Math.floor((Date.now() - c.runStart) / 1000));
      }, 250);
    } else {
      clearInterval(timerRef.current);
      clockRef.current = { runStart: null, base: sec };
    }
    return () => clearInterval(timerRef.current);
  }, [running]);

  const fmt = s => `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;
  const minNow = sec / 60;

  // Current row = the step whose time is closest to (but not past) current time, or next upcoming
  const currentStepIdx = (() => {
    if (!running && sec === 0) return 0;
    let idx = 0;
    for (let i = 0; i < steps.length; i++) {
      if (minNow >= steps[i].time) idx = i;
    }
    return idx;
  })();

  // ── Events ──
  const [events, setEvents] = useState([]);
  const [beanTemp, setBeanTemp] = useState("");
  const [ror, setRor] = useState("");
  const [burner, setBurner] = useState("");

  // ── Live bridge connection ──────────────────────────────────────────────
  // Listens for a local FDC Live Bridge (see /fdc-live-bridge) broadcasting
  // real readings off the Loring over MODBUS. When connected, incoming
  // readings feed straight into the same beanTemp/ror/burner state used for
  // manual entry above — Roast Mode doesn't need to know or care whether a
  // number came from a person typing or the machine itself. When no bridge
  // is present, this simply never connects and manual entry works exactly
  // as it always has — nothing here changes that path.
  //
  // While the roast is running, live readings also log themselves straight
  // onto the curve — no need to also flip on the separate manual Auto-log
  // toggle (that one's still there, untouched, for anyone reading numbers
  // off the touchscreen by eye and typing them in). A live connection is
  // its own reason to log continuously; it shouldn't need a second switch.
  // ── Phase alerts toggle — persisted, on by default ──
  const [alertsEnabled, setAlertsEnabled] = useState(() => {
    try { const v = localStorage.getItem("fdc_alerts_enabled"); return v === null ? true : v === "true"; } catch { return true; }
  });
  useEffect(() => { try { localStorage.setItem("fdc_alerts_enabled", String(alertsEnabled)); } catch {} }, [alertsEnabled]);

  // Recent raw live readings (last 2 minutes) — used to calculate RoR and to
  // spot the charge and the drop from the temperature itself.
  const liveBufRef = useRef([]);
  const [liveTick, setLiveTick] = useState(0);
  const chargeDetectedRef = useRef(false);
  // The Loring's own Charge / Drop signal (sent by the bridge). When it arrives
  // it is parked here and acted on at the next tick, so it can see the current state.
  const machineSignalRef = useRef(null);
  // A sudden fall is only a *possible* charge — an empty, preheating Loring
  // swings by 40°C+ on its own. It becomes a charge once there's proof.
  const chargeCandRef = useRef(null);
  // The real-world moment of charge — lines the audio recording up with the curve.
  const chargeWallMsRef = useRef(null);
  const [audioMeta, setAudioMeta] = useState(null);
  const [chargeChecking, setChargeChecking] = useState(false);
  const dropDetectedRef = useRef(false);
  // RoR is calculated automatically unless a number is typed into the box by
  // hand; clearing the box hands it back to the calculation.
  const [rorTyped, setRorTyped] = useState(false);
  const rorTypedRef = useRef(false);
  const setRorByHand = (v) => { setRor(v); const typed = v !== ""; setRorTyped(typed); rorTypedRef.current = typed; };

  const [liveStatus, setLiveStatus] = useState("connecting"); // "connecting" | "connected" | "disconnected"
  const [liveExtra, setLiveExtra] = useState(null); // { et, inlet, stack } for display only
  const wsRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;

    const connect = () => {
      if (cancelled) return;
      let ws;
      try {
        ws = new WebSocket("ws://localhost:8080");
      } catch {
        setLiveStatus("disconnected");
        retryTimer = setTimeout(connect, 4000);
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        // Reaching the bridge only proves the bridge PROCESS is running —
        // it says nothing about whether the roaster itself is actually
        // connected. Deliberately NOT setting "connected" here; staying
        // at "connecting" until an explicit status message (or a real
        // reading) confirms the roaster side is genuinely live.
      };

      ws.onmessage = (event) => {
        if (cancelled) return;
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "status") {
            setLiveStatus(msg.roasterConnected ? "connected" : "connecting");
            if (!msg.roasterConnected) setLiveExtra(null);
            return;
          }
          if (msg.type === "event") {
            // The roaster itself says the roast started (charge) or ended (drop).
            if (msg.event === "charge" || msg.event === "drop") {
              machineSignalRef.current = { kind: msg.event, ms: Date.now(), bt: typeof msg.bt === "number" ? msg.bt : null };
              setLiveTick(t => t + 1);
            }
            return;
          }
          if (msg.type !== "reading") return;
          setLiveStatus("connected"); // an actual reading is itself proof the roaster is connected
          if (typeof msg.bt === "number") {
            setBeanTemp(String(msg.bt));
            const nowMs = Date.now();
            const buf = liveBufRef.current;
            const envT = typeof msg.et === "number" ? msg.et : (typeof msg.inlet === "number" ? msg.inlet : null);
            buf.push({ ms: nowMs, bt: msg.bt, env: envT, burner: burnerFromBridgeMessage(msg),
              et: typeof msg.et === "number" ? msg.et : null, inlet: typeof msg.inlet === "number" ? msg.inlet : null, stack: typeof msg.stack === "number" ? msg.stack : null });
            while (buf.length && nowMs - buf[0].ms > 180000) buf.shift();
          }
          if (typeof msg.burner === "number") setBurner(String(burnerFromBridgeMessage(msg)));
          // RoR is worked out here from the bean temps over the last 30s; the
          // machine's own figure is only used until there's enough data.
          const calcRor = calcRoRFromSeries(liveBufRef.current.map(p => ({ s: p.ms / 1000, bt: p.bt })), 30);
          const rorVal = calcRor != null ? calcRor : (typeof msg.ror === "number" ? msg.ror : null);
          if (rorVal != null) setRor(String(rorVal));
          setLiveExtra({ et: msg.et, inlet: msg.inlet, stack: msg.stack });

          // The roast is actually underway (Start has been tapped) — log
          // this reading straight onto the curve, same shape logPoint()
          // below produces, using the live BT/RoR/Burner directly rather
          // than re-reading state (avoids a stale-closure round trip).
          if (runningRef.current && typeof msg.bt === "number") {
            setDataPoints(pts => [...pts, {
              t: nowSec() / 60,
              temp: msg.bt,
              ror: rorVal,
              burner: burnerFromBridgeMessage(msg),
              // Return air, inlet and stack — kept so each roast can be checked
              // later for what the Loring's airflow does around first crack.
              ...(typeof msg.et === "number" ? { et: msg.et } : {}),
              ...(typeof msg.inlet === "number" ? { inlet: msg.inlet } : {}),
              ...(typeof msg.stack === "number" ? { stack: msg.stack } : {}),
            }]);
          }
          setLiveTick(t => t + 1);
        } catch { /* ignore malformed message, keep listening */ }
      };

      ws.onclose = () => {
        if (cancelled) return;
        setLiveStatus("disconnected");
        setLiveExtra(null);
        retryTimer = setTimeout(connect, 4000); // keep trying — bridge may start later, or roaster may power on later
      };

      ws.onerror = () => { /* onclose fires right after; retry handled there */ };
    };

    connect();
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  // ── Live curve data — build the real roast curve as you go ──
  const [dataPoints, setDataPoints] = useState([]); // { t: minutes, temp, ror, burner }

  // Read once when Roast Mode opens (this used to re-read the whole history
  // every second).
  const [referenceOptions] = useState(() => {
    let hist = [];
    try { hist = JSON.parse(localStorage.getItem("fdc_history") || "[]"); } catch { hist = []; }
    return hist.filter(r => sameCoffee(r.origin, origin) && Array.isArray(r.curve) && r.curve.length > 2);
  });
  const [referenceRoastId, setReferenceRoastId] = useState("");
  const referenceCurve = (() => {
    const match = referenceOptions.find(r => String(r.id) === String(referenceRoastId));
    if (!match) return null;
    return match.curve.map(p => ({ t: p.t, temp: p.temp })).filter(p => p.temp != null);
  })();

  const getPrediction = () => {
    const recent = dataPoints.filter(p => p.temp != null).slice(-6);
    if (recent.length < 3) return null;
    const t0 = recent[0].t, tN = recent[recent.length - 1].t;
    if (tN - t0 <= 0) return null;
    const nRec = recent.length;
    const sumT = recent.reduce((a, p) => a + p.t, 0), sumTemp = recent.reduce((a, p) => a + p.temp, 0);
    const sumTT = recent.reduce((a, p) => a + p.t * p.t, 0), sumTTemp = recent.reduce((a, p) => a + p.t * p.temp, 0);
    const denom = nRec * sumTT - sumT * sumT;
    if (denom === 0) return null;
    const slope = (nRec * sumTTemp - sumT * sumTemp) / denom;
    const intercept = (sumTemp - slope * sumT) / nRec;
    const nowT = tN, nowTemp = slope * nowT + intercept;
    const w = Math.max(0, Math.min(1, nowT / totalMin));
    const liveDropTime = slope > 0.05 ? nowT + (eor - nowTemp) / slope : totalMin;
    const predDropTime = totalMin * (1 - w) + liveDropTime * w;
    const liveFinalTemp = nowTemp + slope * Math.max(0, totalMin - nowT);
    const predFinalTemp = eor * (1 - w) + liveFinalTemp * w;
    const rangeTime = 1.0 * (1 - w) + 0.25 * w;
    const rangeTemp = 5 * (1 - w) + 1 * w;
    return {
      dropTimeMin: Math.max(0, Math.round(predDropTime * 10) / 10),
      dropRangeMin: Math.round(rangeTime * 10) / 10,
      finalTemp: Math.round(predFinalTemp),
      finalRange: Math.round(rangeTemp * 10) / 10,
    };
  };

  // ── Draft autosave ───────────────────────────────────────────────────────
  // Live logging can build up ten or more minutes of genuinely irreplaceable
  // data with nobody re-typing anything — an accidental Exit tap, a browser
  // refresh, or a crashed tab would previously lose all of it silently. This
  // writes the in-progress curve to localStorage every time a point is
  // added, and is cleared once the roast is actually saved to History. If
  // Roast Mode is reopened for the same batch after an unexpected exit, the
  // draft is picked back up automatically rather than starting from empty.
  // Single fixed slot, not keyed per-batch — profile.batch isn't actually
  // set yet when Roast Mode first opens (the batch number gets typed in
  // later, in the weigh-in step below), so keying on it collided every
  // un-batched roast onto the same slot. Realistically only one roast is
  // ever genuinely in progress at a time anyway, so one slot is correct,
  // not a compromise.
  const draftKey = "fdc_roast_draft_active";
  useEffect(() => {
    if (dataPoints.length === 0) return;
    try { localStorage.setItem(draftKey, JSON.stringify({ dataPoints, events, sec, savedAt: Date.now() })); } catch { /* storage full or unavailable — not fatal, just no recovery this time */ }
  }, [dataPoints, events, sec]);

  const [draftRecovered, setDraftRecovered] = useState(false);
  useEffect(() => {
    if (dataPoints.length > 0) return; // don't clobber a roast already in progress
    try {
      const saved = JSON.parse(localStorage.getItem(draftKey) || "null");
      if (saved && Array.isArray(saved.dataPoints) && saved.dataPoints.length > 0) {
        setDataPoints(saved.dataPoints);
        if (Array.isArray(saved.events)) setEvents(saved.events);
        if (typeof saved.sec === "number") setSec(saved.sec);
        setDraftRecovered(true);
      }
    } catch { /* no valid draft — normal case, nothing to recover */ }
    // Deliberately re-checks whenever dataPoints.length changes rather than
    // running strictly once — but the guard above means once a roast is
    // actually underway (dataPoints.length > 0), every subsequent call is an
    // immediate, harmless no-op. Listing the real dependency here instead of
    // suppressing the warning is what actually broke the last deploy — a
    // disable-comment referencing a rule this project's ESLint config
    // doesn't have installed, treated as a hard error under CI=true.
  }, [dataPoints.length]);

  const clearDraft = () => { try { localStorage.removeItem(draftKey); } catch {} };

  const logPoint = () => {
    const t = parseFloat(beanTemp);
    if (!t) return;
    setDataPoints(pts => withCalcRoR(pts, { t: sec/60, temp: t, ror: rorTyped && ror ? parseFloat(ror) : null, burner: burner?parseFloat(burner):null }));
  };

  // ── Auto-log: samples whatever's in Bean Temp every N seconds while running ──
  const [autoLog, setAutoLog] = useState(false);
  const [autoLogInterval, setAutoLogInterval] = useState(10);
  const autoLogRef = useRef(null);
  const beanTempRef = useRef(beanTemp);
  const rorRef = useRef(ror);
  const burnerRef = useRef(burner);
  useEffect(() => { beanTempRef.current = beanTemp; }, [beanTemp]);
  useEffect(() => { rorRef.current = ror; }, [ror]);
  useEffect(() => { burnerRef.current = burner; }, [burner]);

  useEffect(() => {
    // Also stops if a live connection shows up mid-roast while this was
    // already switched on from before — live logging takes over, this
    // shouldn't keep firing in the background just because the toggle
    // state itself is still "on" from earlier.
    if (autoLog && running && liveStatus !== "connected") {
      autoLogRef.current = setInterval(() => {
        const t = parseFloat(beanTempRef.current);
        if (!t) return;
        setDataPoints(pts => withCalcRoR(pts, { t: nowSec()/60, temp: t, ror: rorTypedRef.current && rorRef.current ? parseFloat(rorRef.current) : null, burner: burnerRef.current?parseFloat(burnerRef.current):null }));
      }, autoLogInterval * 1000);
    } else {
      clearInterval(autoLogRef.current);
    }
    return () => clearInterval(autoLogRef.current);
  }, [autoLog, running, autoLogInterval, liveStatus]);

  const logEvent = (name) => {
    const ev = {
      time: fmt(sec), name,
      temp: beanTemp ? parseFloat(beanTemp) : null,
      ror: ror ? parseFloat(ror) : null,
      burner: burner ? parseFloat(burner) : null,
    };
    // Tapping Charge / Turning point / Drop yourself overrides one the Hub
    // detected on its own — you always have the last word.
    setEvents(e => e.some(x => x.name === name && x.auto) ? e.map(x => (x.name === name && x.auto) ? ev : x) : [...e, ev]);
    if (beanTemp) setDataPoints(pts => withCalcRoR(pts, { t: sec/60, temp: parseFloat(beanTemp), ror: rorTyped && ror ? parseFloat(ror) : null, burner: burner?parseFloat(burner):null }));
  };

  // ── Manual entry: keep the RoR box filled from the logged curve ──
  useEffect(() => {
    if (liveStatus === "connected" || rorTyped) return;
    const last = dataPoints[dataPoints.length - 1];
    if (last && last.ror != null) setRor(String(last.ror));
  }, [dataPoints, liveStatus, rorTyped]);

  // ── Turning point — the lowest bean temp after charge, logged by itself
  // once the temperature has clearly started climbing again. Works for live
  // and hand-typed readings alike. ──
  useEffect(() => {
    if (!running || events.some(e => e.name === "Turning point")) return;
    const pts = dataPoints.filter(p => p.temp != null && p.t > 0.25 && p.t < 4);
    if (pts.length < 3) return;
    const minPt = pts.reduce((m, p) => p.temp < m.temp ? p : m, pts[0]);
    const after = pts.filter(p => p.t > minPt.t);
    if (after.length < 2) return;
    if (after.slice(-2).every(p => p.temp >= minPt.temp + 1.5)) {
      setEvents(e => e.some(x => x.name === "Turning point") ? e : [...e, { time: fmt(Math.round(minPt.t * 60)), name: "Turning point", temp: minPt.temp, ror: null, burner: minPt.burner ?? null, auto: true }]);
    }
  }, [dataPoints, running, events]);

  // ── Charge and drop, spotted from the live bean temperature ──
  // Charge: a sudden fall of 15°C+ from a hot, preheated drum — the clock is
  // set to start from the moment the temperature peaked, not when the Hub
  // noticed. Drop: after first crack (or late enough in the roast), a fall
  // of 10°C+ from the peak — the roast is ended at that peak. Only while a
  // live connection is up; Start and End still work by hand as always.
  useEffect(() => {
    if (liveStatus !== "connected") return;
    const buf = liveBufRef.current;
    if (buf.length < 3) return;
    const last = buf[buf.length - 1], prev = buf[buf.length - 2];
    // The moment of charge/drop is the LAST reading that was still at the
    // peak (within a small tolerance for probe noise) — not the very highest
    // reading, which can sit a few seconds earlier on a gently wobbling curve.
    const peakIn = (ms, tol) => {
      const w = buf.filter(p => last.ms - p.ms <= ms);
      const top = w.reduce((m, p) => p.bt > m.bt ? p : m, w[0]);
      const atPeak = w.filter(p => p.bt >= top.bt - tol);
      return { ...atPeak[atPeak.length - 1], bt: top.bt };
    };
    const burnerNow = burner ? parseFloat(burner) : null;

    // ── The Loring's own signal beats guessing from the temperature ──
    const sig = machineSignalRef.current;
    if (sig) {
      machineSignalRef.current = null;
      if (sig.kind === "charge") {
        if (!running && !roastEnded && sec === 0 && dataPoints.length === 0 && !chargeDetectedRef.current && !events.some(e => e.name === "Charge")) {
          chargeDetectedRef.current = true;
          chargeCandRef.current = null;
          setChargeChecking(false);
          const btAtCharge = sig.bt != null ? sig.bt : last.bt;
          const burnerAtCharge = typeof last.burner === "number" ? last.burner : burnerNow;
          const since = buf.filter(p => p.ms >= sig.ms);
          const elapsed = Math.max(0, Math.round((last.ms - sig.ms) / 1000));
          const seed = since.map(p => ({ t: Math.max(0, (p.ms - sig.ms) / 60000), temp: p.bt, ror: null, burner: typeof p.burner === "number" ? p.burner : burnerNow,
            ...(p.et != null ? { et: p.et } : {}), ...(p.inlet != null ? { inlet: p.inlet } : {}), ...(p.stack != null ? { stack: p.stack } : {}) }));
          secRef.current = elapsed;
          setSec(elapsed);
          setDataPoints(seed);
          setEvents(e => [...e, { time: fmt(0), name: "Charge", temp: btAtCharge, ror: null, burner: burnerAtCharge, auto: true, confirmed: "machine" }]);
          setRunning(true);
        }
        return;
      }
      if (sig.kind === "drop") {
        if (running && !roastEnded && !events.some(e => e.name === "Drop")) {
          dropDetectedRef.current = true;
          const dropSec = Math.max(0, Math.round(nowSec() - (Date.now() - sig.ms) / 1000));
          const btAtDrop = sig.bt != null ? sig.bt : last.bt;
          setRunning(false);
          setRoastEnded(true);
          secRef.current = dropSec;
          setSec(dropSec);
          setDataPoints(pts => pts.filter(p => p.t <= dropSec / 60 + 0.001));
          setEvents(e => [...e, { time: fmt(dropSec), name: "Drop", temp: btAtDrop, ror: null, burner: typeof last.burner === "number" ? last.burner : burnerNow, auto: true, confirmed: "machine" }]);
        }
        return;
      }
    }

    if (!running && !roastEnded && sec === 0 && dataPoints.length === 0 && !chargeDetectedRef.current && !events.some(e => e.name === "Charge")) {
      // Step 1 — a sudden fall from a hot drum marks a possible charge.
      // Step 2 — it's confirmed by what happens next on a real charge:
      //   • the burner is turned up (you set the charge burner), or
      //   • starting near the planned charge temperature, the temperature
      //     turns and climbs gently, like beans do (2–25°/min).
      // Tuned on real Loring S70 readings (29 Sept): preheat swings of 40°C+
      // in 30s, and slow 9°/min reheats from 240°+, are all rejected.
      // An empty drum instead keeps falling, or rebounds fast with the burner
      // untouched — those are dropped and the Hub keeps waiting.
      const cand = chargeCandRef.current;
      if (!cand) {
        const peak = peakIn(20000, 1.0);
        if (peak.bt >= 120 && last.ms > peak.ms && last.bt <= peak.bt - 15 && prev.bt <= peak.bt - 5) {
          chargeCandRef.current = { ms: peak.ms, bt: peak.bt, burner: typeof peak.burner === "number" ? peak.burner : burnerNow };
          setChargeChecking(true);
        }
        return;
      }
      const since = buf.filter(p => p.ms >= cand.ms);
      const ageS = (last.ms - cand.ms) / 1000;
      const burnerUp = cand.burner != null && since.some(p => typeof p.burner === "number" && p.burner >= cand.burner + 10);
      const minP = since.reduce((mm, p) => p.bt < mm.bt ? p : mm, since[0]);
      const climbS = (last.ms - minP.ms) / 1000;
      const climbRor = climbS >= 30 ? calcRoRFromSeries(since.filter(p => p.ms >= minP.ms && last.ms - p.ms <= 30000).map(p => ({ s: p.ms / 1000, bt: p.bt })), 30) : null;
      // A gentle climb only counts as proof when the fall started near this
      // recipe's charge temperature — an empty drum reheating from 240°+ climbs
      // gently too. (Turning the burner up counts wherever it happens.)
      const nearChargeTemp = Math.abs(cand.bt - (profile.chargeProbe ?? charge)) <= 25;
      const steadyClimb = nearChargeTemp && climbRor != null && climbRor >= 2 && climbRor <= 25 && last.bt >= minP.bt + 3;
      const fastRebound = climbRor != null && climbRor > 30;
      if (burnerUp || steadyClimb) {
        chargeDetectedRef.current = true;
        chargeCandRef.current = null;
        setChargeChecking(false);
        const elapsed = Math.max(0, Math.round((last.ms - cand.ms) / 1000));
        const seed = since.map(p => ({ t: (p.ms - cand.ms) / 60000, temp: p.bt, ror: null, burner: typeof p.burner === "number" ? p.burner : burnerNow,
          ...(p.et != null ? { et: p.et } : {}), ...(p.inlet != null ? { inlet: p.inlet } : {}), ...(p.stack != null ? { stack: p.stack } : {}) }));
        secRef.current = elapsed;
        setSec(elapsed);
        setDataPoints(seed);
        setEvents(e => [...e, { time: fmt(0), name: "Charge", temp: cand.bt, ror: null, burner: cand.burner, auto: true, confirmed: burnerUp ? "burner" : "climb" }]);
        setRunning(true);
      } else if (fastRebound || ageS > 120) {
        chargeCandRef.current = null;
        setChargeChecking(false);
      }
      return;
    }

    if (running && !roastEnded && !dropDetectedRef.current && !events.some(e => e.name === "Drop")) {
      // After the turning point, bean temp only ever climbs until the beans
      // leave the drum. So once it has fallen 4°C+ below the roast's peak and
      // stayed there, the beans are out — and the drop was at that peak.
      const tpEvt = events.find(e => e.name === "Turning point");
      if (!tpEvt || nowSec() < 240) return;
      const [tm, ts] = String(tpEvt.time).split(":").map(x => parseInt(x) || 0);
      const tpS = tm * 60 + ts;
      const post = dataPoints.filter(p => p.temp != null && p.t * 60 >= tpS);
      if (post.length < 10) return;
      // Safety: never call a drop early in the roast, or from a peak that is
      // nowhere near the end temperature — the Hub's probe can read oddly
      // around the charge, and a wrong auto-drop would end the roast record.
      if (nowSec() < Math.max(240, (totalMin || 6) * 60 * 0.7)) return;
      const peakPt = post.reduce((mm, p) => p.temp >= mm.temp ? p : mm, post[0]);
      if (peakPt.temp < (eor || 200) - 25 || nowSec() - peakPt.t * 60 > 90) return;
      const tail = post.slice(-2);
      if (nowSec() - peakPt.t * 60 >= 8 && tail.length === 2 && tail.every(p => p.t > peakPt.t && p.temp <= peakPt.temp - 4)) {
        dropDetectedRef.current = true;
        const dropSec = Math.round(peakPt.t * 60);
        setRunning(false);
        setRoastEnded(true);
        secRef.current = dropSec;
        setSec(dropSec);
        setDataPoints(pts => pts.filter(p => p.t <= peakPt.t + 0.001));
        setEvents(e => [...e, { time: fmt(dropSec), name: "Drop", temp: peakPt.temp, ror: null, burner: peakPt.burner ?? burnerNow, auto: true }]);
      }
    }
  }, [liveTick]);

  // ── Batch number — set before the roast (or carried in from the queue) ──
  const [batchId, setBatchId] = useState(profile.batch || "");
  const [showMore, setShowMore] = useState(false);
  const [nextChargeDelta, setNextChargeDelta] = useState(0);
  const [suggestionDecided, setSuggestionDecided] = useState(false);
  const [finishingDay, setFinishingDay] = useState(false);

  // ── Weights ──
  const [green, setGreen] = useState(batchKg || "");
  const [roasted, setRoasted] = useState("");
  // ── Roast date — defaults to today, editable in case a roast starts after
  // midnight or is being logged for an earlier day ──
  const [roastDate, setRoastDate] = useState(localIsoDate());
  // ── Live notes — jotted during the roast itself, saved straight onto the
  // history record rather than only being addable afterwards ──
  const [liveNotes, setLiveNotes] = useState("");
  // ── End-of-day Cool-Down card (Loring manual p. 47) ──
  const [showCoolDown, setShowCoolDown] = useState(false);
  const [coolDownTicks, setCoolDownTicks] = useState({});
  const lossVal = () => {
    const g = parseFloat(green), r = parseFloat(roasted);
    return g && r && g > 0 ? ((g - r) / g * 100).toFixed(1) + "%" : "—";
  };

  // ── Dev triangle — uses shared calcDevTriangle utility ──
  const [fcTemp, setFcTemp] = useState("197");
  const [devTime, setDevTime] = useState("");
  const [devRoR, setDevRoR] = useState("");
  const [devEnd, setDevEnd] = useState("");
  const solveTriangle = () => {
    const result = calcDevTriangle({ devTimeMin: devTime, avgRoR: devRoR, fcTemp, endTemp: devEnd });
    if (!result) return;
    if (result.solvedFor === "endTemp") setDevEnd(String(result.endTemp));
    else if (result.solvedFor === "avgRoR") setDevRoR(String(result.avgRoR));
    else if (result.solvedFor === "devTimeMin") setDevTime(String(result.devTimeMin));
  };

  // ── Live DTR ──
  const fcEvent = events.find(e => e.name === "First crack");
  const fcSec = fcEvent ? (parseInt(fcEvent.time.split(":")[0])*60 + parseInt(fcEvent.time.split(":")[1])) : null;
  const dtrLive = fcSec && sec > fcSec ? (((sec - fcSec) / sec) * 100).toFixed(1) : null;
  const dtrOk = dtrLive ? parseFloat(dtrLive) >= 18 && parseFloat(dtrLive) <= 25 : null;

  // ── MAI Time — colour change (yellow point) to First Crack. A distinct lever from
  // development time: longer MAI time builds more floral structure/complexity and
  // mouthfeel; shorter gives simpler, less-structured florals. Tracked separately
  // from DTR since it's driven by different chemistry (pre-FC Maillard progression).
  const yellowEvent = events.find(e => e.name === "Yellow point");
  const yellowSec = yellowEvent ? (parseInt(yellowEvent.time.split(":")[0])*60 + parseInt(yellowEvent.time.split(":")[1])) : null;
  const maiTimeSec = (yellowSec !== null && fcSec !== null) ? fcSec - yellowSec : null;
  const maiTimeStr = maiTimeSec !== null ? `${Math.floor(maiTimeSec/60)}:${String(maiTimeSec%60).padStart(2,"0")}` : null;

  // ── Phase logic ──
  const phases = [
    { id:"preheat",  label:"Pre-charge",    minStart: 0,            minEnd: 0.5 },
    { id:"drying",   label:"Drying",         minStart: 0.5,          minEnd: dryEndMin },
    { id:"maillard", label:"Maillard",        minStart: dryEndMin,    minEnd: maillardEndMin },
    { id:"fc",       label:"Approaching FC",  minStart: maillardEndMin, minEnd: fcTarget },
    { id:"dev",      label:"Development",     minStart: fcTarget,     minEnd: totalMin },
    { id:"drop",     label:"Drop",            minStart: totalMin,     minEnd: 99 },
  ];
  const activePhase = running || sec > 0
    ? (phases.find(p => minNow >= p.minStart && minNow < p.minEnd) || phases[phases.length-1]).id
    : "preheat";

  const phaseColors = { preheat:"#7A6E63", drying:"#4A90D9", maillard:C.amber, fc:"#E8803A", dev:C.green, drop:C.red };
  const phaseColor = phaseColors[activePhase] || C.inkFaint;

  // ── Continuous auto-save into History ────────────────────────────────────
  // Writes a REAL History record the moment any actual roast data exists —
  // a logged point or event — and keeps updating that same record (via a
  // stable id generated once per session) as the roast progresses. If a
  // session ends at any point, for any reason, whatever was captured is
  // already sitting in Roast History, not waiting on a button click at the
  // very end. The manual "Save roast to history" button below just updates
  // this same record with final weights, rather than creating a second one.
  const sessionRecordIdRef = useRef(null);
  // First moment the clock runs: the charge (auto-detected charges set the
  // clock back to the real moment first, so this is exact either way).
  useEffect(() => { if (running && chargeWallMsRef.current == null) chargeWallMsRef.current = Date.now() - sec * 1000; }, [running]);
  // Recording saved: note it on the roast (and on the copy already in history).
  const onAudioSaved = (meta) => {
    setAudioMeta(meta);
    try {
      const id = sessionRecordIdRef.current;
      if (!id) return;
      const hist = JSON.parse(localStorage.getItem("fdc_history") || "[]");
      localStorage.setItem("fdc_history", JSON.stringify(hist.map(r => r.id === id ? { ...r, audio: meta } : r)));
    } catch { /* the file itself is saved regardless */ }
  };
  const buildHistoryRecord = (id) => {
    const curveAdherence = computeCurveAdherence(dataPoints, steps, dryEndMin, fcTarget, totalMin);
    // eor is the ACTUAL temperature at Drop, not the planned target —
    // logEvent("Drop") already captures whatever Bean °C reads at that
    // moment. Falls back to the planned target only if Drop was never
    // logged, but the real result is what gets recorded whenever it's
    // available — this is what Roast QC's EoR chart and the Agtron
    // calibration are meant to track, not the plan.
    const dropEvent = events.find(e => e.name === "Drop");
    const actualEor = (dropEvent && dropEvent.temp != null) ? dropEvent.temp : (eor || "");
    return { alerts: alertLog.map(({ time, title, level }) => ({ time, title, level })), fcSignals, ...(audioMeta ? { audio: audioMeta } : {}), id, batch:batchId||"", origin:origin||"", green, roasted, loss:lossVal(), score:"", agtron:"", agtronPredicted:agtronPredict.predicted, cultivar:cultivar||"", process:process||"", eor:actualEor, eorTarget:eor||"", notes:liveNotes, events, curve:compactCurve(dataPoints), curveAdherence, maiTime:maiTimeStr, date:roastDate,
 // Carried through from the Profile Builder form (via the full "...form"
 // spread already passed into Roast Mode) so a later "Repeat this roast"
 // can restore the exact inputs used, not just the coffee's static
 // Inventory attributes.
 masl:profile.masl||"", moisture:profile.moisture||"", density:profile.density||"", cupProfile:profile.cupProfile||"",
 chargeOverride:profile.chargeOverride||"", eorOverride:profile.eorOverride||"", batchNumToday:profile.batchNumToday||"", prevRoastLevel:profile.prevRoastLevel||"" };
  };
  // Keeps the roast safe in Roast History as it goes. It used to rewrite (and
  // upload) the whole history on every single reading — about once a second.
  // Now: straight away when an event is logged or the roast ends, otherwise
  // every 15 seconds. The recovery copy below still saves every reading, so
  // nothing in between is ever at risk.
  const lastAutosaveRef = useRef({ at: 0, sig: "" });
  const autosaveNow = () => {
    if (dataPoints.length === 0 && events.length === 0) return;
    if (!sessionRecordIdRef.current) sessionRecordIdRef.current = Date.now();
    const r = buildHistoryRecord(sessionRecordIdRef.current);
    try {
      const hist = JSON.parse(localStorage.getItem("fdc_history") || "[]");
      const idx = hist.findIndex(h => h.id === r.id);
      const updated = idx >= 0 ? hist.map(h => h.id === r.id ? r : h) : [r, ...hist];
      localStorage.setItem("fdc_history", JSON.stringify(updated));
    } catch { /* storage full or unavailable — the recovery copy still holds the roast */ }
  };
  useEffect(() => {
    if (dataPoints.length === 0 && events.length === 0) return;
    const sig = events.map(e => `${e.name}@${e.time}`).join("|") + (roastEnded ? "|end" : "");
    const due = Date.now() - lastAutosaveRef.current.at >= 15000;
    if (sig !== lastAutosaveRef.current.sig || due) {
      lastAutosaveRef.current = { at: Date.now(), sig };
      autosaveNow();
    }
  }, [dataPoints, events, roastEnded]);
  // Leaving the tab or closing it: save right away.
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === "hidden") autosaveNow(); };
    document.addEventListener("visibilitychange", onHide);
    return () => document.removeEventListener("visibilitychange", onHide);
  });

  // ── Save ──
  const [saved, setSaved] = useState(false);
  const [savedRecord, setSavedRecord] = useState(null);
  const saveRoast = () => {
    let hist = [];
    try { hist = JSON.parse(localStorage.getItem("fdc_history")||"[]"); } catch { hist = []; }
    const id = sessionRecordIdRef.current || Date.now();
    const idx = hist.findIndex(h => h.id === id);
    const writeHist = (rec) => {
      const up = idx >= 0 ? hist.map(h => h.id === id ? rec : h) : [rec, ...hist];
      localStorage.setItem("fdc_history", JSON.stringify(up));
    };
    // Save the roast FIRST. Only once it's safely stored does green stock
    // change — so a full browser storage can never leave stock deducted for a
    // roast that wasn't saved.
    let r = buildHistoryRecord(id);
    try { writeHist(r); } catch {
      window.alert("This roast couldn't be saved — the Hub's storage on this computer is full.\n\nNothing has changed: Green stock hasn't been touched, and Roast Mode stays open so nothing is lost.\n\n1. Click ⤓ Backup to download a copy of everything.\n2. In Roasting → Roast history, remove roasts you don't need, or use \"Slim older roast curves\".\n3. Come back and press Save again.");
      return false;
    }
    r = syncInventoryForRoast({ record: r, newGreenKg: green, origin, inventory, setInventory });
    try { writeHist(r); } catch { /* same size as the save that just worked — not expected to fail */ }
    // A batch number offered from the counter is only used up once the roast is saved.
    if (batchId && batchId === peekNextBatchNumber()) generateBatchNumber();
    // Started from a queued order — mark that order roasted.
    if (profile.orderId) {
      try {
        const orders = loadOrders();
        saveOrders(orders.map(o => (String(o.id) === String(profile.orderId) && (o.status === "pending" || o.status === "queued")) ? { ...o, status: "roasted" } : o));
      } catch { /* order list unavailable — the roast itself is still saved */ }
    }
    clearDraft(); // saved for real now — the recovery copy has done its job
    setSavedRecord(r);
    setSaved(true);
  };

  // ── Guidance: next burner move, first-crack prediction, last gas change ──
  const btNow = beanTemp !== "" ? parseFloat(beanTemp) : null;
  const rorNow = ror !== "" ? parseFloat(ror) : null;
  const toSecs = str => { if (!str) return null; const [m2, s2] = str.split(":").map(x => parseInt(x)); return (m2 || 0) * 60 + (s2 || 0); };
  const tpEv = events.find(e => e.name === "Turning point");
  const tpMin = tpEv ? toSecs(tpEv.time) / 60 : 1.5;
  const maxBtAfterTp = dataPoints.filter(p => p.temp != null && p.t >= tpMin).reduce((mx, p) => Math.max(mx, p.temp), -Infinity);
  const burnerPlan = burnerPlanFromSteps(steps).map(p => ({
    ...p,
    done: p.event ? events.some(e => e.name === p.event) : (p.temp != null && maxBtAfterTp >= p.temp),
  }));
  const currentPlannedBurner = [...burnerPlan].reverse().find(p => p.done && p.burner != null)?.burner ?? null;
  const nextMove = burnerPlan.find(p => !p.done && (p.isDrop || (p.burner != null && p.burner !== currentPlannedBurner)));

  // The temperature this coffee has cracked at before — median of past roasts.
  const [pastFcTemps] = useState(() => {
    let hist = [];
    try { hist = JSON.parse(localStorage.getItem("fdc_history") || "[]"); } catch { hist = []; }
    return hist.filter(r => sameCoffee(r.origin, origin) && r.id !== sessionRecordIdRef.current && Array.isArray(r.events))
      .map(r => r.events.find(e => e.name === "First crack")?.temp).filter(t => typeof t === "number" && t > 170 && t < 215);
  });
  const planFcStep = steps.find(st => (st.phase || "").toLowerCase() === "first crack");
  const planFcTemp = planFcStep ? parseFloat(String(planFcStep.temp).replace(/[^0-9.]/g, "")) || 196 : 196;
  const fcTempExpected = pastFcTemps.length >= 2 ? [...pastFcTemps].sort((x, y) => x - y)[Math.floor(pastFcTemps.length / 2)] : planFcTemp;
  const fcBasis = pastFcTemps.length >= 2 ? `from ${pastFcTemps.length} past roasts of this coffee + current RoR` : "from the profile + current RoR";
  const fcLoggedNow = events.some(e => e.name === "First crack");
  // Once the beans pass the temperature this coffee usually cracks at, there's
  // nothing left to predict — first crack is simply due until it's tapped.
  const fcDueNow = running && !fcLoggedNow && btNow != null && btNow >= fcTempExpected;
  const fcPred = (running && !fcLoggedNow && btNow != null && btNow >= 150 && !fcDueNow) ? predictFirstCrack({ dataPoints, secNow: sec, btNow, rorNow, fcTemp: fcTempExpected }) : null;
  const fcPredSec = fcPred && fcPred.inSec != null ? sec + fcPred.inSec : null;
  const lastGasIn = fcPredSec != null ? fcPredSec - 45 - sec : null;
  const etaFor = (p) => {
    if (p.temp != null && btNow != null && rorNow != null && rorNow > 0.5 && p.temp > btNow) return Math.round((p.temp - btNow) / rorNow * 60);
    if (p.event === "First crack" && fcPred && fcPred.inSec != null) return fcPred.inSec;
    return null;
  };
  const envRorNow = liveStatus === "connected" ? calcRoRFromSeries(liveBufRef.current.filter(p => p.env != null).map(p => ({ s: p.ms / 1000, bt: p.env })), 30) : null;

  // ── First crack from the data: suggest, never log on its own ──
  const [fcSuggestion, setFcSuggestion] = useState(null);
  const [fcSignals, setFcSignals] = useState([]); // every suggestion and what was done with it — kept with the roast
  const fcDismissedPeakRef = useRef(-999);
  useEffect(() => {
    if (!running || fcLoggedNow || fcSuggestion || !tpEv) return;
    // Real time, not the on-screen clock — that can lag in a background tab.
    const nowS = Math.floor(nowSec());
    const sig = detectFirstCrackSignature({ dataPoints, secNow: nowS, fcTempExpected, sinceSec: toSecs(tpEv.time) + 60 });
    if (sig && sig.peakSec > fcDismissedPeakRef.current + 20) {
      setFcSuggestion(sig);
      setFcSignals(list => [...list, { time: fmt(sig.sec), temp: sig.temp, lift: sig.lift, dip: sig.dip, seenAt: fmt(nowS), response: "shown" }]);
    }
  }, [dataPoints, running, fcLoggedNow]);
  const acceptFcSuggestion = () => {
    if (!fcSuggestion) return;
    const sig = fcSuggestion;
    setEvents(e => e.some(x => x.name === "First crack") ? e : [...e, { time: fmt(sig.sec), name: "First crack", temp: sig.temp, ror: null, burner: burner ? parseFloat(burner) : null, source: "ror-signature" }]);
    setFcSignals(list => list.map((x, i) => i === list.length - 1 ? { ...x, response: "logged" } : x));
    setFcSuggestion(null);
  };
  const dismissFcSuggestion = () => {
    if (!fcSuggestion) return;
    fcDismissedPeakRef.current = fcSuggestion.peakSec;
    setFcSignals(list => list.map((x, i) => i === list.length - 1 ? { ...x, response: "not yet" } : x));
    setFcSuggestion(null);
  };
  // Tapping First crack yourself while a suggestion is up counts as your answer.
  useEffect(() => { if (fcLoggedNow && fcSuggestion) { setFcSuggestion(null); setFcSignals(list => list.map((x, i) => i === list.length - 1 && x.response === "shown" ? { ...x, response: "logged by hand" } : x)); } }, [fcLoggedNow]);

  // ── One alert at a time, held on screen long enough to act on ──
  const liveAlert = (alertsEnabled && running) ? getPhaseAlert({ sec, events, fcTarget, totalMin, dataPoints, steps, beanTemp, predictedFcMin: fcPredSec != null ? fcPredSec / 60 : null, envRor: envRorNow }) : null;
  const [heldAlert, setHeldAlert] = useState(null);
  const [alertLog, setAlertLog] = useState([]);
  const liveAlertTitle = liveAlert ? liveAlert.title : null;
  useEffect(() => {
    if (!liveAlert) return;
    setHeldAlert({ alert: liveAlert, until: sec + 10 });
    // Logged once per kind of alert — "running hot +8°" and "running hot +9°"
    // are the same alert with a changing number, not two alerts.
    const kindOf = t => (t || "").replace(/[+\-−]?\d+(\.\d+)?/g, "").replace(/\s+/g, " ").trim();
    setAlertLog(log => {
      const lastSame = [...log].reverse().find(x => kindOf(x.title) === kindOf(liveAlert.title));
      if (lastSame && sec - lastSame.lastSec < 60) {
        return log.map(x => x === lastSame ? { ...x, lastSec: sec } : x);
      }
      return [...log, { time: fmt(sec), sec, lastSec: sec, title: liveAlert.title, level: liveAlert.level }];
    });
  }, [liveAlertTitle, sec]);
  const shownAlert = running ? (liveAlert || (heldAlert && sec <= heldAlert.until ? heldAlert.alert : null)) : null;

  // ── Screen: before → during → after ──
  const view = roastEnded ? "after" : (!running && sec === 0 && dataPoints.length === 0) ? "before" : "during";
  const EVENT_ROW = [["Charge", "Charge"], ["Turning point", "Turning point"], ["Yellow point", "Yellowing"], ["First crack", "First crack"], ["Drop", "Drop"]];
  const nextEventName = (EVENT_ROW.find(([name]) => !events.some(e => e.name === name)) || [null])[0];
  // Leaving without saving: the roast was being kept safe in Roast history as
  // it went. Offer to remove it, so false starts don't pile up as "roasts".
  const exitRoastMode = () => {
    if (!saved && sessionRecordIdRef.current && dataPoints.length > 0) {
      const remove = window.confirm("This roast hasn't been saved.\n\nRemove it from Roast history?\n\nOK — remove it\nCancel — keep it");
      if (remove) {
        try {
          const hist = JSON.parse(localStorage.getItem("fdc_history") || "[]");
          localStorage.setItem("fdc_history", JSON.stringify(hist.filter(r => r.id !== sessionRecordIdRef.current)));
          clearDraft();
        } catch { /* leave it — it can still be deleted from Roast history */ }
      }
    }
    onExit();
  };
  const endRoastNow = () => {
    setRunning(false); setRoastEnded(true);
    if (events.find(e => e.name === "Drop")) return;
    // Pressed after the beans were already out? Then the drop was at the
    // peak a moment ago, not now — record it there.
    const tpE = events.find(e => e.name === "Turning point");
    if (tpE) {
      const tpSec = toSecs(tpE.time);
      const post = dataPoints.filter(p => p.temp != null && p.t * 60 >= tpSec && p.t * 60 >= sec - 120);
      const peakPt = post.length ? post.reduce((mm, p) => p.temp >= mm.temp ? p : mm, post[0]) : null;
      const nowBt = beanTemp !== "" ? parseFloat(beanTemp) : null;
      if (peakPt && nowBt != null && peakPt.temp - nowBt >= 2 && sec - peakPt.t * 60 >= 5 && sec - peakPt.t * 60 <= 60 && peakPt.temp >= (eor || 200) - 25) {
        const dropSec = Math.round(peakPt.t * 60);
        secRef.current = dropSec;
        setSec(dropSec);
        setDataPoints(pts => pts.filter(p => p.t <= peakPt.t + 0.001));
        setEvents(e => [...e, { time: fmt(dropSec), name: "Drop", temp: peakPt.temp, ror: null, burner: peakPt.burner ?? null }]);
        return;
      }
    }
    logEvent("Drop");
  };
  const carryOnRoasting = () => {
    // Undo an end that came too soon — the drop is taken back off and the clock carries on.
    setEvents(e => e.filter(x => x.name !== "Drop"));
    setRoastEnded(false);
    setRunning(true);
  };
  const saveAndNextBatch = () => {
    if (!saved && saveRoast() === false) return;
    if (!onNextBatch) { onExit(); return; }
    const lvl = roastLevelBucket((events.find(e => e.name === "Drop") || {}).temp || eor, cultivar);
    const nextForm = {
      ...profile,
      batchId: peekNextBatchNumber(),
      batchNumToday: String((parseInt(profile.batchNumToday) || 1) + 1),
      prevRoastLevel: lvl,
      batchKg: green || profile.batchKg,
      chargeOverride: nextChargeDelta !== 0 ? String(charge + nextChargeDelta) : (profile.chargeOverride || ""),
      orderId: undefined,
      orderLabel: undefined,
    };
    onNextBatch(buildRoastProfileFromForm(nextForm));
  };
  // Space bar logs whichever event is next — hands-free at the roaster.
  const spaceRef = useRef(null);
  spaceRef.current = () => { if (view !== "during" || !nextEventName) return; if (nextEventName === "Drop") endRoastNow(); else logEvent(nextEventName); };
  useEffect(() => {
    const onKey = (e) => {
      if (e.code !== "Space" && e.key !== " ") return;
      const tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || tag === "BUTTON") return;
      e.preventDefault();
      if (spaceRef.current) spaceRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const rmCard = { background:C.cream2, border:`1px solid ${C.border}`, borderRadius:10, padding:"16px 18px", display:"flex", flexDirection:"column", gap:12 };
  const rmLabel = { fontFamily:fontSans, fontSize:10.5, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:C.inkFaint };
  const rmHeaderBtn = { height:36, padding:"0 14px", borderRadius:6, border:"1px solid #5A554C", background:"transparent", color:"#F5F0E8", fontFamily:fontSans, fontSize:12, fontWeight:600, cursor:"pointer" };
  const coolDownCard = (
<div style={{ border:`1px solid ${C.border}`, borderRadius:8, padding:"16px 20px", marginBottom:12, background:C.cream2 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer" }} onClick={()=>setShowCoolDown(v=>!v)}>
            <div style={{ fontFamily:fontSans, fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", color:"#4A90D9" }}>
              Last batch of the day? — Loring Cool-Down sequence {showCoolDown?"▾":"▸"}
            </div>
            <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint }}>manual p. 47</div>
          </div>
          {showCoolDown && (
            <div style={{ marginTop:12 }}>
              {[
                "Tap COOL DOWN at the top of the Main Roast Screen — button turns green, shows COOLING DOWN",
                "Machine State changes to \u201CCooldown\u201D",
                "Roaster Door and Purge Gate open — DROP ROASTED shows green / OPEN",
                "Drum Paddle stays on (DRUM green)",
                "AUTO AIR COOL and MANUAL AIR COOL both turn green",
                "Burner shuts off",
                "Circulation Fan ramps to high speed (CIRC FAN green)",
                "Once cooled: power fully off including utilities, and empty the Chaff Barrel",
              ].map((step,i)=>(
                <label key={i} style={{ display:"flex", alignItems:"flex-start", gap:9, padding:"4px 0", fontFamily:fontSans, fontSize:12.5, color:C.ink, cursor:"pointer", lineHeight:1.4 }}>
                  <input type="checkbox" checked={!!coolDownTicks[i]} onChange={()=>setCoolDownTicks(t=>({...t,[i]:!t[i]}))} style={{ marginTop:2 }}/>
                  <span style={{ textDecoration: coolDownTicks[i]?"line-through":"none", color: coolDownTicks[i]?C.inkFaint:C.ink }}>{step}</span>
                </label>
              ))}
              <div style={{ fontFamily:fontSans, fontSize:11, color:C.inkFaint, marginTop:8 }}>
                Ticks are per-session only — the daily power-off task in the Cleaning tab is the one to log.
              </div>
            </div>
          )}
        </div>
  );

  // ── Styles ──
  const card = (active) => ({
    border: `1px solid ${active ? phaseColor : C.border}`,
    borderLeft: `4px solid ${active ? phaseColor : C.borderSoft}`,
    borderRadius: 8, padding: "16px 20px", marginBottom: 12,
    background: active ? "#fff" : C.cream2,
    opacity: active ? 1 : 0.55,
    transition: "all 0.3s",
  });

  const inp = { fontFamily: fontSans, fontSize: 15, color: C.ink, background: C.cream, border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 12px", outline: "none", width: "100%" };
  const bigInp = { ...inp, fontFamily: fontSerif, fontSize: 22 };

  const PhaseLabel = ({ id, label }) => {
    const isActive = activePhase === id;
    return (
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
        <div style={{ width:8, height:8, borderRadius:"50%", background: isActive ? phaseColors[id] : C.borderSoft, transition:"background 0.3s" }}/>
        <div style={{ fontFamily:fontSans, fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", color: isActive ? phaseColors[id] : C.inkFaint }}>
          {label} {isActive && "← now"}
        </div>
      </div>
    );
  };

  // ── Compact row for a phase that isn't current ──
  // Past phases collapse to a checkmark + the time they were logged; the
  // upcoming ones collapse to just their name plus a quick-log button in
  // case the roast runs ahead of the timer. Plain conditional rendering only
  // — no sticky/fixed positioning involved — this is what actually cuts the
  // page height down, without the CSS trick that caused the last problem.
  const CompactPhaseRow = ({ id, label, event, onLog, logLabel }) => {
    const idx = phases.findIndex(p => p.id === id);
    const curIdx = phases.findIndex(p => p.id === activePhase);
    const isPast = idx < curIdx;
    return (
      <div style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 12px", marginBottom:6, borderRadius:6, background: isPast ? C.cream2 : "transparent", border:`1px solid ${isPast ? C.borderSoft : "transparent"}` }}>
        <div style={{ width:8, height:8, borderRadius:"50%", flexShrink:0, background: event ? C.green : C.borderSoft }}/>
        <div style={{ fontFamily:fontSans, fontSize:11, fontWeight:600, color: isPast ? C.inkLight : C.inkFaint, flex:1 }}>{label}</div>
        {event && <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint }}>{event.time}{event.temp ? ` · ${event.temp}°C` : ""}</div>}
        {onLog && (
          <button onClick={onLog} style={{ background:"transparent", color:C.inkFaint, border:`1px solid ${C.border}`, fontFamily:fontSans, fontSize:10, padding:"3px 9px", borderRadius:4, cursor:"pointer" }}>
            {logLabel || "Log"}
          </button>
        )}
      </div>
    );
  };

  return (
    <div style={{ position:"fixed", inset:0, background:C.cream, zIndex:100, display:"flex", flexDirection:"column", overflow:"hidden" }}>

      {/* ══ HEADER ══ */}
      <div style={{ background:C_FIXED_DARK, padding:"10px 20px", display:"flex", justifyContent:"space-between", alignItems:"center", gap:16, flexShrink:0, flexWrap:"wrap" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, minWidth:0 }}>
          <BeanMark size={26}/>
          <div style={{ minWidth:0 }}>
            <div style={{ fontFamily:fontSerif, fontSize:19, color:"#F5F0E8", lineHeight:1.15 }}>{origin || "Roast"}</div>
            <div style={{ fontFamily:fontSans, fontSize:11, color:"#C9C2B5" }}>{batchId || "no batch #"} · {green || batchKg || "—"} kg · batch {profile.batchNumToday || 1} today · Manual</div>
          </div>
        </div>
        {view === "during" && (
          <div style={{ display:"flex", alignItems:"center", gap:6, fontFamily:fontSans, fontSize:12 }}>
            {[["drying","Drying"],["maillard","Maillard"],["fc","Into first crack"],["dev","Development"]].map(([id,label],i) => {
              const order = ["preheat","drying","maillard","fc","dev","drop"];
              const cur = order.indexOf(activePhase), me = order.indexOf(id);
              const isNow = cur === me, isPast = cur > me;
              return (
                <Fragment key={id}>
                  {i > 0 && <span style={{ color:"#6E705F" }}>—</span>}
                  <span style={{ padding:"4px 11px", borderRadius:20, background: isNow ? "#F5F0E8" : "transparent", color: isNow ? C_FIXED_DARK : "#C9C2B5", fontWeight: isNow ? 700 : 500 }}>{label}{isPast ? " ✓" : ""}</span>
                </Fragment>
              );
            })}
          </div>
        )}
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, fontFamily:fontSans, fontSize:10, fontWeight:700, letterSpacing:"0.06em", color:"#C9C2B5", marginRight:6 }} title={liveStatus==="connected" ? "Reading live off the roaster" : "No live connection — type readings in by hand"}>
            <span style={{ width:8, height:8, borderRadius:"50%", background: liveStatus==="connected" ? "#9DBB8C" : liveStatus==="connecting" ? C.amber : "#7A7466", display:"inline-block" }}/>
            {liveStatus==="connected" ? (running ? "LIVE · LOGGING" : "LIVE") : liveStatus==="connecting" ? "CONNECTING…" : "MANUAL ENTRY"}
          </div>
          {view === "during" && !roastEnded && sec > 0 && (
            <button onClick={() => setRunning(r=>!r)} style={rmHeaderBtn}>{running ? "Pause" : "Resume"}</button>
          )}
          <RoastAudioRecorder batchId={batchId} chargeWallMsRef={chargeWallMsRef} roastEnded={roastEnded} onSaved={onAudioSaved} />
          <button onClick={() => setShowMore(v=>!v)} style={{ ...rmHeaderBtn, background: showMore ? "#F5F0E8" : "transparent", color: showMore ? C_FIXED_DARK : "#F5F0E8" }}>{showMore ? "Close" : "More"}</button>
          {view === "during" && !roastEnded && (
            <button onClick={endRoastNow} style={{ ...rmHeaderBtn, borderColor:"#B94040", color:"#F2B8B0" }}>End roast</button>
          )}
          <button onClick={exitRoastMode} style={{ ...rmHeaderBtn, color:"#C9C2B5" }}>✕ Exit</button>
        </div>
      </div>

      {draftRecovered && (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, padding:"8px 20px", background:"#FCF3E3", borderBottom:`1px solid ${C.amber}` }}>
          <div style={{ fontFamily:fontSans, fontSize:12, color:"#7A5A1E" }}>
            ⚠ Picked up {dataPoints.length} points from an in-progress roast that wasn't saved last time — looks like this session got interrupted. Carry on from here, or clear it below to start fresh.
          </div>
          <button onClick={() => { clearDraft(); setDataPoints([]); setSec(0); setDraftRecovered(false); }} style={{ ...s.btn, fontSize:11, flexShrink:0 }}>Discard & start fresh</button>
        </div>
      )}


      <div style={{ flex:1, overflowY:"auto", position:"relative" }}>

      {/* ══ BEFORE THE ROAST ══ */}
      {view === "before" && !showMore && (
        <div style={{ padding:"22px 28px", display:"flex", flexDirection:"column", gap:18, maxWidth:1400 }}>
          <div>
            <div style={{ fontFamily:fontSans, fontSize:11, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:C.inkFaint }}>{profile.orderId ? "From the queue" : "Ready to roast"}</div>
            <div style={{ fontFamily:fontSerif, fontSize:32, color:C.ink, lineHeight:1.1, marginTop:4 }}>{origin || "Roast"}</div>
            {profile.orderLabel && <div style={{ fontFamily:fontSans, fontSize:13, color:C.inkLight, marginTop:4 }}>{profile.orderLabel}</div>}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(300px, 1fr))", gap:16 }}>
            <div style={rmCard}>
              <div style={rmLabel}>This batch</div>
              <div>
                <div style={s.fieldLabel}>Batch number</div>
                <div style={{ display:"flex", gap:8 }}>
                  <input style={s.input} value={batchId} onChange={e => setBatchId(e.target.value)} placeholder="e.g. R26-112"/>
                  <button type="button" style={{ ...s.btn, flexShrink:0 }} onClick={() => setBatchId(peekNextBatchNumber())}>Next</button>
                </div>
              </div>
              <div>
                <div style={s.fieldLabel}>Green in (kg)</div>
                <input style={s.input} type="number" step="0.1" value={green} onChange={e => setGreen(e.target.value)}/>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <div>
                  <div style={s.fieldLabel}>Batch today</div>
                  <div style={{ fontFamily:fontSerif, fontSize:22, color:C.ink }}>{profile.batchNumToday || 1}</div>
                  <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint }}>from today's roasts</div>
                </div>
                <div>
                  <div style={s.fieldLabel}>Last batch</div>
                  <div style={{ fontFamily:fontSerif, fontSize:22, color:C.ink }}>{profile.prevRoastLevel || "—"}</div>
                  <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint }}>{profile.prevRoastLevel ? "used for residual heat" : "first of the day"}</div>
                </div>
              </div>
              {referenceOptions.length > 0 && (
                <div>
                  <div style={s.fieldLabel}>Compare against</div>
                  <select style={s.input} value={referenceRoastId} onChange={e => setReferenceRoastId(e.target.value)}>
                    <option value="">— None —</option>
                    {referenceOptions.map(r => <option key={r.id} value={r.id}>{r.batch || r.date || "Roast"} — {dmy(r.date) || ""}{r.score ? ` · cup ${r.score}` : ""}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div style={rmCard}>
              <div style={rmLabel}>Targets</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                {[["Charge", `${charge}°`, residualNote ? "adjusted for residual heat" : ""], ["Start burner", `${ibs}%`, tapTarget ? `turning point ~${tapTarget}°` : ""], ["First crack", `~${fmt(Math.round((fcTarget||0)*60))}`, `at ~${Math.round(fcTempExpected)}°`], ["Drop", `${eor}°`, `DTR ${dtr || "—"} · Agtron ~${agtronPredict.range}`]].map(([l, v, sub]) => (
                  <div key={l} style={{ background:C.cream, borderRadius:8, padding:"12px 14px" }}>
                    <div style={{ fontFamily:fontSans, fontSize:11, color:C.inkFaint }}>{l}</div>
                    <div style={{ fontFamily:fontSerif, fontSize:26, color:C.ink, lineHeight:1.2 }}>{v}</div>
                    {sub && <div style={{ fontFamily:fontSans, fontSize:10.5, color:C.inkFaint }}>{sub}</div>}
                  </div>
                ))}
              </div>
              {residualNote && <div style={{ fontFamily:fontSans, fontSize:11, color:C.inkLight, lineHeight:1.5 }}>{residualNote}</div>}
            </div>
            <div style={rmCard}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
                <div style={rmLabel}>{burnerPlan.some(p => p.burner == null && !p.isDrop) ? "Roast plan — target times" : "Burner plan — by bean temp"}</div>
                <button type="button" onClick={() => { setShowMore(true); setStepsTableOpen(true); }} style={{ ...s.btnSm }}>Edit</button>
              </div>
              {burnerPlan.map(p => (
                <div key={p.key} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 12px", background:C.cream, borderRadius:6 }}>
                  <div style={{ fontFamily:fontSans, fontSize:13, fontWeight:600, color:C.ink }}>{p.isDrop ? `Drop at ${Math.round(p.temp)}°` : p.label}</div>
                  <div style={{ fontFamily:fontSans, fontSize: p.burner == null ? 14 : 15, fontWeight:700, color:C.ink }}>{p.burner != null && !p.isDrop ? `${p.burner}%` : (p.timeStr ? `by ${p.timeStr}` : "—")}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ ...rmCard, flexDirection:"row", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:14 }}>
            <div style={{ display:"flex", gap:20, flexWrap:"wrap", fontFamily:fontSans, fontSize:13, color:C.ink }}>
              {(() => {
                const inv = (inventory || []).find(i => sameCoffee(i.name || i.origin, origin));
                const stockKg = inv ? Math.round(parseFloat(inv.bagWeight || 0) * parseFloat(inv.bags || 0) * 10) / 10 : null;
                const probeCharge = profile.chargeProbe ?? charge; // the Hub's own probe reads lower than the Loring's return-air charge
                const warm = liveStatus === "connected" && btNow != null ? Math.abs(btNow - probeCharge) <= 10 : null;
                const checks = [
                  [liveStatus === "connected", liveStatus === "connected" ? "Roaster connected" : "No live connection — readings by hand"],
                  ...(warm === null ? [] : [[warm, warm ? `Warmed up — Hub probe ${btNow}°. Charge on the Loring's ${charge}° return air` : `Hub probe at ${btNow}° — still warming. Charge on the Loring's ${charge}° return air`]]),
                  ...(stockKg === null ? [] : [[stockKg >= (parseFloat(green) || 0), `${stockKg} kg ${origin} in stock`]]),
                ];
                return checks.map(([ok, text], i) => (
                  <span key={i} style={{ display:"flex", alignItems:"center", gap:7 }}><span style={{ color: ok ? C.green : C.amber, fontWeight:700 }}>{ok ? "✓" : "!"}</span>{text}</span>
                ));
              })()}
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              {liveStatus === "connected" && <div style={{ fontFamily:fontSans, fontSize:13, color: chargeChecking ? C.amber : C.inkLight }}>{chargeChecking ? "Possible charge — checking it's beans, not the drum…" : "The clock starts by itself when you charge"}</div>}
              <button onClick={() => setRunning(true)} style={{ ...s.btnPrimary, padding:"12px 22px", fontSize:14 }}>{liveStatus === "connected" ? "Start now" : "Start roast"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ DURING THE ROAST ══ */}
      {view === "during" && !showMore && (() => {
        // The recipe's early curve can't know how your probe reads straight after
        // charge, so "vs plan" starts a minute after the turning point.
        const settled = tpEv && sec >= toSecs(tpEv.time) + 60;
        const tracking = settled ? getCurveTracking({ steps, dataPoints, sec, beanTemp }) : null;
        const off = tracking ? Math.abs(tracking.delta) : 0;
        const offColour = !tracking ? C.ink : off < 5 ? C.ink : off < 8 ? C.amber : C.red;
        const stepNow = steps[currentStepIdx] || {};
        const mmss = x => `${Math.floor(Math.max(0, x) / 60)}:${String(Math.max(0, x) % 60).padStart(2, "0")}`;
        const eta = nextMove ? etaFor(nextMove) : null;
        const pred = getPrediction();
        const alert = shownAlert;
        const danger = alert && alert.level === "danger";
        const tile = { background:C.cream2, border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 16px", display:"flex", flexDirection:"column", gap:4, minWidth:0 };
        const big = { fontFamily:fontSans, fontSize:40, fontWeight:600, color:C.ink, lineHeight:1, fontVariantNumeric:"tabular-nums" };
        return (
          <div style={{ padding:"14px 20px", display:"flex", flexDirection:"column", gap:14 }}>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4, minmax(0, 1fr))", gap:12 }}>
              <div style={tile}>
                <div style={rmLabel}>Time</div>
                <div style={big}>{fmt(sec)}</div>
                <div style={{ fontFamily:fontSans, fontSize:12, color:C.inkFaint }}>{running ? (events.some(e => e.name === "Charge" && e.auto) ? "since charge (auto-detected)" : "since start") : "paused"}</div>
              </div>
              <div style={tile}>
                <div style={rmLabel}>Bean temp</div>
                <div style={big}>{btNow != null ? `${btNow}°` : "—"}</div>
                <div style={{ fontFamily:fontSans, fontSize:12, color:C.inkFaint }}>{tracking ? `plan ${Math.round(tracking.expected * 10) / 10}°` : settled ? "—" : "settling after charge"}</div>
              </div>
              <div style={tile}>
                <div style={rmLabel}>RoR</div>
                <div style={big}>{rorNow != null ? rorNow : "—"}<span style={{ fontSize:15, fontWeight:500, color:C.inkFaint }}> °/min</span></div>
                <div style={{ fontFamily:fontSans, fontSize:12, color:C.inkFaint }}>{stepNow.ror && stepNow.ror !== "—" ? `plan ${stepNow.ror}` : "calculated from bean temp"}</div>
              </div>
              <div style={{ ...tile, borderColor: off >= 5 ? offColour : C.border }}>
                <div style={rmLabel}>vs plan</div>
                <div style={{ ...big, color: offColour }}>{tracking ? `${tracking.delta > 0 ? "+" : tracking.delta < 0 ? "−" : ""}${Math.abs(tracking.delta)}°` : "—"}</div>
                <div style={{ fontFamily:fontSans, fontSize:12, color: off >= 5 ? offColour : C.inkFaint }}>{!tracking ? "from a minute after the turning point" : off < 5 ? "within ±5° · on track" : tracking.delta > 0 ? "running hot" : "running cool"}</div>
              </div>
            </div>

            <div style={{ display:"flex", gap:14, alignItems:"stretch", flexWrap:"wrap" }}>
              <div style={{ flex:"1 1 560px", minWidth:0, background:C.cream2, border:`1px solid ${C.border}`, borderRadius:10, padding:"10px 12px" }}>
                {referenceOptions.length > 0 && (
                  <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:4 }}>
                    <select style={{ ...s.input, width:"auto", fontSize:11, padding:"3px 6px" }} value={referenceRoastId} onChange={e => setReferenceRoastId(e.target.value)} title="Show a past roast of this coffee on the chart">
                      <option value="">No reference roast</option>
                      {referenceOptions.map(r => <option key={r.id} value={r.id}>vs {r.batch || r.date || "roast"}{r.score ? ` · cup ${r.score}` : ""}</option>)}
                    </select>
                  </div>
                )}
                <LiveRoastChart
                  dataPoints={dataPoints} steps={steps} charge={charge} eor={eor} totalMin={totalMin} dryEndMin={dryEndMin}
                  tapTime={1.15} secNow={sec} running={running} referenceCurve={referenceCurve} events={events}
                  height={400}
                  predFcMin={!fcLoggedNow && fcPredSec != null ? fcPredSec / 60 : null}
                  predDropMin={pred ? pred.dropTimeMin : null}
                />
              </div>

              <div style={{ flex:"0 0 340px", display:"flex", flexDirection:"column", gap:12 }}>
                {fcSuggestion && (
                  <div style={{ border:`2px solid ${C.amber}`, borderRadius:10, padding:"14px 16px", background:C.cream2 }}>
                    <div style={{ ...rmLabel, color:C.amber }}>Looks like first crack</div>
                    <div style={{ fontFamily:fontSans, fontSize:22, fontWeight:700, color:C.ink, marginTop:4 }}>{fmt(fcSuggestion.sec)} · {fcSuggestion.temp}°</div>
                    <div style={{ fontFamily:fontSans, fontSize:12, color:C.inkLight, lineHeight:1.45, marginTop:4 }}>
                      RoR lifted {fcSuggestion.lift}° then dipped {fcSuggestion.dip}° with the burner steady. Check the trier — beans swelling, crease opening, chaff, a sharp vinegar smell.
                    </div>
                    <div style={{ display:"flex", gap:8, marginTop:10 }}>
                      <button onClick={acceptFcSuggestion} style={{ ...s.btnPrimary, flex:1 }}>Log it at {fmt(fcSuggestion.sec)}</button>
                      <button onClick={dismissFcSuggestion} style={{ ...s.btn }}>Not yet</button>
                    </div>
                  </div>
                )}
                <div style={{ border:`1px solid ${C.amber}`, borderRadius:10, padding:"14px 16px", background:C.cream2 }}>
                  <div style={{ ...rmLabel, color:C.amber }}>Next move</div>
                  {!nextMove ? (
                    <div style={{ fontFamily:fontSans, fontSize:15, color:C.inkLight, marginTop:6 }}>No more burner changes — watch for the drop.</div>
                  ) : nextMove.isDrop ? (
                    <>
                      <div style={{ fontFamily:fontSans, fontSize:26, fontWeight:700, color:C.ink, marginTop:4 }}>Drop at {Math.round(nextMove.temp)}°</div>
                      <div style={{ fontFamily:fontSans, fontSize:13, color:C.inkLight }}>{eta != null ? `about ${mmss(eta)} from now` : "check DTR, RoR and aroma together"}</div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontFamily:fontSans, fontSize:26, fontWeight:700, color:C.ink, marginTop:4 }}>Burner to {nextMove.burner}%</div>
                      <div style={{ fontFamily:fontSans, fontSize:13, color:C.inkLight }}>
                        {nextMove.event === "Charge" ? `at charge (${charge}°)` : nextMove.event === "Turning point" ? "once the turning point is past" : nextMove.event === "First crack" ? (fcDueNow ? "as soon as you hear first crack — tap it when you do" : eta != null ? `at first crack · about ${mmss(eta)} from now` : "at first crack") : `when bean hits ${Math.round(nextMove.temp)}°${eta != null ? ` · about ${mmss(eta)} from now` : ""}`}
                      </div>
                      {nextMove.temp != null && btNow != null && (
                        <div style={{ height:6, borderRadius:3, background:C.cream, overflow:"hidden", marginTop:10 }}>
                          <div style={{ width:`${Math.max(4, Math.min(100, 100 - (nextMove.temp - btNow) * 4))}%`, height:6, background:C.amber }}/>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div style={{ border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 16px", background:C.cream2, display:"flex", flexDirection:"column", gap:10 }}>
                  <div style={rmLabel}>Coming up</div>
                  {!fcLoggedNow && (
                    <div style={{ display:"flex", justifyContent:"space-between", gap:10 }}>
                      <div>
                        <div style={{ fontFamily:fontSans, fontSize:14, fontWeight:600, color:C.ink }}>Last gas change</div>
                        <div style={{ fontFamily:fontSans, fontSize:11.5, color:C.inkFaint }}>{fcDueNow ? "hold the burner steady now" : lastGasIn == null ? (fcPred && fcPred.stall ? "RoR falling too fast to reach first crack" : "shown from ~150°") : lastGasIn > 0 ? "then hold steady into first crack" : "hold the burner steady now"}</div>
                      </div>
                      <div style={{ fontFamily:fontSans, fontSize:20, fontWeight:600, color:C.ink }}>{fcDueNow ? "now" : lastGasIn != null && lastGasIn > 0 ? mmss(lastGasIn) : lastGasIn != null ? "now" : "—"}</div>
                    </div>
                  )}
                  {!fcLoggedNow && (
                    <div style={{ display:"flex", justifyContent:"space-between", gap:10 }} title={fcBasis}>
                      <div>
                        <div style={{ fontFamily:fontSans, fontSize:14, fontWeight:600, color:C.ink }}>First crack</div>
                        <div style={{ fontFamily:fontSans, fontSize:11.5, color: fcDueNow ? C.amber : C.inkFaint }}>{fcDueNow ? `past ~${Math.round(fcTempExpected)}° — tap First crack (or Space) when you hear it` : fcPredSec != null ? `at ~${Math.round(fcTempExpected)}° · ${fcBasis}` : "planned — live from ~150°"}</div>
                      </div>
                      <div style={{ fontFamily:fontSans, fontSize:20, fontWeight:600, color: fcDueNow ? C.amber : C.ink }}>{fcDueNow ? "due" : `~${mmss(fcPredSec != null ? fcPredSec : Math.round((fcTarget || 0) * 60))}`}</div>
                    </div>
                  )}
                  <div style={{ display:"flex", justifyContent:"space-between", gap:10 }}>
                    <div>
                      <div style={{ fontFamily:fontSans, fontSize:14, fontWeight:600, color:C.ink }}>Drop</div>
                      <div style={{ fontFamily:fontSans, fontSize:11.5, color:C.inkFaint }}>at {eor}° · Agtron ~{agtronPredict.range}</div>
                    </div>
                    <div style={{ fontFamily:fontSans, fontSize:20, fontWeight:600, color:C.ink }}>~{pred ? mmss(Math.round(pred.dropTimeMin * 60)) : mmss(Math.round((totalMin || 0) * 60))}</div>
                  </div>
                  {dtrLive && (
                    <div style={{ display:"flex", justifyContent:"space-between", gap:10 }}>
                      <div>
                        <div style={{ fontFamily:fontSans, fontSize:14, fontWeight:600, color:C.ink }}>Development so far</div>
                        <div style={{ fontFamily:fontSans, fontSize:11.5, color:C.inkFaint }}>target {dtr}</div>
                      </div>
                      <div style={{ fontFamily:fontSans, fontSize:20, fontWeight:600, color:C.ink }}>{dtrLive}%</div>
                    </div>
                  )}
                </div>

                {alert ? (
                  <div style={{ border:`1px solid ${danger ? C.red : C.amber}`, borderRadius:10, padding:"12px 16px", background:C.cream2 }}>
                    <div style={{ ...rmLabel, color: danger ? C.red : C.amber }}>{danger ? "Act now" : "Heads up"}</div>
                    <div style={{ fontFamily:fontSans, fontSize:15, fontWeight:700, color:C.ink, marginTop:4 }}>{alert.title}</div>
                    <div style={{ fontFamily:fontSans, fontSize:12.5, color:C.inkLight, lineHeight:1.45, marginTop:3 }}>{alert.body}</div>
                  </div>
                ) : (
                  <div style={{ border:`1px dashed ${C.border}`, borderRadius:10, padding:"11px 16px", fontFamily:fontSans, fontSize:12.5, color:C.inkFaint }}>
                    {alertsEnabled ? "No alerts — nothing needs you right now." : "Alerts are switched off (More → Alerts)."}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"repeat(5, minmax(0, 1fr))", gap:10 }}>
              {EVENT_ROW.map(([name, label]) => {
                const ev = events.find(e => e.name === name);
                const isNext = !ev && name === nextEventName;
                const autoHint = liveStatus === "connected" && (name === "Charge" || name === "Turning point" || name === "Drop");
                return (
                  <button key={name} onClick={() => name === "Drop" ? endRoastNow() : logEvent(name)} style={{
                    minHeight:76, borderRadius:10, textAlign:"left", padding:"0 16px", cursor:"pointer", display:"flex", flexDirection:"column", justifyContent:"center", gap:3,
                    border: isNext ? "none" : ev ? `1px solid ${C.border}` : `1px dashed ${C.border}`,
                    background: isNext ? C.amber : ev ? C.cream2 : "transparent",
                    color: isNext ? "#fff" : C.inkLight, fontFamily:fontSans,
                  }}>
                    <span style={{ fontSize: isNext ? 17 : 15, fontWeight:700, color: isNext ? "#fff" : C.ink }}>{label}{ev ? " ✓" : ""}</span>
                    <span style={{ fontSize:12 }}>{ev ? `${ev.time}${ev.temp != null ? ` · ${ev.temp}°` : ""}${ev.auto ? " · auto" : ""}` : isNext ? (autoHint ? "detected by itself — or tap / Space" : "tap, or press Space") : autoHint ? "detected by itself" : ""}</span>
                  </button>
                );
              })}
            </div>

            {liveStatus !== "connected" && (
              <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap", background:C.cream2, border:`1px solid ${C.border}`, borderRadius:10, padding:"10px 16px" }}>
                <div style={rmLabel}>Readings by hand</div>
                <label style={{ display:"flex", alignItems:"center", gap:6, fontFamily:fontSans, fontSize:12, color:C.inkFaint }}>Bean °C
                  <input style={{ ...s.input, width:90, fontSize:16 }} type="number" value={beanTemp} onChange={e => setBeanTemp(e.target.value)} placeholder="165"/>
                </label>
                <label style={{ display:"flex", alignItems:"center", gap:6, fontFamily:fontSans, fontSize:12, color:C.inkFaint }}>Burner %
                  <input style={{ ...s.input, width:80, fontSize:16 }} type="number" value={burner} onChange={e => setBurner(e.target.value)} placeholder="55"/>
                </label>
                <button onClick={logPoint} disabled={!beanTemp} style={{ ...s.btnPrimary, opacity: beanTemp ? 1 : 0.5 }}>● Log point</button>
                <button onClick={() => setAutoLog(a => !a)} style={{ ...s.btn, background: autoLog ? C.green : "transparent", color: autoLog ? "#fff" : C.inkMid, borderColor: autoLog ? C.green : C.border }}>{autoLog ? `● Auto-log every ${autoLogInterval}s` : "Auto-log off"}</button>
                <div style={{ fontFamily:fontSans, fontSize:11, color:C.inkFaint }}>RoR is worked out from your logged temps.</div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ══ AFTER THE DROP ══ */}
      {view === "after" && !showMore && (() => {
        const dropEv = events.find(e => e.name === "Drop");
        const fcEv = events.find(e => e.name === "First crack");
        const dropSec = dropEv ? toSecs(dropEv.time) : sec;
        const fcS = fcEv ? toSecs(fcEv.time) : null;
        const dtrFinal = fcS != null && dropSec > fcS ? Math.round(((dropSec - fcS) / dropSec) * 1000) / 10 : null;
        const dtrRange = String(dtr || "").match(/(\d+(?:\.\d+)?)\D+(\d+(?:\.\d+)?)/);
        const dtrLo = dtrRange ? parseFloat(dtrRange[1]) : 18, dtrHi = dtrRange ? parseFloat(dtrRange[2]) : 25;
        const adherence = computeCurveAdherence(dataPoints, steps, dryEndMin, fcTarget, totalMin);
        const fcDiff = fcS != null && fcTarget ? fcS - Math.round(fcTarget * 60) : null;
        const tpE2 = events.find(e => e.name === "Turning point");
        const settledPts = tpE2 ? dataPoints.filter(p => p.temp != null && p.t * 60 >= toSecs(tpE2.time) + 60 && p.t * 60 <= dropSec) : [];
        const settledDev = settledPts.length >= 5 ? Math.round(settledPts.reduce((q, p) => q + Math.abs(p.temp - (getExpectedTempAtTime(steps, p.t) ?? p.temp)), 0) / settledPts.length * 10) / 10 : null;
        const kind = t => (t || "").toLowerCase();
        const hadCrash = alertLog.some(a => kind(a.title).includes("crash"));
        const hadFlick = alertLog.some(a => kind(a.title).includes("flick"));
        const results = [
          ["First crack", fcEv ? fcEv.time : "—", fcS == null ? "not logged" : `target ${fmt(Math.round((fcTarget || 0) * 60))} · ${Math.abs(fcDiff) <= 15 ? "on target" : fcDiff > 0 ? `${fcDiff}s late` : `${-fcDiff}s early`}`],
          ["Drop", dropEv && dropEv.temp != null ? `${dropEv.temp}°` : "—", `target ${eor}°`],
          ["Total time", fmt(dropSec), `planned ${fmt(Math.round((totalMin || 0) * 60))}`],
          ["Development (DTR)", dtrFinal != null ? `${dtrFinal}%` : "—", `target ${dtr || "—"}${dtrFinal != null ? (dtrFinal < dtrLo ? " · short" : dtrFinal > dtrHi ? " · long" : " · on target") : ""}`],
          ["Stayed on the curve", settledDev != null ? `±${settledDev}°` : adherence ? `±${adherence.meanAbsDev}°` : "—", "average distance from plan, after the turning point"],
        ];
        // One plain suggestion for next time — only ever applied if approved.
        let suggestion = null, delta = 0;
        if (fcDiff != null && fcDiff > 20) { suggestion = `First crack came ${fcDiff} seconds late. Try charging at ${charge + 1}° instead of ${charge}° on the next batch.`; delta = 1; }
        else if (fcDiff != null && fcDiff < -20) { suggestion = `First crack came ${-fcDiff} seconds early. Try charging at ${charge - 1}° instead of ${charge}° on the next batch.`; delta = -1; }
        else if (hadFlick) { suggestion = "There was a flick after first crack — ease the burner a little earlier on the approach next time."; }
        else if (dtrFinal != null && dtrFinal < dtrLo) { suggestion = `Development was short (${dtrFinal}%) — give it a little longer after first crack next time.`; }
        else if (dtrFinal != null && dtrFinal > dtrHi) { suggestion = `Development ran long (${dtrFinal}%) — drop a little sooner after first crack next time.`; }
        if (!fcEv) suggestion = "First crack wasn't logged, so timing and development can't be judged. Tap First crack — or press Space — when you hear it. It also teaches the Hub when this coffee cracks on your Loring, which sharpens every prediction.";
        const onTarget = !suggestion && !hadCrash;
        const nextNum = peekNextBatchNumber();
        return (
          <div style={{ padding:"22px 28px", display:"flex", flexDirection:"column", gap:18, maxWidth:1400 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", flexWrap:"wrap", gap:12 }}>
              <div>
                <div style={{ fontFamily:fontSans, fontSize:11, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:C.inkFaint }}>Roast summary{batchId ? ` · ${batchId}` : ""}</div>
                <div style={{ fontFamily:fontSerif, fontSize:32, color:C.ink, lineHeight:1.1, marginTop:4 }}>{origin || "Roast"} — {!fcEv ? "first crack not logged" : onTarget ? "on target" : "worth a look"}</div>
              </div>
              {!saved && <button onClick={carryOnRoasting} style={{ ...s.btnSm }}>Not finished? Carry on roasting</button>}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:12 }}>
              {results.map(([l, v, sub]) => (
                <div key={l} style={{ background:C.cream2, border:`1px solid ${C.border}`, borderRadius:10, padding:"14px 16px" }}>
                  <div style={{ fontFamily:fontSans, fontSize:12, color:C.inkFaint }}>{l}</div>
                  <div style={{ fontFamily:fontSans, fontSize:28, fontWeight:600, color:C.ink, lineHeight:1.2, fontVariantNumeric:"tabular-nums" }}>{v}</div>
                  <div style={{ fontFamily:fontSans, fontSize:12, color:C.inkFaint }}>{sub}</div>
                </div>
              ))}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(340px, 1fr))", gap:16, alignItems:"start" }}>
              <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                <div style={rmCard}>
                  <div style={rmLabel}>What happened</div>
                  <div style={{ fontFamily:fontSans, fontSize:14, color:C.ink }}><span style={{ color: hadCrash ? C.amber : C.green, fontWeight:700 }}>{hadCrash ? "!" : "✓"}</span> {hadCrash ? "RoR crash warning during the roast" : "No RoR crash"}</div>
                  <div style={{ fontFamily:fontSans, fontSize:14, color:C.ink }}><span style={{ color: hadFlick ? C.amber : C.green, fontWeight:700 }}>{hadFlick ? "!" : "✓"}</span> {hadFlick ? "Flick warning after first crack" : "No flick after first crack"}</div>
                  {fcSignals.length > 0 && (
                    <div style={{ fontFamily:fontSans, fontSize:12.5, color:C.inkLight, lineHeight:1.5 }}>
                      First-crack signal from the RoR: {fcSignals.map(x => `${x.time} (${x.temp}°) — ${x.response === "logged" ? "you logged it" : x.response === "not yet" ? "you said not yet" : x.response === "logged by hand" ? "you logged first crack yourself" : "no answer"}`).join("; ")}
                      {fcEv && fcEv.source !== "ror-signature" ? ` · first crack logged at ${fcEv.time}` : ""}
                    </div>
                  )}
                  {alertLog.length > 0 && (
                    <div style={{ display:"flex", flexDirection:"column", gap:3, marginTop:4 }}>
                      {alertLog.map((a, i) => <div key={i} style={{ fontFamily:fontSans, fontSize:12, color:C.inkLight }}>{a.time} — {a.title}</div>)}
                    </div>
                  )}
                </div>
                <div style={rmCard}>
                  <div style={rmLabel}>Next time</div>
                  <div style={{ fontFamily:fontSans, fontSize:15, color:C.ink, lineHeight:1.5 }}>{suggestion || "Everything landed on target — repeat this profile."}</div>
                  {delta !== 0 && !suggestionDecided && (
                    <>
                      <div style={{ fontFamily:fontSans, fontSize:11, color:C.inkFaint }}>A suggestion only — nothing changes unless you apply it.</div>
                      <div style={{ display:"flex", gap:10 }}>
                        <button onClick={() => { setNextChargeDelta(delta); setSuggestionDecided(true); }} style={{ ...s.btn, borderColor:C.amber, color:C.amber, fontWeight:700 }}>Apply to next batch</button>
                        <button onClick={() => setSuggestionDecided(true)} style={s.btn}>Ignore</button>
                      </div>
                    </>
                  )}
                  {suggestionDecided && delta !== 0 && <div style={{ fontFamily:fontSans, fontSize:12, color:C.inkFaint }}>{nextChargeDelta !== 0 ? `Next batch will charge at ${charge + nextChargeDelta}°.` : "Ignored — next batch keeps the same charge."}</div>}
                </div>
                <div>
                  <div style={s.fieldLabel}>Notes</div>
                  <input style={s.input} value={liveNotes} onChange={e => setLiveNotes(e.target.value)} placeholder="Anything worth remembering about this batch"/>
                </div>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                <div style={rmCard}>
                  <div style={rmLabel}>Weigh out</div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                    <div><div style={s.fieldLabel}>Green in (kg)</div><input style={{ ...s.input, fontSize:20 }} type="number" step="0.1" value={green} onChange={e => setGreen(e.target.value)}/></div>
                    <div><div style={s.fieldLabel}>Roasted out (kg)</div><input style={{ ...s.input, fontSize:20, borderColor: roasted ? C.border : C.amber }} type="number" step="0.1" value={roasted} onChange={e => setRoasted(e.target.value)} autoFocus={!saved}/></div>
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
                    <div style={{ fontFamily:fontSans, fontSize:13, color:C.inkLight }}>Weight loss</div>
                    <div style={{ fontFamily:fontSerif, fontSize:26, color:C.ink }}>{lossVal()}</div>
                  </div>
                </div>
                {!saved ? (
                  <>
                    <div style={{ fontFamily:fontSans, fontSize:12.5, color:C.inkLight, lineHeight:1.55 }}>
                      On save: {green || "the green"} kg comes off {origin || "this coffee"} in Green stock, the batch goes to QC approval{profile.orderId ? ", and its order is marked roasted" : ""}.
                    </div>
                    <button onClick={saveAndNextBatch} style={{ ...s.btnPrimary, padding:"14px", fontSize:15 }}>Save &amp; start next batch · {nextNum === batchId ? "next number" : nextNum}</button>
                    <button onClick={() => { if (saveRoast() === false) return; setFinishingDay(true); setShowCoolDown(true); }} style={{ ...s.btn, padding:"12px", fontSize:13 }}>Save &amp; finish for today — show cool-down steps</button>
                  </>
                ) : (
                  <>
                    <div style={{ fontFamily:fontSans, fontSize:14, color:C.green, fontWeight:700 }}>✓ Saved to history — Green stock updated{profile.orderId ? ", order marked roasted" : ""}</div>
                    {!finishingDay && <button onClick={saveAndNextBatch} style={{ ...s.btnPrimary, padding:"12px" }}>Start next batch</button>}
                    <button onClick={onExit} style={s.btn}>Close Roast Mode</button>
                  </>
                )}
                {finishingDay && coolDownCard}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ══ MORE — everything else, one tap away: steps table, phase notes and
          tools, all readings and stats, the full event log, notes, cool-down ══ */}
      {showMore && (
        <div>
      {/* ── LIVE READINGS STRIP ── */}
      <div style={{ background:C.cream3, borderBottom:`1px solid ${C.border}`, padding:"8px 20px", display:"flex", gap:16, alignItems:"center", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:6, paddingRight:12, borderRight:`1px solid ${C.border}` }} title={liveStatus==="connected" ? "Reading live off the roaster via the FDC Live Bridge" : "No live bridge detected — type readings in manually, same as always"}>
          <div style={{ width:8, height:8, borderRadius:"50%", background: liveStatus==="connected" ? C.green : liveStatus==="connecting" ? C.amber : C.inkFaint, flexShrink:0 }}/>
          <div style={{ fontFamily:fontSans, fontSize:10, fontWeight:700, color: liveStatus==="connected" ? C.green : C.inkFaint }}>
            {liveStatus==="connected" ? (running ? "LIVE · LOGGING" : "LIVE") : liveStatus==="connecting" ? "CONNECTING…" : "MANUAL ENTRY"}
          </div>
          {liveStatus==="connected" && liveExtra && (
            <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint }}>
              ET {liveExtra.et}° · Inlet {liveExtra.inlet}° · Stack {liveExtra.stack}°
            </div>
          )}
        </div>

        <div onClick={()=>setAlertsEnabled(v=>!v)} style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer", paddingRight:12, borderRight:`1px solid ${C.border}` }} title="Live phase alerts — reacts to real RoR trend and elapsed time, not a fixed timer">
          <div style={{ width:26, height:15, borderRadius:8, background: alertsEnabled ? C.amber : C.border, position:"relative", transition:"background 0.15s", flexShrink:0 }}>
            <div style={{ width:11, height:11, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left: alertsEnabled ? 13 : 2, transition:"left 0.15s" }}/>
          </div>
          <div style={{ fontFamily:fontSans, fontSize:10, fontWeight:600, color: alertsEnabled ? C.ink : C.inkFaint }}>Alerts</div>
        </div>
        {[["Bean °C", beanTemp, setBeanTemp, "165"], ["RoR °C/min", ror, setRorByHand, "auto"], ["Burner %", burner, setBurner, "55"]].map(([label, val, set, ph]) => {
          const isBean = label === "Bean °C";
          const bt = isBean ? parseFloat(val) : NaN;
          const beanFault = isBean && bt >= M.beanFaultC;
          const beanWarn = isBean && !beanFault && bt >= M.beanWarnC;
          return (
          <div key={label} style={{ display:"flex", alignItems:"center", gap:8 }}>
            <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint, width:70 }}>{label}</div>
            <input style={{ fontFamily:fontSerif, fontSize:18, color: beanFault?C.red:C.ink, background: beanFault?"#FDEAEA":beanWarn?"#FCF3E3":"#fff", border:`${beanFault||beanWarn?"2px":"1px"} solid ${beanFault?C.red:beanWarn?C.amber:C.border}`, borderRadius:5, padding:"4px 8px", width:80, outline:"none" }}
              type="number" value={val} onChange={e=>set(e.target.value)} placeholder={ph}/>
            {beanFault && <div style={{fontFamily:fontSans,fontSize:10,fontWeight:700,color:"#fff",background:C.red,padding:"3px 8px",borderRadius:4}}>MACHINE FAULT ≥{M.beanFaultC}°C</div>}
            {beanWarn && <div style={{fontFamily:fontSans,fontSize:10,fontWeight:700,color:C.amber}}>⚠ {M.brand} warning ≥{M.beanWarnC}°C</div>}
            {label === "RoR °C/min" && val !== "" && (liveStatus === "connected" || !rorTyped) && <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint}} title="Worked out from the bean temperatures over the last 30–45 seconds. Type a number to override; clear the box to go back to automatic.">calculated</div>}
          </div>
          );
        })}
        <button onClick={logPoint} disabled={!beanTemp} style={{
          fontFamily:fontSans, fontSize:12, fontWeight:700, padding:"7px 16px", borderRadius:5, cursor: beanTemp?"pointer":"not-allowed",
          background: beanTemp ? C.amber : C.cream2, color: beanTemp ? "#fff" : C.inkFaint, border:"none",
        }}>● Log point</button>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          {liveStatus==="connected" ? (
            <div style={{ fontFamily:fontSans, fontSize:11, color:C.inkFaint, padding:"6px 12px" }} title="Auto-log isn't needed while a live connection is doing the same job — turning it on too would double up points on the curve.">
              Auto-log — not needed, live is logging
            </div>
          ) : (
            <>
              <button onClick={()=>setAutoLog(a=>!a)} style={{
                fontFamily:fontSans, fontSize:11, fontWeight:700, padding:"6px 12px", borderRadius:5, cursor:"pointer",
                background: autoLog ? C.green : C.cream2, color: autoLog ? "#fff" : C.inkMid, border:`1px solid ${autoLog?C.green:C.border}`,
              }}>{autoLog ? "● Auto-log ON" : "Auto-log OFF"}</button>
              {autoLog && (
                <select value={autoLogInterval} onChange={e=>setAutoLogInterval(parseInt(e.target.value))} style={{ fontFamily:fontSans, fontSize:11, padding:"5px 6px", borderRadius:5, border:`1px solid ${C.border}`, background:"#fff", color:C.inkMid }}>
                  {[5,10,15,30].map(s => <option key={s} value={s}>every {s}s</option>)}
                </select>
              )}
            </>
          )}
        </div>
        {dataPoints.length > 0 && (
          <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint }}>{dataPoints.length} logged</div>
        )}
        {running && (() => {
          const tracking = getCurveTracking({ steps, dataPoints, sec, beanTemp });
          if (!tracking) return null;
          const abs = Math.abs(tracking.delta);
          const colour = abs < 5 ? C.green : abs < 8 ? C.amber : C.red;
          const arrow = tracking.delta > 0.5 ? "▲" : tracking.delta < -0.5 ? "▼" : "●";
          const liveAdherence = computeCurveAdherence(dataPoints, steps, dryEndMin, fcTarget, totalMin);
          return (
            <div style={{ display:"flex", alignItems:"center", gap:14 }}>
              <div style={{ display:"flex", alignItems:"center", gap:6 }} title={`Expected ${tracking.expected}°C at this point in the profile's own curve`}>
                <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint }}>vs curve</div>
                <div style={{ fontFamily:fontSerif, fontSize:16, color:colour, fontWeight:700 }}>{arrow} {tracking.delta > 0 ? "+" : ""}{tracking.delta}°</div>
              </div>
              {liveAdherence && (
                <div style={{ display:"flex", alignItems:"center", gap:6 }} title="Running average — updates as the roast progresses">
                  <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint }}>mean abs dev</div>
                  <div style={{ fontFamily:fontSerif, fontSize:14, color:C.inkMid }}>{liveAdherence.meanAbsDev}°C</div>
                </div>
              )}
              {liveAdherence && (
                <div style={{ display:"flex", alignItems:"center", gap:6 }} title="Area between actual and reference curves so far, in °C·min">
                  <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint }}>total accum. dev</div>
                  <div style={{ fontFamily:fontSerif, fontSize:14, color:C.inkMid }}>{liveAdherence.totalAccumDev}°C·min</div>
                </div>
              )}
            </div>
          );
        })()}
        {dtrLive && (
          <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:14 }}>
            {maiTimeStr && (
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint }}>MAI time</div>
                <div style={{ fontFamily:fontSerif, fontSize:16, color:C.inkMid, fontWeight:700 }}>{maiTimeStr}</div>
              </div>
            )}
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint }}>Live DTR</div>
              <div style={{ fontFamily:fontSerif, fontSize:22, color: dtrOk ? C.green : C.red, fontWeight:700 }}>{dtrLive}%</div>
              <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint }}>target {dtr}</div>
            </div>
          </div>
        )}
      </div>

      {/* ── Date + notes — a slim fixed bar, not part of the scrollable
          area, so it's always visible regardless of scroll position ── */}
      <div style={{ background:C.cream2, borderBottom:`1px solid ${C.border}`, padding:"6px 20px", display:"flex", gap:10, alignItems:"center", flexShrink:0 }}>
        <input type="date" style={{ ...s.input, fontSize:11, padding:"4px 8px", width:130 }} value={roastDate} onChange={e=>setRoastDate(e.target.value)} title="Roast date"/>
        <input style={{ ...s.input, fontSize:11, padding:"4px 8px", flex:1 }} value={liveNotes} onChange={e=>setLiveNotes(e.target.value)} placeholder="Notes — jot anything worth remembering as you go…" />
      </div>


      {/* ── PHASE LOG + CURVE — side by side, so the curve never needs any
          scroll trick to stay visible; it's just sitting in its own column ──
          No max-width/centering here — this uses the full width of whatever
          screen it's on rather than leaving room empty on either side. */}
      <div style={{ flex:1, overflowY:"auto", padding:"16px 28px", width:"100%" }}>

        <div style={{ display:"flex", gap:28, alignItems:"flex-start", flexWrap:"wrap" }}>

          {/* ── LEFT COLUMN: phase log — fixed-ish width, doesn't need to
              stretch just because the screen is wide ── */}
          <div style={{ flex:"0 1 460px", minWidth:320, maxWidth:520 }}>

        {/* ═══ ROAST STEPS TABLE ═══ */}
        {/* Collapsed by default — it's the full planned profile for reference,
            not something you need open while actually logging phases. */}
        <div style={{ marginBottom:24 }}>
          <div onClick={()=>setStepsTableOpen(v=>!v)} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer", fontFamily:fontSans, fontSize:10, fontWeight:700, color:C.inkFaint, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:10 }}>
            <span>Roast steps — click any cell to edit</span>
            <span>{stepsTableOpen ? "▲ hide" : "▼ show"}</span>
          </div>
          {stepsTableOpen && (
          <>
          <div style={{ border:`1px solid ${C.border}`, borderRadius:8, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ background:C.cream3 }}>
                  {["Phase","Time","Bean temp","Burner %","RoR target","Notes"].map(h=>(
                    <th key={h} style={{ ...s.thStyle, fontSize:10 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {steps.map((row, idx) => {
                  const isCurrent = idx === currentStepIdx && (running || sec > 0);
                  const isPast = idx < currentStepIdx;
                  const editCell = (field, display, width) => {
                    const key = `${idx}-${field}`;
                    return editingCell === key ? (
                      <input
                        autoFocus
                        style={{ fontFamily:fontSans, fontSize:12, width: width||60, padding:"2px 4px", border:`1px solid ${C.amber}`, borderRadius:3, outline:"none" }}
                        value={row[field]}
                        onChange={e=>updateStep(idx, field, e.target.value)}
                        onBlur={()=>setEditingCell(null)}
                        onKeyDown={e=>{ if(e.key==="Enter") setEditingCell(null); }}
                      />
                    ) : (
                      <span onClick={()=>setEditingCell(key)} style={{ cursor:"pointer" }}>{display}</span>
                    );
                  };
                  return (
                    <tr key={idx} style={{
                      borderBottom:`1px solid ${C.borderSoft}`,
                      background: isCurrent ? "#FBF1DF" : isPast ? C.cream2 : "transparent",
                      opacity: isPast && !isCurrent ? 0.5 : 1,
                    }}>
                      <td style={{ ...s.td, fontWeight:700, color: isCurrent ? C.amber : C.ink, whiteSpace:"nowrap" }}>
                        {isCurrent && "→ "}{row.phase}
                      </td>
                      <td style={{ ...s.td, fontFamily:fontSerif }}>{editCell("timeStr", row.timeStr)}</td>
                      <td style={s.td}>{editCell("temp", (typeof row.temp==="number"?row.temp:row.temp)+"°C")}</td>
                      <td style={s.td}>{editCell("burner", row.burner+"%")}</td>
                      <td style={s.td}>{editCell("ror", row.ror)}</td>
                      <td style={{ ...s.td, fontSize:11, color:C.inkLight, maxWidth:280 }}>{editCell("notes", row.notes, 260)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint, marginTop:6 }}>
            Generated from your profile targets — edit any value to match real machine behaviour. Highlighted row follows the timer.
          </div>
          </>
          )}
        </div>


        {/* ═══ PHASE 1: PRE-CHARGE ═══ */}
        {activePhase !== "preheat" ? (
          <CompactPhaseRow id="preheat" label="Pre-charge" event={events.find(e=>e.name==="Charge")} onLog={()=>logEvent("Charge")} logLabel="Log charge"/>
        ) : (
        <div style={card(true)}>
          <PhaseLabel id="preheat" label="Pre-charge"/>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
            {[["Charge temp", `${charge}°C`], ["IBS%", `${ibs}%`], ["TAP target", `~${tapTarget}°C`]].map(([l,v])=>(
              <div key={l} style={{ background:C.cream2, borderRadius:6, padding:"10px 12px" }}>
                <div style={{ fontFamily:fontSans, fontSize:9, color:C.inkFaint, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>{l}</div>
                <div style={{ fontFamily:fontSerif, fontSize:22, color:C.amber, fontWeight:700 }}>{v}</div>
              </div>
            ))}
          </div>
          {cultivarNote && <div style={{ fontFamily:fontSans, fontSize:11, color:C.inkMid, marginTop:12, lineHeight:1.6 }}>{cultivarNote}</div>}
          {residualNote && <div style={{ fontFamily:fontSans, fontSize:11, color:C.inkLight, marginTop:8, lineHeight:1.6, fontStyle:"italic" }}>{residualNote}</div>}
          <div style={{ display:"flex", gap:8, marginTop:12 }}>
            <button onClick={()=>logEvent("Charge")} style={{ background:C.ink, color:"#fff", border:"none", fontFamily:fontSans, fontSize:12, fontWeight:700, padding:"8px 20px", borderRadius:5, cursor:"pointer" }}>
              ● Log charge
            </button>
            <button onClick={()=>logEvent("Turning point")} style={{ background:C.cream3, color:C.inkMid, border:`1px solid ${C.border}`, fontFamily:fontSans, fontSize:12, padding:"8px 20px", borderRadius:5, cursor:"pointer" }}>
              ● Log turning point
            </button>
          </div>
          {(() => {
            const tapEvent = events.find(e => e.name === "Turning point");
            if (!tapEvent) return null;
            const expectedTapSec = (1.15 + (charge > 172 ? 0.1 : 0)) * 60; // matches generateRoastSteps' tapTime formula exactly
            const tapSec = parseInt(tapEvent.time.split(":")[0])*60 + parseInt(tapEvent.time.split(":")[1]);
            const timingDiff = tapSec - expectedTapSec;
            const tempDiff = tapEvent.temp ? tapEvent.temp - tapTarget : null;
            let msg = "";
            if (Math.abs(timingDiff) < 8 && (tempDiff === null || Math.abs(tempDiff) < 3)) {
              msg = "✓ Turning point landed on plan — thermal momentum is where it should be.";
            } else if (timingDiff < -8) {
              msg = "TAP came early — more thermal momentum than planned. Ease off the burner a touch through drying.";
            } else if (timingDiff > 8) {
              msg = "TAP came late — less thermal momentum than planned. Nudge the burner up through drying to compensate.";
            } else if (tempDiff !== null && tempDiff < -3) {
              msg = "TAP temp lower than target — consider slightly higher heat application to meet the planned curve.";
            } else if (tempDiff !== null && tempDiff > 3) {
              msg = "TAP temp higher than target — system is carrying more heat than expected, ease back slightly.";
            }
            return msg ? (
              <div style={{ marginTop:10, fontFamily:fontSans, fontSize:11, color:C.inkLight, lineHeight:1.6 }}>{msg}</div>
            ) : null;
          })()}
        </div>
        )}

        {/* ═══ PHASE 2: DRYING ═══ */}
        {activePhase !== "drying" ? (
          <CompactPhaseRow id="drying" label="Drying" event={events.find(e=>e.name==="Yellow point")} onLog={()=>logEvent("Yellow point")} logLabel="Log yellow point"/>
        ) : (
        <div style={card(true)}>
          <PhaseLabel id="drying" label={`Drying phase — target end ~${dryEndMin} min`}/>
          <div style={{ fontFamily:fontSans, fontSize:12, color:C.inkMid, lineHeight:1.7, marginBottom:10 }}>
            Keep burner at <strong>IBS {ibs}%</strong> to build momentum. Bean probe will read low — drum air is doing the work.
            Watch for colour change from green → yellow. RoR should be climbing or stable. TAP should arrive around <strong>~{tapTarget}°C</strong>.
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
            <div style={{ background:C.cream2, borderRadius:6, padding:"10px 12px" }}>
              <div style={{ fontFamily:fontSans, fontSize:9, color:C.inkFaint, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>Dry end target</div>
              <div style={{ fontFamily:fontSerif, fontSize:20, color:C.amber }}>{dryEndMin} min</div>
            </div>
            <div style={{ background:C.cream2, borderRadius:6, padding:"10px 12px" }}>
              <div style={{ fontFamily:fontSans, fontSize:9, color:C.inkFaint, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>Min burner (Loring)</div>
              <div style={{ fontFamily:fontSerif, fontSize:20, color:C.amber }}>20%</div>
            </div>
          </div>
          <button onClick={()=>logEvent("Yellow point")} style={{ marginTop:12, background:C.cream3, color:C.inkMid, border:`1px solid ${C.border}`, fontFamily:fontSans, fontSize:12, padding:"7px 18px", borderRadius:5, cursor:"pointer" }}>
            ● Log yellow point
          </button>
          {(process==="Natural"||process==="Honey"||process==="Anaerobic"||(cv&&cv.group==="Ethiopian")) && (
            <div style={{ marginTop:12, fontFamily:fontSans, fontSize:11, color:C.inkLight, lineHeight:1.6, background:C.cream2, borderRadius:6, padding:"9px 12px" }}>
              Tipping risk here is usually about inlet air <em>temperature</em>, not fan speed — the two are separate levers. If scorch marks show up, try holding more airflow volume at a slightly lower burner rather than less air pushed harder.
            </div>
          )}
        </div>
        )}

        {/* ═══ PHASE 3: MAILLARD ═══ */}
        {activePhase !== "maillard" ? (
          <CompactPhaseRow id="maillard" label="Maillard" />
        ) : (
        <div style={card(true)}>
          <PhaseLabel id="maillard" label={`Maillard — ~${dryEndMin}–${maillardEndMin} min`}/>
          <div style={{ fontFamily:fontSans, fontSize:12, color:C.inkMid, lineHeight:1.7, marginBottom:10 }}>
            Begin tapering heat. Reduce burner toward <strong>{profile.maillardBurner || 60}%</strong> to ensure declining RoR into FC.
            This is where flavour complexity builds — caramelisation, Maillard browning. Don't flatten RoR here.
            Approaching FC the RoR will often stall — anticipate it before it happens.
          </div>
          <div style={{ fontFamily:fontSans, fontSize:11, color:C.inkFaint }}>
            FC expected around <strong style={{ color:C.amber }}>{fcTarget} min</strong> — that's {Math.round(fcTarget*60 - sec > 0 ? fcTarget*60 - sec : 0)}s from now
          </div>
          <div style={{ marginTop:10, fontFamily:fontSans, fontSize:11, color:C.inkLight, lineHeight:1.6, background:C.cream2, borderRadius:6, padding:"9px 12px" }}>
            This stretch — yellow point to FC — is its own lever, separate from development time. Run it longer for more floral structure and mouthfeel; shorter gives simpler, less-structured florals. Logged as <strong>MAI time</strong> once FC is hit.
          </div>
        </div>
        )}

        {/* ═══ PHASE 4: APPROACHING FC ═══ */}
        {activePhase !== "fc" ? (
          <CompactPhaseRow id="fc" label="Approaching first crack" event={events.find(e=>e.name==="First crack")} onLog={()=>logEvent("First crack")} logLabel="Log FC"/>
        ) : (
        <div style={card(true)}>
          <PhaseLabel id="fc" label="Approaching first crack"/>
          <div style={{ fontFamily:fontSans, fontSize:12, color:C.inkMid, lineHeight:1.7, marginBottom:12 }}>
            Stay alert. RoR often flatlines just before FC — counter it before it crashes. Internal bean pressure approaching peak.
            {cv?.speed === "fast" ? " Fast-roasting cultivar — FC may arrive early." : cv?.speed === "slow" ? " Slow cultivar — FC may take longer than expected." : ""}
          </div>
          <button onClick={()=>logEvent("First crack")} style={{ background:C.amber, color:"#fff", border:"none", fontFamily:fontSans, fontSize:14, fontWeight:700, padding:"10px 28px", borderRadius:6, cursor:"pointer" }}>
            🔔 Log first crack
          </button>
          {fcEvent && (() => {
            const missMin = (fcSec/60) - fcTarget;
            const missDegF = missMin * 1.8 * 5; // approx °C/min RoR ~5 near FC, converted to °F for fcMissImpact scale
            const driftSec = fcMissImpact(missDegF);
            if (Math.abs(missMin) < 0.15) return (
              <div style={{ marginTop:10, fontFamily:fontSans, fontSize:12, color:C.green }}>✓ FC landed on target ({fcEvent.time}).</div>
            );
            return (
              <div style={{ marginTop:10, fontFamily:fontSans, fontSize:12, color: C.amber }}>
                FC landed at {fcEvent.time} — {missMin>0?"late":"early"} by ~{Math.abs(Math.round(missMin*60))}s vs target ({Math.floor(fcTarget)}:{String(Math.round((fcTarget%1)*60)).padStart(2,"0")}).
                Expect development timing to drift ~{driftSec}s from plan — {missMin>0 ? "consider a slightly earlier drop to hold DTR%." : "consider extending development a touch to hold DTR%."}
              </div>
            );
          })()}
        </div>
        )}

        {/* ═══ PHASE 5: DEVELOPMENT ═══ */}
        {activePhase !== "dev" ? (
          <CompactPhaseRow id="dev" label="Development" event={events.find(e=>e.name==="Second crack")} onLog={()=>logEvent("Second crack")} logLabel="Log 2nd crack"/>
        ) : (
        <div style={card(true)}>
          <PhaseLabel id="dev" label="Development"/>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12 }}>
            <div style={{ background:C.cream2, borderRadius:6, padding:"10px 12px" }}>
              <div style={{ fontFamily:fontSans, fontSize:9, color:C.inkFaint, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>Drop trigger — EoR temp</div>
              <div style={{ fontFamily:fontSerif, fontSize:20, color:C.red, fontWeight:700 }}>{eor}°C</div>
            </div>
            <div style={{ background:C.cream2, borderRadius:6, padding:"10px 12px" }}>
              <div style={{ fontFamily:fontSans, fontSize:9, color:C.inkFaint, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>Drop trigger — colour (Agtron)</div>
              <div style={{ fontFamily:fontSerif, fontSize:20, color:agtronPredict.colour, fontWeight:700 }}>{agtronPredict.range} <span style={{fontSize:12,fontWeight:400,color:C.inkLight}}>{agtronPredict.level}</span></div>
            </div>
          </div>
          <div style={{ background:"#FDF6E8", border:`1px solid ${C.amber}`, borderRadius:6, padding:"10px 14px", marginBottom:12, fontFamily:fontSans, fontSize:11, color:C.inkMid, lineHeight:1.6 }}>
            <strong style={{color:C.amber}}>Drop by temp or colour — not DTR.</strong> DTR is not a reliable trigger for dropping a batch — drop based on colour or bean temperature instead. Colour is the more universal signal — probe calibration varies machine to machine, but colour at a given development stage doesn't. Use DTR below as a QC sanity-check after the fact, not the trigger itself.
          </div>
          {dtrLive && (
            <div style={{ background: dtrOk ? "#EAF5EC" : "#FDEAEA", border:`1px solid ${dtrOk ? C.green : C.red}`, borderRadius:6, padding:"10px 14px", marginBottom:12, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ fontFamily:fontSans, fontSize:12, color: dtrOk ? C.green : C.red }}>{dtrOk ? "✓ DTR in range (QC check)" : "⚠ DTR outside range (QC check)"}</div>
              <div style={{ fontFamily:fontSerif, fontSize:26, color: dtrOk ? C.green : C.red, fontWeight:700 }}>{dtrLive}%</div>
              <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint }}>target {dtr}</div>
            </div>
          )}
          <div style={{ fontFamily:fontSans, fontSize:12, color:C.inkMid, lineHeight:1.6, marginBottom:12 }}>
            Maintain gentle declining RoR. Don't crash it. Drop at <strong>{eor}°C</strong> or when colour reaches <strong>{agtronPredict.range} Agtron</strong> — whichever you can read most reliably in the moment.
          </div>
          <button onClick={()=>logEvent("Second crack")} style={{ background:C.cream3, color:C.inkMid, border:`1px solid ${C.border}`, fontFamily:fontSans, fontSize:12, padding:"7px 18px", borderRadius:5, cursor:"pointer", marginBottom:16 }}>
            ● Log second crack (if pushing darker)
          </button>

          {/* Dev Triangle */}
          <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:12, marginTop:4 }}>
            <div style={{ fontFamily:fontSans, fontSize:9, fontWeight:700, color:C.inkFaint, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:10 }}>Dev triangle — solve for missing value</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
              {[["FC temp (°C)", fcTemp, setFcTemp], ["Dev time (min)", devTime, setDevTime], ["Avg RoR (°C/min)", devRoR, setDevRoR], ["End temp (°C)", devEnd, setDevEnd]].map(([label, val, set]) => (
                <div key={label}>
                  <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint, marginBottom:3 }}>{label}</div>
                  <input style={{ fontFamily:fontSans, fontSize:13, background:"#fff", border:`1px solid ${C.border}`, borderRadius:5, padding:"6px 8px", width:"100%", outline:"none", color:C.ink }}
                    type="number" step="0.1" value={val} onChange={e=>set(e.target.value)}/>
                </div>
              ))}
            </div>
            <button onClick={solveTriangle} style={{ ...s.btn, fontSize:12 }}>Solve</button>
          </div>
        </div>
        )}

        {/* ═══ PHASE 6: DROP ═══ */}
        {/* Always shown in full once roast is ended or reaches Drop — this is
            the finishing action (weigh + save), it shouldn't collapse away. */}
        {activePhase !== "drop" && !roastEnded ? (
          <CompactPhaseRow id="drop" label="Drop & weigh" event={events.find(e=>e.name==="Drop")} onLog={()=>logEvent("Drop")} logLabel="Log drop"/>
        ) : (
        <div style={card(true)}>
          <PhaseLabel id="drop" label="Drop & weigh"/>
          <button onClick={()=>logEvent("Drop")} style={{ background:C.red, color:"#fff", border:"none", fontFamily:fontSans, fontSize:14, fontWeight:700, padding:"10px 28px", borderRadius:6, cursor:"pointer", marginBottom:16 }}>
            🔴 Log drop
          </button>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12 }}>
            <div>
              <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint, marginBottom:4 }}>Green in (kg)</div>
              <input style={bigInp} type="number" step="0.1" value={green} onChange={e=>setGreen(e.target.value)}/>
            </div>
            <div>
              <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint, marginBottom:4 }}>Roasted out (kg)</div>
              <input style={bigInp} type="number" step="0.1" value={roasted} onChange={e=>setRoasted(e.target.value)}/>
            </div>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", background:C.cream2, borderRadius:6, padding:"12px 16px", marginBottom:16 }}>
            <div style={{ fontFamily:fontSans, fontSize:12, color:C.inkLight }}>Weight loss</div>
            <div style={{ fontFamily:fontSerif, fontSize:28, color:C.amber, fontWeight:700 }}>{lossVal()}</div>
          </div>
          {saved
            ? <>
                <div style={{ fontFamily:fontSans, fontSize:13, color:C.green, textAlign:"center", padding:"12px 0" }}>✓ Saved to history — inventory updated</div>
                {/* AI Roast Assistant temporarily removed — see note near
                    ASSISTANT_WORKER_URL. Re-add <AIReviewButton roast={savedRecord}
                    onReview={...}/> here once a working key/Worker is back. */}
              </>
            : <button onClick={saveRoast} style={{ ...s.btnPrimary, width:"100%", fontSize:14, padding:"12px" }}>Save roast to history</button>
          }
        </div>
        )}

          </div>
          {/* ── LEFT COLUMN ends here ── */}

          {/* ── RIGHT COLUMN: curve — grows to fill whatever width is left
              over, so it actually benefits from a wide screen ── */}
          <div style={{ flex:"1 1 600px", minWidth:320 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12, marginBottom:10 }}>
              {referenceOptions.length > 0 ? (
                <select style={{ ...s.input, maxWidth:320 }} value={referenceRoastId} onChange={e=>setReferenceRoastId(e.target.value)}>
                  <option value="">Compare against a past roast…</option>
                  {referenceOptions.map(r => <option key={r.id} value={r.id}>{r.batch || r.date || "Roast"} — {dmy(r.date) || ""}</option>)}
                </select>
              ) : <div style={{ fontFamily:fontSans, fontSize:11, color:C.inkFaint }}>No past roasts of this coffee yet to compare against.</div>}
              {(() => {
                const pred = (running || sec > 0) ? getPrediction() : null;
                if (!pred) return null;
                const toMMSS = m => { const mm=Math.floor(m), ss=Math.round((m-mm)*60); return `${mm}:${String(ss).padStart(2,"0")}`; };
                return (
                  <div style={{ display:"flex", gap:20 }}>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint }}>Predicted drop</div>
                      <div style={{ fontFamily:fontSerif, fontSize:16, color:C.ink }}>{toMMSS(pred.dropTimeMin)} <span style={{fontSize:11,color:C.inkFaint,fontFamily:fontSans}}>± {toMMSS(pred.dropRangeMin)}</span></div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontFamily:fontSans, fontSize:10, color:C.inkFaint }}>Predicted final temp</div>
                      <div style={{ fontFamily:fontSerif, fontSize:16, color:C.ink }}>{pred.finalTemp}°C <span style={{fontSize:11,color:C.inkFaint,fontFamily:fontSans}}>± {pred.finalRange}°C</span></div>
                    </div>
                  </div>
                );
              })()}
            </div>

            <LiveRoastChart
              dataPoints={dataPoints}
              steps={steps}
              charge={charge}
              eor={eor}
              totalMin={totalMin}
              dryEndMin={dryEndMin}
              tapTime={1.15}
              secNow={sec}
              running={running}
              referenceCurve={referenceCurve}
              events={events}
            />
          </div>

        </div>
        {/* ── end two-column row ── */}

        {/* AI Roast Assistant temporarily removed — the Anthropic organization
            behind fdc-roast-assistant's API key was disabled, and Anthropic
            confirmed that's final. Re-add:
            <RoastAssistantChat context={{ origin, cultivar, process,
              phase: phases.find(p => p.id === activePhase)?.label || activePhase,
              elapsed: fmt(sec), beanTempC: beanTemp || null, rorCPerMin: ror || null,
              burnerPct: burner || null, chargeTargetC: charge, eorTargetC: eor,
              fcTargetMin: fcTarget, totalMin,
              events: events.map(e => ({ time: e.time, name: e.name, temp: e.temp })),
            }} />
            here once a working key/Worker is back in place. */}

        {coolDownCard}

        {/* ── EVENT LOG ── */}
        {events.length > 0 && (
          <div style={{ border:`1px solid ${C.border}`, borderRadius:8, overflow:"hidden", marginBottom:16 }}>
            <div style={{ fontFamily:fontSans, fontSize:9, fontWeight:700, color:C.inkFaint, textTransform:"uppercase", letterSpacing:"0.1em", padding:"10px 14px", background:C.cream3 }}>Event log</div>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr style={{ background:C.cream2 }}>
                {["Time","Event","Bean °C","RoR","Burner %"].map(h=><th key={h} style={s.thStyle}>{h}</th>)}
              </tr></thead>
              <tbody>
                {events.map((ev,i)=>(
                  <tr key={i} style={{ borderBottom:`1px solid ${C.borderSoft}` }}>
                    <td style={{ ...s.td, fontFamily:fontSerif, color:C.amber, fontWeight:700 }}>{ev.time}</td>
                    <td style={{ ...s.td, fontWeight: ev.name==="First crack"||ev.name==="Drop" ? 700 : 400 }}>{ev.name}{ev.auto && <span style={{ fontSize:10, color:C.inkFaint, marginLeft:6 }}>auto</span>}</td>
                    <td style={s.td}>{ev.temp ? ev.temp+"°C" : "—"}</td>
                    <td style={s.td}>{ev.ror ? ev.ror+"°/m" : "—"}</td>
                    <td style={s.td}>{ev.burner ? ev.burner+"%" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ textAlign:"center", paddingBottom:20 }}>
          <button onClick={onExit} style={{ ...s.btn, fontSize:12 }}>Exit roast mode</button>
        </div>
      </div>
        </div>
      )}
      </div>
    </div>
  );
}


// ── MACHINE REFERENCE PAGE (Roastery section) ─────────────────────────────────
// The bookshelf for the S70 itself — fault/warning lookup plus the machine's
// hard numbers, kept off the working screens.
// ── ROAST ARCHITECT SOFTWARE REFERENCE ────────────────────────────────────────
// Loring's Windows desktop app for designing/editing roast profiles and
// managing files on v2 roasters over Ethernet. Source: Loring's official
// Roast Architect Help Guide (v2.2.9607) + loring.com product pages,
// current as of July 2026.
const ROAST_ARCHITECT_SECTIONS = [
  { id:"overview", title:"What it is & licensing", body: (
    <div style={{fontFamily:fontSans,fontSize:12.5,color:C.inkMid,lineHeight:1.6}}>
      <p style={{margin:"0 0 10px"}}>Windows desktop app (Windows 10/11) for designing, editing and managing roast profiles for v2 Loring roasters. Talks over Ethernet — early serial-numbered (v1) systems aren't supported. Commercially released December 2025 after running as a free beta since 2018.</p>
      <ul style={{margin:0,paddingLeft:18}}>
        <li>Requires a licence: subscription (2-week free trial, cancel during trial for no charge) or a one-time purchased key.</li>
        <li>Software updates are free once licensed.</li>
        <li>Once activated, the licence stays valid offline for a grace period before it needs to re-check in with Loring's licence server — check <em>Account → License Management</em> for the "Validity with Grace Period" countdown.</li>
        <li>Mac support is only via Parallels (paid Windows VM, ~$99/yr + a ~$199 Windows 11 Pro licence) — no native Mac build.</li>
        <li><strong>Two capabilities beyond profile design, confirmed from Loring's own documentation:</strong> it can push roaster software/firmware updates from the PC to the machine, and it converts saved roast data files between older formats and the current Loring Control System (LCS v2) — useful if migrating profiles from a machine that predates the v2 screen.</li>
        <li>Supports connecting to multiple roasters from one install, if managing more than one machine.</li>
        <li>Installer prerequisite worth knowing ahead of time: Windows will prompt to install ASP.NET Core 8.0 Runtime and .NET 8.0 Desktop Runtime if they're not already present — normal, just say yes when it asks.</li>
      </ul>
    </div>
  )},
  { id:"filetypes", title:"File types", body: (
    <div style={{fontFamily:fontSans,fontSize:12.5,color:C.inkMid}}>
      {[
        [".lrcp","v2 Baseline file — contains a v2 Roaster baseline."],
        [".lrcp","v2 Burner Recipe file — burner % schedule triggered by bean temp."],
        [".lrcp","v2 Profile (Bean Temperature) Recipe file — the Roast Profile curve."],
        [".lprd",'v2 Products file — must be named exactly "RoasterProducts" to populate the dropdown on the roaster screen. Only one can be active on the machine at a time.'],
      ].map(([ext,desc],i)=>(
        <div key={i} style={{display:"flex",gap:12,padding:"7px 0",borderBottom: i<3?`1px solid ${C.borderSoft}`:"none"}}>
          <div style={{fontFamily:"monospace",fontWeight:700,color:C.ink,flexShrink:0,width:52}}>{ext}</div>
          <div style={{lineHeight:1.5}}>{desc}</div>
        </div>
      ))}
    </div>
  )},
  { id:"connecting", title:"Connecting to the S70", body: (
    <div style={{fontFamily:fontSans,fontSize:12.5,color:C.inkMid,lineHeight:1.6}}>
      <p style={{margin:"0 0 10px"}}>The <strong>Navigator panel</strong> auto-scans your LAN for Loring devices (PLC + HMI). When it finds both paired together, it groups them as a recognised Roaster with its own flame icon. Your PC/laptop must be on the same subnet as the roaster — for the Skelligs S70 that's the confirmed static-IP setup on 192.168.60.x (not the default 192.168.1.x), so double-check this before troubleshooting a "device not found" issue.</p>
      <ul style={{margin:0,paddingLeft:18}}>
        <li>You may need to temporarily disable your firewall for auto-detection to work.</li>
        <li>Devices can also be added manually by entering a known IP address directly.</li>
        <li>Once connected, you can open, edit, upload, download, and FTP-browse files on the roaster directly from the Navigator panel.</li>
      </ul>
    </div>
  )},
  { id:"quickstart", title:"Quick start guides", body: (
    <div style={{fontFamily:fontSans,fontSize:12.5,color:C.inkMid}}>
      {[
        { h:"Create a new profile", steps:["File → New → Recipe","Choose a template: Light, Medium, or Dark Roast","Edit using the graphical or table editing tools","Save As to store locally, then upload to the roaster"] },
        { h:"Create a new Products file", steps:["File → New → Products → Products File","Editor opens pre-populated with an example list — edit as a simple text list, one product per line"] },
        { h:"Open a local file", steps:["File → Open → Open Recipe File / Open Products File","Opens in view-only mode by default — toggle Edit on the ribbon to modify"] },
        { h:"Modify a file already on the roaster", steps:["Locate the roaster in the Navigator panel, expand FILES","Double-click (or right-click → Open) the file — it highlights blue and the toolbar shows \"Remote File\"","Enable Edit, make changes","Save writes straight back to the roaster, overwriting the live file — Save As instead copies it locally first"] },
        { h:"Upload a file to the roaster", steps:["Navigator → find the roaster → expand FILES","Right-click the destination folder → Upload","Select the file in the file browser"] },
        { h:"Import from Cropster or Artisan", steps:["Cropster: export the Production Roast as .xls/.xlsx first, then File → Import in Roast Architect","Artisan: File → Import directly","Either way, choose how many anchor points to generate from the raw data (Settings → Conversion controls the default)","Note: exporting back out to Cropster isn't supported yet"] },
      ].map((g,i)=>(
        <div key={i} style={{marginBottom: i<5?14:0}}>
          <div style={{fontWeight:700,color:C.ink,marginBottom:4}}>{g.h}</div>
          <ol style={{margin:0,paddingLeft:18,lineHeight:1.55}}>
            {g.steps.map((st,j)=><li key={j}>{st}</li>)}
          </ol>
        </div>
      ))}
    </div>
  )},
  { id:"profile", title:"Editing a Roast Profile", body: (
    <div style={{fontFamily:fontSans,fontSize:12.5,color:C.inkMid,lineHeight:1.6}}>
      <p style={{margin:"0 0 10px"}}>A Roast Profile is a Bean Temperature curve made of <strong>Anchor Points</strong> — drag them on the graph, or type exact time (MM:ss) and temperature values into the Anchor Points table. In "Profile Roast" mode the roaster follows this curve via PID; in Manual mode it's shown only as a reference overlay.</p>
      <div style={{fontWeight:700,color:C.ink,margin:"10px 0 4px"}}>Named anchor points</div>
      <ul style={{margin:0,paddingLeft:18}}>
        <li><strong>Charge</strong> — starting temperature.</li>
        <li><strong>Turn-Around-Point (TAP)</strong> — not optional; the point bean temp first rises again after Charge. It's an expectation, not a guarantee — actual TAP shifts with Charge temp, Initial Burner Setting, batch weight, and roaster pre-heat state. Ctrl + right-mouse-drag the TAP leaders to scale the whole curve.</li>
        <li><strong>Color Change</strong> (optional) — expected yellowing point.</li>
        <li><strong>1st / 2nd Crack</strong> (optional) — expected crack points.</li>
        <li><strong>End of Roast (EoR)</strong> — not optional; can't be set beyond the last anchor point. If EoR temp is set lower than the final profile point, the curve renders dashed past that point. Ctrl + right-mouse-drag the EoR leaders to scale the whole curve.</li>
      </ul>
      <div style={{display:"flex",gap:8,alignItems:"flex-start",background:"#FDF3EF",border:"1px solid #E5C8BC",borderRadius:6,padding:"9px 12px",margin:"10px 0"}}>
        <span style={{color:"#8B3A2A",fontSize:13,flexShrink:0}}>⚠</span>
        <span style={{color:"#8B3A2A",fontSize:12}}>If EoR temp exceeds the highest point on the profile, the roaster will push hard to reach it fast — burner set point can spike and EoR time can extend unexpectedly.</span>
      </div>
      <div style={{fontWeight:700,color:C.ink,margin:"10px 0 4px"}}>Roast settings</div>
      <ul style={{margin:0,paddingLeft:18}}>
        <li><strong>Batch</strong> — designed batch weight; the roaster auto-scales the profile for different batch sizes from this.</li>
        <li><strong>Initial Burner Setting</strong> — the % held from Charge until TAP (shown as an orange band on the graph); the curve itself isn't followed until after TAP.</li>
        <li><strong>Auto Cool</strong> — opens the Purge Gate and ramps burner to 100% a set number of degrees before EoR, to burn off Cooler Tray smoke. Shown as a purple band. When enabled, the roast actually ends when the Air Cool Time elapses, not at the EoR temperature.</li>
      </ul>
      <div style={{fontWeight:700,color:C.ink,margin:"10px 0 4px"}}>Useful ribbon tools</div>
      <ul style={{margin:0,paddingLeft:18}}>
        <li><strong>Show RoR</strong> — overlays Rate-of-Rise on a secondary axis.</li>
        <li><strong>Compare Original</strong> — ghosts in the as-opened historical version alongside your edits, so you can see exactly what's changed. "Update on Save" refreshes that baseline each save.</li>
        <li><strong>Compare to External Recipe</strong> — overlays a second, unrelated profile (in purple) purely for visual comparison.</li>
        <li><strong>Snap EoR to Max Temp / EoR Crop</strong> — quick cleanup tools for tidying the curve's endpoint.</li>
      </ul>
    </div>
  )},
  { id:"burner", title:"Burner Recipe", body: (
    <p style={{fontFamily:fontSans,fontSize:12.5,color:C.inkMid,lineHeight:1.6,margin:0}}>
      A Burner Recipe is a set of burner % steps, each triggered once bean temperature crosses its paired trigger point — it keeps firing in sequence until the burner value hits zero, then holds until EoR. Manual roasts on the machine generate one automatically, alongside a Roast Profile recorded from the same roast — the two live in the same file but don't affect each other, and you can toggle between editing either one without losing changes to the other.
    </p>
  )},
  { id:"products", title:"Products file", body: (
    <p style={{fontFamily:fontSans,fontSize:12.5,color:C.inkMid,lineHeight:1.6,margin:0}}>
      A simple line-per-product text list that fills the Product dropdown on the roaster screen — whatever's selected at Charge shows on-screen during the roast and gets logged in the Post-Roast Notes. Remember: the file must be named <span style={{fontFamily:"monospace"}}>RoasterProducts</span> or the roaster won't recognise it, and only one can be active at a time.
    </p>
  )},
  { id:"gotchas", title:"Gotchas worth flagging to the team", body: (
    <ul style={{fontFamily:fontSans,fontSize:12.5,color:C.inkMid,lineHeight:1.6,margin:0,paddingLeft:18}}>
      <li style={{marginBottom:6}}>Uploading a file with the <strong>same name</strong> as one already on the roaster overwrites it instantly and irreversibly — no undo.</li>
      <li style={{marginBottom:6}}>Editing a file opened directly off the roaster: <strong>Save</strong> pushes straight back to the machine. Use <strong>Save As</strong> if you want a local copy first.</li>
      <li style={{marginBottom:6}}>Products files must be named exactly <span style={{fontFamily:"monospace"}}>RoasterProducts</span> — anything else silently fails to populate the dropdown.</li>
      <li>Cropster import is one-way — there's no export back to Cropster yet.</li>
    </ul>
  )},
];

function RoastArchitectReference({ defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [openItem, setOpenItem] = useState("overview");

  return (
    <div style={{marginBottom:24}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",padding:"12px 16px",background:C.cream2,border:`1px solid ${C.border}`,borderRadius:8}} onClick={()=>setOpen(o=>!o)}>
        <div>
          <div style={{fontFamily:fontSans,fontSize:10,fontWeight:700,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.1em"}}>Roast Architect software {open?"▾":"▸"}</div>
          <div style={{fontFamily:fontSans,fontSize:11,color:C.inkFaint,marginTop:2}}>Loring's roast-profile design app for v2 roasters — connecting, file types, editing profiles, gotchas.</div>
        </div>
      </div>
      {open && (
        <div style={{border:`1px solid ${C.border}`,borderTop:"none",borderRadius:"0 0 8px 8px",overflow:"hidden"}}>
          <div style={{padding:"10px 14px",borderBottom:`1px solid ${C.borderSoft}`,background:C.cream,fontFamily:fontSans,fontSize:11,color:C.inkFaint}}>
            Source: Loring's official Roast Architect Help Guide (v2.2.9607) — <a href="https://loring.com/helpfiles/RA/pdf/RAHelp.pdf" target="_blank" rel="noreferrer" style={{color:C.amber}}>loring.com/helpfiles/RA/pdf/RAHelp.pdf ↗</a>. Content current as of July 2026.
          </div>
          {ROAST_ARCHITECT_SECTIONS.map((sec,i) => {
            const isOpen = openItem === sec.id;
            return (
              <div key={sec.id} style={{borderBottom: i<ROAST_ARCHITECT_SECTIONS.length-1?`1px solid ${C.borderSoft}`:"none"}}>
                <div onClick={()=>setOpenItem(isOpen?null:sec.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",cursor:"pointer"}}>
                  <div style={{flex:1,fontFamily:fontSans,fontSize:13,fontWeight:600,color:C.ink}}>{sec.title}</div>
                  <div style={{fontFamily:fontSans,fontSize:11,color:C.inkFaint}}>{isOpen?"▾":"▸"}</div>
                </div>
                {isOpen && <div style={{padding:"0 14px 14px 14px"}}>{sec.body}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── MACHINE PROFILE SETTINGS ─────────────────────────────────────────────────
// Edits the one machine profile this account has. Today that's always the
// Loring S70 — this screen exists so a second machine, when it's actually
// built, is "fill in a form" rather than a code change. Saving here updates
// the burner limits and bean-temp thresholds used throughout the recipe
// engine and roast charts immediately (see M in the module header).
function MachineProfileSettings({ machineProfile, onSave }) {
  const [form, setForm] = useState(machineProfile);
  const [savedFlash, setSavedFlash] = useState(false);
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const numField = (k) => ({
    value: form[k],
    onChange: e => update(k, e.target.value === "" ? "" : Number(e.target.value)),
  });
  const save = () => {
    onSave(form);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1800);
  };
  return (
    <div style={{ paddingTop: 24, maxWidth: 560 }}>
      <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 20, lineHeight: 1.5 }}>
        One profile per roaster account. These numbers drive the burner limits, bean-temp
        warning/fault lines on the roast chart, and — later — which live-bridge protocol to use.
        The fault/warning code reference and the live data bridge itself are still Loring-specific
        and aren't controlled from here yet.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 4 }}>
        <div>
          <div style={s.fieldLabel}>Brand</div>
          <input style={s.input} value={form.brand} onChange={e => update("brand", e.target.value)} />
        </div>
        <div>
          <div style={s.fieldLabel}>Model</div>
          <input style={s.input} value={form.model} onChange={e => update("model", e.target.value)} />
        </div>
      </div>

      <div style={s.sectionLabel}>Connection type</div>
      <select style={s.input} value={form.connectionType} onChange={e => update("connectionType", e.target.value)}>
        <option value="native">Native bridge (MODBUS / S7)</option>
        <option value="probe-tap">Universal probe-tap (BT/ET only)</option>
        <option value="manual">Manual entry only</option>
      </select>
      <div style={s.hint}>
        Native bridges are built per brand as real subscribers appear — see the roadmap notes.
        Universal probe-tap works on any machine but only reads BT/ET, no burner% or fault codes.
      </div>

      <div style={s.sectionLabel}>Burner limits</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div>
          <div style={s.fieldLabel}>Minimum burner floor (%)</div>
          <input type="number" style={s.input} {...numField("burnerFloorPct")} />
        </div>
        <div>
          <div style={s.fieldLabel}>Maillard-start cap (%)</div>
          <input type="number" style={s.input} {...numField("maillardCapPct")} />
        </div>
      </div>

      <div style={s.sectionLabel}>Bean temperature thresholds</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div>
          <div style={s.fieldLabel}>Warning (°C)</div>
          <input type="number" style={s.input} {...numField("beanWarnC")} />
        </div>
        <div>
          <div style={s.fieldLabel}>Fault (°C)</div>
          <input type="number" style={s.input} {...numField("beanFaultC")} />
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 22 }}>
        <button style={s.btnPrimary} onClick={save}>Save machine profile</button>
        {savedFlash && <div style={{ fontFamily: fontSans, fontSize: 12, color: C.green }}>Saved</div>}
      </div>
    </div>
  );
}

function MachineReference() {
 return (
 <div style={{paddingTop:24}}>
 <div style={{fontFamily:fontSans,fontSize:11,color:C.inkFaint,marginBottom:16,lineHeight:1.5}}>
 Everything about the machine itself, sourced from the Loring Owners Manual (1007056 Rev B). Coffee diagnostics live under Quality — this page is for when the roaster misbehaves, not the roast.
 </div>
 <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:20}}>
 {[["Bean temp warning",`${M.beanWarnC}°C`,"manual p. 64"],["Bean temp fault",`${M.beanFaultC}°C`,"factory-set, can't change"],["Roast burner range",`${M.burnerFloorPct}–100%`,"p. 54 — idle is a separate scale"]].map(([label,val,src])=>(
 <div key={label} style={{border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 14px",background:C.cream2}}>
 <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint,textTransform:"uppercase",letterSpacing:"0.08em"}}>{label}</div>
 <div style={{fontFamily:fontSerif,fontSize:22,color:C.ink,margin:"4px 0 2px"}}>{val}</div>
 <div style={{fontFamily:fontSans,fontSize:10,color:C.inkFaint}}>{src}</div>
 </div>
 ))}
 </div>
 <MachineFaultReference defaultOpen={true}/>
 <RoastArchitectReference/>
 </div>
 );
}

// ══════════════════════════════════════════════════════════════════════════════
// PACKAGING & TRACEABILITY PIPELINE
// Green Coffee → Production Roast → QC Approval → Packaging → Finished Goods →
// Customer Allocation → Dispatch (future)
//
// Green Coffee and Production Roast already exist (Inventory, Roast Mode /
// History) — this module adds the four stages after the roast: QC Approval,
// Packaging (SKU + lot + barcode + label), Finished Goods, and Customer
// Allocation. Dispatch is stubbed as "coming soon" rather than faked — no
// carrier/tracking integration exists yet, and pretending otherwise would be
// worse than an honest placeholder.
// ══════════════════════════════════════════════════════════════════════════════

// ── QC APPROVAL ──────────────────────────────────────────────────────────────
// Keyed by roast history id, stored separately from fdc_history so nothing
// about History's own save/edit logic needs to change.
const QC_APPROVAL_KEY = "fdc_qc_approval";
function loadQcApprovals() {
  try { return JSON.parse(localStorage.getItem(QC_APPROVAL_KEY) || "{}"); } catch { return {}; }
}
function saveQcApprovals(a) { localStorage.setItem(QC_APPROVAL_KEY, JSON.stringify(a)); }

// ── SHELF LIFE / EXPIRY POLICY ────────────────────────────────────────────────
const SHELF_LIFE_KEY = "fdc_shelf_life_policy";
const SHELF_LIFE_DEFAULTS = {
  defaultDays: 365,
  byProcess: { Washed: 365, Natural: 270, Honey: 300, Anaerobic: 270, Decaf: 270 },
};
function loadShelfLifePolicy() {
  try {
    const saved = JSON.parse(localStorage.getItem(SHELF_LIFE_KEY));
    return saved && saved.byProcess ? saved : SHELF_LIFE_DEFAULTS;
  } catch { return SHELF_LIFE_DEFAULTS; }
}
function saveShelfLifePolicy(p) { localStorage.setItem(SHELF_LIFE_KEY, JSON.stringify(p)); }
function shelfLifeDaysFor(process, policy) {
  const key = Object.keys(policy.byProcess || {}).find(k => (process || "").toLowerCase().includes(k.toLowerCase()));
  return key ? policy.byProcess[key] : policy.defaultDays;
}

// ── SKU AUTO-GENERATION ───────────────────────────────────────────────────────
// Format: <ORIGIN3><PROCESS1>-<BAGSIZE>-<YYMMDD>-<SEQ> e.g. HAL-W-250-260125-01
// Sequence resets per origin+process+size+day combination, not globally — so
// two different packaging runs of the same coffee/size on the same day get
// distinct, still-traceable SKUs rather than colliding.
const SKU_COUNTER_KEY = "fdc_sku_counters";
function loadSkuCounters() {
  try { return JSON.parse(localStorage.getItem(SKU_COUNTER_KEY) || "{}"); } catch { return {}; }
}
function saveSkuCounters(c) { localStorage.setItem(SKU_COUNTER_KEY, JSON.stringify(c)); }

function originCode(origin) {
  const letters = (origin || "").toUpperCase().replace(/[^A-Z]/g, "");
  return (letters.slice(0, 3) || "XXX").padEnd(3, "X");
}
function processCode(process) {
  const p = (process || "").toLowerCase();
  if (p.includes("decaf")) return "D";
  if (p.includes("natural")) return "N";
  if (p.includes("honey")) return "H";
  if (p.includes("anaerobic")) return "A";
  if (p.includes("washed")) return "W";
  return "X";
}
function dateCode(d) {
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return yy + mm + dd;
}
function generateSKU({ origin, process, bagSizeG, date }) {
  const base = `${originCode(origin)}-${processCode(process)}-${bagSizeG}-${dateCode(date)}`;
  const counters = loadSkuCounters();
  const next = (counters[base] || 0) + 1;
  counters[base] = next;
  saveSkuCounters(counters);
  return `${base}-${String(next).padStart(2, "0")}`;
}
// Auto-incrementing batch numbers with a fixed year prefix — same counter
// pattern as the SKU/lot numbers in Packaging, just simpler (no date/origin
// split needed, batches are sequential regardless of coffee).
const BATCH_PREFIX = "R26-";
const BATCH_COUNTER_KEY = "fdc_batch_counter";
// What the next batch number will be, without using it up.
function peekNextBatchNumber() {
  let n = 100;
  try { n = parseInt(localStorage.getItem(BATCH_COUNTER_KEY)) || 100; } catch {}
  return BATCH_PREFIX + (n + 1);
}

function generateBatchNumber() {
  let n = 100;
  try { n = parseInt(localStorage.getItem(BATCH_COUNTER_KEY)) || 100; } catch {}
  const next = n + 1;
  localStorage.setItem(BATCH_COUNTER_KEY, String(next));
  return BATCH_PREFIX + next;
}

function generateLotNumber({ roastBatch, date }) {
  const batchPart = (roastBatch || "UNBATCHED").toUpperCase().replace(/\s+/g, "");
  return `LOT-${dateCode(date)}-${batchPart}`;
}

// ── CODE 39 BARCODE (self-contained, no external library) ───────────────────
// Code 39 supports 0-9, A-Z, space, and - . $ / + % — exactly the character
// set our SKUs and lot numbers use. Chosen over EAN-13/UPC because retail
// barcodes require a purchased GS1 company prefix; Code 39 is the standard
// choice for internal traceability labels and reads on any generic scanner.
const CODE39_PATTERNS = {
  "0":"NNNWWNWNN","1":"WNNWNNNNW","2":"NNWWNNNNW","3":"WNWWNNNNN","4":"NNNWWNNNW",
  "5":"WNNWWNNNN","6":"NNWWWNNNN","7":"NNNWNNWNW","8":"WNNWNNWNN","9":"NNWWNNWNN",
  "A":"WNNNNWNNW","B":"NNWNNWNNW","C":"WNWNNWNNN","D":"NNNNWWNNW","E":"WNNNWWNNN",
  "F":"NNWNWWNNN","G":"NNNNNWWNW","H":"WNNNNWWNN","I":"NNWNNWWNN","J":"NNNNWWWNN",
  "K":"WNNNNNNWW","L":"NNWNNNNWW","M":"WNWNNNNWN","N":"NNNNWNNWW","O":"WNNNWNNWN",
  "P":"NNWNWNNWN","Q":"NNNNNNWWW","R":"WNNNNNWWN","S":"NNWNNNWWN","T":"NNNNWNWWN",
  "U":"WWNNNNNNW","V":"NWWNNNNNW","W":"WWWNNNNNN","X":"NWNNWNNNW","Y":"WWNNWNNNN",
  "Z":"NWWNWNNNN","-":"NWNNNNWNW",".":"WWNNNNWNN"," ":"NWWNNNWNN","$":"NWNWNWNNN",
  "/":"NWNWNNNWN","+":"NWNNNWNWN","%":"NNNWNWNWN","*":"NWNNWNWNN",
};
function buildCode39Bars(value) {
  const text = "*" + String(value).toUpperCase() + "*";
  const narrow = 2, wide = 6;
  const bars = [];
  let x = 0;
  for (const ch of text) {
    const pattern = CODE39_PATTERNS[ch] || CODE39_PATTERNS["-"];
    for (let j = 0; j < pattern.length; j++) {
      const isBar = j % 2 === 0;
      const w = pattern[j] === "W" ? wide : narrow;
      if (isBar) bars.push({ x, w });
      x += w;
    }
    x += narrow; // inter-character gap
  }
  return { bars, totalWidth: x };
}
function code39SVGMarkup(value, height = 50) {
  const { bars, totalWidth } = buildCode39Bars(value);
  const rects = bars.map(b => `<rect x="${b.x}" y="0" width="${b.w}" height="${height}" fill="#000"/>`).join("");
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${totalWidth}" height="${height}" viewBox="0 0 ${totalWidth} ${height}">${rects}</svg>`;
}
function Code39Barcode({ value, height = 50, showText = true }) {
  const markup = code39SVGMarkup(value, height);
  return (
    <div style={{ textAlign: "center" }}>
      <div dangerouslySetInnerHTML={{ __html: markup }} />
      {showText && <div style={{ fontFamily: "monospace", fontSize: 11, letterSpacing: 3, marginTop: 4, color: C.ink }}>{value}</div>}
    </div>
  );
}

// ── CUSTOM BARCODE IMAGES ────────────────────────────────────────────────────
// Lets a real barcode image (e.g. a purchased/retail EAN) be used on the
// printed label instead of the auto-generated Code 39 bars, per product
// code. Stored as base64 data URLs, synced like everything else. When a
// product code has no custom image, the auto-generated barcode is used —
// nothing changes for coffees that don't need this.
const CUSTOM_BARCODES_KEY = "fdc_custom_barcodes";
function loadCustomBarcodes() {
  try { return JSON.parse(localStorage.getItem(CUSTOM_BARCODES_KEY) || "{}"); } catch { return {}; }
}
function saveCustomBarcodes(b) { localStorage.setItem(CUSTOM_BARCODES_KEY, JSON.stringify(b)); }

function loadImageAsync(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// ── PRODUCTS & SKUs ───────────────────────────────────────────────────────────
// The retail products as Skelligs list them — product name, SKU and barcode
// per coffee and bag size. Labels, finished goods, allocation, stocktake and
// invoices all use these where a coffee has one. Seeded from Skelligs' SKU
// list (Claire Curran-Murphy, 30/09/26); editable in Packaging.
const PRODUCTS_KEY = "fdc_products";
const PRODUCT_SEED = [
  { id: "sk093", sku: "SK093", name: "NEEDLE'S EYE",     coffee: "Carmo",       describe: "Brazil Carmo",       bagSizeG: 250, ean: "5391007019139", wsPrice: "7.77", rrp: "12.95", caseSize: "10" },
  { id: "sk094", sku: "SK094", name: "LIGHTHOUSE ROAD",  coffee: "Pinhal",      describe: "Brazil Pinhal",      bagSizeG: 250, ean: "5391007019177", wsPrice: "7.77", rrp: "12.95", caseSize: "10" },
  { id: "sk095", sku: "SK095", name: "WASHERWOMAN ROCK", coffee: "Halo Beriti", describe: "Ethiopia Halo Beriti", bagSizeG: 250, ean: "5391007019214", wsPrice: "8.37", rrp: "13.95", caseSize: "10" },
  { id: "sk096", sku: "SK096", name: "SOUTH LANDING",    coffee: "El Faro",     describe: "Colombia El Faro",   bagSizeG: 250, ean: "5391007019191", wsPrice: "9.57", rrp: "15.95", caseSize: "10" },
  { id: "sk097", sku: "SK097", name: "BLIND MANS COVE",  coffee: "POC Decaf",   describe: "Decaf",              bagSizeG: 250, ean: "5391007019153", wsPrice: "8.97", rrp: "14.95", caseSize: "10" },
];
function loadProducts() {
  try { const raw = localStorage.getItem(PRODUCTS_KEY); return raw == null ? PRODUCT_SEED : JSON.parse(raw); } catch { return PRODUCT_SEED; }
}
function saveProducts(p) { localStorage.setItem(PRODUCTS_KEY, JSON.stringify(p)); }
// The product for a coffee and bag size: exact name first, then a close match
// (so older names like "Halo - Ethiopia" still find Washerwoman Rock).
function productFor(origin, bagSizeG) {
  const list = loadProducts().filter(p => parseInt(p.bagSizeG) === parseInt(bagSizeG));
  return list.find(p => sameCoffee(p.coffee, origin)) || list.find(p => p.coffee && origin && coffeeNamesMatch(p.coffee, origin)) || null;
}
// How a coffee + size is named on screen and on paperwork.
function productLabel(origin, bagSizeG) {
  const p = productFor(origin, bagSizeG);
  const size = bagSizeG >= 1000 ? `${bagSizeG / 1000}kg` : `${bagSizeG}g`;
  return p ? `${p.name} · ${p.describe || origin} ${size} (${p.sku})` : `${size} ${origin || ""}`.trim();
}
// EAN-13 check: the last digit must match the other twelve.
function eanValid(code) {
  const c = String(code || "").replace(/\s/g, "");
  if (!/^\d{13}$/.test(c)) return false;
  const d = c.split("").map(Number);
  const sum = d.slice(0, 12).reduce((a, v, i) => a + v * (i % 2 ? 3 : 1), 0);
  return (10 - sum % 10) % 10 === d[12];
}
// Draws a standard EAN-13 retail barcode (bars + digits underneath) into a
// box on a canvas. Bars are whole pixels wide so they print crisply.
function drawEan13(ctx, code, x, y, w, h) {
  const L = ["0001101","0011001","0010011","0111101","0100011","0110001","0101111","0111011","0110111","0001011"];
  const R = L.map(p => p.split("").map(b => b === "1" ? "0" : "1").join(""));
  const G = R.map(p => p.split("").reverse().join(""));
  const PARITY = ["LLLLLL","LLGLGG","LLGGLG","LLGGGL","LGLLGG","LGGLLG","LGGGLL","LGLGLG","LGLGGL","LGGLGL"];
  const d = String(code).split("").map(Number);
  let bits = "101";
  for (let i = 1; i <= 6; i++) bits += PARITY[d[0]][i - 1] === "L" ? L[d[i]] : G[d[i]];
  bits += "01010";
  for (let i = 7; i <= 12; i++) bits += R[d[i]];
  bits += "101";
  // 95 bars wide plus clear space either side (11 left, 7 right) = 113 units.
  // Bars are whole printer dots; retail scanners want each unit ≥ ~0.26 mm,
  // which is 3 dots at 300 dpi — so the code uses all the room it's given.
  const mod = Math.max(1, Math.floor(w / 113));
  const x0 = Math.round(x + 11 * mod + (w - 113 * mod) / 2);
  const digitH = Math.round(mod * 9);
  const barH = Math.max(10, h - digitH);
  const guard = i => i < 3 || (i >= 45 && i < 50) || i >= 92;
  for (let i = 0; i < 95; i++) if (bits[i] === "1") ctx.fillRect(x0 + i * mod, y, mod, guard(i) ? barH + Math.round(digitH * 0.5) : barH);
  ctx.save();
  ctx.fillStyle = "#000";
  ctx.font = `${Math.round(mod * 8.5)}px Arial, sans-serif`;
  ctx.textAlign = "center"; ctx.textBaseline = "top";
  const ty = y + barH + Math.round(mod * 0.5);
  ctx.fillText(String(d[0]), x0 - 5 * mod, ty);
  for (let i = 0; i < 6; i++) ctx.fillText(String(d[1 + i]), x0 + (3 + 7 * i + 3.5) * mod, ty);
  for (let i = 0; i < 6; i++) ctx.fillText(String(d[7 + i]), x0 + (50 + 7 * i + 3.5) * mod, ty);
  ctx.restore();
  return { moduleMm: null, mod };
}

// ── PACKAGING BATCHES ─────────────────────────────────────────────────────────
const PACKAGING_KEY = "fdc_packaging_batches";
function loadPackagingBatches() {
  try { return JSON.parse(localStorage.getItem(PACKAGING_KEY) || "[]"); } catch { return []; }
}
function savePackagingBatches(b) { localStorage.setItem(PACKAGING_KEY, JSON.stringify(b)); }

// ── CUSTOMERS & ALLOCATIONS ───────────────────────────────────────────────────
const CUSTOMERS_KEY = "fdc_customers";
function loadCustomers() { try { return JSON.parse(localStorage.getItem(CUSTOMERS_KEY) || "[]"); } catch { return []; } }
function saveCustomers(c) { localStorage.setItem(CUSTOMERS_KEY, JSON.stringify(c)); }

const ALLOCATIONS_KEY = "fdc_allocations";
function loadAllocations() { try { return JSON.parse(localStorage.getItem(ALLOCATIONS_KEY) || "[]"); } catch { return []; } }
function saveAllocations(a) { localStorage.setItem(ALLOCATIONS_KEY, JSON.stringify(a)); }

// Finished Goods is derived, not stored — packaged bags minus allocated bags,
// grouped by SKU. Single source of truth: packaging batches + allocations.
function computeFinishedGoods(batches, allocations) {
  const bySku = {};
  batches.forEach(b => {
    if (!bySku[b.sku]) {
      bySku[b.sku] = { sku: b.sku, origin: b.origin, process: b.process, bagSizeG: b.bagSizeG, packaged: 0, allocated: 0, lots: [] };
    }
    bySku[b.sku].packaged += b.qtyBags;
    bySku[b.sku].lots.push({ lot: b.lot, qty: b.qtyBags, expiryDate: b.expiryDate, packagedDate: b.packagedDate });
  });
  allocations.forEach(a => {
    if (bySku[a.sku]) bySku[a.sku].allocated += a.qty;
  });
  return Object.values(bySku).map(r => {
    const earliestExpiry = r.lots.reduce((min, l) => (!min || l.expiryDate < min) ? l.expiryDate : min, null);
    return { ...r, available: r.packaged - r.allocated, earliestExpiry };
  });
}

// Same numbers as computeFinishedGoods, rolled up by product (coffee +
// process + bag size) — one line for "250g Halo Beriti" however many
// packing days it came from. Batches stay listed underneath, oldest
// best-before first, which is also the order new allocations draw from.
function computeFinishedGoodsByProduct(batches, allocations) {
  const byCode = {};
  computeFinishedGoods(batches, allocations).forEach(r => {
    const code = productCode({ origin: r.origin, process: r.process, bagSizeG: r.bagSizeG });
    if (!byCode[code]) byCode[code] = { code, origin: r.origin, process: r.process, bagSizeG: r.bagSizeG, packaged: 0, allocated: 0, available: 0, earliestExpiry: null, skus: [] };
    const p = byCode[code];
    p.packaged += r.packaged; p.allocated += r.allocated; p.available += r.available;
    if (r.earliestExpiry && (!p.earliestExpiry || r.earliestExpiry < p.earliestExpiry)) p.earliestExpiry = r.earliestExpiry;
    p.skus.push(r);
  });
  return Object.values(byCode).map(p => {
    const skus = p.skus.slice().sort((a, b) => (a.earliestExpiry || "9999").localeCompare(b.earliestExpiry || "9999"));
    return { ...p, skus, origin: skus[skus.length - 1]?.origin || p.origin };
  }).sort((a, b) => (a.origin || "").localeCompare(b.origin || "") || a.bagSizeG - b.bagSizeG);
}
function bagSizeText(g) { return g >= 1000 ? `${g / 1000}kg` : `${g}g`; }

// Formats a stored ISO date ("2026-07-27") as DD-MM-YYYY for display on
// the printed label only — the underlying stored value stays ISO so
// sorting/comparisons elsewhere in the app are unaffected.
// ── DATES ─────────────────────────────────────────────────────────────────────
// Dates are STORED as 2026-09-29 (so sorting, "this month" and date inputs
// keep working) but always SHOWN European-style: 29/09/26.
// Today's date (or a given moment) as 2026-09-29 in LOCAL time. The UTC
// version ran an hour behind Irish summer time — a roast saved just after
// midnight got yesterday's date.
function localIsoDate(d = new Date()) {
  const x = d instanceof Date ? d : new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
}
function dmy(v) {
  if (!v) return "";
  if (v instanceof Date) { if (isNaN(v)) return ""; v = localIsoDate(v); }
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1].slice(2)}` : String(v);
}
function todayDmy() {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getFullYear()).slice(2)}`;
}

function formatDateDMY(iso) {
  return dmy(iso) || "—";
}

// A STABLE product code — origin + process + size, no date/sequence —
// the same code every time this coffee/size is packaged. This is what
// gets barcoded, so a till can scan it and look up one price regardless
// of which day's batch it came from. Reuses the same origin/process
// shorthand already used inside generateSKU, just without the date part.
function productCode({ origin, process, bagSizeG }) {
  return `${originCode(origin)}-${processCode(process)}-${bagSizeG}`;
}

// ── ALLOCATION DOCUMENTS (packing slip / priced document / VAT invoice) ─────
const INVOICE_SETTINGS_KEY = "fdc_invoice_settings";
const INVOICE_SETTINGS_DEFAULTS = { businessName: "", businessAddress: "", vatNumber: "", vatRatePct: "", invoicePrefix: "INV-", nextInvoiceNumber: 1 };
function loadInvoiceSettings() {
  try { return { ...INVOICE_SETTINGS_DEFAULTS, ...JSON.parse(localStorage.getItem(INVOICE_SETTINGS_KEY) || "{}") }; } catch { return { ...INVOICE_SETTINGS_DEFAULTS }; }
}
function saveInvoiceSettings(s) { localStorage.setItem(INVOICE_SETTINGS_KEY, JSON.stringify(s)); }

const CONTRACT_PRICES_KEY = "fdc_contract_prices";
function loadContractPrices() { try { return JSON.parse(localStorage.getItem(CONTRACT_PRICES_KEY) || "{}"); } catch { return {}; } }
function saveContractPrices(p) { localStorage.setItem(CONTRACT_PRICES_KEY, JSON.stringify(p)); }

// Opens a print window for the chosen document type, same pattern as
// printLabel() — "Save as PDF" in the print dialog gives a downloadable
// file. docType is "packing" | "priced" | "invoice".
function printAllocationDocument(alloc, fgRow, docType, unitPrice, settings) {
  const qty = alloc.qty;
  const price = parseFloat(unitPrice) || 0;
  const lineTotal = Math.round(qty * price * 100) / 100;
  const vatRate = docType === "invoice" ? (parseFloat(settings.vatRatePct) || 0) : 0;
  const vatAmount = docType === "invoice" ? Math.round(lineTotal * vatRate) / 100 : 0;
  const grandTotal = Math.round((lineTotal + vatAmount) * 100) / 100;

  let docNumber = "";
  if (docType !== "packing") {
    const num = settings.nextInvoiceNumber || 1;
    docNumber = `${settings.invoicePrefix || "INV-"}${String(num).padStart(4, "0")}`;
    saveInvoiceSettings({ ...settings, nextInvoiceNumber: num + 1 });
  }

  const title = docType === "packing" ? "PACKING SLIP" : docType === "priced" ? "DELIVERY NOTE" : "INVOICE";
  const showPrice = docType !== "packing";
  const showVat = docType === "invoice";
  const prodS = fgRow ? productFor(fgRow.origin, fgRow.bagSizeG) : null;
  const description = prodS ? `${productLabel(fgRow.origin, fgRow.bagSizeG)} — batch ${alloc.sku}` : `${fgRow?.origin || alloc.sku}${fgRow?.bagSizeG ? ` — ${fgRow.bagSizeG}g` : ""} (${alloc.sku})`;

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>${title} — ${alloc.customerName || ""}</title>
<style>
  body { font-family: Georgia, "Times New Roman", serif; color:#1C1814; padding: 40px; }
  .top-row { display:flex; justify-content:space-between; margin-bottom:30px; }
  .biz b { font-size:18px; }
  .biz div { font-size:13px; line-height:1.5; margin-top:6px; font-family: -apple-system, Arial, sans-serif; }
  .doc-title { text-align:right; }
  .doc-title h1 { margin:0; font-size:24px; letter-spacing:0.05em; }
  .doc-title .meta { font-family: monospace; font-size:12px; color:#555; margin-top:6px; }
  .bill-to { font-size:13px; margin-bottom:26px; font-family: -apple-system, Arial, sans-serif; }
  table { width:100%; border-collapse:collapse; font-family: -apple-system, Arial, sans-serif; font-size:13px; margin-bottom:14px; }
  th { text-align:left; color:#777; font-size:11px; text-transform:uppercase; letter-spacing:0.04em; border-bottom:1px solid #ddd; padding:8px 4px; }
  td { padding:8px 4px; border-bottom:1px solid #eee; }
  .totals { display:flex; justify-content:flex-end; }
  .totals table { width:260px; }
  .totals td { border:none; padding:4px 8px; }
  .totals .grand { font-weight:700; font-size:15px; border-top:1px solid #333; }
  .footnote { font-size:10.5px; color:#888; margin-top:30px; font-family: -apple-system, Arial, sans-serif; }
  @media print { body { padding: 0 30px; } }
</style></head><body>
  <div class="top-row">
    <div class="biz">
      <b>${settings.businessName || "—"}</b>
      <div>${(settings.businessAddress || "").replace(/\n/g, "<br>")}${settings.vatNumber ? `<br>VAT: ${settings.vatNumber}` : ""}</div>
    </div>
    <div class="doc-title">
      <h1>${title}</h1>
      <div class="meta">${docNumber ? docNumber + "<br>" : ""}${dmy(alloc.date)}</div>
    </div>
  </div>
  <div class="bill-to"><b>${docType === "packing" ? "Delivered to" : "Bill to"}</b><br>${alloc.customerName || ""}</div>
  <table>
    <thead><tr><th>Description</th><th>Qty</th>${showPrice ? "<th>Unit price</th><th>Line total</th>" : ""}</tr></thead>
    <tbody>
      <tr>
        <td>${description}</td>
        <td>${qty}</td>
        ${showPrice ? `<td>€${price.toFixed(2)}</td><td>€${lineTotal.toFixed(2)}</td>` : ""}
      </tr>
    </tbody>
  </table>
  ${showPrice ? `<div class="totals"><table>
    <tr><td>Subtotal</td><td style="text-align:right;">€${lineTotal.toFixed(2)}</td></tr>
    ${showVat ? `<tr><td>VAT (${vatRate}%)</td><td style="text-align:right;">€${vatAmount.toFixed(2)}</td></tr>` : ""}
    <tr class="grand"><td>Total</td><td style="text-align:right;">€${grandTotal.toFixed(2)}</td></tr>
  </table></div>` : ""}
  ${showVat ? `<div class="footnote">Confirm this VAT rate and format meet your obligations before relying on it for your accounts.</div>` : ""}
</body></html>`;

  const win = window.open("", "_blank");
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 300);
}

// Same as printAllocationDocument, but for several allocation lines at
// once — one packing slip / priced document / VAT invoice covering
// everything undocumented for a customer, rather than a single coffee.
// lines is an array of { alloc, fgRow, unitPrice }, all belonging to the
// same customer.
function printAllocationDocumentMulti(lines, docType, settings) {
  const showPrice = docType !== "packing";
  const showVat = docType === "invoice";
  const vatRate = showVat ? (parseFloat(settings.vatRatePct) || 0) : 0;
  const customerName = lines[0]?.alloc.customerName || "";
  const today = localIsoDate();

  const rows = lines.map(({ alloc, fgRow, unitPrice }) => {
    const price = parseFloat(unitPrice) || 0;
    const lineTotal = Math.round(alloc.qty * price * 100) / 100;
    const prodM = fgRow ? productFor(fgRow.origin, fgRow.bagSizeG) : null;
    const description = prodM ? `${productLabel(fgRow.origin, fgRow.bagSizeG)} — batch ${alloc.sku}` : `${fgRow?.origin || alloc.sku}${fgRow?.bagSizeG ? ` — ${fgRow.bagSizeG}g` : ""} (${alloc.sku})`;
    return { description, qty: alloc.qty, price, lineTotal };
  });
  const subtotal = Math.round(rows.reduce((a, r) => a + r.lineTotal, 0) * 100) / 100;
  const vatAmount = showVat ? Math.round(subtotal * vatRate) / 100 : 0;
  const grandTotal = Math.round((subtotal + vatAmount) * 100) / 100;

  let docNumber = "";
  if (docType !== "packing") {
    const num = settings.nextInvoiceNumber || 1;
    docNumber = `${settings.invoicePrefix || "INV-"}${String(num).padStart(4, "0")}`;
    saveInvoiceSettings({ ...settings, nextInvoiceNumber: num + 1 });
  }

  const title = docType === "packing" ? "PACKING SLIP" : docType === "priced" ? "DELIVERY NOTE" : "INVOICE";
  const rowsHtml = rows.map(r => `
      <tr>
        <td>${r.description}</td>
        <td>${r.qty}</td>
        ${showPrice ? `<td>€${r.price.toFixed(2)}</td><td>€${r.lineTotal.toFixed(2)}</td>` : ""}
      </tr>`).join("");

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>${title} — ${customerName}</title>
<style>
  body { font-family: Georgia, "Times New Roman", serif; color:#1C1814; padding: 40px; }
  .top-row { display:flex; justify-content:space-between; margin-bottom:30px; }
  .biz b { font-size:18px; }
  .biz div { font-size:13px; line-height:1.5; margin-top:6px; font-family: -apple-system, Arial, sans-serif; }
  .doc-title { text-align:right; }
  .doc-title h1 { margin:0; font-size:24px; letter-spacing:0.05em; }
  .doc-title .meta { font-family: monospace; font-size:12px; color:#555; margin-top:6px; }
  .bill-to { font-size:13px; margin-bottom:26px; font-family: -apple-system, Arial, sans-serif; }
  table { width:100%; border-collapse:collapse; font-family: -apple-system, Arial, sans-serif; font-size:13px; margin-bottom:14px; }
  th { text-align:left; color:#777; font-size:11px; text-transform:uppercase; letter-spacing:0.04em; border-bottom:1px solid #ddd; padding:8px 4px; }
  td { padding:8px 4px; border-bottom:1px solid #eee; }
  .totals { display:flex; justify-content:flex-end; }
  .totals table { width:260px; }
  .totals td { border:none; padding:4px 8px; }
  .totals .grand { font-weight:700; font-size:15px; border-top:1px solid #333; }
  .footnote { font-size:10.5px; color:#888; margin-top:30px; font-family: -apple-system, Arial, sans-serif; }
  @media print { body { padding: 0 30px; } }
</style></head><body>
  <div class="top-row">
    <div class="biz">
      <b>${settings.businessName || "—"}</b>
      <div>${(settings.businessAddress || "").replace(/\n/g, "<br>")}${settings.vatNumber ? `<br>VAT: ${settings.vatNumber}` : ""}</div>
    </div>
    <div class="doc-title">
      <h1>${title}</h1>
      <div class="meta">${docNumber ? docNumber + "<br>" : ""}${dmy(today)}</div>
    </div>
  </div>
  <div class="bill-to"><b>${docType === "packing" ? "Delivered to" : "Bill to"}</b><br>${customerName}</div>
  <table>
    <thead><tr><th>Description</th><th>Qty</th>${showPrice ? "<th>Unit price</th><th>Line total</th>" : ""}</tr></thead>
    <tbody>${rowsHtml}</tbody>
  </table>
  ${showPrice ? `<div class="totals"><table>
    <tr><td>Subtotal</td><td style="text-align:right;">€${subtotal.toFixed(2)}</td></tr>
    ${showVat ? `<tr><td>VAT (${vatRate}%)</td><td style="text-align:right;">€${vatAmount.toFixed(2)}</td></tr>` : ""}
    <tr class="grand"><td>Total</td><td style="text-align:right;">€${grandTotal.toFixed(2)}</td></tr>
  </table></div>` : ""}
  ${showVat ? `<div class="footnote">Confirm this VAT rate and format meet your obligations before relying on it for your accounts.</div>` : ""}
</body></html>`;

  const win = window.open("", "_blank");
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 300);
}

// ── LABEL SETTINGS ────────────────────────────────────────────────────────
// User-adjustable label sizing, edited from the "Label settings" panel in
// Packaging (with a live preview) instead of needing a code change every
// time the layout needs a nudge.
const LABEL_SETTINGS_KEY = "fdc_label_settings";
const LABEL_SETTINGS_DEFAULTS = { labelWidthMm: 89, labelHeightMm: 28, brandSizeMm: 2.6, nameSizeMm: 4.6, dateSizeMm: 2.7, batchSizeMm: 2.3, textColWidthMm: 46, barcodeHeightMm: 16, barcodeWidthMm: 39 };
function loadLabelSettings() {
  try { return { ...LABEL_SETTINGS_DEFAULTS, ...JSON.parse(localStorage.getItem(LABEL_SETTINGS_KEY) || "{}") }; } catch { return { ...LABEL_SETTINGS_DEFAULTS }; }
}
function saveLabelSettings(s) { localStorage.setItem(LABEL_SETTINGS_KEY, JSON.stringify(s)); }

// Draws the label onto a canvas at the given settings — shared by both the
// real download and the live preview in the settings panel, so the two
// can never drift apart from each other.
async function buildLabelCanvas(batch, settingsIn) {
  const st = { ...LABEL_SETTINGS_DEFAULTS, ...settingsIn };
  const DPI = 300;
  const mmToPx = mm => Math.round(mm / 25.4 * DPI);
  const W = mmToPx(st.labelWidthMm), H = mmToPx(st.labelHeightMm);
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, W, H);
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#1C1814";

  const padX = mmToPx(1);
  const textColX = padX;
  const textColW = mmToPx(st.textColWidthMm);
  let y = mmToPx(3.6);

  // Coffees with a Skelligs product: product name, what's in the bag, SKU,
  // dates, lot — and the product's own retail barcode.
  const prod = productFor(batch.origin, batch.bagSizeG);
  if (prod) {
    ctx.font = `bold ${mmToPx(st.nameSizeMm)}px -apple-system, Arial, sans-serif`;
    ctx.fillText(prod.name, textColX, y, textColW);
    y += mmToPx(st.dateSizeMm) + mmToPx(1.0);
    ctx.font = `${mmToPx(st.dateSizeMm)}px -apple-system, Arial, sans-serif`;
    const size = batch.bagSizeG >= 1000 ? `${batch.bagSizeG / 1000}kg` : `${batch.bagSizeG}g`;
    ctx.fillText(`${prod.describe || batch.origin} · ${size}`, textColX, y, textColW);
    y += mmToPx(3.0);
    ctx.fillText(`Roast: ${formatDateDMY(batch.roastDate)}`, textColX, y, textColW);
    y += mmToPx(3.0);
    ctx.fillText(`Best before: ${formatDateDMY(batch.expiryDate)}`, textColX, y, textColW);
    y += mmToPx(3.0);
    ctx.font = `bold ${mmToPx(st.batchSizeMm)}px monospace`;
    ctx.fillText(`SKU ${prod.sku}`, textColX, y, textColW);
    y += mmToPx(st.batchSizeMm) + mmToPx(0.6);
    ctx.font = `${mmToPx(st.batchSizeMm)}px monospace`;
    ctx.fillStyle = "#7A6E63";
    ctx.fillText(`Lot ${batch.roastBatch || batch.lot || batch.sku}`, textColX, y, textColW);
    ctx.fillStyle = "#1C1814";
    // A retail barcode needs every bit of width it can get: it takes all the
    // space from the end of the text to the edge of the label (its clear
    // margins sit in that space), rather than the fixed barcode width setting.
    const barAreaX = textColX + textColW;
    const barAreaW = W - barAreaX;
    const barAreaH = Math.min(mmToPx(st.barcodeHeightMm), H - mmToPx(3));
    if (eanValid(prod.ean)) {
      drawEan13(ctx, prod.ean.replace(/\s/g, ""), barAreaX, mmToPx(2), barAreaW, barAreaH);
    } else {
      ctx.font = `${mmToPx(st.batchSizeMm)}px monospace`;
      ctx.fillText("Barcode number not valid", barAreaX, mmToPx(6), barAreaW);
    }
    return canvas;
  }

  ctx.font = `bold ${mmToPx(st.brandSizeMm)}px -apple-system, Arial, sans-serif`;
  ctx.fillText("SKELLIGS COFFEE", textColX, y, textColW);
  y += mmToPx(st.nameSizeMm) + mmToPx(0.5);

  ctx.font = `bold ${mmToPx(st.nameSizeMm)}px -apple-system, Arial, sans-serif`;
  ctx.fillText((batch.origin || "—").slice(0, 22), textColX, y, textColW);
  y += mmToPx(4.0);

  ctx.font = `${mmToPx(st.dateSizeMm)}px -apple-system, Arial, sans-serif`;
  ctx.fillText(`Roast: ${formatDateDMY(batch.roastDate)}`, textColX, y, textColW);
  y += mmToPx(3.0);
  ctx.fillText(`Best before: ${formatDateDMY(batch.expiryDate)}`, textColX, y, textColW);
  y += mmToPx(3.0);

  ctx.font = `${mmToPx(st.batchSizeMm)}px monospace`;
  ctx.fillStyle = "#7A6E63";
  ctx.fillText(`Batch: ${batch.sku}`, textColX, y, textColW);
  ctx.fillStyle = "#1C1814";

  const code = productCode({ origin: batch.origin, process: batch.process, bagSizeG: batch.bagSizeG });
  const barAreaX = textColX + textColW + mmToPx(2);
  const barAreaW = mmToPx(st.barcodeWidthMm);
  const barHeightPx = mmToPx(st.barcodeHeightMm);
  const barY = mmToPx(2);

  const customSrc = loadCustomBarcodes()[code];
  let usedCustomImage = false;
  if (customSrc) {
    try {
      const img = await loadImageAsync(customSrc);
      const imgScale = Math.min(barAreaW / img.width, barHeightPx / img.height);
      const drawW = img.width * imgScale;
      const drawH = img.height * imgScale;
      ctx.drawImage(img, barAreaX + (barAreaW - drawW) / 2, barY + (barHeightPx - drawH) / 2, drawW, drawH);
      usedCustomImage = true;
    } catch {
      // Falls through to the auto-generated barcode below if the stored image fails to load.
    }
  }
  if (!usedCustomImage) {
    const { bars, totalWidth } = buildCode39Bars(code);
    const scale = barAreaW / totalWidth;
    bars.forEach(b => {
      ctx.fillRect(barAreaX + b.x * scale, barY, Math.max(1, b.w * scale), barHeightPx);
    });
  }
  return canvas;
}

async function downloadLabelPNG(batch) {
  // Chrome's print pipeline can't reliably handle small custom label
  // sizes (confirmed: DYMO Connect prints the same physical roll cleanly
  // on its own). So instead of fighting Chrome's print dialog, this
  // generates a pixel-exact image at whatever label width/height is set
  // in the Label settings panel in Packaging, and downloads it — open
  // that file directly in DYMO Connect to actually print it.
  const canvas = await buildLabelCanvas(batch, loadLabelSettings());
  canvas.toBlob(blob => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `label-${batch.sku}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, "image/png");
}

// Products & SKUs — edit the retail products labels and paperwork use.
function ProductsPanel() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(loadProducts);
  const update = (next) => { setRows(next); saveProducts(next); };
  const setField = (id, k, v) => update(rows.map(r => r.id === id ? { ...r, [k]: v } : r));
  const addRow = () => update([...rows, { id: String(Date.now()), sku: "", name: "", coffee: "", describe: "", bagSizeG: 1000, ean: "", wsPrice: "", rrp: "", caseSize: "" }]);
  const removeRow = (id) => { if (window.confirm("Remove this product? Labels for that coffee and size go back to the standard layout.")) update(rows.filter(r => r.id !== id)); };
  let coffees = [];
  try { coffees = [...new Set(JSON.parse(localStorage.getItem("fdc_inventory") || "[]").map(i => String(i.name || i.origin || "").trim()).filter(Boolean))]; } catch { coffees = []; }
  const cell = { ...s.input, fontSize: 12, padding: "5px 7px" };
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 16, overflow: "hidden" }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", padding: "12px 16px", background: C.cream2 }}>
        <div>
          <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em" }}>Products & SKUs {open ? "▾" : "▸"}</div>
          <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginTop: 2 }}>{rows.length} product{rows.length === 1 ? "" : "s"} — name, SKU and barcode used on labels, finished goods and invoices</div>
        </div>
      </div>
      {open && (
        <div style={{ padding: 14 }}>
          <datalist id="product-coffees">{coffees.map(c => <option key={c} value={c} />)}</datalist>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1050 }}>
              <thead><tr style={{ background: C.cream3 }}>{["Coffee (Green stock)", "Size", "Product name", "On the label", "SKU", "Barcode (EAN-13)", "W/S €", "RRP €", "Case", ""].map(h => <th key={h} style={s.thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {rows.map(r => {
                  const eanOk = eanValid(r.ean);
                  return (
                    <tr key={r.id} style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                      <td style={s.td}><input list="product-coffees" style={{ ...cell, width: 120 }} value={r.coffee} onChange={e => setField(r.id, "coffee", e.target.value)} /></td>
                      <td style={s.td}><select style={{ ...cell, width: 80 }} value={r.bagSizeG} onChange={e => setField(r.id, "bagSizeG", parseInt(e.target.value))}>{[250, 500, 1000].map(g => <option key={g} value={g}>{g >= 1000 ? "1kg" : `${g}g`}</option>)}</select></td>
                      <td style={s.td}><input style={{ ...cell, width: 150 }} value={r.name} onChange={e => setField(r.id, "name", e.target.value)} /></td>
                      <td style={s.td}><input style={{ ...cell, width: 150 }} value={r.describe} onChange={e => setField(r.id, "describe", e.target.value)} placeholder="e.g. Brazil Carmo" /></td>
                      <td style={s.td}><input style={{ ...cell, width: 70, fontFamily: "monospace" }} value={r.sku} onChange={e => setField(r.id, "sku", e.target.value)} /></td>
                      <td style={s.td}>
                        <input style={{ ...cell, width: 130, fontFamily: "monospace", borderColor: r.ean && !eanOk ? C.red : undefined }} value={r.ean} onChange={e => setField(r.id, "ean", e.target.value)} />
                        <div style={{ fontFamily: fontSans, fontSize: 10, color: !r.ean ? C.inkFaint : eanOk ? C.green : C.red }}>{!r.ean ? "none yet" : eanOk ? "✓ valid" : "✗ check the number"}</div>
                      </td>
                      <td style={s.td}><input style={{ ...cell, width: 60 }} type="number" step="0.01" value={r.wsPrice} onChange={e => setField(r.id, "wsPrice", e.target.value)} /></td>
                      <td style={s.td}><input style={{ ...cell, width: 60 }} type="number" step="0.01" value={r.rrp} onChange={e => setField(r.id, "rrp", e.target.value)} /></td>
                      <td style={s.td}><input style={{ ...cell, width: 48 }} type="number" value={r.caseSize} onChange={e => setField(r.id, "caseSize", e.target.value)} /></td>
                      <td style={s.td}><button style={{ ...s.btnSm, color: C.red, borderColor: C.red }} onClick={() => removeRow(r.id)}>×</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
            <button style={s.btn} onClick={addRow}>+ Add product (e.g. the 1kg bags)</button>
            <span style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint }}>Saved as you type. A coffee and size without a product here keeps the standard label.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function LabelSettingsPanel() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(loadLabelSettings);
  const [customBarcodes, setCustomBarcodes] = useState(loadCustomBarcodes);
  const [newCode, setNewCode] = useState("");
  const canvasRef = useRef(null);
  const sampleBatch = { origin: "Halo Beriti — Ethiopia", roastDate: "2026-07-27", expiryDate: "2027-07-22", sku: "HAL-W-250-260727-01", process: "Washed", bagSizeG: 250 };

  const handleBarcodeUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file || !newCode.trim()) return;
    const reader = new FileReader();
    reader.onload = () => {
      const updated = { ...customBarcodes, [newCode.trim().toUpperCase()]: reader.result };
      setCustomBarcodes(updated);
      saveCustomBarcodes(updated);
      setNewCode("");
      e.target.value = "";
    };
    reader.readAsDataURL(file);
  };
  const removeCustomBarcode = (code) => {
    const updated = { ...customBarcodes };
    delete updated[code];
    setCustomBarcodes(updated);
    saveCustomBarcodes(updated);
  };

  useEffect(() => {
    if (!open || !canvasRef.current) return;
    let cancelled = false;
    buildLabelCanvas(sampleBatch, settings).then(preview => {
      if (cancelled || !canvasRef.current) return;
      canvasRef.current.width = preview.width;
      canvasRef.current.height = preview.height;
      canvasRef.current.getContext("2d").drawImage(preview, 0, 0);
    });
    return () => { cancelled = true; };
  }, [open, settings]);

  const update = (k, v) => setSettings(sv => ({ ...sv, [k]: v }));
  const save = () => saveLabelSettings(settings);
  const reset = () => { setSettings({ ...LABEL_SETTINGS_DEFAULTS }); saveLabelSettings(LABEL_SETTINGS_DEFAULTS); };

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 20, overflow: "hidden" }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: C.cream2, cursor: "pointer" }}>
        <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em" }}>Label settings {open ? "▾" : "▸"}</div>
        <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint }}>Adjust text and barcode sizing on the printed label</div>
      </div>
      {open && (
        <div style={{ padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16, background: "#f5f5f5", padding: 12, borderRadius: 8 }}>
            <canvas ref={canvasRef} style={{ width: "100%", maxWidth: 445, height: "auto", border: "1px solid #ccc", background: "#fff" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {[
              ["Label width", "labelWidthMm", 20, 100, 0.5],
              ["Label height", "labelHeightMm", 10, 40, 0.5],
              ["Brand text size", "brandSizeMm", 1.5, 4, 0.1],
              ["Coffee name size", "nameSizeMm", 3, 7, 0.1],
              ["Date text size", "dateSizeMm", 1.5, 4, 0.1],
              ["Batch text size", "batchSizeMm", 1.5, 3.5, 0.1],
              ["Text column width", "textColWidthMm", 10, 60, 1],
              ["Barcode height", "barcodeHeightMm", 8, 24, 0.5],
              ["Barcode width", "barcodeWidthMm", 15, 70, 1],
            ].map(([label, key, min, max, step]) => (
              <div key={key}>
                <div style={{ display: "flex", justifyContent: "space-between", fontFamily: fontSans, fontSize: 11, color: C.inkLight, marginBottom: 3 }}>
                  <span>{label}</span><span>{settings[key]}mm</span>
                </div>
                <input type="range" min={min} max={max} step={step} value={settings[key]} onChange={e => update(key, parseFloat(e.target.value))} style={{ width: "100%", accentColor: C.amber }} />
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button style={s.btnPrimary} onClick={save}>Save settings</button>
            <button style={s.btn} onClick={reset}>Reset to defaults</button>
          </div>
          <div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, marginTop: 10, lineHeight: 1.5 }}>
            Preview uses a sample coffee — actual labels use each batch's real name, dates, and batch number. Settings apply to every label downloaded from here on, until changed again.
          </div>

          <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
            <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>Custom barcode images</div>
            <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 10, lineHeight: 1.5 }}>
              Upload a real barcode image (e.g. a retail EAN) against a product code to use it on that coffee's labels instead of the auto-generated one. The product code is the short one shown under the barcode on each label — e.g. HAL-W-250.
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <input
                type="text"
                placeholder="Product code, e.g. HAL-W-250"
                value={newCode}
                onChange={e => setNewCode(e.target.value)}
                style={{ ...s.input, flex: 1 }}
              />
              <label style={{ ...s.btn, cursor: newCode.trim() ? "pointer" : "not-allowed", display: "flex", alignItems: "center", opacity: newCode.trim() ? 1 : 0.5 }}>
                Upload image
                <input type="file" accept="image/*" onChange={handleBarcodeUpload} style={{ display: "none" }} disabled={!newCode.trim()} />
              </label>
            </div>
            {Object.keys(customBarcodes).length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {Object.entries(customBarcodes).map(([code, src]) => (
                  <div key={code} style={{ display: "flex", alignItems: "center", gap: 10, border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 10px" }}>
                    <img src={src} alt={code} style={{ height: 24, maxWidth: 80, objectFit: "contain", background: "#fff" }} />
                    <div style={{ fontFamily: "monospace", fontSize: 11, color: C.ink, flex: 1 }}>{code}</div>
                    <button style={s.btn} onClick={() => removeCustomBarcode(code)}>Remove</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function printLabel(batch) {
  // Prints at whatever label width/height is set in the Label settings
  // panel in Packaging (defaults to the standard 89mm x 28mm DYMO
  // address label). The barcode encodes the stable product code (short
  // — reliable to scan at native proportions, no distortion needed).
  // The full batch SKU is shown as plain text for traceability instead
  // — a person reads that, it doesn't need to be scanned. If a custom
  // barcode image has been uploaded for this product code, that image
  // is used instead of the auto-generated bars.
  const st = loadLabelSettings();
  const LW = st.labelWidthMm, LH = st.labelHeightMm;
  const hScale = LW / 89;
  const vScale = LH / 28;
  const fontScale = Math.min(hScale, vScale);
  const mm = n => (n * hScale).toFixed(2);
  const mmV = n => (n * vScale).toFixed(2);
  const px = n => (n * fontScale).toFixed(2);
  const code = productCode({ origin: batch.origin, process: batch.process, bagSizeG: batch.bagSizeG });
  const customSrc = loadCustomBarcodes()[code];
  const codeBlockMarkup = customSrc
    ? `<img src="${customSrc}" alt="${code}" />`
    : code39SVGMarkup(code, 40);
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Label — ${batch.sku}</title>
<style>
  @page { margin:0; size: ${LW}mm ${LH}mm; }
  html, body { width:${LW}mm; height:${LH}mm; }
  * { box-sizing:border-box; margin:0; padding:0; font-family:-apple-system,"Helvetica Neue",Arial,sans-serif; }
  .label { position:absolute; top:0; left:0; width:${LW}mm; height:${LH}mm; overflow:hidden; display:flex; gap:${mm(3)}mm; padding:${mmV(1.5)}mm ${mm(2)}mm; align-items:center; }
  .info { width:${mm(50)}mm; flex-shrink:0; overflow:hidden; }
  .brand { font-size:${px(6.5)}px; font-weight:700; letter-spacing:0.06em; text-transform:uppercase; color:#1C1814; line-height:1.2; }
  .name { font-size:${px(11)}px; font-weight:700; color:#1C1814; margin-top:2px; line-height:1.2; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .dates { font-size:${px(6.5)}px; color:#333; margin-top:3px; line-height:1.4; }
  .batch { font-family:monospace; font-size:${px(6)}px; color:#7A6E63; margin-top:3px; line-height:1.2; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .code-block { width:${st.barcodeWidthMm}mm; flex-shrink:0; display:flex; flex-direction:column; align-items:center; justify-content:center; overflow:hidden; }
  .code-block svg, .code-block img { width:100%; height:auto; display:block; }
  @media print { html, body { -webkit-print-color-adjust:exact; print-color-adjust:exact; } }
</style></head><body>
<div class="label">
  <div class="info">
    <div class="brand">Skelligs Coffee</div>
    <div class="name">${batch.origin || "—"}</div>
    <div class="dates">Roast: ${formatDateDMY(batch.roastDate)}<br/>Best before: ${formatDateDMY(batch.expiryDate)}</div>
    <div class="batch">Batch: ${batch.sku}</div>
  </div>
  <div class="code-block">
    ${codeBlockMarkup}

  </div>
</div>
<script>window.onload = () => { window.focus(); window.print(); };</script>
</body></html>`;
  const win = window.open("", "_blank");
  if (!win) { window.alert("Please allow pop-ups for this site to print the label."); return; }
  win.document.write(html);
  win.document.close();
}

// ── PIPELINE OVERVIEW ─────────────────────────────────────────────────────────
function PackagingOverviewTab({ inventory }) {
  const [history] = useState(() => { try { return JSON.parse(localStorage.getItem("fdc_history") || "[]"); } catch { return []; } });
  const [approvals] = useState(loadQcApprovals);
  const [batches] = useState(loadPackagingBatches);
  const [allocations] = useState(loadAllocations);
  const fg = computeFinishedGoods(batches, allocations);

  const totalGreenKg = inventory.reduce((a, i) => a + Math.round(parseFloat(i.bagWeight || 0) * parseFloat(i.bags || 0) * 10) / 10, 0);
  const totalRoasts = history.filter(r => r.roasted).length;
  const pendingQc = history.filter(r => r.roasted && (!approvals[r.id] || approvals[r.id].status === "pending")).length;
  const approvedQc = Object.values(approvals).filter(v => v.status === "approved").length;
  const totalPackagedBags = batches.reduce((a, b) => a + b.qtyBags, 0);
  const totalFgAvailable = fg.reduce((a, r) => a + r.available, 0);
  const totalAllocated = allocations.reduce((a, al) => a + al.qty, 0);

  const stages = [
    { label: "Green Coffee", value: `${totalGreenKg}kg`, note: "in stock" },
    { label: "Production Roast", value: totalRoasts, note: "roasts with output" },
    { label: "QC Approval", value: approvedQc, note: `${pendingQc} pending`, warn: pendingQc > 0 },
    { label: "Packaging", value: totalPackagedBags, note: "bags packaged" },
    { label: "Finished Goods", value: totalFgAvailable, note: "bags available" },
    { label: "Customer Allocation", value: totalAllocated, note: "bags allocated" },
    { label: "Dispatch", value: "—", note: "coming soon", faint: true },
  ];

  return (
    <div style={{ paddingTop: 24 }}>
      <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 20, lineHeight: 1.5 }}>
        The full traceability chain from green coffee to customer allocation. Each stage's numbers come straight from the records in the tabs alongside this one — nothing here is entered separately.
      </div>
      <div style={{ display: "flex", alignItems: "stretch", gap: 6, overflowX: "auto", paddingBottom: 10 }}>
        {stages.map((st, i) => (
          <Fragment key={st.label}>
            <div style={{
              minWidth: 128, border: `1px solid ${st.warn ? C.amber : C.border}`, borderRadius: 8, padding: "14px 12px",
              background: st.faint ? C.cream : C.cream2, flexShrink: 0,
            }}>
              <div style={{ fontFamily: fontSans, fontSize: 9, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>{st.label}</div>
              <div style={{ fontFamily: fontSerif, fontSize: 22, color: st.faint ? C.inkFaint : C.ink }}>{st.value}</div>
              <div style={{ fontFamily: fontSans, fontSize: 10, color: st.warn ? C.amber : C.inkFaint, marginTop: 2 }}>{st.note}</div>
            </div>
            {i < stages.length - 1 && <div style={{ display: "flex", alignItems: "center", color: C.inkFaint, fontSize: 18, flexShrink: 0 }}>→</div>}
          </Fragment>
        ))}
      </div>

      {fg.some(r => r.earliestExpiry && (new Date(r.earliestExpiry) - new Date()) / 86400000 < 60) && (
        <div style={{ marginTop: 20, background: "#FDF3E3", border: `1px solid ${C.amber}`, borderRadius: 8, padding: "12px 16px" }}>
          <div style={{ fontFamily: fontSans, fontSize: 11, fontWeight: 700, color: "#8B5A1E", marginBottom: 6 }}>Approaching expiry (within 60 days)</div>
          {fg.filter(r => r.earliestExpiry && (new Date(r.earliestExpiry) - new Date()) / 86400000 < 60 && r.available > 0).map(r => (
            <div key={r.sku} style={{ fontFamily: fontSans, fontSize: 12, color: C.inkMid, padding: "3px 0" }}>
              {r.sku} — {r.available} bags available, earliest lot best-before {dmy(r.earliestExpiry)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── QC APPROVAL ────────────────────────────────────────────────────────────────
function QCApprovalTab() {
  const [history] = useState(() => { try { return JSON.parse(localStorage.getItem("fdc_history") || "[]"); } catch { return []; } });
  const [approvals, setApprovals] = useState(loadQcApprovals);
  const [notesDraft, setNotesDraft] = useState({});

  const setStatus = (roastId, status) => {
    const existing = approvals[roastId] || {};
    const updated = { ...approvals, [roastId]: { ...existing, status, date: localIsoDate(), notes: notesDraft[roastId] ?? existing.notes ?? "" } };
    setApprovals(updated);
    saveQcApprovals(updated);
  };
  const saveNote = (roastId) => {
    if (notesDraft[roastId] === undefined) return;
    const existing = approvals[roastId] || { status: "pending" };
    const updated = { ...approvals, [roastId]: { ...existing, notes: notesDraft[roastId] } };
    setApprovals(updated);
    saveQcApprovals(updated);
  };

  const eligible = history.filter(r => r.roasted);

  return (
    <div style={{ paddingTop: 24 }}>
      <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 16, lineHeight: 1.5 }}>
        Approve or reject each roast before it can move to Packaging. Cupping score and colour readings come straight from Roast History / QC Tools.
      </div>
      {eligible.length === 0 ? (
        <div style={{ fontFamily: fontSans, fontSize: 13, color: C.inkFaint }}>No roasted batches with logged output yet — nothing to approve.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {eligible.map(r => {
            const app = approvals[r.id] || { status: "pending" };
            const statusColor = app.status === "approved" ? C.green : app.status === "rejected" ? C.red : C.amber;
            return (
              <div key={r.id} style={{ border: `1px solid ${C.border}`, borderLeft: `4px solid ${statusColor}`, borderRadius: 8, padding: "14px 16px", background: "#fff" }}>
                <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                  <div>
                    <div style={{ fontFamily: fontSerif, fontSize: 15, color: C.ink }}>{r.origin || "Untitled"} — {r.batch || "no batch #"}</div>
                    <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginTop: 2 }}>{dmy(r.date) || ""} · {r.roasted}kg roasted · {r.cultivar || "—"} · {r.process || "—"}</div>
                  </div>
                  <div style={{ display: "flex", gap: 12, alignItems: "center", fontFamily: fontSans, fontSize: 11, color: C.inkLight }}>
                    {r.score && <span>Score {r.score}</span>}
                    {r.agtron && <span>Agtron {r.agtron}</span>}
                    <span style={{ fontWeight: 700, color: statusColor, textTransform: "uppercase", fontSize: 10, letterSpacing: "0.05em" }}>{app.status}</span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <button onClick={() => setStatus(r.id, "approved")} style={{ ...s.btnSmall, background: app.status === "approved" ? C.green : C.cream, color: app.status === "approved" ? "#fff" : C.inkMid }}>✓ Approve</button>
                  <button onClick={() => setStatus(r.id, "rejected")} style={{ ...s.btnSmall, background: app.status === "rejected" ? C.red : C.cream, color: app.status === "rejected" ? "#fff" : C.inkMid }}>✕ Reject</button>
                  <input
                    style={{ ...s.input, flex: 1, minWidth: 180, fontSize: 12, padding: "5px 8px" }}
                    placeholder="QC notes (optional)"
                    value={notesDraft[r.id] ?? app.notes ?? ""}
                    onChange={e => setNotesDraft(n => ({ ...n, [r.id]: e.target.value }))}
                    onBlur={() => saveNote(r.id)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── PACKAGING ──────────────────────────────────────────────────────────────────
function PackagingRunTab() {
  const [history] = useState(() => { try { return JSON.parse(localStorage.getItem("fdc_history") || "[]"); } catch { return []; } });
  const [approvals] = useState(loadQcApprovals);
  const [batches, setBatches] = useState(loadPackagingBatches);
  const [policy, setPolicy] = useState(loadShelfLifePolicy);
  const [showPolicy, setShowPolicy] = useState(false);
  const [editingBatchId, setEditingBatchId] = useState(null);
  const [editDraft, setEditDraft] = useState({});

  // Approved roasts that still have coffee left to bag — one card each, so
  // there's no dropdown to re-pick the roast. Approval itself happens in the
  // QC Suite; fully-packed roasts drop off this list (they're in the batch
  // list below).
  const alreadyPackagedKg = (rid) => batches.filter(b => String(b.roastId) === String(rid)).reduce((a, b) => a + (b.qtyBags * b.netWeightPerBagG / 1000), 0);
  const toPack = history
    .filter(r => r.roasted && approvals[r.id]?.status === "approved")
    // Under a quarter kilo left can't fill even one 250g bag — treat as done
    // so rounding leftovers don't sit in the list forever.
    .filter(r => parseFloat(r.roasted) - alreadyPackagedKg(r.id) >= 0.25)
    .sort((a, b) => (b.id || 0) - (a.id || 0));
  const approvedCount = history.filter(r => r.roasted && approvals[r.id]?.status === "approved").length;
  const lastBatchForOrigin = (origin) => batches.filter(b => sameCoffee(b.origin, origin)).slice().sort((a, b) => (b.id || 0) - (a.id || 0))[0] || null;
  const lastBatchAny = () => batches.slice().sort((a, b) => (b.id || 0) - (a.id || 0))[0] || null;

  const startEditBatch = (b) => {
    setEditingBatchId(b.id);
    setEditDraft({ origin: b.origin || "", qtyBags: b.qtyBags, bagSizeG: b.bagSizeG, packagedBy: b.packagedBy || "", notes: b.notes || "" });
  };
  const cancelEditBatch = () => setEditingBatchId(null);
  const saveEditBatch = (id) => {
    const updated = batches.map(b => {
      if (b.id !== id) return b;
      const nextQty = parseInt(editDraft.qtyBags) || b.qtyBags;
      const nextSize = parseInt(editDraft.bagSizeG) || b.bagSizeG;
      let materialsUsed = b.materialsUsed;
      // A batch that took materials when it was packed gives them back and
      // takes the corrected amount if its bag count or size is edited.
      if (Array.isArray(b.materialsUsed) && (nextQty !== b.qtyBags || nextSize !== b.bagSizeG)) {
        returnPackagingMaterials(b.materialsUsed);
        materialsUsed = takePackagingMaterials(nextSize, nextQty);
      }
      return { ...b, origin: editDraft.origin, qtyBags: nextQty, bagSizeG: nextSize, packagedBy: editDraft.packagedBy, notes: editDraft.notes, materialsUsed };
    });
    setBatches(updated);
    savePackagingBatches(updated);
    setEditingBatchId(null);
  };

  const updatePolicy = (next) => { setPolicy(next); saveShelfLifePolicy(next); };

  return (
    <div style={{ paddingTop: 24 }}>
      <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 16, lineHeight: 1.5 }}>
        Every approved roast with coffee still to bag appears below. Open one, split it into bags, and it generates the SKU, lot number and label. Approving roasts happens in the QC Suite.
      </div>

      <LabelSettingsPanel />
      <ProductsPanel />

      {/* Shelf life policy */}
      <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 20, overflow: "hidden" }}>
        <div onClick={() => setShowPolicy(o => !o)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: C.cream2, cursor: "pointer" }}>
          <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em" }}>Shelf life / expiry policy {showPolicy ? "▾" : "▸"}</div>
          <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint }}>Default {policy.defaultDays}d</div>
        </div>
        {showPolicy && (
          <div style={{ padding: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
              <div>
                <div style={s.fieldLabel}>Default (days)</div>
                <input style={s.input} type="number" value={policy.defaultDays} onChange={e => updatePolicy({ ...policy, defaultDays: parseInt(e.target.value) || 0 })} />
              </div>
              {Object.keys(policy.byProcess).map(k => (
                <div key={k}>
                  <div style={s.fieldLabel}>{k} (days)</div>
                  <input style={s.input} type="number" value={policy.byProcess[k]} onChange={e => updatePolicy({ ...policy, byProcess: { ...policy.byProcess, [k]: parseInt(e.target.value) || 0 } })} />
                </div>
              ))}
            </div>
            <div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, lineHeight: 1.5 }}>
              These are food-safety "best before" days from roast date, matched by process — not a claim about peak flavour, which is much shorter. Overridable per packaging run below.
            </div>
          </div>
        )}
      </div>

      {toPack.length === 0 ? (
        <div style={{ fontFamily: fontSans, fontSize: 13, color: C.inkFaint }}>
          {approvedCount === 0 ? "No approved roasts yet — approve one in QC Suite → QC approval first." : "Everything approved has been bagged. New roasts appear here once they're approved in the QC Suite."}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {toPack.map(r => (
            <PackRoastCard
              key={r.id}
              roast={r}
              batches={batches}
              setBatches={setBatches}
              policy={policy}
              defaultBagSize={lastBatchForOrigin(r.origin)?.bagSizeG || 250}
              defaultPackagedBy={lastBatchAny()?.packagedBy || ""}
            />
          ))}
        </div>
      )}

      {/* Recent batches */}
      {batches.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
            Recent packaging batches
          </div>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: C.cream3 }}>
                  {["SKU", "Origin", "Bags", "Size", "Packaged", "Best before", ""].map(h => <th key={h} style={s.thStyle}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {batches.slice(0, 20).map(b => (
                  <Fragment key={b.id}>
                    <tr style={{ borderBottom: editingBatchId === b.id ? "none" : `1px solid ${C.borderSoft}` }}>
                      <td style={{ ...s.td, fontFamily: "monospace", color: C.amber }}>{b.sku}</td>
                      <td style={s.td}>{b.origin}</td>
                      <td style={s.td}>{b.qtyBags}</td>
                      <td style={s.td}>{b.bagSizeG}g</td>
                      <td style={s.td}>{dmy(b.packagedDate)}</td>
                      <td style={s.td}>{dmy(b.expiryDate)}</td>
                      <td style={{ ...s.td, display: "flex", gap: 6 }}>
                        <button style={s.btnSm} onClick={() => downloadLabelPNG(b)}>Download</button>
                        <button style={s.btnSm} onClick={() => startEditBatch(b)}>Edit</button>
                      </td>
                    </tr>
                    {editingBatchId === b.id && (
                      <tr style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                        <td colSpan={7} style={{ padding: "12px 14px", background: C.cream2 }}>
                          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
                            <div>
                              <div style={s.fieldLabel}>Name shown on label</div>
                              <input style={s.input} value={editDraft.origin} onChange={e => setEditDraft(d => ({ ...d, origin: e.target.value }))} />
                            </div>
                            <div>
                              <div style={s.fieldLabel}>Qty (bags)</div>
                              <input style={s.input} type="number" value={editDraft.qtyBags} onChange={e => setEditDraft(d => ({ ...d, qtyBags: e.target.value }))} />
                            </div>
                            <div>
                              <div style={s.fieldLabel}>Bag size</div>
                              <select style={s.input} value={editDraft.bagSizeG} onChange={e => setEditDraft(d => ({ ...d, bagSizeG: e.target.value }))}>
                                {[250, 500, 1000].map(g => <option key={g} value={g}>{g}g</option>)}
                              </select>
                            </div>
                            <div>
                              <div style={s.fieldLabel}>Packaged by</div>
                              <input style={s.input} value={editDraft.packagedBy} onChange={e => setEditDraft(d => ({ ...d, packagedBy: e.target.value }))} />
                            </div>
                          </div>
                          <div style={{ marginBottom: 10 }}>
                            <div style={s.fieldLabel}>Notes</div>
                            <input style={s.input} value={editDraft.notes} onChange={e => setEditDraft(d => ({ ...d, notes: e.target.value }))} />
                          </div>
                          <div style={{ fontFamily: fontSans, fontSize: 10.5, color: C.inkFaint, marginBottom: 10 }}>
                            SKU, lot number, and dates aren't changed here — this only fixes what's shown on the label and stock records.
                          </div>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button style={s.btnPrimary} onClick={() => saveEditBatch(b.id)}>Save</button>
                            <button style={s.btn} onClick={cancelEditBatch}>Cancel</button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── PACK A ROAST (one card per approved roast, used by Packaging) ─────────────
// Replaces the separate Roast → Package tab: approval lives in the QC Suite,
// packing lives here. Bag size defaults to what was last used for this coffee
// and "packed by" to the last name entered. Saves exactly the same packaging
// batch record as before.
function PackRoastCard({ roast, batches, setBatches, policy, defaultBagSize, defaultPackagedBy }) {
  const [open, setOpen] = useState(false);
  const [bagSizeG, setBagSizeG] = useState(defaultBagSize);
  const [qtyBags, setQtyBags] = useState("");
  const [netWeightOverride, setNetWeightOverride] = useState("");
  const [shelfDaysOverride, setShelfDaysOverride] = useState("");
  const [packagedBy, setPackagedBy] = useState(defaultPackagedBy);
  const [packagedDate, setPackagedDate] = useState(localIsoDate());
  const [notes, setNotes] = useState("");
  const [lastCreated, setLastCreated] = useState(null);

  const alreadyPackagedKg = batches.filter(b => String(b.roastId) === String(roast.id)).reduce((a, b) => a + (b.qtyBags * b.netWeightPerBagG / 1000), 0);
  const remainingKg = Math.round((parseFloat(roast.roasted) - alreadyPackagedKg) * 100) / 100;
  const netWeightPerBagG = netWeightOverride ? parseFloat(netWeightOverride) : bagSizeG;
  const shelfDays = shelfDaysOverride ? parseInt(shelfDaysOverride) : shelfLifeDaysFor(roast.process, policy);
  const expiryDate = (() => {
    const d = packagedDate ? new Date(packagedDate) : new Date();
    d.setDate(d.getDate() + (parseInt(shelfDays) || 365));
    return localIsoDate(d);
  })();
  const requestedKg = qtyBags ? Math.round((parseFloat(qtyBags) * netWeightPerBagG / 1000) * 100) / 100 : 0;
  const overCapacity = qtyBags && requestedKg > remainingKg + 0.001;

  const createBatch = () => {
    if (!qtyBags || parseFloat(qtyBags) <= 0 || overCapacity) return;
    const dateObj = packagedDate ? new Date(packagedDate) : new Date();
    const sku = generateSKU({ origin: roast.origin, process: roast.process, bagSizeG, date: dateObj });
    const lot = generateLotNumber({ roastBatch: roast.batch, date: dateObj });
    const record = {
      id: Date.now(), roastId: roast.id, roastBatch: roast.batch || "", origin: roast.origin || "", cultivar: roast.cultivar || "",
      process: roast.process || "", roastDate: roast.date || "", bagSizeG, qtyBags: parseInt(qtyBags), netWeightPerBagG,
      packagedDate, packagedBy, sku, lot, shelfLifeDays: parseInt(shelfDays) || 365, expiryDate, notes,
    };
    // One bag packed = one label + one bag off Packaging materials.
    record.materialsUsed = takePackagingMaterials(bagSizeG, parseInt(qtyBags));
    const updated = [record, ...batches];
    setBatches(updated);
    savePackagingBatches(updated);
    setLastCreated(record);
    setQtyBags(""); setNotes("");
  };

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden", background: C.cream2 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, padding: "14px 16px" }}>
        <div>
          <div style={{ fontFamily: fontSerif, fontSize: 16, color: C.ink }}>{roast.origin || "Untitled"} — {roast.batch || "no batch #"}</div>
          <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginTop: 2 }}>{dmy(roast.date) || ""} · {roast.roasted}kg roasted · {roast.process || "—"}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.06em" }}>Left to bag</div>
            <div style={{ fontFamily: fontSerif, fontSize: 20, color: C.ink }}>{remainingKg}kg</div>
          </div>
          <button style={open ? s.btn : s.btnPrimary} onClick={() => setOpen(o => !o)}>{open ? "Close" : "Pack this roast"}</button>
        </div>
      </div>

      {open && (
        <div style={{ borderTop: `1px solid ${C.borderSoft}`, padding: 16, background: C.cream }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12, marginBottom: 12 }}>
            <div>
              <div style={s.fieldLabel}>Bag size</div>
              <select style={s.input} value={bagSizeG} onChange={e => setBagSizeG(parseInt(e.target.value))}>
                {[250, 500, 1000].map(g => <option key={g} value={g}>{g}g</option>)}
              </select>
            </div>
            <div>
              <div style={s.fieldLabel}>Qty (bags)</div>
              <input style={s.input} type="number" min="1" value={qtyBags} onChange={e => setQtyBags(e.target.value)} placeholder="e.g. 80" />
            </div>
            <div>
              <div style={s.fieldLabel}>Packaged by</div>
              <input style={s.input} value={packagedBy} onChange={e => setPackagedBy(e.target.value)} placeholder="Name" />
            </div>
            <div>
              <div style={s.fieldLabel}>Packaged date</div>
              <input style={s.input} type="date" value={packagedDate} onChange={e => setPackagedDate(e.target.value)} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 12, marginBottom: 12 }}>
            <div>
              <div style={s.fieldLabel}>Net weight/bag (g)</div>
              <input style={s.input} type="number" value={netWeightOverride} onChange={e => setNetWeightOverride(e.target.value)} placeholder={String(bagSizeG)} />
            </div>
            <div>
              <div style={s.fieldLabel}>Shelf life (days)</div>
              <input style={s.input} type="number" value={shelfDaysOverride} onChange={e => setShelfDaysOverride(e.target.value)} placeholder={String(shelfLifeDaysFor(roast.process, policy))} />
            </div>
            <div>
              <div style={s.fieldLabel}>Notes</div>
              <input style={s.input} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" />
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <div style={{ fontFamily: fontSans, fontSize: 12, color: C.inkLight }}>
              Bagging <strong style={{ color: C.ink }}>{requestedKg}kg</strong> · Best before <strong style={{ color: C.ink }}>{dmy(expiryDate)}</strong>
              {(() => {
                const mats = packagingMaterialsFor(bagSizeG, loadPackagingStock());
                const q = parseInt(qtyBags) || 0;
                if (!mats.length) return <span style={{ marginLeft: 10, color: C.inkFaint }}>· no {bagSizeText(bagSizeG)} bags/labels tracked in Packaging materials</span>;
                return <span style={{ marginLeft: 10 }}>· uses {mats.map(m => `${q || "—"} × ${m.name} (${m.quantity} left)`).join(", ")}</span>;
              })()}
              {overCapacity && <span style={{ color: C.red, fontWeight: 700, marginLeft: 10 }}>⚠ More than the {remainingKg}kg left</span>}
            </div>
            <button style={s.btnPrimary} onClick={createBatch} disabled={!qtyBags || parseFloat(qtyBags) <= 0 || overCapacity}>Create batch &amp; generate label</button>
          </div>
        </div>
      )}

      {lastCreated && (
        <div style={{ borderTop: `1px solid ${C.green}`, padding: "12px 16px", background: C.cream3, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontFamily: "monospace", fontSize: 13, color: C.ink }}>
            <span style={{ fontFamily: fontSans, color: C.green, fontWeight: 700 }}>✓ Batch created</span>
            <span>{lastCreated.sku}</span>
            <span>{lastCreated.lot}</span>
            <span>best before {dmy(lastCreated.expiryDate)}</span>
          </div>
          <button style={s.btnPrimary} onClick={() => downloadLabelPNG(lastCreated)}>Download label (PNG)</button>
        </div>
      )}
    </div>
  );
}

// ── FINISHED GOODS ─────────────────────────────────────────────────────────────
function FinishedGoodsTab() {
  const [batches, setBatches] = useState(loadPackagingBatches);
  const [allocations] = useState(loadAllocations);
  const products = computeFinishedGoodsByProduct(batches, allocations);
  const [expanded, setExpanded] = useState(null);

  // Fix for a packing batch filed under the wrong product (e.g. Pinhal packed
  // while its record had the wrong name/process). Gives that batch the other
  // product's coffee name and process, so it joins that product's line and
  // new labels carry the right code. Batch number, lot, SKU, dates and any
  // allocations stay exactly as they were.
  const moveBatchTo = (fromRow, targetCode) => {
    const target = products.find(x => x.code === targetCode);
    if (!target) return;
    const t0 = target.skus[target.skus.length - 1] || target.skus[0];
    const ok = window.confirm(`Move ${fromRow.packaged} bags (${fromRow.sku}) to ${productLabel(t0.origin, target.bagSizeG)}?\n\nThe batch keeps its lot number, SKU, dates and allocations — only the coffee name and process change.`);
    if (!ok) return;
    const updated = batches.map(b => b.sku === fromRow.sku ? { ...b, origin: t0.origin, process: t0.process } : b);
    setBatches(updated);
    savePackagingBatches(updated);
  };

  return (
    <div style={{ paddingTop: 24 }}>
      <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 16, lineHeight: 1.5 }}>
        Bagged stock by product — packaged minus what's allocated to customers. Click a product to see the packing batches behind it.
      </div>
      {products.length === 0 ? (
        <div style={{ fontFamily: fontSans, fontSize: 13, color: C.inkFaint }}>No finished goods yet — create a packaging batch first.</div>
      ) : (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
            <thead>
              <tr style={{ background: C.cream3 }}>
                {["Product", "Code", "Packaged", "Allocated", "Available", "Earliest best-before", ""].map(h => <th key={h} style={s.thStyle}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {products.map(p => {
                const soon = p.earliestExpiry && (new Date(p.earliestExpiry) - new Date()) / 86400000 < 60;
                const isOpen = expanded === p.code;
                return (
                  <Fragment key={p.code}>
                    <tr style={{ borderBottom: `1px solid ${C.borderSoft}`, cursor: "pointer" }} onClick={() => setExpanded(isOpen ? null : p.code)}>
                      <td style={{ ...s.td, fontWeight: 600 }}>{productLabel(p.origin, p.bagSizeG)}</td>
                      <td style={{ ...s.td, fontFamily: "monospace", color: C.amber }}>{p.code}</td>
                      <td style={s.td}>{p.packaged}</td>
                      <td style={s.td}>{p.allocated}</td>
                      <td style={{ ...s.td, fontWeight: 700, color: p.available > 0 ? C.green : C.inkFaint }}>{p.available}</td>
                      <td style={{ ...s.td, color: soon ? C.red : C.inkLight }}>{dmy(p.earliestExpiry) || "—"}{soon ? " ⚠" : ""}</td>
                      <td style={{ ...s.td, color: C.inkFaint, fontSize: 11 }}>{p.skus.length} batch{p.skus.length === 1 ? "" : "es"} {isOpen ? "▲" : "▼"}</td>
                    </tr>
                    {isOpen && p.skus.map(r => (
                      <tr key={r.sku} style={{ background: C.cream2, borderBottom: `1px solid ${C.borderSoft}` }}>
                        <td style={{ ...s.td, paddingLeft: 28, fontSize: 12, color: C.inkLight }}>{r.lots.map(l => l.lot).join(", ")}</td>
                        <td style={{ ...s.td, fontFamily: "monospace", fontSize: 12, color: C.amber }}>{r.sku}</td>
                        <td style={{ ...s.td, fontSize: 12 }}>{r.packaged}</td>
                        <td style={{ ...s.td, fontSize: 12 }}>{r.allocated}</td>
                        <td style={{ ...s.td, fontSize: 12, fontWeight: 600 }}>{r.available}</td>
                        <td style={{ ...s.td, fontSize: 12, color: C.inkLight }}>{dmy(r.earliestExpiry) || "—"}</td>
                        <td style={s.td} onClick={e => e.stopPropagation()}>
                          <select style={{ ...s.input, fontSize: 11, padding: "3px 6px", maxWidth: 150 }} value="" onChange={e => { if (e.target.value) moveBatchTo(r, e.target.value); }} title="Filed under the wrong product? Move this batch">
                            <option value="">Move to…</option>
                            {products.filter(x => x.code !== p.code && x.bagSizeG === p.bagSizeG).map(x => <option key={x.code} value={x.code}>{x.code} — {(x.skus[x.skus.length - 1]?.origin) || x.origin}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── CUSTOMER ALLOCATION ────────────────────────────────────────────────────────
// Wholesale price for one bag of a coffee, the way Pricing works it out — but
// with that coffee's own green cost from Green stock (falling back to the
// blended cost). Null until Pricing has been opened and set up once.
function wholesalePriceFor(origin, bagSizeG) {
  if (!bagSizeG) return null;
  // A Skelligs product's W/S price comes first — it's the agreed trade price,
  // and doesn't need Pricing to have been set up.
  const prod = productFor(origin, bagSizeG);
  if (prod && parseFloat(prod.wsPrice) > 0) return Math.round(parseFloat(prod.wsPrice) * 100) / 100;
  let w = null;
  try { w = JSON.parse(localStorage.getItem("fdc_pricing_wholesale") || "null"); } catch { w = null; }
  if (!w) return null;
  let inv = [];
  try { inv = JSON.parse(localStorage.getItem("fdc_inventory") || "[]"); } catch { inv = []; }
  const item = inv.find(i => coffeeNamesMatch(i.name || i.origin || "", origin || ""));
  // Then your prices: this coffee's own price, then your price for the bag size.
  const own = item && w.coffeeWholesale && w.coffeeWholesale[item.id] ? parseFloat(w.coffeeWholesale[item.id][bagSizeG]) : NaN;
  if (own > 0) return Math.round(own * 100) / 100;
  if (w.ourWholesale && parseFloat(w.ourWholesale[bagSizeG]) > 0) return Math.round(parseFloat(w.ourWholesale[bagSizeG]) * 100) / 100;
  const green = item && parseFloat(item.costPerKg) > 0 ? parseFloat(item.costPerKg) : w.blendedGreenCost;
  if (!(green > 0)) return null;
  const pkg = (w.pkg && w.pkg[bagSizeG] != null) ? w.pkg[bagSizeG] : (bagSizeG >= 1000 ? 1.10 : bagSizeG >= 500 ? 0.75 : 0.55);
  const cogs = (green * (w.lossAdj || 1) + (w.nonGreenPerKg || 0)) * (bagSizeG / 1000) + pkg;
  const m = (w.wholesaleMargin || 0) / 100;
  return Math.round((m > 0 && m < 1 ? cogs / (1 - m) : cogs) * 100) / 100;
}

// Allocations made together share an orderRef; older single records are grouped by customer + date.
function allocGroupKey(a) { return a.orderRef != null ? "o" + a.orderRef : "l" + a.customerId + ":" + a.date; }

function AllocationTab() {
  const [customers, setCustomers] = useState(loadCustomers);
  const [batches] = useState(loadPackagingBatches);
  const [allocations, setAllocations] = useState(loadAllocations);
  const fg = computeFinishedGoods(batches, allocations);

  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: "", contact: "" });
  const addCustomer = () => {
    if (!newCustomer.name.trim()) return;
    const c = { id: Date.now(), ...newCustomer };
    const updated = [...customers, c];
    setCustomers(updated); saveCustomers(updated);
    setNewCustomer({ name: "", contact: "" });
    setShowAddCustomer(false);
  };

  const [editingCustomerId, setEditingCustomerId] = useState(null);
  const [customerDraft, setCustomerDraft] = useState({ name: "", contact: "" });
  const startEditCustomer = (c) => { setEditingCustomerId(c.id); setCustomerDraft({ name: c.name, contact: c.contact || "" }); };
  const cancelEditCustomer = () => setEditingCustomerId(null);
  const saveEditCustomer = (id) => {
    const updated = customers.map(c => c.id === id ? { ...c, name: customerDraft.name, contact: customerDraft.contact } : c);
    setCustomers(updated); saveCustomers(updated);
    setEditingCustomerId(null);
  };
  const removeCustomer = (id) => {
    if (!window.confirm("Remove this customer? Past allocations already made to them keep showing their name, but they won't be selectable for new ones.")) return;
    const updated = customers.filter(c => c.id !== id);
    setCustomers(updated); saveCustomers(updated);
  };

  const [editingAllocId, setEditingAllocId] = useState(null);
  const [allocDraft, setAllocDraft] = useState({ qty: "", customerId: "", notes: "" });
  const startEditAlloc = (a) => { setEditingAllocId(a.id); setAllocDraft({ qty: a.qty, customerId: a.customerId, notes: a.notes || "" }); };
  const cancelEditAlloc = () => setEditingAllocId(null);
  const saveEditAlloc = (id) => {
    const customer = customers.find(c => String(c.id) === String(allocDraft.customerId));
    const updated = allocations.map(a => a.id === id ? { ...a, qty: parseInt(allocDraft.qty) || a.qty, customerId: allocDraft.customerId, customerName: customer?.name || a.customerName, notes: allocDraft.notes } : a);
    setAllocations(updated); saveAllocations(updated);
    setEditingAllocId(null);
  };
  const removeAlloc = (id) => {
    if (!window.confirm("Remove this allocation? The stock will show as available again in Finished Goods.")) return;
    const updated = allocations.filter(a => a.id !== id);
    setAllocations(updated); saveAllocations(updated);
  };

  // Allocate by product ("250g Halo Beriti"), not by packing batch. The
  // Hub draws from the batch with the earliest best-before first, and saves
  // one allocation record per batch used — the same record shape as before,
  // so recall trace, dispatch and documents keep working unchanged.
  const products = computeFinishedGoodsByProduct(batches, allocations);
  const [customerId, setCustomerId] = useState("");
  const [notes, setNotes] = useState("");
  const [newCustInline, setNewCustInline] = useState(null); // null = closed, string = name being typed
  const newLine = () => ({ key: Math.random().toString(36).slice(2), code: "", qty: "" });
  const [draftLines, setDraftLines] = useState([]); // the order so far: one line per coffee
  const removeLine = (key) => setDraftLines(ls => ls.filter(l => l.key !== key));
  const addToBasket = (code, q) => setDraftLines(ls => {
    const i = ls.findIndex(l => l.code === code);
    if (i >= 0) return ls.map((l, j) => j === i ? { ...l, qty: String((parseInt(l.qty) || 0) + q) } : l);
    return [...ls, { ...newLine(), code, qty: String(q) }];
  });
  const [entryCode, setEntryCode] = useState("");
  const [entryQty, setEntryQty] = useState("");
  const lineInfo = draftLines.map(l => {
    const p = products.find(x => x.code === l.code);
    const q = parseInt(l.qty) || 0;
    return { ...l, p, q, over: !!(p && q > p.available), dup: !!(l.code && draftLines.filter(x => x.code === l.code).length > 1) };
  });
  const filledLines = lineInfo.filter(l => l.code && l.q > 0);
  const totalBags = filledLines.reduce((n, l) => n + l.q, 0);
  const entryProduct = products.find(p => p.code === entryCode);
  const entryLeft = entryProduct ? entryProduct.available - lineInfo.filter(l => l.code === entryCode).reduce((n, l) => n + l.q, 0) : 0;
  const entryQ = parseInt(entryQty) || 0;
  const entryOver = !!(entryProduct && entryQ > entryLeft);
  const addToOrder = () => {
    if (!entryProduct || entryQ <= 0 || entryOver) return;
    addToBasket(entryCode, entryQ);
    setEntryCode(""); setEntryQty("");
  };
  const orderValid = !!customerId && filledLines.length > 0 && lineInfo.every(l => (!l.code && !l.qty) || (l.code && l.q > 0 && !l.over && !l.dup));

  // Orders waiting to be allocated — picking one fills in the customer, the
  // coffee and the number of bags (from the order's kg), and the order is
  // marked fulfilled once it's allocated.
  const [planOrders, setPlanOrders] = useState(loadOrders);
  const [orderSel, setOrderSel] = useState("");
  const openOrders = planOrders.filter(o => o.status === "roasted" || o.status === "queued" || o.status === "pending");
  const selectedOrder = openOrders.find(o => String(o.id) === String(orderSel));
  const bagsForOrder = (o, p) => (o && p && parseFloat(o.qtyKg) > 0) ? String(Math.ceil(parseFloat(o.qtyKg) * 1000 / p.bagSizeG)) : "";
  const pickOrder = (id) => {
    setOrderSel(id);
    const o = openOrders.find(x => String(x.id) === String(id));
    if (!o) return;
    let cust = customers.find(c => (c.name || "").trim().toLowerCase() === (o.customerName || "").trim().toLowerCase());
    if (!cust && (o.customerName || "").trim()) {
      cust = { id: Date.now(), name: o.customerName.trim(), contact: "" };
      const upC = [...customers, cust];
      setCustomers(upC); saveCustomers(upC);
    }
    if (cust) setCustomerId(String(cust.id));
    const match = products.filter(p => p.available > 0 && coffeeNamesMatch(p.origin || "", o.origin || ""))[0];
    if (match) { const q = parseInt(bagsForOrder(o, match)) || 0; if (q > 0) addToBasket(match.code, q); }
  };

  const nextOrderNo = () => {
    const nums = allocations.map(a => /^A-(\d+)$/.exec(a.orderNo || "")).filter(Boolean).map(m => parseInt(m[1]));
    return "A-" + String((nums.length ? Math.max(...nums) : 0) + 1).padStart(3, "0");
  };

  // One order = one customer + any number of coffees. Each coffee still draws
  // from the batch with the earliest best-before first and saves one record
  // per batch used (same record shape as before, so Finished Goods, dispatch
  // and recall trace keep working), all tagged with the same orderRef.
  const [openGroups, setOpenGroups] = useState({});
  const createOrder = () => {
    if (!orderValid) return;
    const customer = customers.find(c => String(c.id) === String(customerId));
    const date = localIsoDate();
    const baseId = Date.now();
    const orderNo = nextOrderNo();
    const records = [];
    let n = 0;
    filledLines.forEach(l => {
      let remaining = l.q;
      l.p.skus.filter(r => r.available > 0).forEach(r => {
        if (remaining <= 0) return;
        const take = Math.min(remaining, r.available);
        records.push({ id: baseId + n++, sku: r.sku, productCode: l.p.code, qty: take, customerId, customerName: customer?.name || "", date, notes, status: "allocated", orderRef: baseId, orderNo, ...(selectedOrder ? { orderId: selectedOrder.id } : {}) });
        remaining -= take;
      });
    });
    if (records.length === 0) return;
    const updated = [...records, ...allocations];
    setAllocations(updated); saveAllocations(updated);
    if (selectedOrder) {
      const upO = loadOrders().map(o => String(o.id) === String(selectedOrder.id) ? { ...o, status: "fulfilled" } : o);
      saveOrders(upO); setPlanOrders(upO); setOrderSel("");
    }
    setDraftLines([]); setNotes("");
  };

  const availableProducts = products.filter(p => p.available > 0);

  // Orders as shown in the list: records made together, or older records of the same customer on the same day.
  const orderGroups = (() => {
    const m = new Map();
    allocations.forEach(a => {
      const k = allocGroupKey(a);
      if (!m.has(k)) m.set(k, { key: k, lines: [], customerId: a.customerId, customerName: a.customerName, date: a.date, orderNo: a.orderNo || "", notes: "" });
      const g = m.get(k);
      g.lines.push(a);
      if (!g.notes && a.notes) g.notes = a.notes;
    });
    return [...m.values()];
  })();
  const [editingGroupKey, setEditingGroupKey] = useState(null);
  const [groupDraft, setGroupDraft] = useState({ customerId: "", notes: "" });
  const startEditGroup = (g) => { setEditingGroupKey(g.key); setGroupDraft({ customerId: g.customerId, notes: g.notes }); };
  const saveEditGroup = (key) => {
    const customer = customers.find(c => String(c.id) === String(groupDraft.customerId));
    const updated = allocations.map(a => allocGroupKey(a) === key ? { ...a, customerId: groupDraft.customerId, customerName: customer?.name || a.customerName, notes: groupDraft.notes } : a);
    setAllocations(updated); saveAllocations(updated);
    setEditingGroupKey(null);
  };
  // Dispatch a whole order at once: carrier, tracking and date go onto every line of it.
  const [dispatchGroupKey, setDispatchGroupKey] = useState(null);
  const [dispatchForm, setDispatchForm] = useState({ carrier: "", trackingNumber: "", dispatchDate: "" });
  const startGroupDispatch = (g) => {
    const d = g.lines.find(a => a.dispatch?.carrier)?.dispatch;
    setDispatchGroupKey(g.key);
    setDispatchForm({ carrier: d?.carrier || "", trackingNumber: d?.trackingNumber || "", dispatchDate: d?.dispatchDate || localIsoDate() });
  };
  const saveGroupDispatch = (key) => {
    const updated = allocations.map(a => allocGroupKey(a) === key ? { ...a, dispatch: { ...dispatchForm, delivered: a.dispatch?.delivered || false } } : a);
    setAllocations(updated); saveAllocations(updated);
    setDispatchGroupKey(null);
  };
  const toggleGroupDelivered = (g) => {
    const allDelivered = g.lines.every(a => a.dispatch?.delivered);
    const updated = allocations.map(a => allocGroupKey(a) === g.key && a.dispatch?.carrier ? { ...a, dispatch: { ...a.dispatch, delivered: !allDelivered } } : a);
    setAllocations(updated); saveAllocations(updated);
  };
  const removeGroup = (g) => {
    if (!window.confirm(`Remove this whole order (${g.lines.length} line${g.lines.length === 1 ? "" : "s"}, ${g.lines.reduce((n, a) => n + (a.qty || 0), 0)} bags)? The stock will show as available again in Finished Goods.`)) return;
    const updated = allocations.filter(a => allocGroupKey(a) !== g.key);
    setAllocations(updated); saveAllocations(updated);
  };
const [invoiceSettings, setInvoiceSettings] = useState(loadInvoiceSettings);
  const [showInvoiceSettings, setShowInvoiceSettings] = useState(false);
  const [contractPrices, setContractPrices] = useState(loadContractPrices);
  const [docAllocId, setDocAllocId] = useState(null);
  const [docType, setDocType] = useState("packing");
  const [docPrice, setDocPrice] = useState("");

  const saveInvoiceSettingsForm = () => { saveInvoiceSettings(invoiceSettings); setShowInvoiceSettings(false); };

  const priceKeyFor = (a) => {
    const fgRow = fg.find(r => r.sku === a.sku);
    const code = fgRow ? productCode({ origin: fgRow.origin, process: fgRow.process, bagSizeG: fgRow.bagSizeG }) : a.sku;
    return `${a.customerId}:${code}`;
  };
  // Your price list (Green → Pricing) is the default for every customer. A
  // price is only remembered as a customer's own agreed price when it's been
  // typed in differently from the list — so raising your list prices reaches
  // everyone who hasn't got a special deal.
  const listPriceFor = (a) => {
    const fgRow = fg.find(r => r.sku === a.sku);
    return fgRow ? wholesalePriceFor(fgRow.origin, fgRow.bagSizeG) : null;
  };
  const rememberPrice = (prices, a, value) => {
    const key = priceKeyFor(a);
    const v = parseFloat(value);
    if (!(v > 0)) return prices;
    const list = listPriceFor(a);
    const next = { ...prices };
    if (list != null && Math.abs(v - list) < 0.005) delete next[key]; // same as the list — no special price
    else next[key] = v;
    return next;
  };
  const priceSourceNote = (a, value) => {
    const key = priceKeyFor(a);
    const list = listPriceFor(a);
    const v = parseFloat(value);
    const agreed = contractPrices[key];
    if (list != null && v > 0 && Math.abs(v - list) < 0.005) return { text: "from your price list", list };
    if (agreed != null && v > 0 && Math.abs(v - agreed) < 0.005) return { text: "this customer's agreed price", list, agreed: true };
    if (v > 0) return { text: "will be kept as this customer's agreed price", list };
    return { text: list != null ? "" : "set your wholesale prices in Green → Pricing", list };
  };

  const openDocPanel = (a) => {
    setDocAllocId(a.id);
    setDocType("packing");
    const key = priceKeyFor(a);
    const fgRow = fg.find(r => r.sku === a.sku);
    const ws = fgRow ? wholesalePriceFor(fgRow.origin, fgRow.bagSizeG) : null;
    setDocPrice(contractPrices[key] != null ? String(contractPrices[key]) : ws != null ? String(ws) : "");
  };
  const closeDocPanel = () => setDocAllocId(null);

  const generateDoc = (a) => {
    const fgRow = fg.find(r => r.sku === a.sku);
    if (docType !== "packing" && docPrice) {
      const updatedPrices = rememberPrice(contractPrices, a, docPrice);
      setContractPrices(updatedPrices);
      saveContractPrices(updatedPrices);
    }
    const latestSettings = loadInvoiceSettings();
    printAllocationDocument(a, fgRow, docType, docPrice, latestSettings);
    setInvoiceSettings(loadInvoiceSettings());
    const updatedAllocs = allocations.map(x => x.id === a.id ? { ...x, documented: true } : x);
    setAllocations(updatedAllocs); saveAllocations(updatedAllocs);
    setDocAllocId(null);
  };

  // ── Whole-order documents: pick a customer, document everything of theirs
  // that hasn't been documented yet — nothing gets billed twice. ──
  const [orderCustomerId, setOrderCustomerId] = useState("");
  const [orderDocType, setOrderDocType] = useState("packing");
  const [orderPrices, setOrderPrices] = useState({});

  const undocumentedForCustomer = (custId) => allocations.filter(a => String(a.customerId) === String(custId) && !a.documented);
  const [docGroupKey, setDocGroupKey] = useState(null); // set when "Document" is pressed on one order
  const docBoxRef = useRef(null);
  const orderLines = docGroupKey
    ? allocations.filter(a => allocGroupKey(a) === docGroupKey && !a.documented)
    : (orderCustomerId ? undocumentedForCustomer(orderCustomerId) : []);
  const docGroup = docGroupKey ? orderGroups.find(g => g.key === docGroupKey) : null;
  const openGroupDoc = (g) => {
    setDocGroupKey(g.key); setOrderCustomerId(String(g.customerId)); setOrderPrices({}); setOrderDocType("packing");
    setTimeout(() => { try { docBoxRef.current && docBoxRef.current.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (e) {} }, 50);
  };

  const orderPriceFor = (a) => {
    if (orderPrices[a.id] !== undefined) return orderPrices[a.id];
    const key = priceKeyFor(a);
    if (contractPrices[key] != null) return String(contractPrices[key]);
    const fgRow = fg.find(r => r.sku === a.sku);
    const ws = fgRow ? wholesalePriceFor(fgRow.origin, fgRow.bagSizeG) : null;
    return ws != null ? String(ws) : "";
  };
  const setOrderPriceFor = (id, val) => setOrderPrices(p => ({ ...p, [id]: val }));

  const generateOrderDoc = () => {
    if (!orderCustomerId || orderLines.length === 0) return;
    const lines = orderLines.map(a => ({ alloc: a, fgRow: fg.find(r => r.sku === a.sku), unitPrice: orderPriceFor(a) }));
    if (orderDocType !== "packing") {
      let updatedPrices = { ...contractPrices };
      lines.forEach(({ alloc, unitPrice }) => { if (unitPrice) updatedPrices = rememberPrice(updatedPrices, alloc, unitPrice); });
      setContractPrices(updatedPrices);
      saveContractPrices(updatedPrices);
    }
    const latestSettings = loadInvoiceSettings();
    printAllocationDocumentMulti(lines, orderDocType, latestSettings);
    setInvoiceSettings(loadInvoiceSettings());
    const includedIds = new Set(orderLines.map(a => a.id));
    const updatedAllocs = allocations.map(a => includedIds.has(a.id) ? { ...a, documented: true } : a);
    setAllocations(updatedAllocs); saveAllocations(updatedAllocs);
    setOrderPrices({});
    setOrderDocType("packing");
    setDocGroupKey(null);
  };

  return (
    <div style={{ paddingTop: 24 }}>
      <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 16, lineHeight: 1.5 }}>
        Commit finished goods to a customer. This reduces what shows as available in Finished Goods — dispatch/shipping tracking isn't built yet, so this is the record of what's been promised so far.
      </div>

      <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 20, overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", cursor: "pointer", background: C.cream2 }} onClick={() => setShowInvoiceSettings(v => !v)}>
          <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em" }}>Business details {showInvoiceSettings ? "▾" : "▸"}</div>
          <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint }}>Used on every generated document</div>
        </div>
        {showInvoiceSettings && (
          <div style={{ padding: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
              <div>
                <div style={s.fieldLabel}>Business name</div>
                <input style={s.input} value={invoiceSettings.businessName} onChange={e => setInvoiceSettings(v => ({ ...v, businessName: e.target.value }))} placeholder="Skelligs Coffee" />
              </div>
              <div>
                <div style={s.fieldLabel}>VAT number</div>
                <input style={s.input} value={invoiceSettings.vatNumber} onChange={e => setInvoiceSettings(v => ({ ...v, vatNumber: e.target.value }))} placeholder="IE1234567A" />
              </div>
              <div>
                <div style={s.fieldLabel}>VAT rate (%)</div>
                <input style={s.input} type="number" step="0.1" value={invoiceSettings.vatRatePct} onChange={e => setInvoiceSettings(v => ({ ...v, vatRatePct: e.target.value }))} placeholder="e.g. 13.5" />
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={s.fieldLabel}>Business address</div>
              <input style={s.input} value={invoiceSettings.businessAddress} onChange={e => setInvoiceSettings(v => ({ ...v, businessAddress: e.target.value }))} placeholder="Malahide, Co. Dublin, Ireland" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div>
                <div style={s.fieldLabel}>Invoice number prefix</div>
                <input style={s.input} value={invoiceSettings.invoicePrefix} onChange={e => setInvoiceSettings(v => ({ ...v, invoicePrefix: e.target.value }))} />
              </div>
              <div>
                <div style={s.fieldLabel}>Next invoice number</div>
                <input style={s.input} type="number" value={invoiceSettings.nextInvoiceNumber} onChange={e => setInvoiceSettings(v => ({ ...v, nextInvoiceNumber: parseInt(e.target.value) || 1 }))} />
              </div>
            </div>
            <div style={{ fontFamily: fontSans, fontSize: 10.5, color: C.inkFaint, marginBottom: 12 }}>
              Confirm your VAT rate and that this document format meets your obligations with your accountant before relying on the invoice option for your accounts.
            </div>
            <button style={s.btnPrimary} onClick={saveInvoiceSettingsForm}>Save</button>
          </div>
        )}
      </div>

      {/* Customers */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={s.fieldLabel}>Customers</div>
          <button style={{ ...s.btn, fontSize: 12 }} onClick={() => setShowAddCustomer(v => !v)}>{showAddCustomer ? "Cancel" : "+ Add customer"}</button>
        </div>
        {showAddCustomer && (
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <input style={{ ...s.input, flex: 1 }} placeholder="Customer name" value={newCustomer.name} onChange={e => setNewCustomer(c => ({ ...c, name: e.target.value }))} />
            <input style={{ ...s.input, flex: 1 }} placeholder="Contact (optional)" value={newCustomer.contact} onChange={e => setNewCustomer(c => ({ ...c, contact: e.target.value }))} />
            <button style={s.btnPrimary} onClick={addCustomer}>Add</button>
          </div>
        )}
        {customers.length === 0 ? (
          <div style={{ fontFamily: fontSans, fontSize: 12, color: C.inkFaint }}>No customers yet.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {customers.map(c => (
              editingCustomerId === c.id ? (
                <div key={c.id} style={{ display: "flex", gap: 8, alignItems: "center", background: C.cream2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 10px" }}>
                  <input style={{ ...s.input, flex: 1 }} value={customerDraft.name} onChange={e => setCustomerDraft(d => ({ ...d, name: e.target.value }))} placeholder="Customer name" />
                  <input style={{ ...s.input, flex: 1 }} value={customerDraft.contact} onChange={e => setCustomerDraft(d => ({ ...d, contact: e.target.value }))} placeholder="Contact (optional)" />
                  <button style={s.btnPrimary} onClick={() => saveEditCustomer(c.id)}>Save</button>
                  <button style={s.btn} onClick={cancelEditCustomer}>Cancel</button>
                </div>
              ) : (
                <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, background: C.cream2, border: `1px solid ${C.border}`, borderRadius: 20, padding: "4px 6px 4px 12px" }}>
                  <span style={{ fontFamily: fontSans, fontSize: 11, color: C.inkMid, flex: 1 }}>{c.name}{c.contact ? ` — ${c.contact}` : ""}</span>
                  <button onClick={() => startEditCustomer(c)} style={{ background: "none", border: "none", cursor: "pointer", color: C.inkFaint, fontSize: 11, fontFamily: fontSans }}>Edit</button>
                  <button onClick={() => removeCustomer(c.id)} style={{ background: "none", border: "none", cursor: "pointer", color: C.inkFaint, fontSize: 16, lineHeight: 1 }}>×</button>
                </div>
              )
            ))}
          </div>
        )}
      </div>

      {/* New order */}
      <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: "16px 18px", marginBottom: 20, background: C.cream2 }}>
        <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>New order</div>
        {openOrders.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={s.fieldLabel}>From a planned order (optional)</div>
            <select style={s.input} value={orderSel} onChange={e => e.target.value ? pickOrder(e.target.value) : setOrderSel("")}>
              <option value="">— Not from a planned order —</option>
              {openOrders.map(o => <option key={o.id} value={o.id}>{o.customerName || "—"} · {o.origin} · {o.qtyKg}kg{o.dueDate ? ` · due ${dmy(o.dueDate)}` : ""} · {o.status}</option>)}
            </select>
            {selectedOrder && (
              <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginTop: 6, lineHeight: 1.5 }}>
                Customer and coffee filled in; bags worked out from {selectedOrder.qtyKg}kg. The planned order is marked fulfilled when you allocate.
                {draftLines.length === 0 && " No bagged stock of this coffee yet — pack it first."}
              </div>
            )}
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
          <div>
            <div style={s.fieldLabel}>Customer</div>
            <select style={s.input} value={newCustInline !== null ? "__new" : customerId} onChange={e => { if (e.target.value === "__new") { setNewCustInline(""); } else { setNewCustInline(null); setCustomerId(e.target.value); } }}>
              <option value="">— Select —</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              <option value="__new">+ New customer…</option>
            </select>
            {newCustInline !== null && (
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <input style={{ ...s.input, flex: 1 }} autoFocus placeholder="Customer name" value={newCustInline} onChange={e => setNewCustInline(e.target.value)} />
                <button style={s.btnPrimary} disabled={!newCustInline.trim()} onClick={() => {
                  const c = { id: Date.now(), name: newCustInline.trim(), contact: "" };
                  const upC = [...customers, c];
                  setCustomers(upC); saveCustomers(upC);
                  setCustomerId(String(c.id)); setNewCustInline(null);
                }}>Add</button>
                <button style={s.btn} onClick={() => setNewCustInline(null)}>Cancel</button>
              </div>
            )}
          </div>
          <div>
            <div style={s.fieldLabel}>Order notes (optional)</div>
            <input style={s.input} placeholder="e.g. deliver Thursday, PO 4471" value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
        </div>
        <div style={s.fieldLabel}>Add a coffee</div>
        <div style={{ display: "grid", gridTemplateColumns: "3fr 90px 170px auto", gap: 10, alignItems: "center", marginBottom: 12 }}>
          <select style={s.input} value={entryCode} onChange={e => setEntryCode(e.target.value)}>
            <option value="">— Select coffee —</option>
            {availableProducts.map(p => <option key={p.code} value={p.code}>{productLabel(p.origin, p.bagSizeG)} — {p.available} available</option>)}
          </select>
          <input style={s.input} type="number" min="1" placeholder="Bags" value={entryQty} onChange={e => setEntryQty(e.target.value)} onKeyDown={e => { if (e.key === "Enter") addToOrder(); }} />
          <div style={{ fontFamily: fontSans, fontSize: 11, color: entryOver ? C.red : C.inkFaint, fontWeight: entryOver ? 600 : 400 }}>
            {entryOver ? `Only ${entryLeft} left to add` : entryProduct ? `${entryLeft} available` : ""}
          </div>
          <button style={s.btn} onClick={addToOrder} disabled={!entryProduct || entryQ <= 0 || entryOver}>Add to order</button>
        </div>
        {lineInfo.length > 0 && (
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, background: C.cream, marginBottom: 4 }}>
            {lineInfo.map(l => (
              <div key={l.key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 12px", borderBottom: `1px solid ${C.borderSoft}` }}>
                <div style={{ fontFamily: fontSans, fontSize: 12.5, color: C.ink, flex: 1 }}>{l.p ? productLabel(l.p.origin, l.p.bagSizeG) : l.code}</div>
                <div style={{ fontFamily: fontSans, fontSize: 12.5, color: C.inkMid }}>{l.q} bags</div>
                <button onClick={() => removeLine(l.key)} title="Take off this order" style={{ background: "none", border: "none", cursor: "pointer", color: C.inkFaint, fontSize: 18, lineHeight: 1 }}>×</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 14, paddingTop: 12, borderTop: `1px dashed ${C.border}` }}>
          <div style={{ fontFamily: fontSans, fontSize: 12, color: C.inkMid }}>{filledLines.length} coffee{filledLines.length === 1 ? "" : "s"} · {totalBags} bags</div>
          <button style={s.btnPrimary} onClick={createOrder} disabled={!orderValid}>Allocate order</button>
        </div>
        <div style={{ fontFamily: fontSans, fontSize: 10.5, color: C.inkFaint, marginTop: 8 }}>Bags come from the oldest best-before batch first. A coffee that spans two batches stays one line here.</div>
      </div>

      {/* Generate a combined document for a customer */}
      {customers.length > 0 && (
        <div ref={docBoxRef} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: "16px 18px", marginBottom: 20, background: C.cream2 }}>
          <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>Generate order document</div>
          {docGroup && (
            <div style={{ fontFamily: fontSans, fontSize: 11.5, color: C.inkMid, marginBottom: 10 }}>
              For order {docGroup.orderNo || dmy(docGroup.date)} only. <button type="button" onClick={() => setDocGroupKey(null)} style={{ background: "none", border: "none", padding: 0, color: C.amber, cursor: "pointer", fontSize: 11.5, fontFamily: fontSans }}>Show everything undocumented for this customer</button>
            </div>
          )}
          <div style={{ marginBottom: 12, maxWidth: 320 }}>
            <div style={s.fieldLabel}>Customer</div>
            <select style={s.input} value={orderCustomerId} onChange={e => { setOrderCustomerId(e.target.value); setOrderPrices({}); setDocGroupKey(null); }}>
              <option value="">— Select —</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {orderCustomerId && (
            orderLines.length === 0 ? (
              <div style={{ fontFamily: fontSans, fontSize: 12, color: C.inkFaint }}>Nothing undocumented for this customer — everything already has a document.</div>
            ) : (
              <>
                <div style={{ fontFamily: fontSans, fontSize: 11.5, color: C.inkMid, marginBottom: 10 }}>{orderLines.length} undocumented allocation{orderLines.length === 1 ? "" : "s"} for this customer, going into one document:</div>
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  {[["packing", "Packing slip"], ["priced", "Priced document"], ["invoice", "VAT invoice"]].map(([val, label]) => (
                    <button key={val} onClick={() => setOrderDocType(val)} style={{ ...s.btnSm, background: orderDocType === val ? C.amber : C.cream3, color: orderDocType === val ? "#fff" : C.inkMid, borderColor: orderDocType === val ? C.amber : C.border }}>{label}</button>
                  ))}
                </div>
                {orderDocType !== "packing" && (
                  <div style={{ marginBottom: 12 }}>
                    {orderLines.map(a => (
                      <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "5px 0" }}>
                        <div style={{ fontFamily: "monospace", fontSize: 12, color: C.amber, width: 130 }}>{a.sku}</div>
                        <div style={{ fontFamily: fontSans, fontSize: 12, color: C.inkMid, flex: 1 }}>{a.qty} bags — {dmy(a.date)}</div>
                        <input style={{ ...s.input, width: 110 }} type="number" step="0.01" placeholder="Price per bag (€)" value={orderPriceFor(a)} onChange={e => setOrderPriceFor(a.id, e.target.value)} />
                        {(() => {
                          const note = priceSourceNote(a, orderPriceFor(a));
                          return (
                            <div style={{ fontFamily: fontSans, fontSize: 10.5, color: C.inkFaint, width: 190, lineHeight: 1.35 }}>
                              {note.text}
                              {note.agreed && note.list != null && <> · <button type="button" onClick={() => setOrderPriceFor(a.id, String(note.list))} style={{ background: "none", border: "none", padding: 0, color: C.amber, cursor: "pointer", fontSize: 10.5, textDecoration: "underline" }}>use price list €{note.list.toFixed(2)}</button></>}
                            </div>
                          );
                        })()}
                      </div>
                    ))}
                  </div>
                )}
                <button style={s.btnPrimary} onClick={generateOrderDoc}>Generate &amp; print / download ({orderLines.length} item{orderLines.length === 1 ? "" : "s"})</button>
              </>
            )
          )}
        </div>
      )}

      {/* Orders */}
      {orderGroups.length > 0 && (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
          {orderGroups.map(g => {
            const bags = g.lines.reduce((n, a) => n + (a.qty || 0), 0);
            const coffees = new Set(g.lines.map(a => a.productCode || a.sku)).size;
            const docCount = g.lines.filter(a => a.documented).length;
            const sentCount = g.lines.filter(a => a.dispatch?.carrier).length;
            const deliveredCount = g.lines.filter(a => a.dispatch?.delivered).length;
            const open = !!openGroups[g.key];
            const pill = (txt, col) => <span style={{ fontFamily: fontSans, fontSize: 11, fontWeight: 600, color: col, border: `1px solid ${col}`, borderRadius: 99, padding: "1px 8px", marginRight: 6, whiteSpace: "nowrap" }}>{txt}</span>;
            return (
              <div key={g.key} style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 14px", flexWrap: "wrap" }}>
                  <button onClick={() => setOpenGroups(o => ({ ...o, [g.key]: !o[g.key] }))} style={{ background: "none", border: "none", cursor: "pointer", color: C.inkFaint, fontSize: 13, padding: 0, width: 16 }}>{open ? "▾" : "▸"}</button>
                  <div style={{ width: 84, fontFamily: "monospace", fontSize: 11, color: C.inkFaint, lineHeight: 1.4 }}>{g.orderNo || "—"}<br />{dmy(g.date)}</div>
                  <div style={{ flex: "1 1 160px", minWidth: 140 }}>
                    <div style={{ fontFamily: fontSans, fontWeight: 700, fontSize: 13, color: C.ink }}>{g.customerName}</div>
                    {g.notes && <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkLight }}>{g.notes}</div>}
                  </div>
                  <div style={{ fontFamily: fontSans, fontSize: 12, color: C.inkMid, flex: "1 1 130px" }}>{coffees} coffee{coffees === 1 ? "" : "s"} · {bags} bags</div>
                  <div style={{ flex: "1 1 170px" }}>
                    {docCount === g.lines.length ? pill("Documented", C.green) : docCount > 0 ? pill("Part documented", C.amber) : pill("No document", C.inkFaint)}
                    {deliveredCount === g.lines.length ? pill("Delivered", C.green) : sentCount > 0 ? pill("Dispatched", C.amber) : null}
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button style={s.btnSm} onClick={() => startEditGroup(g)}>Edit</button>
                    <button style={s.btnSm} onClick={() => openGroupDoc(g)}>Document</button>
                    <button style={s.btnSm} onClick={() => startGroupDispatch(g)}>{sentCount > 0 ? "Dispatch details" : "Dispatch"}</button>
                    {sentCount > 0 && <button style={s.btnSm} onClick={() => toggleGroupDelivered(g)}>{deliveredCount === g.lines.length ? "Undo delivered" : "Mark delivered"}</button>}
                    <button style={s.btnSm} onClick={() => removeGroup(g)}>Remove</button>
                  </div>
                </div>
                {dispatchGroupKey === g.key && (
                  <div style={{ padding: "12px 14px", background: C.cream2, borderTop: `1px solid ${C.borderSoft}` }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
                      <div>
                        <div style={s.fieldLabel}>Carrier</div>
                        <input style={s.input} value={dispatchForm.carrier} onChange={e => setDispatchForm(f => ({ ...f, carrier: e.target.value }))} placeholder="e.g. DPD, An Post" />
                      </div>
                      <div>
                        <div style={s.fieldLabel}>Tracking number</div>
                        <input style={s.input} value={dispatchForm.trackingNumber} onChange={e => setDispatchForm(f => ({ ...f, trackingNumber: e.target.value }))} />
                      </div>
                      <div>
                        <div style={s.fieldLabel}>Dispatch date</div>
                        <input style={s.input} type="date" value={dispatchForm.dispatchDate} onChange={e => setDispatchForm(f => ({ ...f, dispatchDate: e.target.value }))} />
                      </div>
                    </div>
                    <div style={{ fontFamily: fontSans, fontSize: 10.5, color: C.inkFaint, marginBottom: 10 }}>Applies to every line of this order.</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button style={s.btnPrimary} onClick={() => saveGroupDispatch(g.key)} disabled={!dispatchForm.carrier.trim()}>Save dispatch</button>
                      <button style={s.btn} onClick={() => setDispatchGroupKey(null)}>Cancel</button>
                    </div>
                  </div>
                )}
                {editingGroupKey === g.key && (
                  <div style={{ padding: "12px 14px", background: C.cream2, borderTop: `1px solid ${C.borderSoft}` }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                      <div>
                        <div style={s.fieldLabel}>Customer</div>
                        <select style={s.input} value={groupDraft.customerId} onChange={e => setGroupDraft(d => ({ ...d, customerId: e.target.value }))}>
                          {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <div style={s.fieldLabel}>Order notes</div>
                        <input style={s.input} value={groupDraft.notes} onChange={e => setGroupDraft(d => ({ ...d, notes: e.target.value }))} />
                      </div>
                    </div>
                    <div style={{ fontFamily: fontSans, fontSize: 10.5, color: C.inkFaint, marginBottom: 10 }}>Changes apply to every line of this order. Open the order to change bags on one line.</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button style={s.btnPrimary} onClick={() => saveEditGroup(g.key)}>Save</button>
                      <button style={s.btn} onClick={() => setEditingGroupKey(null)}>Cancel</button>
                    </div>
                  </div>
                )}
                {open && (
                  <div style={{ background: C.cream2, padding: "4px 14px 10px 48px", borderTop: `1px solid ${C.borderSoft}` }}>
                    {g.lines.map(a => (
                      <Fragment key={a.id}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "7px 0", borderBottom: `1px solid ${C.borderSoft}`, flexWrap: "wrap" }}>
                          <div style={{ fontFamily: "monospace", fontSize: 12, color: C.amber, width: 140 }}>{a.sku}</div>
                          {editingAllocId === a.id ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                              <input style={{ ...s.input, width: 80 }} type="number" min="1" value={allocDraft.qty} onChange={e => setAllocDraft(d => ({ ...d, qty: e.target.value }))} />
                              <span style={{ fontFamily: fontSans, fontSize: 12, color: C.inkMid }}>bags</span>
                              <button style={s.btnPrimary} onClick={() => saveEditAlloc(a.id)}>Save</button>
                              <button style={s.btn} onClick={cancelEditAlloc}>Cancel</button>
                            </div>
                          ) : (
                            <>
                              <div style={{ fontFamily: fontSans, fontSize: 12, color: C.inkMid, flex: 1 }}>{a.qty} bags{a.documented ? <span style={{ color: C.green, fontWeight: 600 }}> · ✓ documented</span> : ""}{a.dispatch?.carrier ? <span style={{ color: a.dispatch.delivered ? C.green : C.amber, fontWeight: 600 }}> · {a.dispatch.delivered ? "delivered" : "dispatched"}</span> : ""}</div>
                              <button style={s.btnSm} onClick={() => startEditAlloc(a)}>Edit bags</button>
                              <button style={s.btnSm} onClick={() => removeAlloc(a.id)}>Remove line</button>
                              <button style={s.btnSm} onClick={() => openDocPanel(a)}>Document this line</button>
                            </>
                          )}
                        </div>
                        {docAllocId === a.id && (
                          <div style={{ padding: "12px 0" }}>
                            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                              {[["packing", "Packing slip"], ["priced", "Priced document"], ["invoice", "VAT invoice"]].map(([val, label]) => (
                                <button key={val} onClick={() => setDocType(val)} style={{ ...s.btnSm, background: docType === val ? C.amber : C.cream3, color: docType === val ? "#fff" : C.inkMid, borderColor: docType === val ? C.amber : C.border }}>{label}</button>
                              ))}
                            </div>
                            {docType !== "packing" && (
                              <div style={{ marginBottom: 10, maxWidth: 200 }}>
                                <div style={s.fieldLabel}>Price per bag (€)</div>
                                <input style={s.input} type="number" step="0.01" value={docPrice} onChange={e => setDocPrice(e.target.value)} placeholder="e.g. 9.50" />
                                {(() => {
                                  const note = priceSourceNote(a, docPrice);
                                  return (
                                    <div style={{ fontFamily: fontSans, fontSize: 10.5, color: C.inkFaint, marginTop: 4, lineHeight: 1.35 }}>
                                      {note.text}
                                      {note.agreed && note.list != null && <> · <button type="button" onClick={() => setDocPrice(String(note.list))} style={{ background: "none", border: "none", padding: 0, color: C.amber, cursor: "pointer", fontSize: 10.5, fontFamily: fontSans }}>use list price €{Number(note.list).toFixed(2)}</button></>}
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                            <div style={{ display: "flex", gap: 8 }}>
                              <button style={s.btnPrimary} onClick={() => generateDoc(a)}>Generate &amp; print / download</button>
                              <button style={s.btn} onClick={closeDocPanel}>Cancel</button>
                            </div>
                          </div>
                        )}
                      </Fragment>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── DISPATCH (future)// ── DISPATCH (future) ─────────────────────────────────────────────────────────
function DispatchTab() {
  const [allocations, setAllocations] = useState(loadAllocations);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ carrier: "", trackingNumber: "", dispatchDate: "" });

  const startDispatch = (a) => {
    setEditing(a.id);
    setForm({ carrier: a.dispatch?.carrier || "", trackingNumber: a.dispatch?.trackingNumber || "", dispatchDate: a.dispatch?.dispatchDate || localIsoDate() });
  };
  const saveDispatch = (id) => {
    const updated = allocations.map(a => a.id === id ? { ...a, dispatch: { ...form, delivered: a.dispatch?.delivered || false } } : a);
    setAllocations(updated); saveAllocations(updated);
    setEditing(null);
  };
  const toggleDelivered = (id) => {
    const updated = allocations.map(a => a.id === id ? { ...a, dispatch: { ...(a.dispatch || {}), delivered: !(a.dispatch?.delivered) } } : a);
    setAllocations(updated); saveAllocations(updated);
  };
  const statusOf = (a) => {
    if (!a.dispatch || !a.dispatch.carrier) return { label: "Not dispatched", color: C.inkFaint };
    if (a.dispatch.delivered) return { label: "Delivered", color: C.green };
    return { label: "Dispatched", color: C.amber };
  };

  return (
    <div style={{ paddingTop: 24 }}>
      <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 16, lineHeight: 1.5 }}>
        Record what's actually gone out for each allocation — carrier, tracking number, and delivery confirmation. This is a manual record, not a live carrier integration — no automatic tracking lookup.
      </div>
      {allocations.length === 0 ? (
        <div style={{ fontFamily: fontSans, fontSize: 13, color: C.inkFaint }}>No allocations yet — commit stock to a customer in Allocation first.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {allocations.map(a => {
            const st = statusOf(a);
            const isEditing = editing === a.id;
            return (
              <div key={a.id} style={{ border: `1px solid ${C.border}`, borderLeft: `4px solid ${st.color}`, borderRadius: 8, padding: "14px 16px", background: "#fff" }}>
                <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                  <div>
                    <div style={{ fontFamily: "monospace", fontSize: 13, color: C.amber, fontWeight: 600 }}>{a.sku}</div>
                    <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginTop: 2 }}>{a.qty} bags · {a.customerName} · allocated {dmy(a.date)}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ fontFamily: fontSans, fontSize: 11, fontWeight: 700, color: st.color, textTransform: "uppercase", letterSpacing: "0.05em" }}>{st.label}</div>
                    {!isEditing && <button style={s.btnSm} onClick={() => startDispatch(a)}>{a.dispatch?.carrier ? "Edit" : "Mark dispatched"}</button>}
                    {a.dispatch?.carrier && !isEditing && (
                      <button style={{ ...s.btnSm, background: a.dispatch.delivered ? C.cream2 : C.green, color: a.dispatch.delivered ? C.inkMid : "#fff" }} onClick={() => toggleDelivered(a.id)}>
                        {a.dispatch.delivered ? "Undo delivered" : "Mark delivered"}
                      </button>
                    )}
                  </div>
                </div>
                {a.dispatch?.carrier && !isEditing && (
                  <div style={{ fontFamily: fontSans, fontSize: 12, color: C.inkMid, marginTop: 8 }}>
                    {a.dispatch.carrier} · {a.dispatch.trackingNumber || "no tracking #"} · dispatched {dmy(a.dispatch.dispatchDate)}
                  </div>
                )}
                {isEditing && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
                      <div>
                        <div style={s.fieldLabel}>Carrier</div>
                        <input style={s.input} value={form.carrier} onChange={e => setForm(f => ({ ...f, carrier: e.target.value }))} placeholder="e.g. An Post" />
                      </div>
                      <div>
                        <div style={s.fieldLabel}>Tracking number</div>
                        <input style={s.input} value={form.trackingNumber} onChange={e => setForm(f => ({ ...f, trackingNumber: e.target.value }))} />
                      </div>
                      <div>
                        <div style={s.fieldLabel}>Dispatch date</div>
                        <input style={s.input} type="date" value={form.dispatchDate} onChange={e => setForm(f => ({ ...f, dispatchDate: e.target.value }))} />
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button style={s.btnPrimary} onClick={() => saveDispatch(a.id)}>Save</button>
                      <button style={s.btn} onClick={() => setEditing(null)}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ── RECALL TRACE ──────────────────────────────────────────────────────────────
// Look up a lot number or SKU and see everything it touched: the roast it
// came from, the packaging run, and every customer allocation + dispatch
// status. Pulls straight from packaging batches / allocations / roast
// history already in the Hub — nothing new is tracked, this is a lookup.
function TraceStep({ label, detail, faint }) {
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 10 }}>
      <div style={{ width: 8, height: 8, borderRadius: "50%", background: faint ? C.borderSoft : C.amber, marginTop: 5, flexShrink: 0 }} />
      <div>
        <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
        <div style={{ fontFamily: fontSans, fontSize: 13, color: faint ? C.inkFaint : C.inkMid, marginTop: 2 }}>{detail}</div>
      </div>
    </div>
  );
}

function exportTraceReport(batch, roast, allocs) {
  const dateStr = todayDmy();
  const r2 = (label, value, bold) => `<tr><td class="lbl">${label}</td><td class="val${bold ? " bld" : ""}">${value}</td></tr>`;
  const sec = (title) => `<tr><td colspan="2" class="sec">${title}</td></tr>`;
  const allocRows = allocs.length
    ? allocs.map(a => r2(
        `${a.customerName || "—"} — ${dmy(a.date) || "—"}`,
        `${a.qty} bags` + (a.dispatch?.carrier ? ` · ${a.dispatch.delivered ? "Delivered" : "Dispatched"} via ${a.dispatch.carrier}${a.dispatch.trackingNumber ? " (" + a.dispatch.trackingNumber + ")" : ""}` : " · Not dispatched")
      )).join("")
    : r2("—", "No allocations yet");

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Recall Trace — ${batch.sku}</title>
<style>
  @page { margin: 18mm 20mm; size: A4 portrait; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, "Helvetica Neue", Arial, sans-serif; font-size: 13px; color: #1C1814; background: #fff; max-width: 680px; padding: 32px 0; }
  h1 { font-size: 24px; font-weight: 700; color: #1C1814; margin-bottom: 5px; }
  .sub { font-size: 12px; color: #7A6E63; padding-bottom: 16px; margin-bottom: 24px; border-bottom: 2px solid #1C1814; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 7px 0; vertical-align: middle; border-bottom: 1px solid #EDE8DE; line-height: 1.3; }
  td.lbl { color: #4A3F35; width: 58%; padding-left: 14px; font-size: 13px; }
  td.val { color: #1C1814; text-align: right; font-size: 13px; }
  td.val.bld { font-weight: 700; }
  td.sec { font-size: 10px; font-weight: 700; color: #8B3A2A; text-transform: uppercase; letter-spacing: 0.1em; padding: 22px 0 7px; border-bottom: 1.5px solid #C17F24; }
  .footer { margin-top: 36px; font-size: 10px; color: #A89E93; border-top: 1px solid #D5CCBE; padding-top: 10px; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; padding: 0; } }
</style>
</head>
<body>
<h1>Recall Trace</h1>
<div class="sub">Skelligs Coffee &nbsp;&middot;&nbsp; ${batch.sku} &nbsp;&middot;&nbsp; ${dateStr}</div>
<table>
  ${sec("Roast")}
  ${r2("Origin", roast?.origin || "—")}
  ${r2("Batch #", roast?.batch || "—")}
  ${r2("Roast date", dmy(roast?.date) || "—")}
  ${sec("Packaging")}
  ${r2("SKU", batch.sku, true)}
  ${r2("Lot", batch.lot)}
  ${r2("Bags", `${batch.qtyBags} × ${batch.bagSizeG}g`)}
  ${r2("Packaged", dmy(batch.packagedDate))}
  ${r2("Best before", dmy(batch.expiryDate))}
  ${sec("Customer allocations")}
  ${allocRows}
</table>
<div class="footer">Skelligs Coffee &nbsp;&middot;&nbsp; Generated ${dateStr} &nbsp;&middot;&nbsp; fdc-roasting-hub.pages.dev</div>
</body></html>`;

  const win = window.open("", "_blank");
  if (!win) { window.alert("Please allow pop-ups for this site to export the report."); return; }
  win.document.write(html);
  win.document.close();
  setTimeout(() => { win.focus(); win.print(); }, 400);
}

function RecallTraceTab() {
  const [query, setQuery] = useState("");
  const [batches] = useState(loadPackagingBatches);
  const [allocations] = useState(loadAllocations);
  const [history] = useState(() => { try { return JSON.parse(localStorage.getItem("fdc_history") || "[]"); } catch { return []; } });

  const q = query.trim().toLowerCase();
  const matches = q ? batches.filter(b => (b.lot || "").toLowerCase().includes(q) || (b.sku || "").toLowerCase().includes(q)) : [];

  return (
    <div style={{ paddingTop: 24 }}>
      <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 16, lineHeight: 1.5 }}>
        Trace a lot number or SKU through the full chain — the roast it came from, the packaging run, and every customer allocation and dispatch status. Everything below is pulled from records already in the Hub.
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        <input style={{ ...s.input, maxWidth: 360 }} value={query} onChange={e => setQuery(e.target.value)} placeholder="Enter lot number or SKU" />
      </div>
      {q && matches.length === 0 && (
        <div style={{ fontFamily: fontSans, fontSize: 13, color: C.inkFaint }}>No packaging batch found matching "{query}".</div>
      )}
      {matches.map(batch => {
        const roast = history.find(r => String(r.id) === String(batch.roastId));
        const allocs = allocations.filter(a => a.sku === batch.sku);
        return (
          <div key={batch.id} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: "16px 20px", marginBottom: 20, background: "#fff" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div style={{ fontFamily: fontSerif, fontSize: 16, color: C.ink }}>{batch.sku}</div>
              <button style={s.btnPrimary} onClick={() => exportTraceReport(batch, roast, allocs)}>Export PDF</button>
            </div>
            <TraceStep label="Green lot / Roast" detail={roast ? `${roast.origin || "—"} — batch ${roast.batch || "—"} — roasted ${dmy(roast.date) || "—"}` : "Roast record not found"} />
            <TraceStep label="Packaged" detail={`${batch.qtyBags} × ${batch.bagSizeG}g — lot ${batch.lot} — packaged ${dmy(batch.packagedDate)} — best before ${dmy(batch.expiryDate)}`} />
            {allocs.length === 0 ? (
              <TraceStep label="Customer allocations" detail="None yet" faint />
            ) : allocs.map(a => (
              <TraceStep key={a.id} label="Allocated" detail={`${a.qty} bags — ${a.customerName} — ${dmy(a.date)}${a.dispatch?.carrier ? ` — ${a.dispatch.delivered ? "Delivered" : "Dispatched"} via ${a.dispatch.carrier}${a.dispatch.trackingNumber ? " (" + a.dispatch.trackingNumber + ")" : ""}` : " — Not dispatched"}`} />
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ── SAMPLE EVALUATION ─────────────────────────────────────────────────────────
// Cups and scores samples offered by importers BEFORE a purchase decision —
// distinct from QC Tools, which cups coffee already bought and roasted.
// Reuses the same CVA Affective form (CVA_ATTRS) already used in QC Tools,
// so the scoring scale is consistent across both, but stores separately
// (fdc_sample_evals) since a sample being cupped has no roast/history record.
const SAMPLE_EVAL_KEY = "fdc_sample_evals";
function loadSampleEvals() { try { return JSON.parse(localStorage.getItem(SAMPLE_EVAL_KEY) || "[]"); } catch { return []; } }
function saveSampleEvals(e) { localStorage.setItem(SAMPLE_EVAL_KEY, JSON.stringify(e)); }

function SampleEvaluationTab({ inventory = [], setInventory }) {
  const [samples, setSamples] = useState(loadSampleEvals);
  // "Buy" → add the coffee to Green stock, carrying over what the sample
  // already records (name, process, cultivar, altitude, price per kg).
  const [addingId, setAddingId] = useState(null);
  const [addDraft, setAddDraft] = useState({ bagWeight: "", bags: "", arrived: localIsoDate() });
  const addToGreenStock = (sample) => {
    if (!setInventory || !(parseFloat(addDraft.bags) > 0) || !(parseFloat(addDraft.bagWeight) > 0)) return;
    const item = {
      id: Date.now(), name: sample.origin || "", origin: "", process: sample.process || "Washed", masl: sample.masl || "",
      moisture: "", density: "", cultivar: sample.cultivar || "", bagWeight: addDraft.bagWeight, bags: addDraft.bags,
      costPerKg: sample.pricePerKg || "", arrived: addDraft.arrived, supplierEmail: "", fromSampleId: sample.id,
    };
    const upInv = [...inventory, item];
    setInventory(upInv); saveInventory(upInv);
    const upS = samples.map(x => x.id === sample.id ? { ...x, addedToStockId: item.id } : x);
    setSamples(upS); saveSampleEvals(upS);
    setAddingId(null);
  };
  const [showForm, setShowForm] = useState(false);
  const initForm = () => ({
    origin: "", supplier: "", process: "Washed", cultivar: "", masl: "", pricePerKg: "",
    dateReceived: localIsoDate(), notes: "", decision: "undecided",
    cva: Object.fromEntries(CVA_ATTRS.map(a => [a, "7"])),
  });
  const [form, setForm] = useState(initForm);
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setCva = (k, v) => setForm(f => ({ ...f, cva: { ...f.cva, [k]: v } }));
  const avgScore = () => {
    const vals = CVA_ATTRS.map(k => parseFloat(form.cva[k]) || 0);
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
  };

  const save = () => {
    if (!form.origin.trim()) return;
    const rec = { id: Date.now(), ...form, score: avgScore() };
    const updated = [rec, ...samples];
    setSamples(updated); saveSampleEvals(updated);
    setForm(initForm()); setShowForm(false);
  };

  const setDecision = (id, decision) => {
    const updated = samples.map(s => s.id === id ? { ...s, decision } : s);
    setSamples(updated); saveSampleEvals(updated);
  };
  const removeSample = (id) => {
    if (!window.confirm("Remove this sample evaluation?")) return;
    const updated = samples.filter(s => s.id !== id);
    setSamples(updated); saveSampleEvals(updated);
  };

  const decisionColor = { buy: C.green, pass: C.red, undecided: C.amber };

  return (
    <div style={{ paddingTop: 24 }}>
      <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 16, lineHeight: 1.5 }}>
        Cup and score samples offered by importers before deciding whether to buy — separate from QC Tools, which cups coffee you already own. Uses the same CVA Affective scoring scale.
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={s.sectionLabel}>Samples</div>
        <button style={s.btnPrimary} onClick={() => setShowForm(v => !v)}>{showForm ? "Cancel" : "+ Log sample"}</button>
      </div>

      {showForm && (
        <div style={{ background: C.cream2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "16px 18px", marginBottom: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1.5fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div><div style={s.fieldLabel}>Origin / coffee name</div><ComboBox value={form.origin} onChange={v => setF("origin", v)} options={ORIGINS} placeholder="e.g. Yirgacheffe lot 4" style={s.input} /></div>
            <div><div style={s.fieldLabel}>Supplier / importer</div><input style={s.input} value={form.supplier} onChange={e => setF("supplier", e.target.value)} placeholder="e.g. Cafe Imports" /></div>
            <div><div style={s.fieldLabel}>Process</div><select style={s.input} value={form.process} onChange={e => setF("process", e.target.value)}>{["Washed", "Natural", "Honey", "Anaerobic", "Wet-hulled"].map(o => <option key={o}>{o}</option>)}</select></div>
            <div><div style={s.fieldLabel}>Cultivar</div><ComboBox value={form.cultivar} onChange={v => setF("cultivar", v)} options={CULTIVARS} placeholder="e.g. Gesha" style={s.input} /></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
            <div><div style={s.fieldLabel}>MASL</div><input style={s.input} value={form.masl} onChange={e => setF("masl", e.target.value)} placeholder="e.g. 1900" /></div>
            <div><div style={s.fieldLabel}>Offered price (€/kg green)</div><input style={s.input} type="number" step="0.01" value={form.pricePerKg} onChange={e => setF("pricePerKg", e.target.value)} placeholder="e.g. 9.50" /></div>
            <div><div style={s.fieldLabel}>Date received</div><input style={s.input} type="date" value={form.dateReceived} onChange={e => setF("dateReceived", e.target.value)} /></div>
          </div>

          <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>CVA Affective Assessment</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
            {CVA_ATTRS.map(attr => (
              <div key={attr}>
                <div style={{ fontFamily: fontSans, fontSize: 9, color: C.inkFaint, marginBottom: 2 }}>{attr}</div>
                <input style={{ ...s.input, fontSize: 12 }} type="number" min="1" max="9" step="1" value={form.cva[attr]} onChange={e => setCva(attr, e.target.value)} />
              </div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 10, borderTop: `1px solid ${C.border}`, marginBottom: 14 }}>
            <div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkLight }}>Average liking across {CVA_ATTRS.length} attributes</div>
            <div style={{ fontFamily: "Georgia,serif", fontSize: 20, color: C.amber, fontWeight: 700 }}>{avgScore()} <span style={{ fontSize: 11, color: C.inkLight, fontFamily: fontSans }}>/9</span></div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <div style={s.fieldLabel}>Notes</div>
            <textarea style={{ ...s.input, height: 60, resize: "vertical" }} value={form.notes} onChange={e => setF("notes", e.target.value)} placeholder="Cup notes, fit for your line, anything else..." />
          </div>
          <button style={s.btnPrimary} onClick={save}>Save evaluation</button>
        </div>
      )}

      {samples.length === 0 ? (
        <div style={{ fontFamily: fontSans, fontSize: 13, color: C.inkFaint }}>No samples logged yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {samples.map(sample => (
            <div key={sample.id} style={{ border: `1px solid ${C.border}`, borderLeft: `4px solid ${decisionColor[sample.decision] || C.amber}`, borderRadius: 8, padding: "14px 16px", background: "#fff" }}>
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                <div>
                  <div style={{ fontFamily: fontSerif, fontSize: 15, color: C.ink }}>{sample.origin || "Untitled"}</div>
                  <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginTop: 2 }}>{sample.supplier || "—"} · {sample.process || "—"} · {sample.cultivar || "—"} · {sample.dateReceived || ""}{sample.pricePerKg ? ` · €${sample.pricePerKg}/kg` : ""}</div>
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <div style={{ fontFamily: fontSerif, fontSize: 18, color: C.amber, fontWeight: 700 }}>{sample.score}<span style={{ fontSize: 11, color: C.inkLight, fontFamily: fontSans }}>/9</span></div>
                  <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: decisionColor[sample.decision] || C.amber, textTransform: "uppercase", letterSpacing: "0.05em" }}>{sample.decision}</div>
                </div>
              </div>
              {sample.notes && <div style={{ fontFamily: fontSans, fontSize: 12, color: C.inkMid, marginBottom: 10, lineHeight: 1.5 }}>{sample.notes}</div>}
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setDecision(sample.id, "buy")} style={{ ...s.btnSmall, background: sample.decision === "buy" ? C.green : C.cream, color: sample.decision === "buy" ? "#fff" : C.inkMid }}>✓ Buy</button>
                <button onClick={() => setDecision(sample.id, "pass")} style={{ ...s.btnSmall, background: sample.decision === "pass" ? C.red : C.cream, color: sample.decision === "pass" ? "#fff" : C.inkMid }}>✕ Pass</button>
                <button onClick={() => setDecision(sample.id, "undecided")} style={{ ...s.btnSmall, background: sample.decision === "undecided" ? C.amber : C.cream, color: sample.decision === "undecided" ? "#fff" : C.inkMid }}>? Undecided</button>
                {sample.decision === "buy" && setInventory && (
                  sample.addedToStockId
                    ? <span style={{ fontFamily: fontSans, fontSize: 11, color: C.green, alignSelf: "center", marginLeft: 6 }}>✓ In Green stock</span>
                    : <button onClick={() => { setAddingId(addingId === sample.id ? null : sample.id); setAddDraft({ bagWeight: "", bags: "", arrived: localIsoDate() }); }} style={{ ...s.btnSmall, marginLeft: 6, borderColor: C.green, color: C.green }}>+ Add to Green stock</button>
                )}
                <button onClick={() => removeSample(sample.id)} style={{ ...s.btnSmall, marginLeft: "auto", color: C.red }}>Remove</button>
              </div>
              {addingId === sample.id && (
                <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.borderSoft}` }}>
                  <div><div style={s.fieldLabel}>Bag weight (kg)</div><input style={{ ...s.input, width: 110 }} type="number" value={addDraft.bagWeight} onChange={e => setAddDraft(d => ({ ...d, bagWeight: e.target.value }))} placeholder="60" /></div>
                  <div><div style={s.fieldLabel}>Bags bought</div><input style={{ ...s.input, width: 100 }} type="number" value={addDraft.bags} onChange={e => setAddDraft(d => ({ ...d, bags: e.target.value }))} placeholder="5" /></div>
                  <div><div style={s.fieldLabel}>Arrived</div><input style={{ ...s.input, width: 150 }} type="date" value={addDraft.arrived} onChange={e => setAddDraft(d => ({ ...d, arrived: e.target.value }))} /></div>
                  <button style={s.btnPrimary} onClick={() => addToGreenStock(sample)} disabled={!(parseFloat(addDraft.bags) > 0) || !(parseFloat(addDraft.bagWeight) > 0)}>Add {sample.origin || "coffee"}</button>
                  <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, flexBasis: "100%" }}>Process, cultivar, altitude{sample.pricePerKg ? " and price" : ""} come from the sample — fill in the rest in Green stock.</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Organised around the working day rather than content type:
//   ROASTING — everything used while planning and running roasts
//   QUALITY  — everything after the beans drop: history, diagnostics, cupping
//   ROASTERY — machine care, references, and the business end
// ── LORING BURNER CODES ─────────────────────────────────────────────────────
// The Loring sends the burner % as a coded number: 100% arrives as 256, 95% as
// 149 and 20% as 32 (the digits of the percentage, written in hex). Older
// bridge versions passed the code on as if it were the percentage, so burner
// figures saved before the fix are wrong. loringBurnerFromCode turns a code
// back into the real percentage; it returns null when a number can't be a
// code, so we never guess.
function loringBurnerFromCode(v) {
  if (typeof v !== "number" || !Number.isInteger(v) || v < 0) return null;
  const hex = v.toString(16);
  if (!/^[0-9]+$/.test(hex)) return null;
  const pct = parseInt(hex, 10);
  return pct <= 100 ? pct : null;
}
// Bridge v2.2 and later says so in every reading (a "bridge" field) and already
// sends true percentages; anything older sends the code.
function burnerFromBridgeMessage(msg) {
  if (typeof msg.burner !== "number") return null;
  if (msg.bridge) return msg.burner;
  const decoded = loringBurnerFromCode(msg.burner);
  return decoded != null ? decoded : msg.burner;
}
// Roasts saved before this moment came from the old bridge.
const BURNER_FIX_CUTOFF_MS = 1793121600000; // 27/10/26 17:20 UTC
// One-time repair of saved roasts. Only live-bridge roasts (their curve points
// carry "temp") and only when EVERY burner number in the roast looks like a
// code. Each repaired roast is marked so it is never converted twice, which
// also keeps it safe when several devices sync.
function migrateBurnerCodes() {
  let hist;
  try { hist = JSON.parse(localStorage.getItem("fdc_history") || "[]"); } catch { return; }
  if (!Array.isArray(hist)) return;
  let changed = 0;
  const out = hist.map(r => {
    if (!r || r.burnerFixed || typeof r.id !== "number" || r.id >= BURNER_FIX_CUTOFF_MS) return r;
    const curve = Array.isArray(r.curve) ? r.curve : null;
    if (!curve || !curve.some(p => p && "temp" in p && typeof p.burner === "number")) return r;
    const events = Array.isArray(r.events) ? r.events : [];
    const nums = [
      ...curve.filter(p => p && typeof p.burner === "number").map(p => p.burner),
      ...events.filter(e => e && typeof e.burner === "number").map(e => e.burner),
    ];
    if (!nums.every(v => loringBurnerFromCode(v) != null)) return r;
    changed++;
    return {
      ...r,
      curve: curve.map(p => (p && typeof p.burner === "number") ? { ...p, burner: loringBurnerFromCode(p.burner) } : p),
      ...(Array.isArray(r.events) ? { events: r.events.map(e => (e && typeof e.burner === "number") ? { ...e, burner: loringBurnerFromCode(e.burner) } : e) } : {}),
      burnerFixed: true,
    };
  });
  if (changed) { try { localStorage.setItem("fdc_history", JSON.stringify(out)); } catch { /* ignore */ } }
}

// ── SUB-VIEWS ───────────────────────────────────────────────────────────────
// Several related screens live under one tab as a small switcher, so the
// section menu stays short. The chosen view is remembered per tab on this
// device. Each view is an existing screen, unchanged.
function SubViews({ id, views }) {
  const storeKey = `fdc_subview_${id}`;
  const [idx, setIdx] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(storeKey), 10);
      return v >= 0 && v < views.length ? v : 0;
    } catch { return 0; }
  });
  const pick = n => {
    setIdx(n);
    try { localStorage.setItem(storeKey, String(n)); } catch { /* ignore */ }
  };
  const cur = views[idx] || views[0];
  return (
    <div>
      <div role="tablist" aria-label="Views" style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "18px 0 0" }}>
        {views.map((v, i) => (
          <button key={v.label} role="tab" aria-selected={idx === i} onClick={() => pick(i)} style={{
            fontFamily: fontSans, fontSize: 12, fontWeight: 600, letterSpacing: "0.02em",
            cursor: "pointer", padding: "6px 14px", borderRadius: 999,
            background: idx === i ? C.amber : "none",
            color: idx === i ? "#fff" : C.inkMid,
            border: `1px solid ${idx === i ? C.amber : C.border}`,
            transition: "all 0.12s",
          }}>{v.label}</button>
        ))}
      </div>
      {cur.node}
    </div>
  );
}

const SECTIONS = [
 { name:"Today", short:"Today", tabs:["Today"] },
 { name:"Green Suite", short:"Green", tabs:["Stock","Costs & pricing","Samples"] },
 { name:"Roasting Suite", short:"Roasting", tabs:["Plan","Recipes","Roast history"] },
 { name:"QC Suite", short:"QC", tabs:["Approval","QC tools"] },
 { name:"Packaging & Dispatch Suite", short:"Packaging & Dispatch", tabs:["Pack","Bag stock","Orders & dispatch","Recall trace"] },
 { name:"Roastery Suite", short:"Roastery", tabs:["Cleaning","Machine","Month-end report"] },
];

// ── TODAY — exception-first landing tab ─────────────────────────────────────
// Pulls straight from Production Planning (orders), Inventory (green stock),
// Roast History + QC Approval (roasts waiting on a look), and Packaging
// (finished goods nearing best-before). Nothing new is tracked here — this
// is a read-only summary over data that already exists elsewhere in the Hub.
function computeSystemHealth({ inventory, history, batches, allocations, syncStatus, syncConfigured }) {
  const issues = [];

  if (syncConfigured && syncStatus !== "synced" && syncStatus !== "syncing" && syncStatus !== "idle") {
    issues.push({ label: "Cloud sync", detail: "Sync isn't currently connected — changes made now may not be reaching the cloud. Check your connection." });
  }

  inventory.forEach(item => {
    const bags = parseFloat(item.bags);
    const bagWeight = parseFloat(item.bagWeight);
    const roasted = parseFloat(item.totalRoastedKg);
    const name = item.name || item.origin || "Unnamed coffee";
    if (!isNaN(bags) && bags < 0) issues.push({ label: name, detail: "Bag count is negative — check Inventory Edit.", section: 1, tab: 0 });
    if (bags > 0 && (isNaN(bagWeight) || bagWeight <= 0)) issues.push({ label: name, detail: "Has stock recorded but no valid bag weight set.", section: 1, tab: 0 });
    if (!isNaN(roasted) && roasted < 0) issues.push({ label: name, detail: "Roasted total is negative — check Inventory Edit.", section: 1, tab: 0 });
  });

  const emptyHistoryStubs = history.filter(r => !r.origin && !r.green && !r.roasted && (!r.events || r.events.length === 0));
  if (emptyHistoryStubs.length > 0) {
    issues.push({ label: "Roast History", detail: `${emptyHistoryStubs.length} blank roast ${emptyHistoryStubs.length === 1 ? "entry" : "entries"} logged with no coffee, weight, or events — safe to delete if these were test rows.`, section: 2, tab: 2 });
  }

  const fg = computeFinishedGoods(batches, allocations);
  fg.forEach(r => {
    if (r.allocated > r.packaged) {
      issues.push({ label: r.sku, detail: `Allocated (${r.allocated}) exceeds packaged (${r.packaged}) — more promised to customers than was ever bagged.`, section: 4, tab: 1 });
    }
  });

  return issues;
}

function TodayTab({ inventory, setSection, goTo }) {
  const [orders] = useState(loadOrders);
  const [approvals] = useState(loadQcApprovals);
  const [history] = useState(() => { try { return JSON.parse(localStorage.getItem("fdc_history") || "[]"); } catch { return []; } });
  const [batches] = useState(loadPackagingBatches);
  const [allocations] = useState(loadAllocations);

  const daysUntil = (dateStr) => dateStr ? Math.ceil((new Date(dateStr) - new Date()) / 86400000) : null;

  const overdueOrders = orders.filter(o => (o.status === "pending" || o.status === "queued") && o.dueDate && daysUntil(o.dueDate) < 0);
  const dueSoonOrders = orders.filter(o => (o.status === "pending" || o.status === "queued") && o.dueDate && daysUntil(o.dueDate) >= 0 && daysUntil(o.dueDate) <= 3);
  const queuedCount = orders.filter(o => o.status === "queued").length;

  const totalKg = item => Math.round(parseFloat(item.bagWeight || 0) * parseFloat(item.bags || 0) * 10) / 10;
  const totalRoastedFor = item => Math.round((parseFloat(item.totalRoastedKg) || 0) * 10) / 10;
  const greenInFor = item => Math.round((totalKg(item) + totalRoastedFor(item)) * 10) / 10;
  const runningLow = item => { const gi = greenInFor(item); return gi > 0 && (totalKg(item) / gi) < 0.2; };
  const lowStockItems = inventory.filter(runningLow);
  const totalGreenKg = inventory.reduce((a, i) => a + totalKg(i), 0);

  const pendingQcRoasts = history.filter(r => r.roasted && (!approvals[r.id] || approvals[r.id].status === "pending"));

  const fg = computeFinishedGoods(batches, allocations);
  const expiringSoon = fg.filter(r => r.earliestExpiry && r.available > 0 && daysUntil(r.earliestExpiry) !== null && daysUntil(r.earliestExpiry) < 60);
  const totalFgAvailable = fg.reduce((a, r) => a + r.available, 0);

  const overduePremises = overduePremisesTasks();

  const attention = [
    ...overdueOrders.map(o => `${o.origin || "Order"} for ${o.customerName || "—"} — overdue`),
    ...lowStockItems.map(i => `${i.name || i.origin} green stock — running low`),
    pendingQcRoasts.length > 0 ? `${pendingQcRoasts.length} roast${pendingQcRoasts.length > 1 ? "s" : ""} sitting in QC, ready for a look` : null,
    ...expiringSoon.map(r => `${r.sku} — ${r.available} bags approaching best-before`),
    ...overduePremises.map(t => `${t.task} — premises cleaning overdue`),
    (() => { const d = new Date(); const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); return d.getDate() >= last - 2 ? "Month-end stock report is due — Green Suite → Month-end report" : null; })(),
  ].filter(Boolean);

  // System health — data-integrity checks, separate from the business-priority
  // "Worth doing today" list above. Catches the exact class of bug that's
  // bitten Inventory before (drifted totals, missing bag weights, orphaned
  // records) at the moment it happens rather than weeks later.
  const syncStatus = useSyncStatus();
  const systemHealthIssues = computeSystemHealth({ inventory, history, batches, allocations, syncStatus, syncConfigured: !!SYNC_WORKER_URL });

  const statCard = (label, value, note) => (
    <div style={{ background: C.cream2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "14px 18px" }}>
      <div style={{ fontFamily: fontSans, fontSize: 10, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: fontSerif, fontSize: 30, color: C.ink }}>{value}</div>
      {note && <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginTop: 4 }}>{note}</div>}
    </div>
  );

  return (
    <div style={{ paddingTop: 24 }}>
      <div style={{ fontFamily: fontSans, fontSize: 11, color: C.inkFaint, marginBottom: 20, lineHeight: 1.5 }}>
        Pulled straight from Production Planning, Inventory, Quality, and Packaging — nothing re-entered here.
      </div>

      {attention.length > 0 && (
        <div style={{ background: C.cream2, border: `1px solid ${C.amber}`, borderLeft: `3px solid ${C.amber}`, borderRadius: 6, padding: "14px 18px", marginBottom: 20 }}>
          <div style={{ fontFamily: fontSans, fontSize: 10, color: C.amber, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 10 }}>Worth doing today</div>
          {attention.map((item, i) => (
            <div key={i} style={{ fontFamily: fontSans, fontSize: 13, color: C.inkMid, padding: "6px 0", borderTop: i > 0 ? `1px solid ${C.borderSoft}` : "none" }}>{item}</div>
          ))}
        </div>
      )}
      {attention.length === 0 && (
        <div style={{ fontFamily: fontSans, fontSize: 13, color: C.green, marginBottom: 20 }}>✓ Nothing urgent — everything's on track.</div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 24 }}>
        {statCard("Roast queue", queuedCount, "batches queued today")}
        {statCard("Green stock", totalGreenKg + "kg", lowStockItems.length > 0 ? `${lowStockItems.length} coffee${lowStockItems.length > 1 ? "s" : ""} running low` : "across inventory")}
        {statCard("Finished goods", totalFgAvailable, "bags available")}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div>
          <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>Awaiting QC approval</div>
          {pendingQcRoasts.length === 0 ? (
            <div style={{ fontFamily: fontSans, fontSize: 12, color: C.inkFaint }}>Nothing waiting.</div>
          ) : pendingQcRoasts.slice(0, 6).map(r => (
            <div key={r.id} style={{ display: "flex", justifyContent: "space-between", fontFamily: fontSans, fontSize: 12.5, color: C.inkMid, padding: "7px 0", borderTop: `1px solid ${C.borderSoft}` }}>
              <span>{r.origin || "Untitled"} — {r.batch || "no batch #"}</span>
              <span style={{ color: C.inkFaint }}>{dmy(r.date) || ""}</span>
            </div>
          ))}
          <button style={{ ...s.btn, fontSize: 12, marginTop: 10 }} onClick={() => goTo ? goTo(3, 0) : setSection(3)}>View QC approval →</button>
        </div>

        <div>
          <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>Orders due</div>
          {overdueOrders.length === 0 && dueSoonOrders.length === 0 ? (
            <div style={{ fontFamily: fontSans, fontSize: 12, color: C.inkFaint }}>Nothing due soon.</div>
          ) : (
            <>
              {overdueOrders.map(o => (
                <div key={o.id} style={{ display: "flex", justifyContent: "space-between", fontFamily: fontSans, fontSize: 12.5, color: C.red, padding: "7px 0", borderTop: `1px solid ${C.borderSoft}` }}>
                  <span>{o.customerName || "—"} — {o.origin}</span>
                  <span>overdue</span>
                </div>
              ))}
              {dueSoonOrders.map(o => (
                <div key={o.id} style={{ display: "flex", justifyContent: "space-between", fontFamily: fontSans, fontSize: 12.5, color: C.inkMid, padding: "7px 0", borderTop: `1px solid ${C.borderSoft}` }}>
                  <span>{o.customerName || "—"} — {o.origin}</span>
                  <span style={{ color: C.inkFaint }}>{daysUntil(o.dueDate)}d</span>
                </div>
              ))}
            </>
          )}
          <button style={{ ...s.btn, fontSize: 12, marginTop: 10 }} onClick={() => goTo ? goTo(2, 0) : setSection(2)}>View production planning →</button>
        </div>
      </div>

      <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${C.border}` }}>
        <div style={{ fontFamily: fontSans, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>System check</div>
        {systemHealthIssues.length === 0 ? (
          <div style={{ fontFamily: fontSans, fontSize: 13, color: C.green }}>✓ No data issues found — sync, inventory, and packaging all check out.</div>
        ) : (
          <div style={{ border: `1px solid ${C.red}`, borderLeft: `3px solid ${C.red}`, borderRadius: 6, overflow: "hidden" }}>
            {systemHealthIssues.map((iss, i) => {
              const clickable = iss.section !== undefined && goTo;
              return (
                <div
                  key={i}
                  onClick={clickable ? () => goTo(iss.section, iss.tab || 0) : undefined}
                  style={{
                    padding: "9px 14px",
                    borderTop: i > 0 ? `1px solid ${C.borderSoft}` : "none",
                    background: C.cream2,
                    cursor: clickable ? "pointer" : "default",
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
                  }}
                >
                  <div>
                    <div style={{ fontFamily: fontSans, fontSize: 12.5, fontWeight: 600, color: C.ink }}>{iss.label}</div>
                    <div style={{ fontFamily: fontSans, fontSize: 11.5, color: C.inkMid, marginTop: 2 }}>{iss.detail}</div>
                  </div>
                  {clickable && <div style={{ fontFamily: fontSans, fontSize: 12, color: C.inkFaint, flexShrink: 0 }}>→</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function App() {
 useState(() => { try { migrateBurnerCodes(); } catch { /* never block the app */ } return 0; });
 const [theme, setTheme] = useState(initialTheme);
 const toggleTheme = () => {
   const next = theme === "dark" ? "light" : "dark";
   applyTheme(next);
   try { localStorage.setItem(THEME_KEY, next); } catch {}
   setTheme(next);
 };
 // machineProfile is read fresh here (after SyncGate has already resolved
 // any cross-device pull) rather than trusting the module-level M set at
 // load time, so a profile pulled from another device is picked up on
 // first render instead of only after the next manual save.
 const [machineProfile, setMachineProfile] = useState(() => {
   const p = loadMachineProfile();
   applyMachineProfile(p);
   return p;
 });
 const updateMachineProfile = (next) => {
   saveMachineProfile(next);
   setMachineProfile(next);
 };
 const [section,setSection]=useState(0);
 const [tabs,setTabs]=useState([0,0,0,0,0,0]); // remembered sub-tab per section
 const tab = tabs[section];
 const setTab = i => setTabs(ts => ts.map((t,s)=> s===section ? i : t));
 // Navigates to an exact section+tab pair reliably — setTab above has a
 // stale-closure quirk when the section is also changing in the same
 // click (it captures the OLD section from this render), so anything
 // that needs to jump to a specific tab in a DIFFERENT section uses this
 // instead.
 const goTo = (sectionIdx, tabIdx = 0) => {
 setSection(sectionIdx);
 setTabs(ts => ts.map((t,s)=> s===sectionIdx ? tabIdx : t));
 };
 const [inventory,setInventory]=useState(loadInventory);
 const [roastMode, setRoastMode] = useState(null);
 const [roastSession, setRoastSession] = useState(0);
 const startRoastMode=profile=>{setRoastMode(profile); setRoastSession(k=>k+1);};
 const cur = SECTIONS[section];

 return (
 <>
 {/* ── MOBILE COMING-SOON SPLASH ──
 Stopgap while the rest of the tabs get the same responsive treatment as
 Profile Builder. Shown instead of the real app below 680px width (phones
 only — matches the Profile Builder breakpoint). Remove the .fdcMobileSplash
 rule block below once every tab has been checked on mobile. */}
 <div className="fdcMobileSplash" style={{minHeight:"100vh",background:C.cream,color:C.ink,flexDirection:"column",alignItems:"center",justifyContent:"center",textAlign:"center",padding:"40px 32px",boxSizing:"border-box"}}>
 <div style={{fontSize:32,marginBottom:20}}>☕</div>
 <div style={{fontFamily:fontSans,fontSize:11,letterSpacing:"0.12em",color:C.inkFaint,textTransform:"uppercase",marginBottom:8}}>First Draft Coffee</div>
 <div style={{fontFamily:fontSerif,fontWeight:600,fontSize:22,color:C.ink,marginBottom:16}}>FDC Roasting Hub</div>
 <div style={{fontFamily:fontSerif,fontWeight:600,fontSize:28,color:C.ink,lineHeight:1.25,marginBottom:14}}>Coming soon<br/>to mobile</div>
 <div style={{fontFamily:fontSans,fontSize:14,color:C.inkFaint,lineHeight:1.6,maxWidth:280}}>
 The full toolkit needs a bit more room to work properly. Pull up your tablet or desktop for now.
 </div>
 </div>
 <style>{`
 .fdcMobileSplash { display: none; }
 @media (max-width: 680px) {
 .fdcMobileSplash { display: flex !important; }
 .fdcAppRoot { display: none !important; }
 }
 `}</style>

 <div className="fdc-bg-texture fdcAppRoot" style={{background:C.cream,minHeight:"100vh",padding:"0 0 40px"}}>
 {/* Ambient botanical texture, applied once here for every page rather
 than per-component — z-index:-1 guarantees it can never sit above or
 intercept clicks meant for anything real (every real section already
 sits at z-index:1+), so this can't interfere with functionality no
 matter what's on screen. Adjust the two "0.1" opacity values below to
 make it more or less visible. */}
 <style>{`
 .fdc-bg-texture { position: relative; }
 .fdc-bg-texture::before {
 content: "";
 position: fixed;
 inset: 0;
 z-index: -1;
 pointer-events: none;
 background-image: url(${botanicalLeftImg}), url(${botanicalRightImg});
 background-repeat: no-repeat, no-repeat;
 background-position: top left, bottom right;
 background-size: 420px auto, 420px auto;
 opacity: 0.1;
 }
 `}</style>

 {/* ── ROAST MODE OVERLAY ── */}
 {roastMode && (
   <RoastMode
     key={roastSession}
     profile={roastMode}
     inventory={inventory}
     setInventory={setInventory}
     onExit={() => setRoastMode(null)}
     onNextBatch={startRoastMode}
   />
 )}

 {/* Header */}
 <div style={{borderBottom:`1px solid ${C.border}`,padding:"20px 28px 0",background:C.cream,position:"relative",zIndex:1}}>
 <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 18 }}>
   <img src={logoImg} alt="FDC Roasting Hub" style={{ width: 42, height: "auto" }} />
   <div style={{flex:1}}>
     <div style={{ fontFamily: fontSans, fontSize: 10, color: C.amber, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 5, fontWeight: 600 }}>
       Loring S70 Peregrine
     </div>
     <div style={{ fontFamily: fontSerif, fontSize: 23, color: C.ink, letterSpacing: "0.14em", fontWeight: 400, textTransform: "uppercase" }}>
       FDC Roasting Hub
     </div>
   </div>
   {/* Section switcher — the day's three modes */}
   <div style={{display:"flex",gap:2,background:C.cream3,border:`1px solid ${C.border}`,borderRadius:8,padding:3}}>
     {SECTIONS.map((sec,i)=>(
       <button key={sec.name} onClick={()=>setSection(i)} style={{
         fontFamily:fontSans, fontSize:11, fontWeight:600, letterSpacing:"0.06em", textTransform:"uppercase",
         background: section===i ? "#fff" : "none",
         border: section===i ? `1px solid ${C.border}` : "1px solid transparent",
         borderRadius:6, cursor:"pointer", padding:"7px 16px",
         color: section===i ? C.ink : C.inkFaint,
         boxShadow: section===i ? "0 1px 2px rgba(28,24,20,0.06)" : "none",
         transition:"all 0.12s",
       }} title={sec.name}>{sec.short||sec.name}</button>
     ))}
   </div>
   <div style={{ display:"flex", alignItems:"center", gap:14, marginLeft:14 }}>
     <SyncStatusDot />
     <PooledLearningToggle />
     <button onClick={toggleTheme} title={theme==="dark" ? "Switch to light mode" : "Switch to dark mode"} style={{
       fontFamily:fontSans, fontSize:14, color:C.inkFaint, background:"none", border:"none", cursor:"pointer", padding:0,
     }}>{theme==="dark" ? "☼" : "◐"}</button>
     <button onClick={exportAllDataAsFile} title="Download everything the Hub tracks as one JSON file" style={{
       fontFamily:fontSans, fontSize:11, color:C.inkFaint, background:"none", border:"none", cursor:"pointer", padding:0,
     }}>⤓ Backup</button>
     <RoasterAccountMenu />
   </div>
 </div>

 {/* Sub-tabs for the active section */}
 <div style={{display:"flex",gap:0}}>
 {cur.tabs.map((t,i)=>(
 <button key={t} onClick={()=>setTab(i)} style={{
 fontFamily:fontSans, fontSize:13, fontWeight:500, background:"none", border:"none",
 cursor:"pointer", padding:"8px 20px 10px",
 color: tab===i ? C.ink : C.inkFaint,
 borderBottom: tab===i ? `2px solid ${C.amber}` : "2px solid transparent",
 marginBottom:"-1px", transition:"color 0.12s",
 }}>{t}</button>
 ))}
 </div>
 </div>

 {/* Content */}
 <div style={{padding:"0 28px",maxWidth:1600,position:"relative",zIndex:1}}>
 {section===0&&tab===0&&<TodayTab inventory={inventory} setSection={setSection} goTo={goTo}/>}
 {section===1&&tab===0&&<SubViews key="stock" id="stock" views={[
 {label:"Green stock", node:<Inventory inventory={inventory} setInventory={setInventory}/>},
 {label:"Stocktake", node:<StocktakeTab inventory={inventory} setInventory={setInventory}/>},
 ]}/>}
 {section===1&&tab===1&&<SubViews key="costs" id="costs" views={[
 {label:"Cost & traceability", node:<InventoryCostAndTraceability inventory={inventory} setInventory={setInventory}/>},
 {label:"Pricing", node:<Pricing inventory={inventory}/>},
 ]}/>}
 {section===1&&tab===2&&<SampleEvaluationTab inventory={inventory} setInventory={setInventory}/>}
 {section===2&&tab===0&&<ProductionPlanningTab inventory={inventory} onStartRoast={startRoastMode}/>}
 {section===2&&tab===1&&<SubViews key="recipes" id="recipes" views={[
 {label:"Profile builder", node:<ProfileBuilder onStartRoast={startRoastMode} inventory={inventory}/>},
 {label:"Blend builder", node:<BlendBuilder inventory={inventory}/>},
 ]}/>}
 {section===2&&tab===2&&<SubViews key="history" id="history" views={[
 {label:"Roast history", node:<History inventory={inventory} setInventory={setInventory}/>},
 {label:"Roast QC", node:<RoastQC/>},
 {label:"Correlation", node:<CorrelationView/>},
 {label:"Curve consistency", node:<CurveConsistencyView/>},
 ]}/>}
 {section===3&&tab===0&&<QCApprovalTab/>}
 {section===3&&tab===1&&<QCTools/>}
 {section===4&&tab===0&&<SubViews key="pack" id="pack" views={[
 {label:"Pack a roast", node:<PackagingRunTab/>},
 {label:"Overview", node:<PackagingOverviewTab inventory={inventory}/>},
 ]}/>}
 {section===4&&tab===1&&<SubViews key="bagstock" id="bagstock" views={[
 {label:"Finished goods", node:<FinishedGoodsTab/>},
 {label:"Packaging materials", node:<div style={{paddingTop:24}}><PackagingStockTab/></div>},
 ]}/>}
 {section===4&&tab===2&&<SubViews key="orders" id="orders" views={[
 {label:"Allocation & invoices", node:<AllocationTab/>},
 {label:"Dispatch", node:<DispatchTab/>},
 ]}/>}
 {section===4&&tab===3&&<RecallTraceTab/>}
 {section===5&&tab===0&&<SubViews key="cleaning" id="cleaning" views={[
 {label:"Cleaning schedule", node:<CleaningSchedule/>},
 {label:"Premises", node:<PremisesCleaningTab/>},
 ]}/>}
 {section===5&&tab===1&&<SubViews key="machine" id="machine" views={[
 {label:"Machine reference", node:<MachineReference/>},
 {label:"Machine profile", node:<MachineProfileSettings machineProfile={machineProfile} onSave={updateMachineProfile}/>},
 ]}/>}
 {section===5&&tab===2&&<MonthEndReportTab inventory={inventory}/>}
 </div>

 {/* Footer */}
 <div style={{padding:"24px 28px 0",marginTop:24,borderTop:`1px solid ${C.border}`,maxWidth:1320,position:"relative",zIndex:1}}>
 <div style={{fontFamily:fontSans,fontSize:11,color:C.inkFaint}}>
 © {new Date().getFullYear()} First Draft Coffee. All rights reserved.
 </div>
 </div>
 </div>
 </>
 );
}

export default function AppRoot() {
  return (
    <AuthGate>
      <SyncGate>
        <App />
      </SyncGate>
    </AuthGate>
  );
}
